/** The ENGLISH catalogue.
 *
 *  Typed `Catalog`, so it must carry every key `ro.ts` declares: a Romanian
 *  string added without its English twin fails `tsc`, which is the whole point.
 *  Keep the sections and the order of `ro.ts` — the two files are read side by
 *  side, and a key that drifts out of its section is a key nobody finds.
 *
 *  Same voice as the Romanian: plain, direct, second person. This is a tool
 *  people work in all day, not a brochure.
 */
import type { Catalog } from "./ro";

export const en: Catalog = {
  // --- words that repeat across every screen -----------------------------
  "comun.se_incarca": "Loading…",
  "comun.salveaza": "Save",
  "comun.se_salveaza": "Saving…",
  "comun.salvat": "saved",
  "comun.renunta": "Cancel",
  "comun.creeaza": "Create",
  "comun.mai_departe": "Next",
  "comun.inapoi": "← Back",
  "comun.adauga": "Add",
  "comun.email": "Email",
  "comun.nume": "Name",
  "comun.parola": "Password",
  "comun.inapoi_la_proiecte": "← Your projects",
  "comun.doar_citire": "You only have read access to this project",

  // --- the API client: what shows when the server is silent or choking ----
  "api.server_mut": "The server is not responding. Check your connection and try again.",
  "api.esec": "Something went wrong ({stare}) on {metoda} {cale}.",
  "api.esec_incarcare": "Something went wrong ({stare}) while uploading the documents.",

  // --- the crashed interface: the safety net at the root (`components/InterfataCazuta`) --
  "cadere.titlu": "This screen stopped because of an error.",
  "cadere.text": "We received its report. Your saved work is safe; reload the page to continue.",
  "cadere.cod": "Error code: {cod}",
  "cadere.reincarca": "Reload the page",

  // --- the account menu, top right ---------------------------------------
  "cont.titlu": "Your account",
  "cont.proiecte": "Projects",
  "cont.tronsoane": "Modules",
  "cont.organizatie": "Organisation",
  "cont.profil": "Profile",
  "cont.iesi": "Sign out",

  // --- access refusals, whole screens ------------------------------------
  "inexistent.negasit": "Not found",
  "fara_acces.titlu": "You do not have access to this project",
  "fara_acces.exista_numit": "<b>{proiect}</b> exists",
  "fara_acces.exista": "The project exists",
  "fara_acces.in_org": " in {organizatie}",
  "fara_acces.explicatie": ", but your account is not on its list. Ask someone who administers it for access.",
  "fara_acces.cine_poate": "Who can give you access",
  "fara_acces.subiect_mail": "Access request: {proiect}",
  "fara_acces.proiect_anonim": "project",
  "fara_acces.inapoi": "← My projects",

  // --- the role banner ---------------------------------------------------
  "rol.banda_citire": "You have <b>read</b> access to this project — you can see everything, but you cannot change anything. Access is granted by whoever administers the project.",

  // --- the project name, in the header and on the dossier title ----------
  "nume_proiect.camp": "Project name",
  "nume_proiect.esec": "Could not save the name.",
  "nume_proiect.redenumeste": "Click to rename the project",
  "nume_proiect.salvat": "name saved",

  // --- the app shell: the left rail --------------------------------------
  "rail.dosar": "Project documents",
  "rail.verificari": "Checks",
  "rail.verificari_picate#n": "Checks — 1 critical or global check fails|Checks — {n} critical and global checks fail|Checks — {n} critical and global checks fail",
  "rail.colectie": "Unit catalogue — the apartment templates",
  "rail.setari": "Project settings — access, name, archiving",
  "rail.aria": "Project navigation",
  "rail.proiect": "Project — the solution you were looking at",
  "rail.solutii": "Project solutions — the whole tree, with preview",
  "app.se_incarca_viewerul": "Loading the viewer…",

  // --- the sign-in screen ------------------------------------------------
  "login.pitch_titlu": "AI that designs the physical world",
  "login.pitch_text": "From the planning certificate to the complete documentation: massing, architecture, structure, services and bill of quantities — with every figure explained and checked against the law.",
  "login.beta": "Closed beta · Romania",
  "login.titlu": "Sign in",
  "login.intra": "Sign in",
  "login.se_verifica": "Checking…",
  "login.sau": "or",
  "login.google": "Continue with Google",
  "login.google_titlu": "Turns on once the platform has a domain",
  "login.doar_invitatie": "Access is by invitation during the closed beta.",
  "login.credentiale": "Wrong email or password.",
  "login.esec": "Could not sign you in. Try again.",

  // --- your own profile --------------------------------------------------
  "profil.titlu": "Profile",
  "profil.salvat": "Saved.",
  "profil.limba_sectiune": "Interface language",
  "profil.limba_eticheta": "Language",
  "profil.limba_nota": "Changes the language the platform speaks to you in. Figures, project names and documents stay as they are — it is the interface that is translated.",
  "profil.limba_esec": "Could not change the language.",
  "profil.voce_sectiune": "Assistant",
  "profil.voce_citeste": "Read the assistant's answers out loud",
  "profil.voce_nota": "This choice belongs to this browser, not to your account: on another computer the assistant stays silent until you turn it on there too.",
  "profil.parola_sectiune": "Password",
  "profil.parola_curenta": "Current password",
  "profil.parola_noua": "New password (at least 10 characters)",
  "profil.schimba_parola": "Change password",
  "profil.parola_schimbata": "Changed.",
  "profil.parola_nota": "Changing the password closes your sessions on every other device. This one stays open.",
  "profil.parola_gresita": "The current password is not right.",
  "profil.parola_esec": "Could not change the password.",

  // --- accepting an invitation -------------------------------------------
  "invitatie.pitch_titlu": "You have been invited",
  "invitatie.pitch_text": "Pick a password and go straight into the organisation's projects. The email address is the one the invitation was sent to and cannot be changed here.",
  "invitatie.titlu": "Invitation",
  "invitatie.se_verifica": "Checking the invitation…",
  "invitatie.creeaza_cont": "Create your account",
  "invitatie.in_org": "In <b>{organizatie}</b>, as {rol}.",
  "invitatie.parola_noua": "Password (at least 10 characters)",
  "invitatie.se_creeaza": "Creating the account…",
  "invitatie.buton": "Create the account",
  "invitatie.esec": "Could not finish. Try again.",
  "invitatie.inexistenta": "That invitation does not exist.",
  "invitatie.anulata": "That invitation was revoked.",
  "invitatie.folosita": "That invitation has already been used.",
  "invitatie.expirata": "That invitation has expired.",
  "invitatie.nefolosibila": "That invitation cannot be used.",

  // --- the organisation: members and invitations -------------------------
  "org.titlu": "Organisation",
  "org.membri": "Members",
  "org.invitatii": "Invitations",
  "org.dezactivat": "disabled",
  "org.reactiveaza": "Re-enable",
  "org.dezactiveaza": "Disable",
  "org.confirma_reactivare": "Re-enable the account {email}?",
  "org.confirma_dezactivare": "Disable the account {email}?",
  "org.rol_membru": "member",
  "org.rol_admin": "admin",
  "org.rol_owner": "owner",
  "org.invita": "Invite",
  "org.link_titlu": "The invitation link.",
  "org.link_text": "Email is not sent yet (SMTP is not configured), so send it yourself. It cannot be recovered once you close this page.",
  "org.nicio_invitatie": "No invitations.",
  "org.expira": "expires",
  "org.stare_acceptata": "accepted",
  "org.stare_expirata": "expired",
  "org.stare_deschisa": "open",
  "org.confirma_stergere_invitatie": "Delete the invitation for {email}?",
  "org.esec_stergere_invitatie": "Could not delete the invitation.",
  "org.esec_invitatie": "Could not create the invitation.",
  "org.esec_stare_cont": "Could not change the state of the account.",
  "org.email_invalid": "That email address is not valid.",
  "org.deja_membru": "They are already a member.",
  "org.deja_invitat": "There is already an open invitation for that address.",
  "org.fara_drept_invitare": "You are not allowed to invite into this organisation.",
  "org.nu_te_dezactivezi": "You cannot disable yourself.",
  "org.doar_owner": "Only an owner can disable another owner.",

  // --- the project list ---------------------------------------------------
  "proiecte.titlu": "Projects",
  "proiecte.toate": "All",
  "proiecte.cauta": "Search by name…",
  "proiecte.nou": "New project",
  "proiecte.esec_incarcare": "Could not load the projects.",
  "proiecte.esec_creare": "Could not create the project.",
  "proiecte.confirma_arhivare": "Archive “{proiect}”? Nothing is deleted, it just leaves the list.",
  "proiecte.arhivare_interzisa": "You are not allowed to archive “{proiect}” — you only have read access to it.",
  "proiecte.esec_arhivare": "Could not archive “{proiect}”.",
  "proiecte.arhiveaza": "Archive the project",
  "proiecte.gol_titlu": "No projects yet.",
  "proiecte.gol_text": "A project holds the documents, the brief and every solution generated on it — site plan, architecture, structure.",
  "proiecte.fara_potrivire": "No project matches “{cautare}”.",
  "proiecte.teren": "{suprafata} m² of land",
  "proiecte.fara_dosar_inca": "nothing from the documents yet",
  "proiecte.se_genereaza": "generating {ce}",
  "proiecte.neconforme": "{n} failing",
  "proiecte.fara_dosar": "no documents",
  "proiecte.actualizat": "updated {cand}",
  "eticheta.apartament#n": "FLAT · 1 ROOM · {s} m² USABLE|FLAT · {n} ROOMS · {s} m² USABLE|FLAT · {n} ROOMS · {s} m² USABLE",
  "eticheta.apartament_scurt#n": "FLAT · 1 RM · {s} m²|FLAT · {n} RM · {s} m²|FLAT · {n} RM · {s} m²",
  "proiecte.numar#n": "1 project|{n} projects|{n} projects",
  "proiecte.ruleaza_acum": "{n} running now",
  "proiecte.documente#n": "1 document|{n} documents|{n} documents",
  "proiecte.azi": "today, {ora}",
  "proiecte.ieri": "yesterday",

  // --- the new project: its type, then its name ---------------------------
  "tip.complet": "Complete project",
  "tip.structura": "Structural project",
  "tip.complet_scurt": "Complete",
  "tip.structura_scurt": "Structure",
  "proiect_nou.eticheta": "New project",
  "proiect_nou.inapoi_proiecte": "Projects",
  "proiect_nou.titlu": "What kind of project are you starting?",
  "proiect_nou.sub": "The choice decides what goes into the documents and what Optimus generates from them.",
  "proiect_nou.incarci": "You upload",
  "proiect_nou.primesti": "You get",
  "proiect_nou.pasi#n": "1 step|{n} steps|{n} steps",
  "proiect_nou.livrabile#n": "1 deliverable|{n} deliverables|{n} deliverables",
  "proiect_nou.complet_de_la": "You start from the plot",
  "proiect_nou.complet_desc": "You upload the plot documents and Optimus designs the whole building: from the site plan to the cost estimate.",
  "proiect_nou.complet_incarci": "Your zoning verification letter, plat map or site plan, title deed, geotechnical report and topographic survey.",
  "proiect_nou.complet_cta": "Start a complete project",
  "proiect_nou.complet_desen": "The whole building, on its plot",
  "proiect_nou.structura_de_la": "You start from an architecture",
  "proiect_nou.structura_desc": "You upload an existing architectural design and Optimus designs its structure, without changing the plans.",
  "proiect_nou.structura_incarci": "The architectural drawings as DWG, for every level, and the geotechnical report.",
  "proiect_nou.structura_cta": "Start a structural project",
  "proiect_nou.structura_desen": "The skeleton of the same building: columns, beams, walls, slabs",
  "proiect_nou.inapoi_alegere": "Back to the choice",
  "proiect_nou.ai_ales": "You chose",
  "proiect_nou.schimba": "Change",
  "proiect_nou.nume_titlu": "What is the project called?",
  "proiect_nou.nume_camp": "Project name",
  "proiect_nou.nume_exemplu": "e.g. Riverside Apartments",
  "proiect_nou.nume_nota": "A working name is enough — you can change it any time from the documents page.",
  "proiect_nou.organizatia": "Organization",
  "proiect_nou.creeaza": "Create the project",
  "proiect_nou.se_creeaza": "Creating…",
  "proiect_nou.urmeaza": "What comes next",
  "proiect_nou.fara_organizatie": "You are not in any organization, so there is nowhere to create a project. Ask whoever runs your organization for an invitation.",
  "proiect_nou.geometrie_titlu": "How does the structure start?",
  "proiect_nou.geometrie_sub": "Optimus proposes the macro geometry on your architecture, or reads it from drawings that already have it. The detailed analysis is the same.",
  "proiect_nou.geometrie_macro_ce": "Macro geometry",
  "proiect_nou.geometrie_macro_desc": "The positions and sizes of the vertical elements — columns and concrete walls — and the positions of the beams, on every level. It is the skeleton the whole analysis is built on.",
  "proiect_nou.detaliu_ce": "Detailed analysis",
  "proiect_nou.detaliu_desc": "The physical model of the building (finite elements, modal and seismic analysis), the sizing of beams and slabs and the reinforcement of every element, so that the code checks pass — for example:",
  "proiect_nou.verif_1": "lateral displacements: SLS and ULS drift and the whiplash effect (P100-1)",
  "proiect_nou.verif_2": "the capacity moments of beams and columns against the analysis demands (M_Rd ≥ M_Ed) and the shear force by zone",
  "proiect_nou.verif_3": "the capacity hierarchy at joints: strong column – weak beam (ΣM_Rc ≥ γ_Rd·ΣM_Rb)",
  "proiect_nou.verif_4": "section ductility: compressed zone depth, confinement of the ends, minimum and maximum reinforcement ratios",
  "proiect_nou.verif_5": "slabs: reinforcement for the moments and the deflection",
  "proiect_nou.verif_6": "foundation: bearing pressure against p_conv, settlements, sliding and overturning",
  "proiect_nou.generata_eticheta": "Optimus proposes",
  "proiect_nou.generata_nume": "Generate the macro geometry and the detailed analysis",
  "proiect_nou.generata_desc": "Optimus places the vertical elements and beams on the imported architecture, searches for the variant that passes the checks, then runs the detailed analysis on it.",
  "proiect_nou.generata_incarci": "The architectural drawings as DWG, for every level, and the geotechnical report.",
  "proiect_nou.generata_cta": "Start with the generated geometry",
  "proiect_nou.importata_eticheta": "From your drawings",
  "proiect_nou.importata_nume": "Import the macro geometry and generate the detailed analysis",
  "proiect_nou.importata_desc": "Optimus reads from the drawings the columns, concrete walls and beams drawn on the structural layers, keeps them in place and runs the detailed analysis on them: the analysis and verification of an existing structure. What is not drawn gets completed, and it says so.",
  "proiect_nou.importata_incarci": "The DWG drawings with the structure drawn on its layers — columns, concrete walls, beams —, for every level, and the geotechnical report.",
  "proiect_nou.importata_cta": "Start with the imported geometry",
  "proiect_nou.geometrie_generata_scurt": "generated macro geometry",
  "proiect_nou.geometrie_importata_scurt": "macro geometry imported from the drawings",
  "proiect_nou.oricand": "Whatever you choose, you can start the other one later from the solutions list, on the same project.",
  "livrabil.situatie": "Site plan",
  "livrabil.fatada": "Facade",
  "livrabil.apartamente": "Apartment plans",
  "livrabil.subsol": "Basement plan",
  "livrabil.structura": "Structure",
  "livrabil.sanitare": "Plumbing",
  "livrabil.electrice": "Electrical",
  "livrabil.hvac": "HVAC",
  "livrabil.deviz": "Full cost estimate",
  "livrabil.plan_structura": "Structural plans",
  "livrabil.plan_structura_desc": "Columns, beams, walls and slabs on every level, plus the foundation.",
  "livrabil.deviz_structura": "Structural cost estimate",
  "livrabil.deviz_structura_desc": "Concrete, rebar and formwork, with their quantities and cost.",

  // --- the bar that starts a project --------------------------------------
  "pasi.dosar": "Upload documents",
  "pasi.localizare": "Plot details",
  "pasi.tema": "Project brief",
  "pasi.volumetrie": "Massing",
  "pasi.fatada": "Facade",
  "pasi.solutii": "Solutions",
  "pasi.proiect_arhitectura": "Upload the design",
  "pasi.arhitectura_importata": "Imported architecture",

  // --- costs --------------------------------------------------------------
  "costuri.capcana": "the project's full bill of quantities · the cost in the solution registry is the one COMPARABLE between branches (different chapters)",
  "costuri.fisa": "solution sheet →",

  // --- platform navigation: the chain, the switcher, phased solutions (nav/) ---
  "solutie.amplasarea_n": "Site plan {n}",
  "solutie.arhitectura_n": "Architecture {n}",
  "solutie.schita": "Sketch",
  "solutie.schita_n": "Structure 0 · sketch",
  "solutie.n": "Structure {n}",
  "solutie.editare_n": "{nume} · Edit {k}",
  "solutie.editare_nr": "{n} · edit {k}",
  "faza.macro": "Macro geometry",
  "faza.detaliu": "Detailed analysis",
  "faza.macro_scurt": "macro",
  "faza.detaliu_scurt": "detail",
  "faza.rafinare": "refinement",
  "faza.rafinare_titlu": "The check on the conformity model did not pass; the geometry is adjusted once, in the same solution",
  "faza.trecerea": "pass {k} of {n}",
  "faza.se_opreste": "stopping…",
  "faza.amplasare": "Placement",
  "faza.agent": "Circulation and landscaping",
  "faza.etaje": "Floors",
  "faza.subsol": "Basement",
  "faza.import": "Import",
  "faza.recomp": "Re-partitioning",
  "faza.fatada": "Façade",
  "durata.sub_minut": "under a minute",
  "durata.min": "{n} min",
  "durata.ore_min": "{h} h {m} min",
  "faza.nu_trece_motiv": "fails: {motiv}",
  "faza.macro_editata": "drawn by you",
  "faza.macro_importata": "imported from the drawings",
  "faza.detaliu_singur": "you start it after the macro geometry",
  "faza.detaliu_urmeaza": "you start it",
  "faza.detaliu_la_cerere": "you start it when you are done drawing",
  "stare.gata": "ready",
  "stare.nu_trece": "fails",
  "stare.in_lucru": "in progress",
  "stare.la_rand": "queued",
  "stare.asteapta": "waiting",
  "stare.picata": "failed",
  "stare.oprita": "stopped",
  "stare.nepornita": "not started",
  "stare.sarit": "skipped",
  "stare.exclus": "excluded",
  "lant.aria": "Project chain",
  "lant.situatie": "Site",
  "lant.arhitectura": "Architecture",
  "lant.structura": "Structure",
  "lant.instalatii": "MEP",
  "lant.costuri": "Planning",
  "lant.in_lucru": "{n} in progress",
  "lant.asteapta": "{n} waiting",
  "lant.nu_trec": "{n} failing",
  "lant.nimic_pe": "nothing on {solutie}",
  "lant.nimic": "—",
  "lant.alege": "Choose: {etapa}",
  "lant.faza_aria": "Solution phase",
  "lant.dupa_detaliu": "— after the detailed analysis",
  "nav.pe": "on {solutie}",
  "variante.titlu": "Escape route variants",
  "variante.fara_scari": "no stairs · {locuri} spaces",
  "variante.cu_scari": "{n} stairs · {locuri} spaces",
  "variante.preferata": "preferred variant",
  "variante.necesar": "required: {necesar} spaces",
  "nav.fantoma": "{etapa} — being generated",
  "nav.eroare.necunoscuta": "The solution in the address does not exist.",
  "nav.eroare.necunoscuta_text": "It may have been deleted, or redone from scratch. You are not moved to another one silently: choose from here.",
  "nav.eroare.alta_disciplina": "The solution in the address belongs to another stage.",
  "nav.eroare.alta_disciplina_text": "The address names a row that does not belong to this page.",
  "nav.eroare.corp_necunoscut": "The building in the address is not part of this solution.",
  "nav.eroare.corp_necunoscut_text": "Choose a building from the menu, or open the solution without one.",
  "nav.eroare.implicita": "Open the default solution",
  "nav.eroare.registru": "Could not read the project's solutions.",
  "nav.eroare.registru_text": "The server answered: {motiv}. The screen does not know yet which solutions the project has, so it shows nothing in their place.",
  "nav.eroare.reincearca": "Try again",
  "comutator.titlu.site_plan": "Site plans",
  "comutator.titlu.architecture": "Architectures",
  "comutator.titlu.structure": "Structures",
  "comutator.gata": "{n} ready",
  "comutator.inchide": "Close",
  "comutator.filtru": "Filter",
  "comutator.pe": "On {solutie} ({n})",
  "comutator.toate": "All ({n})",
  "comutator.fir_curent": "{eticheta} · current thread",
  "comutator.curenta": "current",
  "cand.generata": "generated {cand}",
  "cand.pornita": "started {cand}",
  "cand.azi": "today, {ora}",
  "cand.ieri": "yesterday, {ora}",
  "cand.titlu": "Generated: {la}",
  "cand.titlu_pornita": "Started: {la} — not finished yet",
  "comutator.deschide": "Open",
  "comutator.amplasari": "Site plans",
  "comutator.importate": "Imported",
  "comutator.fara_importate": "No imported architecture.",
  "comutator.gol": "Nothing yet.",
  "comutator.fara_arhitectura_str": "without architecture",
  "comutator.aval_arhitectura": "Architecture: {solutie}",
  "comutator.arhitectura_vine": "Architecture: being generated",
  "comutator.fara_arhitectura": "Architecture: nothing yet",
  "comutator.aval_structura": "Structure: {rezumat}",
  "comutator.fara_structura": "no structure",
  "comutator.schita_in_lucru": "Sketch in progress",
  "comutator.schita_fara_solutii": "Sketch · no solutions",
  "comutator.schita_plus#n": "Sketch + 1 solution|Sketch + {n} solutions|Sketch + {n} solutions",
  "comutator.solutii#n": "1 solution|{n} solutions|{n} solutions",
  "comutator.nota.architecture": "Choosing another architecture moves the Site to its placement, and the Structure to the newest ready solution on it.",
  "comutator.nota.structure": "Choosing another structure moves the Site and the Architecture to the ones under it.",
  "comutator.importa": "Import architecture (DWG)",
  "comutator.toate_solutiile": "All solutions →",
  "actiune.porneste_detaliat": "Start the detailed analysis",
  "actiune.genereaza": "Generate the structure",
  "actiune.importa": "Import the structure",
  "actiune.editeaza_macro": "Edit the macro geometry",
  "actiune.reia_schita": "Redo the sketch",
  "actiune.reruleaza": "Rerun the macro geometry",
  "actiune.opreste": "Stop",
  "actiune.sterge_solutia": "Delete the solution",
  "actiune.sterge_ramura": "Delete the branch",
  "actiune.esec": "Could not find out what would be deleted.",
  "actiune.esec_pornire": "Could not start the run.",
  "actiune.esec_oprire": "Could not stop the run.",
  "actiune.esec_stergere": "Could not delete the solution.",
  "actiune.esec_favorita": "Could not mark the solution as the favourite.",
  "cascada.amplasari#n": "1 site plan|{n} site plans|{n} site plans",
  "cascada.arhitecturi#n": "1 architecture|{n} architectures|{n} architectures",
  "cascada.schite#n": "1 sketch|{n} sketches|{n} sketches",
  "cascada.solutii#n": "1 structure|{n} structures|{n} structures",
  "cascada.nimic": "nothing",
  "cascada.confirma_sterge": "Delete {ce}? Running jobs ({vii}) are stopped. This cannot be undone.",
  "cascada.confirma_schita": "Redo the sketch: this deletes {ce}, with their runs ({vii} running). Continue?",
  "cascada.confirma_reruleaza": "Rerun the macro geometry: the current detailed analysis is deleted, with its cost. Running jobs that are stopped: {vii}. Continue?",
  "cascada.confirma_detaliu": "Start the detailed analysis again: the current one is deleted, with everything it computed. Running jobs that are stopped: {vii}. Continue?",
  "anunt.structura_gata": "{nume} is ready on {arh} — you can switch to the structure",
  "anunt.fatada_gata": "Facade · {nume} ready — the model is complete",
  "anunt.fatada_nu_trece": "Facade · {nume} failed; the default style remains",
  "anunt.editata": "{nume} created from {din}",
  "anunt.deschide": "Open",
  "anunt.inchide": "Dismiss",
  "bloc.schimba": "Switch ▾",
  "bloc.corp": "Building {cod}",
  "bloc.favorita": "favourite",
  "bloc.fa_favorita": "Make favourite",
  "bloc.fa_favorita_titlu": "Make it the favourite — the next stages build on it",
  "bloc.editata_din": "edited by you from {solutie} · {n} locked",
  "bloc.editata_blocate": "edited · {n} locked",
  "bloc.editata_titlu": "The geometry was drawn by a person: the touched elements are locked, and the detailed analysis does not move them.",
  "bloc.importata": "imported from the drawings",
  "bloc.importata_titlu": "The macro geometry is read from the architectural drawings: the drawn positions and sections stay in place, and the detailed analysis does not move them. What is not drawn is completed by Optimus and reported as such.",
  "bloc.importa_titlu": "Reads from the drawings the columns, concrete walls and beams drawn on the structural layers and runs the analysis on them; the generated solution stays alongside.",
  "bloc.import_fara_structura": "The architectural drawings have no structural element drawn (columns, concrete walls or beams on the structural layers) — there is nothing to import.",
  "bloc.arhitectura_editata": "edited {la}",
  "bloc.nesincronizata": "computed on the architecture before its edit",
  "bloc.circulatie_veche_cu_scari": "the circulation lacks the {n} escape stairs of the favourite architecture",
  "bloc.circulatie_veche_fara_scari": "the circulation has escape stairs the favourite architecture no longer has",
  "bloc.circulatie_veche_titlu": "The favourite architecture has other escape stairs at grade than the ones the circulation was traced after. «Redo the plan with the agent» brings it up to date; nothing starts on its own.",
  "bloc.nesincronizata_titlu": "The architecture was edited after this calculation. «Redo the sketch» rebuilds the structure on the current architecture.",
  "bloc.schita_fara_detaliu": "A sketch has no detailed analysis: «Generate the structure» or «Import the structure» first builds the macro geometry, and the detailed analysis starts from it.",
  "bloc.schita_fara_rerulare": "The sketch has no macro geometry to rerun; «Redo the sketch» draws it again.",
  "bloc.schita_fara_stergere": "The sketch belongs to the architecture and is not deleted separately; «Redo the sketch» rebuilds it.",
  "bloc.editeaza_titlu": "Opens the editor on this solution; it stays as it is, and saving creates a new solution.",
  "bloc.mai_mult": "More actions",
  "bloc.incercari_parcare": "Parking Inspector",
  "bloc.incercari_parcare_titlu": "The architecture's parking trials: the packing, the ramps, the lost spaces",
  "incercari.irosit_rampe": "{mp} m² wasted",
  "incercari.irosit_rampe_titlu": "carriageway laid only to reach the ramps, with no stalls to justify it (on the gate → ramp path)",
  "bloc.excavatia": "Placement on modules",
  "bloc.excavatia_titlu": "The buildings placed on parking modules and the excavation decided by the site plan: before, after and the difference",
  "bloc.pud": "PUD export",
  "bloc.pud_titlu": "The PUD's U3 «Urban regulations» sheet from this plan: PDF, DXF and the information with its sources",
  "bloc.refa_excavatia": "Redo the excavation",
  "bloc.excavatia_refa_titlu": "Rerun the site plan from scratch: this solution and everything built on it are deleted, and the buildings and the excavation are placed on parking modules again.",
  "cascada.confirma_refa_excavatia": "Redo the excavation: {ce} are deleted, with their runs ({vii} running), and the site plan is generated again with the buildings placed on modules. Continue?",
  "bloc.refa_subsolul": "Regenerate the underground architecture",
  "bloc.subsol_titlu": "Redoes the architecture's basements between the columns of its structure sketch, on the same row; the floors stay.",
  "ansamblu.titlu": "Project solutions",
  "ansamblu.esec": "Could not load the solutions.",
  "ansamblu.solutii#n": "1 solution|{n} solutions|{n} solutions",
  "ansamblu.interne": "Show internal trials",
  "ansamblu.opreste_tot": "Stop everything",
  "ansamblu.se_sterg": "Deleting…",
  "ansamblu.regenereaza": "Regenerate from scratch",
  "ansamblu.regenereaza_titlu": "Stops everything running, deletes all solutions and starts the site plan again, as right after configuring the project; the architecture and the structure sketches follow on their own",
  "ansamblu.regenereaza_titlu_structura": "Stops everything running, deletes all solutions and re-imports the dossier drawings, as right after configuring the project; the structure follows on its own",
  "ansamblu.se_regenereaza": "Regenerating…",
  "ansamblu.confirma_regenereaza": "Regenerate everything from scratch? Running jobs are stopped and ALL {n} solutions of the project are deleted ({situatie} site plans, {arhitectura} architectures, {schite} sketches, {solutii} structure solutions), then generation starts again, as right after configuring the project. This cannot be undone.",
  "ansamblu.esec_regenereaza": "Could not regenerate the solutions.",
  "ansamblu.amplasari#n": "1 site plan|{n} site plans|{n} site plans",
  "ansamblu.arhitecturi#n": "1 architecture|{n} architectures|{n} architectures",
  "ansamblu.schite#n": "1 sketch|{n} sketches|{n} sketches",
  "ansamblu.solutii_str#n": "1 solution|{n} solutions|{n} solutions",
  "ansamblu.nimic_inca": "nothing yet",
  "ansamblu.costuri_gata": "estimates on the detailed analysis",
  "ansamblu.costuri_dupa": "appear after the detailed analysis",
  "ansamblu.in_lucru_acum": "in progress now",
  "ansamblu.terminate_recent": "finished recently",
  "ansamblu.col_solutie": "Solution",
  "ansamblu.col_faze": "Phases",
  "ansamblu.col_cifra": "Key figure",
  "ansamblu.col_cost": "Cost",
  "ansamblu.col_actiuni": "Actions",
  "ansamblu.gol": "No solutions yet. Start from the dossier or from the project brief.",
  "ansamblu.parcare": "parking {livrat}/{necesar}",
  "ansamblu.parcare_necesar": "{necesar} spaces needed",
  "ansamblu.apartamente#n": "1 flat|{n} flats|{n} flats",
  "ansamblu.corp_sarit": "{cod} skipped",
  "ansamblu.lucrare_vie": "in progress: {lucrare}",
  "ansamblu.lucrare_picata": "failed: {lucrare}",
  "ansamblu.lucrari_titlu": "Work on this architecture",
  "ansamblu.incercari_picate#n": "1 failed attempt before|{n} failed attempts before|{n} failed attempts before",
  "ansamblu.opreste_lucrarea": "Stop: {lucrare}",
  "ansamblu.lucrare_nota": "The architecture opens on what it has now; the work rewrites it when it finishes.",
  "ansamblu.actiuni_pentru": "Actions for {nume}",
  "ansamblu.varianta_arhitectura": "+ architecture on it",
  "ansamblu.alege_rand": "Click a row to preview it here; double click opens the solution.",
  "ansamblu.inchide_previzualizarea": "Close the preview and show the events (Esc)",
  "evenimente.titlu": "Events",
  "evenimente.viu": "live",
  "evenimente.repaus": "every 30 s",
  "evenimente.gol": "Nothing has happened in the project yet.",
  "evenimente.esec": "Could not read the events.",
  "ansamblu.previzualizare": "preview · {etapa} {pe}",
  "ansamblu.previzualizare_aria": "Preview: {nume}",
  "ansamblu.deschide": "Open the solution",
  "ansamblu.apartamente": "Flats",
  "ansamblu.niveluri": "Levels",
  "ansamblu.pleaca_din": "built on",
  "ansamblu.parcare_eticheta": "Parking",
  "ansamblu.parcare_titlu": "Parking spaces against the requirement: {subsol} in the basement, {sol} at grade. Required: {necesar}, {regula}.",
  "ansamblu.parcare_necesar_titlu": "How many parking spaces the brief asks for: {necesar}, {regula}. How many come out is known after the architecture, once the basement is drawn.",
  "ansamblu.parcare_regula_architecture": "flats × spaces per flat — the basement's target; the spaces at grade belong to the site plan",
  "ansamblu.parcare_regula_site_plan": "flats × spaces per flat, plus the visitors' share, which stays at grade",
  "ansamblu.parcare_subsol": "In the basement",
  "ansamblu.parcare_sol": "At grade",
  "ansamblu.verde_total": "Green area",
  "ansamblu.verde_pe_sol": "Green on soil",
  "ansamblu.rampe_legate": "Ramps connected",
  "ansamblu.in_lucru_nota": "The solution is in progress: it opens on what is already done.",
  "act.dosar": "Dossier",
  "act.fel.extraction": "Reading the documents",
  "act.fel.external_architecture": "Importing the architecture drawings",
  "act.fel.basement_architecture": "Redoing the basement",
  "act.fel.facade": "Facade (the agent)",
  "act.fel.recomp": "Repartitioning the affected flats",
  "act.fel.geotech": "Geotechnical study",
  "costuri.dupa_detaliu": "Excavation and structure appear after the detailed analysis.",
  "costuri.vezi_solutia": "← to the solution",
  "costuri.asteapta.solutii#n": "Waiting for 1 solution to be generated|Waiting for {n} solutions to be generated|Waiting for {n} solutions to be generated",
  "costuri.asteapta.fara_numar": "Waiting for the solutions to be generated",
  "costuri.asteapta.nota": "Planning opens on its own when the first solution is ready.",
  "costuri.nimic.titlu": "Planning doesn't exist yet",
  "costuri.nimic.nota": "It appears once the project has a generated architecture.",
  // --- COSTURI · ipotezele, scenariile și asistentul (`viewer/cost/`) -------
  "costuri.tab.ipoteze": "Assumptions",
  "costuri.tab.rezultate": "Results",
  "costuri.tab.scenarii": "Scenarios",
  "costuri.tab.oferte": "Quotes",
  // supplier quotes and per-scenario recipes/prices (`viewer/cost/PanouOferte.tsx`, `CatalogBoq.tsx`)
  "sc.autor.oferta": "quote",
  "sc.preturi_proprii": "{n} own prices and recipes",
  "lc.sc.in_scenariu": "in scenario",
  "lc.sc.editezi": "What you edit",
  "lc.sc.proiectul": "The project",
  "lc.sc.scenariul": "Scenario «{nume}»",
  "lc.sc.explicatie": "You are editing the recipes and prices of scenario «{nume}»: what you change here stays on it only; everything else follows the project.",
  "lc.sc.pret_impus": "quoted price",
  "lc.sc.pret_impus_d": "the work has a ready-made price, {p}; the consumptions above only give the split into material and labour",
  "lc.sc.scoate_pret_impus": "Remove the quoted price",
  "lc.sc.din_oferta": "quote",
  "lc.sc.pret_inainte": "before the quote: {p}",
  "lc.sc.lista_scenariului": "scenario «{nume}»",
  "of.intro": "Drop a supplier's quote here — a price list or a quote for works. I read it, put its prices on the project's recipes, items and resources, and make a new scenario from the current one with them, so you see what they change. The current scenario stays as it is. The quote also goes into the project documents, under «Rates & prices»; a document uploaded there is read the same way, on its own, and shows up here.",
  "of.zona_titlu": "Drop the quote here or choose the file",
  "of.zona_d": "PDF (scanned too), Excel, Word, image or CSV · up to 20 MB",
  "of.se_urca": "Uploading: {n}",
  "of.fara_lista": "A quote's prices go on the bill of quantities, and the project has none yet (no architecture, no structure).",
  "of.niciuna": "No quotes yet.",
  "of.format": "«{nume}» can't be read: send a PDF, Excel, Word, image or CSV.",
  "of.nu_s_a_urcat": "«{nume}» was not uploaded: {e}",
  "of.stare.se_citeste": "reading",
  "of.stare.aplicata": "applied",
  "of.stare.eroare": "error",
  "of.se_citeste_d": "Reading the quote and looking for the price of each thing in the project — a minute or two; a large price list, a few.",
  "of.scenariu_curent": "current scenario: «{nume}»",
  "of.deschide_scenariul": "Open scenario «{nume}»",
  "of.aplica_iar": "Apply again",
  "of.aplica_iar_t": "One more scenario from this quote, made from the current scenario",
  "of.sterge": "Delete",
  "of.sterge_confirma": "Delete the quote «{nume}»? The document also leaves the project documents; the scenario made from it stays.",
  "of.preturi_puse": "{n} prices applied",
  "of.respinse": "{n} set aside",
  "of.respinse_titlu": "Set aside",
  "of.impact": "{v} on the direct cost",
  "of.th.tinta": "In the project",
  "of.th.linie": "Line in the quote",
  "of.th.oferta": "Quoted price, excl. VAT",
  "of.th.acum": "Now",
  "of.th.nou": "From the quote",
  "of.fel_tinta.reteta": "recipe",
  "of.fel_tinta.articol": "item",
  "of.fel_tinta.resursa": "resource",
  "of.fel.lucrare": "installed",
  "of.curs": "rate {c} {m}/€",
  "of.curs_estimat": "estimated rate {c} {m}/€",
  "of.curs_estimat_t": "The quote's currency has no project rate; the rate is the one estimated when reading, at the quote's date.",
  // --- RESOURCE MARKET (`viewer/cost/ExploreazaPiata.tsx`, `ImportPiata.tsx`) ---
  "piata_res.buton": "Market",
  "piata_res.buton_t": "Explore the market: prices seen for this resource, from several sources",
  "piata_res.chip": "market",
  "piata_res.titlu": "Explore the market — {nume} ({um})",
  "piata_res.inchide": "Close",
  "piata_res.in_proiect": "In the project",
  "piata_res.fara_tinte": "The project's bill of quantities doesn't use this resource: its price changes nothing.",
  "piata_res.din_piata": "from the market",
  "piata_res.pus_de_om": "set by hand",
  "piata_res.rezumat": "Market, on the catalogue basis (delivered to site, no pump, excl. VAT): lowest {min} · median {med} · highest {max} — {preturi} from {surse}",
  "piata_res.preturi#n": "one price|{n} prices|{n} prices",
  "piata_res.surse#n": "one source|{n} sources|{n} sources",
  "piata_res.colectat": "collected on {la}",
  "piata_res.fara_preturi": "No prices yet — the collection found nothing for this resource.",
  "piata_res.foloseste_minim": "Use the lowest: {p}",
  "piata_res.foloseste": "Use",
  "piata_res.foloseste_t": "Put this price on the project's resources above",
  "piata_res.scoate": "Remove the market price",
  "piata_res.scoate_t": "The target goes back to its price before the import (the global one)",
  "piata_res.doar_exacte": "exact matches only",
  "piata_res.doar_exacte_t": "An equivalent (another exposure class, an old steel grade) is shown but never used: a low price for a different product would quietly spoil the cost.",
  "piata_res.th.sursa": "Source",
  "piata_res.th.produs": "Product · basis",
  "piata_res.th.vazut": "Seen",
  "piata_res.th.pe_baza": "{m}/{um} on basis",
  "piata_res.th.stare": "Status",
  "piata_res.nivel.1": "Level 1: close to a transaction, or official",
  "piata_res.nivel.2": "Level 2: good indicative",
  "piata_res.nivel.3": "Level 3: weak",
  "piata_res.livrare.franco": "delivered to site",
  "piata_res.livrare.din_statie": "ex works",
  "piata_res.livrare.franco_cu_pompa": "delivered, pump included",
  "piata_res.livrare.din_magazin": "store pickup",
  "piata_res.ajustare.transport": "+delivery {v} lei/{um}",
  "piata_res.ajustare.pompa": "−pump {v} lei/{um}",
  "piata_res.acces.inclus": "access fee in the price (supplier's zone)",
  "piata_res.acces.neinclus": "without the city access fee",
  "piata_res.ajustare_t": "Brought to the catalogue basis (delivered to site, no pump) with the collector's assumption, written here so it can be judged.",
  "piata_res.echivalent": "equivalent",
  "piata_res.fara_baza": "cannot be brought to basis",
  "piata_res.cel_mai_mic": "lowest",
  "piata_res.stare.ok": "good",
  "piata_res.stare.suspect": "suspicious",
  "piata_res.stare.respins": "rejected",
  "piata_res.cu_tva": "incl. VAT",
  "piata_res.fara_tva": "+VAT",
  "piata_res.furnizor": "supplier: {f}",
  "piata_res.autoritate": "buyer: {a}",
  "piata_res.vazut_la": "seen on {la}",
  "piata_res.doc.fisa_tehnica": "data sheet",
  "piata_res.doc.dop": "declaration of performance",
  "piata_res.doc.agrement": "technical approval",
  "piata_res.doc.alt": "document",
  "piata_res.lucrez": "Applying…",
  "piata_res.pus#n": "The market price is now on one target.|The market price is now on {n} targets.|The market price is now on {n} targets.",
  "piata_res.scos#n": "The market price is removed from one target.|The market price is removed from {n} targets.|The market price is removed from {n} targets.",
  "piata_res.respinse": "Not applied to: {l}",
  "piata_res.fara_curs": "The project has no leu/euro rate, so market prices cannot be applied.",
  "piata_res.nu_s_a_citit": "Market prices could not be read: {e}",
  "piata_res.salveaza_intai": "Save or cancel your changes before taking prices from the market.",
  "piata_res.pe_proiect": "Prices go on the project: every scenario inherits them, except what each one changes.",
  "piata_res.import.buton": "Import market prices",
  "piata_res.import.titlu": "Import market prices into the project",
  "piata_res.import.au_pret#n": "one resource has a market price|{n} resources have a market price|{n} resources have a market price",
  "piata_res.import.th.resursa": "Resource",
  "piata_res.import.th.acum": "Now",
  "piata_res.import.th.piata": "Market",
  "piata_res.import.th.dif": "Difference",
  "piata_res.import.importa#n": "Import one|Import {n}|Import {n}",
  "piata_res.import.gol": "No project resource has a market price yet: the collection found nothing for them, or the bill of quantities doesn't use them.",
  "piata_res.motiv.pus_de_om": "set by hand — not overwritten unless ticked",
  "piata_res.motiv.fara_curs": "no project rate",
  "costuri.tab.boq": "Bill of quantities",
  "costuri.tab.devizul": "Estimate",
  // --- BILL OF QUANTITIES (`viewer/cost/ListaCantitati.tsx`, `CatalogBoq.tsx`) ---
  "lc.titlu": "Bill of quantities",
  "lc.generata": "generated {la} · catalogue {cat} · EUR excl. VAT",
  "lc.recalculeaza": "Recalculate",
  "lc.export_tot": "Export all (Excel)",
  "lc.export_subiecte": "Export {n} subjects (Excel)",
  "lc.fara_detaliu": "The bill of quantities is built on the thread's architecture; until the structure's detailed design, the structure's quantities are estimated per built m².",
  "lc.eroare": "The bill could not be computed: {e}",
  "lc.reincearca": "Try again",
  "lc.subiecte": "Subjects",
  "lc.toate": "all subjects",
  "lc.cauta": "Search an item…",
  "lc.vazut": "{n} items shown · {v}",
  "lc.th_pu_total": "Unit price",
  "lc.th_total": "Total",
  "lc.total_subiect": "TOTAL {s}",
  "lc.subiect.rezumat": "{n} items · ≈ {est}",
  "lc.fara_pret": "resource without a price",
  "lc.neaplicate": "{n} catalogue items have no quantity on this project",
  "lc.plc.direct": "Direct cost",
  "lc.plc.direct_d": "material + services {m} · labour {l}",
  "lc.plc.general": "Grand total (excl. VAT)",
  "lc.plc.general_d": "the contractor's price, with {p}; contingencies sit in the reserve under Results",
  "lc.plc.pe_m2": "Per m² gross",
  "lc.plc.pe_m2_d": "{a} m² including the basement",
  "lc.plc.articole": "Items",
  "lc.plc.articole_d": "≈ {est} · {dev} from estimates · {om} set by hand",
  "lc.plc.proiect": "Flats",
  "lc.plc.proiect_d": "{c} buildings · {s} basement levels · {p} m² of parking",
  "lc.rec.direct": "Total direct cost",
  "lc.rec.general": "GRAND TOTAL (excl. VAT)",
  "lc.fel.calculat": "measured",
  "lc.fel.estimat": "estimated quantity",
  "lc.fel.solutie": "from estimate",
  "lc.fel.om": "set by hand",
  "lc.tip.material": "material",
  "lc.tip.manopera": "labour",
  "lc.tip.utilaj": "equipment",
  "lc.tip.transport": "transport",
  "lc.um.ora": "hour",
  "lc.det.cantitatea": "Quantity",
  "lc.det.calculata": "measured: {v}",
  "lc.det.pe_corpuri": "By building",
  "lc.det.specificatii": "Specifications",
  "lc.det.observatii": "Notes",
  "lc.det.pret": "Price",
  "lc.det.reteta": "recipe {cod}",
  "lc.din_deviz.structura": "Item taken from the structure estimate: quantities from the detailed design, prices from the project's rates (the «Prices, zone & norms» tab).",
  "lc.din_deviz.fatada": "Item taken from the facade estimate: quantity from the facade takeoff, price from the facade catalogue (corrected from the «Envelope» row of the results).",
  "lc.res.tip": "Type",
  "lc.res.resursa": "Resource",
  "lc.res.consum": "Consumption",
  "lc.res.cantitate": "Quantity",
  "lc.res.pret": "Unit price",
  "lc.res.pret_ora": "Price / hour",
  "lc.res.total": "Total",
  "lc.res.cod": "Code",
  "lc.res.sursa": "Price source",
  "lc.res.fara_faza": "No phase — tower cranes and resources not used in the list",
  "lc.res.si_la": "also in: {faze}",
  "lc.lista.materiale": "Materials",
  "lc.lista.manopera": "Labour",
  "lc.lista.utilaje": "Equipment",
  "lc.lista.transport": "Transport",
  "lc.catalog.gasite": "{n} recipes",
  "lc.catalog.nimic": "No recipe passes the filters.",
  "lc.catalog.din_calcul": "from the structural design",
  "lc.catalog.structura_nota": "the structure estimate stands on them; their rates are in «Prices, zone & norms»",
  "lc.catalog.activitati": "activities",
  "lc.catalog.modificata": "modified",
  "lc.catalog.ca_global": "as in the global catalogue",
  "lc.catalog.adauga_din_global": "Add from the global catalogue ({n})…",
  "lc.catalog.goala": "The recipes the bill uses are cloned onto the project when it is first computed; until then, add from the global catalogue.",
  "lc.catalog.resurse_goale": "No resources on the project yet: they are cloned along with the recipes in use, or added from the global catalogue.",
  "lc.catalog.deschide": "Recipes and resources",
  "lc.catalog.la_lista": "← The bill",
  "lc.catalog.titlu": "Bill of quantities catalogue",
  "lc.catalog.versiune": "global catalogue {v} · {n} changes from it",
  "lc.catalog.retete": "Recipes",
  "lc.catalog.resurse": "Resources",
  "lc.catalog.explicatie": "The recipes and resources the bill uses are cloned onto the project: what you see here belongs to the project and is edited here — a consumption, a price, a quantity set by hand, a hidden or a new recipe. The global catalogue stays in the platform; recipes can still be added from it, and «Back to global values» restores its values.",
  "lc.catalog.din_proiect": "project",
  "lc.catalog.ascunsa": "hidden",
  "lc.catalog.rezerva": "fallback",
  "lc.catalog.rezerva_t": "Fallback recipe: it enters the list only while {sursa} is missing. The recipe is fixed like any other; its quantity is the estimate.",
  "lc.catalog.revino": "Back to global values",
  "lc.catalog.sterge": "Delete recipe",
  "lc.catalog.nume": "Item name",
  "lc.catalog.specificatii": "Technical specifications",
  "lc.catalog.observatii": "Notes",
  "lc.catalog.cheie": "Quantity from the takeoff",
  "lc.catalog.cheie_t": "the key by which the engine takes the quantity from the solutions; a recipe without a key gets its quantity by hand",
  "lc.catalog.cantitate_om": "Quantity set on the project",
  "lc.catalog.masurat": "measured {v}",
  "lc.catalog.platit_in": "paid in {cod}, cranes × months",
  "lc.factor.macarale_buc": "Cranes",
  "lc.factor.macara_luni": "Months per crane",
  "lc.catalog.ascunde": "not in this project's bill",
  "lc.catalog.scoate": "remove the consumption",
  "lc.catalog.pu": "Unit price",
  "lc.catalog.pu_d": "material {m} · labour {l} · equipment {u} · transport {tr}",
  "lc.catalog.alege_resursa": "choose a resource…",
  "lc.catalog.adauga_consum": "Add consumption",
  "lc.catalog.sursa": "Price source: {s}",
  "lc.catalog.alege": "Pick a recipe from the list to see it and change it on the project.",
  "lc.catalog.adauga_reteta": "+ New project recipe",
  "lc.catalog.reteta_noua": "New item",
  "lc.catalog.global_era": "global: {v}",
  "lc.catalog.calcat": "Someone else changed the catalogue in the meantime; you now see their version. Redo the change if still needed.",
  "lc.subiect.terasamente": "Earthworks and shoring",
  "lc.subiect.structura": "Structural frame",
  "lc.subiect.arhitectura": "Architecture — masonry, finishes, interior joinery",
  "lc.subiect.fatada": "Envelope — facade, external joinery, terraces",
  "lc.subiect.electrice": "Electrical and low-current installations",
  "lc.subiect.sanitare": "Plumbing and fire-fighting installations",
  "lc.subiect.termice": "Heating, ventilation and air conditioning",
  "lc.subiect.lifturi": "Lifts and platforms",
  "lc.subiect.exterioare": "External works and utility connections",
  "lc.subiect.santier": "Site organisation",
  "lc.capitol.santier.acces": "Truck access fees in the city",
  "lc.capitol.santier.organizare": "Site organisation — cabins, fencing, temporary utilities",
  "costuri.tab.verificate": "checked",
  "costuri.tab.de_verificat": "to check",
  "costuri.asistent": "Estimate assistant",
  "costuri.pas.scenarii": "Reading the project's scenarios…",
  "costuri.pas.scenarii_gata": "Read the scenarios.",
  "costuri.pas.deviz": "Reading the estimate…",
  "costuri.pas.deviz_gata": "Read the estimate.",
  "costuri.pas.articole": "Reading the execution estimate items…",
  "costuri.pas.articole_gata": "Read the items.",
  "costuri.pas.simuleaza": "Working out the variant…",
  "costuri.pas.simuleaza_gata": "Worked out the variant, nothing saved.",
  "costuri.pas.creeaza": "Creating the new scenario…",
  "costuri.pas.creeaza_gata": "Created a new scenario; the old one is still under «Scenarios».",
  "costuri.pas.alege": "Switching to the chosen scenario…",
  "costuri.pas.alege_gata": "Switched to the chosen scenario.",
  "bc.cum.ajustat": "{cum} · × {f} in this scenario = {total}",
  "bc.r.cTerenBani.element": "Land — the part paid in cash",
  "bc.r.cTerenBani.obs": "on top of the {p} % of GFA ceded in apartments",
  "bc.r.cTerenBani.de_unde": "The cash price of the land, entered in the assumptions.",
  "bc.ui.ajustat": "× {f} scenario",
  "bc.ui.banda": "Net profit by apartment price",
  "bc.ui.fara_solutie": "The summary is built on the thread's architecture solution; the project has none yet.",
  "bc.ui.banda_sub": "every other assumption as it is",
  "bc.ui.ce_construim": "What gets built · the quantities behind the figures",
  "ip.pret_teren_bani": "Land price paid in cash",
  "ip.ajutor.cedat": "The seller receives turnkey apartments out of the GFA instead of cash.",
  "ip.ajutor.teren_bani": "Zero when the land is paid in apartments only.",
  "ip.ajutor.pret_ap": "Net price per m² of net area. Results also show how the profit moves at ±20 %.",
  "ip.piata": "Market in the area: {pret}/m² net",
  "ip.piata_pe_finisaj": "{oferte} with {clasa} finishes in «Sales in the area», {cerut}/m² asked, without VAT",
  "ip.piata_pe_toate": "{oferte} in «Sales in the area», {cerut}/m² asked, without VAT",
  "ip.piata_foloseste": "Use",
  "ip.piata_folosit": "in use",
  "ip.piata_t": "The price recommended by the area profile. It is applied only with «Use»; until then the current value stays.",
  "ip.implicit": "default",
  "ip.modificat": "changed",
  "ip.valideaza": "Confirm",
  "ip.validata": "confirmed",
  "ip.validata_de": "Confirmed by {de} · {la}",
  "ip.scenariul": "Scenario",
  "ip.alt_scenariu": "Another scenario →",
  "ip.ce_decizi": "What you decide",
  "ip.ce_decizi_sub": "the developer's assumptions · EUR, excl. VAT",
  "ip.grup.teren": "Land",
  "ip.grup.vanzari": "Sales",
  "ip.grup.calendar": "Timeline",
  "ip.din_proiect": "From the project",
  "ip.din_proiect_sub": "measured or calculated · changed at the source",
  "ip.din_program": "from the programme",
  "ip.ajutor.durata_program": "The duration belongs to the construction programme, in months; change it in the «Programme» tab — cranes per period, headcounts, lags —, not here.",
  "ip.repere": "Market benchmarks",
  "ip.repere_sub": "ratios and prices per m² from comparable projects, until the project has its own figure",
  "ip.verificate_de": "Assumptions confirmed by {de} · {la}",
  "ip.raman_implicite": "{n} of {tot} assumptions still have their default value, unconfirmed.",
  "ip.retrage": "Withdraw the confirmation",
  "ip.la_rezultate": "Results →",
  "ip.sari": "Skip to the results",
  "ip.am_verificat": "I've checked the assumptions → Results",
  "sc.baza": "Base",
  "sc.calcat": "Someone else changed the scenarios meanwhile; you're now seeing theirs. Redo your change if it's still needed.",
  "sc.fara_abateri": "the default assumptions",
  "sc.grafic": "Cost against return",
  "sc.grafic_sub": "each dot is a scenario; click it to add it to the comparison",
  "sc.grafic_aria": "The scenarios by total investment cost and ROI per year",
  "sc.ax_cost": "Total investment cost",
  "sc.ax_roi": "ROI per year",
  "sc.roi": "Total ROI",
  "sc.roi_an": "ROI per year",
  "sc.nume_nou": "Scenario {n}",
  "sc.intro": "The same project measurements, different assumptions. The assistant makes a new scenario for every variant asked for; «Use» takes you back to any of them.",
  "sc.nou": "+ New scenario",
  "sc.lista": "All scenarios",
  "sc.lista_sub": "tick to compare with the current one",
  "sc.compara": "Compare",
  "sc.compara_cu": "Compare «{nume}» with the current scenario",
  "sc.th.scenariu": "Scenario",
  "sc.th.ce_e_altfel": "What differs",
  "sc.redenumeste": "Rename",
  "sc.curent": "current",
  "sc.autor.asistent": "assistant",
  "sc.autor.om": "person",
  "sc.foloseste": "Use",
  "sc.sterge": "Delete",
  "sc.comparatie": "Comparison",

  // --- COSTURI · rezumatul de business case (`viewer/cost/`) --------------
  "bc.sursa.acte": "deeds",
  "bc.sursa.puz": "zoning",
  "bc.sursa.proiect": "project",
  "bc.sursa.tema": "brief",
  "bc.sursa.referinta": "benchmark",
  "bc.sursa.formula": "formula",
  "bc.sursa.ipoteza": "assumption",
  "bc.sursa.lege": "law",
  "bc.obs.masurat_arhitectura": "measured on the architecture",
  "bc.r.teren.obs_plan": "the site plan's plot",
  "bc.r.teren.de_unde_plan": "The plot the thread's site plan is drawn on — the same one the architecture works its FAR and site coverage on.",
  "bc.r.teren.semnal_acte": "The land deeds say {acte} m²; the site plan is drawn on {plan} m². The summary follows the plan.",
  "bc.r.scd.de_unde_masurat": "The gross floor area above ground, measured on the thread's architecture (the figures register: each storey's slab to the finished face of the façade).",
  "bc.r.suPeGba.de_unde_masurat": "The net area of the dwellings (rooms, kitchen, bathroom, hall — no balconies), measured on the architecture, divided by the GFA.",
  "bc.r.terasePeGba.de_unde_masurat": "The dwellings' balconies, loggias and terraces, measured on the architecture, divided by the GFA.",
  "bc.r.comPeGba.de_unde_masurat": "The measured architecture has no retail space.",
  "bc.r.mpLoc.de_unde_masurat": "The basement's gross area measured on the architecture, divided by the spaces counted in it.",
  "bc.r.apMediu.de_unde_masurat": "The net area measured on the architecture, divided by its apartments.",
  "bc.r.subsol.de_unde_masurat": "The basement's gross area, measured on the architecture (the sum of the slabs below ground).",
  "bc.r.circ.cum_plan": "{tot} m², of which {trotuare} m² footpaths",
  "bc.taxa.autorizatie": "building permit",
  "bc.taxa.isc_calitate": "ISC quality",
  "bc.taxa.isc_control": "ISC state control",
  "bc.taxa.csc": "Builders' Social Fund",
  "bc.taxa.timbru_arhitectura": "architecture stamp",
  "bc.ui.pret_apartamente": "Apartment price",
  "bc.ui.tic": "Total investment cost",
  "bc.ui.pe_m2_construit": "{v} built",
  "bc.ui.constructii": "Construction",
  "bc.ui.din_tic": "{p} of the total cost",
  "bc.ui.venituri": "Developer revenue",
  "bc.ui.din_proiect": "{p} of the project",
  "bc.ui.profit_net": "Net profit",
  "bc.ui.dupa_impozit": "after {p} tax",
  "bc.ui.roi_pe_an": "{p} per year · {n} years",
  "bc.ui.roi_pe_an_flux": "{p} per year · money tied up {m} of {n} years on average",
  "bc.ui.echilibru": "Break-even price",
  "bc.th.element": "Item",
  "bc.th.cantitate": "Quantity",
  "bc.th.um": "Unit",
  "bc.th.parametru": "Price / parameter",
  "bc.th.valoare": "Amount ({moneda})",
  "bc.th.din_tic": "% TIC",
  "bc.th.observatii": "Notes",
  // --- the glossary behind the hover explanations (`viewer/cost/Termeni.tsx`) --
  "termen.explicatie": "What {ce} means",
  "termen.tic.nume": "TIC — total investment cost",
  "termen.tic.ce": "Everything the investor pays before selling: the land (in cash and in kind), the works with their legal fees and levies, financing, cadastre, management, the sales commission and the contingency. Every row is reported against it («% TIC»), and so is the return: ROI = net profit / TIC.",
  "termen.roi.nume": "ROI — return on investment",
  "termen.roi.ce": "Net profit divided by the total investment cost (TIC). «ROI per year» divides it by how many years the money stays tied up, on average: with the execution schedule the outlays go out month by month and a euro does not stay the whole duration, so the annual ROI can come out higher than the total one; without a schedule, all the money stays for the whole project.",
  "termen.gba.nume": "GBA / GFA — gross building area",
  "termen.gba.ce": "The sum of all floor areas, measured to the outer face of the walls — what Romanian plans call SCD. Above ground unless stated otherwise; the basement is given separately.",
  "termen.scd.nume": "SCD — gross floor area (Romanian acronym)",
  "termen.scd.ce": "The sum of the built areas of the above-ground storeys, to the outer face of the walls: site area × FAR. The same thing as GBA.",
  "termen.adc.nume": "ADC — built-up floor area (Romanian acronym)",
  "termen.adc.ce": "The sum of the storeys' built areas; for the basement, the sum of the slabs below ground level, as measured on the architecture.",
  "termen.su.nume": "Su — net (usable) area",
  "termen.su.ce": "The inside area of the apartment, between wall faces, without balconies, terraces and common parts. It is what gets sold and what the price per m² refers to.",
  "termen.cut.nume": "CUT — floor area ratio (FAR)",
  "termen.cut.ce": "Gross floor area divided by the site area. The maximum comes from the zoning plan (PUZ) or the local regulation; the summary uses the ratio of the drawn solution.",
  "termen.pot.nume": "POT — site coverage",
  "termen.pot.ce": "The building footprint divided by the site area, as a percentage.",
  "termen.puz.nume": "PUZ — zonal urban plan",
  "termen.puz.ce": "The planning document that says what may be built on the site: floor area ratio, coverage, height, setbacks, uses.",
  "termen.rlu.nume": "RLU — local urban regulation",
  "termen.rlu.ce": "The municipality's building rules that accompany the urban plan: setbacks, utility connections, permit conditions.",
  "termen.cc.nume": "CC — construction cost",
  "termen.cc.ce": "All the construction works: excavation, structure, architecture, envelope, services, basement, landscaping, infrastructure and related costs — without land, fees, financing, management and commission. The percentage items (fees, management, commission, contingency) are applied to it.",
  "termen.isc.nume": "ISC — the State Inspectorate for Construction",
  "termen.isc.ce": "The authority that controls quality in construction. The investor pays it two levies on the value of the works: quality (Law 10/1995) and state control (Law 50/1991).",
  "termen.tva.nume": "VAT — value added tax",
  "termen.tva.ce": "All amounts here exclude VAT, the way contractors' estimates are compared.",
  "termen.xps.nume": "XPS — extruded polystyrene",
  "termen.xps.ce": "Rigid insulation with low water absorption: at the plinth, below ground and on terraces, where ordinary polystyrene (EPS) does not last.",
  "termen.psi.nume": "PSI — fire prevention and fighting",
  "termen.psi.ce": "The fire safety services: detection and alarm, hydrants, sprinklers, smoke extraction.",
  "termen.rf.nume": "RF — fire resistant",
  "termen.rf.ce": "A door or wall with certified fire resistance (for example EI 60), required at fire compartment boundaries; «without RF» is an ordinary door.",
  "termen.pu.nume": "P.U. — unit price",
  "termen.pu.ce": "The price of one unit of the item (a m³, a m², a kg, a piece), split into material and labour.",
  "termen.um.nume": "U.M. — unit of measure",
  "termen.um.ce": "What the item is counted in: m³, m², m, kg, pieces, man-hours.",
  "termen.lag.nume": "lag — the gap between trades",
  "termen.lag.ce": "How many working days after the previous trade a trade starts, level by level. Kept to the end, it gives each trade one continuous contracting period, ending at lag + margin after the one before.",
  "termen.zl.nume": "zl — working days",
  "termen.zl.ce": "Monday to Friday, without public holidays, 8 hours each.",
  "termen.luni_macara.nume": "crane-months",
  "termen.luni_macara.ce": "One tower crane rented for one month. Their sum over the whole programme gives the cost of lifting equipment.",
  "termen.ore_om.nume": "man-hours",
  "termen.ore_om.ce": "The working hours of a single person: 10 man-hours are one person for 10 hours or two for 5 each. The recipes give them per unit of the item.",
  "termen.front.nume": "structure front",
  "termen.front.ce": "A building whose structure is being cast now, with its cranes. How many fronts run at once is decided by the site's crane cap; the next building starts when cranes are freed.",
  "termen.plafon.nume": "crane cap",
  "termen.plafon.ce": "The largest number of tower cranes that can work on the site at the same time (4 by default). It can be raised for a period, to start more buildings in parallel.",
  "termen.echilibru.nume": "break-even price",
  "termen.echilibru.ce": "The average apartment sale price at which gross profit reaches zero: below it the project loses money.",
  "termen.marja.nume": "gross margin",
  "termen.marja.ce": "Gross profit divided by revenue, as a percentage.",
  "termen.lant_critic.nume": "critical chain",
  "termen.lant_critic.ce": "The sequence of stages that sets the total duration: the structure, level by level, and the trades that follow it at their lag. A day lost on it delays the end by a day; stages outside it have float.",
  "bc.det.sursa_cifrei": "source of the figure on this row",
  "bc.det.de_unde": "Where from",
  "bc.det.cum_iese": "How it works out",
  "bc.det.se_schimba_din": "Changed from:",
  "bc.det.pe_articole": "itemised",
  "bc.det.in_lucru": "processing",
  "bc.estimat": "Estimated",
  "bc.ui.note": "Notes",
  "bc.det.titlu_terasamente": "Execution estimate · earthworks",
  "bc.det.titlu_structura": "Execution estimate · structure",
  "bc.det.din_deviz": "prices: {versiune}",
  "bc.det.fara_articole": "The detailed design's estimate has no items in this chapter.",
  "bc.det.tronsoane": "Buildings in the estimate",
  "bc.det.toate_tronsoanele#n": "The whole building|All {n} buildings|All {n} buildings",
  "bc.det.gropi_tot": "Where the soil goes",
  "bc.det.gropi_tot_text": "{tot} m³ leave the site: {groapa} m³ from the shared basement pit outside the buildings, worked out in the summary, and {fundatii} m³ from the buildings' estimate. The authorised tip is chosen once, for the whole volume; after «Regenerate», its price per m³ also feeds the excavation row.",
  "bc.det.corp_complex": "whole complex",
  "bc.det.sapatura_corp_in_complex": "The excavation is not this building's: the pit is dug once, over the whole basement of the complex{volum}, and sits in the solution's estimate.",
  "bc.det.sapatura_corp_volum": " ({n} m³)",
  "bc.det.sapatura_corp_link": "Whole-complex estimate →",
  "bc.det.sapatura_complex_text": "The pit of the whole basement: {arie} m² × {h} m, {volum} m³ with the deepenings under the {n} rafts and the buildings' trenches; paid once, as the «whole complex».{corpuri}",
  "bc.det.sapatura_complex_corpuri": " The sum of the buildings' own pits, each dug on its own section, would be {n} m³ — without the parking between them.",
  "bc.sect.ipoteze": "Assumptions",
  "bc.sect.cantitati": "Derived quantities",
  "bc.sect.costuri": "1 · Development costs",
  "bc.sect.vanzari": "2 · Sales at {pret} net",
  "bc.sect.rezultate": "3 · Results and return",
  "bc.sect.flux": "4 · Money over time",
  "bc.r.capitalMax.element": "Peak capital employed",
  "bc.r.capitalMax.obs": "the most money out of pocket before the proceeds come in",
  "bc.r.capitalMax.de_unde": "Monthly cash flow: costs are settled in the month they are incurred — land and fees at the start, construction and contingency along the construction programme, management half at the start and half monthly, financing monthly, commission, cadastre and profit tax at the sale — and the proceeds come at handover.",
  "bc.r.capitalMax.de_unde_ipoteza": "Without a construction programme all the money goes in at the start and comes back at the end of the assumed duration: the capital employed is the total cost.",
  "bc.r.capitalMax.cum": "the largest negative balance of the cumulative flow, over {luni} months",
  "bc.r.capitalAni.element": "Capital-years employed",
  "bc.r.capitalAni.obs": "each outlay × the years until the proceeds",
  "bc.r.capitalAni.de_unde": "The sum of the monthly outlays multiplied by the years left until the proceeds: money spent in the last year is tied up for one year, money spent at the start for the whole duration.",
  "bc.r.capitalAni.cum": "Σ month's outlay × years until the proceeds",
  "bc.r.rir.element": "Internal rate of return (IRR)",
  "bc.r.rir.obs": "the annual rate at which money in and money out balance",
  "bc.r.rir.fara": "the flow has no change of sign: it cannot be computed",
  "bc.r.rir.de_unde": "The annual rate at which the present value of the monthly flow is zero, with the proceeds at handover.",
  "bc.r.rir.cum": "the monthly rate that balances the {luni}-month flow, annualised",
  "bc.r.roiAn.obs_flux": "net profit / capital-years",
  "bc.r.roiAn.de_unde_flux": "The annual ROI on capital employed: net profit divided by capital-years — each monthly outlay × the years it stays tied up until the proceeds —, not by total cost × duration. Costs are settled monthly, along the construction programme.",
  "bc.um.eur_ani": "€·years",
  "bc.ui.capital_max": "Capital employed",
  "bc.ui.capital_max_sub": "IRR {p} per year",
  "bc.ui.capital_max_fara": "IRR undefined",
  "termen.rir.nume": "IRR — internal rate of return",
  "termen.rir.ce": "The annual rate at which the money put in and the money taken out balance, month by month: the interest a bank would have to pay to match the project.",
  "termen.capital_max.nume": "peak capital employed",
  "termen.capital_max.ce": "The most money the developer has out of pocket at any one time before the proceeds: the lowest point of the cumulative monthly cash flow.",
  "termen.capital_ani.nume": "capital-years",
  "termen.capital_ani.ce": "The sum of the monthly outlays multiplied by the years left until the proceeds. Net profit divided by it gives the annual ROI on capital employed: money from the last year stays one year, money from the start the whole duration.",
  "bc.grup.teren": "Land",
  "bc.grup.sapatura_structura": "Excavation and structure",
  "bc.grup.arhitectura_instalatii": "Architecture, envelope and building services",
  "bc.grup.subsol": "Basement and parking",
  "bc.grup.amenajari": "Landscaping and utilities",
  "bc.grup.dezvoltare": "Fees, financing and allowances",
  "bc.um.loc": "space",
  "bc.um.buc": "pcs",
  "bc.um.ani": "years",
  "bc.um.m2_util": "m² net",
  "bc.um.loc_ap": "spaces/apt",
  "bc.um.pe_m2_util": "{moneda}/m² net",
  "bc.obs.formula": "formula",
  "bc.obs.benchmark": "comparable-project benchmark",
  "bc.obs.benchmark_scurt": "benchmark",
  "bc.obs.ipoteza": "assumption",
  "bc.obs.reper": "benchmark",
  "bc.obs.reper_subsol": "benchmark per m² of basement",
  "bc.obs.boq": "from the bill of quantities",
  "bc.obs.boq_estimat": "from the bill of quantities · {p} % of the value on estimated quantities",
  "bc.obs.boq_estimat_tot": "quantities estimated per m² in the bill of quantities — nothing drawn yet",
  "bc.de_unde.boq": "The sum of the row's {n} items in the bill of quantities (the «Bill of quantities» tab), direct cost — material, labour, equipment, transport — plus the contractor's mark-ups. Items measured on the solution and items estimated from rooms, areas, storeys and basements are marked there, each with its explanation.",
  "bc.unde.boq": "The «Bill of quantities» tab: the project's recipes and resources, quantities set by hand",
  "bc.unde.program": "the «Programme» tab: cranes per period, headcounts, lags",
  "bc.obs.deviz": "execution estimate",
  "bc.obs.cota_vigoare": "current rate",
  "bc.obs.cota_cc": "share of CC",
  "bc.obs.numarate_arhitectura": "counted on the architecture solution",
  "bc.de_unde.raport_comparabil": "Ratio from a comparable project.",
  "bc.de_unde.pret_mana": "Sale price entered by hand.",
  "bc.de_unde.canonic": "Counted in the canonical document of the architecture solution.",
  "bc.de_unde.cota_cc": "A share of the construction cost.",
  "bc.de_unde.reper_scd": "Benchmark per m² of GFA for a mid-range apartment block, Bucharest 2026 — contractor price, excl. VAT. It stays a benchmark until the discipline has its own design.",
  "bc.de_unde.reper_subsol": "Benchmark per m² of basement for the underground car park of an apartment block, Bucharest 2026 — contractor price, excl. VAT.",
  "bc.par.fix": "fixed",
  "bc.par.din_cc": "{p} % of CC",
  "bc.par.pe_scd": "{pret} GFA",
  "bc.cum.cu_spor": "direct cost {direct} × {spor} = {total} · mark-ups: {sporuri}",
  "bc.cum.deviz_si_lista": "{deviz} from the trade estimate + {lista} from bill items the estimate doesn't have: {care}",
  "bc.cum.apartamente": "{su} / {mp} = {n} apartments",
  "bc.unde.carte_funciara": "Project documents → Land register",
  "bc.unde.regim_urbanistic": "Location → Zoning",
  "bc.unde.plan_situatie": "Solutions → Site plan",
  "bc.unde.arhitectura": "Solutions → Architecture",
  "bc.unde.retete": "The structure's detailed design, and the «Prices, region & norms» tab on this page",
  "bc.unde.fatada": "The architecture's «Façade» panel: the style and the composition change the take-off",
  "bc.unde.gropi": "The tip map in the estimate below, and the «Prices, region & norms» tab",
  "bc.r.teren.element": "Site area",
  "bc.r.teren.obs_acte": "from the land deeds",
  "bc.r.teren.obs_ipoteza": "illustrative area",
  "bc.r.teren.de_unde_acte": "The area in the land deeds.",
  "bc.r.teren.de_unde_ipoteza": "Illustrative value — the project has no site area yet.",
  "bc.r.cut.element": "Floor area ratio (CUT)",
  "bc.r.cut.obs": "per the applicable zoning plan (PUZ / RLU)",
  "bc.r.cut.obs_solutie": "the solution's ratio · zoning: max. {max}",
  "bc.r.cut.de_unde": "The floor area ratio from the zoning regulations.",
  "bc.r.cut.de_unde_solutie": "The ratio used by the thread's site plan: the summary follows the drawn solution, not the permitted maximum.",
  "bc.r.cut.semnal_rezerva": "Zoning allows {max}; the solution uses {cut} — {mp} m² of GFA left unused.",
  "bc.r.scd.element": "Above-ground GFA (SCD)",
  "bc.r.scd.de_unde": "Site area times the floor area ratio.",
  "bc.r.cedat.element": "Share of GFA ceded to the land seller",
  "bc.r.cedat.obs": "land paid in turnkey apartments",
  "bc.r.cedat.de_unde": "Negotiated term: the land is paid in apartments, not in cash.",
  "bc.r.cedat.cum": "{p} % of GFA",
  "bc.r.suPeGba.element": "Apartment net area / GFA",
  "bc.r.suPeGba.de_unde": "Ratio taken from a comparable project.",
  "bc.r.suPeGba.cum": "{raport} → {su} m² net out of {scd} m² GFA",
  "bc.r.suPeGba.semnal": "Not measured on this project's solution.",
  "bc.r.terasePeGba.element": "Terraces / GFA",
  "bc.r.terasePeGba.cum": "{raport} → {mp} m² of terraces",
  "bc.r.comPeGba.element": "Retail / GFA",
  "bc.r.comPeGba.cum": "{raport} → {mp} m² of retail",
  "bc.r.locuri.element": "Underground parking spaces",
  "bc.r.locuri.obs_estimat": "estimated",
  "bc.r.locuri.de_unde_estimat": "Estimate — the project has no solution with parking yet.",
  "bc.r.locuri.cum": "{locuri} × {mp} = {subsol} m² of basement",
  "bc.r.mpLoc.element": "Basement area per parking space",
  "bc.r.mpLoc.obs": "benchmark (incl. driveways, plant rooms)",
  "bc.r.mpLoc.de_unde": "Benchmark that includes driveways and plant rooms.",
  "bc.r.apMediu.element": "Average apartment",
  "bc.r.apMediu.de_unde": "Average net area from a comparable project.",
  "bc.r.verde.element": "Green areas",
  "bc.r.verde.obs": "zoning: min. {p} % of the plot",
  "bc.r.verde.obs_proiect": "drawn on the site plan · zoning: min. {minim} m²",
  "bc.r.verde.de_unde": "The minimum the regulations require.",
  "bc.r.verde.de_unde_proiect": "The green areas drawn on the thread's site plan.",
  "bc.r.verde.semnal_sub_minim": "{desenat} m² drawn, below the zoning minimum of {minim} m².",
  "bc.r.circ.element": "Surface roads and paths",
  "bc.r.circ.obs_proiect": "roadway and footpaths drawn on the site plan",
  "bc.r.circ.de_unde_proiect": "The roads and footpaths drawn on the thread's site plan, measured on its document (overlaps counted once).",
  "bc.r.pTerase.element": "Terrace price",
  "bc.r.pCom.element": "Retail price",
  "bc.r.pParc.element": "Underground parking price",
  "bc.r.durata.element": "Project duration",
  "bc.r.durata.obs": "from the preliminary contract to the handover of the last phase",
  "bc.r.durata.de_unde": "The assumed development duration.",
  "bc.r.durata.cum": "{n} years — divides the ROI into the annual return",
  "bc.r.impozit.element": "Corporate income tax rate",
  "bc.r.impozit.de_unde": "Corporate income tax, Fiscal Code art. 17: 16 %, unchanged in 2026 (checked on 28.10.2026). Dividends are taxed separately, at the shareholder.",
  "bc.r.gbaRez.element": "Above-ground GFA — residential",
  "bc.r.gbaRez.de_unde": "Total GFA minus the retail share.",
  "bc.r.gbaCom.element": "Above-ground GFA — retail",
  "bc.r.gbaCom.de_unde": "The retail share of GFA.",
  "bc.r.su.element": "Apartment net area (sellable)",
  "bc.r.su.de_unde": "GFA times the net/GFA ratio.",
  "bc.r.terase.element": "Terraces / balconies",
  "bc.r.terase.de_unde": "GFA times the terraces/GFA ratio.",
  "bc.r.ap.element": "Number of apartments",
  "bc.r.ap.element_desenate": "Number of apartments (drawn)",
  "bc.r.ap.obs_formula": "net area / average apartment",
  "bc.r.ap.de_unde_formula": "Net area divided by the average apartment.",
  "bc.r.ap.cum_proiect": "{n} apartments · {mp} m² average net",
  "bc.r.ap.semnal": "The benchmark would give {ref} apartments on the same net area — a difference of {dif}. Revenue is worked out from net area, not from the count, so it does not change.",
  "bc.r.subsol.element": "Underground parking (basement GFA)",
  "bc.r.subsol.de_unde": "Spaces times area per space.",
  "bc.r.groapa.element": "Basement pit footprint",
  "bc.r.groapa.obs": "from the site plan",
  "bc.r.groapa.de_unde": "The excavation area drawn on the thread's site plan.",
  "bc.r.nivSub.element": "Basement levels",
  "bc.r.nivSub.de_unde": "The basement area the parking needs, divided by the pit footprint.",
  "bc.r.hGroapa.element": "Pit depth",
  "bc.r.hGroapa.de_unde": "Basement levels × storey height, plus what lies under the lowest floor: the raft, the blinding and the gravel bed.",
  "bc.r.locPerAp.element": "Parking spaces per apartment",
  "bc.r.locPerAp.obs": "norm check",
  "bc.r.locPerAp.de_unde": "Spaces divided by apartments — to compare with the zoning norm.",
  "bc.r.cTerenNatura.element": "Land — paid in kind",
  "bc.r.cTerenNatura.par": "€0 cash",
  "bc.r.cTerenNatura.obs": "see Revenue",
  "bc.r.cTerenNatura.de_unde": "No cash leaves the project: the seller receives apartments.",
  "bc.r.cTeren.element": "Land-related costs",
  "bc.r.cTeren.obs": "notary, fees, zoning certificate",
  "bc.r.cTeren.de_unde": "Notary, fees, plot merger, zoning certificate.",
  "bc.r.sapatura.element": "Bulk excavation and earthworks",
  "bc.r.sapatura.obs_groapa": "shared basement pit + buildings' estimate",
  "bc.r.sapatura.obs_groapa_plan": "basement pit from the site plan",
  "bc.r.sapatura.de_unde_deviz": "The earthworks items of the execution estimate: volumes measured on the computed pit and foundations, with haulage to the authorised tip chosen on the map, plus the contractor's mark-ups.",
  "bc.r.sapatura.de_unde_izolate": "The buildings are computed in isolation, so the structural estimate holds only the excavation under their footprints: the foundations and, where there is one, the pit of their basement section. The pit of the whole shared basement is worked out here — the site-plan footprint × the depth, minus what the estimate already has — at the estimate's excavation price per m³ (dig, load, haul to the authorised tip), with the same mark-ups.",
  "bc.r.sapatura.de_unde_groapa": "The site-plan pit footprint × the depth, at a benchmark price per m³: dig, load and haul to the authorised tip. The itemised estimate appears after the detailed design.",
  "bc.r.sapatura.de_unde_ref": "Benchmark per m² of basement: excavation, loading and haulage. The itemised estimate appears after the detailed design.",
  "bc.r.sapatura.cum_minus": "− {deviz} m³ already in the buildings' estimate = {rest} m³",
  "bc.r.sapatura.cum_fundatii": "{groapa} + {fundatii} buildings' estimate = {total}",
  "bc.r.sapatura.obs_complex": "execution estimate · the whole complex's pit",
  "bc.r.sapatura.de_unde_complex": "The earthworks articles of the execution estimate, for the WHOLE complex: the pit over the whole basement (the outline from the architecture, down below the parking slab and its ballast), the deepenings under the buildings' rafts and their trenches, with haulage to the tip chosen on the map, plus the contractor's mark-ups. The buildings no longer each pay the pit under their own footprint.",
  "bc.r.sapatura.semnal_fara_plan": "The site plan gives no pit footprint: the basement is taken on a single level, so the pit may come out shallower and wider than it is.",
  "bc.r.sprijiniri.element": "Excavation support and dewatering",
  "bc.r.sprijiniri.obs_groapa": "anchored wall along the pit perimeter",
  "bc.r.sprijiniri.de_unde_groapa": "The pit is deeper than an unsupported bank can stand, so it is shored. The perimeter is estimated from the footprint (≈ {factor} × √area), the wall goes {infigere} m below the pit bottom, at a benchmark price per m² of wall; dewatering is in the same price. The structural estimate does not include them.",
  "bc.r.sprijiniri.de_unde_ref": "Benchmark per m² of basement for shoring and dewatering. The structural estimate does not include them.",
  "bc.r.structura.element": "Structural frame",
  "bc.r.structura.element_tronsoane": "Structural frame — the buildings",
  "bc.r.structura.obs_ref": "benchmark · the estimate comes with the detailed design",
  "bc.r.structura.obs_corpuri#n": "execution estimate · 1 building|execution estimate · {n} buildings|execution estimate · {n} buildings",
  "bc.r.structura.obs_in_lucru": "the structure is being calculated · benchmark until then",
  "bc.r.structura.de_unde_deviz": "The execution estimate of the detailed design: quantities measured on the computed geometry and reinforcement — concrete, rebar bar by bar, formwork — times the recipe prices, plus the contractor's mark-ups. Contingencies and VAT are left out: the summary has its own contingency and is excl. VAT.",
  "bc.r.structura.de_unde_ref": "Benchmark per m²: {supra} of above-ground GFA and {sub} of basement. The itemised estimate appears after the detailed design.",
  "bc.r.structura.semnal_banda": "Comes to {v} of floor area, outside the usual {min}–{max} range — check the items.",
  "bc.r.structuraSubsol.element": "Structure — basement between buildings",
  "bc.r.structuraSubsol.obs": "not computed yet · benchmark",
  "bc.r.structuraSubsol.de_unde": "The buildings are computed in isolation, each with the basement section under its footprint; the shared basement between them is not computed yet. Its area is the whole basement minus what lies under the buildings, at the benchmark price per m² of basement — not at the buildings' price: the section under a building carries the whole building and is walled on every side, while the basement between them only carries the soil above.",
  "bc.r.structuraSubsol.cum_acoperit": "under the buildings: {aria} × {sub}/{tot} = {acoperit} m²",
  "bc.r.structuraSubsol.cum_rata": "the buildings' substructure comes to {rata}, rafts included",
  "bc.r.structuraSubsol.semnal_fara_arie": "It is not known how much of the basement lies under the buildings (the site plan gives no storey count): the whole basement was taken, which may count the computed sections twice.",
  "bc.r.arhitectura.element": "Architecture — partitions and finishes",
  "bc.r.arhitectura.obs": "masonry, screeds, plaster, flooring, doors",
  "bc.r.anvelopa.element": "Envelope — façades, windows, roof",
  "bc.r.anvelopa.obs": "ETICS / ventilated façade, windows, roof terraces",
  "bc.r.anvelopa.obs_deviz": "façade estimate",
  "bc.r.anvelopa.obs_se_calculeaza": "being calculated from the façade",
  "bc.r.anvelopa.obs_dupa_fatada": "after the architecture's façade",
  "bc.r.anvelopa.de_unde_asteapta": "This row comes only from the façade estimate of the architecture on the thread, itemised. Until then it has no figure — not even the per-m² benchmark.",
  "bc.r.anvelopa.semnal_in_afara_totalului": "The envelope is not in the total yet: the total and the profit change when the façade estimate arrives.",
  "bc.r.anvelopa.obs_corpuri#n": "façade estimate · 1 building|façade estimate · {n} buildings|façade estimate · {n} buildings",
  "bc.r.anvelopa.de_unde_deviz": "The façade estimate of the architecture on the thread: each building's take-off (the thermal-system layers, the ventilated façade, the cladding, sills, railings, windows and doors, terraces) × the façade rates — a market estimate, to be reviewed. The quantity is the insulated area of the walls, parapets, cheeks and plinth.",
  "bc.r.anvelopa.semnal_nepretuite#n": "One take-off row has no rate and is left out of the sum|{n} take-off rows have no rate and are left out of the sum|{n} take-off rows have no rate and are left out of the sum",
  "bc.fat.titlu": "Façade estimate",
  "bc.fat.sursa": "prices: {versiune}",
  "bc.fat.nepretuite#n": "No rate, left out of the sum: {lista}|No rate, left out of the sum ({n}): {lista}|No rate, left out of the sum ({n}): {lista}",
  "bc.fat.corpuri": "Buildings",
  "bc.fat.toate_corpurile#n": "The whole building|All {n} buildings|All {n} buildings",
  "bc.fat.pret_din": "Price: {sursa}",
  "bc.fat.informative": "Quantities for information",
  "bc.fat.informative_text": "Shown, not paid separately: the tiles and joints of a cladding priced per m², the number of window and door units.",
  "bc.fat.dibluri_pe_placa": "{n} per 0.5 m² board",
  "bc.fat.camp.cantitate": "Quantity",
  "bc.fat.camp.pu_material_eur": "Material unit price, €",
  "bc.fat.camp.pu_manopera_eur": "Labour unit price, €",
  "bc.fat.calculat": "changed · calculated: {v}",
  "bc.fat.dublu_clic": "Double-click to change",
  "bc.fat.revino": "Back to the calculated value",
  "bc.fat.nu_s_a_salvat": "Not saved",
  "bc.fat.cum_corectezi": "Double-click a quantity or a unit price to change it; ↺ goes back to the calculated value. Prices change for the whole project, quantities only for this solution.",
  "bc.fat.modificate#n": "one row changed|{n} rows changed|{n} rows changed",
  "bc.fat.art.adeziv_lipire": "bonding adhesive",
  "bc.fat.art.dibluri_eps": "insulation anchors",
  "bc.fat.art.masa_spaclu": "base coat (reinforcement layer)",
  "bc.fat.art.plasa_fibra": "glass-fibre mesh",
  "bc.fat.art.dibluri_vata": "stone wool anchors",
  "bc.fat.art.membrana_vant": "wind barrier membrane",
  "bc.fat.art.amorsa": "primer",
  "bc.fat.art.tencuiala_siliconica": "silicone render",
  "bc.fat.art.suruburi_substructura": "self-drilling screws (rail to bracket)",
  "bc.fat.art.prinderi_placaj": "stainless rivets / clips",
  "bc.fat.art.ancore_piatra": "pin anchors for stone",
  "bc.fat.cap.termosistem": "Thermal insulation system",
  "bc.fat.cap.ventilata": "Ventilated façade",
  "bc.fat.cap.finisaje": "Finishes and cladding",
  "bc.fat.cap.glafuri_ornamente": "Sills, bands, ornaments",
  "bc.fat.cap.balustrade": "Railings",
  "bc.fat.cap.tamplarie": "Windows and doors",
  "bc.fat.cap.terase": "Terraces, balconies, loggias",
  "bc.fat.cap.cortina": "Curtain wall",
  "bc.fat.cap.intrare": "Building entrance",
  "bc.fat.cap.electrice": "Electrical installations",
  "bc.fat.el.perete": "wall",
  "bc.fat.el.atic": "parapet",
  "bc.fat.el.obraz": "cheek",
  "bc.fat.el.soclu": "plinth",
  "bc.fat.el.intrados": "soffit",
  "bc.fat.el.spalet": "reveal",
  "bc.fat.el.despartitor": "divider",
  "bc.fat.el.balcon": "balcony",
  "bc.fat.el.terasa": "terrace",
  "bc.fat.el.camp_vitrat": "glazed field",
  "bc.fat.el.glaf": "sill",
  "bc.fat.el.glaf_atic": "parapet coping",
  "bc.fat.el.brau": "band",
  "bc.fat.el.muchie_placa": "slab edge",
  "bc.fat.el.ornament": "decorative element",
  "bc.fat.el.balustrada": "railing",
  "bc.fat.el.balustrada_terasa": "terrace railing",
  "bc.fat.el.garda": "guard",
  "bc.fat.el.ecran": "screen",
  "bc.fat.el.mana_curenta": "handrail",
  "bc.fat.el.profil_sticla": "glass base profile",
  "bc.fat.el.fereastra": "window",
  "bc.fat.el.usa": "door",
  "bc.fat.el.usa_intrare_bloc": "building entrance door",
  "bc.fat.el.zona_opaca": "spandrel (fire zone)",
  "bc.fat.el.fereastra_cortina": "curtain wall vent",
  "bc.fat.el.consola": "fixing brackets",
  "bc.fat.el.bariera_foc": "slab-edge fire barrier",
  "bc.fat.el.montant": "mullions",
  "bc.fat.el.prag": "threshold",
  "bc.fat.el.substructura": "subframe",
  "bc.fat.el.copertina": "canopy",
  "bc.fat.el.pardoseala_intrare": "entrance floor",
  "bc.fat.el.iluminat_intrare": "entrance lighting",
  "bc.fat.vitraj.single": "single glazing",
  "bc.fat.vitraj.double": "double glazing",
  "bc.fat.vitraj.triple": "triple glazing",
  "bc.fat.deschidere.fixed": "fixed",
  "bc.fat.deschidere.casement": "casement",
  "bc.fat.deschidere.tilt_turn": "tilt and turn",
  "bc.fat.deschidere.sliding": "sliding",
  "bc.fat.art.etics": "EPS insulation boards",
  "bc.fat.art.etics_intrados": "EPS boards on soffits",
  "bc.fat.art.xps_soclu": "XPS boards on the plinth",
  "bc.fat.art.tencuiala_baza": "reinforced base coat",
  "bc.fat.art.vata_minerala_fatada": "stone wool boards",
  "bc.fat.art.substructura_aluminiu": "aluminium subframe",
  "bc.fat.art.substructura": "subframe",
  "bc.fat.art.tencuiala_decorativa": "decorative render",
  "bc.fat.art.placaj_ceramic": "ceramic cladding",
  "bc.fat.art.placaj_metalic": "metal cladding",
  "bc.fat.art.placaj_hpl": "HPL cladding",
  "bc.fat.art.placaj_piatra": "stone cladding",
  "bc.fat.art.piatra_travertin": "travertine",
  "bc.fat.art.piatra_granit": "granite",
  "bc.fat.art.piatra_ardezie": "slate",
  "bc.fat.art.piatra_zidita": "stone veneer",
  "bc.fat.art.lemn_vertical": "vertical timber",
  "bc.fat.art.lemn_ingust": "narrow timber",
  "bc.fat.art.lemn_riflaj": "fluted timber",
  "bc.fat.art.lemn_panouri": "timber panels",
  "bc.fat.art.caramida_aparenta": "facing brick",
  "bc.fat.art.sticla": "glass",
  "bc.fat.art.lamele_verticale": "vertical fins",
  "bc.fat.art.tabla_vopsita": "painted sheet metal",
  "bc.fat.art.tabla_perforata": "perforated sheet metal",
  "bc.fat.art.perete_verde": "green screen (trellis, no plants)",
  "bc.fat.art.ecran_fix": "fixed screen",
  "bc.fat.art.perete_cortina": "curtain wall (the opening is under windows)",
  "bc.fat.art.glaf_aluminiu": "aluminium sill",
  "bc.fat.art.glaf_atic": "parapet coping",
  "bc.fat.art.aluminiu": "aluminium profile",
  "bc.fat.art.profil_eps": "EPS profile",
  "bc.fat.art.cornisa_eps": "EPS cornice",
  "bc.fat.art.parapet_glass": "glass railing",
  "bc.fat.art.parapet_slats": "slatted railing",
  "bc.fat.art.parapet_solid": "solid parapet",
  "bc.fat.art.parapet_planter": "planter",
  "bc.fat.art.parapet_sticla": "glass railing",
  "bc.fat.art.parapet_lamele": "slatted railing",
  "bc.fat.art.mana_curenta_otel": "painted steel handrail",
  "bc.fat.art.mana_curenta_aluminiu": "aluminium handrail",
  "bc.fat.art.mana_curenta_inox": "stainless steel handrail",
  "bc.fat.art.mana_curenta_lemn": "timber handrail",
  "bc.fat.art.profil_sticla_aluminiu": "aluminium glass base profile",
  "bc.fat.art.profil_sticla_inox": "stainless glass base profile",
  "bc.fat.art.tamplarie_pvc": "PVC windows and doors",
  "bc.fat.art.tamplarie_aluminiu": "aluminium windows and doors",
  "bc.fat.art.tamplarie_lemn": "timber windows and doors",
  "bc.fat.art.tamplarie_lemn_aluminiu": "timber-aluminium windows and doors",
  "bc.fat.art.usa_intrare_bloc": "aluminium building entrance door with side panel and fanlight",
  "bc.fat.art.cortina_vitrata": "stick curtain wall, solar-control triple glazing",
  "bc.fat.art.cortina_opaca": "enamelled-glass spandrel with insulated back panel",
  "bc.fat.art.fereastra_cortina": "hidden tilt-and-turn vent in the curtain wall",
  "bc.fat.art.consola_cortina": "bracket with cast-in channel",
  "bc.fat.art.bariera_foc_cortina": "fire and smoke barrier, mineral wool and steel sheet",
  "bc.fat.art.montant_cortina": "aluminium mullions",
  "bc.fat.art.prag_aluminiu": "aluminium threshold",
  "bc.fat.art.sapa_panta": "sloped screed",
  "bc.fat.art.hidroizolatie_balcon": "liquid waterproofing",
  "bc.fat.art.hidroizolatie": "bituminous waterproofing",
  "bc.fat.art.termoizolatie_terasa": "terrace insulation (XPS)",
  "bc.fat.art.pietris": "gravel",
  "bc.fat.art.gresie_exterior": "outdoor tiles",
  "bc.fat.art.copertina_beton": "cantilevered concrete canopy, waterproofed, with drip edge",
  "bc.fat.art.copertina_metal": "steel canopy with laminated glass, on tie rods",
  "bc.fat.art.spot_exterior": "recessed outdoor LED spotlight (IP65)",
  "bc.fat.art.dale_portelanate": "porcelain pavers",
  "bc.fat.art.dale_terasa": "terrace pavers",
  "bc.fat.art.deck_compozit": "composite decking",
  "bc.fat.art.deck_lemn": "timber decking",
  "bc.fat.art.ploturi_reglabile": "adjustable pedestals",
  "bc.fat.art.ploturi_grinzisoare": "pedestals with joists",
  "bc.fat.art.ploturi": "pedestals",
  "bc.fat.art.grinzisoare": "joists",
  "bc.fat.art.montanti": "aluminium rails (T/L profile)",
  "bc.fat.art.console": "aluminium brackets with thermal pad",
  "bc.fat.art.ancore": "metal anchors",
  "bc.r.sanitare.element": "Plumbing",
  "bc.r.sanitare.obs": "cold and hot water, drainage, sanitary ware",
  "bc.r.termice.element": "Heating and ventilation",
  "bc.r.termice.obs": "boilers / heat pumps, distribution, ventilation",
  "bc.r.electrice.element": "Electrical and low-voltage systems",
  "bc.r.electrice.obs": "panels, sockets, lighting, intercom, data",
  "bc.r.psi.element": "Fire safety systems",
  "bc.r.psi.obs": "detection, hydrants, stair smoke venting",
  "bc.r.lifturi.element": "Lifts",
  "bc.r.lifturi.obs": "benchmark per m² of GFA",
  "bc.r.lifturi.obs_lista": "{n} drawn · {statii} stops · {platforme} entrance platforms",
  "bc.r.lifturi.de_unde_lista": "The lifts drawn in the architecture — the shafts in the cores, each counted once over its full height — with their stops (the shaft on each level, basements included) and the entrance platforms, from the bill of quantities: direct cost plus the contractor's mark-ups. The price per unit is that of one lift with its stops and platforms.",
  "bc.r.subsolHidro.element": "Basement — waterproofing",
  "bc.r.subsolHidro.obs": "membrane / watertight tank, protection",
  "bc.r.subsolInstalatii.element": "Basement — car park services",
  "bc.r.subsolInstalatii.obs": "ventilation and smoke extract, sprinklers, lighting, pumping",
  "bc.r.subsolFinisaje.element": "Basement — car park finishes",
  "bc.r.subsolFinisaje.obs": "floor finish, markings, painting, doors",
  "bc.r.cVerde.element": "Landscaping",
  "bc.r.cVerde.obs": "quantity from zoning",
  "bc.r.cVerde.obs_proiect": "quantity from the site plan",
  "bc.r.cVerde.de_unde": "The quantity is the regulatory minimum; the price, a benchmark.",
  "bc.r.cVerde.de_unde_proiect": "The quantity is the one drawn on the site plan; the price, a benchmark.",
  "bc.r.cCirc.obs_proiect": "quantity from the site plan",
  "bc.r.cCirc.de_unde": "Quantity and price from a comparable project.",
  "bc.r.cCirc.de_unde_proiect": "The quantity is the roadway and footpaths drawn on the site plan; the price, from a comparable project.",
  "bc.r.cInfra.element": "Public infrastructure + utilities",
  "bc.r.cInfra.obs": "connections, RLU art. 30–38",
  "bc.r.cInfra.de_unde": "Connections and works the regulations require — the reference sheet's amount (€2,200,000 for 37,500 m² GFA), scaled to the project's GFA.",
  "bc.r.cConexe.element": "Construction-related soft costs",
  "bc.r.cConexe.obs": "architects, consultants, surveys",
  "bc.r.cConexe.de_unde": "Design, consultancy, surveys — the reference sheet's amount (€450,000 for 37,500 m² GFA), scaled to the project's GFA.",
  "bc.r.cProgram.element": "Execution programme levers",
  "bc.r.cProgram.obs": "what shortening costs: pump, overtime, shifts, fast formwork, precast; or a tight yard",
  "bc.r.cProgram.de_unde": "The execution programme: every lever active in its settings (or a restriction hit) has its own cost formula; the sum goes into construction, hence into taxes, management, commission and contingency.",
  "bc.r.constructii.element": "Total construction",
  "bc.r.constructii.obs": "{scd} GFA · {construit} built",
  "bc.r.constructii.de_unde": "The sum of the disciplines, landscaping and utilities — before fees, financing and the developer's allowances.",
  "bc.r.taxe.element": "Statutory construction fees and levies",
  "bc.r.taxe.obs": "Romania, 2026 · on the value of the works",
  "bc.r.taxe.de_unde": "What the developer pays on the value of the works, by law: the building permit fee (Fiscal Code art. 474, residential building), the ISC quality levy (Law 10/1995 art. 40), the ISC control levy (Law 50/1991 art. 30), the Builders' Social Fund (Law 215/1997) and the architecture stamp (Law 35/1994). Checked on 28.10.2026. Site set-up is not here: the contractor carries it in its price.",
  "bc.r.finant.element": "Construction financing (interest)",
  "bc.r.finant.de_unde": "The cost of financing — the reference sheet's amount (€1,150,000 for 37,500 m² GFA), scaled to the project's GFA.",
  "bc.r.cadastru.element": "Cadastre, acceptance, registration",
  "bc.r.cadastru.de_unde": "The reference sheet's amount (€90,000 for 37,500 m² GFA), scaled to the project's GFA.",
  "bc.r.mgmt.element": "Development management",
  "bc.r.comis.element": "Sales and marketing commission",
  "bc.r.rezerva.element": "Contingency",
  "bc.r.rezerva.de_unde": "Contingency for the unforeseen.",
  "bc.r.tic.element": "Total investment cost (TIC)",
  "bc.r.tic.obs": "{v} built",
  "bc.r.tic.de_unde": "Land, construction and development costs.",
  "bc.r.vAp.element": "Apartments (net area)",
  "bc.r.vAp.obs": "developer's share",
  "bc.r.vAp.de_unde": "Net area × scenario price × developer's share.",
  "bc.r.vTer.obs": "same price in every scenario",
  "bc.r.vTer.de_unde": "The terrace price does not change between scenarios.",
  "bc.r.vCom.element": "Retail",
  "bc.r.vCom.obs": "85 % of GFA is sellable",
  "bc.r.vCom.de_unde": "85 % of the retail GFA is sellable; the price comes from the assumptions.",
  "bc.r.vPar.element": "Underground parking",
  "bc.r.vPar.obs": "price per space from the assumptions",
  "bc.r.vPar.de_unde": "Price per space from the assumptions.",
  "bc.r.venituri.element": "Total developer revenue",
  "bc.r.venituri.obs": "{p} % of the project",
  "bc.r.venituri.de_unde": "The sum of the four categories.",
  "bc.r.brut.element": "Gross profit from sales",
  "bc.r.brut.obs": "revenue − TIC",
  "bc.r.brut.de_unde": "Revenue minus the total investment cost.",
  "bc.r.net.element": "Net profit (after tax)",
  "bc.r.net.par": "tax {p} %",
  "bc.r.net.de_unde": "Gross profit minus corporate income tax.",
  "bc.r.marja.element": "Gross margin on revenue",
  "bc.r.marja.obs": "gross profit / revenue",
  "bc.r.marja.de_unde": "How much of the revenue is left before tax.",
  "bc.r.roi.element": "Return on investment (ROI)",
  "bc.r.roi.obs": "net profit / TIC",
  "bc.r.roi.de_unde": "Net profit relative to the total cost.",
  "bc.r.roiAn.element": "ROI per year",
  "bc.r.roiAn.obs": "over {n} years",
  "bc.r.roiAn.de_unde": "ROI divided by the project duration.",
  "bc.r.roiAn.cum": "ROI / {n} years",
  "bc.r.prag.element": "Break-even sale price",
  "bc.r.prag.obs": "gross profit = 0",
  "bc.r.prag.de_unde": "The price at which gross profit reaches zero, all else unchanged.",

  // --- Studio: the discipline canvas --------------------------------------
  "studio.se_incarca_modelul": "Loading the 3D model…",
  "studio.structura_v": "Structure v{v}",
  "studio.favorita": "favourite",
  "studio.fa_favorita": "make favourite",
  "studio.fa_favorita_titlu": "Make it the favourite — everything further is built on it",
  "studio.parcare_fara_rampe": "running: {locuri} spaces found, searching for ramps",
  "studio.pe_solutie": "on {solutie}",
  "studio.fara_solutie": "No {disciplina} solution.",
  "studio.fara_pe": "No {disciplina} solution on {solutie}.",
  "studio.fara_solutie_text": "Generate one from <b>Solutions</b> and it gets drawn here.",
  "studio.gen.site_plan": "Generating the site plan",
  "studio.gen.architecture": "Generating the architecture",
  "studio.gen.architecture_import": "Understanding the architecture",
  "studio.import_picat": "The architectural solution could not be imported",
  "studio.import_picat_text": "The import of the architectural drawings failed.",
  "studio.import_anulat": "The import of the architectural drawings was cancelled.",
  "studio.import_fara_puls": "The import of the architectural drawings stopped responding and was abandoned.",
  "studio.import_inapoi": "Back to the documents",
  "studio.gen.structure": "Generating the structure",

  // --- project settings ----------------------------------------------------
  "setari.titlu": "Project settings",
  "setari.general": "General",
  "setari.general_descriere": "The project name, the organisation, archiving",
  "setari.acces": "Access",
  "setari.acces_descriere": "Who sees the project and who can work on it",
  "setari.general_doar_citire": "You only have read access to this project — the name and archiving can be changed by someone with write access.",
  "setari.numele": "Project name",
  "setari.despre": "About the project",
  "setari.organizatie": "Organisation",
  "setari.creat": "Created",
  "setari.modificat": "Last changed",
  "setari.arhivare": "Archiving",
  "setari.arhivare_titlu": "Take the project off the list.",
  "setari.arhivare_text": "Nothing is deleted: the documents, the solutions and the checks stay in the database. The project leaves the list for everyone.",
  "setari.sterge_solutii": "Delete all solutions",
  "setari.sterge_solutii_titlu": "Delete all the project's solutions.",
  "setari.sterge_solutii_text": "Site plan, architecture, structure — with their runs and results. The dossier, the read documents and the preferences stay; the sketches start again on their own on new architectures.",
  "setari.confirma_sterge_solutii": "Delete ALL {n} solutions of the project ({situatie} site plans, {arhitectura} architectures, {schite} sketches, {solutii} structure solutions), with their runs? Running jobs are stopped. This cannot be undone.",
  "setari.sterse_solutii": "Deleted {solutii} solutions, {joburi} runs, {artefacte} artefacts.",
  "setari.esec_sterge_solutii": "Could not delete the solutions.",
  "setari.arhiveaza": "Archive",
  "setari.confirma_arhivare": "Archive “{proiect}”?\n\nIt leaves the project list. Nothing is deleted — the solutions, the documents and the checks stay.",
  "setari.esec_arhivare": "Could not archive the project.",

  // --- access to the project ----------------------------------------------
  "acces.rol_viewer": "View",
  "acces.rol_editor": "Edit",
  "acces.rol_admin": "Administer",
  "acces.esti_viewer": "you can see the project",
  "acces.esti_editor": "you can work on the project",
  "acces.esti_admin": "you administer the project",
  "acces.esec_citire": "Could not read the project's access list.",
  "acces.esec_schimbare": "Could not change the access.",
  "acces.incuiat_afara": "You cannot take away your own right to administer the project. Give someone else the “Administer” role first.",
  "acces.nu_e_membru": "They are not in the project's organisation. Invite them there first.",
  "acces.interzis": "You are not allowed to change access to this project.",
  "acces.rol_inexistent": "That role does not exist.",
  "acces.doar_vezi": "You can see who has access, but you cannot change it — that is for whoever administers the project, or an owner of the organisation.",
  "acces.organizatia": "The organisation",
  "acces.toti_din": "Everyone in {organizatie}",
  "acces.inclusiv_viitorii": "including whoever joins the team from now on",
  "acces.nu_e_pe_org": "the project is not shared with the organisation — only the people below see it",
  "acces.oameni": "People",
  "acces.tu": " — you",
  "acces.dezactivat": "disabled",
  "acces.al_organizatiei": "{rol} of the organisation",
  "acces.din_organizatie": "from the organisation",
  "acces.scoate": "Remove access",
  "acces.nimeni": "Nobody but you.",
  "acces.toti_au_acces": "everyone in the organisation already has access",
  "acces.alege_om": "pick someone from the organisation…",
  "acces.fara_acces": "No access",
  "acces.sub_organizatie": " — below the organisation's",
  "acces.confirma_scoatere_org": "Remove access for the whole of {organizatie}? Only the people given access separately will be left.",
  "acces.confirma_scoatere": "Remove {nume}'s access to the project?",
  // --- a structure solution's score, explained ---------------------------
  "scor.validari": "checks",
  "scor.cost": "cost",
  "scor.uniformitate": "uniformity",
  "scor.grila": "grid",
  "scor.parte": "{nume} {valoare} of {total}",
  "scor.conformare": "+{valoare} conformance ({detaliu})",
  "scor.globale_picate#n": "1 global check failing|{n} global checks failing|{n} global checks failing",
  "scor.tipuri_stalpi": "{clase} column types across {elemente} elements",
  "scor.tipuri_grinzi": "{clase} beam types across {elemente} elements",
  "scor.diametre": "{nr} rebar diameters",
  "scor.diametre_tinta": " (the target is {tinta})",
  "scor.travee_modul": "{peModul} of {nr} bays on the {modul} m module",
  "scor.deschideri": "{nr} distinct spans",
  "scor.deschideri_dominanta": " (most frequent {metri} m, {travee} bays)",
  "scor.titlu": "Structure score {valoare} — lower is better, on a fixed 0 to 100 scale.",
  "scor.titlu_fara_cifra": "Structure score — lower is better, on a fixed 0 to 100 scale.",
  "scor.ponderi": "Failing checks weigh 55 %, the cost of building 10 %, how repetitive the structure is 25 % (the fewer distinct column types, beam types and rebar diameters, the easier it is to build and to control), and the grid 10 %: 5 % how many bays sit on the parking module (a whole number of spaces between two columns) and 5 % how many distinct spans the frame has (equal spans = the same beam formwork, reused).",
  "scor.cost_nemasurat": "The cost could not be placed on the scale (the starting solution's cost is unknown), so its term sits in the middle.",
  "scor.curat": "“Clean” does not mean a score of 0, it means zero failing checks: even a structure that passes everything costs something and has more than one element type.",
  "scor.alege_platforma": "This score is what the platform uses to choose which solution to carry on from.",

  // --- the vocabulary of the disciplines ----------------------------------
  "disciplina.site_plan": "Site plan",
  "disciplina.architecture": "Architecture",
  "disciplina.structure": "Structure",

  // --- PUD export (`pages/Pud.tsx`) -----------------------------------------
  "pud.titlu": "PUD export — sheet U3",
  "pud.inapoi": "← the site plan",
  "pud.explica": "The «Urban regulations» sheet is composed from the site plan, from the dossier's documents and from the corrections below. Whatever is missing appears on the sheet as «to be completed»; it does not stop the export. The documentation is a DRAFT, unsigned.",
  "pud.genereaza": "Generate the sheet",
  "pud.se_genereaza": "Generating…",
  "pud.regenereaza": "Regenerate",
  "pud.esec_pornire": "Could not start the export.",
  "pud.rulare": "Run: {stare}",
  "pud.picat": "The export failed: {motiv}",
  "pud.fara_export": "No sheet generated on this plan yet.",
  "pud.descarca": "Download",
  "pud.descarca_pdf": "PDF",
  "pud.descarca_pdf_titlu": "Sheet U3 as PDF, to scale, on its sheet size",
  "pud.descarca_dxf": "DXF",
  "pud.descarca_dxf_titlu": "Sheet U3 as DXF: the drawing in metres, on the project's coordinates, on PUD_* layers",
  "pud.coala": "{coala} at 1:{scara}",
  "pud.fapte": "The sheet's information",
  "pud.fapte_explica": "Every value with its source. Type in the «correction» column to override the source; leave it empty to return to it. Corrections apply at the next generation.",
  "pud.col_fapt": "Information",
  "pud.col_valoare": "Value",
  "pud.col_sursa": "Source",
  "pud.col_corectie": "Correction",
  "pud.stare.cunoscut": "known",
  "pud.stare.aproximat": "approximate",
  "pud.stare.lipsa": "missing",
  "pud.lipsa#n": "one item to complete|{n} items to complete|{n} items to complete",
  "pud.salveaza_corectii": "Save the corrections",
  "pud.corectii_salvate": "The corrections were saved; generate the sheet again to see them.",
  "pud.esec_corectii": "Could not save the corrections.",
  "pud.previzualizare": "Preview",
  "pud.avert.acte": "The drawn plot ({contur} m²) does not match the area in the deeds ({acte} m²). The indicators and the balance are computed on the drawn plot; check the outline and the parcels in the certificate.",
  "pud.avert.balcoane": "Cantilevered balconies and terraces ({m2} m²) extend beyond the footprint and are not checked against the buildable limit; the rule for cantilevers is still to be agreed with the architects.",
  "pud.sursa.certificat_urbanism": "the urbanism certificate",
  "pud.sursa.carte_funciara": "the land register",
  "pud.sursa.topo": "the topographic survey",
  "pud.sursa.puz": "the applicable PUZ",
  "pud.sursa.document": "the site plan",
  "pud.sursa.arhitectura": "the architecture",
  "pud.sursa.avize": "the utility approvals",
  "pud.sursa.manual": "corrected by hand",
  "pud.sursa.osm": "OpenStreetMap",
  "pud.sursa.geocodat": "geocoding",
  "pud.sursa.calculat": "computed",

  // --- the state of a run, wherever it shows ------------------------------
  "job.in_lucru": "running",
  "job.in_coada": "queued",
  "job.pasul": "Step {i}/{n}",
  "job.asteapta": "waiting",
  "job.pasi_faza": "{i}/{n} · {faza}",
  // WHAT the run waits for, then WHY in brackets: the card's chip keeps only
  // the first part, so the brackets must hold only the explanation.
  "asteapta.arhitectura_rampe": "waiting for the architecture (the ramps chosen in the basement; then the agent draws the circulation and landscaping)",
  "asteapta.arhitectura_noua": "waiting for the new architecture (the chosen ramps; then the agent draws the circulation and landscaping)",
  "asteapta.subsol_refacut": "waiting for the rebuilt basement (the chosen ramps; then the agent draws the circulation and landscaping)",
  "asteapta.schita_structura": "waiting for the structure sketch (the columns the basement is laid out between)",
  // A job's stage (`stage_cod`), under the progress bar. The services write
  // the Romanian text in `stage`; `etape.test.ts` keeps ro.ts identical to it.
  // site plan
  "etapa.caut_asezari": "searching for placements",
  "etapa.pregatesc_planul": "preparing the plan for the agent: the excavation and the architecture ramps",
  "etapa.gandesc_planul": "thinking the plan through (round {tur})",
  "etapa.scriu_planul": "writing the plan onto the solution",
  // architecture
  "etapa.citesc_planul_de_situatie": "reading the site plan",
  "etapa.compartimentez_etajele": "laying out the floors",
  "etapa.adaptez_excavatia": "fitting the excavation to the parking lanes",
  "etapa.citesc_arhitectura": "reading the architecture",
  "etapa.scriu_solutia": "writing the solution",
  "etapa.trag_traseele": "routing the services",
  "etapa.citesc_stalpii": "reading the columns from the structure",
  "etapa.refac_subsolurile": "rebuilding the basements",
  "etapa.refac_subsolul": "rebuilding {subsol}",
  "etapa.caut_rampele": "searching for the ramps",
  "etapa.grefez_subsolul": "grafting the basement onto the architecture",
  "etapa.compartimentez_atinse#n": "laying out 1 affected apartment · {faza}|laying out {n} affected apartments · {faza}|laying out {n} affected apartments · {faza}",
  "etapa.scriu_apartamentele": "writing the apartments into the solution · {faza}",
  // structure
  "etapa.citesc_studiul_geotehnic": "reading the geotechnical study",
  "etapa.astept_studiul_geotehnic": "waiting for the geotechnical study to be read",
  "etapa.hidratez_proiectul": "loading the project data",
  "etapa.lantul_ruleaza": "the chain is running",
  "etapa.reia_dupa_repornire": "resuming the chain after a restart",
  "etapa.reia_dupa_oom": "resuming from the failed step, after running out of memory",
  "etapa.se_opreste": "stopping ({motiv})",
  // facade
  "etapa.citesc_documentul": "reading the solution's document",
  "etapa.aduc_contextul": "fetching the neighbours and the surrounding city",
  "etapa.citesc_profilul_strazilor": "reading the street profiles from the orthophoto (takes a few minutes)",
  "etapa.compun_plansa": "composing the sheet",
  "etapa.scriu_fisierele": "writing the files",
  "etapa.citesc_referintele": "reading the references",
  "etapa.gandesc_stilul": "working out the style (round {tur})",
  "etapa.imbrac_solutia": "dressing the solution in the style",
  "etapa.compun_fatada": "composing the facade architecture",
  "etapa.designerul_priveste": "a designer is comparing the images with the references",
  "etapa.privesc_inainte": "rendering the facade as it was",
  "etapa.designerul_verifica": "a designer is comparing before and after with the request",
  "etapa.randez_imagini#n": "rendering 1 image (series {serie})|rendering {n} images (series {serie})|rendering {n} images (series {serie})",
  // the dossier and the architecture import
  "etapa.citesc_actele": "reading the documents",
  "etapa.recitesc_schita": "re-reading the plot sketch from {fisier}",
  "etapa.caut_campuri": "looking for {campuri} in {fisier}",
  "etapa.ma_uit_in": "looking into {fisier} ({i}/{n})",
  "etapa.caut_conturul_lotului": "looking for the plot outline",
  "etapa.asez_lotul_dupa_plansa": "placing the plot after the cadastral sheet in the certificate",
  "etapa.caut_frontul_stradal": "looking for the street front on the map",
  "etapa.citesc_limita_zonelor": "reading the zone boundary from the zoning drawing",
  "etapa.citesc_planse_arhitectura": "reading the architecture drawings",
  "etapa.astept_randul_importului": "waiting in line: another project is importing its drawings now",
  "etapa.refolosesc_citirea": "reusing the previous reading",
  "etapa.emit_documentul": "writing the canonical document",
  "etapa.clasific_planse": "classifying the drawings",
  "etapa.citesc_inaltimile": "reading the heights from the elevations",
  "etapa.stabilesc_niveluri": "settling the stack of levels",
  "etapa.masor_golurile": "measuring the openings on the elevations",
  "etapa.aleg_layerele#n": "choosing the layers on 1 drawing|choosing the layers on {n} drawings|choosing the layers on {n} drawings",
  "etapa.gasesc_planul_nivelului": "finding the level plan on each drawing",
  "etapa.aliniez_nivelurile": "aligning the levels",
  "etapa.citesc_functiunile": "reading the room functions",
  // control plane
  "etapa.se_reia": "resuming",
  // A structure step's title (`job_step.title_cod`), in the run's note.
  // The Romanian text is the one `structure/app.py::TITLURI_PAS` writes.
  "titlu_pas.numerotat": "Step {nr} ({pas})",
  "titlu_pas.numerotat_corp": "Step {nr} ({pas}) · {corp}",
  "titlu_pas.conformare": "conformance analysis (7C–9C)",
  "titlu_pas.conformare_corp": "conformance analysis (7C–9C) · {corp}",
  "titlu_pas.asamblare": "assembling the buildings",
  "titlu_pas.imbinare": "joining the buildings",
  "titlu_pas.imbinare_corp": "joining the buildings · {corp}",
  "titlu_pas.necunoscut": "step «{pas}»",
  "titlu_pas.necunoscut_corp": "step «{pas}» · {corp}",
  "timp.acum": "just now",
  "timp.minute": "{n} min ago",
  "timp.ore": "{n} h ago",

  // --- the resolution the solution was measured at ------------------------

  // --- the compatibility board --------------------------------------------


  // --- one solution's card -------------------------------------------------

  // --- one solution's panel ------------------------------------------------
  "picate.toate_trec": "All checks pass.",
  "picate.fara_detaliu": "Which ones is not recorded in the result.",
  "picate.titlu#n": "1 failed check, by category:|{n} failed checks, by category:|{n} failed checks, by category:",
  "picate.rand": "{n} × {eticheta}",
  "picate.abatere_cifre": ": {valoare} against the limit {prag}{um}",

  // --- the Solutions screen -------------------------------------------------
  "solutii.se_opreste": "Stopping…",
  "solutii.opreste_tot_titlu": "Stop everything running or waiting on the project, in any discipline",
  "solutii.oprite": "{n} stopped",
  "solutii.in_oprire": "{n} stopping",
  "solutii.nu_se_pot_opri": "{lista} — the solver that started runs to the end, but nothing chains on top of it any more",
  "solutii.nimic_oprit": "There was nothing to stop.",
  "solutii.esec_oprire": "Could not stop the runs.",
  // --- the checks screen (the architecture side) -------------------------
  // The VALIDATOR's vocabulary — the verdict names, the rule sentences, the
  // balance — stays Romanian: it comes from the server together with the rules
  // themselves (`requirement_ro`, `condition_ro`, `detail_ro`). What is
  // translated here is the shell around it.
  "verificari.titlu": "Checks",
  "verificari.fara_solutie": "This project has no favourite ★ architecture solution, and the checks run on one. Pick one from ",
  "verificari.se_ruleaza": "Running the checks…",
  "verificari.serviciu_mut": "The validation service is not responding. Try again.",
  "verificari.esec": "Could not run the checks.",
  "verificari.esec_elemente": "Could not load the rule's elements.",
  "verificari.arata_numai": "Show only {stare}",
  "verificari.categorii": "Categories — architecture",
  "verificari.nimic_sub_filtru": "Nothing under this filter.",
  "verificari.inapoi": "Back to the category's chapters",
  "verificari.capitole#n": "one chapter|{n} chapters|{n} chapters",
  "verificari.reguli#n": "one rule|{n} rules|{n} rules",
  "verificari.norma_capitolului": "The chapter's standard",
  "verificari.pdf_titlu": "{act} — PDF file, downloads",
  "verificari.link_titlu": "{act} — opens on legislatie.just.ro",
  "verificari.cadran_conforme": "{conforme} of {total} rules passing",
  "verificari.cadran_masurate": "{masurate} of {total} rules measured",
  "verificari.cadran_neverificabile": "{n} of {total} unverifiable",
  "verificari.daca": "If",
  "verificari.atunci": "Then",
  "verificari.se_aplica_la": "Applies to: {ce}",
  "verificari.detalii": "How it is checked",
  "verificari.regula_cazuta": "The rule failed while running.",
  "verificari.se_incarca_elemente": "Loading the elements…",
  "verificari.col_element": "Element",
  "verificari.col_masurat": "Measured",
  "verificari.col_prag": "Threshold",
  "verificari.col_verdict": "Verdict",
  "verificari.vezi_pe_plansa": "See it on the drawing",
  "verificari.mai_multe#n": "show one more element|show {n} more elements|show {n} more elements",
  "verificari.pe_plansa": "{element} · on the drawing",
  "verificari.bilant": "Balance for this solution",
  "verificari.bilant_cifra": " of ",
  "verificari.bilant_conforme": " passing",
  "verificari.pragul": "threshold: {prag}",
  "verificari.procent_conforme": "{procent} % passing",
  "verificari.tab_arhitectura": "Architecture",
  "verificari.tab_structura": "Structure",
  // --- the unit catalogue -------------------------------------------------
  "nou.titlu": "New unit",
  "nou.subtitlu": "pick where it may stand on the block — that is where its shape comes from",
  "nou.cod": "Template code",
  "nou.nota": "It is born empty, with the typical footprint of its category: drag its sides and corners to fit it, and as you put rooms and walls on the edge, the edge follows them. It is saved as a draft — no plan uses it until you publish it.",
  "nou.deschide": "New unit",
  "nou.deschide_titlu": "Start a template from scratch, with the typical shape of the chosen category",
  "nou.pornire_titlu": "Where do you start from?",
  "nou.pornire_sub": "it already has its shape — what is written on it is what is left",
  "nou.gol": "Empty sheet",
  "nou.gol_sub": "the typical footprint of the category, with no room in it",
  "nou.existent": "From a unit",
  "nou.existent_sub": "another unit of the same category, copied so you can change it",
  "nou.imagine": "From an image",
  "nou.imagine_sub": "a scanned or photographed plan, transcribed exactly: walls, openings, rooms and furniture in place",
  "nou.alege_modulul": "The unit it starts from",
  "nou.niciun_modul": "This category has no unit to copy yet.",
  "nou.modul_cifre": "{arie} m² usable · {flats} flats",
  "nou.alege_imaginea": "Choose the image",
  "nou.schimba_imaginea": "Another image",
  "nou.decupaj_titlu": "Crop the plan",
  "nou.decupaj_sub": "keep only the unit's plan",
  "nou.decupaj_nota": "Drag the corners or edges; dragging on the image draws a new rectangle.",
  "nou.decupaj_px": "{w} × {h} px of the sheet are kept.",
  "nou.decupaj_tot": "Whole image",
  "nou.decupaj_gata": "Use this crop",
  "nou.decupeaza_iar": "Crop again",
  "nou.pdf_necitit": "Could not read the PDF. Try a picture or another file.",
  "nou.pdf_randez": "preparing the page from the PDF…",
  "nou.imagine_lipsa": "Choose the image first.",
  "nou.modul_lipsa": "Choose the unit it starts from first.",
  "nou.se_citeste": "Reading the plan…",
  // Which step the read is on, under the bar. The steps are the finalize
  // stages (`finalizare::_etapa`): on a large sheet a single one runs for
  // minutes, and a bar that fills on its own says nothing about whether work
  // is happening or it has hung.
  "nou.pas_tura": "{pas} · round {tura}",
  "nou.etapa.pregatire": "Preparing the sheet",
  "nou.etapa.cote": "Reading the written dimensions",
  "nou.etapa.citire": "Reading the drawing",
  "nou.etapa.finalizare": "Laying the plan onto the ink",
  "nou.pas.la_rand": "waiting its turn",
  "nou.pas.goluri_pe_ziduri": "Placing openings on their walls",
  "nou.pas.lipeste_fete": "Snapping walls to the ink",
  "nou.pas.aliniaza_ziduri": "Straightening the walls",
  "nou.pas.inchide_noduri": "Closing the corners",
  "nou.pas.stalpi_praguri": "Placing columns and thresholds",
  "nou.pas.cote": "Fixing dimensions and finding the scale",
  "nou.pas.sonda_scarii": "Deriving the scale from written areas",
  "nou.pas.potriveste_rotunjeste": "Fitting to dimensions and the 5 cm grid",
  "nou.pas.camere": "Deriving rooms from the walls",
  "nou.pas.mobilier": "Placing the furniture",
  "nou.pas.scara": "Writing the scale",
  "nou.pas.asociaza": "Linking walls to rooms",
  "nou.pas.portanta": "Finding the load-bearing walls",
  "nou.pas.mobila_din_ziduri": "Pushing furniture out of walls",
  "nou.pas.tocurile_golurilor": "Snapping door frames to the ink",
  "import.reia": "try again",
  "import.renunta": "Cancel the reading and delete the module",
  "import.inchide": "Dismiss",
  "colectie.redenumeste": "rename",

  // --- EXPORTUL PLANȘEI unui modul (viewer/tronson/export) -----------------
  "export.buton": "Export",
  "export.pdf": "PDF",
  "export.pdf_titlu": "The module's sheet as a PDF: the drawing to scale, on A3 or A2, with a title block",
  "export.dwg": "DWG",
  "export.dwg_titlu": "The module's sheet as a DWG: the drawing in metres, on layers — walls, doors, windows, rooms, furniture, dimensions",
  "export.esuat": "The PDF could not be generated.",
  "export.pornit": "Exporting “{cod}” as {format}…",
  "export.gata": "“{cod}” was exported as {format}.",
  "export.pardoseala": "Floor",
  "export.pereti": "Walls",
  "export.dwg_esuat": "The DWG could not be generated.",
  "export.dwg_pierdute": "The DWG was written, but {n} of {din} entities were lost in the conversion.",
  "export.titlu_plansa": "Module plan · section at 1.15 m",
  "export.categorie": "Category",
  "export.versiune": "Version",
  "export.apartamente": "Flats",
  "export.arie_utila": "Usable area",
  "export.amprenta": "Footprint",
  "export.front": "Front",
  "export.scara": "Scale",
  "export.data": "Date",
  "export.autor": "Author",
  "export.sursa": "Source",
  "colectie.redenumeste_titlu": "A new name for “{cod}”",
  "colectie.redenumeste_camp": "New name",
  "colectie.redenumeste_regula": "Capitals, digits, space, “_” and “-”, at most {n} characters. The name links the module to its tracing paper and its labels; the server moves them all together.",
  "import.titlu": "Drawings being read",
  "import.pornit": "sending the drawing…",
  "import.gata": "done — the module is in the collection",
  "import.esuat": "the reading stopped",
  "nou.import_prea_mica": "The sheet is too small: under 250 px on the long side no wall is visible any more.",
  "nou.import_fara_plan": "The reading did not end with a plan in the time available. Try again; if it repeats, the sheet is probably too faint.",
  "nou.import_refuz": "The model refused to read the image.",
  "nou.import_mort": "The reading stopped midway (the service was restarted).",
  "nou.import_eroare_interna": "The reading failed on the server. Try again; if it repeats, send us the image.",
  "nou.import_imagine_lipsa": "The image did not reach the server. Try again.",
  "calc.titlu": "Tracing",
  "calc.pune": "Put an image to trace under the drawing",
  "calc.necalibrat": "not calibrated",
  "calc.latime": "{m} m wide",
  "calc.opacitate": "How much of it shows",
  "calc.ascunde": "Hide the tracing image",
  "calc.arata": "Show the tracing image",
  "calc.scoate": "Remove the tracing image",
  "colectie.titlu": "Unit catalogue",
  "colectie.subtitlu": "the modules a block is composed from — two or more apartments on either side of a corridor",
  "colectie.capat": "End",
  "colectie.capat_sub": "closes the block at one end — it has no neighbour on one side",
  "colectie.mijloc": "Middle",
  "colectie.mijloc_sub": "repeats along the block, between two modules",
  "colectie.colt": "Corner",
  "colectie.colt_sub": "joins two wings at 90° — it is placed, not stretched",
  "colectie.izolat": "Standalone",
  "colectie.izolat_sub": "stands on its own plot — a whole dwelling, with no corridor and no neighbour",
  "colectie.camere": "{mix} rooms",
  "colectie.dublu_clic": "double-click — opens the editor",
  "prov.titlu": "Where this module came from",
  "prov.al_cui": "In the collection of",
  "prov.desenat_de": "Drawn by",
  "prov.sursa": "Source",
  "prov.masurat": "Scale",
  "prov.nota": "Version note",
  "prov.nota_desenului": "Drawing note",
  "prov.scris_la": "Written on",
  "imparte.titlu": "Who has access to {cod}",
  "imparte.doar_proprietarul": "You can see who has access, but only the module's owner can change it.",
  "imparte.niciunul": "Nobody but you.",
  "imparte.toata_organizatia": "Everyone in {nume}",
  "imparte.vede": "View",
  "imparte.scrie": "Edit",
  "imparte.scoate": "Remove access",
  "imparte.da_organizatiei": "Give everyone in {nume} access",
  "imparte.alege_om": "pick someone from the organisation…",
  "imparte.toti_au": "everyone in the organisation already has access",
  "imparte.gata": "Done",
  "colectie.imparte": "access",
  "colectie.impartit": "received",
  "colectie.impartit_titlu": "Someone else owns this module and gave you access",
  "colectie.nucleu": "⌂ core",
  "colectie.ciorna": "draft · v{versiune}",
  "colectie.ciorna_titlu": "version {versiune} — saved, but unpublished: no plan uses it yet",
  "colectie.editat": "edited · v{versiune}",
  "colectie.editat_titlu": "version {versiune}, the project's own",
  "colectie.neverificat": "not checked yet — the figures are written on publishing",
  "colectie.apartamente": "flats",
  "colectie.util": "m² usable",
  "colectie.front": "frontage {metri} m",
  "colectie.front_rigid": " · rigid",
  "colectie.front_gama": " · {minim}–{maxim}",
  "colectie.editeaza": "Edit →",
  "colectie.vedere_plan": "Plan",
  "colectie.vedere_design": "Design",
  "colectie.vedere_macheta": "Model",
  "colectie.scara": "drawn at the nominal frontage ({metri} m)",
  "colectie.se_incarca_macheta": "Loading the model…",
  "colectie.toate_globale": "All {n} are global — drawn from drawings, shared by every project.",
  "colectie.unele_ale_proiectului": "{ale} of {n} belong to this project; the rest are global.",
  "colectie.banda": " Band depth {metri} m.",
  "colectie.ciorne#n": "One is a draft|{n} are drafts|{n} are drafts",
  "colectie.ciorne_explicatie": " — publish them from the editor for plans to use them. ",
  "colectie.ascunde_ciornele": "hide the drafts",
  "colectie.arata_ciornele": "show the drafts",
  "colectie.se_incarca_modulul": "Loading module {cod}…",
  "colectie.sterge_ciorna": "Delete the draft",
  "colectie.sterge_ciorna_intrebare": "Delete the project's drafts of module {cod}?\n\nEvery project version under this code is lost; if a global module with the same code exists, plans fall back to it. This cannot be undone.",
  "colectie.ciorna_stearsa": "The draft of module {cod} was deleted.",
  // --- unit tags ----------------------------------------------------------
  // The project's own words, put on modules. The vocabulary belongs to the
  // project and may sit empty: a tag taken off the last module stays in the
  // list, so it is PICKED rather than typed again.
  "etichete.titlu": "Tags",
  "etichete.explicatie": "the project's own words — put them on modules to find and group them",
  "etichete.niciuna": "no tags",
  "etichete.adauga": "+ tag",
  "etichete.adauga_titlu": "Pick one of the project's tags, or write a new one",
  "etichete.scoate_titlu": "Take “{nume}” off this module — the tag stays in the project",
  "etichete.cauta": "pick one or write a new one",
  "etichete.creeaza": "create “{nume}”",
  "etichete.nimic_de_ales": "No tags in this project yet — write one.",
  "etichete.toate_puse": "Every tag in this project is already on this module — write a new one.",
  "etichete.gata": "done",
  "etichete.plin": "at most {n} tags on a module",
  // --- the project dossier -----------------------------------------------
  // The names of the dossier pieces (“Planning certificate”…) come from the
  // server with the categories; what is translated here is the screen's shell.
  "dosar.mort": " · no sign of life, probably stopped — start it again",
  "dosar.secunde": "{n} s",
  "dosar.minute": "{n} min",
  "dosar.de_unde.regula": "recognised from the name of the document and its folder",
  "dosar.de_unde.titlu": "inferred from the context of the whole document list",
  "dosar.de_unde.continut": "read from the document — it was opened and looked at",
  "dosar.de_unde.manual": "moved here by you",
  "dosar.de_unde.oferte": "uploaded as a quote, under Planning → Quotes",
  "dosar.esec_incarcare": "Could not load the documents.",
  "dosar.altele": "Other",
  "dosar.neacceptate": "Not accepted: {fisiere}",
  "dosar.esec_urcare": "The upload failed.",
  "dosar.esec_reprocesare": "Could not reprocess “{fisier}”.",
  "dosar.se_reproceseaza": "reprocessing",
  "dosar.se_citeste": "being read",
  "dosar.se_citeste_oferta": "being read as a quote",
  "dosar.desen_se_pregateste": "preparing the drawing",
  "dosar.desen_la_rand": "the drawing is queued",
  "dosar.fisiere#n": "{n} document|{n} documents|{n} documents",
  "dosar.adauga_fisiere": "Add documents",
  "dosar.vezi_solutiile": "See the solutions",
  "dosar.vezi_solutiile_titlu": "The architecture imported from the drawings — the structure starts from it",
  "dosar.structura_genereaza": "Generate the structure",
  "dosar.structura_deschide": "Open the structure",
  "dosar.structura_porneste": "Starting…",
  "dosar.structura_motiv_gol": "Turns on once the architectural drawings and the geotechnical report are in.",
  "dosar.structura_motiv_fara_geo": "The geotechnical report is missing.",
  "dosar.structura_motiv_fara_arh": "The architectural drawings are missing.",
  "dosar.structura_motiv_gata": "The drawings are imported, then the structure starts on them by itself.",
  "dosar.structura_motiv_pe_arh": "The structure starts on the imported architecture.",
  "dosar.structura_trage": "Drop the whole project here, as it is",
  "dosar.structura_trage_rest": " — the drawings and the report are recognised and land in their place.",
  "dosar.structura_optional": "Optional",
  "dosar.structura_retete": "with them, the cost estimate uses your prices — quotes filed here are read on their own and show up under Planning → Quotes",
  "dosar.loc.cerut": "Required · {n} of 2",
  "dosar.loc.arh_titlu": "The architectural design",
  "dosar.loc.arh_desc": "The DWG drawings of every level — basement, ground floor, upper floors, roof — and the sections, for the heights.",
  "dosar.loc.geo_titlu": "The geotechnical report",
  "dosar.loc.geo_desc": "The report as PDF. It gives the bearing soil, the groundwater and the seismicity.",
  "dosar.loc.geo_nota": "Read at the foundations, when the structure starts: bearing soil, groundwater, ag and TC.",
  "dosar.loc.lipseste": "missing",
  "dosar.loc.blocheaza": "missing — the structure cannot start without it",
  "dosar.loc.in_lucru#n": "preparing {n} of {total}|preparing {n} of {total}|preparing {n} of {total}",
  "dosar.loc.planse#n": "1 drawing|{n} drawings|{n} drawings",
  "dosar.loc.primit": "received",
  "dosar.loc.trage": "Drop the files here or",
  "dosar.loc.alege": "pick them",
  "dosar.plansa.plan_nivel": "floor plan",
  "dosar.plansa.sectiune": "section",
  "dosar.plansa.fatada": "elevation",
  "dosar.plansa.plan_situatie": "site plan",
  "dosar.plansa.plan_incadrare": "location plan",
  "dosar.plansa.releveu": "survey",
  "dosar.structura_import_ruleaza": "The architectural drawings are being imported — wait for the import to finish.",
  "dosar.structura_esec": "Could not start the structural solution.",
  "dosar.structura_importa": "Import the structure",
  "dosar.structura_motiv_gata_import": "The drawings get imported, then the structure drawn in them is read and analysed on its own.",
  "dosar.structura_motiv_pe_arh_import": "The structure is imported from the architecture's drawings.",
  "dosar.continua": "Continue to Plot details",
  "dosar.continua_titlu_citit": "Go to the next step with what was read from the documents",
  "dosar.continua_titlu": "You can continue without reading the documents — the plot can be drawn on the map",
  "dosar.trage_aici": "Just drop all the relevant documents here",
  "dosar.trage_aici_rest": " — the document is classified and processed automatically.",
  "dosar.gol.titlu": "Add any document regarding your plot to get started.",
  "dosar.gol.exemple": "For example",
  "dosar.gol.ex_urbanism": "Zoning verification letter",
  "dosar.gol.ex_plan_zona": "Plat map",
  "dosar.gol.ex_plan_lot": "Site plan",
  "dosar.gol.ex_proprietate": "Title deed",
  "dosar.gol.ex_geotehnic": "Geotechnical report",
  "dosar.gol.ex_topografic": "Topographic survey",
  "dosar.gol.sari": "You can also skip to Plot details and draw the plot yourself.",
  "dosar.import_ruleaza": "Importing the external solution",
  "dosar.import_esec": "The import failed",
  "dosar.citire_ruleaza": "Processing the documents",
  "dosar.citire_esec": "Processing failed",
  "dosar.se_incarca_grup": "Uploading",
  "dosar.urcat_din": "{trimis} of {total}",
  "dosar.se_trimite": "sending…",
  "dosar.se_incarca_rand": "uploading",
  "dosar.necitit": "could not be read",
  "dosar.desen_necitit": "the drawing could not be read",
  "dosar.referinte_lipsa": "missing references",
  "dosar.reproceseaza_titlu": "Reprocess {fisier} — re-reads the document and, if it is a drawing, looks into it again (after a change of area of interest)",
  "dosar.reproceseaza_aria": "Reprocess {fisier}",
  "dosar.sterge_aria": "Delete {fisier}",
  "dosar.confirma_stergere_citit": "Delete “{fisier}”?\n\nThe documents have already been read, and what was understood from it stays in the project data until you press “Read the documents” again.",
  "dosar.confirma_stergere": "Delete “{fisier}”?",
  "dosar.confirma_stergere_oferta": "Delete “{fisier}”?\n\nIt is also the quote under Planning → Quotes and leaves there too. The scenario made from it stays.",
  "dosar.confirma_stergere_plansa_importata": "Delete “{fisier}”?\n\nIt is the drawing the architecture was imported from ({niveluri}). The architecture does not change with the documents: once it is deleted, the architecture and the structure on it stay on a drawing that is no longer here, until you re-import.",
  "dosar.piesa": "Document",
  "dosar.deschide_viewer": "Open in the viewer",
  "dosar.desen_gata_titlu": "the drawing is prepared — it opens instantly",
  "dosar.desen_lent_titlu": "the drawing is parsed on first opening (20–40 s)",
  "dosar.desen_gata": "The drawing is prepared — it opens instantly.",
  "dosar.desen_gata_insa": " However, {motiv}.",
  "dosar.desen_eroare": "The drawing could not be parsed: {motiv}",
  "dosar.desen_in_fundal": "The drawing is being prepared in the background; until then the first opening takes as long as parsing it.",
  "dosar.marime": "Size",
  "dosar.folder": "Folder",
  "dosar.radacina": "root",
  "dosar.plansa": "Drawing",
  "dosar.de_ce_aici": "Why it was filed here",
  "dosar.ce_s_a_intamplat": "What happened while reading",
  "dosar.fara_motiv": "no reason recorded",
  "dosar.reproceseaza_act_titlu": "Throw away what was derived from this document and read it again",
  "dosar.se_reproceseaza_act": "Reprocessing…",
  "dosar.reproceseaza_act": "Reprocess the document",
  "dosar.sterge_fisierul": "Delete the document",
  "dosar.oferta": "Quote",
  "dosar.oferta_se_citeste": "Being read as a quote, under Planning → Quotes.",
  "dosar.oferta_aplicata": "Read as a quote: it made the scenario «{scenariu}».",
  "dosar.oferta_aplicata_fara": "Read as a quote; the scenario made from it is no longer in the project.",
  "dosar.oferta_eroare": "Reading it as a quote failed; the reason is under Planning → Quotes.",
  "dosar.oferta_necitita": "It is read as a quote on its own once the project has costs — a bill of quantities to put its prices on.",
  "dosar.oferta_deschide": "Open in Quotes",

  // --- the drawings changed since the architecture was imported ---------
  "planse.titlu": "The drawings changed since the import",
  "planse.text": "The imported architecture came from the drawings of that moment and does not change with the documents. A structure started on it works on a building the documents no longer describe.",
  "planse.stearsa": "“{fisier}” ({niveluri}) was deleted from the documents",
  "planse.noua": "“{fisier}” is a floor plan uploaded after the import",
  "planse.reimporta": "Re-import the drawings",
  "planse.reimporta_titlu": "Reads the current drawings into a new architecture, which becomes the favourite ★; the structure is searched on it by itself",
  "planse.se_reimporta": "Starting the re-import…",
  "planse.esec_reimport": "The re-import did not start.",
  "planse.confirma_reimport": "The re-import reads the current drawings in the documents into a new architecture, which becomes the favourite ★, and the structure is searched on it by itself.\n\nThe current architecture and the structure solutions on it stay in the project, but they are no longer the ones being worked on. Re-import?",
  "planse.confirma_pe_vechi": "The drawings changed since the architecture was imported — {ce}. The structure would start on the architecture of that moment, not on the current drawings.\n\nStart the structure on the old architecture anyway? Otherwise, “Re-import the drawings”.",
  "planse.ce_sterse": "deleted from the documents: {fisiere}",
  "planse.ce_noi": "uploaded after the import: {fisiere}",
  // --- the unit editor ----------------------------------------------------
  // The palette, the properties panel and the translation of the checks live in
  // `viewer/tronson/editor/` and stay Romanian — see `docs/internationalizare.md`.
  "editor.se_incarca": "Loading module {cod}…",
  "editor.bagheta_intrebare": "The module already has {piese} pieces of furniture and {aparate} fittings.\n\nOK — replaces all of them with the automatic layout.\nCancel — keeps the furniture and only runs the services over it.",
  "editor.bagheta_sarite#n": " One room was left empty: {motive}.| {n} rooms were left empty: {motive}.| {n} rooms were left empty: {motive}.",
  "editor.bagheta_mobilier": "{piese} pieces of furniture and ",
  "editor.bagheta_raport": "{mobilier}{aparate} electrical fittings.{sarite}",
  "editor.bagheta_titlu": "Laid out automatically: {raport}",
  "editor.cod_nou": "The new template's code (letters and digits, e.g. M9):",
  "editor.cod_luat": "The code {cod} is already used by another template. Pick another one.",
  "editor.cod_invalid": "The code starts with a letter and has at most 16 letters, digits, “-” or “_”.",
  "editor.calc_esuat": "The image could not be read. Try a JPEG or a PNG.",
  "editor.inapoi": "Back to the catalogue",
  "editor.versiune": "v{versiune} · {stare}",
  "editor.publicat": "published",
  "editor.ciorna": "draft",
  "editor.global": "global — unmodified",
  "editor.versiuni.titlu": "Versions",
  "editor.versiuni.inlocuita": "replaced",
  "editor.versiuni.persoana": "person",
  "editor.versiuni.sistem": "system",
  "editor.versiuni.din": "from v{v}",
  "editor.versiuni.deschisa": "open",
  "editor.versiuni.eroare": "Versions could not be loaded",
  "editor.ascunde_axe": "Hide the setting-out axes",
  "editor.arata_axe": "Show the setting-out axes — drag an axis to move every wall on its line",
  "editor.ascunde_etichete": "Hide the room names and areas",
  "editor.arata_etichete": "Show the room names and areas",
  "editor.ascunde_obiecte": "Hide object labels (UME doors, hatches)",
  "editor.arata_obiecte": "Show object labels (UME doors, hatches)",
  "editor.ascunde_cote": "Hide the dimensions",
  "editor.arata_cote": "Show the dimensions",
  "editor.ascunde_apartamente": "Stop colouring the apartments",
  "editor.ascunde_mobilier": "Hide the furniture",
  "editor.arata_mobilier": "Show the furniture",
  "editor.arata_apartamente": "Colour the apartments",
  "editor.ascunde_foc": "Hide the fire-resistance colours (ISU)",
  "editor.arata_foc": "Show the fire-resistance colours (ISU)",
  "editor.ascunde_pardoseli": "Hide the floor finishes — the rooms stay white",
  "editor.arata_pardoseli": "Show the room floor finishes",
  "editor.estompeaza_electrice": "Fade the electrical installation — the walls stay",
  "editor.ascunde_electrice": "Hide the electrical installation",
  "editor.arata_electrice": "Show the electrical installation",
  "editor.inchide": "Close",
  "editor.nesalvat": "unsaved",
  "nivel.contur": "Storey outline",
  "nivel.contur_citire": "The outline is the storey slab, the envelope the rooms are rebuilt in. Drag its CORNERS and EDGES; a facade wall dragged outwards takes it along. On save it is written back to the solution as the storey polygon.",
  "nivel.salveaza": "Save to solution",
  "nivel.salveaza_titlu": "Rewrites in place, in the solution document, what you changed on this storey and on the ticked storeys",
  "nivel.se_duce_pe": "Also goes to",
  "nivel.se_duce_pe_nota": "The storeys of the same building, with how much they share with the open storey. A change only reaches their identical part — on a set-back storey, only the shared portion.",
  "nivel.fara_altele": "The building has no other storey that shares anything with this one.",
  "nivel.titlu_sect": "Storey",
  "nivel.filtru.axe": "Axes",
  "nivel.cladirea": "Building",
  "nivel.inaltime": "Storey height",
  "nivel.scris": "Written to the solution: {obiecte} objects changed, {sterse} deleted, on {niveluri} storeys.",
  "nivel.inchide_editarea": "Close editing",
  "viz.grup.depanare": "Debug",
  "viz.grup.unelte": "Tools",
  "viz.rigla": "Ruler",
  "viz.rigla_titlu": "Ruler — two clicks on the drawing and it writes the distance between them. Dragging still pans.",
  "viz.rigla_stinge_titlu": "Ruler — turn off and clear the measurements ({n}). Escape does the same.",
  "viz.editeaza_planul": "Edit plan",
  "viz.editeaza_planul_titlu": "Open the storey plan on the work table: walls, axes and dimensions to drag and type",
  "viz.evacuare_recalc": "Recalculate evacuation routes",
  "viz.evacuare_recalc_titlu": "Rebuilds the evacuation routes and traces on the current bays and aisles, with the existing SAS exits, halls and corridors. Nothing on the floor is moved or removed.",
  "viz.evacuare_recalc_nesalvat": "Save the parking edits first: the recalculation works on the floor written in the solution.",
  "viz.evacuare_recalc_lucru": "Recalculating the evacuation routes…",
  "viz.evacuare_recalc_picat": "The evacuation route recalculation failed on this level; the routes shown are the previous ones. Error: {eroare}",
  "viz.evacuare_recalc_fara_nivel": "This level has no evacuation routes to rebuild.",
  "viz.evacuare_recalc_ok": "Evacuation routes rebuilt: {iesiri} exits, longest route {lung} m.",
  "viz.evacuare_recalc_gata": "Evacuation routes rebuilt.",
  "viz.evacuare_recalc_fara": "Bays without a route under 40 m: {n}, in red on the floor.",
  "viz.evacuare_recalc_negasite": "Exits no longer on a facade: {iesiri}.",
  "viz.editeaza_parcajul": "Edit parking",
  "viz.editeaza_parcajul_titlu": "Move, add and delete parking bays on this floor. Written in place into the solution; «Redo the basement» would overwrite what you edited.",
  "parcaj.titlu": "Parking editing",
  "parcaj.salveaza_titlu": "Rewrite in place, in the solution document, the moved, added and deleted bays",
  "parcaj.unealta.sageata": "Select",
  "parcaj.unealta.sageata_titlu": "Press a bay and drag it: it slides along one of its axes — along the row or in depth, whichever way you pull; with ⇧ it moves freely. Arrows nudge it by 5 cm (⇧: 50 cm), R rotates it, Delete removes it.",
  "parcaj.unealta.nou": "New bay",
  "parcaj.unealta.nou_titlu": "Press on the floor to place a 2.50 × 5.00 bay: it takes the orientation of its neighbour or of the aisle and snaps to neighbours (N)",
  "parcaj.liber": "Free",
  "parcaj.liber_titlu": "Free move: the bay goes wherever you drag it, not only along its row — for bays misplaced against the aisle. Without this mode, holding ⇧ does the same.",
  "parcaj.unghi": "Angle",
  "parcaj.unghi_titlu": "The bay's angle in degrees; type a value and press Enter. Or drag the round handle beyond the bay's front (⇧: in 15° steps).",
  "parcaj.alese": "{n} bays selected — moved, rotated and deleted together",
  "parcaj.aleea": "Aisle · {l} m long · {w} m wide — drag its ends to lengthen or shorten it, the middle handle to rotate it",
  "parcaj.unghi_alee_titlu": "The aisle axis angle in degrees; type a value and press Enter. Or drag the round handle at its middle (⇧: in 15° steps). It turns around its middle, keeping its length.",
  "parcaj.se_reciteste": "Written. Reloading the floor…",
  "parcaj.sterge_aleea_titlu": "Remove the aisle from the floor (Delete). Bays that entered only from it are left unserved and show in amber; extend another aisle up to them.",
  "parcaj.ales": "Bay {n} · {fel} · {w} × {d} m",
  "parcaj.roteste": "Rotate 90°",
  "parcaj.roteste_titlu": "Turn the bay a quarter turn around its centre (R)",
  "parcaj.sterge": "Delete",
  "parcaj.sterge_titlu": "Remove the bay from the floor (Delete)",
  "parcaj.sfat": "Press a bay to select it, then drag it. ⇧+click selects several. Press an aisle to drag its ends.",
  "parcaj.sfat_nou": "Press on the floor where you want the new bay. Esc returns to selection.",
  "parcaj.peste": "Overlaps: {ce}. Cannot be saved like this.",
  "parcaj.nedeservit": "No aisle to enter from (under 2.5 m of gap in front of one of its sides).",
  "parcaj.fel.loc": "another bay",
  "parcaj.fel.stalp": "a column",
  "parcaj.fel.alee": "an aisle",
  "parcaj.fel.camera": "a room",
  "parcaj.fel.perete": "a wall",
  "parcaj.fel.rampa": "a ramp",
  "parcaj.fel.culoar": "an accessible aisle",
  "parcaj.fel.contur": "the floor edge",
  "parcaj.refuz_suprapuneri": "{n} bays overlap something. Move them first — overlaps are not saved.",
  "parcaj.scris": "Written to the solution: {mutate} bays moved, {noi} new, {sterse} deleted, {alei} aisles changed. Checks and evacuation computed earlier may be stale.",
  "parcaj.pleci_nesalvat": "You are leaving parking editing with unsaved changes. They are lost — none of them is written to the solution.\n\nOK — leave anyway.\nCancel — stay and save.",
  "nivel.corpuri_pe_masa": "Every building on this storey is on the sheet, each with its own axes. You work on one at a time: click another to bring it onto the table — changes to the one you left are kept until you save.",
  "nivel.nu_se_poate": "The storey could not be opened in the editor: {motiv}",
  "nivel.pleci_nesalvat": "You are leaving the storey plan with unsaved changes. They are lost — none of them is written to the solution.\n\nOK — leave anyway.\nCancel — stay and save.",
  "editor.salvat": "saved",
  "editor.salveaza_ciorna": "Save the draft",
  "editor.salveaza_ca_nou": "Save as new",
  "editor.salveaza_ca_nou_titlu": "Put the drawing aside as a new template, under another code, without changing this one",
  "editor.publica": "Publish",
  "editor.avertismente_titlu": "What the catalogue flags in this module",
  "editor.avertismente_toast_ciorna#n": "Draft saved with 1 catalogue warning.|Draft saved with {n} catalogue warnings.|Draft saved with {n} catalogue warnings.",
  "editor.avertismente_toast_publicat#n": "Published with 1 catalogue warning.|Published with {n} catalogue warnings.|Published with {n} catalogue warnings.",
  "editor.avertismente_sfat": "The module still saves and publishes. A failed rule still lets the site plan use it; broken data stops it until you fix it.",
  "editor.abatere_camera.kitchen": "the kitchen",
  "editor.abatere_camera.living": "the living room",
  "editor.abatere_camera.bedrooms_sum": "the bedrooms",
  "editor.abatere_camera.sanitary_sum": "the bathrooms",
  "editor.abatere_ce.room": "a room",
  "editor.abatere_ce.wall": "a wall",
  "editor.abatere_ce.door": "a door",
  "editor.abatere_ce.window": "a window",
  "editor.abatere.camere_fara_prag": "Flat {locuinta} has {camere} rooms, and the rules have no minimums for that many.",
  "editor.abatere.util_sub_prag": "Flat {locuinta} ({camere} rooms) has {arie} m² of usable area, under the {prag} m² minimum.",
  "editor.abatere.camera_sub_prag": "Flat {locuinta} ({camere} rooms): {camera} is {arie} m², under the {prag} m² minimum.",
  "editor.abatere.variante_implicite": "{numar} variants are marked default; there must be one.",
  "editor.abatere.variante_util": "Variant {varianta} declares {declarate} usable areas for {locuinte} flats.",
  "editor.abatere.variante_exterior": "Variant {varianta} declares {declarate} outdoor spaces for {locuinte} flats.",
  "editor.abatere.suma_camerelor": "Flat {locuinta}: the rooms add up to {arie} m², but variant {varianta} declares {declarata} m².",
  "editor.abatere.front_in_afara": "The nominal front is outside the minimum–maximum range.",
  "editor.abatere.front_rigid": "The module is declared rigid, but its front is a range, not a single value.",
  "editor.abatere.absorbanti": "The module shrinks by {strangere} m, but the absorbing rooms can only take {capacitate} m.",
  "editor.abatere.colt_oglinda": "A corner module cannot mirror exactly.",
  "editor.abatere.nucleu_lipsa": "The module is a core module but declares no core.",
  "editor.abatere.nucleu_fara_loc": "The module has a stair but does not say where it stands (the core rectangle is missing).",
  "editor.abatere.nucleu_mic": "The core rectangle is {latime} × {adancime} m — too small for a stair.",
  "editor.abatere.nucleu_in_afara": "The core rectangle falls outside the module.",
  "editor.abatere.scara_neancorata": "The stair starts at {u} m in a module that stretches by {intindere} m; anchor it at the fixed end.",
  "editor.abatere.nucleu_rect_fara_nucleu": "The module has a core rectangle but declares no core.",
  "editor.abatere.parti_fara_nucleu": "The module declares core parts but has no core.",
  "editor.abatere.parte_in_afara": "The core part «{parte}» falls outside the module.",
  "editor.abatere.parti_suprapuse": "Two core parts overlap («{parte}» on another).",
  "editor.abatere.scara_mai_mica": "The stair is {scara} m², smaller than the lift ({lift} m²): the part is mislabelled or the stair is missing.",
  "editor.abatere.k_forfecat": "At a={a} there are vertices with different stretch ({k1} and {k2}); the wall between them would tear when stretched.",
  "editor.abatere.k_pliat": "A fixed vertex (a={a0}) stands past a moving one (a={a1}); the module would fold when stretched.",
  "editor.abatere.k_in_nucleu": "{numar} vertices in the core zone stretch while the core does not.",
  "editor.abatere.camera_peste_nucleu": "The room {camera} covers {arie} m² of the stair core.",
  "editor.abatere.scara_fara_loc": "The core declares {parti} parts and no stair, and what is left cannot hold one.",
  "editor.abatere.lifturi": "The core declares {declarate} lifts but {desenate} are drawn.",
  "editor.abatere.arc_fara_ax": "The curved wall {perete} has no axis.",
  "editor.abatere.arc_ax_zero": "The curved wall {perete} has an axis of zero length.",
  "editor.abatere.arc_intins": "The curved wall {perete} has vertices that stretch differently; an arc moves whole.",
  "editor.abatere.straturi_grosime": "The wall {perete} has {straturi} m of layers in a {grosime} m thickness.",
  "editor.abatere.straturi_fara_ax": "The wall {perete} has layers but no axis.",
  "editor.abatere.parapet_cat_nivelul": "The parapet {perete} is as tall as the storey; that makes it a wall, not a parapet.",
  "editor.abatere.perete_prea_inalt": "The wall {perete} is {inaltime} m tall in a {nivel} m storey.",
  "editor.abatere.inaltime_egala_nivelului": "The wall {perete} declares a height equal to the storey's; leave it empty.",
  "editor.abatere.perete_dublu": "The wall id {perete} is used twice.",
  "editor.abatere.perete_fara_inel": "The wall {perete} is not a closed outline.",
  "editor.abatere.beton_intins": "The concrete wall {perete} would stretch; a cast wall moves whole.",
  "editor.abatere.intins_transversal": "Something stretches across the band ({ce}).",
  "editor.abatere.gol_fara_perete": "The opening ({ce}) sits on wall {perete}, which no longer exists.",
  "editor.abatere.gol_transversal": "An opening stretches across the band.",
  "editor.abatere.acoperire": "Rooms and walls cover only {procent}% of the module; {rest} m² is neither.",
  "editor.abatere.logie_nedesenata": "Flat {locuinta} has a loggia, but it is not drawn.",
  "editor.abatere.desen_vs_declarat": "Flat {locuinta} is drawn at {desenat} m² but declares {declarat} m².",
  "editor.abatere.schema": "The field «{cale}» has a value the catalogue does not accept ({regula}): {valoare}",
  "editor.abatere.modul_dublu": "The module {modul} appears twice in the catalogue.",
  "editor.abatere.catalog_gol": "The catalogue has no modules.",
  "editor.abatere.adancimi_amestecate": "The catalogue mixes different band depths ({adancimi} m).",
  "editor.abatere.nimic_publicat": "No module is published.",
  "editor.abatere.alpha_rupt": "The module's summary ({module}) no longer matches its drawing; publish it again.",
  "editor.abatere.nimic_utilizabil": "No published module can be used ({module}).",
  "editor.abatere.nederivabil": "The drawing cannot be read yet ({eroare}); it is probably half drawn.",
  "editor.pleci_nesalvat": "You are leaving the editor with unsaved changes. They are lost — none of them is written to the catalogue.\n\nOK — leave anyway.\nCancel — stay and save the draft.",
  "editor.urmarea_ciorna": "Saved as a draft, v{versiune}",
  "editor.urmarea_ciorna_sfat": "A draft goes into no plan. “{publica}” makes it the final version.",
  "editor.urmarea_publicat": "Published: v{versiune} is the final version",
  "editor.urmarea_publicat_sfat": "From now on the project's plans use this version.",
  "editor.se_incarca_macheta": "Loading the model…",
  "editor.unealta.perete": "Straight wall",
  "editor.unealta.arc": "Curved wall",
  "editor.unealta.parapet": "Parapet",
  "editor.unealta.balcon": "Balcony",
  "editor.unealta.usa": "Single door",
  "editor.unealta.usaDubla": "Double door",
  "editor.unealta.usaGlisanta": "Sliding door",
  "editor.unealta.fereastra": "Window",
  "editor.unealta.camera": "Rectangular room",
  "editor.unealta.cameraL": "L-shaped room",
  "editor.unealta.cameraT": "T-shaped room",
  "editor.unealta.zonaDesfumare": "Smoke extraction zone",
  "editor.sfat.zona_desfumare": "click corner after corner, then close it by clicking the first point. It is drawn OVER the room it sits in, with no walls around it",
  "prop.strange_titlu": "Collapse the panel",
  "prop.desfa_titlu": "Expand the panel",
  "paleta.zona_desfumare": "Smoke extraction zone",
  "editor.unealta.cameraFaraPereti": "Room without walls",
  "editor.sfat.camera_fara_pereti": "click corner after corner, then close it by clicking the first point or pressing Enter. It builds no walls around it — pick the room type afterwards in the panel",
  "paleta.camera_fara_pereti": "Room without walls",
  "editor.unealta.intinde": "Stretch",
  "editor.sfat.intinde": "drag a window over part of the plan, then drag it. What it fully contains moves, and the walls it cuts through stretch to follow — the plan stays connected",
  "paleta.demoleaza": "Break wall",
  "paleta.intinde": "Stretch",
  "paleta.intinde_nota": "key S",
  "editor.cota_sterge": "delete this dimension",
  "editor.unealta.cota": "Dimension",
  "editor.sfat.cota": "click one point, then another: the measure is written between them. Points snap to wall corners",
  "paleta.cota": "Dimension",
  "editor.unealta.cameraLibera": "Free-form room",
  "editor.unealta.scara": "Straight stair",
  "editor.unealta.scaraU": "U-shaped stair",
  "editor.unealta.lift": "Lift",
  "editor.unealta.ghena": "Duct shaft",
  "editor.unealta.mobila": "Furniture",
  "editor.unealta.aparat": "Electrical fitting",
  "editor.unealta.pensula_pardoseala": "Brush — floor",
  "editor.unealta.pensula_pereti": "Brush — walls",
  "editor.sfat.perete": "click and click, or press and drag, to draw a wall",
  "editor.sfat.camera_libera": "click corner to corner; click the first corner to close",
  "editor.sfat.camera": "press and drag to pull the room out, anywhere on the sheet",
  "editor.sfat.parapet": "same as a wall, but it comes out 15 cm thick and 1.00 m high",
  "editor.sfat.balcon": "drag the rectangle against the facade; the free sides close with a parapet",
  "editor.sfat.scara": "draw the first run; type the steps, then the distance between runs",
  "editor.sfat.scara_gata": "press where it starts, by a wall, and drag the way it climbs; then edit it like any stair",
  "editor.sfat.scara_gata_trage": "drag the way it climbs",
  "editor.sfat.piesa": "click where you want it; drag the corners like any other piece",
  "editor.sfat.mobila": "click where you want the piece; hold Shift on it to rotate",
  "editor.sfat.aparat": "click next to a wall — the fitting snaps to it by itself",
  "editor.sfat.pensula": "click on rooms to give them the chosen finish",
  "editor.sfat.gol": "click on a wall to put an opening in it",
  "editor.afara.titlu": "Outdoor space",
  "editor.afara.balcon": "Cantilevered balcony",
  "editor.afara.logie": "Loggia",
  "editor.afara.locuinta": "Belongs to dwelling",
  "editor.afara.nota_balcon": "outside the band: it counts neither as useful nor as built area",
  "editor.afara.nota_logie": "carved out of the volume: not useful area, but it is built area",
  "editor.esc": "Esc — back to selection",
  "editor.vedere_plan": "Plan",
  "editor.vedere_design": "Design",
  "editor.vedere_macheta": "Model",
  "editor.adauga_ax": "Add axis",
  "editor.ax_are_deja": "This wall is already on an axis",
  "editor.dubleaza": "Duplicate",
  "editor.sterge": "Delete",
  "editor.editat_la": "Edited {cand}",
  "editor.editat_la_nesalvat": "Edited {cand} · unsaved",
  "editor.intoarce_sensul": "Flip the swing",
  "editor.oglindeste_balamaua": "Mirror the hinge",
  // Short label that names the key: the selection bar fits one word, and "H"/"V"
  // are the very keys that do the same thing, so the button teaches them in
  // passing. The full wording lives in the tooltip.
  "editor.oglindeste_h": "Flip H",
  "editor.oglindeste_v": "Flip V",
  "editor.deselecteaza": "Deselect",
  // The four turns of the selected object — see `model::intoarcerile`. These
  // are tooltip labels, hence written out in full: the buttons carry icons only.
  "editor.roteste_stanga": "Rotate a quarter turn left (R)",
  "editor.roteste_dreapta": "Rotate a quarter turn right (R)",
  // WHY IT CANNOT BE DONE — written on the dimmed button, so that the absence
  // is an answer rather than a silence.
  "editor.intoarceri.camera": "a room does not turn: its walls do",
  "editor.intoarceri.anvelopa": "the module outline changes from the panel: front, band, arms",
  "editor.intoarceri.gol_rotire": "an opening turns together with the wall it sits in",
  "editor.intoarceri.fereastra": "a window has no handedness: mirrored, it is still itself",
  "editor.intoarceri.parte": "a mirrored rectangle is still itself; rotate it, or set its sizes in the panel",
  "editor.intoarceri.nucleu_gol": "the core has no part to mirror",
  "editor.intoarceri.nimic": "not available on what is selected",
  // THE BAR'S MIRROR LABELS ARE THE SCREEN'S, not the item's. The panel has its
  // own ("left-right", "front-back"), and rightly so: there one ticks the
  // item's own flags, read along its own axes. The bar's button is a MIRROR
  // held over the drawing, and at 90° the two stop being the same thing — see
  // `model::mainaOglindita`.
  "editor.intoarceri.ogl_h": "Mirror horizontally (H)",
  "editor.intoarceri.ogl_v": "Mirror vertically (V)",
  // --- room kinds, as written on the drawing --------------------------------
  "fel.camera.living": "Living room",
  "fel.camera.kitchen": "Kitchen",
  "fel.camera.bedroom": "Bedroom",
  "fel.camera.dormitor": "Bedroom",
  "fel.camera.bath": "Bathroom",
  "fel.camera.bath2": "Bathroom 2",
  "fel.camera.wc": "WC",
  "fel.camera.shower": "Shower",
  "fel.camera.hall": "Hallway",
  "fel.camera.hol": "Common corridor",
  "fel.camera.administrativ": "Administrative room",
  "fel.camera.sas": "Vestibule",
  "fel.camera.balcony": "Balcony",
  "fel.camera.store": "Storage",
  "fel.camera.ghena": "Service shaft",
  "fel.scurt.desfumare": "Smoke vent",
  "fel.camera.desfumare": "Smoke extraction zone",
  "fel.camera.tech": "Technical room",
  "fel.camera.gospodarie": "Water plant",
  "fel.camera.scara": "Staircase",
  "fel.camera.lift": "Lift",
  "fel.camera.office": "Study",
  "fel.camera.dressing": "Walk-in closet",
  "fel.camera.pantry": "Pantry",
  "fel.camera.terasa": "Terrace",
  "fel.camera.terasa_circulabila": "Roof terrace",
  "fel.camera.garage": "Garage",
  "fel.camera.boxe": "Storage units",
  "fel.camera.room": "Unknown room",
  "editor.uneste_camerele": "Merge rooms",
  "editor.mobileaza_camera": "Furnish the room",
  "editor.mobileaza_camera_titlu": "Places the furniture by the kind of room — the services already there are left alone",
  "editor.instalatia_camerei": "Room services",
  "editor.camera_fel": "Kind",
  "editor.gol_model": "Model",
  "editor.gol_model_nota": "one hinged leaf, two leaves meeting in the middle, or one leaf sliding along the wall",
  "editor.gol_rol": "Role",
  "editor.gol_intrare": "entrance door of the dwelling",
  "editor.gol_intrare_nota": "inferred: it opens from the shared corridor into a room; drawn heavier",
  "editor.gol_interioara": "interior door",
  "editor.gol_rol_scris": "role written in the document; drawn accordingly",
  "editor.gol_latime_exterioara": "Outer width",
  "editor.gol_latime_exterioara_nota": "the opening in the wall, frame to frame — the figure dimensioned on the drawing",
  "editor.gol_latime_interioara": "Inner width",
  "editor.gol_latime_interioara_nota": "the clear width you walk through: the opening minus the two frames",
  "editor.gol_toc": "Frame",
  "editor.gol_toc_nota": "frame width along the wall; written to the document and carried into the solution",
  "fel.usa.single": "single",
  "fel.usa.double": "double",
  "fel.usa.sliding": "sliding",
  "editor.camera_fel_nota": "the name on the drawing, the default floor finish and the automatic furnishing follow the kind",
  "editor.camera_nume": "Name",
  "editor.camera_nume_nota": "the word written on the drawing, when the room is not called after its kind: “Cellar”, “Workshop”. The kind above stays the one the engines read.",
  "editor.sterge_tronson": "Delete the module",
  "editor.sterge_tronson_titlu": "Delete everything the project has on this module — drafts and the published version; if a global module with the same code exists, plans fall back to it",
  "editor.sterge_tronson_global_titlu": "Global module, shared by every project — it cannot be deleted from a project. Only modules saved as new or rewritten in the project can be deleted.",
  "editor.sterge_tronson_impartit_titlu": "A module shared with you — only whoever drew it can delete it.",
  "editor.sterge_tronson_platforma_titlu": "A platform module, common to every project — deleting it removes it for everyone.",
  "editor.sterge_tronson_platforma_intrebare": "Delete PLATFORM module {cod}?\n\nIt is one of the shared ones every project starts from: it disappears for everyone, not just from your screen. Plans using it will be left without it. This cannot be undone.",
  "editor.sterge_tronson_intrebare": "Delete module {cod}?\n\nEvery project version under this code is lost — drafts and the published one alike. If a global module with the same code exists, plans fall back to it; otherwise the module is gone for good. This cannot be undone.",
  // --- the project's location and brief ----------------------------------
  "loc.dezvoltare.rezidential_colectiv": "Residential — multi-family",
  "loc.dezvoltare.rezidential_individual": "Residential — single-family",
  "loc.dezvoltare.birouri": "Offices",
  "loc.dezvoltare.comercial": "Retail",
  "loc.dezvoltare.mixt": "Mixed use",
  "loc.dezvoltare.industrial": "Industrial",
  "loc.subsol.fara": "No basement",
  "loc.subsol.unul": "With a basement",
  "loc.subsol.doua": "With two basements",
  "loc.subsol.demisol": "With a semi-basement",
  "loc.ap.doua_camere": "2-room flats",
  "loc.ap.garsoniera": "Studio",
  "loc.ap.trei_camere": "3-room flats",
  "loc.ap.patru_camere": "4-room flats",
  "loc.ap.tip_nou": "New type",
  "loc.retragere.strada": "Setback from the street",
  "loc.retragere.strada_sub": "from the building line, for buildings",
  "loc.retragere.vecini": "Setback from neighbours",
  "loc.retragere.vecini_sub": "side and rear, from the property boundary",
  "loc.retragere.intre": "Between buildings",
  "loc.retragere.intre_sub": "the distance between the blocks on the plot",
  "loc.regim.pot": "Max site coverage",
  "loc.regim.pot_sub": "ground footprint, of the plot",
  "loc.regim.cut": "Max floor area ratio",
  "loc.regim.cut_sub": "gross floor area, as a ratio",
  "loc.regim.verde": "Green space",
  "loc.regim.verde_sub": "minimum, of the plot area",
  "loc.regim.verde_sol": "Unexcavated green space",
  "loc.regim.verde_sol_sub": "on natural ground, of the green space",
  "loc.scurt.certificat_urbanism": "PC",
  "loc.scurt.cadastru": "LR",
  "loc.scurt.plan_topografic": "survey",
  "loc.scurt.studiu_geotehnic": "geo",
  "loc.scurt.solutie_arhitecturala": "arch. report",
  "loc.scurt.solutie_structurala": "str. report",
  "loc.scurt.calculat": "outline",
  "loc.lung.certificat_urbanism": "from the planning certificate",
  "loc.lung.cadastru": "from the land register",
  "loc.lung.plan_topografic": "from the topographic survey",
  "loc.lung.studiu_geotehnic": "from the geotechnical study",
  "loc.lung.solutie_arhitecturala": "from the architecture report",
  "loc.lung.solutie_structurala": "from the structure report",
  "loc.lung.calculat": "computed from the outline",
  "loc.din_acte": "from the documents",
  "loc.din_acte_scurt": "documents",
  "loc.din_scurt": "from {sursa}",
  "loc.din_fisier": "from {fisier}",
  "loc.schimbat_de_tine": "changed by you",
  "loc.implicit": "default",
  "loc.retrageri_pe_laturi": "per side, from the act:",
  "loc.expl_baza_implicit": "general rule: our default H/2 = {m} m",
  "loc.expl_baza_act": "general rule for this kind of side, {sursa}: {m} m",
  "loc.expl_portiune": "portion from the certificate, {unde}: {m} m",
  "loc.expl_manual": "drawn by hand on this side: {m} m",
  "loc.expl_verdict_manual": "the hand-drawn value applies; it beats everything",
  "loc.expl_verdict_baza": "the general rule applies: no act portion on this piece",
  "loc.expl_verdict_inlocuieste": "the act portion applies: it replaces our default",
  "loc.expl_verdict_max": "the largest of the act's requirements applies",
  "loc.punct_nord": "north", "loc.punct_sud": "south", "loc.punct_est": "east", "loc.punct_vest": "west",
  "loc.punct_nord-est": "north-east", "loc.punct_nord-vest": "north-west", "loc.punct_sud-est": "south-east", "loc.punct_sud-vest": "south-west",
  "loc.se_incarca": "loading…",
  "loc.esec_generare": "Could not start the generation.",
  "loc.marcheaza_zona_chip": "mark at least one access zone on the outline",
  "loc.fara_contur": "Draw the plot on the map first",
  "loc.fara_zona": "Mark at least one access zone on the plot outline",
  "loc.porneste_titlu": "Search for layouts on the plot you drew",
  "loc.pornesc": "starting…",
  "loc.porneste": "Start the generation",
  "loc.adresa_necunoscuta": "not known yet",
  "loc.teren_mp": "{suprafata} m² per the documents",
  "loc.teren_contur": "{suprafata} m² from the outline",
  "loc.acces.eticheta": "truck access",
  "loc.acces.A": "zone A (centre)",
  "loc.acces.B": "zone B",
  "loc.acces.niciuna": "outside Bucharest, no fee",
  "loc.acces.necunoscut": "no coordinates",
  "loc.acces.automat": "from coordinates",
  "loc.acces.de_om": "chosen by you",
  "loc.acces.la_limita": "{m} m from the zone boundary — confirm",
  "loc.acces.titlu": "The truck access fee in Bucharest is per vehicle and per day, different in zone A (the centre) and zone B and absent outside the city; the estimate prices it on the site's zone. Pick another zone if the point is not on the plot.",
  "loc.acces.revino": "back to coordinates",
  "loc.contur_nesigur": "unreliable outline — check the points",
  "loc.contur_nesigur_arii": "area from the points {contur} m², in the document {act} m²",
  "loc.contur_nesigur_taie": "the outline crosses itself",
  "loc.contur_nesigur_puncte": "vertices to check: {puncte}",
  "loc.hasura_titlu": "What is left of the plot after the setbacks — hatched on the map. It is redrawn when you change the setbacks.",
  "loc.edificabil": "buildable {suprafata} m²",
  "loc.fara_contur_text": "The documents give the address but not the outline — there is no cadastral annex or topographic survey for it to come from. Draw the plot on the map so we can go further.",
  "loc.deseneaza_terenul": "Draw the plot",
  "loc.incarca_actele": "Or upload them in ",
  "loc.dosar": "Upload documents",
  "loc.incarca_actele_rest": " — the address, the outline and the zoning figures come out of them on their own.",
  "loc.start.titlu": "Where is the plot?",
  "loc.start.sub": "Nothing in the documents says where you are building. Type the address and we put it on the map.",
  "loc.cere.titlu": "Now draw the plot",
  "loc.cere.text": "Click each corner of the plot, then close the outline. Everything from here on is measured on it.",
  "loc.cauta_adresa": "500 Market St, San Francisco, CA",
  "loc.caut": "searching…",
  "loc.cauta": "Search",
  "loc.nicio_adresa": "No address found. Try it differently — street, number, town.",
  "loc.nepotriviri#n": "one mismatch between documents|{n} mismatches between documents|{n} mismatches between documents",
  "loc.nep.folosit": "using {sursa}",
  "loc.nep.elemente#n": "one item|{n} items|{n} items",
  "loc.nep.camp.adresa": "Address",
  "loc.nep.camp.suprafata_teren_mp": "Plot area",
  "loc.nep.camp.suprafata_acte_mp": "Area per documents",
  "loc.nep.camp.suprafata_masurata_mp": "Measured area",
  "loc.nep.camp.numar_cadastral": "Cadastral number",
  "loc.nep.camp.carte_funciara": "Land register",
  "loc.nep.camp.categoria_folosinta": "Land use category",
  "loc.nep.camp.contur": "Outline",
  "loc.nep.camp.laturi": "Sides",
  "loc.nep.camp.vecinatati": "Neighbours",
  "loc.nep.camp.regim_inaltime": "Height regime",
  "loc.nep.camp.pot_max": "Maximum POT",
  "loc.nep.camp.cut_max": "Maximum CUT",
  "loc.nep.camp.beneficiar": "Beneficiary",
  "loc.salveaza_terenul": "Save the plot",
  "loc.se_salveaza_terenul": "Saving the plot's new position: sides, street fronts and access zones are being rebuilt from the OpenStreetMap data…",
  "loc.teren_salvat": "The plot is saved at its new position.",
  "loc.muta_terenul": "Move the plot",
  "loc.roteste_stanga": "Rotate 1° to the left",
  "loc.roteste_dreapta": "Rotate 1° to the right",
  "loc.renunta": "cancel",
  "loc.redeseneaza_parcela": "Redraw the plot",
  "loc.deseneaza_parcela": "Draw the plot",
  "loc.marcheaza_zone_titlu": "Mark on the outline the zones where the vehicle access can go",
  "loc.deseneaza_intai": "Draw the plot first",
  "loc.zone": "Access zones ({n})",
  "loc.marcheaza_zona": "Mark the access zone",
  "loc.cere_zona.titlu": "The documents do not show the street frontage. ",
  "loc.cere_zona.text": "Mark on the map the stretch of the outline where the vehicle access can go: two clicks on the same side.",
  "loc.zone_din_acte_sub": "street frontage read from {sursa}; you can narrow the zone on the map or add another",
  "loc.tema": "The project brief",
  "loc.tema_sub": "the required fields come with proposed values — you can go through them in 30 s",
  "loc.tip_dezvoltare": "Development type",
  "loc.regim_inaltime": "Height regime *",
  "loc.nespecificat": "— not specified —",
  "loc.subsoluri": "Basements",
  "loc.etaje": "Floors above ground",
  "loc.regim_din_acte": "{regim} (from the documents)",
  "tema.regim_pe_zone": "per zone: {lista}",
  "loc.doar_parter": "none (ground floor only)",
  "loc.etaje_n": "{n} (G+{n})",
  "loc.cerinte": "Planning requirements *",
  "loc.mp": "{suprafata} m²",
  "loc.baza_procente": "the percentages apply to {suprafata} m², the outline's area",
  "loc.baza_procente_acte": "the percentages apply to {suprafata} m² per the documents — no outline drawn",
  "loc.contur_vs_acte": "The outline measures {contur} m², the certificate says {acte} m² ({semn}{procent} %). Every calculation uses the outline; the figure in the documents may add up overlapping parcels.",
  "loc.fara_teren": "without the plot area, the percentages have nothing to give m² from",
  "loc.revino_titlu": "Discard your corrections and go back to what the documents say",
  "loc.revino": "back to the documents",
  "loc.retrageri": "Setbacks",
  "loc.metri_titlu": "metres — Enter saves, Escape cancels",
  "loc.sub_norma_titlu": "below half the reference height ({inaltime} m / 2). It is applied anyway, and the code check reports it.",
  "loc.sub_h2": "below H/2 = {metri} m",
  "loc.h2": "H/2 = {metri} m",
  "loc.din_act": "{regula} = {metri} m, as the document requires",
  "loc.regula_h": "H/{impartitor}",
  "loc.sub_act": "below {regula} = {metri} m, which the document requires",
  "loc.formula_scurt": "{raport} × H, at least {minim} m",
  "loc.editeaza_retrageri": "Edit setbacks",
  "loc.salveaza_retrageri": "Save the setbacks",
  "loc.salveaza_retrageri_titlu": "Writes the setbacks dragged on the map and leaves editing",
  "loc.retrageri_neatinse": "No setback has been dragged since editing started",
  "loc.renunta_retrageri_titlu": "Throws away the setbacks dragged since editing started and leaves it; what is stored stays untouched",
  "loc.retrageri_nesalvate": "unsaved",
  "loc.tras_desen": "drawn on the map: {metri} m",
  "loc.tras_desen_interval": "drawn on the map: between {de_la} and {pana_la} m",
  "loc.tras_desen_titlu": "Setbacks dragged by hand on the map. On the faces where they were dragged they apply instead of the requirement here; “Back to the documents” discards them.",
  "loc.revino_la_acte": "Back to the documents",
  "loc.revino_la_acte_titlu": "Empties the setbacks dragged by hand and leaves the ones from the certificate; it is written on “Save the setbacks”",
  "loc.editeaza_retrageri_titlu": "Drag each boundary's handle to change its setback; below what the document requires, the lost strip is hatched yellow",
  "loc.din_banda": "from the CU — by height",
  "loc.din_formula": "from the CU — a rule",
  "loc.din_categorii": "from the CU — by street category",
  "loc.din_regula_titlu": "The certificate gives a rule rather than a number; the figure is that rule evaluated for the chosen building.",
  "loc.aliniament_categorii": "the document requires {metri} m for the strictest street category; the category is not stated in the certificate",
  "loc.h_referinta": "Reference H: {niveluri} storeys × {hNivel} m = {total} m, at the eaves",
  "loc.fara_regim": "without a height regime, H/2 has nothing to come from",
  "loc.structura_ap": "Flat mix *",
  "loc.mp_utili": "m² usable",
  "loc.scoate_tipul": "Remove the type",
  "loc.total_gresit": "total {procent} % — it must be 100 %",
  "loc.valori_implicite": "default values",
  "loc.tip_nou": "new type",
  "loc.finisaj": "Target finish",
  "loc.finisaj_placeholder": "Standard",
  "loc.constrangeri": "Constraints from the certificate",
  "loc.necesita_pud": "requires a detailed plan",
  "loc.parcele": "Plots",
  "loc.parcele_total": "{n} · outline {suprafata} m²",
  "loc.parcele_acte": "the documents add up to {suprafata} m²",
  "loc.col_cadastral": "Cadastral no.",
  "loc.bucati": "Land parcels",
  "loc.bucati_total": "{n} cadastral records · {suprafata} m² total",
  "loc.col_adresa": "Address",
  "loc.col_acte": "Documents",
  "loc.col_masurat": "Measured",
  "loc.col_pot": "Site coverage",
  "loc.bucati_regim_diferit": "The parcels have different site coverage in their documents; the plot carries a single figure, and the buildable zone is drawn from it.",
  "loc.profil_strada": "Street profile",
  "loc.profil_strada_sub": "what the street in front of the plot looks like today; read from the orthophoto when the PUD sheet is generated, and shown on it as “existing, to be verified”",
  "loc.profil_din_ortofoto": "from orthophoto · confidence {s} %",
  "loc.profil_corectat": "written by you",
  "loc.profil_lipsa": "not read yet (read when the PUD sheet is generated)",
  "loc.profil_corecteaza": "correct",
  "loc.profil_scrie": "write the profile",
  "loc.profil_placeholder": "trotuar 2\ncarosabil 7 dublu sens\nverde 3",
  "loc.profil_feluri": "one line per band, from the plot boundary outwards: kind and width in metres, and on a carriageway «sens unic» (one-way) or «dublu sens» (two-way); kinds: {feluri}",
  "loc.profil_rand_neinteles": "cannot read the line “{rand}”",
  "loc.profil_salveaza": "Save the profile",
  "loc.profil_revino": "back to the reading",
  "loc.profil_revino_titlu": "discard what you wrote and bring back the profile read from the orthophoto",
  "loc.profil_capat": "opposite side: {ce}",
  "loc.banda_trotuar": "sidewalk",
  "loc.banda_verde": "green",
  "loc.banda_carosabil": "carriageway",
  "loc.banda_parcare_paralela": "parallel parking",
  "loc.banda_parcare_perpendiculara": "90° parking",
  "loc.banda_pista_biciclete": "cycle lane",
  "loc.banda_tramvai": "tram",
  "loc.zone_regim": "Zoning areas",
  "loc.zone_regim_sub": "the plot falls in {n} zoning units, each with its own regime",
  "loc.zona_avertisment": "The site plan runs on the whole plot with the regime of zone {zona}; zones are not part of generation yet.",
  "loc.zona_activa_chip": "used for generation",
  "loc.zona_foloseste": "use for generation",
  "loc.zona_foloseste_titlu": "The site plan will start from this zone's regime on the whole plot",
  "loc.zona_arie": "{suprafata} m² · {procent} % of the plot",
  "loc.zona_arie_acte": "{suprafata} m² in the documents",
  "loc.zona_limita": "Zone boundary",
  "loc.zona_limita_act_fasie": "from the document: a {m} m strip from the street",
  "loc.zona_limita_act_suprafata": "from the document: {suprafata} m² from the street",
  "loc.zona_limita_rest": "the rest of the plot",
  "loc.zona_limita_manual": "drawn by you",
  "loc.zona_limita_plansa_astra": "read from the drawing",
  "loc.zona_limita_lipsa": "unknown from the documents",
  "loc.traseaza_limita": "Draw the boundary",
  "loc.traseaza_limita_titlu": "Drag on the map the line between this zone and the rest of the plot",
  "loc.salveaza_limita": "Save the boundary",
  "loc.inverseaza_partea": "Swap sides",
  "loc.renunta_limita_titlu": "Leave the boundary as it was; nothing is written",
  "loc.limita_sub": "Drag the ends of the line along the outline; the coloured part is zone {zona}.",
  "loc.pozitie_text": "in the document: “{text}”",
  "loc.drumuri": "Road from the documents",
  "loc.cale_acces": "Access way",
  "loc.drumuri_sub": "not counted in the plot area",
  "loc.col_artera": "Street",
  "loc.col_gabarit": "Length × width",
  "loc.gabarit": "{lungime} × {latime} m",
  "loc.drum_de_ce": "Recognised as a road: {motiv}",
  "loc.drum_retragere": "Setback from the alignment, on the sides touching the road",
  "loc.drum_nelipit": "The road is {metri} m from the plot, so it gives no street frontage — the land between belongs to someone else.",
  "loc.metri": "{metri} m",
  "loc.din_ce_s_a_citit": "Additional information",
  "loc.fara_acte_citite": "No uploaded documents",

  "loc.detalii_teren": "The plot",
  "loc.detalii_teren_sub": "what the certificate allows on it — change any figure that was read wrong",
  "loc.la_tema": "Continue to Project brief",
  "loc.la_tema_titlu": "Next step: what gets built on this plot",

  "tema.spune.titlu": "Tell us what you want to build",
  "tema.spune.placeholder": "A 42-apartment building over an underground garage, retail on the ground floor, and a courtyard between the two wings.",
  "tema.spune.nota_imagini": "You can also say what you like in the facade images: they are numbered as in the tray of the Facade step — «the balconies of the first, the colour of the second». The facade agent reads it from here.",
  "tema.fara_abonament": "not in your subscription",
  "tema.marime.titlu": "Number of floors",
  "tema.preset": "proposed",
  "tema.din_cu": "The certificate allows {regim}.",
  "tema.peste_cu": "above the {regim} the certificate allows",
  "tema.fara_cu": "No height regime was read from the documents — the proposal is ten floors.",
  "tema.terase_retrase": "Set-back terraces on the top floor (free ends step back to the first axis; the terrace leaves the CUT and is recovered in footprint)",
  "tema.inaltimi_diferite": "Stepped floors within a building (pieces from the regime down to regime − 3, at expansion joints)",
  "tema.corpuri_diferite": "Buildings of different heights (from the minimum regime up to the chosen one)",
  "tema.regim_minim": "Minimum regime",
  "tema.spatii_comerciale": "Commercial (service) ground floors — the chosen buildings get a 5 m commercial ground floor at pavement level, behind a curtain wall",
  "tema.procent_comercial": "Share of the permitted built area (%)",
  "tema.functiuni": "Buildings of another use on the plot, as targets of built area: placed before the dwellings, with 5 m storeys, as tall as allowed, no internal partitioning",
  "tema.functiune.scoala": "School (up to 3 storeys)",
  "tema.functiune.birouri": "Office building (as tall as the height regime allows)",
  "tema.functiune.comercial": "Standalone commercial building (up to 2 storeys, on the street)",
  "tema.functiune.adc_m2": "Target gross floor area (m²)",
  "tema.functiune.fara_m2": "Enter the target floor area for: {lista}. A ticked box without a figure builds nothing.",
  "tema.calitate.titlu": "Build standard",
  "tema.calitate.arata": "Custom settings",
  "tema.calitate.ascunde": "Hide custom settings",
  "tema.calitate.aparte": "custom",
  "tema.calitate.pe_capitole#n": "{n} custom setting|{n} custom settings|{n} custom settings",
  "tema.nivel.buget": "Low budget",
  "tema.nivel.buget_sub": "Economical solutions, basic finishes, nothing beyond what the code asks for.",
  "tema.nivel.standard": "Standard",
  "tema.nivel.standard_sub": "The usual market level, off-the-shelf materials.",
  "tema.nivel.premium": "Premium",
  "tema.nivel.premium_sub": "Better materials, insulation and ventilation above code.",
  "tema.nivel.lux": "Luxury",
  "tema.nivel.lux_sub": "No compromise: selected materials, complete systems.",
  "tema.cap.compartimentare": "Partitioning",
  "tema.cap.finisaje": "Finishes",
  "tema.cap.hvac": "HVAC",
  "tema.cap.sanitare": "Plumbing",
  "tema.cap.electrice": "Electrical",
  "tema.fara_parcela": "draw the plot in Plot details",
  "tema.fara_zona": "mark an access zone in Plot details",
  "tema.la_fatada": "Continue to Facade",
  "tema.la_volumetrie": "Continue to Massing",
  "tema.la_volumetrie_titlu": "Next step: how the buildings sit on the plot",
  // The local market on the brief (`pages/PiataPeBrief.tsx`): the research
  // status and the configuration recommended by the sales around the plot.
  "tema.piata.titlu": "The market around the plot",
  "tema.piata.la_rand": "The search for flats for sale around the plot is queued behind another one and starts when that one ends.",
  "tema.piata.cat_min": "~{n} min",
  "tema.piata.cat_minim": "at least ~{n} min",
  "tema.piata.cat_necunoscut": "a few minutes",
  "tema.piata.pe": "from {oferte} around the plot, {data}",
  "tema.piata.foloseste_t": "Sets the brief's flat percentages and areas from the study; the build standard stays as it is. You can change them any time.",
  "tema.piata.fara_cautare": "The sales around the plot haven't been searched yet.",
  "tema.piata.cauta": "Search now",
  "tema.piata.eroare": "The search for sales around the plot failed.",
  "tema.piata.reincearca": "Try again",

  // --- Massing (step 3½: the volume sketch on the plot) -------------------
  "vol.titlu": "How do the buildings sit on the plot?",
  "vol.sub": "Let the engine propose, or draw rough volumes yourself. The sketch is an inspiration: the engine takes its orientations, building count, shapes and positions, but optimises for CUT.",
  "vol.mod.motor": "Let the engine propose",
  "vol.mod.motor_sub": "As before: the three solutions come out of the search, with no guidance.",
  "vol.mod.desen": "I draw the massing",
  "vol.mod.desen_sub": "The first solution will be the closest to the sketch, the second the best by CUT, the third a different one.",
  "vol.paleta": "Add",
  "vol.forma.bara": "Bar",
  "vol.forma.L": "L",
  "vol.forma.U": "U",
  "vol.forma.C": "C",
  "vol.sugestii": "Suggestions",
  "vol.sugestii_titlu": "The engine proposes a few layouts; pick one and adjust it",
  "vol.sugestii_caut": "looking for layouts…",
  "vol.sugestii_nimic": "no layout came out",
  "vol.sugestii_in": "{n} layouts in {s} s",
  "vol.sterge": "Delete the volume",
  "vol.sterge_tot": "Clear all",
  "vol.anuleaza": "Undo",
  "vol.refa": "Redo",
  "vol.oglindeste": "Wings on the other side",
  "vol.capat": "Wing at the other end",
  "vol.estimat": "estimated",
  "vol.estimat_titlu": "Figures estimated from the drawn volumes with the engine's rules. Composition from modules and the real terraces can change the result; the solutions should not come out far below them.",
  "vol.amprenta": "Footprint",
  "vol.pot": "POT",
  "vol.cut": "CUT",
  "vol.din_max": "of {max}",
  "vol.regim": "{n} storeys · required distance between bodies {gap} m",
  "vol.in_afara": "{n} volumes leave the buildable zone",
  "vol.prea_aproape": "{n} pairs closer than the required distance",
  "vol.fara_contur": "Draw the plot in Plot details before the massing.",
  "vol.fara_acces": "Mark an access zone in Plot details before the massing.",
  "vol.gol": "No volume yet: add one from the palette or ask for suggestions.",
  "vol.estimare_eroare": "The estimate failed: {motiv}",
  "vol.vecin": "neighbour",
  "vol.limita": "boundary",
  "vol.la_fatada": "Continue to Facade",
  "vol.legenda_zona": "buildable zone",
  "vol.legenda_halo": "required distance around the volume",
  "vol.incadreaza": "Fit",
  "vol.incadreaza_titlu": "Whole plot back in view (or double-click the background)",
  "tema.la_fatada_titlu": "Next step: how the building looks",

  // --- Fațada (pasul 4) ---------------------------------------------------
  "fat.titlu": "What should the building look like?",
  "fat.sub": "Pick one to three facades you like, even from different styles. We'll prepare up to three variants based on them.",
  "fat.sistem.titlu": "Facade system",
  "fat.sistem.sub": "It sets the cost and the thickness, not the style.",
  "fat.sistem.din_standard": "from the building standard",
  "fat.sistem.cost": "relative cost",
  "fat.sistem.etics": "Insulated render",
  "fat.sistem.etics_sub": "EPS insulation with decorative render and colour accents on the volumes.",
  "fat.sistem.etics_placari": "Render + cladding",
  "fat.sistem.etics_placari_sub": "Mostly render, with cladding at the ground floor, entrances and loggias.",
  "fat.sistem.ventilata": "Ventilated facade",
  "fat.sistem.ventilata_sub": "Cladding on a subframe, mineral wool, aluminium windows.",
  "fat.alege.titlu": "Facades you like",
  "fat.alege.max": "one, two or three",
  "fat.tab.recomandate": "Recommended",
  "fat.tab.stiluri": "By style",
  "fat.tab.toate": "All images",
  "fat.recomandate.sub": "Six directions that look good on almost any building.",
  "fat.alege_pentru_mine": "Choose for me",
  "fat.incarca": "Upload your photo",
  "fat.incarca_sub": "A facade you saw on the street or online. JPG or PNG; you can drop it here.",
  "fat.incarca_eroare": "That file isn't an image. Upload a JPG or a PNG.",
  "fat.toate_stilurile": "All styles",
  "fat.stil.vezi#n": "See the image|See all {n}|See all {n}",
  "fat.stil.alege": "Choose this style",
  "fat.stil.ales": "Chosen",
  "fat.mai_multe#n": "one more|{n} more|{n} more",
  "fat.mareste": "Enlarge",
  "fat.tava.titlu": "Your picks",
  "fat.tava.gol": "No facade picked yet. If you start now, we'll pick three directions for you.",
  "fat.tava.un_stil": "All variants will share the {stil} style.",
  "fat.tava.o_poza": "All variants will start from your photo.",
  "fat.tava.mai_multe": "You'll get variants based on each pick: {lista}.",
  "fat.tava.poza_ta": "your photo",
  "fat.tava.plin": "You can pick up to three. Remove one to add another.",
  "fat.tava.scoate": "Remove",
  "fat.tava.slot_gol": "Pick from the images",
  "fat.tava.scoasa": "removed",
  "fat.tava.scoasa_titlu": "This image was removed from the catalog. Remove it from your picks and choose another.",
  "fat.lupa.alege": "Pick this facade",
  "fat.lupa.aleasa": "Picked · {n} of 3",
  "fat.lupa.plin": "You already picked three",
  "fat.lupa.inchide": "Close",
  "fat.lupa.inainte": "Next image",
  "fat.lupa.inapoi": "Previous image",
  "fat.lupa.foto": "Photo:",
  "fat.lupa.ai": "AI-generated illustration: this building does not exist.",
  "fat.ai_nota": "The catalog images are AI-generated illustrations: buildings that do not exist, shown only as style directions.",
  "fat.lupa.poza_ta": "Your photo",
  "fat.lupa.poza_ta_sub": "It stays in the project, visible only to its team.",
  "fat.tag.tencuiala": "render",
  "fat.tag.caramida": "brick",
  "fat.tag.piatra": "stone",
  "fat.tag.beton": "exposed concrete",
  "fat.tag.sticla": "glass",
  "fat.tag.metal": "metal cladding",
  "fat.tag.lemn": "timber",
  "fat.tag.ceramica": "ceramics",
  "fat.tag.fibrociment": "fibre cement",
  "fat.tag.balcoane": "balconies",
  "fat.tag.loggii": "loggias",
  "fat.tag.terase": "terraces",
  "fat.tag.retrageri": "setbacks",
  "fat.tag.console": "cantilevered volumes",
  "fat.tag.bow_window": "bay windows",
  "fat.tag.lamele": "vertical fins",
  "fat.tag.brise_soleil": "sunshades",
  "fat.tag.grila": "grid",
  "fat.tag.rame": "window surrounds",
  "fat.tag.benzi": "horizontal bands",
  "fat.tag.perforatii": "perforated screens",
  "fat.tag.vegetatie": "planting",
  "fat.tag.curbe": "curves",
  "fat.tag.arcade": "arcades",
  "fat.tag.cornisa": "cornice",
  "fat.tag.soclu": "plinth",
  "fat.tag.ferestre_mari": "floor-to-ceiling glass",
  "fat.tag.ferestre_punctuale": "punched windows",
  "fat.tag.culoare": "colour",
  "fat.stil.contemporary_urban": "Contemporary Urban",
  "fat.stil.contemporary_urban.desc": "Clear volumes, a steady rhythm of windows and balconies, a neutral palette.",
  "fat.stil.contemporary_urban.mat": "render · anthracite accents · glass balustrades",
  "fat.stil.modern_premium": "Modern Premium",
  "fat.stil.modern_premium.desc": "Generous glazing, slim profiles, bronze accents, a distinct ground floor.",
  "fat.stil.modern_premium.mat": "glass · bronze metal · large-format ceramics",
  "fat.stil.minimalist": "Minimalist",
  "fat.stil.minimalist.desc": "Very clean volumes, few materials, large calm surfaces.",
  "fat.stil.minimalist.mat": "white render · deep openings · hidden details",
  "fat.stil.contemporary_classic": "Contemporary Classic",
  "fat.stil.contemporary_classic.desc": "Base, body and crown, vertical rhythm, simplified window surrounds.",
  "fat.stil.contemporary_classic.mat": "stone · ivory and greige · metal accents",
  "fat.stil.industrial": "Industrial / Urban Loft",
  "fat.stil.industrial.desc": "Exposed brick, black metal, generous openings, visible structure.",
  "fat.stil.industrial.mat": "brick · black metal · concrete",
  "fat.stil.organic": "Organic / Soft",
  "fat.stil.organic.desc": "Curved lines, rounded corners, flowing balconies, a sculptural volume.",
  "fat.stil.organic.mat": "curved balconies · glass balustrades · white",
  "fat.stil.biophilic": "Biophilic",
  "fat.stil.biophilic.desc": "Planting is part of the facade: built-in planters and green terraces.",
  "fat.stil.biophilic.mat": "planters · green terraces · timber",
  "fat.stil.scandinavian": "Scandinavian / Timber",
  "fat.stil.scandinavian.desc": "Light tones, timber, generous windows, quiet details.",
  "fat.stil.scandinavian.mat": "timber · light tones · large windows",
  "fat.stil.high_tech": "High-Tech",
  "fat.stil.high_tech.desc": "Glass, aluminium, fins and perforations, parametric geometry.",
  "fat.stil.high_tech.mat": "glass · aluminium · fins",
  "fat.stil.moft": "Statement facades",
  "fat.stil.moft.desc": "Balconies and volumes you notice from afar: staggered cantilevers, colour, play.",
  "fat.stil.moft.mat": "cantilevers · colour · free geometry",
  "fat.stil.clasic_istoric": "Historic classic",
  "fat.stil.clasic_istoric.desc": "The classic European apartment block: stone, wrought-iron balconies, mansard roof.",
  "fat.stil.clasic_istoric.mat": "stone · wrought iron · mansard",
  "fat.stil.caramida_nordica": "Nordic brick",
  "fat.stil.caramida_nordica.desc": "Brick over the whole volume, simple openings, light metal balconies.",
  "fat.stil.caramida_nordica.mat": "brick · metal balconies · simple openings",
  "fat.stil.mediteranean": "Mediterranean",
  "fat.stil.mediteranean.desc": "Mostly white, deep terraces, shade and greenery.",
  "fat.stil.mediteranean.mat": "white · deep terraces · shade",
  "fat.stil.art_deco": "Art Deco / Streamline",
  "fat.stil.art_deco.desc": "Rounded corners, horizontal bands, curved balconies, warm colours.",
  "fat.stil.art_deco.mat": "rounded corners · horizontal bands · pastels",
  "fat.stil.ceramica": "Ceramic tiles",
  "fat.stil.ceramica.desc": "Ceramic tiles over the whole facade: colour and texture that last.",
  "fat.stil.ceramica.mat": "ceramic tiles · colour · texture",
  "fat.stil.brutalist": "Exposed concrete",
  "fat.stil.brutalist.desc": "Concrete left bare, repeated modules, a strong volume.",
  "fat.stil.brutalist.mat": "exposed concrete · modules · mass",

  // --- why there is no buildable area (see `edificabil.ts`) --------------
  "edificabil.fara_contur": "the plot outline has not been drawn",
  "edificabil.fara_retrageri": "no setback, and no height regime for one to come from",
  "edificabil.retrageri_zero": "setbacks of zero — the area is the whole plot",
  "edificabil.acoperit": "the setbacks cover the whole plot",
  // --- the map: drawing the plot and the entrances -----------------------
  "harta.strada": "street",
  "harta.zona_exact": "existing access",
  "harta.zona_din": "access zone · from {sursa}",
  "harta.sursa_osm": "the OpenStreetMap map",
  "harta.zona_manual": "access zone · marked by you",
  "harta.zona_regim": "{nume} · {suprafata} m²",
  "harta.metri": "{metri} m",
  "harta.esec": "The map could not be set up.",
  "harta.inchide_conturul": "click to close the outline",
  "harta.trage_coltul": "drag it; right-click removes it",
  "harta.adauga_colt": "drag to add a corner",
  "harta.zona": "right-click removes it",
  "harta.trage_ma": "drag me",
  "harta.trage_terenul": "drag the pin or the outline",
  "harta.roteste": "rotate",
  "harta.retragere_din_act": "setback the act requires on this portion:",
  "harta.desenezi": "Drawing the plot",
  "harta.ajustezi": "Adjusting the plot",
  "harta.instructiuni_desen_1": "Click on the map for each corner. ",
  "harta.instructiuni_desen_2": "Click the first corner",
  "harta.instructiuni_desen_3": ", or double-click, to close the outline.",
  "harta.instructiuni_ajustare_1": "Drag the corners to move them. Drag a ",
  "harta.instructiuni_ajustare_2": "midpoint",
  "harta.instructiuni_ajustare_3": " to add a new corner. ",
  "harta.instructiuni_ajustare_4": "Right-click",
  "harta.instructiuni_ajustare_5": " a corner to remove it.",
  "harta.colturi#n": " {n} corner| {n} corners| {n} corners",
  "harta.arie": "{suprafata} m²",
  "harta.se_potriveste": "matches the documents",
  "harta.actele_spun": "the documents say {suprafata} m²",
  "harta.diferenta": " ({semn}{procent} %)",
  "harta.fundal_desen": "OpenStreetMap background while you draw — property boundaries cannot be traced on Google tiles.",
  "harta.se_taie": "the outline crosses itself — the area means nothing",
  "harta.se_taie_scurt": "the outline crosses itself",
  "harta.sterge_coltul": "delete the last corner",
  "harta.de_la_zero": "Draw the plot",
  "harta.gata": "done",
  "harta.marchezi_zone": "Marking the access zones",
  "harta.instructiuni_zone_1": "Two clicks on the same side",
  "harta.instructiuni_zone_2": " of the outline delimit the zone where the vehicle access can go. Remove a misplaced zone with × in the list or by right-clicking it.",
  "harta.scoate_zona": "remove zone",
  "harta.renunta_capat": "drop this end",
  "harta.capat_pus": "first end placed — click the same side for the second",
  "harta.zona_departe": "click right on the plot outline — the zone end snaps to the property line, not to the road axis",
  "harta.zona_alta_latura": "the second end must be on the same side as the first",
  "harta.zona_scurta": "the zone is shorter than {metri} m — a two-lane access plus the pedestrian platform does not fit",
  "harta.fara_zona": "nothing can be generated without an access zone",
  "harta.fundal_zone": "OpenStreetMap background while you mark — the zone goes into the site plan, like the outline.",
  // --- the lineage edge legend --------------------------------------------
  // --- the editor palette: groups, families and object catalogues ---
  "mobilier.scara": "Straight stair",
  "mobilier.scaraU": "U-shaped stair",
  "mobilier.lift": "Elevator",
  "mobilier.ghena": "Duct shaft",
  "mobilier.canapea": "Sofa",
  "mobilier.canapea_colt": "Corner sofa",
  "mobilier.fotoliu": "Armchair",
  "mobilier.scaun": "Chair",
  "mobilier.taburet": "Stool",
  "mobilier.pat": "Double bed",
  "mobilier.pat_o_persoana": "Single bed",
  "mobilier.noptiera": "Bedside table",
  "mobilier.dulap": "Wardrobe",
  "mobilier.masa": "Table",
  "mobilier.masa_scaune": "Table with chairs",
  "mobilier.masa_rotunda": "Round table",
  "mobilier.masuta_cafea": "Coffee table",
  "mobilier.masuta": "Side table",
  "mobilier.birou": "Desk",
  "mobilier.biblioteca": "Bookcase",
  "mobilier.raft": "Open shelving",
  "mobilier.comoda_tv": "TV unit",
  "mobilier.tv": "Television",
  "mobilier.insula_bucatarie": "Custom kitchen island",
  "mobilier.insula_chiuveta": "Kitchen island with sink",
  "mobilier.corp_bucatarie": "Base cabinet",
  "mobilier.corp_sertare": "Drawer unit",
  "mobilier.corp_suspendat": "Wall cabinet",
  "mobilier.aragaz": "Cooker",
  "mobilier.cuptor": "Oven",
  "mobilier.hota": "Cooker hood",
  "mobilier.frigider": "Fridge",
  "mobilier.chiuveta": "Kitchen sink",
  "mobilier.masina_vase": "Dishwasher",
  "mobilier.masina_spalat": "Washing machine",
  "mobilier.cada": "Bathtub",
  "mobilier.cada_ovala": "Oval bathtub",
  "mobilier.dus": "Shower cubicle",
  "mobilier.dus_deschis": "Walk-in shower",
  "mobilier.wc": "Toilet",
  "mobilier.wc_suspendat": "Wall-hung toilet",
  "mobilier.lavoar": "Washbasin",
  "mobilier.lavoar_dublu": "Double washbasin",
  "mobilier.sezlong": "Sun lounger",
  "mobilier.fotoliu_exterior": "Outdoor armchair",
  "mobilier.canapea_exterior": "Outdoor sofa",
  "mobilier.masa_exterior": "Outdoor table",
  "mobilier.masuta_exterior": "Round side table",
  "mobilier.umbrela": "Parasol",
  "mobilier.planta_terasa": "Terrace plant",
  "mobilier.wc_clasic": "Toilet (sheet symbol)",
  "mobilier.aragaz_clasic": "Cooker (sheet symbol)",
  "mobilier.frigider_clasic": "Fridge (sheet symbol)",
  "mobilier.masina_vase_clasic": "Dishwasher (plan symbol)",
  "mobilier.covor": "Rug",
  "mobilier.covor_rotund": "Round rug",
  "mobilier.veioza": "Table lamp",
  "mobilier.planta": "Plant",
  "mobilier.planta_mica": "Small plant",
  "aparat.priza": "Schuko socket",
  "aparat.priza_simpla": "Single socket",
  "aparat.priza_date": "Data outlet",
  "aparat.priza_dubla": "Double socket",
  "aparat.priza_blat": "Worktop socket",
  "aparat.priza_forta": "Power socket",
  "aparat.priza_tv": "TV outlet",
  "aparat.interfon": "Intercom",
  "aparat.sonerie": "Doorbell",
  "aparat.termostat": "Thermostat",
  "aparat.intrerupator": "Single switch",
  "aparat.intrerupator_dublu": "Two-gang switch",
  "aparat.comutator": "Two-way / intermediate switch",
  "aparat.intrerupator_general": "Master switch",
  "aparat.banda_led": "LED strip",
  "aparat.plafoniera": "Ceiling light",
  "aparat.spot": "Downlight",
  "aparat.aplica": "Wall light",
  "aparat.tablou": "Consumer unit",
  "aparat.doza": "Mounting box",
  "aparat.calorifer": "Radiator",
  "aparat.port_prosop": "Towel rail",
  "aparat.incalzire_pardoseala": "Underfloor heating",
  "aparat.distribuitor_incalzire": "Heating manifold",
  "aparat.grila_extractie": "Extract grille",
  "aparat.grila_introducere": "Supply grille",
  "aparat.hota": "Cooker hood",
  "aparat.unitate_ventilare": "Ventilation unit",
  "circuit.lumina": "Lighting",
  "circuit.prize": "Sockets",
  "circuit.forta": "Power",
  "circuit.date": "Low voltage",
  "categorie.sezut": "Seating",
  "categorie.dormit": "Sleeping",
  "categorie.mese": "Tables and storage",
  "categorie.bucatarie": "Kitchen",
  "categorie.electrocasnice": "Appliances",
  "categorie.sanitare": "Sanitary",
  "categorie.decor": "Lighting and decor",
  "categorie.biblioteca": "Library",
  "paleta.grup.camera": "Create room",
  "paleta.grup.mobilare": "Furnishing",
  "paleta.grup.exterior": "Outdoor furniture",
  "paleta.fam.sezlong": "Sun lounger",
  "paleta.fam.sezut_ext": "Outdoor seating",
  "paleta.fam.masa_ext": "Outdoor tables",
  "paleta.fam.umbrela": "Parasol and plants",
  "paleta.grup.electrocasnice": "Appliances",
  "paleta.grup.goluri": "Doors and windows",
  "paleta.grup.sanitare": "Plumbing fixtures",
  "paleta.grup.electrice": "Electrical fittings",
  "paleta.grup.incalzire": "Heating",
  "paleta.grup.ventilatie": "Ventilation",
  "paleta.fam.corp_incalzire": "Heat emitters",
  "paleta.fam.pardoseala_calda": "Underfloor heating",
  "paleta.fam.guri_aer": "Air grilles",
  "paleta.fam.unitate_ventilare": "Ventilation unit",
  "paleta.grup.structura": "Structure",
  "paleta.perete": "Straight wall",
  "paleta.camera": "Room",
  "paleta.camera_dreptunghi": "Rectangular room",
  "paleta.camera_l": "L-shaped room",
  "paleta.camera_t": "T-shaped room",
  "paleta.camera_libera": "Free-form room",
  "paleta.usa": "Single door",
  "paleta.usa_dubla": "Double door",
  "paleta.usa_glisanta": "Sliding door",
  "paleta.fereastra": "Window",
  "paleta.scara": "Straight stair",
  "paleta.scara_u": "U-shaped stair",
  "paleta.scara_custom": "Custom stair",
  "paleta.scara_gata_dreapta": "Straight stair",
  "paleta.scara_gata_dreapta_podest": "Straight with landing",
  "paleta.scara_gata_u": "U-shaped",
  "paleta.scara_gata_u_doua": "U with two landings",
  "paleta.lift": "Elevator",
  "paleta.ghena": "Duct shaft",
  "paleta.parapet": "Railing",
  "paleta.balcon": "Balcony",
  "paleta.fam.canapea": "Sofa",
  "paleta.fam.pat": "Bed",
  "paleta.fam.masa": "Table",
  "paleta.fam.depozitare": "Wardrobe",
  "paleta.fam.bucatarie": "Kitchen",
  "paleta.fam.decor": "Rugs and plants",
  "paleta.fam.insula": "Custom kitchen island",
  "paleta.fam.frigider": "Fridge",
  "paleta.fam.aragaz": "Cooker",
  "paleta.fam.hota": "Cooker hood",
  "paleta.fam.masina": "Washing machine",
  "paleta.fam.wc": "Toilet",
  "paleta.fam.lavoar": "Washbasin",
  "paleta.fam.cada": "Bath and shower",
  "paleta.fam.priza": "Socket",
  "paleta.fam.intrerupator": "Switch",
  "paleta.fam.iluminat": "Light fitting",
  "paleta.fam.curenti_slabi": "Low voltage",
  "paleta.fam.tablou": "Consumer unit",
  "paleta.fam.doza": "Mounting box",
  "paleta.strange": "collapse",
  "paleta.strange_titlu": "Collapse the palette",
  "paleta.desfa_titlu": "Expand the palette",
  "prop.electrice.titlu": "Electrical services",
  "prop.electrice.gol": "Nothing in this room. “Lay the services” runs them by the usual practice, starting from the furniture that is in it now.",
  "prop.electrice.alege": "Select the fitting — its properties appear below",
  "prop.electrice.sterge": "Delete the fitting",
  "prop.electrice.adauga": "Add",
  "prop.electrice.pune": "Lay the room's services",
  "prop.electrice.pune_titlu": "Places the sockets, the switch and the light fitting by the wand's rules — in this room only, the rest of the module stays as it is",
  "prop.electrice.urmeaza": "follows the {piesa}",
  "prop.electrice.doza#n": "1 module|{n} modules|{n} modules",
  "prop.doza.sect": "Mounting box",
  "prop.doza.module": "{n} mod",
  "prop.doza.adauga": "Add",
  "prop.doza.scoate": "Take out of the box",
  "prop.doza.total#n": "1 module|{n} modules|{n} modules",
  "prop.doza.rama": "Frame",
  "prop.indice": "Index",
  "prop.indice.comanda_corpuri#n": "controls 1 light|controls {n} lights|controls {n} lights",
  "prop.indice.comanda_prize#n": "1 socket|{n} sockets|{n} sockets",
  "prop.indice.comandat#n": "switched by 1 switch|switched by {n} switches|switched by {n} switches",
  "prop.indice.general": "controls the whole dwelling",
  "prop.doza.capace#n": "1 blank cover|{n} blank covers|{n} blank covers",
  "editor.intoarceri.doza": "the wall decides how a box stands",
  "editor.intoarceri.banda": "the strip follows its wall",
  "editor.intoarceri.simbol_pe_zid": "the symbol stands on its wall: it does not turn over",
  "editor.intoarceri.priza_fara_mana": "a socket has no handedness: mirrored, it is still itself",
  "editor.intoarceri.simetric": "the symbol is symmetric: mirrored, it is still itself",
  "prop.doza.gol": "The box is empty — add sockets or switches",
  "prop.doza.circuit_aparate": "By its devices",
  "prop.aparat.urmeaza": "Follows",
  "prop.aparat.urmeaza_nota": "sits {parte} the piece and moves with it",
  "prop.aparat.parte_stanga": "to the left of",
  "prop.aparat.parte_dreapta": "to the right of",
  "prop.aparat.parte_spate": "behind",
  "prop.aparat.dezleaga": "Unlink",
  "prop.aparat.dezleaga_titlu": "It stays where it is, but the piece no longer carries it",

  // --- finishes, fitting groups and the Design palette ---
  "finisaj.parchet_standard": "Standard parquet",
  "finisaj.parchet_stejar": "Oak parquet",
  "finisaj.parchet_nuc": "Walnut parquet",
  "finisaj.parchet_brad": "Herringbone parquet",
  "finisaj.punte": "Timber decking (outdoor)",
  "finisaj.dale_mari": "Tiles 60 × 60",
  "finisaj.marmura": "Marble",
  "finisaj.gresie_baie": "Floor tiles 20 × 20",
  "finisaj.gresie_bucatarie": "Floor tiles 30 × 30",
  "finisaj.beton": "Polished concrete",
  "finisaj.mocheta": "Carpet",
  "finisaj.vopsea_alba": "White paint",
  "finisaj.vopsea_calda": "Warm paint",
  "finisaj.vopsea_gri": "Grey paint",
  "finisaj.vopsea_salvie": "Sage paint",
  "finisaj.vopsea_indigo": "Indigo paint",
  "finisaj.faianta": "Wall tiles 20 × 20",
  "finisaj.faianta_mare": "Wall tiles 30 × 60",
  "finisaj.caramida": "Exposed brick",
  "finisaj.lambriu": "Timber panelling",
  "grupa.prize": "Sockets",
  "grupa.comenzi": "Switches",
  "grupa.iluminat": "Lighting",
  "grupa.tablou": "Consumer unit",
  "design.bagheta_mobilier": "Auto-furnish",
  "design.bagheta_mobilier_sub": "furniture, by what each room is for",
  "design.bagheta_mobilier_titlu": "Place furniture the way a flat is normally laid out — the existing wiring is left alone",
  "design.bagheta_electric": "Auto-wire",
  "design.bagheta_electric_sub": "sockets and switches, next to the furniture already placed",
  "design.bagheta_electric_titlu": "Run the wiring over the furniture that is already there — the furniture is left alone",
  "editor.bagheta_intrebare_mobilier": "The module already has {piese} pieces of furniture.\n\nOK — replace them with the automatic layout.\nCancel — change nothing.",
  "editor.bagheta_intrebare_electric": "The module already has {aparate} electrical devices.\n\nOK — replace them with the automatic wiring.\nCancel — change nothing.",
  "design.bagheta": "Lay out automatically",
  "design.bagheta_sub": "furniture and services, by the kind of each room",
  "design.bagheta_titlu": "Furnishes and runs the services by ordinary housing practice — then move whatever you do not like",
  "design.selecteaza": "Select",
  "design.vopseste": "Paint",
  "design.mobilier": "Furniture",
  "design.finisaje": "Finishes",
  "design.electrice": "Electrical",
  "design.pardoseala": "Floor",
  "design.pereti": "Walls",
  "design.circuite": "Circuits",
  "design.la_placa": "at slab level",
  "design.de_la_pardoseala": "{n} cm above the floor",
  "design.sub_placa": "below the slab",
  "design.peste_pardoseala": "above the floor",
  "design.modul_finisaj": "{n} cm module",

  "prop.aparat.sect": "Fitting",
  "prop.aparat.fel": "Kind",
  "prop.aparat.inaltime": "Mounting height",
  "prop.aparat.inaltime_placa": "follows the slab ({n} m)",
  "prop.aparat.inaltime_fel": "the kind's usual height",
  "prop.aparat.cota_bifa": "The kind's usual height",
  "prop.aparat.rotire": "Rotation",
  "prop.aparat.rotire_nota": "it snaps to the wall when placed; Shift on it turns it",
  "prop.circuit.sect": "Circuit",
  "prop.circuit.pe": "On circuit",
  "prop.circuit.conductor": "Conductor",
  "prop.circuit.firul": "The cable runs",
  "prop.circuit.firul_nota": "between them the wall stays free of cables — that is where a nail goes",
  "prop.circuit.sus": "30 cm below the slab",
  "prop.circuit.jos": "30 cm above the floor",
  // --- the module editor · the rest of the properties panel --------------
  "prop.sect.marimi": "Dimensions",
  "prop.sect.atribute": "Attributes",
  "prop.sect.pozitie": "Position",
  "prop.sect.portanta": "Load bearing",
  "prop.sect.piesa": "Item",
  "prop.sect.finisaje": "Finishes",
  "prop.categorie": "Category",
  "prop.categorie_nota": "where the module sits in the bar. Can be changed at any time — the drawing stays as it is, only its place in the whole changes.",
  "prop.sect.modul": "Module",
  "prop.sect.alcatuire": "Contents",
  "prop.titlu.anvelopa": "Module outline",
  "prop.titlu.nucleu": "Stair and lift core",
  "prop.titlu.usa": "Door",
  "prop.titlu.fereastra": "Window",
  "prop.perete.structural": "Structural",
  "prop.perete.partition": "Partition",
  "prop.perete.facade": "Facade",
  "prop.perete.fire": "Fire wall",
  "prop.perete.parapet": "Parapet",
  "prop.perete.parapet_sticla": "Glass railing",
  "prop.parte.scara": "Stair",
  "prop.parte.lift": "Lift shaft",
  "prop.parte.ghena": "Service duct",
  "prop.parte.sas": "Lobby",
  "prop.parte.boxe": "Storage units",
  "prop.parte.hol": "Hall",
  "prop.material.beton": "Reinforced concrete",
  "prop.material.zidarie": "Masonry",
  "prop.material.polistiren": "Polystyrene",
  "prop.material.gips_carton": "Plasterboard (drywall)",
  "prop.material.sticla": "Glass",
  "prop.material.perete_cortina": "Curtain wall",
  "prop.brat": "Leg",
  "prop.brat_nota": "the side of the square the corner holds; both legs measure the same",
  "prop.crestatura": "Notch",
  "prop.crestatura_nota": "how much is missing from the square, at the inner corner",
  "prop.front": "Front",
  "prop.front_nota": "the step at which the next module starts",
  "prop.banda": "Band depth",
  "prop.banda_nota": "shared by the whole catalogue (15.30 across all seven); change it and the module no longer joins the others",
  "prop.colturi": "Corners",
  "prop.colturi_desenate": "outline drawn by hand — the shape is the polygon's, not the figures'",
  "prop.colturi_deduse": "outline derived from the figures; it is written from the first drag",
  "prop.amprenta": "Footprint",
  "prop.anvelopa_citire": "The outline is ONLY THE SHAPE the module occupies, and nothing inside it moves with it. Drag the CORNERS (the white squares) or the EDGES (the blue bars, perpendicular to them); double-click a bar to split an edge in two, so the shape can grow new sides. The figures above are read back from the polygon: the front is its extent along x, because that is the step at which the next module starts. If what is drawn then stops filling the shape, or spills out of it, the catalogue says so at publish time: it has one check for coverage and one for the seam wall («front = last room + 0.30 m»).",
  "prop.latime": "Width",
  "prop.adancime": "Width",
  "prop.arie": "Area",
  "prop.arie_parte_nota": "how much of the core footprint it takes",
  "prop.fel": "Kind",
  "prop.parte_citire": "The catalogue requires the part to sit inside the core box and not overlap another one. If it spills out, you are told at publish time.",
  "prop.muchie_nota": "the rooms around it follow the edge",
  "prop.front_nucleu_nota": "how much of the module front the core takes",
  "prop.scara_adancime": "Stair depth",
  "prop.put_adancime": "Shaft depth",
  "prop.zid_grosime": "Its wall thickness",
  "prop.zid_grosime_nota": "the concrete box around the flights and the shafts",
  "prop.lifturi": "Lifts",
  "prop.um_buc": "pcs",
  "prop.nucleu_citire": "The stair drawing is rebuilt from these figures on save — like the areas. What you see now is the transcription from the drawing.",
  "prop.nucleu_lipsa": "The module draws a core but does not declare its dimensions (<b>core</b> is missing from the document).",
  "prop.grosime": "Thickness",
  "prop.lungime": "Length",
  "prop.lungime_nota": "also changes by dragging an end",
  "prop.unghi": "Angle",
  "prop.unghi_nota": "0° is horizontal, 90° vertical",
  "prop.indreapta": "Straighten wall",
  "prop.indreapta_titlu": "Squares the wall to the nearest right angle, keeping its length and midpoint",
  "prop.pozitie_y": "Position Y",
  "prop.pozitie_x": "Position X",
  "prop.pozitie_nota": "the face it is measured from; moves this piece only",
  "prop.inaltime": "Height",
  "prop.inaltime_piesa_nota": "the kind's usual height; once written, it stays with the piece",
  "panza.scara_pas_cm": "riser {cm} cm",
  "panza.banda_fara_zid": "a strip goes on a wall",
  "panza.banda_scurta": "the strip is under 10 cm",
  "panza.scara_pas_rau": "riser {cm} cm — far outside 14-19, shorten the runs",
  "panza.scara_sens_sus": "run climbs up",
  "panza.scara_sens_jos": "run climbs down",
  "panza.scara_sens_stanga": "run climbs left",
  "panza.scara_sens_dreapta": "run climbs right",
  "panza.scara_latura_sus": "next run — above",
  "panza.scara_latura_jos": "next run — below",
  "panza.scara_latura_stanga": "next run — to the left",
  "panza.scara_latura_dreapta": "next run — to the right",
  "panza.scara_distanta": "distance between runs",
  "panza.scara_contratrepte": "{n} risers created",
  "panza.scara_ramase": "{n} remaining",
  "prop.sect.scara": "Stair",
  "prop.sect.ax": "Axis",
  "prop.ax_nume": "Name",
  "prop.ax_lungime": "Drawn length",
  "prop.ax_lungime_nota": "appearance only: the axis keeps its position and the same walls on it",
  "editor.redenumeste": "Rename",
  "editor.blocheaza_axele": "Lock every axis",
  "editor.deblocheaza_axele": "Unlock every axis",
  "editor.axele_blocate_nota": "Locked axes no longer move, neither dragged nor nudged from the keyboard.",
  "prop.trage_latimea": "drag to widen or narrow the panel",
  "prop.axe_toate": "Axes",
  "prop.capete_libere": "Free the wall ends",
  "prop.capete_legate": "Tie the wall ends back",
  "prop.capete_libere_nota": "Near a cross wall, less than 12 cm away, the end is lengthened into it and closes the gap. This button leaves the gap open, and the floor runs through it. Tight joints stay closed.",
  "prop.ax_blocat": "Lock",
  "prop.ax_blocat_nota": "A locked axis no longer moves — neither dragged nor nudged from the keyboard. The sizes below are appearance only and stay available.",
  "prop.ax_blocheaza": "Lock",
  "prop.ax_deblocheaza": "Unlock",
  "prop.ax_libera": "Extent",
  "prop.ax_libera_buton": "As its walls require",
  "prop.sect.scara_rampa": "Selected run",
  "prop.sect.scara_podest": "Selected landing",
  "prop.scara_trepte": "Steps",
  "prop.scara_adanc_treapta": "Tread width/depth",
  "prop.scara_podest_lung": "Length",
  "prop.scara_podest_forma": "Shape",
  "prop.scara_podest_forma_nota": "drawn by hand, from the points on the plan",
  "prop.scara_podest_dreptunghi": "Back to a rectangle",
  "prop.scara_podest_lat": "Width",
  "prop.scara_podest_lung_nota": "from one run to the other — moves the second run",
  "prop.scara_rampa_lat": "Run width",
  "prop.scara_rampa_lat_nota": "for the whole stair; the landing follows",
  "prop.scara_lungime": "Run length",
  "prop.scara_lungime_interval": "On a {h} m storey: {min}–{max} m",
  "prop.scara_lat_totala": "Total width",
  "prop.scara_lung_totala": "Total length",
  "prop.scara_lung_totala_nota": "the storey sets the flights; the difference goes into a landing, at least as deep as the flight is wide",
  "prop.scara_lung_totala_fixa": "landing drawn by hand: return it to a rectangle from its panel",
  "prop.scara_lat_totala_nota": "moves the last flight; the difference goes into the landing before it",
  "prop.scara_adauga_podest": "Add a landing",
  "prop.inaltime_scara_nota": "Follows the storey height. Change it in the module panel",
  "prop.scara_adanc_podest": "Landing depth",
  "prop.scara_pas_m": "Riser height",
  "prop.scara_pas_nota": "for the whole stair; empty = storey height divided by steps",
  "prop.scara_trepte_nota": "the run's length follows from them",
  "prop.scara_rezultat": "Stair result",
  "prop.scara_contratrepte_nota": "they follow from the run lengths; once written, they stretch them",
  "prop.scara_pas_derivat": "the storey height divided by the steps",
  "prop.scara_ia_pasul": "Take the storey's riser",
  "prop.scara_contratrepte": "Risers",
  "prop.scara_pas": "Riser height",
  "prop.scara_treapta": "Tread width",
  "prop.scara_rampe": "Per run",
  "prop.scara_nota": "derived from the storey height, not from the box you drag",
  "scara.eroare.pas": "A riser of {h} cm is outside the 16.6–17.5 cm range (R07).",
  "scara.eroare.blondel": "2h + l = {b} cm is outside the 62–64 cm range (Blondel's formula).",
  "scara.eroare.treapta": "A tread of {l} cm is outside the 28–30 cm comfort range.",
  "scara.eroare.lungime": "The run of {L} m is outside the {min}–{max} m range of a {h} m storey (R07, Blondel's formula).",
  "scara.eroare.trepte": "A flight has {n} risers. The rule asks for 3 to 16 (R14, R15).",
  "scara.eroare.lat": "The flight width, {w} m, is under 1.25 m, the main stair's minimum at 9 or more storeys (R20).",
  "scara.eroare.podest": "The landing, {p} m, is narrower than the flight (NP 063, art. 2.2.1.3.c).",
  "scara.eroare.ochi": "{g} cm remain between the flights. The rule asks for at least 20 cm (F39).",
  "prop.inaltime_nivel_nota": "follows the storey ({n} m)",
  "prop.pana_la_placa": "Up to the slab",
  "prop.perete_curbat": "Curved wall",
  "prop.curbura": "Curvature",
  "prop.curbura_drept": "straight — 1 = semicircle",
  "prop.curbura_raza": "radius ≈ {n} m",
  "editor.intoarce_peretele": "Turn it around",
  "prop.straturi.intoarce": "Turn the wall around",
  "prop.straturi.intoarce_titlu": "swaps the start point with the end point: the layers move to the other side",
  "prop.sect.straturi": "Layers",
  "prop.straturi.nota": "left to right, walking from the wall's start point towards its end point",
  "prop.straturi.gol": "A homogeneous wall, of one material. The first layer added keeps the present body and puts a new one beside it.",
  "prop.straturi.adauga_stanga": "Add on the left",
  "prop.straturi.adauga_dreapta": "Add layer",
  "prop.straturi.adauga_titlu": "a new layer, against the wall's right face",
  "prop.straturi.adauga_stanga_titlu": "a new layer, against the wall's left face",
  "prop.straturi.total": "{n} m in total, from {c} layers",
  "prop.straturi.mai_la_stanga": "further left",
  "prop.straturi.mai_la_dreapta": "further right",
  "prop.straturi.sterge": "remove the layer",
  "prop.straturi.fara_ax": "The wall carries no axis, so its left is unknown. Drag it once and layers become possible.",
  "prop.grosime_din_straturi": "the sum of the layers",
  "strat.grupa.structura": "Structure and masonry",
  "strat.grupa.izolatii": "Insulation",
  "strat.grupa.finisaje": "Finishes",
  "strat.grupa.bariere": "Membranes and barriers",
  "strat.beton": "Reinforced concrete",
  "strat.caramida": "Brick masonry",
  "strat.bca": "AAC block masonry",
  "strat.vata_minerala": "Mineral wool",
  "strat.polistiren": "Polystyrene (EPS/XPS)",
  "strat.gol_aer": "Air gap",
  "strat.tencuiala": "Plaster",
  "strat.gips_carton": "Plasterboard",
  "strat.placaj": "Ceramic tiling",
  "strat.lemn": "Timber",
  "strat.hidroizolatie": "Waterproofing membrane",
  "strat.bariera_vapori": "Vapour barrier",
  "prop.tip_perete": "Wall type",
  "prop.material": "Material",
  "prop.neportant": "Non-bearing",
  "prop.neportant_nota": "carries no load down to the foundation",
  "prop.portant": "Load bearing",
  "prop.portant_nota": "reinforced concrete — it continues into the basement",
  "prop.pe_fatada": "On the facade",
  "prop.inchide_nucleul": "Encloses the core",
  "prop.da": "yes",
  "prop.nu": "no",
  "prop.parapet": "Sill",
  "prop.pe_peretele": "On wall",
  "prop.pe_peretele_nota": "moves by dragging the opening on the drawing",
  "prop.rotire": "Rotation",
  "prop.piesa.teanc": "Stacking",
  "prop.piesa.teanc_nota": "Which piece shows on top where two overlap.",
  "prop.piesa.in_fata": "Bring to front",
  "prop.piesa.in_spate": "Send to back",
  "prop.piesa.ansamblu": "Assembly",
  "prop.piesa.ansamblu_nota": "This piece moves together with the rest of its assembly.",
  "prop.piesa.separa": "Separate elements",
  "editor.lipire.roteste": "Rotate",
  "editor.lipire.oglinda_h": "Flip ↔",
  "editor.lipire.oglinda_v": "Flip ↕",
  "editor.lipire.pune": "click to place · Esc cancels",
  "editor.unealta.demoleaza": "Break wall",
  "editor.sfat.demoleaza": "Press a wall and drag along it: the dragged stretch is demolished and what remains becomes separate walls.",
  "editor.incadreaza": "Back to whole module",
  "editor.uneste_elementele": "Merge elements",
  "prop.oglindire": "Mirror",
  "prop.oglindire_nota": "about the item's own axis, after rotation (H / V)",
  "prop.oglindeste_h": "Mirror left-right (H)",
  "prop.oglindeste_v": "Mirror front-back (V)",
  "prop.arie_nota": "recomputed on save, on the server",
  "prop.latura": "Side",
  "prop.pardoseala": "Floor",
  "prop.pereti": "Walls",
  "prop.implicit_felului": "the default for this kind of room",
  "prop.modul_pardoselii": "Floor module",
  "prop.fara_rost": "no joint",
  "prop.front_nominal": "Nominal front",
  "prop.front_nominal_nota": "changes by stretching the module",
  "prop.nucleu": "Core",
  "prop.nucleu_are": "has a core",
  "prop.nucleu_adauga": "Add core",
  "prop.nucleu_nota": "the stair, lifts and shaft people go up by — placed once, on the module that carries them",
  "prop.nucleu_sterge_nota": "remove it by selecting it on the drawing and pressing Delete",
  "prop.inaltime_nivel": "Storey height",
  "prop.inaltime_nivel_implicit": "the default for every wall that does not declare its own",
  "prop.camere": "Rooms",
  "prop.goluri": "Openings",
  "prop.piese_mobilier": "Furniture items",
  "prop.aparate_electrice": "Electrical fittings",
  "prop.fara_tablou": "no distribution board — the wires start from it, so the runs cannot be drawn; «furnish automatically» puts one in",
  // --- the module editor · the figures and handles on the canvas ---------
  "panza.cam_libera_sub": "{n} corners · click the first one to close",
  "panza.balcon_prins": "{arie} m² · against the facade, the free sides get a parapet",
  "panza.balcon_departe": "too far from the building — drag it next to a facade",
  "panza.gol_in_beton": "concrete wall — no opening is cut here",
  "panza.latura_cealalta": "{n} m the other way",
  "panza.spartura_sub": "break",
  "panza.ax_sterge": "Remove axis {nume} from the grid (walls stay)",
  "panza.ax_adauga": "Add an axis on this wall",
  "panza.ax_scurteaza": "drag to shorten or lengthen the axis — the drawing only, not its position",
  "panza.ax_titlu": "axis {nume} — drag to move all {n} walls on its line",
  "panza.trage_grosimea": "drag to change the thickness",
  "panza.podest_adauga": "add a point on the landing edge",
  "panza.podest_trage": "drag the point; it snaps to the run corner. Alt-click removes it",
  "panza.trage_capatul": "drag the end anywhere: the wall turns and stretches after it",
  "panza.trage_curbura": "drag to curve the wall",
  "panza.trage_latimea": "drag to resize the window, 5 cm at a time — the other end stays put",
  "panza.trage_marimea": "drag to resize the piece, 5 cm at a time — the opposite edge stays put",
  "panza.trage_coltul": "drag the corner anywhere on the plan",
  "panza.trage_parte": "drag the corner to resize this part; the core box follows",
  "panza.departeaza": "Zoom out",
  "panza.apropie": "Zoom in",
  "panza.incadreaza": "Fit to view (0)",
  "panza.reper_xy": "Drawing directions: X to the right, Y downwards",
  "auto.sarit_mica": "{arie} m² — nothing in the catalogue fits",
  "auto.sarit_fara_retea": "this kind of room has no furnishing recipe yet",
  // --- the module editor · catalogue checks, put the way a drafter reads them
  // --- the module, the panel that only READS (the collection) ------------
  "citire.parametri": "Parameters",
  "citire.inaltime_urmeaza": "follows the storey height",
  "citire.inaltime_declarata": "declared — the storey is {n} m",
  "citire.portant": "Load bearing",
  "citire.portant_da": "Yes — it carries the load down to the foundation",
  "citire.portant_nu": "No",
  "citire.apartament": "Flat",
  "citire.front_minim": "Minimum front",
  "citire.front_rigid": "rigid module — it does not stretch",
  "citire.front_maxim": "Maximum front",
  "citire.implicit": "default",
  "citire.nucleu": "Core",
  "citire.nucleu_lifturi#n": "stair + one lift|stair + {n} lifts|stair + {n} lifts",
  "citire.se_citeste": "Read-only for now. Editing arrives with the write path: a change recomputes the areas and the derived view, then goes through the catalogue checks.",
  "citire.apasa": "Click a wall, a room or an opening to see its parameters.",
  // --- ARCHITECTURE · the canonical vocabulary: the name of each kind ----
  // The keys are the kinds in `viewer/generated/kinds.ts`. The generated table
  // stays the safety net — a kind added to the canonical catalogue that has
  // not reached here yet shows its Romanian label, not a raw key.
  "kind.ada": "Accessible parking space",
  "kind.ada_aisle": "ADA transfer aisle",
  "kind.cat_ladder": "Cat ladder",
  "kind.aisle": "Driving aisle",
  "kind.apron": "Access apron",
  "kind.axis": "Structural axis",
  "kind.balcony": "Balcony",
  "kind.bar": "Bar segment",
  "kind.basement": "Underground car park",
  "kind.bath": "Bathroom",
  "kind.bath2": "Bathroom 2",
  "kind.bazin": "Retention tank",
  "kind.bedroom": "Bedroom",
  "kind.boxe": "Storage units",
  "kind.buildable": "Buildable area",
  "kind.building": "Building",
  "kind.cantilever": "Cantilever slab",
  "kind.offset": "Slab at another level",
  "kind.steps": "Steps",
  "kind.platform": "Platform lift",
  "kind.column": "Column / blade",
  "kind.core": "Core",
  "kind.courtyard": "Inner courtyard",
  "kind.diggable": "Excavatable area",
  "kind.door": "Door",
  "kind.easement": "Easement / protection zone",
  "kind.urban_zone": "Urbanism zone",
  "kind.ecs": "Domestic hot water plant",
  "kind.entrance": "Building entrance",
  "kind.ev": "EV parking space",
  "fatada.titlu": "Facade",
  "fatada.insp.titlu": "Inspiration",
  "fatada.insp.imaginea": "Image {nr}",
  "fatada.insp.fara_dorinte": "The brief's description says nothing specific about this image; the agent took from it what fitted the others.",
  "fatada.insp.poza_scoasa": "The photo «{nume}» is no longer in the brief.",
  "fatada.insp.scoasa": "This image was removed from the catalog.",
  "fatada.insp.inchide": "Close",
  "fatada.insp.soarta.kept": "kept",
  "fatada.insp.soarta.adapted": "adapted",
  "fatada.insp.soarta.impossible": "not possible",
  "fatada.reguli": "rules",
  "fatada.desfa": "Expand",
  "fatada.strange": "Collapse",
  "fatada.porneste_de_la": "Start from…",
  "fatada.aplica_json": "Apply JSON",
  "fatada.json_stricat": "The JSON cannot be read or has no `rules`.",
  "fatada.suprafata": "Surface",
  "fatada.etaje": "Storeys: all, ground, upper, top, 0-5 or 1,3,5",
  "fatada.portiune": "region (u)",
  "fatada.portiune_ex": "Region of the face, with anchors: bay[1].start → bay[1].end",
  "fatada.sus": "Up (applies earlier)",
  "fatada.jos": "Down (wins)",
  "fatada.sterge": "Delete rule",
  "fatada.placi_colorate": "tiles in another colour",
  "fatada.placi_lista": "Facade tiles (by colour and format)",
  "fatada.placi_material": "Material and colour",
  "fatada.placi_format": "Format",
  "fatada.placi_buc": "Pieces",
  "fatada.placi_din_care": "of which, cut to",
  "fatada.placi_inca": "{n} more sizes ({buc} tiles)",
  "fatada.placi_mai_putine": "show fewer",
  "fatada.material": "Material (cost key)",
  "fatada.culoare": "Colour",
  "fatada.la": "Vertical anchor",
  "fatada.sectiune": "Section",
  "fatada.finisaj": "finish",
  "fatada.brau": "band",
  "fatada.balustrada": "railing",
  "fatada.fereastra": "window",
  "fatada.tamplarie": "joinery",
  "fatada.profil": "profile",
  "fatada.vitraj": "Glazing",
  "fatada.vitraj.single": "single",
  "fatada.vitraj.double": "double",
  "fatada.vitraj.triple": "triple",
  "fatada.deschidere": "Opening",
  "fatada.deschidere.fixed": "fixed",
  "fatada.deschidere.casement": "casement",
  "fatada.deschidere.tilt_turn": "tilt & turn",
  "fatada.deschidere.sliding": "sliding",
  "fatada.canat": "leaf",
  "fatada.canate": "leaves",
  "fatada.rama": "surround",
  "fatada.glaf": "sill",
  "fatada.glaf_expl": "The exterior sill, as a facade panel hung on the window (ml, m³)",
  "fatada.fara_glaf": "no sill",
  "fatada.spalet": "reveal",
  "fatada.spalet_expl": "The insulation returned into the window reveal (perimeter × depth)",
  "fatada.cantitati": "Quantities (m², lm, m³)",
  "fatada.previzualizeaza": "Preview",
  "fatada.se_previzualizeaza": "Rebuilding the facade…",
  "fatada.previzualizat": "The facade is in the model ({s} s). Nothing saved.",
  "fatada.salveaza": "Save",
  "fatada.se_salveaza": "Writing on the solution…",
  "fatada.salveaza_expl": "Writes the rules on the open solution (only on it). Can be undone while you stay on the project.",
  "fatada.salvat": "The facade is written on the open solution.",
  "fatada.anuleaza": "Undo",
  "fatada.se_anuleaza": "Restoring…",
  "fatada.anuleaza_expl": "Puts back the facade from before the last change.",
  "fatada.anulat": "The previous facade is back.",
  "fatada.reface": "Redo",
  "fatada.se_reface": "Redoing…",
  "fatada.reface_expl": "Puts back the change you just undid.",
  "fatada.refacut": "The change is back on the facade.",
  "fatada.discutie_asteapta": "Writing the facade to the solution — the conversation waits a moment.",
  "fatada.calcat": "The facade changed on the solution meanwhile; I re-read it, nothing was written.",
  "fatada.pas_panou": "the rules in the panel",
  "fatada.regenereaza": "Regenerate architecture",
  "fatada.se_porneste": "Starting…",
  "fatada.regenereaza_expl": "Starts a new architecture from the site plan, with the project style (minutes).",
  "fatada.job_pornit": "The architecture is being regenerated; follow it on the board.",
  "fatada.fara_arh": "There is no architecture to preview on.",
  "fatada.asistent": "Facade assistant",
  "fatada.surf.facade": "face",
  "fatada.surf.plinth": "plinth",
  "fatada.surf.recess": "loggias",
  "fatada.surf.parapet": "attic",
  "fatada.surf.soffit": "soffit",
  "fatada.surf.balcony": "balconies",
  "fatada.surf.terrace": "terrace floors",
  "fatada.surf.loggia": "loggias (floor, railing)",
  "fatada.pardoseala": "floor",
  "fatada.pardoseala.tiles": "bonded tiles",
  "fatada.pardoseala.raised": "slabs on pedestals",
  "fatada.pardoseala.deck": "deck",
  "fatada.ancora.slab": "at slab",
  "fatada.ancora.floor": "at floor",
  "fatada.ancora.opening.top": "at lintel",
  "fatada.ancora.opening.bottom": "at sill",
  "fatada.ancora.mid": "at mid-height",
  "fatada.sectiune.rect": "rectangle",
  "fatada.sectiune.cornice": "cornice",
  "fatada.sectiune.slope": "slope",
  "fatada.sectiune.half_round": "half round",
  "kind.terasa_circulabila": "Walkable terrace",
  "kind.facade": "Facade wall",
  "kind.band": "Relief band",
  "kind.brick": "Face brick",
  "kind.cladding": "Cladding",
  "kind.glazing": "Glazing (curtain wall)",
  "kind.curtain_wall": "Curtain wall",
  "kind.glass": "Glass railing",
  "kind.insulation": "Thermal insulation",
  "kind.plaster": "Decorative plaster",
  "kind.render": "Base render",
  "kind.sill": "Sill",
  "kind.flooring": "Outdoor floor",
  "kind.slats": "Slatted railing",
  "kind.solid": "Solid railing",
  "kind.planter": "Planter",
  "kind.subframe": "Ventilated facade subframe",
  "kind.screen": "Fixed screen",
  "kind.fdcp": "Dry riser inlet",
  "kind.fire": "Fire wall",
  "kind.fire_door": "Fire-rated door",
  "kind.shelter_door": "Airtight protective steel door",
  "kind.fire_spur": "Fire brigade access",
  "kind.flat": "Flat",
  "kind.floor": "Floor slab",
  "kind.foundation": "Foundation beam",
  "kind.frame": "Frame beam",
  "kind.ghena": "Service duct / board",
  "kind.gospodarie": "Water plant",
  "kind.green_deep": "Green area on ground",
  "kind.green_slab": "Green area over slab",
  "kind.hall": "Hall",
  "kind.hol": "Distribution hall",
  "kind.administrativ": "Administrative room",
  "kind.comercial": "Commercial space",
  "kind.birouri": "Offices",
  "kind.invatamant": "Education",
  "kind.intrare_acoperita": "Covered entrance",
  "kind.kitchen": "Kitchen",
  "kind.lane": "Circulation lane",
  "kind.lift": "Lift",
  "kind.lintel": "Lintel",
  "kind.living": "Living room",
  "kind.loop": "Loop road",
  "kind.neighbour": "Neighbour boundary",
  "kind.on_grade": "Slab on ground",
  "kind.open": "Open boundary",
  "kind.pad": "Pad footing",
  "kind.parapet": "Parapet wall",
  "kind.partition": "Partition",
  "kind.party": "Party wall",
  "kind.path": "Footpath",
  "kind.ramp": "Ramp",
  "kind.ramp_access": "Ramp to underground car park",
  "kind.road": "Road",
  "kind.room": "Room",
  "kind.sas": "Air lock",
  "kind.coridor_evacuare": "Escape corridor",
  "kind.casa_scarii_evacuare": "Evacuation stair",
  "kind.scara": "Stairwell",
  "kind.shower": "Shower room",
  "kind.sidewalk": "Pavement",
  "kind.site": "Site plan",
  "kind.slab_panel": "Slab panel",
  "kind.spine": "Spine road",
  "kind.std": "Parking space",
  "kind.store": "Store room",
  "kind.office": "Study",
  "kind.dressing": "Walk-in closet",
  "kind.pantry": "Pantry",
  "kind.terasa": "Terrace",
  "kind.tech": "Technical room",
  "kind.garage": "Garage",
  "kind.ala": "Civil-defence shelter",
  "kind.ala_sas": "Shelter airlock",
  "kind.ala_gs": "Shelter toilets",
  "kind.ala_iesire": "Shelter emergency exit",
  "kind.ala_perete": "Shelter protective wall",
  "kind.street": "Street boundary",
  "kind.strip": "Strip footing",
  "kind.structural": "Structural wall",
  "kind.te": "Electrical plant",
  "kind.desfumare": "Smoke extraction zone",
  "kind.te_desfumare": "Smoke extraction plant",
  "kind.tie": "Tie beam",
  "kind.transfer": "Transfer beam",
  "kind.tree": "Tree",
  "kind.tronson": "Module",
  "kind.turning_head": "Turning head",
  "kind.wc": "WC",
  "kind.window": "Window",
  "kind.hatch": "Airtight steel hatch",
  "kind.access": "Access at the site boundary",
  "kind.crossing": "Pedestrian crossing",
  "kind.playground": "Playground",
  "kind.square": "Small plaza",
  "kind.waste_platform": "Waste collection area",
  "nivel.apartment": "Typical floor",
  "nivel.basement_storey": "Basement",
  "nivel.ground": "Ground floor",
  "nivel.parking": "Parking basement",
  "nivel.roof": "Roof",
  // --- ARCHITECTURE · the inspector: WHAT the object you clicked is -------
  "insp.nedeclarat": "not declared",
  "insp.grup.identitate": "Identity",
  "insp.grup.forma": "Shape",
  "insp.grup.coordonate": "Coordinates",
  "insp.grup.evacuare": "Escape",
  "insp.grup.retrageri": "Setbacks",
  "insp.grup.geometrie": "Geometry",
  "insp.rand.arie": "Area",
  "insp.rand.gabarit": "Bounding size",
  "insp.rand.colturi": "Corners",
  "insp.rand.orientare": "Orientation",
  "insp.rand.centru": "Centre",
  "insp.rand.dimensiuni": "Dimensions",
  "insp.rand.sectiune": "Section",
  "insp.rand.tip": "Type",
  "insp.rand.denumire": "Name",
  "insp.rand.lungime": "Length",
  "insp.rand.latime": "Width",
  "insp.rand.inaltime": "Height",
  "insp.rand.grosime": "Thickness",
  "insp.rand.rezistenta_foc": "Fire resistance",
  "insp.rand.cod": "Code on the drawing",
  "insp.rand.lungime_axa": "Axis length",
  "insp.rand.numar": "Number",
  "insp.rand.sus": "Top",
  "insp.rand.jos": "Bottom",
  "insp.rand.de_la": "From",
  "insp.rand.pana_la": "To",
  "insp.rand.usa": "Door",
  "insp.rand.nucleu": "Core",
  "insp.rand.nucleu_centru": "centre {xy}",
  "insp.rand.arie_declarata": "Declared area",
  "insp.rand.arie_geometrica": "Geometric area",
  "insp.rand.lungime_desfasurata": "Developed length",
  "insp.rand.utila": "Usable area",
  "insp.rand.minim_legal": "Legal minimum",
  "insp.rand.front": "Facade frontage",
  "insp.rand.balcon_separat": "Balcony (counted apart)",
  "insp.rand.camere_desenate": "Rooms drawn",
  "insp.rand.camere_locuibile": "Habitable rooms",
  "insp.rand.in_locuinta": "In flat",
  "insp.rand.al_locuintei": "Belongs to flat",
  "insp.rand.ap_pe_nivel": "Flats per storey",
  "insp.rand.nuclee": "Cores",
  "insp.rand.utila_nivel": "Usable area / storey",
  "insp.rand.amprenta": "Footprint",
  "insp.rand.regim": "Storey count",
  "insp.rand.functiune": "Use",
  "insp.rand.cornisa": "Height at eaves",
  "insp.rand.urbanism": "Planning",
  "insp.rand.incendiu": "Fire",
  "insp.rand.guverneaza": "Governed by",
  "insp.rand.aliniere": "Alignment",
  "insp.forma.dreptunghi": " (rectangle)",
  "insp.forma.l": " (L shape)",
  "insp.evac.punct": "The point that asked for it",
  "insp.evac.fara_scara": "Without this stair",
  "insp.evac.niciun_traseu": "no route at all",
  "insp.evac.cu_ea": "With it",
  "insp.evac.limita": "Required limit",
  "insp.evac.toleranta": "Measurement tolerance",
  "insp.grup.ala": "Civil-defence shelter",
  "insp.ala.adapost": "Shelter",
  "insp.ala.persoane": "People",
  "insp.ala.cladire": "Under building",
  "insp.ala.adapostire": "Sheltering area",
  "insp.ala.necesar": "required",
  "insp.ala.calcul": "How the requirement is derived",
  "insp.ala.h": "Height used",
  "insp.ala.pe_persoana": "Area per person",
  "insp.ala.leaga_volum": "volume governs",
  "insp.ala.leaga_arie": "area governs",
  "insp.ala.tinta": "Target for a full shelter",
  "insp.ala.arie": "Area with airlock and toilets",
  "insp.ala.bucata": "Building piece",
  "insp.ala.sas": "Airlock",
  "insp.ala.grup_sanitar": "Toilets",
  "insp.ala.cabine": "WC cubicles",
  "insp.ala.lavoare": "Wash basins",
  "insp.ala.iesire": "Exit",
  "insp.ala.locuri": "Parking bays taken",
  "insp.ala.neconcordanta": "Non-conformity",
  "insp.ala.rol": "Role",
  "insp.ala.gol": "Opening",
  "insp.ala.perete": "Wall",
  "insp.ala.beton_etans": "reinforced concrete, sealed",
  "insp.evac.traseu_sas": "Route to the air lock",
  "insp.evac.limita_scurt": "Limit",
  "insp.evac.doua_directii": "{n} m (two directions)",
  "insp.familie.wall": "Wall",
  "insp.familie.opening": "Opening",
  "insp.familie.space": "Space",
  "insp.familie.slab": "Slab",
  "insp.familie.column": "Column",
  "insp.familie.beam": "Beam",
  "insp.familie.ladder": "Ladder",
  "insp.familie.flat": "Flat",
  "insp.familie.core": "Core",
  "insp.nivel.situatie": "Site plan",
  "insp.nivel.parter": "Ground floor",
  "insp.nivel.subsol": "Level {n}",
  "insp.nivel.etaj": "Floor {n}",
  "insp.chip.nivel_intreg": "whole storey",
  "insp.chip.nivel": "level {n}",
  "insp.chip.etaj": "floor {n}",
  "insp.chip.la_sol": "at grade",
  "insp.chip.dizabilitati": "for disabled users",
  "insp.chip.nu_se_parcheaza": "no parking on it",
  "insp.chip.sub_perete": "under a building wall",
  "insp.chip.modul_parcare": "parking module",
  "insp.chip.pe_traversare": "where the aisle crosses",
  "insp.chip.intoarsa": "turned to follow the stairwell",
  "insp.chip.persoane": "{n} occupants",
  "insp.chip.comuna_parcaj": "shared with the car park",
  "insp.chip.proprie": "its own",
  "insp.chip.situatie": "site plan",
  "insp.chip.limita_parcela": "plot boundary",
  "insp.chip.spre_strada": "towards the street",
  "insp.chip.spre_vecin": "towards the neighbour",
  "insp.chip.retragere_presupusa": "assumed setback",
  "insp.titlu.loc_adaptat": "Accessible space",
  "insp.titlu.loc_parcare": "Parking space",
  "insp.titlu.fasie_ada": "Accessible transfer aisle",
  "insp.titlu.lamela": "Structural blade",
  "insp.titlu.alee": "Aisle",
  "insp.rand.eticheta": "Piece",
  "insp.rand.fel": "Kind",
  "insp.rand.nota": "Step note",
  "insp.rand.sens": "Direction",
  "insp.sens.dublu": "two-way",
  "insp.sens.unic": "one-way",
  "insp.chip.dublu_sens": "two-way",
  "insp.chip.sens_unic": "one-way",
  "insp.titlu.stalp": "Column",
  "insp.titlu.perete_antifoc": "Fire wall",
  "insp.titlu.usa_antifoc": "Fire door",
  "insp.titlu.rampa": "Ramp between levels",
  "insp.titlu.balcon": "Balcony",
  "insp.titlu.tronson": "Module",
  "insp.titlu.coridor": "Common corridor",
  "insp.titlu.cladire": "Building {id}",
  "insp.titlu.corp": "Building block",
  "insp.titlu.acces": "Building entrance",
  "insp.titlu.scara_evacuare": "Parking escape stair",
  "insp.titlu.trotuar": "Sidewalk",
  "insp.titlu.trecere": "Pedestrian crossing",
  "insp.titlu.zona": "Outdoor area",
  "insp.fel.trotuar": "sidewalk",
  "insp.fel.trecere": "pedestrian crossing",
  "insp.fel.zona": "area",
  "insp.rand.id_agent": "Agent id",
  "insp.rand.alee_traversata": "Aisle crossed",
  "insp.rand.niveluri": "Levels served",
  "insp.rand.tipar": "Pattern",
  "insp.rand.rezistenta": "Fire resistance",
  "insp.rand.persoane": "People · flows × width",
  "insp.rand.zona_libera": "No-parking zone at grade",
  "insp.titlu.latura": "Side {n}",
  "insp.stall.adaptat": "accessible ({w} × {d} m + 1.50 m aisle)",
  "insp.stall.standard": "standard",
  "insp.stall.dimensiuni": "{w} × {d} m",
  "insp.flat.tip": "{camere} rooms / {persoane} occupants",
  "insp.flat.toate_pe_fatada": "{n} — all on the facade",
  "insp.flat.utili": "{eticheta} — {arie}",
  "insp.flat.utili_um": "{eticheta} — {arie} usable",
  "insp.gov.urbanism": "planning",
  "insp.gov.incendiu": "fire",
  // --- ARCHITECTURE · the norms the inspector cites ----------------------
  // `norm` (the act's designation) is NOT translated: it is the name of the
  // legal text, and whoever looks it up looks it up as written. What it
  // requires, and how much, is translated.
  "norma.stall.size": "Car parking space, minimum dimensions",
  "norma.aisle.width": "Driving aisle for 90° parking: two-way / one-way",
  "norma.ada.count": "Spaces for disabled users: at least 4% of the total, but never fewer than two",
  "norma.ada.strip": "Shared transfer aisle between two accessible spaces — no parking on it and no driving aisle across it",
  "norma.ada.level": "Accessible spaces sit on the first underground level, on a step-free route to the exit",
  "norma.fire.wall": "Fire wall between fire compartments",
  "norma.fire.door": "Fire-rated door where a driving aisle crosses the fire wall",
  "norma.fire.core": "Escape stairwell, fire-rated enclosure",
  "norma.evac.two_dir": "Maximum escape distance in a car park, two directions of escape",
  "norma.evac.dead_end": "Maximum escape distance in a dead end (a single direction)",
  "norma.ramp.slope": "Maximum slope of the access ramp into a covered car park",
  "norma.flat.useful": "Minimum usable area of a dwelling, by type",
  "norma.room.living": "Minimum area of the living room",
  "norma.room.bedroom": "Minimum area of the bedrooms, summed",
  "norma.room.kitchen": "Minimum area of the kitchen",
  "norma.room.sanitary": "Sanitary fittings by room count: from 3 rooms, a separate WC",
  "norma.room.store": "Storage space within the dwelling",
  "norma.room.hall": "Clear circulation width within the dwelling",
  "norma.flat.insolation": "Every habitable room has direct daylight and at least 1½ h of sunlight on 21 February — which is why no habitable room can be internal",
  "norma.bar.corridor": "Common distribution corridor in a residential building",
  "norma.flat.balcony": "The balcony is counted apart — it does not enter the usable area",
  "norma.evac.flat": "Distance from the flat door to the stairwell, two directions",
  "norma.struct.column": "A column or blade rises from the raft to the roof and may stand only within a wall; under a building the line of columns is the wall itself",
  "norma.sursa_proiect": "Project rule — see structure.py",
  "norma.val.stall_size": "2.50 × 5.50 m",
  "norma.val.aisle_width": "6.00 m / 5.00 m",
  "norma.val.ada_count": "≥ 4%, min. 2",
  "norma.val.ada_strip": "1.50 m",
  "norma.val.hall_width": "1.20 m",
  "norma.val.corridor_width": "1.50 m",
  // --- ARCHITECTURE · the viewer: top bar, layers, the sun ---------------
  "viz.doc_necitit": "The solution document could not be read.",
  "viz.se_incarca": "loading…",
  "viz.se_incarca_desenul": "Loading the drawing…",
  "viz.grup.plansa": "Sheet",
  "viz.grup.corp": "Block",
  "viz.grup.nivel": "Storey",
  "viz.grup.vedere": "View",
  "viz.arata_fatada": "Facade rules",
  "viz.arata_insorire": "Facade sunlight",
  "viz.arata_schita": "Sketch",
  "viz.arata_schita_titlu": "The volumes you drew, which this plan came from",
  "viz.arata_evacuare": "Escape routes",
  "viz.arata_evacuare_titlu": "The longest route of each SAS exit, to the front of its farthest bay (NP 127, 40 m): dashed, with arrows toward the exit, along lanes; a 0.90 m strip through the corridors between bays",
  "viz.arata_circulatie": "Traffic signs",
  "viz.arata_circulatie_titlu": "The basement's traffic signs, placed by the generator on the final lanes: a give-way plate at the mouth of every minor aisle, on the right of the arriving direction; the lane-choice arrow one bay before it and the direction arrow in the first bay after the junction; two arrows at the ramp head; one pair mid-way along stretches over 40 m",
  "viz.arata_soare": "Light & shadows",
  "viz.arata_soare_titlu": "The sun slider: time of day and season. The model opens at noon",
  "viz.arata_oras": "City",
  "viz.arata_oras_titlu": "Buildings, streets and parks around the plot, within 2 km, from OpenStreetMap. White: known height; grey: estimated",
  "viz.sit_sub_plan": "Site plan underneath",
  "viz.sit_sub_plan_titlu": "Draw the site plan under the storey plan, to see which way each flat faces",
  "viz.strat.structura": "Structure",
  "viz.strat.cladire": "Building and façades",
  "viz.strat.structura_se_construieste": "the structure model is being built…",
  "viz.grup.afisare": "Show",
  "viz.filtru.cote": "Dimensions",
  "viz.filtru.camere": "Rooms",
  "viz.filtru.etichete": "Labels",
  "viz.filtru.apartamente": "Flats",
  "viz.filtru.mobilier": "Furniture",
  "viz.filtru.foc": "Fire resistance",
  "viz.filtru.rampe_acces": "Ramps G → -1",
  "viz.filtru.rampe_interior": "Ramps -1 → -2",
  "viz.filtru.ascunde": "Hide: {ce}",
  "viz.filtru.arata": "Show: {ce}",
  "macheta.ap.garsoniera": "Studio",
  "macheta.ap.camere": "{n} rooms",
  "macheta.ap.balcon": "balcony {arie} off the {camera}",
  "macheta.ap.balcon_fara": "balcony {arie}",
  "macheta.camera.living": "Living room",
  "macheta.camera.bedroom": "Bedroom",
  "macheta.camera.dormitor": "Bedroom",
  "macheta.camera.kitchen": "Kitchen",
  "macheta.camera.bath": "Bathroom",
  "macheta.camera.bath2": "Bathroom",
  "macheta.camera.shower": "Shower",
  "macheta.camera.wc": "Toilet",
  "macheta.camera.hall": "Hall",
  "macheta.camera.hol": "Hall",
  "macheta.camera.store": "Storage",
  "macheta.camera.balcony": "Balcony",
  "macheta.camera.loggia": "Loggia",
  "macheta.camera.terrace": "Terrace",
  "macheta.camera.terasa_circulabila": "Terrace",
  "macheta.camera.scara": "Stairs",
  "viz.grup.etaj": "Storey",
  "viz.circulatie_in_lucru": "Circulation is in progress and will be updated soon.",
  "viz.etaj.tot": "All",
  "viz.etaj.tot_titlu": "The whole building",
  "viz.etaj.parter": "G",
  "viz.etaj.parter_titlu": "Ground floor",
  "viz.etaj.titlu": "Storey {n}",
  "viz.etaj.taste": "Page Up / Page Down: the storey above / below",
  "viz.grup.camera": "Camera",
  "viz.grup.tus": "Ink",
  "viz.plan_situatie": "Site plan",
  "viz.niveluri": "Storeys",
  "viz.toate_corpurile": "All blocks",
  "viz.toate_corpurile_titlu": "The floor plan of the whole scheme: every block that reaches this level, in their common frame",
  "viz.vedere_2d": "2D",
  "viz.vedere_2d_titlu": "The technical sheet, black and white: poché on the walls, no materials and no shadows",
  "viz.vedere_3d": "3D",
  "viz.vedere_3d_titlu": "The model seen at an angle, cut at 1.15 m",
  "viz.sit_plan_titlu": "Sheet A01 — the rendered site plan, with dimensions and the area schedule",
  "viz.sit_macheta_titlu": "The site as a volume, under the sun of this hour and place",
  "viz.sit_macheta_gen": "The model opens once the architecture on this plan has its storeys written",
  "viz.cote_asezare": "Placement",
  "viz.cote_asezare_titlu": "Setbacks to the boundary, gaps between blocks, distance to the driveway and to the car park",
  "viz.zona_interzisa": "No-build zone",
  "viz.zona_interzisa_titlu": "Hatches the band where nothing may be built — the plot, less the buildable area the engine computed",
  "viz.cam.ansamblu": "Whole scheme",
  "viz.cam.strada": "From the street",
  "viz.cam.curte": "From the courtyard",
  "viz.cam.pasare": "Bird's eye",
  "viz.tus_zi": "Day",
  "viz.tus_zi_titlu": "Black on white — the printed sheet",
  "viz.tus_noapte": "Night",
  "viz.tus_noapte_titlu": "White on black — the sheet on screen, as in CAD",
  "viz.soare.titlu": "Shadows & sunlight",
  "viz.soare.ora_din_zi": "time of day",
  "viz.soare.amiaza": "noon",
  "viz.soare.anotimp": "Season",
  "viz.soare.azimut": "azimuth",
  "viz.soare.inaltime": "altitude",
  "viz.soare.umbra": "shadow",
  "viz.ins.titlu": "Facade sunlight",
  "viz.ins.norma": "21 Dec · Order MS 119/2014",
  "viz.ins.fara_coordonate": "The document carries no coordinates, so the sun is computed for Bucharest. The figures below are not those of this site.",
  "viz.ins.fara_corpuri": "No block with a height — there is nothing to measure.",
  "viz.ins.conform": "≥ 1.5 ✓",
  "viz.ins.neconform": "< 1.5 ✗",
  "viz.ins.arh_se_incarca": "fetching the chained architecture… · ",
  "viz.ins.fara_arh": "no chained architecture — the volumes have no openings · ",
  "viz.ins.arh_fara_goluri": "the chained architecture has no openings on the outlines · ",
  "viz.ins.arh_in_lucru": "the chained architecture is still running · ",
  "viz.ins.fatada_in_lucru": "the facade agent is working — {stadiu} · ",
  "viz.ins.ferestre#n": "one window from the chained architecture · |{n} windows from the chained architecture · |{n} windows from the chained architecture · ",
  "viz.ins.sub_prag": "{n} of {total} facades under the threshold",
  "viz.ins.toate_trec": "all {total} facades pass the threshold",
  "viz.ins.masurare": " · measured at 1.50 m, with the shading between the proposed blocks",
  "viz.cifre.cota": "level",
  "viz.cifre.utila_fara_balcoane": "excl. balconies and loggias",
  "viz.cifre.utila_balcoane": "balconies and loggias",
  "viz.cifre.utila_terase": "terraces",
  "viz.cifre.etaj_toate": "{nivel} · all buildings",
  "viz.cifre.suprafete": "Areas",
  "viz.cifre.pe_camere#n": "studios|{n} rooms|{n} rooms",
  "viz.cifre.apartamente_rand": "Flats",
  "viz.cifre.arie_utila_rand": "Usable area",
  "viz.cifre.arie_construita": "Built area",
  "viz.cifre.arie_construita_desf": "Gross floor area",
  "viz.cifre.cladirea": "Building {cod}",
  "viz.cifre.ansamblu": "Development",
  "viz.cifre.niveluri#n": "1 storey|{n} storeys|{n} storeys",
  "viz.cifre.cladiri#n": "1 building|{n} buildings|{n} buildings",
  "viz.gen.titlu": "General areas",
  "viz.gen.situatie": "General summary",
  "viz.gen.su_fara_terase": "Net area (without terraces)",
  "viz.gen.su_terase": "Net area of terraces, balconies, loggias",
  "viz.gen.su_comune": "Net area of common spaces",
  "viz.gen.st_construit": "Gross built area",
  "viz.gen.pct_su_st": "% net (without terraces) / gross built",
  "viz.gen.pct_su_st_fara_comune": "% net (without terraces) / gross without common spaces",
  "ind.titlu": "Indicators",
  "ind.verde": "Green space",
  "ind.pot": "Site coverage (POT)",
  "ind.cut": "Floor area ratio (CUT)",
  "ind.regim": "Height regime",
  "ind.niv": "floors",
  "ind.max": "max {v}",
  "ind.supraterana": "Built above ground",
  "ind.subterana": "Built below ground",
  "ind.comercial": "Commercial ground floor",
  "ind.comercial_pct": "{pct} % of the floor area",
  "ind.zona_titlu": "Zone {nume}",
  "ind.amprenta": "Footprint",
  "ind.din_teren": "of {v}",
  "ind.teren": "Site area",
  "ind.verde_total": "Proposed green space",
  "ind.verde_sol": "Green on natural soil",
  "ind.verde_subsol": "Green over the basement",
  "ind.sapatura": "Parking excavation",
  "viz.legenda_strange": "Collapse",
  "viz.legenda_strange_aria": "collapse the legend",
  "viz.legenda_arata": "Legend and scale",
  "viz.legenda_arata_aria": "show the legend and the scale",
  "legenda.isu.titlu": "Fire safety legend (ISU)",
  "legenda.isu.ala": "Civil-defence shelter wall, reinforced concrete ≥ 40 cm",
  "legenda.isu.usa_ei": "Fire-rated door, class EI₂ (-C: self-closing)",
  "legenda.isu.um": "Airtight metal door (UME)",
  "legenda.isu.om": "Airtight metal hatch (OME)",
  "viz.luna.1": "January",
  "viz.luna.2": "February",
  "viz.luna.3": "March",
  "viz.luna.4": "April",
  "viz.luna.5": "May",
  "viz.luna.6": "June",
  "viz.luna.7": "July",
  "viz.luna.8": "August",
  "viz.luna.9": "September",
  "viz.luna.10": "October",
  "viz.luna.11": "November",
  "viz.luna.12": "December",
  // --- ARCHITECTURE · the sheet's layers and the legend's functions ------
  "strat.pereti": "Walls",
  "strat.pereti_structural": "Structural",
  "strat.pereti_facade": "Facade",
  "strat.pereti_party": "Party walls",
  "strat.pereti_partition": "Partitions",
  "strat.pereti_fire": "Fire walls",
  "strat.foc": "Fire resistance (ISU)",
  "strat.descarcare": "Load lines (from the floors above)",
  "strat.parapete": "Parapets",
  "strat.repere": "References: buildings and the plot boundary",
  "strat.usi": "Doors",
  "strat.ferestre": "Windows",
  "strat.spatii": "Spaces",
  "strat.materiale": "Materials (floor finishes)",
  "strat.adancime": "Shadows and depth",
  "strat.placi": "Slabs and cantilevers",
  "strat.structura": "Structure drawn",
  "strat.dedesubt": "The storey below (columns and beams)",
  "strat.gazde": "Possible supports for props",
  "strat.apartamente": "Flats",
  "strat.mobilier": "Furniture",
  "arh.recomp.in_lucru": "being partitioned…",
  "recomp.titlu": "Repartitioning",
  "recomp.in_lucru": "Being partitioned… you can leave this screen; the plan reloads by itself.",
  "recomp.esuat": "Repartitioning found nothing; the flat stayed as it was.",
  "recomp.provizoriu": "Provisional answer — the full search is still running; the variants replace themselves when it is done.",
  "recomp.se_incarca": "Loading variants…",
  "recomp.fara_variante": "No variants kept for this flat.",
  "recomp.si_pe_oglinzi": "Apply to the {n} identical (mirrored) flats too",
  "recomp.camere_m2": "{m2} m² in rooms",
  "recomp.util_m2": "Useful {m2} m²",
  "recomp.praguri": "{n} catalogue thresholds missed",
  "recomp.scrisa": "Written in the plan",
  "recomp.trimisa": "Writing…",
  "recomp.aplica": "Apply variant",
  "recomp.eroare": "Could not start writing the variant.",
  "recomp.picat": "The variant could not be written; the plan stayed as it was.",
  "recomp.varianta_n": "Variant {n} of {m}",
  "recomp.anterioara": "Previous variant",
  "recomp.urmatoarea": "Next variant",
  "recomp.fel.living": "Living room",
  "recomp.fel.bedroom": "Bedroom",
  "recomp.fel.kitchen": "Kitchen",
  "recomp.fel.bath": "Bathroom",
  "recomp.fel.hall": "Hall",
  "recomp.fel.room": "Room",
  "recomp.fel.niche": "Niche",
  "strat.etichete": "Room names",
  "strat.obiecte": "Labels (object types)",
  "strat.parcaj": "Parking sheet — numbers, aisles",
  "strat.alei": "Aisles — their width",
  "strat.rampe_acces": "Access ramp (ground → -1)",
  "strat.rampe_interior": "Ramp -1 → -2",
  "strat.cote": "Dimensions",
  "strat.cote_fatada": "On the facade — solid, void, solid",
  "strat.cote_axe": "Between column axes",
  "strat.cote_total": "Total per side",
  "strat.cote_camere": "Clear room sizes",
  "strat.cote_goluri": "Internal walls",
  "strat.cote_ala": "ALA shelters — SAS, doors, columns",
  "strat.nuclee": "Cores (stair, lift, duct)",
  "strat.scari": "Cat ladders",
  "viz.prag": "threshold",
  "strat.circulatie": "Circulation and blocks",
  "strat.nedesenate": "Undrawn objects",
  "corp.functiune.scoala": "School",
  "corp.functiune.birouri": "Offices",
  "corp.functiune.comercial": "Retail",
  "functiune.living": "Living room",
  "functiune.kitchen": "Kitchen",
  "functiune.bedroom": "Bedroom",
  "functiune.dormitor": "Bedroom",
  "functiune.hall": "Hall",
  "functiune.hol": "Landing",
  "functiune.administrativ": "Administrative room",
  "functiune.comercial": "Commercial space",
  "functiune.birouri": "Offices",
  "functiune.invatamant": "Education",
  "functiune.bath": "Bathroom",
  "functiune.bath2": "Bathroom",
  "functiune.shower": "Shower room",
  "functiune.wc": "WC",
  "functiune.store": "Store room",
  "functiune.balcony": "Balcony",
  "functiune.sas": "Air lock",
  "functiune.scara": "Stairwell",
  "functiune.lift": "Lift",
  "functiune.ghena": "Service duct",
  "functiune.bazin": "Tank",
  "functiune.tech": "Plant room",
  "categorie.structura": "Structure",
  "plan.rampa_spre": "ramp → level {n}",
  "plan.rampa_dedesubt": "ramp → level {n}, under the access ramp",
  "plan.materiale_esec": "The model's materials could not be loaded.",
  "plan.terasa_necirculabila": "Non-walkable roof",
  "plan.trepte_sus": "UP · {n} risers",
  "plan.platforma": "Platform lift",
  // --- ARCHITECTURE · the checks: verdict, tally, filter -----------------
  "verdict.conform": "Compliant",
  "verdict.neconform": "Non-compliant",
  "verdict.necesita_confirmare": "Needs confirmation",
  "verdict.inaplicabil": "Not applicable",
  "verdict.neverificabil": "Cannot be checked",
  // The nouns a rule counts, by the KIND of element. These are enumerable —
  // `base_kind` is a code — so they live here; the rule's own free label
  // (`base_label`) comes from the validator and stays Romanian until the
  // server axis is done (see `docs/internationalizare.md`).
  "subst.flat#n": "one flat|{n} flats|{n} flats",
  "subst.flatRoom#n": "one room|{n} rooms|{n} rooms",
  "subst.balcony#n": "one balcony|{n} balconies|{n} balconies",
  "subst.building#n": "one building|{n} buildings|{n} buildings",
  "subst.corridor#n": "one corridor|{n} corridors|{n} corridors",
  "subst.bar#n": "one module|{n} modules|{n} modules",
  "subst.element#n": "one element|{n} elements|{n} elements",
  "val.fara_capitol": "No chapter",
  "val.conforme_din": "{conforme} of {total} compliant",
  "val.din": "{conforme} of {total}",
  "val.de_confirmat": "{n} need confirmation",
  "val.inaplicabil_alte": "does not apply to another {n}",
  "val.inaplicabil_la": "does not apply to {substantiv}",
  "val.instante#n": "one instance|{n} instances|{n} instances",
  "val.neverificabila": "cannot be checked on this model",
  "val.fara_instante": "no instances",
  "val.scop.apartamente#n": "one flat|{n} flats|{n} flats",
  "val.scop.niveluri#n": "one storey|{n} storeys|{n} storeys",
  "val.scop.etaje_identice": "identical floors, checked one by one",
  "val.scop.elemente#n": "one element|{n} elements|{n} elements",
  "val.scop.masuratori#n": "one measurement|{n} measurements|{n} measurements",
  "val.nicio_neconformitate": "no non-compliance",
  "val.filtru_reguli#n": "one rule|{n} rules|{n} rules",
  // --- ARCHITECTURE · the storey's name, and why an object was not drawn --
  "nivelul.nr": "Level {n}",
  "nivelul.parcare": "Car park · level {n}",
  "nivelul.subsol": "Basement {n}",
  "nivelul.parter": "Ground floor",
  "nivelul.parter_comercial": "Commercial ground floor",
  "nivelul.etaj": "Floor {n}",
  "motiv.planseu_inexistent": "No such storey",
  "motiv.pe_situatie": "On the site plan",
  "motiv.tip_nedesenat": "Type the plan does not draw",
  "motiv.pe_fatada": "On the facade and in 3D",
  "motiv.peste_sectiune": "Above the plan's cut",
  "motiv.tip_organizatoric": "Groups others, is not drawn",
  "motiv.fara_geometrie": "No geometry",
  // --- THE SITE PLAN · the A01 area schedule and the labels on it --------
  // The schedule rows are set in CAPITALS: that is the printed sheet's
  // convention, not an oversight — see `siteGlyphs`.
  "a01.suprafata_teren": "PLOT AREA",
  "a01.total_verde": "TOTAL GREEN AREA PROPOSED",
  "a01.verde_sol": "DEEP GREEN AREA PROPOSED",
  "a01.verde_subsol": "GREEN AREA OVER BASEMENT PROPOSED",
  "a01.sapatura": "EXCAVATION FOR THE CAR PARK",
  "a01.acces": "entrance",
  "a01.acces_cladire": "building",
  "a01.cota_lungime": "length {m} m",
  "a01.cota_latime": "width {m} m",
  "a01.rampa_acces": "access ramp",
  "insp.inchide": "Close (Esc)",
  "insp.sus": "Select the whole dwelling",
  // --- DOCUMENT IMPORT · the dossier slots -------------------------------
  // The labels COME FROM THE SERVER, translated there (see `importer/i18n/`).
  // These are the net for the screen drawn before it has answered.
  "dosar.categorie.certificat_urbanism": "Planning certificate",
  "dosar.categorie.cadastru": "Cadastre & land registry",
  "dosar.categorie.plan_topografic": "Topographic survey",
  "dosar.categorie.studiu_geotehnic": "Geotechnical report",
  "dosar.categorie.solutie_arhitecturala": "Architectural design",
  "dosar.categorie.solutie_structurala": "Structural design",
  "dosar.categorie.retete_preturi": "Rates & prices",
  "dosar.categorie.altele": "Other",
  "dosar.categorie.mixt": "File with several documents",
  "dosar.piesa.pagina": "page {p}",
  "dosar.piesa.pagini": "pages {de_la}–{pana_la}",
  "dosar.piesa.din": "from {fisier}",
  "dosar.piese": "What it contains",

  // --- STRUCTURĂ · banii -------------------------------------------------
  "struct.bani.lei": "lei",
  "struct.bani.milioane_lei": "{v} m lei",

  // --- STRUCTURĂ · felul elementului -------------------------------------
  "struct.tip.grinda": "beam",
  "struct.tip.stalp": "column",
  "struct.tip.perete": "wall",
  "struct.tip.lamela": "structural wall",
  "struct.tip.ochi": "slab panel",
  "struct.tip.ax": "foundation axis",
  "struct.tip.placa_sol": "slab on grade",
  "struct.tip.zidarie": "confined masonry wall",

  // --- STRUCTURĂ · verdictul unui element --------------------------------
  "struct.verdict.conform": "passes",
  "struct.verdict.se_verifica": "PASSES!",
  "struct.verdict.nu_se_verifica": "FAILS!!!",
  "struct.verdict.atentie": "CAUTION!!!",
  "struct.verdict.atentie_scurt": "CAUTION",
  "struct.verdict.la_limita": "review",
  "struct.verdict.neconform": "fails",

  // --- STRUCTURĂ · indexul de elemente -----------------------------------
  "struct.elem.criteriu_pmm": "P–M–M at the critical point",
  "struct.elem.sect_grosime": "{n} cm thick",
  "struct.elem.sect_placa": "{n} cm slab",
  "struct.elem.det_L": "L {n} m",
  "struct.elem.det_H": "H {n} m",
  "struct.elem.det_B": "B {n} m",
  "struct.elem.det_h": "h {n} cm",
  "struct.elem.det_stalpi#n": "{n} column|{n} columns|{n} columns",
  "struct.elem.niv_directie": "direction {dir}",
  "struct.elem.niv_fundatie": "foundation",
  "struct.elem.niv_jos": "bottom level",
  "struct.elem.ver_presiune_birou": "Effective pressure — in-house method",
  "struct.elem.ver_presiune_np112": "Effective pressure — NP 112",
  "struct.elem.ver_desprindere": "Footing uplift (active zone)",
  "struct.elem.desprindere_activ": "{n} m active",
  "struct.elem.ver_tasare": "Absolute settlement",
  "struct.elem.ver_presiune_model": "Pressure from the model (FEM reactions)",

  // --- STRUCTURĂ · clasa de execuție a elementului -----------------------
  "struct.clasa.identice#n": "{clasa} · {n} identical element|{clasa} · {n} identical elements|{clasa} · {n} identical elements",
  "struct.clasa.singurul": "{clasa} · the only one of its kind",

  // --- STRUCTURĂ · eticheta unei soluții ---------------------------------

  // --- MEP · the tab, the layers and the panel (`instalatii/`) ------------
  "inst.grup": "MEP services",
  "inst.verif.electrice_legat.pica": "Unconnected electrical devices: {n}",
  "inst.verif.lift.ok": "No route through a lift shaft",
  "inst.verif.lift.pica": "Routes through a lift shaft: {n}",
  "inst.verif.apa_alimentare.ok": "Every flat gets water from a building riser",
  "inst.verif.apa_alimentare.pica": "Flats or risers without water from the service connection: {n}",
  "inst.verif.ele_alimentare.ok": "Every flat has a board fed from the electrical riser",
  "inst.verif.ele_alimentare.pica": "Flats or risers without power from the metering cabinet: {n}",
  "inst.verif.ele_coloana.ok": "Voltage drop on the flat cables is within budget",
  "inst.verif.ele_coloana.pica": "Flat cables with too much voltage drop: {n}",
  "inst.verif.coloana_camera.ok": "No stack through another flat's habitable room",
  "inst.verif.coloana_camera.pica": "Stacks through habitable rooms (to divert or box in): {n}",
  "inst.verif.colector.ok": "Every stack reaches the collector and the street connection",
  "inst.verif.colector.pica": "Stacks with no route to the collector: {n}",
  "inst.verif.electrice_legat.ok": "Every electrical device is connected to the board",
  "inst.verif.cadere.pica": "Circuits with too much voltage drop: {n}",
  "inst.verif.cadere.ok": "Circuit voltage drop is under the limit",
  "inst.verif.comanda.pica": "Lights or switches without a pair: {n}",
  "inst.verif.comanda.ok": "Every light has its switch",
  "inst.verif.bucla.pica": "Underfloor loops too long: {n}",
  "inst.verif.bucla.ok": "Every underfloor loop is under the maximum length",
  "inst.verif.incalzire_legat.pica": "Unconnected heat emitters: {n}",
  "inst.verif.incalzire_legat.ok": "Every heat emitter is connected to the manifold",
  "inst.verif.ventilatie_legat.pica": "Unconnected air grilles: {n}",
  "inst.verif.ventilatie_legat.ok": "Every air grille is connected",
  "inst.verif.apa_viteza.pica": "Water pipes too narrow for their flow: {n}",
  "inst.verif.apa_viteza.ok": "Water velocity stays under the maximum",
  "inst.verif.ramura.pica": "Unvented branches too long: {n}",
  "inst.verif.ramura.ok": "No unvented branch longer than the maximum",
  "inst.verif.coloana.pica": "Stacks over capacity: {n}",
  "inst.verif.coloana.ok": "Every stack carries its flow",
  "inst.rol.bucla": "Underfloor loop",
  "inst.rol.alimentare": "Flat supply",
  "inst.rol.principala": "Main",
  "inst.rol.bransament": "Service connection",
  "inst.rol.colector": "Collector",
  "inst.rol.racord_retea": "Street sewer connection",
  "inst.rol.ventilatie_primara": "Primary vent",
  "inst.val.secunde": "{s} s",
  "inst.val.cota_racord": "manhole invert at {m} m; street sewer level to confirm with the utility",
  "inst.val.bucati": "{n} pcs",
  "inst.val.ghene": "{n}, of which {propuse} proposed",
  "inst.val.niveluri": "{n}: {calculate} computed, {copiate} copied from identical storeys",
  "inst.intrare.durata": "Routing time",
  "inst.intrare.racord_canalizare": "Street sewer connection",
  "inst.intrare.aparate": "Devices placed in modules",
  "inst.intrare.niveluri": "Storeys",
  "inst.optiune.boiler": "water heater",
  "inst.optiune.centrala": "boiler in the flat",
  "inst.optiune.modul_termic": "flat heat interface unit",
  "inst.optiune.radial": "radial: one pipe through the screed to each fixture",
  "inst.optiune.mixt": "mixed: through the screed to the wet wall, then along the wall",
  "inst.setare.pas_pardoseala": "Underfloor pipe spacing (m)",
  "inst.setare.wc_max": "Maximum WC – shaft distance (m)",
  "inst.setare.cadere_pardoseala": "How far a branch may drop through the floor (m)",
  "inst.setare.sursa_calda": "Hot water",
  "inst.setare.apa_varianta": "Water connection",
  "inst.setari.eroare": "The settings were not saved.",
  "inst.setari.salveaza": "Save and route again",
  "inst.setari.nota": "A layer over the code, for the whole project. Values below the code minimum are refused when routing.",
  "inst.setari": "Settings",
  "inst.solutia": "MEP v{n}",
  "inst.document_necitit": "The services document could not be read. Reload the page.",
  "inst.picata": "Routing the services failed: {motiv}",
  "inst.in_lucru": "Routing…",
  "inst.regenereaza": "Route again",
  "inst.genereaza": "Route the services",
  "inst.nicio_solutie": "This architecture has no services yet. The engine routes between the sanitary fixtures and the devices placed in the modules (Design palette): radiators, underfloor heating, air grilles, switches, lights, sockets. It adds no devices; whatever is missing shows up as a check.",
  "inst.fara_arhitectura": "Pick an architecture in the chain: the services are routed on a finished architecture.",
  "inst.verificari": "Checks",
  "inst.intrari": "Values used",
  "inst.cantitati": "Quantities",
  "inst.strat.aprinde": "Show: {ce}",
  "inst.strat.stinge": "Hide: {ce}",
  "inst.categorie.apa": "Water",
  "inst.categorie.ventilatie": "Ventilation",
  "inst.categorie.electrice": "Electrical",
  "inst.categorie.incendiu": "Fire",
  "inst.sistem.canalizare": "Drainage",
  "inst.sistem.pluviale": "Rainwater",
  "inst.sistem.ventilatie": "Ventilation",
  "inst.sistem.incalzire": "Heating",
  "inst.sistem.apa_rece": "Cold water",
  "inst.sistem.apa_calda": "Hot water",
  "inst.sistem.incendiu": "Firefighting",
  "inst.sistem.electrice": "Electrical",
  "inst.camp.sistem": "System",
  "inst.camp.rol": "Role",
  "inst.camp.diametru": "Size",
  "inst.camp.panta": "Slope",
  "inst.camp.material": "Material",
  "inst.camp.mediu": "Runs",
  "inst.camp.lungime": "Length",
  "inst.camp.echipament": "Equipment",
  "inst.camp.dimensiuni": "Dimensions",
  "inst.rol.ramura_wc": "WC branch",
  "inst.rol.ramura_obiecte": "Small fixtures branch",
  "inst.rol.racord": "Connection",
  "inst.rol.coloana": "Riser",
  "inst.rol.distributie": "Wall distribution",
  "inst.rol.intrare_sapa": "Single pipe through the screed",
  "inst.rol.tur": "Flow",
  "inst.rol.retur": "Return",
  "inst.rol.circuit": "Electrical circuit",
  "inst.rol.canal": "Air duct",
  "inst.mediu.placare": "in the wall lining",
  "inst.mediu.sapa": "through the screed",
  "inst.mediu.ghena": "in the shaft",
  "inst.mediu.plafon": "below the ceiling",
  "inst.mediu.aparent": "exposed",
  "inst.mediu.ingropat": "buried in the ground",
  "inst.echipament.ghena_propusa": "Proposed service shaft",
  "inst.echipament.contor": "Water meter",
  "inst.echipament.modul_termic": "Heat interface unit",
  "inst.echipament.distribuitor": "Manifold",
  "inst.echipament.tablou": "Distribution board",
  "inst.echipament.radiator": "Heating radiator",
  "inst.echipament.grila": "Extract grille",
  "inst.echipament.piesa_curatire": "Cleanout",
  "inst.echipament.centrala": "Flat boiler",
  "inst.echipament.boiler": "Water heater",
  "inst.echipament.camin_bransament": "Service chamber with the main meter",
  "inst.echipament.bmpt": "Electrical service box (BMPT)",
  "inst.echipament.firida_contorizare": "Distribution and metering cabinet",
  "inst.echipament.camin_racord": "Sewer connection manhole",
  "inst.material.pp": "PP",
  "inst.material.pe_x": "PE-X",
  "inst.material.pe_xa": "PE-Xa",
  "inst.material.tabla_zincata": "Galvanised sheet",
  "inst.material.cablu_cyy": "CYY-F cable in conduit",
  "inst.material.cablu_tub": "Cable in conduit",
  "inst.material.ppr": "PP-R",
  "inst.verif.racord.ok": "Every sanitary fixture has a drain and water",
  "inst.verif.racord.pica": "Fixtures without a drain or water: {n}",
  "inst.verif.wc_separat.ok": "Every WC has its own DN 100 branch",
  "inst.verif.wc_separat.pica": "Shared or undersized WC branches: {n}",
  "inst.verif.panta.ok": "Every drainage branch has the minimum slope",
  "inst.verif.panta.pica": "Branches below the minimum slope: {n}",
  "inst.verif.banda.ok": "Drainage stays in the bottom band of the wall lining",
  "inst.verif.banda.pica": "Drainage branches too high on the wall: {n}",
  "inst.verif.bucatarie.ok": "The kitchen drains into a stack with no WC",
  "inst.verif.bucatarie.pica": "Kitchen branches on a stack with a WC: {n}",
  "inst.verif.ghena.ok": "Risers have 10 cm in the shaft, between them and to the walls",
  "inst.verif.ghena.pica": "Risers too tight or outside a shaft: {n}",
  "inst.verif.apa_sus.ok": "On the wall, water runs above the drainage",
  "inst.verif.apa_sus.pica": "Water pipes too low on the wall: {n}",
  "inst.verif.apa_calda.ok": "Hot water only goes to fixtures with a mixer",
  "inst.verif.apa_calda.pica": "Hot water to fixtures without a mixer: {n}",
  "inst.verif.usa.ok": "No wall route crosses a door opening",
  "inst.verif.usa.pica": "Routes through a door opening: {n}",
  "inst.verif.dormitor.ok": "No pipe on a bedroom wall",
  "inst.verif.dormitor.pica": "Pipes on a bedroom wall: {n}",
  "inst.verif.tablou.ok": "No water pipe above the distribution board",
  "inst.verif.tablou.pica": "Water pipes above the distribution board: {n}",
  "inst.verif.ventilatie.ok": "Every wet room has its extract grille",
  "inst.verif.ventilatie.pica": "Wet rooms without extraction: {n}",
  "inst.verif.radiator.ok": "Every habitable room is heated: emitter, warm floor or air",
  "inst.verif.radiator.pica": "Habitable rooms without heating in the module: {n}",
  "inst.intrare.ghene": "Service shafts",
  "inst.val.metri": "{m} m",
  "inst.sursa.document": "from the document",
  "inst.sursa.structura": "from the structure",
  "inst.sursa.implicit": "default, to confirm",
  "inst.sursa.sugerat": "suggested, to confirm",

  // --- ASISTENTUL: panoul de chat din editorul de module ------------------
  "asistent.titlu": "Assistant",
  "agent.titlu": "Facade agent",
  "agent.discutie_blocata": "The facade agent is working…",
  "agent.lucreaza": "working on the facade",
  "agent.a_terminat": "finished",
  "agent.buton_lucreaza": "The facade agent is working — open to see what it is doing",
  "agent.pas.incep": "Starting variant {varianta} of {din}, from {referinte} references.",
  "agent.pas.citesc_fatada": "Reading the building's facades.",
  "agent.pas.compun": "Balconies and openings composed.",
  "agent.pas.compun_refuz": "Part of the composition did not fit; adjusting it.",
  "agent.pas.stil_pus": "The building is dressed in the chosen style.",
  "agent.pas.stil_invalid": "The style did not pass the check; correcting it.",
  "agent.pas.peste_nivel": "What I proposed is above the building's level; adapting it with the allowed materials.",
  "agent.pas.verific_randarile": "Looking at the building from {n} angles to compare it with the references.",
  "agent.pas.critic": "A designer compared the images with the references and made remarks ({n}); applying them.",
  "agent.pas.critic_bine": "A designer compared the images with the references and has no objections.",
  "agent.pas.gata": "The facade is done.",
  "agent.pas.editez": "Starting from the current facade and changing only what you asked for.",
  "agent.pas.refac": "Redoing the facade from scratch, after your photo and request.",
  "agent.pas.extrag_stilul": "Reading the style from the photos: how the balconies, volumes, materials and colours look.",
  "agent.pas.vitraj_sub_nivel": "The building standard asks for floor-to-ceiling glass and more of it; composing once more.",
  "agent.pas.tipar_necompus": "What I read in the photo about the balconies is not in the composition yet; composing once more.",
  "agent.pas.privesc_inainte": "Looking at the current facade before changing anything.",
  "agent.pas.verific_schimbarea": "Looking at the building from {n} angles to see the change.",
  "agent.pas.critic_editare": "A designer compared before and after with your request and asks for {n} adjustments; making them.",
  "agent.pas.critic_editare_bine": "A designer compared before and after: the change answers your request.",
  "agent.pas.gata_editare": "The change is on the facade.",
  "agent.pas.oprit": "Stopped on request.",
  "agent.pas.picat": "The agent stopped with an error; the solution keeps its previous style.",
  "agent.pas.fara_fatada": "The solution has no laid-out buildings, so there is no facade to dress.",
  "agent.cu_eroare": "stopped with an error",
  "agent.pasi#n": "1 step|{n} steps|{n} steps",
  "agent.depanare.titlu": "Technical details: what the model asked each tool and what it got back",
  "agent.depanare.ok": "accepted",
  "agent.depanare.refuzat": "refused",
  "agent.depanare.rezultat": "result",
  "agent.depanare.argumente": "the model's request",
  "agent.meta.varianta": "Variant {varianta} of {din}",
  "agent.meta.referinte#n": "1 reference|{n} references|{n} references",
  "agent.meta.unghiuri#n": "1 angle checked|{n} angles checked|{n} angles checked",
  "agent.meta.observatii#n": "1 remark applied|{n} remarks applied|{n} remarks applied",
  "agent.scurt.incep": "Variant {varianta} of {din}",
  "agent.scurt.citesc_fatada": "Facades read",
  "agent.scurt.compun": "Balconies and openings composed",
  "agent.scurt.compun_refuz": "Composition adjusted",
  "agent.scurt.stil_pus": "Style applied",
  "agent.scurt.stil_invalid": "Style corrected",
  "agent.scurt.peste_nivel": "Fitted to the building's standard",
  "agent.scurt.verific_randarile": "Checked from {n} angles",
  "agent.scurt.critic": "Design remarks: {n}",
  "agent.scurt.critic_bine": "No design remarks",
  "agent.scurt.gata": "Done",
  "agent.scurt.editez": "Starting from the current facade",
  "agent.scurt.privesc_inainte": "Current facade reviewed",
  "agent.scurt.verific_schimbarea": "Change seen from {n} angles",
  "agent.scurt.critic_editare": "Adjustments asked: {n}",
  "agent.scurt.critic_editare_bine": "The change matches the request",
  "agent.scurt.gata_editare": "Change applied",
  "agent.scurt.oprit": "Stopped on request",
  "agent.scurt.picat": "Stopped with an error",
  "agent.scurt.fara_fatada": "No facade to dress",
  // --- SITE PLAN AGENT (`viewer/plan_agent/AsistentPlan.tsx`; «Refă planul cu agentul» e în meniul soluției) ---
  "agent_plan.titlu": "Site plan agent",
  "agent_plan.asistent": "Site plan assistant",
  "agent_plan.lucreaza": "working on the plan",
  "agent_plan.discutie_blocata": "The site plan agent is working…",
  "agent_plan.buton_lucreaza": "The site plan agent is working — open to see what it is doing",
  "agent_plan.sel.loc": "stall {nume}",
  "agent_plan.sel.alee": "aisle {nume}",
  "agent_plan.sel.trotuar": "sidewalk {nume}",
  "agent_plan.sel.trecere": "crossing {nume}",
  "agent_plan.sel.zona": "zone {nume}",
  "agent_plan.sel.corp": "building {nume}",
  "agent_plan.sel.scara_evacuare": "evacuation stair {nume}",
  "agent_plan.sel.acces": "entrance {nume}",
  "agent_plan.pas.incep": "Starting from the empty site: circulation, parking spaces, sidewalks, zones.",
  "agent_plan.pas.editez": "Starting from the current plan and changing only what you asked for.",
  "agent_plan.pas.lot_scris": "Written onto the drawing: {ce}.",
  "agent_plan.pas.lot_respins": "A batch did not pass the check ({n} problems); redoing it.",
  "agent_plan.pas.gata": "The plan is done: {locuri} parking spaces, {verde_pct} % green.",
  "agent_plan.pas.gata_cu_blocaje": "The plan is done, with {n} things I could not resolve.",
  "agent_plan.pas.limita": "Stopped at the work limit; the drawing keeps what was written so far.",
  "agent_plan.pas.gata_refuzata": "Tried to finish, but the drawing has {n} open issues; trying again.",
  "agent_plan.pas.oprit_pe_probleme": "Stopped: the plan cannot be closed with {n} open issues; the drawing keeps what was written.",
  "agent_plan.pas.oprit": "Stopped on request.",
  "agent_plan.pas.picat": "The agent stopped with an error; the drawing keeps what was written until then.",
  "agent_plan.pas.fara_cheie": "The agent did not run: the server has no model key.",
  "agent_plan.scurt.incep": "From the empty site",
  "agent_plan.scurt.editez": "Starting from the current plan",
  "agent_plan.scurt.lot_scris": "Written: {ce}",
  "agent_plan.scurt.lot_respins": "Batch rejected ({n})",
  "agent_plan.scurt.gata": "Done: {locuri} spaces, {verde_pct} % green",
  "agent_plan.scurt.gata_cu_blocaje": "Done, with {n} blockers",
  "agent_plan.scurt.limita": "Stopped at the limit",
  "agent_plan.scurt.oprit": "Stopped on request",
  "agent_plan.scurt.picat": "Stopped with an error",
  "agent_plan.scurt.fara_cheie": "Did not run: no key",
  "agent_plan.locuri": "Parking spaces",
  "agent_plan.din_tinta": "{locuri} of {tinta}",
  "agent_plan.verde": "Green space",
  "agent_plan.verde_cota": "{pct} % · quota {cota} %",
  "agent_plan.loturi": "{ok} batches written · {respinse} rejected",
  "agent_plan.tururi#n": "1 round|{n} rounds|{n} rounds",
  "agent_plan.cost": "{cost} $ on the model",
  "agent_plan.revizie": "revision {n}",
  "agent_plan.blocaje#n": "1 blocker|{n} blockers|{n} blockers",
  "agent_plan.refa": "Redo the plan with the agent",
  "agent_plan.refa_expl": "Starts the agent from the empty site: circulation, parking spaces, sidewalks and zones are rewritten onto this plan, batch by batch (minutes).",
  "agent_plan.refa_confirma": "The agent rewrites the circulation, parking spaces, sidewalks and zones of this plan from scratch. The current drawing is lost. Continue?",
  "agent_plan.pornit": "The agent has started; the drawing changes as it writes.",
  "agent_plan.deja_ruleaza": "The agent is already working on this plan; wait for it to finish.",
  "agent_plan.dupa_circulatie": "The assistant becomes available once the plan agent starts on the circulation (after the architecture).",
  "agent_plan.card.pornita": "The agent has started; waiting for its first steps…",
  "agent_plan.card.din_buton": "from “Redo the plan with the agent”",
  "agent_plan.card.gasita": "run started before this page was opened",
  "asistent.deschide": "Ask the assistant for a change",
  "asistent.inchide": "Close the assistant",
  "asistent.conversatie_noua": "New conversation: delete everything said so far",
  "asistent.golire_esec": "Could not delete the conversation on the server. The panel is empty, but what was there may come back on reload.",
  "asistent.strange": "Collapse the panel",
  "asistent.desfa": "Expand the panel",
  "asistent.citeste": "Read the answers out loud",
  "asistent.nu_citi": "Stop reading the answers out loud",
  "asistent.dicteaza": "Say your request out loud",
  "asistent.opreste_dictarea": "Done — send what I said",
  "asistent.fara_microfon": "This browser has no speech recognition. Chrome does.",
  "asistent.microfon_refuzat": "The microphone is blocked for this page. Allow it from the browser address bar and try again.",
  "asistent.microfon_esec": "Could not listen: {cod}.",
  "asistent.scrie": "Say what you want changed…",
  "asistent.trimite": "Send",
  "asistent.ataseaza_poza": "Attach a photo (facade reference)",
  "asistent.scoate_poza": "Remove the photo",
  "asistent.poza_prompt": "Write the facade style from this photo.",
  "asistent.opreste": "Stop",
  "asistent.se_gandeste": "thinking…",
  "asistent.rationament": "Reasoning",
  "asistent.rationament_arata": "See how it reasoned",
  "asistent.gol_titlu": "What should change in this module?",
  "asistent.gol_text": "Say it in your own words. It reads the plan, makes the change on the open drawing and shows you what it touched. Saving stays with you.",
  "asistent.exemplu_usi": "Make every door sliding, except the entrance ones",
  "asistent.exemplu_pereti": "Move the partition walls 12 cm towards the corridor",
  "asistent.exemplu_masura": "What do the bathrooms on side A add up to?",
  "asistent.gol_titlu_fatada": "What should change on the facade?",
  "asistent.gol_titlu_situatie": "What should change on the site plan?",
  "asistent.selectie": "Selected",
  "asistent.scoate_din_selectie": "Remove from selection",
  "asistent.goleste_selectia": "Clear",
  "detalii.titlu_panou": "Element details",
  "detalii.cote": "Dimensions",
  "detalii.cote_titlu": "Show dimensions on the model",
  "detalii.inchide": "Close and deselect",
  "detalii.anterior": "Previous selected element",
  "detalii.urmator": "Next selected element",
  "detalii.din": "{i} of {n}",
  "detalii.lipsa": "The element is no longer in the solution's document.",
  "detalii.grup.dimensiuni": "Dimensions",
  "detalii.grup.material": "Material",
  "detalii.grup.tamplarie": "Joinery",
  "detalii.grup.balcon": "Balcony",
  "detalii.grup.loc": "Location",
  "detalii.grup.cost": "Costs",
  "detalii.cost.se_calculeaza": "Calculating…",
  "detalii.cost.material": "Materials",
  "detalii.cost.manopera": "Labour",
  "detalii.cost.total": "Total cost",
  "detalii.cost.nota": "Direct cost, without mark-ups, at the estimated prices of the façade estimate.",
  "detalii.placa_nr": "Tile",
  "detalii.sectiune": "Section",
  "detalii.profil_mana": "Profile",
  "detalii.v.profil.rect": "rectangular",
  "detalii.v.profil.round": "round (tube)",
  "material.mana_curenta_otel": "Painted steel",
  "material.mana_curenta_aluminiu": "Aluminium",
  "material.mana_curenta_inox": "Stainless steel",
  "material.mana_curenta_lemn": "Timber",
  "material.profil_sticla_aluminiu": "Aluminium",
  "material.profil_sticla_inox": "Stainless steel",
  "detalii.format": "Tile format (stock)",
  "detalii.latime": "Width",
  "detalii.inaltime": "Height",
  "detalii.parapet": "Sill height",
  "detalii.suprafata": "Area",
  "detalii.lungime": "Length",
  "detalii.grosime": "Thickness",
  "detalii.cota_jos": "Bottom level (from floor)",
  "detalii.arie_bruta": "Gross area",
  "detalii.arie_goluri": "Of which openings",
  "detalii.arie_neta": "Net area",
  "detalii.volum": "Volume",
  "detalii.arie": "Area",
  "detalii.adancime": "Depth",
  "detalii.fel": "Type",
  "detalii.pe": "On",
  "detalii.plantare": "Planting",
  "detalii.strat": "Layer",
  "detalii.unde": "On the facade",
  "detalii.placi": "Tiles",
  "detalii.rost": "Joint",
  "detalii.material": "Material",
  "detalii.culoare": "Colour",
  "detalii.rama": "Frame",
  "detalii.culoare_toc": "Frame colour",
  "detalii.canate": "Leaves",
  "detalii.deschidere": "Opening",
  "detalii.vitraj": "Glazing",
  "detalii.profil": "Profile",
  "detalii.adancime_toc": "Frame depth",
  "detalii.prag": "Threshold",
  "detalii.model": "Model",
  "detalii.inchidere": "Enclosure",
  "detalii.pe_colt": "On a corner",
  "detalii.varianta": "Variant",
  "detalii.compozitie": "Composition",
  "detalii.sistem": "System",
  "detalii.montanti": "Mullions",
  "detalii.modul": "Module",
  "detalii.culoare_sticla": "Glass colour",
  "detalii.culoare_opac": "Spandrel colour",
  "detalii.arie_vitrata": "Vision area",
  "detalii.arie_opaca": "Spandrel area",
  "detalii.ferestre_cortina": "Concealed vents",
  "detalii.usi": "Doors",
  "detalii.peste_placa": "Above the slab (parapet)",
  "detalii.balustrade": "On the edge",
  "detalii.corp": "Building",
  "detalii.etaj": "Storey",
  "detalii.coloana": "Column of flats",
  "detalii.v.deschidere.fixed": "fixed",
  "detalii.v.deschidere.casement": "casement",
  "detalii.v.deschidere.tilt_turn": "tilt and turn",
  "detalii.v.deschidere.sliding": "sliding",
  "detalii.v.vitraj.single": "single",
  "detalii.v.vitraj.double": "double",
  "detalii.v.vitraj.triple": "triple",
  "detalii.v.model.single": "single leaf",
  "detalii.v.model.double": "double leaf",
  "detalii.v.model.fixed_door": "door with fixed light",
  "detalii.v.fel.glass": "glass",
  "detalii.v.fel.slats": "slats",
  "detalii.v.fel.solid": "solid parapet",
  "detalii.v.fel.planter": "planter",
  "detalii.v.fel.screen": "fixed screen",
  "detalii.v.fel.plaster": "render",
  "detalii.v.fel.cladding": "cladding",
  "detalii.v.fel.brick": "brick",
  "detalii.v.fel.glazing": "glazed field",
  "detalii.v.fel.curtain_wall": "curtain wall",
  "detalii.v.fel.band": "band",
  "detalii.v.fel.sill": "sill",
  "detalii.v.fel.flooring": "flooring",
  "detalii.v.fel.insulation": "insulation",
  "detalii.v.fel.render": "base coat",
  "detalii.v.fel.subframe": "subframe",
  "detalii.v.pe.balcony": "balcony",
  "detalii.v.pe.terrace": "terrace",
  "detalii.v.pe.opening": "tall window",
  "detalii.v.pe.loggia": "loggia",
  "detalii.v.plantare.hanging": "trailing",
  "detalii.v.plantare.upright": "upright",
  "detalii.v.strat.finish": "finish",
  "detalii.v.strat.thermal": "insulation",
  "detalii.v.strat.render": "base coat",
  "detalii.v.strat.relief": "relief",
  "detalii.v.strat.subframe": "subframe",
  "detalii.v.strat.curtain": "curtain wall",
  "detalii.v.varianta_cortina.standard": "stick (mullion-transom)",
  "detalii.v.varianta_cortina.horizontal": "horizontal only (HL)",
  "detalii.v.varianta_cortina.vertical": "vertical only (VL)",
  "detalii.v.varianta_cortina.structural": "structural glazing (SG)",
  "detalii.v.unde.facade": "facade",
  "detalii.v.unde.plinth": "plinth",
  "detalii.v.unde.recess": "loggia",
  "detalii.v.unde.parapet": "parapet",
  "detalii.v.unde.soffit": "soffit",
  "detalii.v.unde.balcony": "balcony",
  "detalii.v.unde.terrace": "terrace",
  "detalii.v.unde.loggia": "loggia",
  "detalii.v.unde.reveal": "reveal",
  "detalii.v.unde.cheek": "balcony side wall",
  "detalii.v.unde.divider": "divider",
  "detalii.v.unde.floor": "floor",
  "detalii.v.inchidere.box": "box (closed sides)",
  "detalii.v.inchidere.open": "open",
  "detalii.v.da.da": "yes",
  "detalii.v.etaj.parter": "ground floor",
  "material.aluminiu": "Aluminium",
  "material.pvc": "PVC",
  "material.lemn": "Timber",
  "material.dale_terasa": "Terrace pavers",
  "material.dale_portelanate": "Porcelain pavers",
  "material.gresie_exterior": "Outdoor tiles",
  "material.deck_compozit": "Composite decking",
  "material.deck_lemn": "Timber decking",
  "material.etics": "External insulation (ETICS)",
  "material.etics_intrados": "ETICS on the soffit",
  "material.glaf_atic": "Parapet coping",
  "material.glaf_aluminiu": "Aluminium sill",
  "material.hidroizolatie": "Waterproofing",
  "material.hidroizolatie_balcon": "Balcony waterproofing",
  "material.lemn_vertical": "Timber, vertical boards",
  "material.lemn_ingust": "Timber, narrow battens",
  "material.lemn_riflaj": "Timber, fluted",
  "material.lemn_panouri": "Wood-effect panels",
  "material.placaj_hpl": "HPL cladding",
  "material.lamele_verticale": "Vertical fins",
  "material.panou_metal": "Metal panel",
  "material.parapet_lamele": "Slatted parapet",
  "material.parapet_sticla": "Glass parapet",
  "material.piatra_granit": "Granite",
  "material.piatra_travertin": "Travertine",
  "material.piatra_ardezie": "Slate",
  "material.piatra_zidita": "Coursed stone",
  "material.placaj_piatra": "Stone cladding",
  "material.pietris": "Gravel",
  "material.placaj_ceramic": "Ceramic cladding",
  "material.placaj_metalic": "Metal cladding",
  "material.ploturi_reglabile": "Adjustable pedestals",
  "material.sapa_panta": "Sloped screed",
  "material.sticla": "Glass",
  "material.perete_cortina": "Stick curtain wall",
  "material.tencuiala_baza": "Base coat",
  "material.tencuiala_decorativa": "Decorative render",
  "material.termoizolatie_terasa": "Roof insulation",
  "material.xps_soclu": "XPS at the plinth",
  "material.caramida_aparenta": "Facing brick",
  "material.profil_eps": "EPS profile",
  "material.tabla_perforata": "Perforated sheet",
  "material.perete_verde": "Green wall",
  "material.prag_aluminiu": "Aluminium threshold",
  "asistent.gol_text_fatada": "Small changes I make right away; big ones I hand to the facade agent. Every change can be undone while you stay on the project.",
  "asistent.gol_text_situatie": "Say it in your own words. Every change is made by the site plan agent, in a few minutes, and the drawing changes before your eyes.",
  "asistent.gol_titlu_costuri": "What would you like to know about the estimate?",
  "asistent.gol_text_costuri": "I'll tell you where every figure comes from and how it is worked out. A «what if…» becomes a new scenario with its own name, and the page moves to it; the current one stays under «Scenarios».",
  "asistent.exemplu_costuri_de_unde": "Where does the structure cost come from?",
  "asistent.exemplu_costuri_pret": "What if we sell 10 % higher?",
  "asistent.exemplu_costuri_structura": "Make me a scenario with the structure 10 % dearer and a 4-year duration",
  "asistent.scrie_costuri": "Ask about the estimate or ask for a variant…",
  "asistent.exemplu_fatada_culoare": "Make the balcony glass clear, not black.",
  "asistent.exemplu_fatada_material": "Put wood on the top-floor parapet.",
  "asistent.exemplu_fatada_poza": "I want the balconies like in this photo.",
  "asistent.exemplu_situatie_locuri": "I want more parking spaces near the entrance.",
  "asistent.exemplu_situatie_trecere": "Put a pedestrian crossing in front of building C2.",
  "asistent.exemplu_situatie_verde": "Less asphalt; move the playground to the south.",
  "asistent.pas.modific": "Changing the facade…",
  "asistent.pas.modificat": "Changed the facade on the solution",
  "asistent.pas.predau": "Handing the request to the facade agent…",
  "asistent.pas.predat": "The facade agent took over the request",
  "asistent.pas.anulez": "Restoring the previous facade…",
  "asistent.pas.anulat": "The previous facade is back",
  "asistent.pas.refac": "Putting the change back…",
  "asistent.pas.refacut": "The change is back on the facade",
  "asistent.pas.citesc_plan": "Reading the site plan…",
  "asistent.pas.citit_plan": "Site plan read",
  "asistent.pas.predau_plan": "Handing the request to the site plan agent…",
  "asistent.pas.predat_plan": "The site plan agent took over the request",
  "asistent.pas.nu_a_mers": "Didn't work",
  "asistent.doar_citire": "You have read access on this project: the assistant can measure and explain, but cannot change the drawing.",
  "asistent.plan": "Plan",
  "asistent.se_aplica": "applying",
  "asistent.esuat": "failed",
  "asistent.gata": "done",
  "asistent.captura": "Plan snapshot",
  "asistent.ocupat": "This conversation already has an answer under way. Wait for it or stop it.",
  "asistent.prea_multi_pasi": "The assistant took too many steps without asking. Tell it more precisely what you want.",
  "asistent.esec": "Something went wrong with the assistant. The conversation is saved — try again.",
  "asistent.pagina_veche": "This page was opened before a release, so the assistant can no longer run code. Reload — if you have unsaved work, the browser asks first.",
  "asistent.reincarca": "Reload the page",
  "asistent.alta_fereastra": "This conversation is also open in another window, which has just written. Wait a moment and try again.",
  "asistent.iesit": "Your session expired. Sign in again.",
  "asistent.fara_acces": "You do not have write access on this project.",
  "asistent.tokeni": "{in} in · {out} out",
  "asistent.socoteala_nota": "The figures are for the whole conversation: one answer with tools means several requests to the model.",
  "asistent.nou": "New",
  "asistent.incearca": "Try",
  "asistent.arata_pe_macheta": "Point at it on the model",
  "asistent.tasta_clic": "Click",
  "asistent.tasta_mai_multe": "⇧ + click",
  "asistent.tasta_zona": "⌥/Alt + drag",
  "asistent.un_element": "one element",
  "asistent.mai_multe_elemente": "several elements",
  "asistent.o_zona": "a facade zone",
  "asistent.despre": "about {ce}",
  "asistent.n_elemente#n": "1 element|{n} elements|{n} elements",
  "asistent.arata_tot": "Show the whole selection",

  // --- STRUCTURĂ · numele celor 19 pași ----------------------------------
  "struct.pas.titlu": "Step {nr} — {nume}",
  "struct.pas.scurt": "Step {nr}",
  "struct.pas.studiu_geotehnic": "Geotechnical survey",
  "struct.pas.analiza_arhitectura": "Architecture analysis",
  "struct.pas.strategie_arhitecturala": "Structural strategy",
  "struct.pas.model_structura": "Structural model",
  "struct.pas.fundatie_estimata": "Estimated foundation",
  "struct.pas.rezistenta_fundatie": "Foundation pre-sizing",
  "struct.pas.model_fem": "Structural modelling (FEM)",
  "struct.pas.incarcari": "Loads",
  "struct.pas.seism": "Seismic analysis",
  "struct.pas.deplasari_laterale": "Lateral displacements (drift)",
  "struct.pas.combinatii": "Load combinations",
  "struct.pas.eforturi": "Final internal forces",
  "struct.pas.armare_grinzi": "Beam reinforcement",
  "struct.pas.armare_pereti": "Wall reinforcement (basement and structural walls)",
  "struct.pas.armare_stalpi": "Column reinforcement + joints",
  "struct.pas.armare_placa": "Slab reinforcement",
  "struct.pas.verificare_fundatie": "Foundation check (real reactions)",
  "struct.pas.armare_fundatie": "Foundation beam reinforcement",
  "struct.pas.optimizare_structura": "Solution search (optimisation)",
  "struct.pas.link_cu_ref": "{pas} — open with {ref} selected",
  "struct.pas.link_verificare": "{pas} — open the check",

  // --- STRUCTURĂ · cele 6 capitole de citire -----------------------------
  "struct.cap.1.titlu": "What the client gave me",
  "struct.cap.1.intrebare": "“What do my documents say?”",
  "struct.cap.1.granularitate": "per document · layer · level · wall",
  "struct.cap.2.titlu": "What structure I propose",
  "struct.cap.2.intrebare": "“Which system, and where do the elements go?”",
  "struct.cap.2.granularitate": "per building → element",
  "struct.cap.3.titlu": "The model and what pushes on it",
  "struct.cap.3.intrebare": "“Is the model credible? How does it move?”",
  "struct.cap.3.granularitate": "per mode · case · direction · combination · level",
  "struct.cap.4.titlu": "The foundation",
  "struct.cap.4.intrebare": "“Does the footing hold under the real reactions?”",
  "struct.cap.4.granularitate": "per AXIS × combination × method · 3 readings of the same pressure",
  "struct.cap.5.titlu": "The element",
  "struct.cap.5.intrebare": "“Does this element hold, and with what reinforcement?”",
  "struct.cap.5.granularitate": "per element × point × zone",
  "struct.cap.6.titlu": "The solution",
  "struct.cap.6.intrebare": "“Do I have a solution that passes, and what does it cost?”",
  "struct.cap.6.granularitate": "per solution — the branches of the tree, 4 phase roles",
  "struct.cap.ref.p1_geotehnic": "S1 geotechnical",
  "struct.cap.ref.p2_arhitectura": "S2 architecture",
  "struct.cap.ref.p3_strategie": "S3 strategy",
  "struct.cap.ref.p4_model": "S4 model",
  "struct.cap.ref.p5_estimare": "S5 estimate",
  "struct.cap.ref.p6_predimensionare": "S6 pre-sizing",
  "struct.cap.ref.p17_verificarea": "S17 the check",
  "struct.cap.ref.p18_armarea": "S18 reinforcement →",
  "struct.cap.ref.p18_fundare": "S18 foundation",
  "struct.cap.ref.p19_cautarea": "S19 the search",

  // --- STRUCTURĂ · referințele scurte de pe cardul capitolului -----------
  "struct.cap.ref.p7": "S7",
  "struct.cap.ref.p8": "S8",
  "struct.cap.ref.p9": "S9",
  "struct.cap.ref.p10": "S10",
  "struct.cap.ref.p11": "S11",
  "struct.cap.ref.p12": "S12",
  "struct.cap.ref.p13": "S13",
  "struct.cap.ref.p14": "S14",
  "struct.cap.ref.p15": "S15",
  "struct.cap.ref.p16": "S16",

  // --- STRUCTURĂ · Acasă — harta capitolelor -----------------------------
  "struct.acasa.titlu": "Structure — map of the chapters",
  "struct.acasa.chip": "Structure",
  "struct.acasa.sub": "the 19 calculation steps, grouped into 6 chapters to read — the 1→18 numbering is the order they RUN in, not the order they are read in",
  "struct.acasa.vezi_pipeline": "see the calculation pipeline (19 steps) →",
  "struct.acasa.cap_nr": "CH. {nr}",
  "struct.acasa.nerulat": "not run yet",
  "struct.acasa.partial": "in progress / partial",
  "struct.acasa.um_t1": "s · T₁",
  "struct.acasa.um_picate_cost": "failed · {cost} k€",
  "struct.acasa.um_picate": "failed checks",
  "struct.acasa.um_picate_din": "failed out of {total}",
  "struct.acasa.bucla_titlu": "The loop:",
  "struct.acasa.bucla": "chapter 6 rewrites chapter 2 (sections, positions) and replays chapters 3–5. That is why <b>the Solution is the home page</b>, not the last step: the question you walk into a project with is “what is still failing”, not “what does the geotechnical survey say”.",

  // --- STRUCTURĂ · antetul unui pas --------------------------------------
  "struct.antet.capitol": "Ch. {nr} — {titlu}",

  // --- STRUCTURĂ · Pipeline — cei 19 pași --------------------------------
  "struct.pipe.titlu": "Pipeline — the 19 calculation steps",
  "struct.pipe.ordine": "the order they run in",
  "struct.pipe.harta": "map of the chapters →",
  "struct.pipe.harta_titlu": "the same 19 steps, grouped into the 6 chapters you READ — with each chapter's headline figure",
  "struct.pipe.opreste": "Stop",
  "struct.pipe.opreste_titlu": "Stops the chain after the current step",
  "struct.pipe.capitolul": "Chapter {nr}",
  "struct.pipe.imbunatatiri": "Planned improvements →",
  "struct.pipe.fara_reactiuni": "no reactions",
  "struct.pipe.fara_reactiuni_titlu": "The foundation is only PRE-SIZED. Checking it against the real reactions needs steps 7–12 to have run; «Run everything» replays step 6 on its own.",
  "struct.pipe.ruleaza": "running",
  "struct.pipe.ruleaza_titlu": "The server is working on this step right now",

  // --- STRUCTURĂ · antetul comun al unui pas -----------------------------
  "struct.antet.pipeline": "Pipeline",
  "struct.antet.verdicte_titlu": "this step's verdicts, as step 19 collects them on the current state",
  "struct.antet.picate": "{picate} failed out of {total}",
  "struct.antet.toate_trec": "{n} checks · all pass",
  "struct.antet.la_limita": "{n} to review",
  "struct.antet.fara_verdicte": "no verdicts of its own — this step decides, it does not check",

  // --- STRUCTURĂ · fișa pasului, la hover pe titlu -----------------------
  "struct.fisa.intrebare": "The question it answers",
  "struct.fisa.ce_face": "What it does",
  "struct.fisa.primeste": "What it takes in, and from where",
  "struct.fisa.decide": "What this step decides, and nobody else",
  "struct.fisa.verifica": "What it checks",
  "struct.fisa.da_mai_departe": "Who it hands on to",
  "struct.fisa.capcane": "⚠ What is most often misunderstood",
  "struct.fisa.eticheta": "What step {nr} does",
  "struct.fisa.semn": "what this step does",
  "struct.fisa.are_nevoie": "It needs:",

  // --- STRUCTURĂ · notele pasului ----------------------------------------
  "struct.note.titlu": "Step notes",
  "struct.note.ce": "How the figures above were obtained — what was read, what was filled in, what was assumed. These are not checks: nothing here passes or fails. The verdicts sit in this step's check tables and add up in step 19.",
  "struct.note.armarii": "Reinforcement notes",
  "struct.note.armarii_ce": "Calculation diagnostics on individual beams — a bending-moment diagram that does not have the expected shape, a support treated differently. The beams' verdicts (axial force and torsion included) sit in each beam's tables and add up in step 19.",
  "struct.note.modelului": "Model notes",
  "struct.note.modelului_ce": "What went into the model and with what value — walls cast in concrete instead of masonry, axes widened individually by step 17, elements filled in. These are not checks; the model's verdicts are the modal gate and the participating mass, above.",
  "struct.note.panouri": "Panel notes",
  "struct.note.combinatii": "Combination notes",

  // --- STRUCTURĂ · deciziile pasului -------------------------------------
  "struct.decizii.titlu": "Step decisions",
  "struct.decizii.intro": "Parameters this step chose or assumed — not calculation results, and not checks. You change them by changing the input in the last column, then re-running the chain.",
  "struct.decizii.col_ce": "What was decided",
  "struct.decizii.col_valoare": "Value",
  "struct.decizii.col_sursa": "Where it comes from",
  "struct.decizii.col_schimba": "What it changes in the calculation",
  "struct.decizii.col_cum": "How to change it",
  "struct.decizii.pas2": "Step 2 decisions",
  "struct.decizii.pas3": "Step 3 decisions",
  "struct.decizii.pas6": "Step 6 decisions",
  "struct.decizii.pas7": "Step 7 decisions",

  // --- STRUCTURĂ · cartonașul de explicație ------------------------------
  "struct.info.pe_scurt": "In brief",
  "struct.info.de_ce": "Why it is so",
  "struct.info.parametrii": "The parameters, one by one — and where each comes from",
  "struct.info.de_unde_vine": "where it comes from: {sursa}",
  "struct.info.explicatie": "Explanation",
  "struct.info.explicatie_pentru": "Explanation: {ce}",
  "struct.hover.mai_jos": "⌄ more below — scroll inside the card",
  "struct.hover.inchide": "✕ close (Esc)",
  "struct.hover.fixeaza": "click = pin",

  // --- STRUCTURĂ · simbolul explicat -------------------------------------
  "struct.simbol.ce_fel": "What kind of figure it is",
  "struct.simbol.formula": "Formula:",
  "struct.simbol.cum_obtinut": "How the figure was obtained:",
  "struct.simbol.articol": "Clause:",
  "struct.simbol.contexte": "In each check it means:",
  "struct.simbol.de_unde_aici": "Where it comes from here:",

  // --- STRUCTURĂ · op-urile refuzate de Pasul 4 --------------------------
  "struct.opuri.titlu": "Operations refused by the step 4 validation: {n}",
  "struct.opuri.grupate#n": ", grouped into {n} cause|, grouped into {n} causes|, grouped into {n} causes",
  "struct.opuri.col_elemente": "The elements",
  "struct.opuri.col_context": "The context",
  "struct.opuri.col_motiv": "Reason for the refusal",
  "struct.opuri.col_ce": "What happened",
  "struct.opuri.aceeasi_cauza#n": "{n} element with the same cause|{n} elements with the same cause|{n} elements with the same cause",

  // --- STRUCTURĂ · bara corpurilor ---------------------------------------
  "struct.corpuri.aria": "The solution's buildings",
  "struct.corpuri.ansamblu": "ensemble",
  "struct.corpuri.toate_titlu": "The whole complex: every block in one frame, each analysed on its own",
  "struct.corpuri.picate#n": "{cod} — 1 critical or global check fails|{cod} — {n} critical and global checks fail|{cod} — {n} critical and global checks fail",

  // --- STRUCTURĂ · top solicitări ----------------------------------------
  "struct.top.grinzi": "Highest demands — beams",
  "struct.top.stalpi": "Highest demands — columns",
  "struct.top.sub": "{elemente} elements × {combinatii} combinations — the maximum of each element is shown",
  "struct.top.nota_stalpi": "by maximum axial force; the concurrent (N, M₂, M₃) triplets feed straight into P–M–M at step 15",
  "struct.top.nivel": "Level",
  "struct.top.combinatia": "Combination",
  "struct.top.punctul": "Point",
  "struct.top.elementul": "the element →",

  // --- STRUCTURĂ · evidențierea în 3D ------------------------------------
  "struct.evid.titlu": "Highlight",
  "struct.evid.nimic": "nothing",
  "struct.evid.nimic_titlu": "nothing highlighted: the scene's own colours",
  "struct.evid.familie_bare": "bars",
  "struct.evid.familie_etrieri": "stirrups",
  "struct.evid.familie_stalpi": "columns",
  "struct.evid.familie_grinzi": "beams",
  "struct.evid.familie_fundatie": "foundation beams",
  "struct.evid.tip": "type {n}",
  "struct.evid.bucati": "{n} pcs",
  "struct.evid.bucati_in": "{n} pcs in {elemente} elements",
  "struct.evid.elemente": "{n} elements",
  "struct.evid.rand#n": "{n} type of {nume}: pick one to see where it is in the building, or the whole family|{n} types of {nume}: pick one to see where it is in the building, or the whole family|{n} types of {nume}: pick one to see where it is in the building, or the whole family",

  // --- STRUCTURĂ · layerele scenei 3D ------------------------------------
  "struct.layere.fel": "Rendering mode",
  "struct.layere.randare": "{nume} rendering",

  // --- STRUCTURĂ · scheletul de încărcare al modelului 3D ----------------
  "struct.schelet.incarca": "Loading the 3D model",

  // --- STRUCTURĂ · metoda implementată vs normativ -----------------------
  "struct.am.titlu": "Implemented method vs code",
  "struct.am.intro": "The default behaviour is this office's way of working. The code check runs anyway, in parallel — it does not replace the result, it accompanies it. Open a point to see both versions side by side.",
  "struct.am.nume_atentie": "with an impact on this project",
  "struct.am.puncte#n": "— {n} point of divergence|— {n} points of divergence|— {n} points of divergence",
  "struct.am.din_care": ", of which {n} {nume}",
  "struct.am.stringe": "collapse them all",
  "struct.am.desfasoara": "expand them all",
  "struct.am.comutator": "switch:",
  "struct.am.comutator_titlu": "where the switch for this behaviour lives",
  "struct.am.impact": "Impact on this project",
  "struct.am.implementat": "Implemented",
  "struct.am.normativ": "Code",

  // --- STRUCTURĂ · tabelul γ_Rd ------------------------------------------
  "struct.gamma.titlu": "γ_Rd — overstrength in capacity design",
  "struct.gamma.sub": " — {sistem}, class {clasa}",
  "struct.gamma.explicatie": "γ_Rd covers the gap between the design strength of the dissipative zone and the strength it actually develops (real steel is stronger than design steel and hardens past yield). The more ductility the class demands, the deeper the plastic zone is pushed into hardening, so the DCH values are larger than the DCM ones. At DCL there is no capacity design — the design forces are those from the analysis (P100-1/2013 §5.2.3.3.1(11) and §5.5.1(1)) — so γ_Rd = 1.00 everywhere. The values are taken from the code, by the class set at step 3: they change on their own if the class changes, when the step is re-run. The extract giving the clause behind each figure is in",
  "struct.gamma.coloana": " The right-hand column shows what ",
  "struct.gamma.pe_aceeasi_clasa": " would give on the same class — ",
  "struct.gamma.nu_difera": "here it differs on no check",
  "struct.gamma.difera": "it differs on {n} of {total}",
  "struct.gamma.sistem_nota": ". The system is a constant of the method («sistem_calcul»); it also switches two whole formulas (anchorage through the joint and the compression of the joint diagonal), not just the figures below.",
  "struct.gamma.col_verificare": "The check",
  "struct.gamma.col_unde": "Where it enters",
  "struct.gamma.col_sursa": "Source",

  // --- STRUCTURĂ · op-urile refuzate (restul) ----------------------------
  "struct.opuri.aplicarea": "applying the overlay",
  "struct.opuri.tainuite#n": "{n} warning did not fit in the saved report (the server keeps the first 20).|{n} warnings did not fit in the saved report (the server keeps the first 20).|{n} warnings did not fit in the saved report (the server keeps the first 20).",
  "struct.evid.toate#n": "all ({n} type)|all ({n} types)|all ({n} types)",

  // --- STRUCTURĂ · numele layerelor 3D -----------------------------------
  "struct.layere.clasic": "Classic",
  "struct.layere.realist": "Realistic",

  // --- STRUCTURĂ · ce NU e în cazurile de încărcare (Pasul 8) ------------
  "struct.am.incarcari_titlu": "What is NOT in the load cases",
  "struct.am.incarcari_atentie": "on the unsafe side",
  "struct.am.incarcari_intro": "The catalogue above is everything that presses on the structure in this calculation. Here are the actions and assumptions the code requires that did NOT make it in — with their impact measured on this project. The marked ones are on the unsafe side (an action is missing); the rest are choices heavier than the code asks for. Earth pressure on the basement box is declared at step 6.",

  // --- STRUCTURĂ · titlul și explicația unei verificări ------------------
  "struct.ver.sursa_foaie": "the structural engineer's own spreadsheet",
  "struct.ver.sursa_ro": "Romanian legislation",
  "struct.ver.sursa_ec": "Eurocode",
  "struct.ver.ce_verifica": "What it checks",
  "struct.ver.formula": "Formula",
  "struct.ver.simboluri": "What each symbol means",
  "struct.ver.divergenta": "⚠ Here the method differs from {fata_de}",
  "struct.ver.implementat": "Implemented:",
  "struct.ver.normativul_cere": "The code requires:",
  "struct.ver.efectul": "The effect:",
  "struct.ver.ce_ascunde": "What problem it hides",
  "struct.ver.solutii": "Fixes, from the cheapest to the most expensive",
  "struct.ver.automat": "the loop tries it on its own: {cum}",
  "struct.ver.marker_metoda": "⚠ method",
  "struct.ver.marker_metoda_titlu": "the method differs from the Romanian code — see the explanation on hover",

  // --- STRUCTURĂ · proveniența unui simbol -------------------------------
  "struct.info.de_unde_prefix": "where it comes from: ",

  // --- STRUCTURĂ · pagina unui TIP de verificare -------------------------
  "struct.tipver.sursa_foaie": "the engineer's spreadsheet",
  "struct.tipver.sursa_ro": "RO legislation",
  "struct.tipver.sursa_ec": "Eurocode",
  "struct.tipver.verificari": "Checks",
  "struct.tipver.prioritate": "formula priority: spreadsheet → RO legislation → Eurocode",
  "struct.tipver.divergenta": "⚠ the method differs from the code",
  "struct.tipver.divergenta_text": "{implementat} (code: {normativ}) — {efect}",
  "struct.tipver.care_pica": "The elements that fail it",
  "struct.tipver.niciunul": "none",
  "struct.tipver.dupa_deficit": "ordered by shortfall",
  "struct.tipver.col_re": "R/E",
  "struct.tipver.col_deficit": "Shortfall",
  "struct.tipver.col_detaliu": "Detail",
  "struct.tipver.toate_la#n": "all {n} at {nivel}|all {n} at {nivel}|all {n} at {nivel}",
  "struct.tipver.tipar": "→ the common pattern suggests a single decision could fix them all",
  "struct.tipver.solutii": "Fixes, from cheap to expensive",
  "struct.tipver.automat": "the loop tries it on its own",
  "struct.tipver.cand": "When it applies",
  "struct.tipver.link": "{eticheta} →",

  // --- STRUCTURĂ · detaliul unei verificări ------------------------------
  "struct.det.da": "yes",
  "struct.det.nu": "no",
  "struct.det.comparatia": "The comparison",
  "struct.det.fata_de": " against {prag}",
  "struct.det.re": " → R/E = {re}",
  "struct.det.rezerva": " (reserve {n} %)",
  "struct.det.lipsesc": " ({n} % short)",
  "struct.det.marja_chip": "accepted within the practice margin",
  "struct.det.marja": "R/E is below 1, but within the {n} % margin this office accepts when sizing.",
  "struct.det.fara_categorie": "This check does not (yet) have a category in the explanation catalogue — the figures above are the ones the step emitted.",
  "struct.det.valorile": "The values from the calculation",
  "struct.det.vezi_desenele": "see the full drawings and tables →",
  "struct.det.metoda_titlu": "the method differs from the code — see the full explanation",
  "struct.det.ascunde": "▾ hide the explanation",
  "struct.det.explicatia": "▸ the full explanation",

  // --- STRUCTURĂ · numele valorilor brute dintr-o verificare -------------
  "struct.brut.combo": "combination",
  "struct.brut.loc": "location",
  "struct.brut.punct": "point",
  "struct.brut.nivel": "level",
  "struct.brut.M_cap": "M resistance [kNm]",
  "struct.brut.H_liber_m": "clear H [m]",
  "struct.brut.alpha_foaie": "α (spreadsheet)",
  "struct.brut.indice_foaie": "spreadsheet index",
  "struct.brut.pas_cm": "spacing [cm]",
  "struct.brut.rho_eff": "effective ρ",
  "struct.brut.masa_x": "cumulative mass on X [%]",
  "struct.brut.masa_y": "cumulative mass on Y [%]",
  "struct.brut.masa_totala": "total seismic mass [t]",
  "struct.brut.moduri_calculate": "modes computed",
  "struct.brut.moduri_folosite": "modes used in the seismic analysis",
  "struct.brut.moduri_min": "minimum number of modes required",
  "struct.brut.masurat_pe": "measured on",
  "struct.brut.total_aplicat": "load applied [kN]",
  "struct.brut.suma_reactiuni": "sum of reactions [kN]",
  "struct.brut.dezechilibru_kn": "load lost [kN]",
  "struct.brut.dezechilibru_pct": "load lost [%]",
  "struct.brut.prag_pct": "threshold [%]",
  "struct.brut.valoare_pct": "measured [%]",
  "struct.brut.deficit_pct": "shortfall [%]",
  "struct.brut.v_baza": "base shear, response-spectrum analysis [kN]",
  "struct.brut.f_b_echivalent": "base shear, simplified method [kN]",
  "struct.brut.raport_v_fb": "ratio of the two",
  "struct.brut.T_fundamental": "fundamental period [s]",
  "struct.brut.caz": "load case",

  // --- STRUCTURĂ · inventarul de bare și costurile elementului -----------
  "struct.inv.titlu": "Bar schedule & costs",
  "struct.inv.kg_otel": "{n} kg steel",
  "struct.inv.m3_beton": "{n} m³ concrete",
  "struct.inv.precizie_titlu": "the precision is declared per item: exact / partial / lump sum",
  "struct.inv.kg_m3": "{n} kg/m³",
  "struct.inv.kg_m3_titlu": "the benchmark project sits at 165 kg/m³",
  "struct.inv.eroare": "The bar schedule could not be fetched (run step 19 / the bill of quantities).",
  "struct.inv.se_aduce": "fetching the bar schedule…",
  "struct.inv.fara_pozitii": "The bar schedule has no items for this element — its reinforcement goes into the chapter's lump-sum item.",
  "struct.inv.col_poz": "Item",
  "struct.inv.col_rol": "What the bar does",
  "struct.inv.col_lungime": "Length, and what it is made of",
  "struct.inv.col_buc": "Qty",
  "struct.inv.col_kg": "kg",
  "struct.inv.cost_titlu": "The cost, with its build-up",
  "struct.inv.derivat_titlu": "quantity derived from the element's geometry",
  "struct.inv.sumar": "material {material} · labour {manopera} · unit prices from: {sursa}",
  "struct.inv.inca_pozitii": "+ {n} items",
  "struct.inv.nota_lungimi": "each length shows what it is made of (anchorage + span + anchorage) — not a single opaque figure",
  "struct.inv.kg_pe_element": "{n} kg on this element",
  "struct.inv.pierderi": " (+{n} % supply waste)",

  // --- STRUCTURĂ · costul stâlpului întreg -------------------------------
  "struct.stiva.titlu": "Cost of the whole column",
  "struct.stiva.se_aduna": "adding up the stack's bar schedule…",
  "struct.stiva.fara_deviz": "The bill of quantities could not be read (run step 19 / the bill).",
  "struct.stiva.titlu_cu_element": "Cost of the whole column — {element}",
  "struct.stiva.col_beton": "Concrete",
  "struct.stiva.col_cofraj": "Formwork",
  "struct.stiva.col_otel": "Steel",
  "struct.stiva.col_material": "Material",
  "struct.stiva.col_manopera": "Labour",
  "struct.stiva.col_total": "Total",
  "struct.stiva.infra": "substructure",
  "struct.stiva.infra_titlu": "cast with the foundation, so it is paid under the substructure",
  "struct.stiva.tot_stalpul": "{element} — the whole column",
  "struct.stiva.nota": "The same build-up as for a single level, summed over the stack: steel from the bar schedule (laps and waste allowance included), concrete and formwork derived from each level's geometry. The starter bars appear once, at the foot of the stack. Unit prices from: {sursa}.",
  "struct.stiva.densitate": " The column's reinforcement density: {n} kg/m³.",
  "struct.stiva.neatribuit": "{n} kg of the stack's bar schedule did not land on any of its levels",
  "struct.stiva.neatribuit_dupa": " — they are counted in the total, but check the plan ids.",

  // --- STRUCTURĂ · natura unei mărimi ------------------------------------
  "struct.natura.valoare": "applied value",
  "struct.natura.valoare_titlu": "THE DEMAND — the figure the structure produces on this project. It is compared against a limit.",
  "struct.natura.limita": "limit",
  "struct.natura.limita_titlu": "THE LIMIT — the threshold the applied value must not exceed. It comes from the code or from the calculation method.",
  "struct.natura.capacitate": "resistance",
  "struct.natura.capacitate_titlu": "THE RESISTANCE — what the element carries as it is detailed. It is compared against the demand.",
  "struct.natura.geometrie": "dimension",
  "struct.natura.geometrie_titlu": "DIMENSION — a geometric quantity this step decides; it goes on into the working drawing.",
  "struct.natura.intrare": "input datum",
  "struct.natura.intrare_titlu": "INPUT DATUM — taken as it is from the documents, the code or the settings. The engine neither chooses it nor compares it with anything; it feeds the calculations downstream.",
  "struct.natura.derivat": "derived",
  "struct.natura.derivat_titlu": "DERIVED QUANTITY — it is neither looked up nor chosen: it follows from others, and is recomputed whenever they change.",
  "struct.marime.lipsa": "the page did not pass this value on",

  // --- STRUCTURĂ · harta gropilor de pământ ------------------------------
  "struct.gropi.exact_contur": "the plot outline, from the deeds",
  "struct.gropi.exact_punct": "a point from the deeds",
  "struct.gropi.exact_strada": "approximate — geocoded by street",
  "struct.gropi.exact_localitate": "approximate — geocoded by locality, so somewhere in this area",
  "struct.gropi.lotul": "the plot",
  "struct.gropi.tooltip": "{nume}<br>{km} km · {cost}",
  "struct.gropi.folosita": " · in use",
  "struct.gropi.drum_sosea": "road distance · {km} km",
  "struct.gropi.linie_dreapta": "straight line × 1.3 · {km} km (estimate)",
  "struct.gropi.se_incarca": "loading the map…",
  "struct.gropi.col_groapa": "Tip",
  "struct.gropi.col_distanta": "Distance",
  "struct.gropi.col_curse": "Trips",
  "struct.gropi.col_transport": "Haulage",
  "struct.gropi.col_depozitare": "Disposal",
  "struct.gropi.col_total": "Total",
  "struct.gropi.col_acces": "Access fee",
  "struct.gropi.afara_raza": "beyond the radius",
  "struct.gropi.afara_raza_titlu": "Farther than the site's reasonable radius: shown, but not chosen automatically; the estimate uses the capped price unless you pick it",
  "struct.gropi.acces_titlu": "The city access fee on the trips to this pit, above the reference zone: the day's fee divided by the trips that fit in a day, so larger towards a farther pit",
  "struct.gropi.estimare_titlu": "estimate: straight line × 1.3 — the router did not answer for this tip",
  "struct.gropi.masurat_titlu": "distance measured on the road network",

  // --- STRUCTURĂ · barele longitudinale și înfășurătoarea ----------------
  "struct.bare.fibra_sus": "Top reinforcement",
  "struct.bare.fibra_jos": "Bottom reinforcement",
  "struct.bare.suprapunere": "lap {bare} · l₀ = {l0} m at s = {s} m",
  "struct.bare.zona": "\\nzone: {zona}",
  "struct.bare.fortata": "\\nFORCED — no free zone was available",
  "struct.bare.petrecere": "lap {fata} · {bare} · l₀ = {l0} m at {s} m · {zona}",
  "struct.bare.fara_zona": "no free zone",

  // --- STRUCTURĂ · planul din Studio -------------------------------------
  "struct.plan.fundatie": "Foundation — the footing under {nivel}",
  "struct.plan.model": "Structural model — {nivel}",
  "struct.plan.link_ax": "the axis's checks →",
  "struct.plan.nivel_titlu": "{stalpi} columns · {grinzi} beams",
  "struct.plan.fundatie_buton": "foundation",
  "struct.plan.fundatie_titlu": "the footing at its real width (B = {B} m, D_f = {D} m) and step 6's named axes, over the plan of the bottom level — click an axis for its checks",

  // --- STRUCTURĂ · desenul cotat al unui element -------------------------
  "struct.desen.cota_sapatura": "level the excavation starts from",
  "struct.desen.talpa": "footing {n}",
  "struct.desen.umplutura": "backfill {n}",
  "struct.desen.inima": "web {n}",
  "struct.desen.balast": "{n} cm of ballast, then soil",
  "struct.desen.plasa": "mesh",
  "struct.desen.la_mijloc": "mid-depth",
  "struct.desen.plasa_mijloc": "the mesh sits at mid-depth: plain concrete carries the moment, while the mesh works against restrained shrinkage",
  "struct.desen.plasa_jos": "the mesh drops to the bottom face: that is where the tension under load is, and plain concrete cannot carry the moment",
  "struct.desen.moment_nota": " (M = {med} kNm/m, plain concrete {mrd} kNm/m)",

  // --- STRUCTURĂ · localizarea elementului în clădire --------------------
  "struct.loccl.cere_fem": "Locating the element in the building needs the step 7 model (FEM) — run it and come back.",
  "struct.loccl.fara_pas": "<b>{ref}</b> — an element with no reinforcement step (it enters the model with its weight only)",
  "struct.loccl.aceeasi_pagina": "click → selects <b>{ref}</b> on this page",
  "struct.loccl.alt_pas": "click → <b>{pas} · {ref}</b>",

  // --- STRUCTURĂ · lista elementelor (capitolul 5) -----------------------
  "struct.lista.titlu_gol": "The elements",
  "struct.lista.gol": "The list fills up once the reinforcement steps (13–16) have run.",
  "struct.lista.pipeline": "pipeline →",
  "struct.lista.titlu": "Chapter 5 — The elements",
  "struct.lista.sub": "every row carries its verdict — sorted: what fails first, then the smallest reserve",
  "struct.lista.filtru_tip": "Type",
  "struct.lista.tip_toate": "all",
  "struct.lista.tip_grinzi": "beams",
  "struct.lista.tip_stalpi": "columns",
  "struct.lista.tip_pereti": "walls",
  "struct.lista.tip_lamele": "structural walls",
  "struct.lista.tip_ochiuri": "slab panels",
  "struct.lista.filtru_verdict": "Verdict",
  "struct.lista.neconforme": "failing · {n}",
  "struct.lista.la_limita": "review · {n}",
  "struct.lista.doar_probleme": "problems only",
  "struct.lista.doar_probleme_titlu": "hides the elements that pass",
  "struct.lista.filtru_nivel": "Level",
  "struct.lista.col_tip": "Type",
  "struct.lista.col_sectiune": "Section",
  "struct.lista.col_re": "min R/E",
  "struct.lista.col_verdict": "Verdict",
  "struct.lista.col_criteriu": "The governing criterion",
  "struct.lista.deschide": "open →",
  "struct.lista.ascunse": "+ {n} elements that pass — ",
  "struct.lista.arata_toate": "show them all",

  // --- STRUCTURĂ · panoul axului de fundație din Studio ------------------
  "struct.drax.inchide": "close",
  "struct.drax.doar_predim": "The foundation is only pre-sized (step 6) — the check against the real reactions comes after steps 7–12.",
  "struct.drax.dupa_p17": "The axis's checks appear once step 17 has run.",
  "struct.drax.pagina_fundatiei": "the foundation page →",
  "struct.drax.ax": "axis",
  "struct.drax.antet": "{directie} · {stalpi} columns",
  "struct.drax.latit": " · locally widened",
  "struct.drax.tasare": " · settlement {n} cm",
  "struct.drax.lectura_metoda": "p_ef method",
  "struct.drax.lectura_np112": "p_ef NP 112",
  "struct.drax.pagina_axului": "the axis page (section, context) →",
  "struct.drax.verificarile": "The axis's checks",
  "struct.drax.in_tablou": "The axis's checks appear in the global board — ",
  "struct.drax.verificari_link": "Checks →",

  // --- STRUCTURĂ · Studio, vederea «Componente» (tipuri și clase) --------
  "struct.comp.tab_stalpi": "Vertical elements",
  "struct.comp.tab_grinzi": "Beams",
  "struct.comp.tab_fundatie": "Foundation beams",
  "struct.comp.tab_placa": "Slab panels",
  "struct.comp.tab_bare": "Bars",
  "struct.comp.tab_etrieri": "Stirrups",
  "struct.comp.tab_cofrare": "Formwork",
  "struct.comp.tab_gol": "nothing to show yet: the step that produces this family has not run on this solution",
  "struct.comp.fam_placa": "Slab panels",
  "struct.comp.fam_placa_ce": "one type = the panel footprint (to 5 cm), the slab thickness and the four meshes, bottom and top",
  "struct.comp.ochiuri#n": "{n} panel|{n} panels|{n} panels",
  "struct.comp.desface_nota": "Click a type to see its elements; each element opens its own page.",
  "struct.comp.desface_tot": "Expand all",
  "struct.comp.strange_tot": "Collapse all",
  "struct.comp.titlu": "The structure's components",
  "struct.comp.intro": "How many different things the structure is made of. A type is bent or formed once and then repeated; every extra type is one more template on site. The cost is the one from the bill's items, spread over the types — the sum of a family's types is the sum of its items. You will find the same ids in the 3D scene, under «Highlight».",
  "struct.comp.fara_tip": "{pozitii} computed items ({kg} kg, across {elemente} elements) carry no type written by their step — re-run the reinforcement steps so they get one.",
  "struct.comp.fam_bare": "Bars",
  "struct.comp.fam_bare_ce": "a type = the shape and the diameter; length does not count — it is cut, not bent differently",
  "struct.comp.fam_etrieri": "Stirrups and links",
  "struct.comp.fam_etrieri_ce": "a type = the shape, the diameter and the sides of the loop: two stirrups with the same cut length but a different loop are two templates",
  "struct.comp.fam_stalpi": "Columns and blades (storey segments)",
  "struct.comp.fam_stalpi_ce": "a type = the execution class: same section, same shape, same cage",
  "struct.comp.fam_grinzi": "Beams (segments)",
  "struct.comp.fam_grinzi_ce": "a type = the execution class: the section and all the reinforcement; the span does not count",
  "struct.comp.fam_fundatie": "Foundation beams (axes)",
  "struct.comp.fam_fundatie_ce": "a type = the axis's execution class: the section and the whole reinforcement",
  "struct.comp.peste_tinta": "above the office target of {tinta}: every extra type is one more formwork set or bending template",
  "struct.comp.in_tinta": "within the office target of at most {tinta} types",
  "struct.comp.nr_tipuri#n": "{n} type · target {tinta}|{n} types · target {tinta}|{n} types · target {tinta}",
  "struct.comp.gol_bare": "— no types: the reinforcement steps have not run —",
  "struct.comp.gol_clase": "— no classes: the reinforcement step has not run —",
  "struct.comp.col_tip": "Type",
  "struct.comp.col_clasa": "Class",
  "struct.comp.col_ce": "What it is",
  "struct.comp.col_utilizari": "Uses",
  "struct.comp.col_otel": "Steel",
  "struct.comp.col_material": "Material",
  "struct.comp.col_manopera": "Labour",
  "struct.comp.col_cost": "Cost",
  "struct.comp.col_beton": "Concrete",
  "struct.comp.col_cofraj": "Formwork",
  "struct.comp.buc": "{n} pcs",
  "struct.comp.in_elemente": " · across {n} elements",
  "struct.comp.kg_m": " · {n} m",
  "struct.comp.deschideri": " · spans {min}–{max} m",
  "struct.comp.membri_titlu": "show the class's elements",
  "struct.comp.axe#n": "{n} axis|{n} axes|{n} axes",
  "struct.comp.elemente#n": "{n} element|{n} elements|{n} elements",
  "struct.comp.sectiuni_titlu": "Vertical elements — section types",
  "struct.comp.sectiuni_nr#n": "{n} type · target {tinta}|{n} types · target {tinta}|{n} types · target {tinta}",
  "struct.comp.sectiuni_ce": "The b × L formwork of every column or wall piece, on every level — the 30 × 30 column counts too. The office target is at most {tinta} types, {exceptie} only as an exception.",
  "struct.comp.sectiuni_peste": "over the office target of {tinta} types (exception: {exceptie})",
  "struct.comp.sectiuni_in": "within the office target of at most {tinta} types",
  "struct.comp.col_sectiune": "Section",
  "struct.comp.col_piese": "Pieces",
  "struct.comp.col_elemente": "Elements",
  "struct.comp.col_de_ce": "Why it stays",
  "struct.comp.piese#n": "{n} piece|{n} pieces|{n} pieces",
  "struct.comp.piese_titlu": "a piece = the element on one level",
  "struct.comp.sectiuni_fara_armare": "The execution classes — the reinforcement, the formwork per storey height and the cost of each type — appear after the reinforcement steps, in the detailed analysis.",

  // --- STRUCTURE · reinforcement plan (sheets, marks, DXF / DWG) ----------
  "struct.studio.vedere.plan_armare": "Reinforcement plan",
  "struct.studio.gol_plan_armare": "The reinforcement plan is drawn from the reinforcement steps, which have not run on this solution — they do not run on a sketch.",
  "struct.studio.gol_armare_macro": "This row is the «{macro}»; reinforcement runs on the «{detaliu}» — its detail row is in the solution menu.",
  "struct.studio.gol_armare_macro_fara": "This row is the «{macro}»; reinforcement runs on the «{detaliu}», which has not started on this solution yet.",
  "struct.pa.titlu": "The reinforcement plan",
  "struct.pa.gol": "No sheet yet: the drawings appear once the reinforcement steps have run.",
  "struct.pa.coli_grinzi": "Beams",
  "struct.pa.coli_esv": "Vertical elements",
  "struct.pa.coli_fundatie": "Foundation",
  "struct.pa.coli_placa": "Slabs",
  "struct.pa.set_intreg": "Whole set",
  "struct.pa.coala_aleasa": "Selected sheet",
  "struct.pa.descarca_coli_titlu": "download the selected sheet or the whole set, as DXF or DWG",
  "struct.pa.link_element": "element page",
  "struct.pa.se_descarca": "Downloading…",
  "struct.pa.dxf_coala_titlu": "download the sheet as a DXF file — the exact format, as drawn",
  "struct.pa.dwg_coala_titlu": "download the sheet as a DWG file, converted from DXF on the server",
  "struct.pa.dxf_tot_titlu": "download every sheet in a single DXF file",
  "struct.pa.dwg_tot_titlu": "download every sheet in a single DWG file, converted from DXF on the server",
  "struct.pa.dwg_indisponibil": "the DXF → DWG converter is not installed on the server; download the DXF",
  "struct.pa.dwg_pierdute": "The DWG was downloaded, but the conversion lost {n} entities — the DXF is the exact file. {mesaj}",
  "struct.pa.descarcare_esuata": "The file could not be downloaded.",
  "struct.pa.el_titlu": "Reinforcement plan",
  "struct.pa.el_desen": "reinforcement plan of element {ref}",
  "struct.pa.descarca": "Download",
  "struct.pa.descarca_titlu": "download the reinforcement plan of {ref}",
  "struct.pa.fel_dxf": "DXF — exact, as drawn",
  "struct.pa.fel_dwg": "DWG — converted from DXF",
  "struct.pa.dxf_element_titlu": "the element's drawing as a DXF file — the exact format, as drawn",
  "struct.pa.dwg_element_titlu": "the element's drawing as a DWG file, converted from DXF on the server",
  "struct.pa.ecran_complet": "Full screen",
  "struct.pa.iesi_ecran_complet": "Exit full screen",
  "struct.pa.deschide": "Open in Reinforcement plan",
  "struct.pa.deschide_titlu": "the whole sheet, in the Studio's «Reinforcement plan» view",
  "struct.pa.fam_grinda": "beam bar",
  "struct.pa.fam_fundatie": "foundation beam bar",
  "struct.pa.fam_placa_sus": "slab bar, top face",
  "struct.pa.fam_placa_jos": "slab bar, bottom face",
  "struct.pa.fam_vertical": "vertical bar (column, blade)",
  "struct.pa.fam_orizontal": "horizontal wall bar",
  "struct.pa.fam_etrier": "stirrup",
  "struct.pa.fam_agrafa": "tie / rider",
  "struct.pa.marca_eticheta": "mark {marca}: the bar's page",
  "struct.pa.marca_deschide": "click → the mark's page: the dimensioned shape and the elements using it",
  "struct.pa.marca_tip": "type {tip} in the components register",
  "struct.pa.marca_cifre": "{nr} pieces in {elemente} elements · {kg} kg · {m} m",
  "struct.pa.marca_fara_tip": "The type is not in this solution's components register — re-run the reinforcement steps so they write it.",
  "struct.pa.marca_necunoscuta": "Unknown mark",
  "struct.pa.marca_necunoscuta_nota": "«{marca}» is not shaped like a mark: the type number and the family suffix (3g, 7gF, 1s, 2i) or the family and the number (e1, c2, v3, o1).",
  "struct.pa.forma_titlu": "Dimensioned shape",
  "struct.pa.forma_desen": "shape of mark {marca}",
  "struct.pa.forma_lipsa": "The shape is not drawn yet for this mark.",
  "struct.pa.unde_titlu": "Where it is used",
  "struct.pa.unde_sub": "{elemente} elements · {buc} pieces · {kg} kg",
  "struct.pa.unde_gol": "No position in the schedule carries this type.",
  "struct.pa.col_element": "Element",
  "struct.pa.col_nivel": "Storey",
  "struct.pa.col_rol": "Role",
  "struct.pa.col_marca": "Mark",
  "struct.studio.vedere.plan_cofraj": "Formwork plan",
  "struct.studio.gol_plan_cofraj": "The formwork plan is drawn from the structural model (Step 7), which has not run on this solution.",
  "struct.pc.titlu": "The formwork plan",
  "struct.pc.gol": "No sheet yet: the formwork plan appears once the structural model has run.",
  "struct.pc.coli_niveluri": "Sheets",
  "struct.studio.vedere.plan_sapatura": "Excavation plan",
  "struct.studio.gol_plan_sapatura": "The excavation plan is drawn from the structural model (Step 7), which has not run on this solution.",
  "struct.psap.titlu": "The excavation plan",
  "struct.psap.gol": "No sheet yet: the excavation plan appears once the structural model has run with a foundation in it.",
  "struct.psap.coli": "Sheets",

  // --- STRUCTURĂ · planul de cofrare -------------------------------------
  "struct.cof.intro": "How many different formwork sets the structure is cast from: {total} in all, the office target being at most {tinta} of each kind. A set is made once and struck as many times as it is used; every extra type is one more panel to build, store and move. Areas and cost come from the bill's formwork items, spread over the types.",
  "struct.cof.fam_stalpi": "Column formwork",
  "struct.cof.fam_stalpi_ce": "a type = the section (and the shape, for L/T columns) over the storey height; four faces",
  "struct.cof.fam_grinzi": "Beam formwork",
  "struct.cof.fam_grinzi_ce": "a type = the web section below the slab over the CLEAR span between supports — two equal spans mean the same set",
  "struct.cof.fam_fundatie": "Foundation beam formwork",
  "struct.cof.fam_fundatie_ce": "a type = the formed web section; the footing is cast in the trench, so length does not count",
  "struct.cof.fam_pereti": "Wall formwork",
  "struct.cof.fam_pereti_ce": "a type = the thickness over the storey height, two faces",
  "struct.cof.fam_placa": "Slab panels",
  "struct.cof.fam_placa_ce": "a type = the size of the bay between beams (to 5 cm) and the slab thickness",
  "struct.cof.peste_tinta": "above the office target of {tinta}: every extra set is built and stored separately",
  "struct.cof.in_tinta": "within the office target of at most {tinta} different sets",
  "struct.cof.nr_cofraje#n": "{n} set · target {tinta}|{n} sets · target {tinta}|{n} sets · target {tinta}",
  "struct.cof.m2_cost": "{m2} m² · {cost}",
  "struct.cof.gol": "— no formwork: this family's elements are not in the quantities yet —",
  "struct.cof.col_cofraj": "Formwork",
  "struct.cof.col_ce": "What it is",
  "struct.cof.col_montari": "Erections",
  "struct.cof.col_suprafata": "Area",
  "struct.cof.membri_titlu": "show the elements cast with this set",
  "struct.cof.montari#n": "{n} erection|{n} erections|{n} erections",
  "struct.cof.fara_cofraj": "What is not formed",
  "struct.grila.nemasurata": "The grid could not be measured: the model has no bay between two columns.",
  "struct.grila.titlu": "The span grid (clear distance between faces)",
  "struct.grila.pe_modul": "{peModul} of {nr} bays with the clear distance on the {modul} m module",
  "struct.grila.distincte#n": "{n} distinct span|{n} distinct spans|{n} distinct spans",
  "struct.grila.nota": "a bay = the CLEAR distance between the faces of two neighbouring columns on the same beam, counted once per plan; on-module = a whole number of parking spaces between the faces (that is where the cars fit, not between axes), with at most 2 cm wasted; equal spans are the same beam formwork reused",
  "struct.grila.col_deschidere": "Clear span",
  "struct.grila.col_travee": "Bays",
  "struct.grila.col_pe_modul": "On module",
  "struct.grila.col_grinzi": "Beams",
  "struct.grila.multiplu": "✓ {multiplu} spaces × {modul} m",

  // --- STRUCTURĂ · reazemul articulat (pila pe grinda-gazdă) -------------
  "struct.rez.pila": "pier",
  "struct.rez.M_sus": "M goes into the beam above",
  "struct.rez.V_planseu": "V → into the slab",
  "struct.rez.reazem_i": "support i",
  "struct.rez.reazem_j": "support j",
  "struct.rez.gazda_transfer": "host beam (transfer)",
  "struct.rez.titlu_pila": "Pinned support — pier on beam {gazda}",
  "struct.rez.link_element": "the page of element {ref}",
  "struct.rez.pila_text": "<b>{stalp}</b> does not run down to the foundation: it bears on beam {gazda} of the storey below, with a <b>pinned</b> base (M = 0). Only <b>N</b> enters the host beam",
  "struct.rez.pila_text_v": "; the pier's shear <b>V</b> goes into the slab, not into the beam.",
  "struct.rez.rigiditate": "Its lateral stiffness in the frame: ",
  "struct.rez.rigiditate_nota": " — pinned at the base, the pier is a lever on the beam above, so what matters is the beam above (k̄ = Σ(I_b/L_b)/(I_c/h)), not the pier's length.",
  "struct.rez.fara_p13": "The reaction figures come from step 13 (reinforcing the host beam), which has not run on this result.",
  "struct.rez.titlu_gazda#n": "Pinned support — the beam carries the pier|Pinned support — the beam carries the piers|Pinned support — the beam carries the piers",
  "struct.rez.gazda_text": "The beam is a <b>transfer beam</b>: a column of the storey above bears on it with a pinned support. From it the beam takes a point load <b>N = R_d</b> that the slab never gave it; the pier's <b>V</b> goes into the slab, not into the beam.",
  "struct.rez.col_pila": "Pier",
  "struct.rez.col_R_titlu": "the reaction from the analysis",
  "struct.rez.col_Rd_titlu": "the design reaction, amplified by f",
  "struct.rez.col_a_titlu": "the lever arm from support i",
  "struct.rez.col_b_titlu": "the lever arm from support j",
  "struct.rez.col_spor": "added moment R·a·b/L·f",
  "struct.rez.col_spor_titlu": "the added mid-span moment in the beam, from the pier's point load",
  "struct.rez.nota_f": "f = {f} is the reaction amplification factor (the dynamic response of the carried pier); R·a·b/L is the moment of a point load on a simply supported beam of span L.",
  "struct.rez.nota_fara_p13": " The figures come from step 13, which has not run on this result.",

  // --- STRUCTURĂ · bara de secțiune a scenei 3D --------------------------
  "struct.sect.prin_nivel": "{cota} · through {nivel}",
  "struct.sect.titlu_x": "The cutting plane is perpendicular to the grid's X axis: what remains is a section running along Y. The axes are the BUILDING's grid (step 2), so on a rotated building the cut still runs alongside the walls.",
  "struct.sect.titlu_y": "The cutting plane is perpendicular to the grid's Y axis: what remains is a section running along X.",
  "struct.sect.titlu_z": "Cuts HORIZONTALLY, at a level: what remains is a floor plan — exactly the view that shows where the columns and walls land. The slider snaps to the MIDDLE of each storey: cut right at slab level, the whole floor would become an opaque cut face over everything below.",
  "struct.sect.buton": "Section",
  "struct.sect.aprinde_titlu": "Cuts the building with a plane and hides what is in front of it: the only way to see a joint, a beam framing into a column, or the reinforcement inside a column, without switching off half the model.",
  "struct.sect.stinge": "turn the section off",
  "struct.sect.axa_grup": "Section axis",
  "struct.sect.intoarce": "flip it: the other half stays",
  "struct.sect.cursor": "Position of the cutting plane",

  // --- STRUCTURĂ · legenda cazurilor de încărcare ------------------------
  "struct.caz.permanenta": "permanent (G)",
  "struct.caz.variabila": "variable (Q)",
  "struct.caz.seismica": "seismic (E)",
  "struct.caz.torsiune": "seismic — torsion",
  "struct.caz.titlu": "The load cases — what each symbol means",
  "struct.caz.info_titlu": "Elementary load case",
  "struct.caz.info_ce": "A «case» is one single load, applied on its own to the model and run separately. Its result (M, V, N in every member) is kept in the F tensor. A combination is NOT a new analysis: it is the sum of the cases multiplied by coefficients, R = Σ cᵢ·Fᵢ.",
  "struct.caz.info_de_ce": "The structure is linear-elastic, so the response to a sum of loads is the sum of the responses. That is how dozens of combinations come out of a handful of analyses, instead of running FEM dozens of times.",
  "struct.caz.intro": "The letter states the NATURE of the action: <b>G</b> = permanent (always there, it cannot be taken away), <b>Q</b> = imposed (it may or may not be there, and at what value — probabilistic), <b>S</b> = snow (variable too, but with its own ψ), <b>E</b> = the earthquake, <b>T_acc</b> = accidental torsion. The digit or letter that follows is NOT a code index but the physical source, kept separate: G1 the structural concrete, G2a/G2b/G2c the non-structural walls by how they unload (on a beam, on the slab, on the foundation), G3 the finishes, G4 the substructure, Q1 and Q2 the imposed load by the category of the area. The sources are kept apart because they combine differently: one can act favourably in one check and unfavourably in another, and Q1 and Q2 have different ψ.",
  "struct.caz.col_simbol": "Symbol",
  "struct.caz.col_tip": "Type",
  "struct.caz.col_fizic": "What it physically is",
  "struct.caz.col_calcul": "What it is computed from (this project's figures)",
  "struct.caz.col_unde": "Where it is applied on the model",
  "struct.caz.col_cat": "How much it comes to on this project",
  "struct.caz.col_rol": "How it enters the combinations",
  "struct.caz.col_normativ": "Code",
  "struct.caz.de_unde": "where it comes from:",
  "struct.caz.total_aplicat": "total applied",

  // --- STRUCTURĂ · fișa unei încărcări -----------------------------------
  "struct.caz.necunoscut": "—",

  // --- STRUCTURĂ · fișa unei încărcări și a masei seismice ---------------
  "struct.fisai.inchide": "close the card",
  "struct.fisai.nivel_n": "level {n}",
  "struct.fisai.info": "chosen on the safe side",
  "struct.fisai.lipsa": "what is not in the model",
  "struct.fisai.subtitlu": "elementary load case",
  "struct.fisai.total": "total applied",
  "struct.fisai.kn_cladire": "kN over the whole building",
  "struct.fisai.cum": "how it is computed",
  "struct.fisai.unde": "where it is applied",
  "struct.fisai.expus": "the area open to the sky is measured on the mesh at step 7: a cell is roof if it has no other slab above it.",
  "struct.fisai.pe_planse": "per floor",
  "struct.fisai.nota_expus": "{expusa} m² open to the sky out of {total} m²",
  "struct.fisai.nota_planseu": "{n} m² of floor",
  "struct.fisai.opensees": "the pressure enters OpenSees as nodal forces p·A/4, at the corners of each mesh cell.",
  "struct.fisai.link_p8": "the step 8 page →",
  "struct.masa.titlu": "Seismic mass",
  "struct.masa.subtitlu": "the second set of books for the same building",
  "struct.masa.chip_nod": "mass at the node",
  "struct.masa.chip_nu_incarcare": "not a load",
  "struct.masa.totala": "total mass",
  "struct.masa.info_ce": "The mass that oscillates, lumped at the nodes: self-weight, finishes, walls and part of the variable loads.",
  "struct.masa.info_de_ce": "It is not the weight divided by g: the imposed load enters with ψE = φ·ψ2, columns and walls are split half and half between floors, and the substructure is excluded — it does not move relative to the ground (EN 1998-1 §4.3.3.2.2).",
  "struct.masa.infra": "the substructure ({n} t) is weight, not seismic mass: its nodes show up empty in the scene.",
  "struct.masa.compus": "what it is made of",
  "struct.masa.c_structura": "the slab, the beams and the columns",
  "struct.masa.c_finisaje": "the finishes (SDL)",
  "struct.masa.c_pereti": "the non-structural walls",
  "struct.masa.c_utila": "ψE·imposed load",
  "struct.masa.c_zapada": "ψ₂·snow",
  "struct.masa.nota_zapada": "of which {n} t snow",
  "struct.masa.link_p9": "Step 9 — the earthquake →",

  // --- STRUCTURĂ · panoul gropilor de pământ -----------------------------
  "struct.gpan.no_site": "The plot has no coordinates yet. Geocode the address under “Location”, then come back — the distance to the tip is measured from there.",
  "struct.gpan.no_volume": "The excavation hauls nothing away: all the soil is reused as backfill on site.",
  "struct.gpan.no_options": "There is no tip in the database a cost could be worked out for. They are added under “Service providers”.",
  "struct.gpan.fara_motiv": "No tip can be chosen.",
  "struct.gpan.plafon_titlu": "No pit identified",
  "struct.gpan.plafon_eticheta": "capped price",
  "struct.gpan.plafon": "No pit in the catalogue lies within a reasonable radius of the site. The estimate prices disposal at a cap: {km} km of haul and {tarif} €/t at the tip (market maximum). Tips and fill sites exist that are not on the map — a real quote replaces it under “Service providers”, or pick a pit beyond the radius below if you know it takes the soil.",
  "struct.gpan.aleasa_tine": "your choice",
  "struct.gpan.cea_ieftina": "the cheapest",
  "struct.gpan.sumar": "{m3} m³ · {km} km · {curse} trips · haulage {transport} + disposal {depozitare} = ",
  "struct.gpan.pierdut": "{suma} more than {nume}",
  "struct.gpan.regen_titlu": "recompute the bill with the chosen tip",
  "struct.gpan.regen_nimic": "nothing to regenerate — the bill is up to date",
  "struct.gpan.se_regenereaza": "regenerating…",
  "struct.gpan.regenereaza": "regenerate the cost",
  "struct.gpan.harta": "map of the soil tips",
  "struct.gpan.nr_gropi": " · {n} tips",
  "struct.gpan.inchisa": ", closed",
  "struct.gpan.schimbat": "The tip has changed, but the bill above is still the old one — press “regenerate the cost”.",
  "struct.gpan.localitate": "The plot is known only to <b>locality</b> level — the circle on the map is the area, not the parcel. Distances start from its centre, so they carry an error of kilometres: tips close in total cost may swap order. For a firm answer the street address is needed, from the land registry or the planning certificate.",
  "struct.gpan.rutat_tot": "The distances are <b>road distances</b>, measured on the road network. The last kilometres stay approximate: a tip's point is usually the centre of its locality, not the gate of the site.",
  "struct.gpan.estimat_tot": "The router did not answer, so the distances are <b>estimated</b> (straight line × 1.3) — marked with “≈” in the table. An estimate can flip the order of two tips that are close in total, so it is worth reloading the screen before deciding.",
  "struct.gpan.estimat_unele#n": "The distances are road distances, except for <b>{n}</b> tip, marked with “≈” in the table: for it the router did not answer and the estimate stands (straight line × 1.3).|The distances are road distances, except for <b>{n}</b> tips, marked with “≈” in the table: for them the router did not answer and the estimate stands (straight line × 1.3).|The distances are road distances, except for <b>{n}</b> tips, marked with “≈” in the table: for them the router did not answer and the estimate stands (straight line × 1.3).",
  "struct.gpan.tarife": " Tip charges are orders of magnitude until someone confirms them with a quote.",

  // --- STRUCTURĂ · centrul de activitate ---------------------------------
  "struct.act.buton_liber": "activity centre — nothing is running",
  "struct.act.buton_lucru": "activity centre — {n} running",
  "struct.act.acum": "Right now",
  "struct.act.nimic": "Nothing is running on the server.",
  "struct.act.link_pipeline": "pipeline →",
  "struct.act.link_solutii": "the solutions →",

  // --- STRUCTURĂ · sumarul selecției din Studio --------------------------
  "struct.fel.stalp": "The column",
  "struct.fel.grinda": "The beam",
  "struct.fel.perete_beton": "The concrete wall",
  "struct.fel.perete_zidarie": "The non-structural wall",
  "struct.fel.panou": "The slab panel",
  "struct.fel.fundatie": "The foundation beam",
  "struct.fel.fereastra": "The window",
  "struct.fel.usa": "The door",
  "struct.fel.parapet": "The parapet",
  "struct.fel.placa_sol": "The slab on grade",
  "struct.fel.zidarie_confinata": "The confined masonry wall",
  "struct.sumar.inchide": "close the panel",
  "struct.sumar.picate#n": "{n} failed|{n} failed|{n} failed",
  "struct.sumar.la_limita": "{n} to review",
  "struct.sumar.trec": "{n} pass",
  "struct.sumar.sari_titlu": "all its checks, with a route to {pas}",
  "struct.sumar.sari": "jump to the checks →",
  "struct.sumar.nu_e": "the element is not in the results of the reinforcement steps",
  "struct.sumar.se_incarca": "loading the checks…",
  "struct.sumar.cost": "The element's cost",
  "struct.sumar.se_calculeaza": "computing…",
  "struct.sumar.fara_deviz": "The bill of quantities could not be read (run step 19 / the recipes).",
  "struct.sumar.derivat_titlu": "the quantity is derived from the element's geometry — the reinforcement steps do not publish it for this type",
  "struct.sumar.material_manopera": "material {material} · labour {manopera}",
  "struct.sumar.deviz_titlu": "the unit prices come from: {sursa}",
  "struct.sumar.deviz": "the bill →",
  "struct.sumar.nota_derivat": "“~” = a quantity derived from the geometry (concrete and formwork are not published per element); the unit prices are the calibrated ones from the bill.",
  "struct.sumar.pagina": "the element's page →",

  // --- STRUCTURĂ · vocabularul explicațiilor din pași --------------------
  "struct.expl.intrare": "INPUT",
  "struct.expl.decizie": "DECISION",
  "struct.expl.masurat": "MEASURED",
  "struct.expl.intrare_titlu": "a value taken as it is — from the documents, from earlier steps or from the settings; the engine does not choose it",
  "struct.expl.decizie_titlu": "a value this step SETS, by a criterion explained alongside",
  "struct.expl.masurat_titlu": "a value that comes out of the calculation (model or formula), not chosen",
  "struct.expl.legenda_titlu": "What each column means",
  "struct.expl.nr_coloane": " ({n} columns)",
  "struct.expl.col_coloana": "Column",
  "struct.expl.col_ce": "What it stands for",
  "struct.expl.col_cum": "How it is computed",
  "struct.expl.col_validare": "How it is checked",
  "struct.expl.sursa": "Source: ",
  "struct.expl.informativ": "informative, no threshold",
  "struct.expl.din_limita": "of the limit",
  "struct.expl.bara_titlu": "{valoare} {unitate} of {limita} {unitate}",
  "struct.expl.pct_eticheta": "{pct} % {eticheta}",
  "struct.expl.trece_eticheta": "The check passes — explanation",
  "struct.expl.pica_eticheta": "The check fails — explanation",
  "struct.expl.trece": "The check passes",
  "struct.expl.pica": "The check does NOT pass",
  "struct.expl.ce_se_verifica": "What is checked",
  "struct.expl.comparatia": "The comparison made — <b>the project's value</b> against <b>the limit</b>",
  "struct.expl.rezerva": "there is still {n} % of reserve to the threshold",
  "struct.expl.depasit": "the threshold is exceeded by {n} %",
  "struct.expl.de_unde_prag": "Where the threshold comes from",
  "struct.expl.ce_inseamna": "What it means",
  "struct.expl.ce_inseamna_text": "The current foundation geometry does not cover this demand. The local loop in this step is looking for a geometry that does — see the “Searching for the geometry” section.",

  // --- STRUCTURĂ · cele trei modele de rigiditate ------------------------
  "struct.mod.fundatie_nume": "foundation — uncracked concrete",
  "struct.mod.fundatie_ce": "The real E_c (1.0) everywhere. It yields the reactions (steps 6, 17) and the internal forces of the SUBSTRUCTURE: foundation beams, underground columns and walls, basement panels (steps 12, 14, 18).",
  "struct.mod.eforturi_nume": "internal forces — cracked by family",
  "struct.mod.eforturi_ce": "0.80·E_c for columns and walls, 0.50·E_c for beams, h_slab[m]·E_c for the slab (15 cm ⇒ 0.15); the substructure at E_c. It yields M/V/N for the SUPERSTRUCTURE (steps 11–16).",
  "struct.mod.drift_nume": "drift — a uniform 0.5",
  "struct.mod.drift_ce": "0.50·E_c over the whole superstructure, slab included, whatever its thickness; the substructure at E_c. It yields d_re, the factor c, θ and α (step 10).",
  "struct.mod.incastrat_nume": "internal forces — FIXED base",
  "struct.mod.incastrat_ce": "the «internal forces» profile with a fixed base: shorter periods, larger forces. The superstructure is sized on it when ΔT₁ exceeds the threshold.",
  "struct.mod.cutie_nume": "the rigid box (I2m) — uncracked concrete, fixed base",
  "struct.mod.cutie_ce": "displacements only: the I2m condition measures the deformation of the basement box without the soil's compliance, under a static push per direction using M1's storey forces — not a response spectrum analysis of its own. Deliberately on uncracked concrete — on a cracked profile the ratio would come out better precisely because the box was declared.",
  "struct.mod.secunde": "{n} s",
  "struct.mod.mb": " · {n} MB",
  "struct.mod.proces_separat": " · separate process",
  "struct.mod.in_proces": " · in-process",
  "struct.mod.al_patrulea": "The fourth model.",
  "struct.mod.decizie": " ΔT₁ is measured on <b>M1</b> (uncracked concrete): the base on elastic soil against the fixed base, at step 7 — the conservative side, because that way the decision fires more often.",
  "struct.mod.prag": " ({n} %)",
  "struct.mod.decizie2": " When ΔT₁ exceeds the threshold, the superstructure is sized on <b>M4</b> = «internal forces» with a FIXED base; the substructure and the reactions stay on M1, on elastic soil. Drift (<b>M2</b>) has a FIXED base when the strategy pursues a rigid basement box — above the box the building is as if fixed, and the box's rotation on the elastic soil is not a deformation of the structure; with no box requested, M2 rests on the soil. The base used is written on M2's row.",
  "struct.mod.pe_proiect": "On this project: ",
  "struct.mod.m4_activ": "M4 active",
  "struct.mod.fara_m4": "no M4",
  "struct.mod.delta": " — ΔT₁ = {n} %",
  "struct.mod.col_model": "model",
  "struct.mod.col_rigiditate": "stiffness",
  "struct.mod.eroare": "not computed: {eroare}",
  "struct.mod.col_t1_dir": "T₁ (the direction's fundamental)",
  "struct.mod.col_moduri": "modes",
  "struct.mod.col_masa90": "≥ 90 % mass",
  "struct.mod.col_eigen": "eigen",
  "struct.mod.sub7": "The eigen runs of M2 and M3 go in separate processes, alongside M1's ({paralelism} in parallel); their modal caches save step 9 one eigen run each. The periods shown elsewhere on the page — the modal gate, ΔT₁, the comparison with lean — are M1's.",
  "struct.mod.col_tensor": "tensor",
  "struct.mod.col_echilibru": "equilibrium (T3)",
  "struct.mod.col_sarite": "skipped cases",
  "struct.mod.col_statice": "static runs",
  "struct.mod.sub8": "M2 («drift») needs no gravity cases — it only comes in at step 9. The equilibrium shown on the page is M1's, which has the reactions.",
  "struct.mod.col_fb": "equivalent F_b",
  "struct.mod.col_vbaza": "V_base",
  "struct.mod.col_rsa": "RSA",
  "struct.mod.xy_kn": "X {x} · Y {y} kN",
  "struct.mod.xy_s": "X {x} s · Y {y} s",
  "struct.mod.baza_incastrata": "Base: FIXED",
  "struct.mod.baza_elastica": "Base: on elastic soil",
  "struct.mod.impingere": "static push",
  "struct.mod.din_cache": "from cache",
  "struct.mod.recalculat": "recomputed",
  "struct.mod.sub9": "Each model is coherent in itself: its periods, its Γ values, S_d at its periods, its F_b, its storey shear. <b>M1's reactions do not balance M3's forces</b> — they are not from the same model. The spectrum, the modal table and the directions below are M1's; the superstructure is sized on <b>{model}</b>.",
  "struct.mod.factorul_c": "the factor c (E.3)",
  "struct.mod.i2m": "I2m (the rigid box)",
  "struct.mod.bici": "the whip effect",
  "struct.mod.sub10": "Everything that amplifies or divides d_re comes from the same model as d_re: a c from uncracked concrete over a d_re from a cracked one would be two models inside one formula.",
  "struct.mod.suprastructura": "the superstructure",
  "struct.mod.infrastructura": "the substructure (below the fixity section)",
  "struct.mod.reactiunile": "the reactions",
  "struct.mod.alpha": "the α amplification (θ)",
  "struct.mod.nr_elemente": " · {n} elements",
  "struct.mod.nr_taieturi": " · {n} slab cuts",
  "struct.mod.nr_panouri": " · {n} basement panels",
  "struct.mod.cadere": "Fallback: {motiv} — the whole combined tensor is on M1 (conservative, but not what the engineer asked for). Re-run steps 8 and 9, then 11.",
  "struct.mod.titlu": "The three stiffness models",
  "struct.mod.subtitlu": " — the same geometry, the same masses, three calculation stiffnesses, one for each question.",

  // --- STRUCTURĂ · divergențele față de Eurocod --------------------------
  "struct.div.marker_titlu": "divergence {cod} from the Eurocode — see the explanation below",
  "struct.div.titlu": "Where the method differs from the Eurocode",
  "struct.div.puncte#n": " — {n} point, with its figure on this project| — {n} points, each with its figure on this project| — {n} points, each with its figure on this project",
  "struct.div.intro": "The checks above follow <b>P100-1/2013</b> and the engineer's own spreadsheet. Where SR EN 1998-1 asks for something else, the difference is marked in the formula and explained here — with what it would give if we followed the Eurocode.",
  "struct.div.urmam": "we follow: {ce}",
  "struct.div.implementat": "implemented",
  "struct.div.normativ": "SR EN 1998-1",
  "struct.div.de_ce": "Why: {motiv}",
  "struct.div.impact": "on this project",

  // --- STRUCTURĂ · titlul etapei unui pas care rulează -------------------
  "struct.etapa.pas_din": "{pas} ({k}/{n})",
  "struct.etapa.minute": "{n} min",

  // --- STRUCTURĂ · vederea 3D a unui ochi de placă -----------------------
  "struct.ochi.fara_contur": "The panel's outline is not in the step 7 result (an old run) — the 3D view is built from the outline.",
  "struct.ochi.legenda": "A {h} cm slab, {acoperire} cm cover. Bottom mesh: ",
  "struct.ochi.dir_scurta": " — the short direction ({dir}) sits underneath. ",
  "struct.ochi.calareti": "■ top bars",
  "struct.ochi.calareti_text": " over 0.25·l from each supported end. Under the edges: ",
  "struct.ochi.grinda": "■ beam",
  "struct.ochi.perete": "■ wall",
  "struct.ochi.fara_reazem": "; the dashed red line = an unsupported edge.",
  "struct.ochi.rarit": " The drawing shows one bar in {n}, to stay readable; the spacing in the text is the real one.",

  // --- STRUCTURĂ · rețeta de cost a unui element -------------------------
  "struct.cost.kg_otel": "{n} kg steel",
  "struct.cost.material_kg": "material {pret} €/kg",
  "struct.cost.pierderi": " · includes {n} % supply waste",
  "struct.cost.ore_fierari": "{n} steelfixer hours",
  "struct.cost.ore_detaliu": "{bucati} × {tarif} €/h",
  "struct.cost.ore_familie": "{ore} h/kg × {kg} kg {familie}",
  "struct.cost.montaj": "cage assembly",
  "struct.cost.manopera_kg": "labour {pret} €/kg × {kg} kg",
  "struct.cost.m3_beton": "{n} m³ concrete",
  "struct.cost.beton_pret": "{articol} {pret} €/m³",
  "struct.cost.turnare": "pouring + vibrating",
  "struct.cost.turnare_pret": "{pret} €/m³ × {m3} m³",
  "struct.cost.m2_cofraj": "{n} m² formwork",
  "struct.cost.panou": "panel {pret} €/m²",
  "struct.cost.montat": "erected + struck",
  "struct.cost.dulgheri": "carpenters {pret} €/m² × {m2} m²",
  "struct.cost.mustati": "starter bars from the foundation",
  "struct.cost.mustati_nivel": "cast with the foundation · {element}",

  // --- STRUCTURĂ · Studio (modelul 3D) -----------------------------------
  "struct.studio.gol_componente": "Components appear after the reinforcement steps, which have not run on this row — they do not run on a sketch.",
  "struct.studio.gol_model": "Structural model not available.",
  "struct.studio.corp_sarit": "Building {cod} was skipped: its calculation stopped and the solution went on without it, so it has no model.",
  "struct.studio.corp_exclus": "Building {cod} is excluded from the calculation, so it has no model.",
  "struct.studio.corp_motiv": "Reason: {motiv}",
  "struct.studio.gol_complex": "The complex model is not available: no block has reached the structural model yet.",
  "struct.studio.complex_nota": "The whole complex: each block is analysed on its own — verdicts, reinforcement and element pages live on its block.",
  "struct.studio.complex_corp": "Block {corp}",
  "struct.studio.complex_parcare": "Outer wall of the underground parking: drawn on the whole basement outline, with the perimeter wall thickness — not analysed.",
  "struct.studio.complex_deschide": "Open block {corp}",
  "struct.studio.complex_element": "Element page {ref}",
  "struct.studio.complex_radier_corp": "Raft of building {corp}",
  "struct.studio.complex_radier": "The building's raft: drawn from its footprint, with the thickness from Step 6, from its floor down below the raft — its checks and reinforcement live on the building.",
  "struct.studio.complex_placa_parcare": "The parking's slab on ground: drawn on the whole basement outline, outside the buildings, with the slab-on-ground thickness and its ballast — the same assumption the pit under the parking is dug from. Not computed; whether the parking needs a raft is still to be decided.",
  "struct.studio.deschide_pipeline": "open the pipeline →",
  "struct.studio.caz_generic": "load case {caz} — slab pressures + line loads",
  "struct.studio.permanente": "permanent",
  "struct.studio.variabile": "variable",
  "struct.studio.cazuri": "cases",
  "struct.studio.gol_moduri": "The natural modes come out of the modal analysis, so they appear after step 7. What you see now is the geometry, built without any analysis.",
  "struct.studio.seism": "seismic",
  "struct.studio.masa_titlu": "the seismic masses at the nodes (the spheres) — the forces are computed from them",
  "struct.studio.masa": "seismic mass",
  "struct.studio.detalii_incarcari": "the details of the load take-down — the per-storey diagrams, the equilibrium per case — are in ",
  "struct.studio.pagina_p8": "the step 8 page",
  "struct.studio.gol_incarcari": "The loads appear once step 8 has run.",
  "struct.studio.pipeline": "pipeline →",
  "struct.studio.tab_moduri_titlu": "the natural modes of vibration — periods, participating masses, mode shapes",
  "struct.studio.vedere.model": "3D model",
  "struct.studio.vedere.moduri": "Modes",
  "struct.studio.vedere.incarcari": "Loads",
  "struct.studio.vedere.componente": "Components",
  "struct.studio.vedere.lant": "Calculation chain",
  "struct.studio.vedere.setari": "Calculation settings",
  "struct.rez.drift_sls": "SLS drift",
  "struct.rez.drift_uls": "ULS drift",
  "struct.rez.bici": "Whiplash effect",
  "struct.rez.modal": "Mode decoupling",
  "struct.rez.limita": "limit {v}",
  "struct.rez.sursa.preferinta": "limit from the project preference",
  "struct.rez.sursa.arhitectura": "limit from the architecture's partitions and facade",
  "struct.rez.sursa.llm_guess": "estimated limit (LLM), with the reason noted",
  "struct.rez.sursa.default": "default limit: masonry",
  "struct.rez.esv_nivel": "{n} VSE per level ({corp})",
  "struct.rez.fundare": "Foundation",
  "struct.studio.2d_titlu": "the floor plan: step 4's proposal over the architecture drawing",
  "struct.studio.3d_titlu": "the volume: the FEM model with columns, beams, slabs and walls",
  "struct.studio.macheta": "Model",
  "struct.studio.macheta_titlu": "the unified model: the site plan with façades, with the structure inside it",
  "macheta.titlu": "Unified model",
  "macheta.inapoi": "Back to the Studio",
  "macheta.strat.cladire": "Building and façades",
  "macheta.strat.structura": "Structure",
  "macheta.strat.structura_lipsa": "the structure model is being built…",
  "macheta.se_incarca": "Loading the model…",
  "macheta.fara_situatie": "The row has no site plan upstream",
  "macheta.sfat": "Click a column, a beam or a piece of façade: its details, on the right. Switch off «Building» to see the structure.",
  "macheta.piesa.corp": "building {corp}",
  "macheta.piesa.nivel": "level {nivel}",
  "macheta.piesa.fara_detalii": "the complex model says no more about this piece; its sheet is on its building",
  "struct.studio.reguli_titlu": "the placement rules, with their elements, in the checks board",
  "struct.studio.reguli_incalcate": "{n} of {total} rules broken",
  "struct.studio.reguli_trec": "all {n} placement rules pass",
  "struct.studio.stalpi_grinzi": "{stalpi} columns · {grinzi} beams",
  "struct.studio.fund_titlu": "the foundation is the plan's bottom layer: the footing strip at its real width, with the axes named. Click an axis in the drawing → its checks; here → the foundation page, with the global verdicts (settlement, uplift)",
  "struct.studio.fund": "foundation B {B} m · D_f {D} m · ",
  "struct.studio.axe_pica": "{n} of {total} axes fail",
  "struct.studio.axe_limita": "{n} axes to review",
  "struct.studio.axe_trec": "all {n} axes pass",
  "struct.studio.p4_titlu": "the step 4 page — the full proposal, the solving history, the audit",
  // --- editorul soluției de structură ------------------------------------
  "struct.editor.se_incarca": "Opening the editor…",
  "struct.editor.titlu_nou": "Solution {n}, in progress",
  "struct.editor.salveaza": "Save",
  "struct.editor.salveaza_titlu": "Creates the new solution with your geometry. The detailed analysis does not start on its own: you start it when you are done. The source stays as it was.",
  "struct.editor.salveaza_blocat": "The geometry has errors — fix them first",
  "struct.editor.se_salveaza": "Saving…",
  "struct.editor.inchide": "Close the editor",
  "struct.editor.renunti": "Discard what you drew? Nothing has been saved.",
  "struct.editor.pleci_fara_salvare": "Leave the editor without saving? What you drew is lost.",
  "struct.editor.tipuri_esv": "VSE types used: {n}",
  "struct.editor.tipuri_esv_titlu": "Distinct (thickness, length) pairs per building. Free in the editor; the solver does not go past 5, or past how many you left.",
  "struct.editor.clase": "Classes on this level",
  "struct.editor.clase_titlu": "Elements with the same section and shape on the level on screen: one formwork type. The class is not stored — a changed element moves to another class by itself. Hover a row to light them up on the plan, click to pick the first one.",
  "struct.editor.clasa_cip": "Class {c} · {n} elements",
  "struct.editor.clasa_singur": "Alone in its class ({c})",
  "struct.editor.clasa_cip_titlu": "Elements with the same section and shape on this level; hover the chip to light them up on the plan",
  "struct.editor.clasa_doar_el": "This element only",
  "struct.editor.clasa_toata": "Whole class · {n}",
  "struct.editor.clasa_domeniu_titlu": "How many elements the section is written on: only this one, or all with the same section and shape on this level. Moving, deleting, rotating and the top level stay per element.",
  "struct.editor.clasa_nota_el": "Only on {id}: it leaves class {c}.",
  "struct.editor.clasa_nota_toata": "On all {n} elements of class {c}. Moving, deleting and rotating stay per element.",
  "struct.editor.creare_titlu": "Add an element: a vertical (C), a beam (B) or a slab (P). Esc returns to selection.",
  "struct.editor.grinda_cip_copie": "{s} from the copy · Esc",
  "struct.editor.grinda_din_copie": "The Beam tool started with the copied beam's section ({s}): click the first element, then the second.",
  "struct.editor.copiat": "Copied: {c}. ⌘V places one like it under the cursor, ⇧⌘V writes its section on the picked element.",
  "struct.editor.lipit": "{id} is on the plan, a copy of {s}.",
  "struct.editor.lipit_fara_nivel": "The copied element exists on no level of this building.",
  "struct.editor.lipit_prop": "The section of {s} is written on {n} elements.",
  "struct.editor.lipit_prop_unu": "The section of {s} is written on {id}.",
  "struct.editor.meniu_copiaza": "Copy",
  "struct.editor.meniu_lipeste": "Paste {c}",
  "struct.editor.meniu_lipeste_gol": "Paste",
  "struct.editor.meniu_copiaza_prop": "Copy properties",
  "struct.editor.meniu_lipeste_prop": "Paste properties ({c})",
  "struct.editor.meniu_lipeste_prop_gol": "Paste properties",
  "struct.editor.meniu_lipeste_prop_alta": "The copy is from another family: a vertical does not write its section on a beam, nor the other way round.",
  "struct.editor.adauga": "Add",
  "struct.editor.strat_zidarie": "Confined masonry",
  "struct.editor.zc_titlu": "{id}: confined masonry wall, {cm} cm, {n} tie-columns — the set-back storey's structure; not edited here",
  "struct.editor.gemeni": "Mirrored with {ids}",
  "struct.editor.gemeni_titlu": "Its twins in the building's symmetry (mirror, row of cells): what you change here goes to them too, mirrored",
  "struct.editor.simetric": "Symmetric",
  "struct.editor.simetric_titlu": "On: the twins follow the move, the section and the rotation, mirrored about the building's axis. Off: only this element changes.",
  "struct.editor.ajutor": "How it works",
  "struct.editor.grupuri": "Symmetry groups",
  "struct.editor.grupuri_titlu": "The groups this element belongs to in the building's symmetry; click a group to select its members on the plan",
  "struct.editor.grup_orbita": "Orbit {o}",
  "struct.editor.grup_clasa": "Mirror class",
  "struct.editor.grup_pereche": "Pair with {id}",
  "struct.editor.grup_geaman": "Twin of {id}",
  "struct.editor.grup_orbita_titlu": "Elements that are each other's image through the building's symmetry operations (mirrors, rows of cells)",
  "struct.editor.grup_clasa_titlu": "The piece, its twin across the long axis and the pairs of both about the axis between the gable ends",
  "struct.editor.grup_pereche_titlu": "The element's mirror about the axis between the gable ends",
  "struct.editor.grup_geaman_titlu": "The other end of the same wall line, mirrored about the block's long axis",
  "struct.editor.grup_op_oglinda": "mirror along {d}",
  "struct.editor.grup_op_translatie": "step {m} m along {d}",
  "struct.editor.grup_cu_abateri": "with deviations",
  "struct.editor.grup_selectat": "{n} elements selected on the plan",
  "struct.editor.verif_verificat": "checked",
  "struct.editor.verif_de_verificat": "checking soon…",
  "struct.editor.verif_se_verifica": "checking…",
  "struct.editor.verif_eroare": "the check failed",
  "struct.editor.grinda_oblica": "{g} comes out skewed: its ends on {a} and {b} are not on the same axis. Drag an end onto a piece of the other element to straighten it — or leave it, if you mean to re-anchor it differently.",
  "struct.editor.fara_linie_comuna": "{g} comes out skewed: {a} and {b} share no line it could sit straight on — their common concrete does not take the beam's full width, {d} cm short on {axa}. Move one of them by that much, or drag an end.",
  "struct.editor.capat_fara_linie": "{g} comes out skewed and cannot be straightened from here: the concrete of {esv} does not cover the line of the end on {alt}. Move {esv} onto that line, or re-anchor the end on {alt}.",
  "struct.editor.grinda_exista": "There is already a beam between {a} and {b} on this level.",
  "struct.editor.grinda_fara_nivel": "The two elements share no level.",
  "struct.editor.unealta_alege": "Select",
  "struct.editor.unealta_alege_titlu": "Pick, move (drag) and resize elements — V",
  "struct.editor.unealta_esv": "Vertical",
  "struct.editor.unealta_esv_titlu": "Place a vertical element: click on the plan; inside a wall it lands on the wall's axis — C",
  "struct.editor.unealta_grinda": "Beam",
  "struct.editor.unealta_grinda_titlu": "Connect two elements: click a piece of the first, then one of the second; the beam sits on their common line, parallel or perpendicular to the pieces, straight. With Alt, the end attaches where you click, snapped to the element's points — B",
  "struct.editor.domeniu_titlu": "Which levels adding, deleting and resizing work on",
  "struct.editor.domeniu_nivel": "This level",
  "struct.editor.domeniu_toate": "All levels",
  "struct.editor.doar_citire": "computing — the geometry is locked",
  "struct.editor.legenda_nr": "verticals · beams · slab panels per level",
  "struct.editor.axe_arh_titlu": "Axes drawn by the architect (modular grid): elements are centred on them",
  "struct.editor.axe_arh_arata": "Axes",
  "struct.editor.axe_arh_ascunde": "Axes ✓",
  "struct.editor.axe_derivate_titlu": "Building axes derived by the structure from its walls (the drawing has no drawn axes): nodes, façade supports and slab-panel beams sit on them; the walls stay where the architect drew them",
  "struct.editor.axe_derivate_arata": "Derived axes",
  "struct.editor.axe_derivate_ascunde": "Derived axes ✓",
  "struct.editor.calc_arh": "Architecture plan",
  "struct.editor.calc_arh_arata": "Show",
  "struct.editor.calc_arh_ascunde": "Hide",
  "struct.editor.calc_arh_opacitate": "How much of the architecture plan shows over the geometry",
  "struct.editor.calc_arh_se_incarca": "reading the architecture document…",
  "struct.editor.calc_arh_lipsa": "the architecture document could not be read",
  "struct.editor.calc_arh_fara_nivel": "the document has no storey for this level",
  "struct.editor.calc_plansa_se_incarca": "reading the architect's sheet…",
  "struct.editor.calc_plansa_lipsa": "the architect's sheet could not be read, showing the document's plan",
  "struct.editor.calc_plansa_stearsa": "the architect's sheet (“{fisier}”) was deleted from the documents after the import; showing the document's plan",
  "struct.editor.greseli": "{n} geometry errors — the computation waits",
  "struct.editor.corp_indisponibil": "{cod} — no Step 4, can't be edited",
  "struct.editor.plus_picior": "Extend the element {m} m along the wall",
  "struct.editor.plus_brat_nou": "New {m} m arm into the adjoining wall",
  "struct.editor.plus_brat": "Extend the arm {m} m along the wall",
  "struct.editor.forma": "Shape: {f}",
  "struct.editor.forma_compusa": "compound",
  "struct.editor.brat_spre": "Arm {dir}, {m} m from the node",
  "struct.editor.lungime_brat": "How far the arm sticks out of the leg",
  "struct.editor.trage_picior": "Drag to lengthen or shorten the element; against another element they merge",
  "struct.editor.trage_brat": "Drag to lengthen or shorten the arm; back into the leg, it disappears",
  "struct.editor.unire_indiciu": "Release here: {a} merges with {b}",
  "struct.editor.unite": "{b} went into {a}: a single element",
  "struct.editor.unite_grinzi": "{b} went into {a}: a single element; the beam between them ({g}) is gone",
  "struct.editor.unire_forma": "{a} and {b} can't merge: an element has a single leg, with arms only on the other axis",
  "struct.editor.unire_niveluri": "{a} and {b} can't merge: they don't start on the same base, or the one that would become an arm goes higher",
  "struct.editor.unire_plantat": "{a} and {b} can't merge: {esv} stands on the beam between them or on {b}",
  "struct.editor.unire_amprenta": "{a} and {b} can't merge: one carries the footprint from the analysis, not a leg with arms",
  "struct.editor.sterge_brat": "Remove the arm",
  "struct.editor.brate_nota": "The ends of the pieces drag. The «+» circles grow the element along the architectural walls: next to a wall on the other direction they add an arm — L, T, + —, at a corner they take the leg past the arm. Growing also applies to the levels below, shortening also to the levels above.",
  "struct.editor.brate_nota_fara": "The ends of the pieces drag; the architectural walls leave no room for another arm.",
  "struct.editor.iesiri": "{n} verticals stick out of the architectural walls on {nivel}",
  "struct.editor.iesire_cm": "{id} sticks out {cm} cm past the wall",
  "struct.editor.iesire_afara": "{id} stands outside the walls",
  "struct.editor.iesire_gol": "{id} passes through opening {gol} ({tip})",
  "struct.editor.iesire_camera": "{id}: {cm2} cm² of the section inside the rooms",
  "struct.editor.iesiri_verdict": "{n} verticals refused by the placement verdict on {nivel}",
  "struct.editor.din": "copy of {s}",
  "struct.editor.esv_axa": "on axis {axa}",
  "struct.editor.sectiune_la": "Section at {nivel}",
  "struct.editor.grosime": "thickness (b)",
  "struct.editor.lungime": "long side (L)",
  "struct.editor.latime": "width (b)",
  "struct.editor.inaltime": "depth (h)",
  "struct.editor.aplica_toate": "Applies to every level of the element.",
  "struct.editor.aplica_nivel": "Applies to this level; below, the section grows to at least this, above it does not exceed it.",
  "struct.editor.esv_lipsa_nivel": "The element does not exist on this level.",
  "struct.editor.niveluri": "Levels",
  "struct.editor.pana_la": "from the foundation up to {nivel}",
  "struct.editor.plantat": "supported on beam {grinda}",
  "struct.editor.roteste": "Rotate (R)",
  "struct.editor.sterge_tot": "Delete element",
  "struct.editor.sterge_de_aici": "Delete from here up",
  "struct.editor.sectiune_grinda": "Beam section",
  "struct.editor.capat_ajutor": "Drag the circles at the ends to move the anchorage onto another element or another piece of the same one: the beam sits on the pieces' common line, straight. With Alt, the end attaches where you drop it, snapped to the element's points.",
  "struct.editor.capat_titlu": "End anchored on {esv} — drag it where you want it to attach",
  "struct.editor.capat_acelasi": "{g} already has its other end on {esv}",
  "struct.editor.capat_niveluri": "{esv} is missing on {niveluri}, where {g} needs a support",
  "struct.editor.grinda_pe": "On levels: {niveluri}",
  "struct.editor.sterge_grinda_tot": "Delete beam",
  "struct.editor.sterge_grinda_nivel": "Delete from this level",
  "struct.editor.unealta_placa": "Slab",
  "struct.editor.unealta_placa_titlu": "Draw a new slab corner by corner, attached to the level's slab (a cantilever, a roof) — P",
  "struct.editor.placa_trei_colturi": "A slab has at least three corners.",
  "struct.editor.placa_desenata": "The new slab is now part of the level's slab; its panels arrive with the check.",
  "struct.editor.placa_colt_nou": "Add a corner here (or drag it)",
  "struct.editor.placa_colt_titlu": "Drag the corner · Alt-click removes it",
  "struct.editor.straturi": "Layers",
  "struct.editor.strat_fortat": "Hidden, but the current tool shows it",
  "struct.editor.strat_ochiuri": "Slab panels",
  "struct.editor.strat_esv": "Verticals",
  "struct.editor.strat_grinzi": "Beams",
  "struct.editor.strat_pereti": "Arch. walls",
  "struct.editor.strat_etichete": "Labels",
  "struct.editor.semnale_ochiuri": "Panel flags · {n}",
  "struct.editor.semnal_prea_mare": "the {m} m span exceeds the largest panel; Step 4 would close it with another beam",
  "struct.editor.semnal_fara_reazem": "{pct} % of the outline unsupported (no beam, no wall)",
  "struct.editor.semnal_mic": "{m2} m² panel: a sliver between beams and wall",
  "struct.editor.tavan_pus": "The roof over balcony {id} is now part of the slab, as a cantilever; its outline can be edited from its panel.",
  "struct.editor.tavan_titlu": "Balcony {id} below is open to the sky ({m2} m² without slab): click to cover it",
  "struct.editor.tavan_plus": "+ roof {id}",
  "struct.editor.ochi_nou": "New panel",
  "struct.editor.ochi_peste": "slab panel · above {nivel}",
  "struct.editor.ochi_arie": "Area",
  "struct.editor.ochi_gabarit": "Bounding box",
  "struct.editor.ochi_lumina": "Clear span (inscribed circle)",
  "struct.editor.ochi_deschidere": "Short span",
  "struct.editor.ochi_reazeme_titlu": "How the panel outline is supported",
  "struct.editor.ochi_pe_grinzi": "beams {pct} %",
  "struct.editor.ochi_pe_pereti": "walls {pct} %",
  "struct.editor.ochi_fara_reazem": "unsupported {pct} %",
  "struct.editor.ochi_previzualizare": "Preview: the panel is cut here, from the beams on screen. The server names it and classifies its edges when it checks.",
  "struct.editor.ochi_scoate": "Remove the slab from this panel",
  "struct.editor.ochi_scoate_tot": "Remove the slab from this panel, on all levels",
  "struct.editor.ochi_nota": "A panel isn't drawn by hand: the beams make it. A beam drawn across splits it in two, a deleted one merges it with its neighbour. Inside a panel you can only remove or restore the slab; outlines change only on cantilevers.",
  "struct.editor.consola_om": "Slab drawn in the editor",
  "struct.editor.consola_arh": "Cantilever {id}",
  "struct.editor.consola_iese": "projects {m} m",
  "struct.editor.consola_gata": "Done",
  "struct.editor.consola_editeaza": "Edit outline",
  "struct.editor.consola_scoate": "Remove cantilever",
  "struct.editor.consola_ajutor": "Drag the corners: they snap to 5 cm and line up with their neighbours. The «+» on a side's midpoint adds a corner, Alt-click on a corner removes it, Enter finishes. On «All levels» the change also goes to the same cantilever on the other floors.",
  "struct.editor.gol_titlu": "Removed slab",
  "struct.editor.gol_nota": "The slab was removed here. The analysis puts nothing here: no slab, no mass.",
  "struct.editor.gol_pune": "Restore the slab",
  "struct.editor.gol_pune_tot": "Restore the slab, on all levels",
  "struct.studio.p4": "model details (S4) →",

  // --- STRUCTURĂ · desenul cotat al stâlpului ----------------------------
  "struct.dsc.fara_nivel": "The level asked for is not in this step's result.",
  "struct.dsc.placa": "slab {n}",
  "struct.dsc.grinda": "beam {n}",
  "struct.dsc.h_b": "h = {h} cm  (b = {b} into the page)",
  "struct.dsc.talpa": "footing {n}",
  "struct.dsc.umplutura": "backfill {n}",
  "struct.dsc.leg_beton": "column concrete",
  "struct.dsc.leg_placa": "slab",
  "struct.dsc.leg_placa_m": "slab {n} m",
  "struct.dsc.leg_grinda": "beam at the joint",
  "struct.dsc.leg_critica": "l_cr (closely spaced stirrups)",
  "struct.dsc.leg_lap": "lap l₀",
  "struct.dsc.leg_musta": "starter bars from the foundation",
  "struct.dsc.fara_petrecere": "This storey has NO lap: the bar spans two floors and runs through it whole, so that the lap does not fall in the plastic zone at the base of the structure (P100-1 commentary C5.7.3).",
  "struct.dsc.petrecere": "The lap of <b>l₀ = {n} m</b> ",
  "struct.dsc.in_critica": "stayed inside the critical zone at the foot of the storey — l₀ does not fit between the two critical zones — so it carries k_s = 1.25 (P100-1 §5.7.3(4)). Where the plastic mechanism demands significant plastic deformation, §5.7.3(1) does not allow it at all.",
  "struct.dsc.peste_critica": "is placed ABOVE the critical zone l_cr, because the code does not want it inside (P100-1 §5.7.1(2) and §5.7.3(1)).",
  "struct.dsc.fara_grinda": " No beam frames into the joint above, so the floor band is the {placa} m slab itself — hence the clear height of {liber} m.",
  "struct.dsc.cu_grinda": " The floor band at the joint is {banda} m, of which the slab is {placa} m — hence the clear height of {liber} m, less than the storey height.",
  "struct.dsc.stiva": "The whole stack, at its absolute levels",
  "struct.dsc.cu_fundatie": ", with the foundation under the column: footing {B}×{hT} m + web {bI}×{hI} m",
  "struct.dsc.axul": " (axis {ax})",
  "struct.dsc.axul_global": " (axis {ax} — the global width)",
  "struct.dsc.umplutura_text": ", the concrete starting under {n} m of backfill. The starter bars rise from the footing and lap with the column bars.",
  "struct.dsc.subsol": " The drawing's ±0.00 is the <b>basement floor</b>: D′_f = {df} m is measured from it, and the finished ground is {sus} m higher, so from the ground the footing sits at {dext} m",
  "struct.dsc.fiecare_nivel": ". Each storey carries its own H level and clear height.",
  "struct.dsc.la_scara": "At the scale of the whole stack{ce} do not fit in the drawing — switch to a single storey to see them, or read them from the table below.",
  "struct.dsc.etrierii": " the stirrups",
  "struct.dsc.si": " and",
  "struct.dsc.cotele": " the storey levels",
  "struct.dsc.estimat": "An older run: the slab thickness, the beam band and the lap are ESTIMATED (the step did not publish them yet). Re-run step 15 so they come from the calculation.",
  "struct.dsc.fara_fundatie": "The foundation is not drawn: step 6 has not run, so the founding depth is unknown. (We do not put an invented footing under the column.)",

  // --- STRUCTURĂ · terenul și săpătura în 3D -----------------------------
  "struct.teren.strat": "[{de_la}–{pana_la} m] {nume}",
  "struct.teren.apa": "[{n} m below existing ground] GROUNDWATER",
  "struct.teren.mal_vertical": "the {ce} face stays vertical: {adancime} m in {eticheta}, under the {h} m that stand unsupported",
  "struct.teren.mal_taluz": "the {ce} face is battered {panta} — {sursa}",
  "struct.teren.mal_gropii": "of the pit",
  "struct.teren.mal_santurilor": "of the trenches",
  "struct.teren.mal_sapaturii": "of the excavation",
  "struct.teren.locale": "{n} local deepenings of the pit under the blocks' rafts: {volum} m³",
  "struct.teren.corpuri_suma": "sum of the blocks' pits, each dug on its own section: {n} m³",
  "struct.teren.umplutura": "General excavation: {n} m³ go back as compacted fill",
  "struct.teren.volum": "Excavation: {n} m³",
  "struct.teren.volum_detaliu": " (the basement box pit {groapa} m³ + the trenches under the axes {santuri} m³)",
  "struct.teren.spatiu_lucru": "The excavation is not the size of the concrete: {n} m of working space next to the formwork, on each side, and at least the width of the excavator bucket. The trenches (concrete → excavation): {latimi}",
  "struct.teren.taluz": "Of the {total} m³, {taluz} m³ are the batter wedge: this soil is dug out and put back, so it counts as backfill, not as haulage",
  "struct.teren.straturi": "The ground: {n} layers from the geotechnical survey, down to {adancime} m below existing ground. The points are denser where the soil is denser; the colour is that of the earthworks class. The gap in the cloud is the excavation itself.",
  "struct.teren.fara_straturi": "No stratification (step 1 has not run): only the excavation is drawn.",

  // --- STRUCTURĂ · stările comune ale unei pagini de pas -----------------
  "struct.pas.ruleaza": "The step is running…",
  "struct.pas.nerulat": "The step has not run yet. Press “Run” in the menu on the left.",
  "struct.pas.esuat": "The last run failed: {eroare}",
  "struct.pas.eroare_necunoscuta": "unknown error",
  "struct.pas.ruleaza_din_nou": "Run it again from the menu on the left.",
  "struct.pas.rulat_la": "run at {cand}",

  // --- STRUCTURĂ · Pasul 1 — studiul geotehnic ---------------------------
  "struct.geo.ruleaza": "The geotechnical survey (a PDF, scans included) is read by an LLM and the corrected conventional pressure is computed. It takes ~30 seconds for a single document; on a file with several pieces, a triage by title first picks what is worth reading.",
  "struct.geo.p_conv": "Corrected conventional pressure",
  "struct.geo.p_conv_sub": "p̄conv {p} kPa + CD {cd} + CB {cb} · at Df={df} m, B={b} m",
  "struct.geo.praguri": "Check thresholds",
  "struct.geo.praguri_sub": "static (1.10×) · dynamic {n} kPa (1.35×)",
  "struct.geo.seism_inghet": "Seismicity & frost",
  "struct.geo.seism_sub": "Tc {tc} · frost depth {inghet} · groundwater {apa}",
  "struct.geo.stratificatia": "Soil stratification",
  "struct.geo.col_strat": "Layer",
  "struct.geo.col_adancime": "Depth",
  "struct.geo.col_tip": "Type",
  "struct.geo.unitati": "γ [kN/m³] · φ [°] · c [kPa] · E [kPa] · n [%] — extracted from ",
  "struct.geo.faza11": "Phase 1.1 calculation (STAS 3300/2)",
  "struct.geo.strat_fundare": "Founding layer",
  "struct.geo.la_df": "(at Df = {df} m{strat})",
  "struct.geo.stratul_de_la": ", the layer from {de_la} to {pana_la} m below existing ground",
  "struct.geo.coeficienti": "Coefficients",
  "struct.geo.de_unde_k": "Where K1 and K2 come from",
  "struct.geo.de_unde_k_text": "Not from the geotechnical survey and not from a calculation: they are <b>tabulated code values</b>. All the survey provides is the <b>type of the layer</b> the footing lands in — that picks the row in the table.",
  "struct.geo.lant1": "The geotechnical survey ",
  "struct.geo.lant1b": " (PDF) → the stratification in the table above.",
  "struct.geo.lant2": "The footing at Df = {df} m lands in layer ",
  "struct.geo.lant2b": " ({de_la}–{pana_la} m below existing ground)",
  "struct.geo.lant3": "The layer is classified as ",
  "struct.geo.neclasificat": "unclassified",
  "struct.geo.plasticitate": ", plasticity {ce}",
  "struct.geo.sursa_tip_implicit": "from the layer's description in the geotechnical survey",
  "struct.geo.lant4": "The type picks the row in the tables below → ",
  "struct.geo.k1_titlu": "K1 — the width correction (CB)",
  "struct.geo.k2_titlu": "K2 — the depth correction (CD, at Df ≥ 2 m)",
  "struct.geo.sursa_tabele": "Source of the tables: {sursa}.",
  "struct.geo.fara_tabele": "The full tables appear after a fresh run of the step (the saved result is from an earlier version).",
  "struct.geo.col_teren": "Founding ground",
  "struct.geo.rand_aplicat": "← the row applied on this project",
  "struct.geo.formule": "Formulas",
  "struct.geo.f_pbar": "The base conventional pressure (from the survey, at the table's reference values: B = 1.00 m, Df = {df}):",
  "struct.geo.df_referinta_implicit": "2.00 m",
  "struct.geo.f_cd": "The depth correction (Df = {df} m {semn}):",
  "struct.geo.f_cb": "The width correction (B = {b} m {semn}):",
  "struct.geo.f_pconv": "The corrected conventional pressure:",
  "struct.geo.f_static": "The static check threshold (the fundamental combination, ULS):",
  "struct.geo.f_dinamic": "The dynamic check threshold (the seismic combinations):",
  "struct.geo.f_extrase": "Taken straight from the survey (no calculation):",
  "struct.geo.info_citite_titlu": "Values read, not computed",
  "struct.geo.info_citite_ce": "These come out of no formula — they were read as they stand from the geotechnical survey. They feed the following steps as input data.",
  "struct.geo.p_ag": "the ground acceleration for the design earthquake (a fraction of g)",
  "struct.geo.p_ag_sursa": "the P100-1 zoning map, quoted in the survey",
  "struct.geo.p_tc": "the control period of the seismic spectrum, in seconds",
  "struct.geo.p_inghet": "frost",
  "struct.geo.p_inghet_nume": "the depth to which the ground freezes in winter — the footing must sit below it, or the ice heaves and moves the foundation",
  "struct.geo.p_inghet_sursa": "STAS 6054-77, quoted in the survey",
  "struct.geo.p_apa": "water",
  "struct.geo.p_apa_nume": "the depth of the groundwater",
  "struct.geo.p_apa_sursa": "measured in the survey's boreholes",
  "struct.geo.extrase_text": "seismic zone ag = {ag}, Tc = {tc} (P100-1) · frost depth {inghet} (STAS 6054-77) · groundwater {apa}",
  "struct.geo.ks": "Recommended Ks",
  "struct.geo.ks_nota": "(for information only — it is not used directly; it is recomputed from settlements at step 6)",
  "struct.geo.observatii": "Notes",

  // --- STRUCTURĂ · zonarea seismică a amplasamentului --------------------
  "struct.zon.titlu": "Seismic zoning of the site",
  "struct.zon.info_titlu": "Why the map beats the survey, for a_g and T_C",
  "struct.zon.info_ce": "a_g and T_C are not field measurements: they are values read off figures 3.1 and 3.2 of P100-1/2013, indexed by locality. A geotechnical survey transcribes them — and a transcription can be wrong.",
  "struct.zon.info_de_ce": "The other geotechnical parameters (stratification, p_conv, water table, frost depth) stay with the survey: there the survey is the only source, because they are measured on this very site. The reversal of source priority applies only to those two zoning values.",
  "struct.zon.p_ag": "the ground acceleration for a 225-year return period, in g",
  "struct.zon.p_ag_sursa": "P100-1/2013 zoning, figure 3.1, by locality",
  "struct.zon.p_tc": "the control period of the spectrum, in s (only 0.7 / 1.0 / 1.6)",
  "struct.zon.p_tc_sursa": "P100-1/2013 zoning, figure 3.2, by locality",
  "struct.zon.amplasament": "site",
  "struct.zon.necunoscut": "unknown",
  "struct.zon.identificat": "found in the locality table: {localitate}, {judet} county",

  // --- STRUCTURĂ · dosarul geotehnic -------------------------------------
  "struct.dos.titlu": "The geotechnical file — {citite} document(s) read out of {total}",
  "struct.dos.info_titlu": "Why not every document is read",
  "struct.dos.info_ce": "A geotechnical survey for a large project has the summary report plus annexes: borehole logs, laboratory certificates, site plans, photographs. The parameters we need sit in the report and in a few of the annexes.",
  "struct.dos.info_de_ce": "Every document read means one LLM call with the whole PDF attached: time and money. A triage by title decides first what is worth opening. Skipped documents stay in the list, with the reason — if the triage got it wrong, you see it here.",
  "struct.dos.stare_citit": "read",
  "struct.dos.stare_ignorat": "skipped at triage",
  "struct.dos.stare_necitit": "over the limit",
  "struct.dos.stare_neacceptat": "unreadable format",
  "struct.dos.stare_eroare": "error while reading",
  "struct.dos.p_citit_nume": "the document was sent for extraction",
  "struct.dos.p_citit_sursa": "the triage judged it relevant",
  "struct.dos.p_sarit_nume": "it contains no design parameters",
  "struct.dos.p_sarit_sursa": "the triage's decision, with the reason in the column alongside",
  "struct.dos.p_format_nume": "extraction attaches the document as a PDF",
  "struct.dos.p_format_sursa": "a .docx/.xlsx never reaches the model — upload it as a PDF if it carries parameters",
  "struct.dos.rol_parametri": "design parameters",
  "struct.dos.rol_raport": "main report",
  "struct.dos.rol_foraje": "borehole annex",
  "struct.dos.rol_laborator": "laboratory certificate",
  "struct.dos.rol_seismicitate": "seismicity",
  "struct.dos.col_document": "Document",
  "struct.dos.col_rol": "Role",
  "struct.dos.col_stare": "State",
  "struct.dos.col_motiv": "Reason",
  "struct.dos.provenienta": "Where each parameter comes from",
  "struct.dos.completari": "Layers filled in from other pieces of the file",
  "struct.dos.completari_text": "The file is read as ONE survey: the borehole log gives the geometry of the layers, the laboratory certificate gives γ, φ, c, E and I_C at the same depths. An empty field in the base column is taken from the piece covering the same depth — the column does not change, only the gaps are filled.",
  "struct.dos.col_de_la": "From",
  "struct.dos.col_camp": "Field",
  "struct.dos.col_valoare": "Value",
  "struct.dos.col_din": "From",
  "struct.dos.conflicte": "Values that differ between documents",
  "struct.dos.conflicte_text": "Each parameter is taken from the piece that is right FOR IT: the conventional pressure from the report or from the design-parameters sheet, the water level from the borehole log (that is where it is measured), a_g and T_C from the seismicity piece. A value in a laboratory certificate does not become a conventional pressure just because it is a pressure. The remaining values are listed here, so the difference is a decision seen, not one hidden.",
  "struct.dos.col_parametru": "Parameter",
  "struct.dos.col_folosit": "Used",
  "struct.dos.col_alternativ": "Alternative",
  "struct.dos.par_straturi": "the stratification",
  "struct.dos.par_df_ref": "the reference Df for p̄conv",
  "struct.dos.par_apa": "the groundwater level",
  "struct.dos.par_inghet": "the frost depth",
  "struct.dos.par_ks": "recommended Ks",
  "struct.dos.par_df": "recommended Df",
  "struct.dos.par_b": "recommended minimum B",

  // --- STRUCTURĂ · Pasul 12 — eforturile finale --------------------------
  "struct.ef.ruleaza": "The combined forces (R = C·F) are read from the step 11 tensor and the check points are extracted: 5 stations per beam, the foot and head of each column, plus the base reactions — with the combination that gives the extreme and the concurrent components.",
  "struct.ef.nerulat": "The step has not run yet. Press “Run” in the menu on the left (after step 11).",
  "struct.ef.punct_r1_sus": "face of support 1 — minimum moment (top fibre in tension)",
  "struct.ef.punct_r1_jos": "face of support 1 — maximum moment (bottom fibre in tension)",
  "struct.ef.punct_camp": "mid-span — the extreme moment inside the span (the stations + the analytical point where V2 = 0)",
  "struct.ef.punct_camp_v": "span — the extreme shear on the cuts inside the wall panel (where it does not sit at a column face)",
  "struct.ef.punct_r2_sus": "face of support 2 — minimum moment (top fibre in tension)",
  "struct.ef.punct_r2_jos": "face of support 2 — maximum moment (bottom fibre in tension)",
  "struct.ef.punct_baza": "the column foot (above the rigid zone at the foundation/slab)",
  "struct.ef.punct_virf": "the column head = the section below the beam (not the middle of the joint)",

  "struct.ef.scurt_r1_sus": "R1 top",
  "struct.ef.scurt_r1_jos": "R1 bottom",
  "struct.ef.scurt_camp": "span",
  "struct.ef.scurt_camp_v": "span V",
  "struct.ef.scurt_r2_sus": "R2 top",
  "struct.ef.scurt_r2_jos": "R2 bottom",
  "struct.ef.scurt_baza": "foot",
  "struct.ef.scurt_virf": "head",
  "struct.ef.h_element": "the globally unique id from step 4 (GR7.1 = beam 7, level 1); the same position keeps the same number on every level",
  "struct.ef.h_nivel": "the level the element sits on (for beams: the floor above that level)",
  "struct.ef.h_grupare": "the family of combinations the extreme was taken from: fundamental (1.35G + 1.5Q…) or seismic (G + ψ2·Q ± E)",
  "struct.ef.h_punct": "the check point: beams = the faces of the two supports (top/bottom) + mid-span; columns = the foot and the head",
  "struct.ef.h_criteriu": "which extreme was sought at the column: P_min/P_max (the extreme axial force) or M2_max/M3_max (the largest moment in magnitude) — the row's other components are the CONCURRENT ones",
  "struct.ef.h_x": "the station's position, in metres, measured from the AXIS of the column at end 1 of the beam (for columns: from the bottom node); the stations account for the rigid zones",
  "struct.ef.h_combinatia": "the load combination that gives the extreme — every value in the row comes from IT (they are concurrent, not an envelope)",
  "struct.ef.h_P": "the axial force [kN]; negative = compression",
  "struct.ef.h_V2": "the vertical shear force [kN] (beams) / on local axis 2 (columns)",
  "struct.ef.h_V2GS": "the shear at the same station from combination GS = G + ψ2·Q + ψ2·S, without seismic action — it feeds the capacity-related V_Ed at reinforcement",
  "struct.ef.h_V3": "the shear force on the other local axis [kN] (beams: horizontal)",
  "struct.ef.h_T": "the torsional moment [kN·m]",
  "struct.ef.h_M2": "the bending moment about local axis 2 [kN·m] (beams: in the horizontal plane)",
  "struct.ef.h_M3": "the main bending moment [kN·m]; for beams positive = bottom fibre in tension",
  "struct.ef.h_L": "the beam length between the AXES of the columns [m]",
  "struct.ef.h_l0": "the CLEAR span [m] — between the faces of the supports (L minus half the column depths); it is what feeds capacity design at reinforcement",
  "struct.ef.legenda": "What each column means",
  "struct.ef.livrabil": "The deliverable: M / V / N per element",
  "struct.ef.info_titlu": "What the table contains",
  "struct.ef.info_ce": "For every beam and column (with its plan id): the bending moments, the shear forces and the axial force at the check points, with the combination that gives the extreme.",
  "struct.ef.info_de_ce": "The components of a row are CONCURRENT (from the same combination) — not an anonymous envelope. That is what makes a correct P–M–M check at the columns and an economical beam reinforcement possible.",
  "struct.ef.p_M3": "the main bending moment (kN·m)",
  "struct.ef.p_M3_sursa": "the combined tensor of step 11",
  "struct.ef.p_V2": "the vertical shear force (kN)",
  "struct.ef.p_idem": "the same",
  "struct.ef.p_P": "the axial force (kN; negative = compression)",
  "struct.ef.p_V2GS": "the shear from combination GS (G + ψ2·Q, no seismic action) — for the capacity-related V_Ed at reinforcement",
  "struct.ef.p_V2GS_sursa": "step 11's GS combination",
  "struct.ef.p_l0": "the beam's clear span (between the faces of the supports)",
  "struct.ef.p_l0_sursa": "the column geometry from step 4",
  "struct.ef.sub": "beams + columns, over {n} combinations (fundamental + seismic + GS)",
  "struct.ef.export": "The full export",
  "struct.ef.export_sub": "every combination × every check point ({fisier})",
  "struct.ef.descarca": "Download CSV",
  "struct.ef.sanatate#n": "Sanity checks — {n} warning|Sanity checks — {n} warnings|Sanity checks — {n} warnings",
  "struct.ef.tab_grinzi": "Beams ({n})",
  "struct.ef.tab_stalpi": "Columns and blades ({n})",
  "struct.ef.tab_reactiuni": "Base reactions ({n})",
  "struct.ef.toate": "all",
  "struct.ef.fundamentala": "fundamental",
  "struct.ef.seismica": "seismic",
  "struct.ef.cauta": "e.g. GR7 or ST3",
  "struct.ef.conventii": "Sign and reporting conventions",
  "struct.ef.fara_randuri": "No row passes the filters.",
  "struct.ef.fara_reactiuni": "No reaction reported.",
  "struct.ef.col_nod": "Node",
  "struct.ef.col_comb_fz": "Combination (extreme FZ)",
  "struct.ef.nota_reactiuni": "The base reactions feed the foundation design — the CSV holds the values for every combination.",

  // --- STRUCTURĂ · capetele de coloană ale tabelelor de eforturi ---------
  "struct.ef.c_grupare": "Group",
  "struct.ef.c_punct": "Point",
  "struct.ef.c_criteriu": "Criterion",
  "struct.ef.c_combinatia": "Combination",

  // --- STRUCTURĂ · Pasul 8 — încărcările ---------------------------------
  "struct.inc.ruleaza": "The gravity load cases (G1–G3, Q1, Q2, S) are built, applied to the step 7 FEM model, and global equilibrium is checked per case. The results are stored in the force tensor for steps 9–12.",
  "struct.inc.nerulat": "The step has not run yet. Press “Run” in the menu on the left (after step 7).",
  "struct.inc.greutate": "Total permanent weight",
  "struct.inc.info_g_titlu": "The permanent weight (G1 + G2a + G2b + G3)",
  "struct.inc.info_g_ce": "Everything that always sits on the structure: the concrete (G1), the non-structural walls (G2a + G2b) and the finishes (G3), summed over the whole building.",
  "struct.inc.info_g_de_ce": "It is the background the variable loads (imposed, snow) come on top of, and the basis of the seismic mass. Each case is kept separate so that it gets its own coefficient in the combinations.",
  "struct.inc.p_g1": "the self-weight of the concrete (25 kN/m³)",
  "struct.inc.p_g1_sursa": "the step 7 geometry",
  "struct.inc.p_g2": "the non-structural walls, on beams / on the slab",
  "struct.inc.p_g2_sursa": "the walls from step 2 + the g_perete setting",
  "struct.inc.p_g3": "the finishes (SDL)",
  "struct.inc.p_g3_sursa": "the «Building data» tab",
  "struct.inc.cazuri_sub": "{cazuri} load cases on {elemente} elements",
  "struct.inc.echilibru": "Global equilibrium",
  "struct.inc.info_e_titlu": "The equilibrium check (T3)",
  "struct.inc.info_e_ce": "For each case the total applied to the structure is compared with the sum of the vertical reactions at the supports. The two must match (an imbalance below 0.1%).",
  "struct.inc.info_e_de_ce": "If part of the load «goes missing» (disconnected elements, walls mapped wrongly), the sum of the reactions no longer adds up — the check catches exactly this kind of modelling error.",
  "struct.inc.echilibru_ok": "✓ OK",
  "struct.inc.echilibru_nu": "✗ NO",
  "struct.inc.echilibru_ok_sub": "the sum of the reactions = the total applied, for every case",
  "struct.inc.echilibru_nu_sub": "at least one case is out of balance beyond tolerance — see the table",
  "struct.inc.date_intrare": "The input data of the loads",
  "struct.inc.info_d_titlu": "Where each figure comes from",
  "struct.inc.info_d_ce": "Every value the cases are built from: unit weights, finishes, the category's imposed load, the locality's snow, the ψ coefficients.",
  "struct.inc.info_d_de_ce": "A total in kN cannot be checked unless you can see what it was made of. The «where it comes from» column says who chose the value — the design brief, the zoning map, the architecture or the method — and the «clause» column says by what right.",
  "struct.inc.col_marime": "Quantity",
  "struct.inc.col_simbol": "Symbol",
  "struct.inc.col_valoare": "Value",
  "struct.inc.col_de_unde": "Where it comes from",
  "struct.inc.col_articol": "Clause",
  "struct.inc.nota_panouri": "of which {n} kN are the basement panels, applied at the nodes of their shell strips (not as a line load on a member)",
  "struct.inc.echilibru_titlu": "The equilibrium check, case by case",
  "struct.inc.col_caz": "Case",
  "struct.inc.col_total": "Total applied",
  "struct.inc.col_reactiuni": "Sum of the reactions",
  "struct.inc.col_dezechilibru": "Imbalance",
  "struct.inc.echilibru_final_ok": "✓ Global equilibrium is satisfied for every case.",
  "struct.inc.echilibru_final_nu": "• Global equilibrium is NOT satisfied for every case — check how the loads are mapped.",
  "struct.inc.pereti": "The non-structural walls",
  "struct.inc.info_p_titlu": "How the walls are spread over the structure",
  "struct.inc.info_p_ce": "The walls from the architecture (step 2) are mapped onto the beams beneath them — a line load q_perete per metre of beam (G2a). Whatever has no beam below becomes an equivalent uniform load on the slab (G2b).",
  "struct.inc.info_p_de_ce": "A wall on a beam loads that beam directly; a wall «in the field» bears on the slab and spreads out. Keeping them apart keeps the beam forces right.",
  "struct.inc.p_q": "the wall's weight per linear metre (g_perete × the clear height)",
  "struct.inc.p_q_sursa": "the wall's build-up (g_perete_kn_m2)",
  "struct.inc.p_aria": "area C",
  "struct.inc.p_aria_nume": "the common circulation areas (cat. C) of the storey",
  "struct.inc.p_aria_sursa": "step 2 — the room functions",
  "struct.inc.col_pe_grinzi": "On beams",
  "struct.inc.col_pe_placa": "On the slab",
  "struct.inc.col_q": "q wall",
  "struct.inc.col_arie_c": "Area C",

  // --- STRUCTURĂ · Pasul 5 — fundația estimată ---------------------------
  "struct.fnd.ruleaza": "The file and layers are picked by an LLM, then the outline is computed. The first run can take ~1 minute if the drawing has not been interpreted yet.",
  "struct.fnd.lungime": "Estimated foundation length",
  "struct.fnd.nr_axe": "{n} continuous axes — ",
  "struct.fnd.sursa_implicita": "the cumulative length of the structural walls' axis (a continuous line, under the openings too)",
  "struct.fnd.intra_p6": " — it feeds step 6 as a parameter",
  "struct.fnd.axe_grila": "{axe} named axes, {peGrila} on the building grid",
  "struct.fnd.rotire": " (maximum rotation {n}°)",
  "struct.fnd.abatere": "the beams stray from the axis line by at most ",
  "struct.fnd.info_axe_titlu": "Why the axes are straight",
  "struct.fnd.info_axe_de_ce": "A foundation axis is a straight line, and the footing has a width B (0.6–2.5 m): a deviation of tens of centimetres still sits underneath it. One of METRES means the axis really is not straight, and the cause lies further upstream — in extracting the walls or placing the columns.",
  "struct.fnd.p_theta": "the angle of the building grid",
  "struct.fnd.p_theta_sursa": "step 2, measured on the WALLS (not on the beams)",
  "struct.fnd.p_abatere": "deviation",
  "struct.fnd.p_abatere_nume": "how far the beams wander from the straight line that replaced them [m]",
  "struct.fnd.p_abatere_sursa": "computed here",
  "struct.fnd.p_toleranta": "tolerance",
  "struct.fnd.p_toleranta_nume": "below what two lines count as the same axis [m]",
  "struct.fnd.p_toleranta_sursa": "{n} m — the narrowest footing step 6 can choose",
  "struct.fnd.regim": "Height regime",
  "struct.fnd.info_regim_titlu": "The height regime",
  "struct.fnd.info_regim_ce": "The usual shorthand for how many storeys the building has: P = ground floor, P+1 = ground floor plus one storey, S+P+2 = basement, ground floor and two storeys.",
  "struct.fnd.info_regim_de_ce": "The number of storeys feeds the building's total weight (each storey adds load on the foundation). It is taken from step 2 (the architecture analysis), so that every step uses the same list of levels.",
  "struct.fnd.p_niveluri": "levels",
  "struct.fnd.p_niveluri_nume": "the floor plans identified",
  "struct.fnd.p_niveluri_sursa": "step 2 (or, without it, identified here from the file names)",
  "struct.fnd.nr_niveluri": "{n} levels — ",
  "struct.fnd.suprafata": "Gross floor area",
  "struct.fnd.suprafata_sub": "the sum of the storey areas · bounding box {x} × {y} m",
  "struct.fnd.per_nivel": "Areas per level",
  "struct.fnd.col_fisier": "File",
  "struct.fnd.col_suprafata": "Area",
  "struct.fnd.col_sursa": "Source",
  "struct.fnd.eroare_nivel": "error: {eroare}",
  "struct.fnd.identificat": "The structural walls we identified",
  "struct.fnd.fisier": "File",
  "struct.fnd.geometria": "The geometry",
  "struct.fnd.layere_pereti": "Wall layers",
  "struct.fnd.layere_goluri": "Opening layers",
  "struct.fnd.goluri_nota": "windows/doors — the foundation runs straight under these openings",
  "struct.fnd.desen": "Structural walls + foundation axis",

  // --- STRUCTURĂ · colectorul de verificări ------------------------------
  "struct.col.placa": "slab",
  "struct.sev.critica": "critical",
  "struct.sev.critica_ce": "drift and the whiplash effect — the displacements the structural engineer requires to pass, no exceptions. Repaired before any other check, and a solution that fails them cannot beat one that passes them, however good it is otherwise.",
  "struct.sev.globala": "global",
  "struct.sev.globala_ce": "the validity of the analysis and the rest of the displacement control (θ, chord rotation, the rigid box, the separation of the modes) — while one of these fails, every figure below it is computed on a different structure, and the fix is not reinforcement but the layout itself. Repaired right after the critical ones.",
  "struct.sev.importanta": "important",
  "struct.sev.importanta_ce": "the capacity of the vertical elements, the foundation, the placement and the decoupling of the modes — repairing them needs larger sections, that is a new layout, so it propagates.",
  "struct.sev.locala": "local",
  "struct.sev.locala_ce": "solved with reinforcement, a redistribution or an architect's decision, without moving the rest of the solution. Beams and joints are here on purpose: capacity design WANTS the plastic hinges in the beams.",
  "struct.tem.fara": "No theme (a new category)",
  "struct.tem.fara_ce": "Checks whose category is not (yet) caught by any theme in the catalogue. They are shown here so they do not vanish from the board.",

  // --- STRUCTURĂ · tabloul de verificări ---------------------------------
  "struct.vp.pastila_pica": "fail",
  "struct.vp.chip_pica": "fails",
  "struct.vp.pastila_limita": "review",
  "struct.vp.chip_limita": "needs review",
  "struct.vp.pastila_trec": "pass",
  "struct.vp.chip_trece": "passes",
  "struct.vp.pastila_nu_se_cer": "not required",
  "struct.vp.chip_nu_se_cere": "is not required",
  "struct.vp.chip_informativ": "informative",
  "struct.vp.grup_tematica": "Theme",
  "struct.vp.grup_verdict": "Verdict",
  "struct.vp.grup_severitate": "Severity",
  "struct.vp.titlu": "Checks",
  "struct.vp.gol": "The checks board fills up once the chain has run.",
  "struct.vp.esec": "The checks could not be loaded. Reload the page; if it happens again, the structure service is not responding.",
  "struct.vp.esec_randuri": "The checks of this type could not be loaded. Close the card and open it again.",
  "struct.vp.corp_ansamblu": "the shared substructure: the basement walls, the foundation, the box",
  "struct.vp.corp_titlu": "the checks of building {corp}, including its global ones (modes, drift, the rigid box)",
  "struct.vp.corp": "building {corp}",
  "struct.vp.solutie_titlu": "Structure — solution {solutie}",
  "struct.vp.disciplina": "Structure",
  "struct.vp.solutia": "solution {solutie}",
  "struct.vp.filtre": "Filters",
  "struct.vp.filtre_ce": "they combine: what is left passes through all of them",
  "struct.vp.verdict_ce": "what came out of the calculation",
  "struct.vp.arata_numai": "Show only what {ce}",
  "struct.vp.severitate_ce": "how far a failure propagates — what gets fixed first",
  "struct.vp.sev_titlu": "{cod} — {ce}",
  "struct.vp.tematica_ce": "the subject being checked",
  "struct.vp.toate_temele_titlu": "Every theme, one under another",
  "struct.vp.toate_temele": "All themes",
  "struct.vp.toate": "all",
  "struct.vp.din": "{n} of {total}",
  "struct.vp.verificari#n": "check|checks|checks",
  "struct.vp.si": "and",
  "struct.vp.scoate": "Remove the {grup} filter",
  "struct.vp.niciun_filtru": "— no filter, everything is shown",
  "struct.vp.desfa": "clear the filters",
  "struct.vp.tema_sumar#n": "{n} check, {tipuri} types|{n} checks, {tipuri} types|{n} checks, {tipuri} types",
  "struct.vp.tema_pica": " · {n} fail",
  "struct.vp.nimic": "No check passes through all the filters above.",
  "struct.vp.cadran": "{trec} of {masurate} measured checks pass",
  "struct.vp.globale#n": "{n} global failure|{n} global failures|{n} global failures",
  "struct.vp.fata_de": "against {prag}",
  "struct.vp.dir_tip": "direction {dir}{detaliu} · {verdict}",
  "struct.vp.dir_detaliu": " — {ce}",
  "struct.vp.pe_directii#n": " · on direction {lista}| · on directions {lista}| · on directions {lista}",
  "struct.vp.masurat_card": " · {E}",
  "struct.vp.masurat_card_prag": " · {E} against {R}",
  "struct.vp.raportata": "reported",
  "struct.vp.raportata_titlu": "a REPORTED check: it is counted and shown, but it does not stop delivery — it is not a code requirement, and the engineer accepted that it may not pass",
  "struct.vp.informative": "{n} informative",
  "struct.vp.ce_se_face": "What to do",
  "struct.vp.bucla": " · the loop tries it on its own",
  "struct.vp.fara_categorie": "This type does not (yet) have a category in the explanation catalogue — the figures below are the ones the step emitted.",
  "struct.vp.col_verificarea": "The check / the element",
  "struct.vp.col_directie": "Direction",
  "struct.vp.col_masurat": "Measured against the threshold",
  "struct.vp.se_arata": "showing {n} of {total}, the tightest first — ",
  "struct.vp.incarca": "load 200 more",
  "struct.vp.toate_care_pica": "every element that fails this check →",
  "struct.vp.trasa_titlu": "Where the figures come from — “0 means zero”",
  "struct.vp.trasa_ce": "How many checks could be read from each calculation. A calculation not read means verdicts unseen, so an incomplete “0 failures”.",
  "struct.vp.col_calculul": "The calculation",
  "struct.vp.col_verificari": "Checks",
  "struct.vp.col_picate": "Failed",
  "struct.vp.col_citit": "Read",
  "struct.vp.necitit": "not read",
  "struct.vp.vezi_calculul": "see the calculation →",

  // --- STRUCTURĂ · pagina unui element -----------------------------------
  "struct.elp.nota_completa": "Full calculation note",
  "struct.elp.nota_sub": "everything {pas} computes for this element: the sections, the formulas, the chosen reinforcement and why.",
  "struct.elp.nota_pagina_pasului": "step page →",
  "struct.elp.nota_pas.armare_grinzi": "Step 13 (beam reinforcement)",
  "struct.elp.nota_pas.armare_stalpi": "Step 15 (column reinforcement)",
  "struct.elp.nota_pas.armare_pereti": "Step 14 (wall reinforcement)",
  "struct.elp.nota_pas.armare_placa": "Step 16 (slab reinforcement)",
  "struct.elp.nota_pas.armare_fundatie": "Step 18 (foundation reinforcement)",
  "struct.elp.negasit": "The element is not (yet) in the results index — run the reinforcement steps or check the reference.",
  "struct.elp.inapoi": "back to the list →",
  "struct.elp.titlu_3d": "{tip} {ref} — 3D",
  "struct.elp.restrange_titlu": "back into the column, next to the checks",
  "struct.elp.extinde_titlu": "across the full width of the page — the checks move below the drawing",
  "struct.elp.restrange": "shrink the drawing",
  "struct.elp.extinde": "expand the drawing",
  "struct.elp.doar_nivel_stalp": "only the piece of column at this storey",
  "struct.elp.doar_deschidere": "only this span",
  "struct.elp.doar_stalpul": "Only column {ref}",
  "struct.elp.doar_grinda": "Only beam {ref}",
  "struct.elp.stiva_titlu": "the whole stack, from the footing to the top floor",
  "struct.elp.ax_titlu": "the whole collinear axis — {n} spans, end to end",
  "struct.elp.tot_stalpul": "The whole column {ref}",
  "struct.elp.tot_axul": "The whole axis ({n})",
  "struct.elp.capitolul5": "Chapter 5 — The element",
  "struct.elp.urmatoarea_titlu": "the next failing element, by smallest reserve",
  "struct.elp.urmatoarea": "{ref} (the next one that fails) →",
  "struct.elp.re_minim": "Governing check",
  "struct.elp.verificarile": "Its checks",
  "struct.elp.picate": "{n} failed",
  "struct.elp.cantitati": "Quantities",
  "struct.elp.ver_titlu": "The element's checks",
  "struct.elp.ver_sub": "click a check → the full detail in {pas}",
  "struct.elp.col_verificarea": "Check",
  "struct.elp.col_punct_grinda": "Point along the beam",
  "struct.elp.col_punct": "Point",
  "struct.elp.col_valoare": "Demand (E)",
  "struct.elp.col_capabil": "Resistance (R)",
  "struct.elp.col_sursa": "Source",
  "struct.elp.col_efort": "Demand → resistance",
  "struct.elp.rand_titlu": "opens the detail here: the formula, the clause, the values from the calculation, the fixes",
  "struct.elp.marja_titlu": "accepted within the practice margin: R/E below 1, but within {n}%",
  "struct.elp.marja": "margin {n}%",
  "struct.elp.nota_tabel": "click a row = its detail here (the formula, the clause, the values from the calculation, the fixes); the link inside the detail goes to the step's page, for the full drawings and tables",
  "struct.de_hotarat.titlu": "For the designer to decide",
  "struct.de_hotarat.sub": "not checks: they neither pass nor fail, and the element's verdict does not depend on them",
  "struct.elp.infasuratoare": "The envelope along the axis",
  "struct.elp.vedere_cotata": "The dimensioned view — {ce}",
  "struct.elp.toate_nivelurile": "{stem}, every storey",
  "struct.elp.doar_nivelul": "only the element's storey",
  "struct.elp.doar": "Only {ref}",
  "struct.elp.sectiunea": "The dimensioned section",
  "struct.elp.contextul": "Its context in the building",
  "struct.elp.istoric": "Its history (step 19)",
  "struct.elp.istoric_rand": "{de_ce}: {de_la} → {la} ",
  "struct.elp.istoric_re": "(R/E {n})",

  // --- STRUCTURĂ · pagina elementului: verificarea care guvernează, membrii
  //     claselor, fundația axului, desenele lamelei -------------------------
  "struct.elp.guverneaza_titlu": "the check with the smallest R/E reserve on the element: it decides the verdict",
  "struct.membri.col_element": "Element",
  "struct.fel.lamela": "The wall pier",
  "struct.tca.fara_combinatii": "The axis has no combinations with a computed pressure.",
  "struct.cfa.titlu": "The foundation (step 17)",
  "struct.cfa.sub": "the axis footing, the checked combinations and how the pressures are read",
  "struct.cfa.valori": "L = {l} m · B = {b} m · D_f = {d} m · A = {a} m² · W_long = {wl} m³ · W_transv = {wt} m³",
  "struct.cfa.kexc": " · k_exc = {k}",
  "struct.cfa.stalpi": " · {n} columns",
  "struct.lam.sect_titlu": "Section {id} — {t}×{L} cm",
  "struct.lam.leg_capete": "end cages: 2 × {n}Ø{phi} over {l} cm",
  "struct.lam.leg_etrieri": "end stirrups Ø{phi}/{pas} cm, hoop {a}×{b} cm, confined {lconf} cm",
  "struct.lam.leg_inima": "web: {n}Ø{phi}/{pas} mm vertical, on both faces",
  "struct.lam.leg_oriz": "horizontal Ø{phi}/{pas} mm · ties Ø{phia}, {na}/m²",
  "struct.lam.vedere_titlu": "Elevation of stack {stack} — L = {L} m, t = {t} cm",
  "struct.lam.s4_explicatie": "Storey {el} ({zona}): the dimensioned section with the end cages and the web, the stack elevation with the A/B zones and l_cr, the bars with their marks and the P–M–M «onion» of the adopted section.",
  "struct.lam.th_rol": "Role",
  "struct.lam.th_bare": "Bars",
  "struct.lam.th_unde": "Details",
  "struct.lam.th_marca": "Mark",
  "struct.lam.rol_capete": "end cages",
  "struct.lam.rol_etrieri": "end stirrups",
  "struct.lam.rol_vertical": "web, vertical",
  "struct.lam.rol_orizontal": "web, horizontal",
  "struct.lam.rol_agrafe": "ties",
  "struct.lam.rol_agrafe_conf": "confinement cross-ties",
  "struct.lam.unde_capete": "over {l} cm at each end, ρ = {rho} %",
  "struct.lam.unde_etrieri": "hoop {a} × {b} cm, {n} per end, confined length {lconf} cm",
  "struct.lam.unde_inima": "ρ = {rho} %",
  "struct.lam.unde_agrafe": "{n} per m²",
  "struct.lam.unde_agrafe_conf": "{k} per stirrup set, one at each bar row inside the confined length",

  // --- STRUCTURĂ · registrul de soluții ----------------------------------

  // --- STRUCTURĂ · fișa unei soluții -------------------------------------
  "struct.fs.stare_aplicata": "applied",
  "struct.fs.stare_acoperita": "superseded by another change",
  "struct.fs.stare_epuizata": "catalogue exhausted — needs a human decision",
  "struct.fs.stare_refuzata": "refused — frozen by the layout",
  "struct.fs.titlu": "The solution's sheet",
  "struct.fs.gol": "The sheet is written at step 19 — run the chain to the end.",
  "struct.fs.solutii": "Solutions",
  "struct.fs.fisa_nume": "Sheet — {nume}",
  "struct.fs.din_solutia": "from solution {solutie}",
  "struct.fs.rezolutie": " · resolution h = {n} m",
  "struct.fs.durata": " · {n} min",
  "struct.fs.picate": "Failed",
  "struct.fs.din_total": " of {n}",
  "struct.fs.cost": "Structure cost",
  "struct.fs.fata_de_plecare": " · {semn}{suma} € against the starting point",
  "struct.fs.scor": "Score",
  "struct.fs.verdict": "Verdict",
  "struct.fs.picatele": "its failures →",
  "struct.fs.raport": "the full report (S17) →",
  "struct.fs.modificat": "What actually changed",
  "struct.fs.op_uri": "the operations, in plain words",
  "struct.fs.aplicate": "{n} applied",
  "struct.fs.acoperite": "{n} superseded",
  "struct.fs.epuizate": "{n} catalogue exhausted",
  "struct.fs.refuzate": "{n} refused",
  "struct.fs.col_verificarea": "The check that asked for it",
  "struct.fs.col_lipsea": "How much was missing",
  "struct.fs.col_abordarea": "Approach",
  "struct.fs.col_schimbat": "What changed",
  "struct.fs.col_stare": "State",
  "struct.fs.abordare_titlu": "which lever in the catalogue of this check type",
  "struct.fs.abordare": "{n} of {total}",
  "struct.fs.promis_livrat": "Promised vs delivered",
  "struct.fs.promis_sub": "severity before → predicted; the solver's self-correction mechanism",
  "struct.fs.col_parghia": "Lever",
  "struct.fs.col_promis": "Promised → delivered",
  "struct.fs.demotata_titlu": "it delivered under a quarter of its promise twice on the same class — it is no longer tried on this project",
  "struct.fs.demotata": "demoted",
  "struct.fs.amanate": "Deferred · {n}",
  "struct.fs.netratate": "Untreated · {n}",
  "struct.fs.de_ce_oprit": "Why it stopped here",

  // --- STRUCTURE · Step 7, the FEM model ---------------------------------
  "struct.fem.ruleaza": "The step is running…",
  "struct.fem.ruleaza_sub": "The FEM model is being built (T beams with b_eff to EC2, cracked stiffnesses, rigid diaphragms carrying the seismic masses) and the modal check is being run in OpenSees.",
  "struct.fem.nerulat": "The step has not been run yet. Press «Run» in the left-hand menu (after Step 4).",
  "struct.fem.schita_titlu": "Building the 3D model…",
  "struct.fem.schita_sub": "From the geometry of Step 4, with no analysis at all. On a large building it takes a few tens of seconds; it is done once and it stays.",
  "struct.fem.esec": "The last run failed: {motiv}",
  "struct.fem.eroare_necunoscuta": "unknown error",
  "struct.fem.reruleaza": "Run it again from the left-hand menu.",
  "struct.fem.t1": "Fundamental period",
  "struct.fem.t1_titlu": "The fundamental period T₁",
  "struct.fem.t1_ce": "Push the building and let go, and it sways. T₁ is how long one full sway takes, along the direction in which the building is softest.",
  "struct.fem.t1_dece": "The period decides how strongly the building «feels» the earthquake (it is read off the response spectrum at T₁). It is computed from the model with cracked stiffnesses — cracked concrete is softer, so T is longer, and more realistic.",
  "struct.fem.t1_ei": "the cracked stiffnesses (beams / columns)",
  "struct.fem.t1_ei_sursa": "P100-1; constants of the method",
  "struct.fem.t1_m": "seismic mass per storey (D + SDL + ψE·Imposed)",
  "struct.fem.t1_m_sursa": "computed in this step, lumped at the centre of mass",
  "struct.fem.t1_sub": "f₁ = {f} Hz · mode {mod} of {total}",
  "struct.fem.t1_sub_local": " (the earlier modes are local, with negligible mass)",
  "struct.fem.modal_nerulat": "the modal check did not run",
  "struct.fem.masa": "Total seismic mass",
  "struct.fem.masa_titlu": "The seismic mass — what sways in an earthquake",
  "struct.fem.masa_ce": "The formula: M = (D + SDL + ψE·Imposed) / g. D is the self-weight of the concrete (slab + beams + columns), SDL is the finishes and partitions, and of the imposed load (people, furniture) only the fraction ψE counts — the rest is probably not there during the earthquake.",
  "struct.fem.masa_dece": "Seismic force = mass × acceleration, so every tonne counts directly. We divide the weight (kN) by the gravitational acceleration g = 9.81 m/s² to get the mass (tonnes). ψE ≈ 0.24 says that, statistically, only a quarter of the nominal imposed load is actually in the building at any one time.",
  "struct.fem.masa_d": "the self-weight of the concrete structure",
  "struct.fem.masa_d_sursa": "computed from the geometry of Step 4 (25 kN/m³) and the heights from Step 2",
  "struct.fem.masa_sdl": "the weight of the finishes per square metre",
  "struct.fem.masa_sdl_sursa": "Building data → finishes",
  "struct.fem.masa_psi": "the fraction of the imposed load present during the earthquake",
  "struct.fem.masa_psi_sursa": "a constant of the method (P100-1/CR0: φ·ψ2 ≈ 0.24 residential)",
  "struct.fem.masa_g": "the gravitational acceleration (9.81 m/s²)",
  "struct.fem.masa_g_sursa": "a physical constant",
  "struct.fem.masa_sub": "{kn} kN = D + SDL + ψE·Imposed (ψE = {psi})",
  "struct.fem.varianta": "FEM model — {varianta} variant",

  "struct.fem.sub_shell": "shells (mesh {mesh} m) in {ochiuri} slab panels{fasii} + frame elements · {noduri} nodes · flexible slab, distributed mass",
  "struct.fem.sub_fasii": " + {n} basement wall strips",
  "struct.fem.sub_lean": "frame elements · {noduri} nodes ({puncte} subdivision points) · {diafragme} rigid diaphragms",
  "struct.fem.acoperire": "How much of the building is slab",
  "struct.fem.acoperire_titlu": "Slab coverage",
  "struct.fem.acoperire_ce": "The total area of the floor polygons, against the built area of the storeys (the footprints from Step 2) and against the area enclosed by the perimeter columns.",
  "struct.fem.acoperire_dece": "Every gravity load and the whole seismic mass is applied over the shell area: what is not slab has no weight, no mass and no imposed load. The old check («mesh coverage») compared the mesh with the slab polygon, so it read ~100 % even when the polygon covered 28 % of the storey (project 2).",
  "struct.fem.acoperire_amprenta": "footprint",
  "struct.fem.acoperire_amprenta_nume": "the built area of the storeys",
  "struct.fem.acoperire_amprenta_sursa": "Step 2 — the closed outline of the walls + the openings + the cantilever slabs",
  "struct.fem.acoperire_stalpi": "between columns",
  "struct.fem.acoperire_stalpi_nume": "the convex hull of each storey's columns",
  "struct.fem.acoperire_stalpi_sursa": "Step 4, through the model geometry",
  "struct.fem.acoperire_sub": "{placa} m² of slab out of {construit} m² built (Step 2) · {hull} % of the area between columns · mesh {mesh} %",
  "struct.fem.intrari": "Input data",
  "struct.fem.intrari_titlu": "Where each input comes from",
  "struct.fem.intrari_ce": "The analysis model combines data from the earlier steps and from the project settings — each with its own source, so that everything stays consistent along the whole chain.",
  "struct.fem.intrari_geom": "the columns, the beams and their positions",
  "struct.fem.intrari_geom_sursa": "Step 4 (the structural model, on the persistent walls from Step 2)",
  "struct.fem.intrari_h": "the height of each storey (column length, floor levels)",
  "struct.fem.intrari_h_sursa": "Step 2 — the storey levels read off the elevations, through Step 4",
  "struct.fem.intrari_beton": "the concrete class and the modulus of elasticity (how «stiff» the material is)",
  "struct.fem.intrari_beton_sursa": "Building data (EN 1992-1-1, the table of classes)",
  "struct.fem.intrari_finisaj": "the weight of the finishes per square metre",
  "struct.fem.intrari_finisaj_sursa": "Building data",
  "struct.fem.intrari_dest": "the nominal imposed load (people, furniture)",
  "struct.fem.intrari_dest_sursa": "Building data (SR EN 1991-1-1)",
  "struct.fem.intrari_placa": "the thickness of the concrete floor slab",
  "struct.fem.intrari_placa_sursa": "Step 7 — chosen by the solver",
  "struct.fem.intrari_sub": "E = {e} GPa · finishes {finisaj} ({sdl} kN/m²) · {destinatie} ({q} kN/m²) · slab {placa} cm",
  "struct.fem.intrari_etaje": " · storeys {lista} m (Step 2)",
  "struct.fem.scena": "The 3D view of the model",
  "struct.fem.scena_sub": "drag to rotate, scroll to zoom · click a beam, a column or a slab panel for its properties",
  "struct.fem.leg_stalpi": "columns",
  "struct.fem.leg_grinzi_inima": "beams (web under the slab)",
  "struct.fem.leg_grinzi_t": "T beams",
  "struct.fem.leg_brate": "rigid arms (from the column node to the beam's real end; dashed line)",
  "struct.fem.leg_placa_mesh": "slab (ShellMITC4 mesh)",
  "struct.fem.leg_placa_diafragma": "slab (rigid diaphragm)",
  "struct.fem.leg_noduri": "FEM points",
  "struct.fem.leg_pereti_beton": "concrete walls (basement + the balcony sides the architecture asks for)",
  "struct.fem.leg_balcoane": "balconies and terraces (concrete)",
  "struct.fem.leg_logii": "loggias (they do not project beyond the footprint)",
  "struct.fem.leg_reazem": "column bearing on a beam",
  "struct.fem.leg_cm": "centre of mass / diaphragm master",

  "struct.fem.schita_nota": "the model is built from the geometry of Step 4, not analysed: the periods, the participating masses and the modal check come from running Step 7",
  "struct.fem.rulat_la": "run at {data}",
  "struct.fem.subsol": "Rigid basement (the rigid box)",
  "struct.fem.subsol_titlu": "The rigid basement",
  "struct.fem.subsol_ce": "In buildings of three storeys or more with a basement, reinforced concrete walls (~20 cm, flush with the outer face of the column) are cast between the basement columns that walls connect. The substructure becomes a «rigid box», and the superstructure may be taken as fixed at the face of the slab over the basement.",
  "struct.fem.subsol_dece": "P100-1 §4.4.1.7 and C5.8 recommend a rigid-box substructure; the engineer's rule of practice is that the lateral stiffness of the basement should be at least 50 % above that of any storey above ground (the ratio below, on both directions). The columns keep the same section in the basement — the stiffness comes from the walls.",
  "struct.fem.subsol_pereti": "{n} concrete walls",
  "struct.fem.subsol_grosime": " {cm} cm thick",
  "struct.fem.subsol_raport": "k_basement / max(k storeys) = {x}× on X, {y}× on Y",
  "struct.fem.subsol_prag_ok": "≥ {prag}×",
  "struct.fem.subsol_prag_nu": "— the threshold is {prag}×",
  "struct.fem.subsol_nivel": "storey",
  "struct.fem.subsol_nota": "Stiffness per storey: the columns with 12·E·I/h³ (the tensor rotated onto the long side), the concrete walls with 1/(h³/12EI + 1.2·h/GA) along their own direction — gross E throughout, since what matters is the ratio between storeys.",
  "struct.fem.dir_torsiune": "torsion",
  "struct.fem.dir_mixt": "mixed",
  "struct.fem.forme": "Mode shapes",
  "struct.fem.forme_se_aduc": "Fetching the mode shapes…",
  "struct.fem.forme_lipsa": "The current result carries no mode shapes — run Step 7 again (older results did not save them).",
  "struct.fem.moduri": "Modes of vibration",
  "struct.fem.forma_titlu": "The mode shape",
  "struct.fem.forma_ce": "How the building sways in each of its own modes: the deflected shape is animated and exaggerated (the real amplitude depends on the earthquake, not on the mode). The colour shows how much each zone moves (blue = little, red = much).",
  "struct.fem.forma_dece": "The low modes (long T) dominate the seismic response: the first two are usually translations along X/Y, the third torsion. The shapes are reconstructed exactly from the static condensation — the static deflection under the mode's inertia forces.",
  "struct.fem.tab_mod": "Mode {mod} · {t}s · {tip}",
  "struct.fem.mod_sub": "T = {t} s · f = {f} Hz · participating mass: {x}% X, {y}% Y, {rz}% torsion",
  "struct.fem.mod_puritate": " · purity {p}",
  "struct.fem.mase_titlu": "The masses each mode moves",
  "struct.fem.mase_total": "total seismic mass {t} t",
  "struct.fem.mase_criteriu": "the criterion of ≥ 90 % cumulative participating mass, on each direction (P100-1)",
  "struct.fem.mase_cumulat": "cumulative {x} X · {y} Y",
  "struct.fem.col_mod": "Mode",
  "struct.fem.col_masa_x": "mass X",
  "struct.fem.col_masa_y": "mass Y",
  "struct.fem.col_torsiune": "torsion",
  "struct.fem.col_cum_x": "cumulative X",
  "struct.fem.col_cum_y": "cumulative Y",

  "struct.fem.lay.stalpi": "columns",
  "struct.fem.lay.grinzi": "beams",
  "struct.fem.lay.placa": "slab (panels)",
  "struct.fem.lay.armatura": "reinforcement",
  "struct.fem.lay.armatura_n": "reinforcement ({n} elements)",
  "struct.fem.lay.armatura_nerulat": "reinforcement (the steps have not run)",
  "struct.fem.lay.mesh": "FEM mesh (slab)",
  "struct.fem.lay.noduri": "FEM nodes (notation)",
  "struct.fem.lay.cm": "centre of mass",
  "struct.fem.lay.pereti": "non-structural walls",
  "struct.fem.lay.pereti_beton": "concrete walls",
  "struct.fem.lay.zidarie_confinata": "confined masonry",
  "struct.fem.zc_titlu": "Confined masonry wall {id}",
  "struct.fem.zc_sursa": "setback storey treated as an appendage",
  "struct.fem.zc_material": "confined masonry (ZC)",
  "struct.fem.zc_material_sub": "clay blocks with mortar, with reinforced concrete tie-columns and a ring beam",
  "struct.fem.zc_capete_sub": "the concrete columns at the ends; «—» is a wall end with its own tie-column",
  "struct.fem.zc_sect_sub": "wall thickness from the architecture × height below the beam or slab above",
  "struct.fem.zc_info_titlu": "Why the masonry is structure here",
  "struct.fem.zc_info_ce": "The setback storey covers at most 20 % of the floor below and is no taller than it: it is an appendage (P100-1 §8.3.2.2(5)), not a storey. Its walls of at least 24 cm are confined masonry walls on the beams of the floor below, and the columns coming from below continue as 30 × 30.",
  "struct.fem.zc_info_dece": "The whip effect no longer applies to it. Instead its walls carry twice its share of the building's seismic force (eq. 8.2), and the model holds them as masonry shells with half the elastic modulus (cracked masonry, §8.4.1(4)).",
  "struct.fem.zc_greutate": "weight",
  "struct.fem.zc_greutate_sub": "masonry with its plaster, per metre of wall — it rests on the beams below",
  "struct.fem.lay.balcoane": "balconies",
  "struct.fem.lay.fundatie": "foundation (concrete)",
  "struct.fem.lay.fundatie_afara": "foundation (concrete, not in the analysis)",
  "struct.fem.lay.placa_sol": "slab on ground (it carries no structure)",
  "struct.fem.lay.radier": "raft (concrete)",
  "struct.fem.lay.ferestre": "windows ({n})",
  "struct.fem.lay.usi": "doors ({n})",
  "struct.fem.lay.parapete": "parapets ({n})",
  "struct.fem.lay.nuclee": "stairs and lifts ({n})",
  "struct.fem.leg_scara": "stair core (from the architecture, display only)",
  "struct.fem.leg_lift": "lift shaft (from the architecture, display only)",
  "struct.fem.lay.cote": "dimensions",
  "struct.fem.lay.teren": "ground and excavation",
  "struct.fem.psol_titlu": "Slab on ground{nivel}: {arie} m² × {grosime} cm{clasa}{balast}. It is cast (it is in the cost estimate), but it is NOT an element of the analysis model: it bears directly on the ground. That is why it is grey, like the masonry. Its top face is at level 0; between it and the top of the foundation lie the ballast and the fill, which are not drawn.",
  "struct.fem.psol_nivel": ", the floor of storey «{nivel}»",
  "struct.fem.psol_clasa": ", {clasa}",
  "struct.fem.psol_balast": ", on {cm} cm of ballast",
  "struct.fem.rad_lay_titlu": "A {cm} cm raft over the whole footprint ({arie} m², {n} rafts): the thick slab the building stands on, from the lowest floor downwards, on ballast. Only the concrete is drawn, at its real levels; the ground springs are figures, not symbols.",
  "struct.fem.rad_titlu": "Raft",
  "struct.fem.rad_sub": "{arie} m² × {cm} cm",
  "struct.fem.rad_id_corp": "RAFT · {corp}",
  "struct.fem.rad_sursa": "raft · the foundation of the whole footprint",
  "struct.fem.rad_modelare": "grillage of strips on a bed of springs",
  "struct.fem.rad_modelare_sub": "in the analysis model the raft is a grillage of slab strips on ground springs; here the whole slab is shown, as it is poured",
  "struct.fem.fnd_titlu": "{detaliu}{cota}. Only the CONCRETE is drawn, at its real levels: the base, the stem, and the length of column that descends into the stem. The earth fill is not drawn. The ground springs are figures in the axis panel, not symbols over the building.",
  "struct.fem.fnd_sectiune": "the section from Step 6",
  "struct.fem.fnd_cota": " · founding level −{m} m below the floor",

  "struct.fem.mai_multe_straturi": "More layers",
  "struct.fem.lay_toate": "all",
  "struct.fem.lay_toate_titlu": "turn every layer on",
  "struct.fem.lay_nimic": "none",
  "struct.fem.lay_nimic_titlu": "turn every layer off",
  "struct.fem.lay_doar": "only",
  "struct.fem.lay_doar_titlu": "this layer only: it turns the others off",
  "struct.fem.help_noduri": "NOTATION, not matter: the points at which the analysis model is subdivided — the ends of the members, the subdivision nodes of the beams and the base nodes (the largest, filled, at level 0). Displacements and reactions are read there.",
  "struct.fem.help_cote": "The dimensions that can be read at a glance, and only those: the outer sides of the footprint, the height of each storey as a chain on one corner (with the total H beside it) and the storey levels — ±0.00, the natural ground and the founding level. Bays, sections and thicknesses are NOT here: they are in the plans and sections of their own steps, and on the model they would be noise. Oblique ticks and dotted witness lines, as on a drawing.",
  "struct.fem.help_stalpi": "The columns and blades, as volumes. They can be turned off to see what is under them — the foundation, the excavation, the dimensions.",
  "struct.fem.help_grinzi": "The beams: the WEB under the slab, not the full depth of the T (the slab is drawn separately, with its own volume).",
  "struct.fem.baza_incastrata": "fixed base (the foundation is not in the model)",
  "struct.fem.baza_motiv": "the model has a perfectly fixed base: the nodes at level 0 can neither translate nor rotate",
  "struct.fem.baza_beton": ". The foundation concrete can still be seen: tick the «foundation (concrete, not in the analysis)» layer — it is the geometry of Steps 5–6, drawn at its real levels, with no springs and no analysis elements",
  "struct.fem.clasa": "Class",
  "struct.fem.clasa_titlu": "The execution class",
  "struct.fem.clasa_ce": "Identical elements — same section, same shape, same reinforcement and same links — form a class. It is formed up once and bent in series.",
  "struct.fem.clasa_dece": "The fewer classes a structure has, the easier it is to build and to check on site, and the solution's score counts them: a quarter of it measures how many different element types it has.",
  "struct.fem.simetrie": "Symmetry group",
  "struct.fem.simetrie_titlu": "The group with symmetry potential",
  "struct.fem.simetrie_ce": "Columns mirrored about the building's axes and those on the same facade at equal spacing carry the same architecture above them — so they should be the same column. The group says which pieces this one should be identical to and how many of them ended up in the same execution class.",
  "struct.fem.simetrie_dece": "A respected group (at least four in five identical) takes ten per cent off the quality part of the score: the search prefers the solution with identical elements on the contour, not the long wall-column on the side with the long wall.",
  "struct.fem.simetrie_respectata": "respected — bonus in the score",
  "struct.fem.simetrie_nerespectata": "not respected — the members are not in one class",
  "struct.simetrie.identici": "{grup} · {k} of {n} identical",
  "struct.simetrie.nemasurat": "{grup} · {n} columns, not measured yet",

  "struct.fem.gol_grinda": "beam",
  "struct.fem.gol_stalp": "column",
  "struct.fem.gol_ochi": "slab panel",
  "struct.fem.gol_click": "Click a {grinda} or a {stalp} for its analysis properties.",
  "struct.fem.gol_click_ochi": "Click a {ochi} for its id, its spans, its thickness and how its outline closes.",
  "struct.fem.p_ochi": "Slab panel {id}",
  "struct.fem.p_nivel": "storey {n}",
  "struct.fem.p_inchis_grinzi": "{n}% beams",
  "struct.fem.p_inchis_pereti": "{n}% walls",
  "struct.fem.p_inchis_contur": "{n}% building edge",
  "struct.fem.p_modelare": "Modelling",
  "struct.fem.p_modelare_sub": "plate finite elements on an ElasticMembranePlateSection, {cm} cm thick",
  "struct.fem.p_shell_titlu": "Shell, not membrane",
  "struct.fem.p_shell_ce": "Each element is a ShellMITC4 on an ElasticMembranePlateSection: it has BOTH membrane stiffness (in the plane of the slab) AND bending stiffness. The MITC4 formulation avoids shear locking in thin plates.",
  "struct.fem.p_shell_dece": "The engineer's requirement is shells everywhere. A membrane or a rigid diaphragm would carry only the in-plane forces: the slab would not take the gravity load to the beams, and the moments it is reinforced for would never appear.",
  "struct.fem.p_deschideri": "Spans",
  "struct.fem.p_deschideri_sub": "the bounding size on the building's dominant axes; side ratio {raport} — {fel}",
  "struct.fem.p_doua_directii": "two-way slab",
  "struct.fem.p_o_directie": "works essentially one way",
  "struct.fem.p_deschidere_mica": "Short span",
  "struct.fem.p_deschidere_mica_sub": "the diameter of the largest circle inscribed in the panel — the span that governs bending, and one that is still defined for an L-shaped panel, where the bounding box lies",
  "struct.fem.p_suprafata": "Area",
  "struct.fem.p_suprafata_sub": "self-weight, finishes, imposed load and the panel's share of the seismic mass all come out of it; the mesh assigns it {mesh} m²",
  "struct.fem.p_grosime": "Thickness",
  "struct.fem.p_grosime_sub": "the slab thickness chosen by the solver — the same across the whole floor",
  "struct.fem.p_contur": "How the outline closes",
  "struct.fem.p_contur_sub": "how much of the panel's perimeter sits on a beam, on an architectural wall, or on the edge of the building",
  "struct.fem.p_contur_titlu": "A panel does not need beams all round",
  "struct.fem.p_contur_ce": "The panels come from cutting the storey footprint with the beam axes AND with the wall axes from Step 2. A wall broken by a door or a window does not separate, so the two rooms stay one panel.",
  "struct.fem.p_contur_dece": "Classic polygonisation demands PERFECTLY closed rings: on project 2, at ground floor, the beam grid closed 82 m² out of 295 built, and the rest of the slab stayed a single field. The rule is Stefan's: «where the panel is not perfectly closed by beams we allow it to be closed by masonry walls, doors and windows too».",
  "struct.fem.p_categorie": "Use category",
  "struct.fem.p_cat_c": "C — circulation / common areas",
  "struct.fem.p_cat_a": "A — residential",
  "struct.fem.p_cat_sub": "from the use zones of Step 2; it decides the imposed load{detaliu}",
  "struct.fem.p_cat_detaliu": " ({m2} m² of the panel are cat. C)",
  "struct.fem.p_rigiditate": "Stiffness",
  "struct.fem.p_rig_fisurat": "the concrete E scaled by rigiditate_analiza_placa = {fis} (isotropic — it affects the membrane too)",
  "struct.fem.p_rig_nefisurat": "uncracked slab (rigiditate_analiza_placa = 1.0), the usual simplification for the modal model",

  "struct.fem.ps_titlu": "Slab on ground · {ref}",
  "struct.fem.ps_sub": "{arie} m² · {cm} cm{clasa} · it carries no structure",
  "struct.fem.ps_clasa": " · {clasa}",
  "struct.fem.ps_sursa": "slab on ground",
  "struct.fem.ps_cote": "Levels",
  "struct.fem.ps_cote_sub": "the top face is the floor of the storey below{balast}",
  "struct.fem.ps_balast": "; beneath it, {cm} cm of ballast, drawn",
  "struct.fem.ps_modelare": "How it is modelled",
  "struct.fem.ps_rezemata": "bearing on the ground",
  "struct.fem.ps_rezemata_sub": "an elastic bed over its whole area, not spans between supports",
  "struct.fem.ps_info_titlu": "A slab ON GROUND, not a floor slab",
  "struct.fem.ps_info_ce": "It bears on the ground over its whole area, so it has no panels and is not designed for span moments. It does not enter the seismic model: its mass goes straight into the ground.",
  "struct.fem.ps_info_dece": "Its reinforcement is still computed, from what bends it: restrained shrinkage (EC2 §7.3.2), the walls that do not land on a foundation axis and discharge onto it (slab on a Winkler medium, M = q/(4λ)) and, on a garage level, a car wheel (Westergaard, taken at the joint). The figures are in Step 18.",
  "struct.fem.detalii": "Details",
  "struct.fem.link_element": "the element page for {ref} →",
  "struct.fem.link_armare": "Step 18 — its reinforcement →",
  "struct.fem.link_sub": "that is where the E/R checks, the drawn section, the bar schedule and the price are",

  "struct.fem.gf_titlu": "Foundation beam · {ax}",
  "struct.fem.gf_sub": "{n} runs · L = {l} m{b}{afara}",
  "struct.fem.gf_b": " · B = {b} m",
  "struct.fem.gf_afara": " · not in the analysis model",
  "struct.fem.gf_sursa": "foundation beam · {n} runs",
  "struct.fem.gf_sectiune": "The section as built",
  "struct.fem.gf_sectiune_sub": "detail «{detaliu}» from Step 6 — the stiffness in the model, the weight bearing on the ground AND the cost all come out of it",
  "struct.fem.gf_altfel_titlu": "A foundation beam is not a superstructure beam",
  "struct.fem.gf_altfel_ce": "The section does not act together with the slab (it is not a T with b_eff), it sits on a continuous bed of springs instead of on nodes, and the ground cannot take TENSION: the base can lift off.",
  "struct.fem.gf_altfel_dece": "NP 112-2014 §III and annex K: substructures of foundation beams are modelled as beams on a Winkler elastic medium. The base thickness is not free — the cantilever requires h_t ≥ (B − b_web)/2 and ≥ 0.30·B, so a widened axis is also far stiffer.",
  "struct.fem.gf_cote": "top face −{sus} m · founding −{df} m",
  "struct.fem.gf_cote_sub": "measured from level 0 = the floor of the storey below, not from the ground (D_f from Step 6). The {cm} cm above the top face hold the slab on ground and the floor build-up — fill between the axes, and under the columns the column itself, descending into the stem",
  "struct.fem.gf_bara_titlu": "In the model the member sits at level 0, not at the level of the concrete",
  "struct.fem.gf_bara_ce": "The drawn concrete hangs between the two levels above, but the analysis element is a member BETWEEN THE BASE NODES of the columns, at level 0, with ZERO vertical offset (`offset_i = offset_j = [0,0,0]`). There is no «gap» in the model: the column's base node AND the end of the foundation beam are THE SAME node. The member axes are not drawn — at level 0 they run through the concrete, so they would not be visible.",
  "struct.fem.gf_bara_dece": "The idealisation neglects the eccentricity between the member axis (level 0) and the centroid of the concrete section (some 0.8 m lower) — just as superstructure beams have their axis in the plane of the slab rather than at the centroid of the T section. The consequence: the moment in the foundation beam does not carry the lever-arm effect about the column face. The slab on ground is not an analysis element anywhere: it appears in the cost estimate and in the load case (the ground-floor walls that do not land on an axis discharge through it straight into the ground).",
  "struct.fem.gf_armatura": "Reinforcement",
  "struct.fem.gf_armatura_link": "Step 18 — reinforcing the foundation beam on {ax} →",
  "struct.fem.gf_armatura_sub": "the longitudinal bars, the links and the base mesh are chosen there, on the section above; they are not drawn in 3D (only what is physical AND legible goes in there)",
  "struct.fem.gf_bare3d_titlu": "Why the bars are not drawn in 3D",
  "struct.fem.gf_bare3d_ce": "The convention of the 3D view is that only what is PHYSICAL and can be seen is drawn. The bars of a foundation beam are buried in concrete, 1–2 m below level 0, in a member tens of metres long: at the scale at which the building is visible they would be nothing but noise.",
  "struct.fem.gf_bare3d_dece": "The reinforced section, dimensioned, is on the Step 18 page — there the scale is the section's, not the building's, so the bars can actually be read.",
  "struct.fem.gf_nefisurat_titlu": "Why uncracked, when the beams above are at 0.5·EI",
  "struct.fem.gf_nefisurat_ce": "In foundation beams plastic deformation is EXPLICITLY avoided, so the full section is used (cracking = 1.0), not the cracked one from P100-1.",
  "struct.fem.gf_nefisurat_dece": "NP 112-2014 §II.7.5.2. The stiffness of the foundation beam decides how the ground pressures redistribute — cracking assumed for no reason would push the pressures towards the columns.",
  "struct.fem.gf_ei": "E·I_y = {ei} MN·m² in vertical bending",
  "struct.fem.gf_resoarte": "The bed of springs",
  "struct.fem.gf_resoarte_v": "{n} springs · k_v = {min}…{max} kN/m",
  "struct.fem.gf_resoarte_sub": "K_s = {ks} daN/cm³ ({sursa}, Step 6) · spacing {pas} m · k_h = {raport}·k_v",
  "struct.fem.gf_resort_titlu": "What a spring looks like in OpenSees",
  "struct.fem.gf_resort_ce": "Under each node of the grid sits a FIXED ground node, and between them a `zeroLength` element — of zero length, at the node at level 0 — with k_v = K_s·A_tributary on direction 3 and k_h on directions 1 and 2. The rotations stay FREE: the rotational stiffness of the base comes from the neighbouring springs, and a rotational spring would double it. The spring symbol in the scene is drawn under the base, where the soil it replaces is.",
  "struct.fem.gf_resort_dece": "The spring spacing is capped at π/4 of the elastic length λ⁻¹ = {lambda} m (NP 112 annex K.3.2) — below that, the discrete carpet of springs behaves like a continuous one. ",
  "struct.fem.gf_ent": "The springs are TENSION-FREE (ENT): the model is non-linear, so the combinations are no longer strictly superposable.",
  "struct.fem.gf_liniar": "The springs are linear, so that the superposition of effects from Step 11 (R = C·F) stays exact; lift-off is read off the sign of the reactions anyway.",
  "struct.fem.gf_greutate": "Self-weight",
  "struct.fem.gf_greutate_sub": "concrete + the earth on the shoulders of the base; it enters gravity case G4. The substructure mass ({t} t, {pct} % of the total) {verb} the seismic mass",
  "struct.fem.gf_intra": "IS INCLUDED IN",
  "struct.fem.gf_nu_intra": "is NOT included in",

  "struct.fem.gf_masa_titlu": "Why the foundation mass is not seismic mass",
  "struct.fem.gf_masa_ce": "EN 1998-1 §4.3.3.2.2(1)P defines the seismic mass as the mass of the building ABOVE the foundation. The weight of the foundation beams stays in full in case G4 — the reactions, the ground pressures and the cost estimate all see it.",
  "struct.fem.gf_masa_dece": "Mass that stays at ground level produces no inertia forces for the structure to carry, and its sliding modes on k_h (~17 Hz) were diluting the 90 % criterion of Step 9.",
  "struct.fem.zid_titlu": "Non-structural wall {id}",
  "struct.fem.zid_sub": "carries no stiffness",
  "struct.fem.zid_sursa": "the simplified wall from Step 2",
  "struct.fem.sectiune": "Section",
  "struct.fem.brate": "Arms",
  "struct.fem.brate_v": "{n} × {h} cm on {axa}",
  "struct.fem.brate_sub": "the leg on the other axis, cast with the column (T, L or +); the FEM sees the inertia of the whole polygon",
  "struct.fem.zid_grosime": "{cm} cm thick",
  "struct.fem.zid_grosime_sub": "the thickness of the simplified wall, as Step 2 measured it",
  "struct.fem.lungime": "Length",
  "struct.fem.rol": "Role in the model",
  "struct.fem.zid_rol": "a load, not an element",
  "struct.fem.zid_rol_sub": "its weight goes onto the beam beneath it (G2a) or, where there is no beam, uniformly onto the slab (G2b); the stiffness of the masonry is neglected",
  "struct.fem.zid_info_titlu": "Why do non-structural walls appear in 3D?",
  "struct.fem.zid_info_ce": "These are the simplified walls of Step 2 — the rectangles the architect's wall layers were distilled into. The id (S7, E14…) is the same as in Step 2, so the wall can be looked up there.",
  "struct.fem.zid_info_dece": "The grey layer shows EXACTLY the simplified walls, not the raw band on the wall layers: otherwise a wall would appear where no simplified wall exists at all (the halo of the lines, the outline of the hatches, the cut of the area of interest). The lengths cast in concrete are taken out of here — a stretch of wall is either concrete or non-structural. The layer is named after the ROLE, not the material: what stays here carries no load, whatever it is made of.",
  "struct.fem.zid_material_declarat": "declared by the architecture",
  "struct.fem.zid_material_presupus": "assumed — the drawing declares no material anywhere",
  "struct.fem.zid_material_titlu": "How is the material known?",
  "struct.fem.zid_material_ce": "From what the architecture declares for this wall: «concrete», «masonry» or «polystyrene» (the insulation layer drawn as a wall). Documents produced by the architecture solver declare it; a DWG drawing writes the material nowhere, and then what you read here is the assumed masonry.",
  "struct.fem.zid_material_dece": "The layer states the ROLE — this wall carries no load — not what it is made of. Those are two different questions: a 30 cm masonry wall between two rooms carries nothing, while a concrete wall along a balcony does. The material is written separately precisely so that «non-structural» is not read as «made of masonry».",
  "struct.fem.zid_turnat": "Cast in concrete",
  "struct.fem.zid_turnat_v": "{pct} % of the wall — {piese}",
  "struct.fem.zid_turnat_sub": "the rest of the wall is concrete and is drawn on the structural layers; only the non-structural part stayed here",
  "struct.fem.beton_clasa": "Reinforced concrete {clasa}",
  "struct.fem.beton_material_sub": "the concrete class of the analysis model",
  "struct.fem.st_peste_zid": "Cast over",
  "struct.fem.st_peste_zid_v": "wall {id} from Step 2 ({pct} %)",
  "struct.fem.st_peste_zid_sub": "that is why the wall no longer appears on the non-structural walls layer — here it is concrete, not masonry",
  "struct.fem.st_peste_zid_titlu": "Where did the wall go?",
  "struct.fem.st_peste_zid_ce": "This member sits on the footprint of a simplified wall from Step 2. A wall is either concrete or non-structural, not both: the covered part leaves the grey layer and is drawn here, as a structural element.",
  "struct.fem.st_peste_zid_dece": "Without this row, a wall swallowed by a blade column vanished from the 3D view without a trace — on «Olteniței P + 4», the 6.95 m wall E64 on floor 4 could not be seen anywhere, although the 30 × 690 cm blade that had replaced it stood exactly in its place.",
  "struct.fem.gol_usa": "Door",
  "struct.fem.gol_fereastra": "Window",
  "struct.fem.gol_sub": "{nivel} · {l} m · architecture (not in the analysis)",
  "struct.fem.gol_sursa": "the opening from Step 2",
  "struct.fem.gol_latime": "Width",
  "struct.fem.gol_latime_sub": "at the FRAME, as the import measured the hole in the wall",
  "struct.fem.gol_grosime_sub": "the thickness of the wall it pierces",
  "struct.fem.gol_hp": "Sill (HP)",
  "struct.fem.gol_hp_necotat": "not dimensioned on the drawing",
  "struct.fem.gol_hp_sub": "from the FINISHED FLOOR of the storey — the architect's datum and the standard's (STAS 6131 measures from there too)",
  "struct.fem.gol_hp_fara": "the plan carries no «HP=» mark beside this opening",
  "struct.fem.gol_hp_titlu": "Where the sill height comes from",
  "struct.fem.gol_hp_ce": "From the «HP=0.90m» mark written on the drawing beside the opening. The marks do not sit on a joinery layer — in the Olteniței block they are on the drawing's text layer — so they are searched for across the whole drawing, and each goes to ONE opening, the nearest.",
  "struct.fem.gol_hp_dece": "An opening without a mark stays without a sill height; it is not given a «usual» one: an assumed sill looks in 3D exactly like a measured one, and the safety check (STAS 6131) would treat it as a figure from the documents.",
  "struct.fem.gol_h": "Opening height",
  "struct.fem.gol_h_necunoscuta": "not measurable",
  "struct.fem.gol_h_sub": "the lintel at {m} m above the floor",
  "struct.fem.gol_h_fara": "the plans do not dimension the lintel, and the elevation could not be measured",
  "struct.fem.gol_h_titlu": "Why it is measured off the elevation",
  "struct.fem.gol_h_ce": "The plan dimensions where the opening STARTS («HP=») and never where it ends. The only drawing in the documents that shows the lintel is the elevations/sections sheet, where the openings are drawn to scale. There, on each storey, the openings fall into clean families — at Olteniței 2.18 m (the french doors, HP=0.00) and 1.43 m (the windows, HP=0.90).",
  "struct.fem.gol_h_dece": "It is the HEIGHT that is measured, not the level: a level would require the elevation's datum to be calibrated, and the dimension texts sit beside the level line, not on it. A height is a subtraction, so the offset cancels. The lower datum stays «HP=», read off the plan, where it is dimensioned explicitly.",
  "struct.fem.gol_3d": "In 3D",
  "struct.fem.gol_3d_v": "a thin strip on the floor",
  "struct.fem.gol_3d_sub": "the position and the width are measured; the height is not known, so no box is drawn with assumed dimensions",
  "struct.fem.gol_gazda": "Host wall",
  "struct.fem.gol_gazda_sub": "the simplified wall the opening sits in",
  "struct.fem.gol_gazda_fara": "the opening could not be attached to a simplified wall",
  "struct.fem.gol_rol": "none",
  "struct.fem.gol_rol_sub": "openings do not enter the FEM; they say where a beam can pass without spoiling the architecture (a lintel over the opening)",

  "struct.fem.par_sursa": "the non-structural edge from Step 2",
  "struct.fem.par_grosime_sub": "measured off the drawing, or nominal where the parapet is drawn with a single line",
  "struct.fem.inaltime": "Height",
  "struct.fem.par_h": "{m} m",
  "struct.fem.par_h_presupusa": "{m} m (assumed)",
  "struct.fem.par_h_necotata": "not dimensioned on the plan",
  "struct.fem.par_h_sub_presupus": "the plan does not dimension the parapet; the minimum of a safety parapet is assumed (STAS 6131), and it enters the loads and masses with it",
  "struct.fem.par_h_sub_dosar": "the figure from the documents",
  "struct.fem.par_h_sub_vechi": "a result from before the assumed height — run Step 7 again",
  "struct.fem.par_h_titlu": "Where the height comes from",
  "struct.fem.par_h_ce": "On a plan a parapet is a strip, like any wall: the drawing says where it is and how thick, not how tall. When the documents do not give it, 0.90 m is assumed — the minimum of a safety parapet and the usual height of a balcony parapet.",
  "struct.fem.par_h_dece": "Without a height the parapet would have no weight and would enter nowhere; with an assumed one it enters the storey's loads and masses and the fixing moment of the cantilever, and the assumption shows up here and in the decisions of Step 7. The canonical format can carry the figure; once the import measures it off the elevation, that will beat the assumption.",
  "struct.fem.par_rol": "a line load and a mass",
  "struct.fem.par_rol_sub": "g_wall × h per metre: onto the beam beneath it (G2a) or onto the slab (G2b); on a cantilever it loads its tip in Step 16. It is not an element in the FEM and it is not checked",
  "struct.fem.pb_titlu": "Concrete wall {id}",
  "struct.fem.pb_sub": "{nivel} · L = {l} m · reinforced concrete",
  "struct.fem.pb_sursa_balcon": "balcony wall",
  "struct.fem.pb_sursa_subsol": "basement panel",
  "struct.fem.pb_amplasare": "Location",
  "struct.fem.pb_intre_stalpi": "Between columns",
  "struct.fem.pb_pe_balcon": "on the side of an external balcony",
  "struct.fem.pb_balcon_sub": "the architecture asks for concrete on this wall{arh}, through the «pereti_balcon_beton» preference; the other balcony sides stay masonry",
  "struct.fem.pb_balcon_arh": " ({id} in Step 2)",
  "struct.fem.pb_sect_balcon": "thickness × the clear height under the slab above",
  "struct.fem.pb_sect_subsol": "thickness × the clear height under the slab over the basement",
  "struct.fem.pb_balcon_titlu": "Why concrete on THIS balcony side?",
  "struct.fem.pb_balcon_ce": "The wall is listed in the «pereti_balcon_beton» preference, so it is cast in reinforced concrete, monolithic with the cantilever slab. By default, balcony sides are NOT cast in concrete.",
  "struct.fem.pb_balcon_dece": "The rule: a cantilever balcony gets a concrete slab, but its walls are masonry, like any partition — concrete only where the architecture calls for it (on «Olteniței P + 4» there is a single such wall, drawn 30 cm thick). What changes is the weight on the tip of the cantilever: concrete enters at γ = 25 kN/m³ × the cast thickness, masonry at g_wall × the clear height.",
  "struct.fem.pb_subsol_titlu": "Why a concrete wall in the basement?",
  "struct.fem.pb_subsol_ce": "In buildings of three storeys or more with a basement, the substructure is closed into a «rigid box»: reinforced concrete walls between the basement columns, on the axes of the architectural walls.",
  "struct.fem.pb_subsol_dece": "P100-1 §4.4.1.7 recommends rigid-box substructures; the rule of practice is a basement at least 50 % stiffer than the storeys above. The wall is modelled as a PANEL of shells (as a member, its in-plane shear stiffness did not exist in the model), and Step 14 reinforces it at 5 points, as for beams.",
  "struct.fem.pb_pozitie": "Position in the column",
  "struct.fem.pb_fata": "at the outer face (+{cm} cm from the axis)",
  "struct.fem.pb_centrat": "centred",
  "struct.fem.pb_pozitie_sub": "the panel runs from the corner of one column to the corner of the next: its outer face comes into the plane of the columns' outer face, without overlapping them",
  "struct.fem.pb_reazem": "Bears on",
  "struct.fem.pb_pe_fundatie": "the foundation{ax}, top face at {z} m",
  "struct.fem.pb_ax": " ({ax})",
  "struct.fem.pb_pe_placa_sol": "the slab on ground, top face at {z} m",
  "struct.fem.pb_pe_placa_nivel": "the slab of the storey below, top face at {z} m",
  "struct.fem.pb_pe_perete": "the concrete wall below, along the whole clear length (through the slab, top face at {z} m)",
  "struct.fem.pb_pe_cota": "level {z} m",
  "struct.fem.pb_turnat": "cast over {h} m, from the level above down to the soffit of the {ce} above. The weight and the reinforcement are taken over the ANALYSIS height ({calcul} m, from under the slab downwards): the {dif} cm difference is substructure concrete, which is neither in the superstructure mass nor in the foundation section",
  "struct.fem.pb_grinzii": "beam",
  "struct.fem.pb_placii": "slab",
  "struct.fem.pb_nu_pluteste": "the concrete wall does not float: its underside is the top face of whatever holds it",
  "struct.fem.pb_greutate": "Weight",
  "struct.fem.pb_greutate_sub": "it enters G1; the masonry it replaced was taken out of G2c",
  "struct.fem.pb_perimetral": "on the building perimeter",
  "struct.fem.pb_interior": "internal",
  "struct.fem.pb_perimetral_sub": "CR 2-1-1.1 §10.4.2: a perimeter wall requires ≥25 cm",
  "struct.fem.pb_nota": "Its forces are extracted in Step 12 at 5 points (as for beams), and the reinforcement — vertical bars over the basement height plus horizontal distribution bars — is done in Step 14.",

  "struct.fem.st_lamela": "Blade",
  "struct.fem.st_stalp": "Column",
  "struct.fem.st_sursa": "analysis column",
  "struct.fem.st_h_etaj": "Storey height",
  "struct.fem.st_h_etaj_sub": "the real height of this storey — from Step 2 (the elevation levels)",
  "struct.fem.st_i_brut": "I gross",
  "struct.fem.st_i_fisurat": "I cracked (×{f})",
  "struct.fem.st_ei_fisurat": "EI cracked",
  "struct.fem.st_greutate": "Self-weight",
  "struct.fem.st_greutate_sub": "b × h × storey height × 25 kN/m³ (the weight of reinforced concrete)",
  "struct.fem.st_rigid_sus": "Rigid zone at the top",
  "struct.fem.st_rigid_sus_v": "½ beam depth (25 cm)",
  "struct.fem.st_nota": "The cracked stiffness ({f}·EI) follows P100-1 — columns keep more stiffness than beams because of the axial compression.",
  "struct.fem.gr_sursa": "analysis beam",
  "struct.fem.gr_inima": "Web",
  "struct.fem.gr_inima_sub": "out of the full beam {bw} × {h} — the {hf} cm strip is in the slab (shell)",
  "struct.fem.gr_inima_titlu": "Why only the web?",
  "struct.fem.gr_inima_ce": "The slab (shell) already carries the flange in compression/membrane. The beam is given only the concrete BELOW the slab, so the flange is not counted twice.",
  "struct.fem.gr_inima_dece": "The T-section action comes out of the composite offset: the web is rigidly lowered by e = t/2 + h_web/2, and the lever arm × the membrane stiffness of the slab reproduces the composite behaviour exactly.",
  "struct.fem.gr_offset": "Composite offset e",
  "struct.fem.gr_offset_sub": "applied through -jntOffset (a rigid drop), at both ends",
  "struct.fem.gr_a_inima": "A (web)",
  "struct.fem.gr_i_inima": "I web",
  "struct.fem.gr_q_inima": "q web",
  "struct.fem.gr_q_inima_sub": "the weight of the web only — the slab and the finishes load the shells directly",
  "struct.fem.gr_noduri_placa": "Nodes shared with the slab",
  "struct.fem.gr_noduri_placa_v": "{n} (on the mesh lines)",
  "struct.fem.gr_zone_rigide": "Rigid zones",
  "struct.fem.gr_zone_rigide_v": "½ column width at the ends framing into columns",
  "struct.fem.gr_t": "T beam {ref}",
  "struct.fem.tip": "(type {clasa})",
  "struct.fem.gr_titlu": "Beam {ref}",
  "struct.fem.gr_sub": "{nivel} · L = {l} m",
  "struct.fem.gr_beff_titlu": "The effective slab width (EC2 §5.3.2.1)",
  "struct.fem.gr_beff_ce": "How much of the slab works together with the beam as the flange of a T section. Not all the slab manages to take load — only a strip next to the beam.",
  "struct.fem.gr_beff_dece": "b_eff = bw + Σ min(0.2·bᵢ + 0.1·l₀; 0.2·l₀; bᵢ), where bᵢ is half the clear distance to the neighbouring parallel beam and l₀ the distance between points of zero moment.",
  "struct.fem.gr_talpa": "flange left / right",
  "struct.fem.gr_l0": "{l} m ({tip} span)",
  "struct.fem.gr_a_t": "A (T)",
  "struct.fem.gr_i_t": "I major (T)",
  "struct.fem.gr_i_t_sub": "vs rectangle {brut}·10⁻⁴ — the flange adds +{plus}%",
  "struct.fem.gr_tributara": "Tributary width",
  "struct.fem.gr_q_permanent": "q permanent",
  "struct.fem.gr_q_permanent_sub": "slab+web {dead} + finishes {sdl}",
  "struct.fem.gr_q_util": "q imposed",
  "struct.fem.gr_noduri_fem": "FEM nodes",
  "struct.fem.gr_noduri_fem_v": "{n} internal ({seg} segments)",

  "struct.fem.mc_titlu": "The masses taken into account",
  "struct.fem.mc_info_titlu": "How the seismic mass of a storey is made up",
  "struct.fem.mc_info_ce": "Seismic mass per storey = D (slab + beam webs + ½ column below + ½ above) + non-structural walls (½ above + ½ below, mapped onto beams or onto the slab) + finishes + φ·ψ2·imposed (φ=0.8 typical / 1.0 topmost; per A/C zones) + ψ2·snow on the roof — docs/algoritm_eforturi.md P3",
  "struct.fem.mc_col_placa": "Slab",
  "struct.fem.mc_col_grinzi": "Beams (webs)",
  "struct.fem.mc_col_stalpi": "Columns and blades (½+½)",
  "struct.fem.mc_col_pereti_grinzi": "Walls on beams",
  "struct.fem.mc_col_pereti_placa": "Walls on slab",
  "struct.fem.mc_col_finisaje": "Finishes (SDL)",
  "struct.fem.mc_col_utila": "ψE·Imposed",
  "struct.fem.mc_col_zapada": "ψE·Snow",
  "struct.fem.mc_col_total": "Total [t]",
  "struct.fem.mc_nivel": "Storey",
  "struct.fem.mc_nivel_diafragma": "storey {n}",
  "struct.fem.vf_titlu": "Checks",
  "struct.fem.vf_nerulat": "the modal check did not run — run Step 7 again",
  "struct.fem.vf_masa90": "All modes together reach ≥ 90 % of the mass, on X and on Y",
  "struct.fem.vf_masa90_val": "{x} % X · {y} % Y",
  "struct.fem.vf_masa90_prag": "≥ 90 % on X and on Y",
  "struct.fem.vf_masa90_formula": "min({x}; {y}) ≥ 90, with {moduri} modes retained",
  "struct.fem.vf_numar#n": "<b>{n}</b> check|<b>{n}</b> checks|<b>{n}</b> checks",
  "struct.fem.vf_trec": "✓ {n} pass",
  "struct.fem.vf_nu_trec": "✗ {n} fail",
  "struct.fem.vf_vechi": "⚠ An old result — re-run Step 7 for the detailed checks (the saved result predates the gate emitting the four named checks; only what it holds appears below).",
  "struct.fem.vf_col_verificarea": "Check",
  "struct.fem.vf_col_valoarea": "Value",
  "struct.fem.vf_col_pragul": "Threshold",
  "struct.fem.vf_col_verdict": "Verdict",
  "struct.fem.vf_col_formula": "Formula (with the project's values)",
  "struct.fem.vf_nedeterminat": "undetermined",
  "struct.fem.vf_trece": "✓ passes",
  "struct.fem.vf_pica": "✗ fails",
  "struct.fem.vf_review": "◐ review",
  "struct.fem.vf_de_revizuit": "◐ {n} to review",
  "struct.fem.vf_gate_review": "◐ Gate verdict: the periods are close to the 15 % separation — review. The solver keeps trying to reach 15 %.",
  "struct.fem.vf_gate_ok": "✓ Gate verdict: the modes are decoupled — the structure goes on down the chain.",
  "struct.fem.vf_gate_nu": "✗ Gate verdict: the modes are NOT decoupled",
  "struct.fem.vf_directie": "Step 19 stiffens along the recommended direction {directie} (rule R11 — lengthening columns so as to bring the centre of stiffness closer to the centre of mass).",
  "struct.fem.vf_asimetrie": "Flexibility asymmetry (‖asymmetry of the condensed matrix‖):",
  "struct.fem.vf_asimetrie_sub": "— informative, it does not enter the count (only the lean variant reports it).",

  "struct.fem.vf_nedeterminate": "— {n} undetermined",
  "struct.fem.vf_ind_titlu": "What the indicator counts",
  "struct.fem.vf_ind_ce": "The four modal checks of the decoupling gate (the separation of periods T1–T2 and T2–T3, the dominant direction of modes 1 and 2) plus the criterion of ≥ 90 % cumulative participating mass. The flexibility asymmetry is informative only and does not enter the count.",
  "struct.fem.vf_ind_dece": "The modal gate decides whether the structure goes on down the chain or whether Step 19 must stiffen it (rule R11). The full table, with every mode, is below, under «The modal check of the model».",
  "struct.fem.vm_titlu": "The modal check of the model",
  "struct.fem.vm_info_titlu": "Why only a «check»?",
  "struct.fem.vm_info_ce": "The modal analysis here confirms that the model is sound: plausible periods and all the mass recovered in the modes. Visualising the mode shapes (how the building sways in each mode) comes in Step 9 (the seismic analysis).",
  "struct.fem.vm_info_dece": "The lean model has the mass lumped at the centre of mass of each floor, so it has exactly 3 modes per storey (X, Y, torsion). P100-1/EC8 requires the modes considered to cover ≥ 90% of the mass — here, by construction, they cover 100%.",
  "struct.fem.vm_t_nume": "the natural period of the mode (seconds)",
  "struct.fem.vm_t_sursa": "eigen on the statically condensed model (OpenSees + numpy)",
  "struct.fem.vm_m_nume": "the participating mass in translation and torsion",
  "struct.fem.vm_m_sursa": "the classic Γ²/M formula from the eigenvectors",
  "struct.fem.vm_col_masa_x_titlu": "modal effective mass on X, as a PERCENTAGE OF THE TOTAL MASS of the building. The sum down a COLUMN (over all modes) tends to 100% — the sum across a row has no reason to reach 100%, because each column is referred to a different base.",
  "struct.fem.vm_col_masa_y_titlu": "modal effective mass on Y, as a percentage of the total mass of the building (see «mass X»).",
  "struct.fem.vm_col_masa_z": "mass Z",
  "struct.fem.vm_col_masa_z_titlu": "vertical modal effective mass, as a percentage of the total mass.",
  "struct.fem.vm_col_torsiune_titlu": "ROTATIONAL effective mass, as a percentage of the building's total rotational inertia J — a different base from the translation columns, so it does not add up with them.",
  "struct.fem.vm_col_dist": "mode split X/Y/T",
  "struct.fem.vm_col_dist_titlu": "how THIS mode's participation splits between X / Y / torsion (+vertical) — guaranteed to sum to 100%. This is the split the decoupling gate uses (90% on a single direction).",
  "struct.fem.vm_col_tip": "type",
  "struct.fem.vm_col_puritate": "purity",
  "struct.fem.vm_col_puritate_titlu": "the fraction of the total participation that goes along the dominant direction: 1.00 = a pure mode (clean translation), ~0.60 = translation coupled with torsion. For two modes with practically equal periods (a quasi-degenerate pair) the purity of the PAIR is given — taken apart, each is an arbitrary combination of the two, chosen by the solver.",
  "struct.fem.vm_dist_celula": "X / Y / torsion — sums to 100%",
  "struct.fem.vm_cumulat": "Cumulative participating mass: {x}% on X, {y}% on Y — the ≥ 90% criterion (P100-1/EC8) is {verdict}",
  "struct.fem.vm_indeplinit": "met",
  "struct.fem.vm_neindeplinit": "NOT met",
  "struct.fem.vm_moduri": "with {n} modes retained out of {extrase} extracted (minimum required: {minim})",
  "struct.fem.vm_moduri_extra": " — with only {minim} modes the effective mass was {m0}% on 0° and {m90}% on 90°, so the extra modes were needed",
  "struct.fem.vm_directie_deficitara": "the deficient one",
  "struct.fem.vm_pas19": "Step 19 proposes lengthening columns along direction {directie} (rule R11), chosen so as to bring the centre of stiffness closer to the centre of mass; the zones that move most (red) can be seen in the mode shapes above.",
  "struct.fem.vm_comparatie": "Comparison with the lean variant",
  "struct.fem.vm_comparatie_sub": " (same preferences): T₁ shell = {t1s}s vs T₁ lean = {t1l}s ({semn}{delta}%) · mass {masa} t vs {masaLean} t",
  "struct.fem.vm_cmp_placa": " · slab {shell} m² vs {lean} m²",
  "struct.fem.vm_cmp_mesh": " · mesh coverage {mesh}%",
  "struct.fem.vm_nerulat": "The check did not run.",

  "struct.fem.fara_armatura": "no reinforcement designed yet",
  "struct.fem.fim_titlu": "The foundation in the model",
  "struct.fem.fim_incastrat": "⚠ The base of the structure is perfectly FIXED — {motiv}",
  "struct.fem.fim_ce_titlu": "What a «fixed base» means and why it matters",
  "struct.fem.fim_ce_p1": "A perfect fixity means the nodes at level 0 can neither translate nor rotate: the model assumes infinitely stiff ground. No real structure stands like that. The consequences: the periods come out shorter than in reality, and the moments at the column bases come out larger, because nothing there can rotate.",
  "struct.fem.fim_ce_p2": "It is an acceptable assumption on very good ground, and it is the benchmark the calibration against ETABS was done on. To replace it with the real ground, turn on «the foundation in the model» («fundatie_in_model»).",
  "struct.fem.fim_activ_titlu": "The foundation in the model — a grid of beams on elastic ground",
  "struct.fem.fim_da_titlu": "Yes, the foundation is part of this model",
  "struct.fem.fim_da_p1a": "The base is no longer fixed. Under the structure there is a grid of real ",
  "struct.fem.fim_grinzi": "foundation beams",
  "struct.fem.fim_da_p1b": " (frame elements, with their own section and stiffness), and beneath them a ",
  "struct.fem.fim_covor": "carpet of springs",
  "struct.fem.fim_da_p1c": " that stands in for the soil. Their geometry and stiffness come from Step 6; the result goes back there, as a pressure read straight off the spring reactions.",
  "struct.fem.fim_da_p2": "What changes against a fixed base: the base nodes can settle and rotate, so the structure becomes more flexible (longer periods), the moments at the column bases drop, and part of them passes into the foundation beams — which is why those have to be reinforced too.",
  "struct.fem.fim_impact": "The effect on the dynamics",
  "struct.fem.fim_t1": "T₁ = {incastrat} s fixed → {elastic} s on elastic ground",
  "struct.fem.fim_decizie": "The «fixed vs elastic» decision (threshold ΔT₁ = {prag} %)",
  "struct.fem.fim_doua": "2 models",
  "struct.fem.fim_unul": "1 model",
  "struct.fem.fim_retea": "The foundation grid",
  "struct.fem.fim_retea_sub": "beams {b}×{h} m over {l} m · {n} springs",
  "struct.fem.fim_abatere": " · the chain strays from the axis by at most {m} m",
  "struct.fem.fim_transversale": ", {n} eccentric supports",
  "struct.fem.fim_teren": "The stiffness of the ground",
  "struct.fem.fim_teren_sub": "K_s {sursa} from Step 6 · elastic length {lambda} m · spring spacing {pas} m",
  "struct.fem.fim_pas_titlu": "Why the spring spacing is {pas} m:",
  "struct.fem.fim_pas_text": " it is capped at π/4 of the elastic length λ⁻¹ = (4EI/(K_s·B))^¼ = {lambda} m — the distance over which the effect of a concentrated force «dies out» (NP 112 annex K.3.2). Below it, the discrete carpet of springs behaves like a continuous one. ",
  "struct.fem.fim_ent": "The springs are TENSION-FREE (ENT): the model is non-linear.",
  "struct.fem.fim_liniar": "The springs are linear, so that the superposition of effects from Step 11 stays exact.",

  // --- STRUCTURE · Step 17, checking the foundation on real reactions --
  "struct.rzv.fundatia_are_doi": "Why the foundation has two steps, at the two ends of the chain",
  "struct.rzv.acopera_coeficientul_exc": "What does the eccentricity factor cover?",
  "struct.rzv.modelul_asaza_talpa": "The model always places the base centred under the wall axis, but on site the column can bear eccentrically on it: at a free end of the base, at a corner (the end of both bases) or at a party wall, where the base cannot project beyond the property line. That CONSTRUCTIONAL eccentricity concentrates the pressure towards one edge — the factor covers it by multiplying the axis's check pressures (p₁, p_max NP 112, p from the model, the pressure under the columns).",
  "struct.rzv.ia_acoperitor_ax": "It is taken on the safe side PER AXIS — the worst case among its own columns, not the worst on the whole building. Office practice: centred columns 1.00–1.08; eccentric about one axis; eccentric about two axes (a corner); at a party wall up to ~1.4. The situation is detected automatically from the geometry; a «party wall» CANNOT be detected (the property line is not in the model) — if the axis is at a party wall, write the recommended value here (~1.4). The mean pressure used for settlement stays unamplified: settlement is a global phenomenon.",
  "struct.rzv.numara_indicatorul": "What the indicator counts",
  "struct.rzv.celula_tabelele_jos": "Every ✓/✗ cell in the tables below is one check: the pressure on each axis (along three routes of calculation), lift-off, settlement, the pressure and the punching under each column, the six global checks and the reinforcement of each foundation beam.",
  "struct.rzv.proiect_obisnuit_ies": "A normal project produces a few hundred checks. Without a counter, a single exceedance on a single axis is lost among the rows — and that is exactly what happened: an axis that passed the working-method threshold and failed the code one was flagged nowhere.",
  "struct.rzv.verifica_per_ax": "Why it is checked per axis, not globally",
  "struct.rzv.predimensionarea_imparte": "Preliminary sizing divides the total weight by the total base area — an average. In reality each foundation axis carries something different: internal axes gather slabs on both sides, edge ones have a small axial force but a large moment from the earthquake, and that is where the exceedances appear.",
  "struct.rzv.structura_foilor_calcul": "It is the structure of the spreadsheets in the working method: one sheet per axis (AxA, AxB, … Ax1, Ax3), in which the reactions of the nodes on the axis are summed and the pressures at the ends of the base are computed with the eccentricities from the moments. Here the same thing is done automatically, over all the combinations.",
  "struct.rzv.ax_fundatie_verifica": "What a «foundation axis» is and why it is checked separately",
  "struct.rzv.p_metoda_kpa": "p method [kPa]",
  "struct.rzv.arata_axul_desenul": "show the axis in the drawing below",
  "struct.rzv.presiunea_pune_stalp": "The pressure each column puts on the foundation",
  "struct.rzv.verificarile_privesc_fun": "The checks that look at the foundation as a whole",
  "struct.rzv.lunecare_talpa": "Sliding on the base",
  "struct.rzv.rasturnare": "Overturning",
  "struct.rzv.indicator_informativ_int": "An informative indicator: it does not enter the check counter and does not block the optimisation",
  "struct.rzv.lasa_cladirea_neuniform": "Does the building settle unevenly?",
  "struct.rzv.conteaza_doar_lasa": "What matters is not only how much the building settles, but above all how DIFFERENTLY it settles from one axis to the next: the settlement difference divided by the distance between them. This is an INFORMATIVE INDICATOR: the value is computed and shown, but it is not a check that fails — a design decision, since the axis-to-axis criterion is not used for these frames. The verdict stays on the ABSOLUTE settlement per axis.",
  "struct.rzv.np_112_tab": "NP 112 tab. H.1 gives an indicative benchmark: for reinforced concrete frames without masonry infill 0.002; with masonry infill 0.001 (masonry cracks sooner). The maximum absolute settlement is 80 mm — that one stays a mandatory check, per axis.",
  "struct.rzv.bucla_teren_structura": "The ground–structure loop",
  "struct.rzv.inseamna_verificare_glob": "What each global check means",
  "struct.rzv.grinda_fundare_armeaza": "Why a foundation beam is reinforced differently from an ordinary one",
  "struct.rzv.modelata_fundatia_altfel": "How the foundation is modelled — and why differently from the rest",
  "struct.rzv.face_bucla_locala": "What the local loop does and why it is separate from the global one",
  "struct.rzv.actiunile_catalog_cand": "The actions in the catalogue and when they are used",
  "struct.rzv.fundatii_coef_excentrici": "fundatii.COEF_EXCENTRICITATE — a single place to update",
  "struct.rzv.verificarile_bucla_pasul": "the checks that the loop of Step 6 can resolve by changing the foundation geometry; the rest are left to Step 19",
  "struct.rzv.catalogul_actiuni_f1": "the catalogue of actions F1–F5",
  "struct.rzv.suma_reactiunilor_noduri": "the sum of the reactions of the nodes on the axis",
  "struct.rzv.tensorul_combinat_pasulu": "the combined tensor from Step 11",
  "struct.rzv.aria_talpii_ax": "the base area on the axis",
  "struct.rzv.l_partea_acoperita": "L = the part of the axis envelope covered by the foundation (Step 5)",
  "struct.rzv.modulele_rezistenta_b": "the section moduli B·L²/6 and L·B²/6",
  "struct.rzv.pasul_5_segmentele": "Step 5 — the foundation segments grouped on their supporting line",
  "struct.rzv.decizia_pasului_6": "the decision of Step 6 (Step 6 sizes it preliminarily, Step 17 widens it where needed)",
  "struct.rzv.decizie_proiectare_impli": "a design decision; the defaults in a single place, until his own values arrive",
  "struct.rzv.pasul_6_detaliul": "Step 6 — the construction detail + D_f (NP 112 §II.7.5.1)",
  "struct.rzv.stalpi_lamele": "Columns and blades",
  "struct.rzv.guverneaza": "Governs",
  "struct.rzv.p_metoda": "p method",
  "struct.rzv.stalp": "Column",
  "struct.rzv.pasul_4_id": "Step 4 (id_plan) + the base node of the model",
  "struct.rzv.reactiunea_nodului_tenso": "the node reaction, from the tensor of Step 11",
  "struct.rzv.pozitie": "Position",
  "struct.rzv.diferenta_tasare_intre": "the settlement difference between two axes",
  "struct.rzv.tasarea_calculata_per": "the settlement computed per axis, with its own pressure",
  "struct.rzv.distanta_dintre_centrele": "the distance between the centres of the axes",
  "struct.rzv.tasare_relativa": "Relative settlement",
  "struct.rzv.sectiune": "Section",
  "struct.rzv.consola_talpa": "Base cantilever",
  "struct.rzv.f1_latirea_axelor": "F1 — widening the axes that fail",
  "struct.rzv.f2_latirea_intregii": "F2 — widening the whole foundation",
  "struct.rzv.f3_coborarea_cotei": "F3 — lowering the founding level",
  "struct.rzv.f4_grinda_fundare": "F4 — a deeper foundation beam",
  "struct.rzv.f5_portiuni_suplimentare": "F5 — additional lengths",
  "struct.rzv.f6_ingustarea_axelor": "F6 — narrowing the oversized axes",
  "struct.rzv.fundatia_structura_condi": "The foundation and the structure condition each other, so there is no linear order in which every step receives data already computed. The chain breaks the loop at one point, and the foundation is settled in two goes, far apart:",
  "struct.rzv.estimata": "estimated",
  "struct.rzv.pasii_7_12": "Steps 7…12",
  "struct.rzv.re_ruleaza_modelul": "does not re-run the model",
  "struct.rzv.ruleaza_tot": "«Run everything»",
  "struct.rzv.salveaza": "Save",
  "struct.rzv.revino_implicit": "Back to the default",
  "struct.rzv.verificari": "checks",
  "struct.rzv.geometria_gasita": "→ the geometry found",
  "struct.rzv.reluarea_lantului_pasul": "re-running the chain from Step 19",
  "struct.rzv.bucla_pasului_6": "The loop of Step 6 found a geometry that passes, but the result shown is not the final one — run the step again.",
  "struct.rzv.unde": "Where",
  "struct.rzv.referinta": "Reference",
  "struct.rzv.pasul_6_predimensionarea": "Step 6 (the preliminary sizing above) works with an empirically estimated weight and with a mean pressure over the whole length of the base. Step 17 repeats the check with the reactions from the model that ran, axis by axis — and that is where the cases the average hides come out: the edge axes, with a small axial force and a large seismic moment.",
  "struct.rzv.csv_per_ax": "↓ CSV per axis × combination",
  "struct.rzv.motivul_axele_egale": "The reason is that the axes are not equal. An internal axis receives floor from both sides and is well loaded vertically; an edge one receives from a single side, so its vertical force is small — yet in an earthquake it takes the same overturning moment. The average over the whole building (Step 6) hides exactly those axes.",
  "struct.rzv.numele_axului_literele": "The name of the axis. Letters run along the transverse direction, numbers along the building's dominant direction — the convention of the formwork drawings.",
  "struct.rzv.real_acoperita": "actually covered",
  "struct.rzv.fundatie_distanta_dintre": "of foundation, not the distance between the ends.",
  "struct.rzv.constructiva": "constructional",
  "struct.rzv.editabila_per_ax": "editable per axis",
  "struct.rzv.anvelopa_axului_chiar": "How much of the axis envelope is actually foundation. 100 % = an unbroken ribbon; 40 % = the axis has large gaps.",
  "struct.rzv.cate_noduri_reazem": "How many support nodes fall on the axis. With the foundation in the model the count includes the intermediate points on the foundation beam, not just the columns.",
  "struct.rzv.combinatia_incarcari_pro": "The load combination that produces the maximum pressure on this axis — the one that «decides» the verdict.",
  "struct.rzv.presiunea_maxima_margine": "The maximum pressure at the edge of the base, by the formula in the office spreadsheet: the vertical resultant divided by the area, plus the effect of the moment.",
  "struct.rzv.terenul_poate_trage": "the ground cannot pull",
  "struct.rzv.coboara_terenul_sub": "How far the ground settles under this axis, computed with ITS pressure, not with the building average.",
  "struct.rzv.incarcarea_lui": "its own load",
  "struct.rzv.sus": "upwards",
  "struct.rzv.stalpi": "Columns",
  "struct.rzv.latit": "widened",
  "struct.rzv.combinatie": "Combination",
  "struct.rzv.arie_activa": "active area",
  "struct.rzv.strapunge": "punches through",
  "struct.rzv.strapungerea": "Punching",
  "struct.rzv.forta_stalpului_scade": "From the column force the ground reaction inside the control perimeter is subtracted: the soil there pushes back up, so not all of N has to be carried in shear.",
  "struct.rzv.identificatorul_planul_c": "The identifier from the formwork drawing, plus its position.",
  "struct.rzv.cea_mare_forta": "The largest vertical force the column brings down, over all combinations. Positive = pressing down.",
  "struct.rzv.lungimea_talpa_tine": "The length of base that «holds» this column — half the distance to its neighbours on the axis.",
  "struct.rzv.apasarea_locala_teren": "The local bearing pressure on the ground, under this column.",
  "struct.rzv.interior_margine_conteaz": "«internal» or «edge» — it matters for punching: at an edge column the control perimeter is cut off by the edge of the base, so it resists less.",
  "struct.rzv.efortul_forfecare_apare": "The shear stress that appears on the control perimeter — «how hard» the column pushes through the base.",
  "struct.rzv.poate_duce_betonul": "How much the concrete, without shear reinforcement, can carry on that perimeter.",
  "struct.rzv.rezerva_cate_ori": "The margin: how many times larger the capacity is than the demand.",
  "struct.rzv.aluneca": "does not slide",
  "struct.rzv.rastoarna": "does not overturn",
  "struct.rzv.pluteste": "does not float",
  "struct.rzv.smulg": "are not pulled out",
  "struct.rzv.lasa_neuniform": "does not settle unevenly",
  "struct.rzv.card_arata_raportul": "Each card shows the ratio of demand to capacity (or the other way round, as the case may be) for the worst combination; hover over ⓘ for the full formula and the code reference.",
  "struct.rzv.cutremurul_impinge_cladi": "The earthquake pushes the building sideways. The foundation resists through the friction of the base on the soil (like a heavy cupboard on a carpet) plus the earth pressure in front of it.",
  "struct.rzv.forta_orizontala_aplicat": "The horizontal force, applied at the height of the centre of gravity, tends to rotate the whole building over the edge of the foundation. The weight holds it in place.",
  "struct.rzv.daca_panza_freatica": "If the water table is above the base, the water pushes the foundation up, like a boat. It is compared against the self-weight of the substructure — not of the building, because the critical phase is construction, when the building is not yet pressing down.",
  "struct.rzv.doar": "only",
  "struct.rzv.rasturnarea_ansamblu_sta": "In overall overturning, the columns on one side are pulled UP. They are held by the weight of the base and of the earth above it, over the tributary length.",
  "struct.rzv.coeficientul_pat_k": "The subgrade modulus K_s the model's springs were calibrated with was computed from the ESTIMATED pressure (Step 6). Once the model has run we know the real pressure — K_s is recomputed from it and compared.",
  "struct.rzv.incarcarea_vine_jos": "the load comes from below",
  "struct.rzv.infasuratoarea": "the envelope",
  "struct.rzv.b_h_c": "B × H_c and the effective depth d.",
  "struct.rzv.infasuratoarea_momentulu": "The envelope of the bending moment along the whole axis, over all combinations: M⁺ stretches one face, M⁻ the other.",
  "struct.rzv.cea_mare_forta2": "The largest shear force on the axis — the section's tendency to shear.",
  "struct.rzv.aria_armatura_longitudin": "The longitudinal reinforcement area required on each face. In brackets, what decided the value: the moment calculation or the minimum constructional reinforcement.",
  "struct.rzv.armatura_minima_construc": "The minimum constructional reinforcement and the reinforcement ratio achieved. On large concrete sections it often exceeds what the calculation asks for — and then it governs.",
  "struct.rzv.duce_betonul_forfecare": "How much the concrete carries in shear WITHOUT links. If V exceeds it, the links take the difference.",
  "struct.rzv.daca_necesari_calcul": "Whether they are required by calculation. Even when they are not, the constructional ones are provided.",
  "struct.rzv.talpa_lata_decat": "The base is wider than the beam; the parts that project are cantilevers loaded from below, by the ground pressure. They are reinforced transversely.",
  "struct.rzv.grosimea_talpii": "the base thickness",
  "struct.rzv.trei_lucruri_fac": "Three things are done differently from the superstructure:",
  "struct.rzv.rigiditatea_nefisurata": "the stiffness is UNcracked",
  "struct.rzv.sectiunea_dreptunghiular": "the section is rectangular",
  "struct.rzv.doar_fundatie": "from the foundation only",
  "struct.rzv.alege": "How it chooses",
  "struct.rzv.cand_opreste": "When it stops",
  "struct.rzv.rezerva_exact_pragul": "Why a margin and not exactly the threshold",
  "struct.rzv.zero_rezerva": "zero margin",
  "struct.rzv.variabilele_cautate": "the variables searched",
  "struct.rzv.latimi_diferite_axe": "Different widths per axis:",
  "struct.rzv.jurnalul_pas_pas": "The step-by-step log of the search is no longer kept: the only history kept is that of Step 19, «what changed in the structure». What the search here changed is read off the initial → final geometry above, and why it stopped, off «stop».",
  "struct.rzv.creste_b_doar": "Increase B only on the axes whose pressure is over the threshold.",
  "struct.rzv.creste_b_peste": "Increase B everywhere. Proposed only when ≥ 4 axes fail.",
  "struct.rzv.coboara_d_f": "Lower D_f. It raises the conventional pressure and the ballast of earth above the base.",
  "struct.rzv.creste_h_c": "Increase H_c, and with it the effective depth d.",
  "struct.rzv.completeaza_golurile_axe": "Fill the gaps on the axes with coverage below 85 %.",
  "struct.rzv.pas_economie_rulat": "An ECONOMY step, run only once everything passes with the required margin: it thins the axes that pass with more than 25 % margin.",
  "struct.rzv.celalalt_pas_economie": "The other ECONOMY step: it tries to RAISE the base. Without it, D_f would be a «ratchet» — the loop lowers it when needed, and re-running the chain starts from there, so the depth demanded by one pass with a poor model would never be recovered.",
  "struct.rzv.a_ramas_nerezolvat": "What was left unresolved",

  "struct.rzv.valoare_invalida": "invalid value — between 1.00 and 2.00",
  "struct.rzv.revenit_implicit": "back to the default for the situation detected — run Step 6 again for it to enter the calculation",
  "struct.rzv.salvat_reruleaza": "saved — run Step 6 again for it to enter the calculation",
  "struct.rzv.eroare_salvare": "error on save: {motiv}",
  "struct.rzv.sim_situatia": "the situation",
  "struct.rzv.detectare_geometrie": "detected from the geometry",
  "struct.rzv.sim_plaja": "the range",
  "struct.rzv.practica_birou": "office practice (the final values come from the engineer)",
  "struct.rzv.praguri_ambele": "both pressure thresholds are mandatory (the working method + NP 112 tab. I.5)",
  "struct.rzv.praguri_metoda": "only the working-method threshold governs (1.10 / 1.35 · p_conv)",
  "struct.rzv.praguri_np112": "only the code threshold governs (NP 112 tab. I.5)",
  "struct.rzv.sim_praguri": "thresholds",
  "struct.rzv.praguri_sursa": "the working method — «praguri_presiune»",
  "struct.rzv.sim_urmarite": "tracked",
  "struct.rzv.ascunde_lista": "hide the list",
  "struct.rzv.arata_lista": "show the list",
  "struct.rzv.desen": "drawing",
  "struct.rzv.p_unde_nume": "{unde} — {nume}",
  "struct.rzv.nu_s_a_facut": "The check against real reactions — not done yet",
  "struct.rzv.stalpi_lamele2": "Columns and blades",
  "struct.rzv.cautarea_geometriei": "The search for the geometry",
  "struct.rzv.metoda_vs_normativ": "Method vs code",
  "struct.rzv.h_detaliu_t": "H = D_f − h_above (detail «T»: the base plus the web fill the trench)",
  "struct.rzv.h_cu_subsol": "H = D_f − 0.05 (a building with a basement: the base only, and D_f is given by the thickness the width requires)",
  "struct.rzv.h_min": "H = min(L₀/6 ; D_f − 0.05), minimum 0.50 m",
  "struct.rzv.pasul_11_combinatii": "Step 11 — {n} combinations (fundamental + seismic)",
  "struct.rzv.presiunea_citita": "The pressure read",
  "struct.rzv.formula_resort": "p_i = R_spring,i / (B · l_tributary,i);  the maximum over the axis is taken",
  "struct.rzv.acelasi_prag_metoda2": "the same threshold as for the method (1.10 / 1.35·p_conv)",
  "struct.rzv.formula_tasare": "s = 0.8 · Σ (σ_mean,i · h_i / E_i), over slices, down to where σz < 0.2·σgz",
  "struct.rzv.latimi_distincte": "Base widths: {n} distinct sizes across the whole project",
  "struct.rzv.latit_local": "widened locally over the global B = {b} m",

  "struct.rzv.col_dir_ce": "The direction of the axis, referred to the <b>dominant direction of the building</b>, not to the north of the plan: a building rotated 30° from north still has «X» and «Y» axes.",
  "struct.rzv.col_dir_formula": "the dominant angle from the length-weighted mean",
  "struct.rzv.col_l_ce": "The design length of the axis — the part <b>actually covered</b> by foundation, not the distance between the ends.",
  "struct.rzv.col_l_formula": "Σ the lengths of the runs on the line of the axis",
  "struct.rzv.col_l_validare": "it enters A = L·B and the section moduli",
  "struct.rzv.col_b_ce": "The width of the base on this axis — the <b>thickness of the concrete band</b> seen on the plan. Where it says «widened», Step 17 increased it locally beyond the global width ({b} m), because the axis did not pass at the base width.",
  "struct.rzv.col_b_formula": "A = L·B,  W_long = B·L²/6,  W_transv = L·B²/6",
  "struct.rzv.col_b_validare": "the construction minimum {min} m (the excavator bucket); the maximum from the search grid 2.50 m",
  "struct.rzv.col_kexc_ce": "The eccentricity factor of the axis — it covers the <b>constructional</b> eccentricity between the column and the base, which the model does not see (it always puts the base centred under the axis). The axis check pressures (p method, p NP 112, p model) are shown already multiplied by it.",
  "struct.rzv.col_kexc_formula": "p_check = k_exc · p_computed",
  "struct.rzv.col_kexc_validare": "the practical range: centred columns 1.00–1.08; eccentric about one axis; about two axes (a corner); at a party wall ~1.4. The situation is detected from the geometry (columns at free ends of the base, corners, transverse offsets), on the safe side per axis; the value is <b>editable per axis</b> — click the row, then the editor under the table. «Party wall» is set by hand",
  "struct.rzv.col_h_ce": "The depth of the concrete section and the founding depth — the same over the whole foundation: H = {h} m, D_f = {df} m. The section on each axis is {sectiune}, with its underside at level −{df} m.",
  "struct.rzv.col_h_validare": "H is <b>derived</b>, not an optimisation variable: at given reactions it does not enter p = ΣF/(L·B) ± ΣM/W. It is rebuilt after every step that moves D_f",
  "struct.rzv.col_acop_formula": "L_covered / L_envelope",
  "struct.rzv.col_acop_validare": "below <b>70 %</b> it is marked red: the axis is «holed», and treating it as a continuous base is an approximation — it ought to be split into two axes",
  "struct.rzv.validare_stalp_aflat_intersectia": "a column standing at the intersection of two axes splits its load between them (half and half), otherwise it would be counted twice",
  "struct.rzv.validare_p1_b_1": "p₁ ≤ <b>1.10·p_conv</b> in the fundamental group, ≤ <b>1.35·p_conv</b> in the seismic one (here {p1} / {p2} kPa)",
  "struct.rzv.ce_aceeasi_presiune_maxima": "The same maximum pressure, but along the code route: a coefficient μ that accounts for the fact that <b>{p1}</b> of foundation — if the eccentricity exceeds L/6, part of the base lifts and the rest takes everything.",
  "struct.rzv.validare_pragul_depinde_tipul": "the threshold depends on the type of loading (NP 112 tab. I.5): concentric 1.0·p_conv (fundamental) / 1.2 (seismic); eccentric about one direction 1.2 / 1.4; skew 1.4 / 1.6",
  "struct.rzv.ce_presiunea_citita_b": "The pressure read <b>straight off the analysis model</b>: every ground spring under the foundation has a reaction, and that reaction is the resultant of the pressure beneath it. It is the most faithful of the three — it does not assume the base is rigid.",
  "struct.rzv.validare_acelasi_prag_metoda": "the same threshold as for the method (1.10 / 1.35·p_conv)",
  "struct.rzv.validare_diferenta_dintre_axe": "what matters is the difference between axes (the relative settlement) — see the Global tab",
  "struct.rzv.validare_acelasi_prag_ax": "the same threshold as on the axis: 1.10·p_conv (fundamental) / 1.35·p_conv (seismic)",
  "struct.rzv.validare_b_1_00": "<b>≥ 1.00</b>. Below 1, inclined bars are placed at the column or the base is thickened",
  "struct.rzv.validare_raportul_h_r": "the H/R ratio shown has to be <b>≤ 1.00</b>{p1} (EC7 §6.5.3 / NP 112 rel. I.23)",
  "struct.rzv.validare_raportul_m_stab": "the M_stab/M_overt ratio <b>≥ 1.00</b>. The EQU limit state (EC7 §2.4.7.2): the permanent actions are taken with 0.9 because they may be smaller than we estimate, and the destabilising ones with 1.1",
  "struct.rzv.validare_ruleaza_b_p1": "<b>{p1}</b> is run if the water is higher than the founding level; otherwise «n/a» (EC7 §2.4.7.4, the UPL limit state)",
  "struct.rzv.validare_verificarea_locala_acope": "the check is local and on the safe side: in reality the grid of foundation beams is continuous, so a column being pulled out is held by the whole substructure. That is why, with the foundation in the model, cases of lift-off fall sharply",
  "struct.rzv.ce_conteaza_doar_lasa": "What matters is not only how much the building settles, but how{p1} <b>differently</b> it settles from one axis to the next: the settlement difference divided by the distance between them. That is what racks the structure and cracks the walls — uniform settlement does no harm at all.",
  "struct.rzv.validare_b_0_002": "<b>0.002</b> for reinforced concrete frames without masonry infill, <b>0.001</b> with infill (masonry cracks sooner). The maximum absolute settlement: 80 mm. NP 112 tab. H.1",
  "struct.rzv.validare_abatere_b_20": "a deviation <b>≤ 20 %</b>. Beyond that, the bed stiffness in the model no longer matches reality and the chain has to be re-run with a recomputed K_s — that is what the outer loop does (Step 19)",
  "struct.rzv.validare_8_mm_etrieri": "Ø ≥ 8 mm; double links (4 legs) at b ≥ 400 mm. The ✓/✗ verdict compares V with <b>V_Rd,max</b> — the cap of the concrete compression strut, beyond which no reinforcement helps and the section has to be enlarged",
  "struct.rzv.validare_0_10_directie": "ρ ≥ 0.10 % per direction, Ø ≥ 10 mm, spacings 100–250 mm (NP 112 §II.6.1.1(3)). The «slope» verdict (S3) compares <b>{p1}</b> h_t with L_c, not the depth of the whole section: at h_t ≥ L_c (a slope ≥ 45°) the cantilever would no longer need a separate shear check — but we do it anyway, so a ✗ here only means the exemption does not apply",

  "struct.opt.op.redimensioneaza_stalp": "resize the column",
  "struct.opt.op.redimensioneaza_grinda": "resize the beam",
  "struct.opt.op.adauga_stalp": "add a column",
  "struct.opt.op.elimina_stalp": "remove the column",
  "struct.opt.op.adauga_grinda": "add a beam",
  "struct.opt.op.elimina_grinda": "remove the beam",
  "struct.opt.op.uneste_stalpi": "merge the columns",
  "struct.opt.op.gliseaza_stalp_plantat": "slide the planted column",
  "struct.opt.par.cu": "to",
  "struct.opt.par.pentru": "for",
  "struct.opt.par.b_cm": "the width b",
  "struct.opt.par.h_cm": "the depth h",
  "struct.opt.par.L_cm": "the side",
  "struct.opt.par.directie": "on direction",
  "struct.opt.par.pe_grinda": "on beam",
  "struct.opt.par.pe_grinda_unita": "on the merged beam of",
  "struct.opt.par.pozitie": "the position on the beam",
  "struct.opt.par.intre": "between",
  "struct.opt.par.nivel": "storey",

  "struct.rzv.rezumat_doi_pasi": "Step 6 sizes it before the model exists; Step 17 repeats the check against the real reactions, axis by axis.",
  "struct.rzv.col_verificarea": "Check",
  "struct.rzv.col_valoare": "Value",
  "struct.rzv.col_prag": "Threshold",
  "struct.rzv.neaplicabile": "Not applicable / no data:",
  "struct.rzv.tab_axe": "Axes",
  "struct.rzv.tab_global": "Global",
  "struct.rzv.tab_grinzi": "Foundation beams",
  "struct.rzv.tab_model": "Model",
  "struct.rzv.pasul_11_comb": "Step 11 — {n} combinations (fundamental + seismic)",
  "struct.rzv.latimi_talpii": "Base widths: {n} distinct sizes across the whole project",
  "struct.rzv.latimea_globala": "the global width of the foundation",
  "struct.rzv.fara_reactiuni": "no reactions",
  "struct.rzv.o_directie": "1 direction",
  "struct.rzv.doua_directii2": "2 directions",
  "struct.rzv.presiune_resoarte": "pressure from the spring reactions (Step 7)",
  "struct.rzv.pondere_long": "weight {w} — the projection of the seismic direction onto the length of the base",
  "struct.rzv.pondere_transv": "weight {w} — the projection of the seismic direction onto the width of the base",
  "struct.rzv.verificarea_varfului": "A check on the peak, not the mean: under a heavily loaded column the local pressure can exceed the threshold even where the axis mean passes.",
  "struct.rzv.lungimea_aferenta_resort": "with the foundation in the model: the tributary length of the spring under the node",
  "struct.rzv.margine_daca": "an edge case if the distance to the foundation outline is < 1.5·B",
  "struct.rzv.global_toata_cladirea": "Sliding, overturning, uplift, lift-off and the ground–structure loop — checked over the whole building, not on one axis.",
  "struct.rzv.delta_s_l": "Δs / L, over every pair of axes; the maximum is taken",
  "struct.rzv.reazema_teren_nu": "It bears on the ground, not on columns: the moment changes sign, so both the top and the bottom reinforcement are working.",
  "struct.rzv.max_min_statii": "max / min over 5 stations × each element × each combination",
  "struct.rzv.max_v_z": "max |V_z| over stations × combinations",
  "struct.rzv.o_minim": "Minimum Ø",
  "struct.rzv.grinzi_pat_resoarte": "Beams on a bed of springs (Winkler), with K_s from Step 6 — which is why the consistency of K_s is re-checked at the end.",
  "struct.rzv.resoarte_intinse": "Springs in tension (the base has lifted)",
  "struct.rzv.noduri_baza_axe": "Base nodes on axes",
  "struct.rzv.v_presiune_stalp": "the pressure under the column",
  "struct.rzv.v_desprindere": "lift-off of the base",
  "struct.rzv.v_tasare_relativa": "relative settlement",
  "struct.rzv.v_rasturnarea": "overturning",
  "struct.rzv.v_ridicarea": "lift-off of the column",
  "struct.rzv.v_strapungerea": "punching",
  "struct.rzv.v_taietoarea": "shear in the foundation beam",
  "struct.rzv.bucla_misca_doar": "The local loop moves only the foundation geometry (B, D_f, h); the structural geometry is moved by Step 19.",
  "struct.rzv.cu_cel_putin": "by at least",
  "struct.rzv.costul_fundatiei": "The cost of the foundation",
  "struct.rzv.geometria_gasita2": "The geometry found",
  "struct.rzv.f1_formula": "B_axis += 10 cm, up to 2.50 m",
  "struct.rzv.f3_formula": "D_f += 10 cm, up to 3.00 m",
  "struct.rzv.f5_formula": "L_axis += the length missing from the envelope",
  "struct.rzv.f6_formula": "B_axis −= 10 cm, as long as every check keeps passing",
  "struct.rzv.f7_formula": "D_f −= 10 cm, as long as every check keeps passing",
  "struct.rzv.li_pasul_6": "<b>Step 6</b> — pre-sizes the footing: B, D_f, H_c and a K_s from an empirically <i>estimated</i> weight. It cannot know the reactions: the model does not exist yet.",
  "struct.rzv.li_pasii_7_12": "<b>Steps 7…12</b> — the model is built ON that geometry (the Winkler bed of springs needs B, H_c and K_s to exist at all), and only then come the loads, the modes, the combinations and the real reactions.",
  "struct.rzv.bucla_nu_reruleaza": "The local loop <b>does not re-run the model</b>: the geometry found at Step 17 is evaluated with the reactions of the <i>previous</i> geometry. That is why «every check passes at the end of the search» is not the convergence criterion. The real criterion is <b>0 failed checks at iteration 0 of the search</b> — that is, on the geometry the model was actually built on. So that the chain converges instead of oscillating around the threshold, the search keeps a margin for itself (3 % by default), and the runs that follow continue from the modelled geometry, not from Step 6's minimum.",
  "struct.rzv.butonul_ruleaza_tot": "The <b>«Run all»</b> button walks 1…19 in order, once through each step — were it to pass through Step 6 twice, the numbering on screen could no longer be read as an order. The loop has not disappeared, it has merely been stretched across the chain: Step 17 adopts the geometry and the next run starts from it. If you want the loop closed all the way to a fixed point (reactions and geometry that stop changing), that is what Step 19 does, re-running the whole chain as many times as needed.",
  "struct.rzv.situatia_detectata": "detected situation: <b>{eticheta}</b> — {detaliu}",
  "struct.rzv.plaja_implicit": " · range {min}–{max} · default {implicit}",
  "struct.rzv.valoare_per_ax": "value per axis (empty = default):",
  "struct.rzv.n_trec": "{n} pass",
  "struct.rzv.n_nu_trec": "{n} fail",
  "struct.rzv.n_neaplicabile": "{n} not applicable",
  "struct.rzv.verdicte_divergente": "{n} verdicts that differ between thresholds",
  "struct.rzv.toate_trec_geometria": "Every Step 17 check passes on the current foundation geometry — {praguri}.",
  "struct.rzv.bucla_oprita_fara": "Step 6's loop stopped without solving them all",
  "struct.rzv.dupa_iteratii": "after {n} iterations: {oprire}.",
  "struct.rzv.urmeaza_depaseste": "What follows is beyond the foundation geometry — it continues from <b>Step 19</b> (changes in the superstructure, with the chain re-run).",
  "struct.rzv.bucla_rezolvat_tot": "<b>Step 6's loop has solved everything that depends on the foundation geometry</b> ({n} iterations). What is left is not in its catalogue — it is solved through <b>{pas}</b> (the ground–structure loop needs reactions recomputed on the new geometry).",
  "struct.rzv.bucla_oprita_manual": "Step 6's local loop is off ({motiv}), so the geometry was not searched automatically — the checks below stay on the geometry Step 6 proposed.",
  "struct.rzv.nu_e_in_catalog": "not in Step 6's loop catalogue — solved from Step 19",
  "struct.rzv.vezi": "→ see",
  "struct.rzv.verificarea_reactiuni_reale": "Checking on real reactions",
  "struct.rzv.comb_x_axe": "— {c} combinations × {a} axes",
  "struct.rzv.ax_fundatie_verifica2": "One axis = one run of foundation with its columns; an average over the whole building hides the edge axes, with their small vertical force and large moment.",
  "struct.rzv.click_rand_deschide2": "Each row is an axis; the values shown are those of the governing combination (the one giving the largest pressure). Clicking a row opens all of that axis's combinations.",
  "struct.rzv.latimi_rotunjite": "Each axis gets the width <b>its own load</b> asks for (the axes carry different tributary areas — a single width everywhere would be concrete that does no work), but a site cannot work with 10 different widths: setting out and formwork get complicated, and mistakes follow. So the required widths are rounded <b>up</b> to a set of at most {n} distinct sizes — <b>{lista} m</b> — chosen among the computed requirements so that the total concrete (Σ L·B) is a minimum. A design decision («at most 3 sizes in use»).",
  "struct.rzv.b_necesar_per_ax": "B required per axis = the smallest width on the 10 cm grid at which every check on that axis passes with the convergence margin, at the current reactions (eccentricity coefficient included). Cost difference against the previous geometry: {delta} €. Per axis: {detaliu}",
  "struct.rzv.per_ax_necesar_ales": "{ax}: needs {nec} → chosen {ales} m",
  "struct.rzv.th_ax": "Axis",
  "struct.rzv.th_dir": "Dir.",
  "struct.rzv.th_l_m": "L [m]",
  "struct.rzv.th_b_m": "B [m]",
  "struct.rzv.th_excentricitate": "Eccentricity",
  "struct.rzv.th_h_df": "H / Df [m]",
  "struct.rzv.th_acoperire": "Coverage",
  "struct.rzv.th_tasare_cm": "Settlement [cm]",
  "struct.rzv.th_grupare": "Group",
  "struct.rzv.th_prag": "threshold",
  "struct.rzv.th_tip": "type",
  "struct.rzv.centric": "centric",
  "struct.rzv.guv_paren": "(gov. {tip})",
  "struct.rzv.editat": "edited",
  "struct.rzv.v_p1_ce": "the largest pressure at the edge of the footing, by the spreadsheet's formula p₁ = ΣF_Z/A + ΣM/W, on the governing combination ({comb})",
  "struct.rzv.v_p1_regula": "{mult} × p_conv, where p_conv = {p} kPa is the conventional pressure corrected for B and D_f",
  "struct.rzv.v_np_ce": "the same maximum pressure, but computed with μ = {mu} from tab. E.1 — which accounts for the ground being unable to pull on the foundation",
  "struct.rzv.v_np_regula": "{tip} loading, {grupa} group → the multiplier from tab. I.5 applied to p_conv",
  "struct.rzv.fundamentala": "fundamental",
  "struct.rzv.seismica": "seismic",
  "struct.rzv.v_model_ce": "the largest pressure read DIRECTLY from the model: a ground spring's reaction divided by its own area (B × tributary length). It does not assume the footing is rigid — it reads it as the analysis gives it",
  "struct.rzv.v_model_regula": "the same threshold as the method: {mult} × p_conv",
  "struct.rzv.v_tasare_ce": "how far the ground goes down under this axis, computed with ITS own pressure from the quasi-permanent combination G + ψ₂·Q (settlement is a long-term serviceability state: neither the strength combination nor the seismic peak is taken)",
  "struct.rzv.v_tasare_regula": "the smaller of the method's limit and 80 mm — the code's maximum absolute settlement. The RELATIVE settlement between axes is checked separately (the Global tab); that is the criterion which cracks walls",
  "struct.rzv.ax_toate_combinatiile": "{ax} — every combination",
  "struct.rzv.talpa_detalii": " · footing {b} × {h} m at −{d} m · L = {l} m · A = {a} m² · W_long = {wl} m³ · W_transv = {wt} m³",
  "struct.rzv.presiunile_includ_kexc": "The pressures p₁, p_max NP and p model in the table already include k_exc = {k}; p_med (settlement) and p₂ stay unamplified.",
  "struct.rzv.verificarea_ax_fasia": "The check on the axis says whether the strip of footing, taken as a whole, presses too hard. But the load is not uniform along it: it comes in concentrated, through the columns. Here each column is looked at on its own — how hard it presses on the piece of footing under it, and whether it does not <b>punch through</b> it.",
  "struct.rzv.strapungerea_cedarea": "<b>Punching</b> is the failure in which the column «goes through» the footing like a plug, shearing a truncated cone around itself — the failure typical of slabs loaded at a point, and a brittle one: it happens suddenly, without warning. That is why it is not checked at the column face but on a perimeter further out, at 2d from it, where the shear crack actually reaches the surface.",
  "struct.rzv.formula_strapungere": "v_Ed = β · N_red / (u₁ · d)\nN_red = N_Ed − the ground reaction inside the perimeter\nβ = 1.15 interior columns / 1.5 edge ones (NP 112)\nu₁ = the perimeter at 2d,  d = the effective depth",
  "struct.rzv.th_xy": "x, y [m]",
  "struct.rzv.margine": "edge",
  "struct.rzv.interior": "interior",
  "struct.rzv.v_p_stalp_ce": "the local pressure under this vertical element: its largest vertical force divided by the piece of footing carrying it ({l} m × {b} m)",
  "struct.rzv.v_p_stalp_regula": "{mult} × p_conv, as in the check on the axis — but applied locally, where the load comes in concentrated",
  "struct.rzv.v_strapungere_ce": "the shear stress on the control perimeter at 2d from the column face, against what the concrete carries there without transverse reinforcement",
  "struct.rzv.v_strapungere_regula": "v_Rd,c = 0.12·k·(100·ρ₁·f_ck)^⅓, with β = {beta} ({poz} column) and N_red = {nred} kN after subtracting the ground reaction inside the perimeter",
  "struct.rzv.de_margine": "edge",
  "struct.rzv.pana_aici_bucata": "Up to here everything has been «piece by piece»: axes, columns. The checks below look at the building as one body: it <b>does not slide</b> sideways, it <b>does not overturn</b>, it <b>does not float</b> if there is water, the columns <b>are not pulled out</b> of the foundation and it <b>does not settle unevenly</b> enough to crack the walls. The last one — the ground–structure loop — checks whether the assumption the model was built on still holds after the model has run.",
  "struct.rzv.global_toata_cladirea2": "Sliding, overturning, uplift, lift-off and the ground–structure loop — checked over the whole building, not on one element. Hover over each card's title.",
  "struct.rzv.card_lunecare_sub": "H/R at {comb} · μ = {mu} · R_passive {r} kN",
  "struct.rzv.card_rasturnare_sub": "M_stab/M_overturn at {comb} · lever arm {brat} m · z_CM {z} m",
  "struct.rzv.subpresiune_plutire": "Uplift pressure (flotation)",
  "struct.rzv.ridicare_uplift": "Lift-off (uplift)",
  "struct.rzv.stalpi_cu_tractiune": "columns whose tension exceeds the ballast, out of {total}",
  "struct.rzv.tasare_relativa2": "Relative settlement",
  "struct.rzv.informativ": "informative",
  "struct.rzv.geometria_pasului_5": "the geometry of Step 5",
  "struct.rzv.reper_orientativ": "indicative benchmark {v} ‰ (does not block)",
  "struct.rzv.intre_perechea": " · between {a} and {b} ({l} m)",
  "struct.rzv.tasare_absoluta_max": " · max absolute settlement {v} cm",
  "struct.rzv.ks_model_cerut": "K_s in the model {model} → required by the real pressures {cerut} daN/cm³",
  "struct.rzv.ks_vine_din": "The model's K_s comes from: {sursa}",
  "struct.rzv.formula_lunecare": "H_d ≤ V'_d · μ + R_p,d\nμ = the friction coefficient (NP 112 tab. G.1, by the type of stratum)\nR_p,d = ½·K_p·γ·D_f²·L, taken at 50 % (prudent)",
  "struct.rzv.formula_rasturnare": "M_stab = 0.9 · ΣF_Z · arm   vs   M_ovt = 1.1 · ΣH · z_CM\narm = the distance from the centre of the footprint to the edge, along the force",
  "struct.rzv.formula_subpresiune": "V_dst,d = γ_w · h_water · A   ≤   G_stb,d = 0.9 · G_foundation",
  "struct.rzv.formula_ridicare": "tension = max(−N_min, 0)   ≤   ballast = 0.9 · (G_footing + G_backfill)",
  "struct.rzv.grinda_planseu_pe_dos": "A floor beam is loaded from ABOVE and supported at two points: it sags in the middle, so it stretches the bottom fibre in the span and the top fibre over the supports. A foundation beam is exactly the reverse — <b>the load comes from below</b>, from the ground reaction, and the «supports» are the columns, pulling down. The sign convention flips, and the top reinforcement becomes as important as the bottom one.",
  "struct.rzv.grinda_pat_continuu": "On top of that, the beam does not have two supports: it lies on a continuous bed of springs, so the moment changes sign several times along its length. «The end moment» therefore means nothing — what is reported is the <b>envelope</b>: the largest positive M, the largest negative M and the largest shear, wherever on the axis and in whichever combination they occur.",
  "struct.rzv.grinda_a_treia_diferenta": "A third difference: in foundation beams the aim is to <i>avoid</i> plastic deformation in an earthquake (NP 112 §II.7.5.2) — the substructure must stay elastic, the plastic hinges are steered into the superstructure. That is why the minimum reinforcement is stricter than in an ordinary beam, and why their stiffness is taken UNcracked in the model.",
  "struct.rzv.sursa_distanta_centre": "the method document asks for «the distance between the centroids of the cages», not H_c − c as in an ordinary section — a simplification taken deliberately, on the safe side",
  "struct.rzv.sursa_pasul_12_grinzi": "Step 12 — the table of foundation beams",
  "struct.rzv.validare_o_minim_14": "minimum Ø <b>14 mm</b> (NP 112 §II.7.5.2)",
  "struct.rzv.formula_rho_min": "ρ_min = max(0.50·f_ctm/f_yk ; 0.002) for beams designed for seismic action\n(0.26·f_ctm/f_yk for those not loaded by the earthquake)",
  "struct.rzv.formula_v_rd_c": "V_Rd,c = 0.12·k·(100·ρ_l·f_ck)^⅓ · b · d,  minimum 0.035·k^1.5·√f_ck · b·d",
  "struct.rzv.formula_etrieri": "needed if V > V_Rd,c;  ρ_w ≥ max(0.001 ; 0.08·√f_ck/f_yk)",
  "struct.rzv.th_as_jos": "A_s bottom [cm²]",
  "struct.rzv.th_as_sus": "A_s top [cm²]",
  "struct.rzv.th_as_min": "A_s min",
  "struct.rzv.th_etrieri": "Stirrups",
  "struct.rzv.grinda_talpa": " · footing {v} m",
  "struct.rzv.etrieri_necesari_rho": "needed (ρ_w ≥ {v}%)",
  "struct.rzv.nu": "no",
  "struct.rzv.consola_detaliu": "L_c = {lc} m · M = {m} kNm/m · A_s = {as} cm²/m",
  "struct.rzv.consola_panta": "slope: h_t = {h} m",
  "struct.rzv.armaturi_laterale": "{sus} · side bars at least Ø10/300 mm · stirrups Ø ≥ {phi} mm{dubli} · {referinta}",
  "struct.rzv.etrieri_dubli": ", double (b ≥ 400 mm)",
  "struct.rzv.rigiditatea_li": "<b>the stiffness is UNcracked</b> (1.0·EI, against 0.5·EI for beams and 0.7·EI for columns): in the superstructure we count on cracking and yielding to dissipate energy, in the substructure we want exactly the opposite;",
  "struct.rzv.sectiunea_li": "<b>the section is rectangular</b> B×H_c, with no active slab flange — there is no slab above the foundation;",
  "struct.rzv.resoarte_intinse2": "Springs in tension (footing lifted off)",
  "struct.rzv.resoarte_sub": "maximum at {comb} · {n} springs in the model",
  "struct.rzv.noduri_baza_axe2": "Base nodes on the axes",
  "struct.rzv.noduri_sub": "nodes at axis intersections (they split their load between footings) · {n} outside any axis",
  "struct.rzv.v_presiune_ax": "the pressure on the axis",
  "struct.rzv.v_lunecarea": "sliding",
  "struct.rzv.v_subpresiunea": "uplift pressure",
  "struct.rzv.bucla_locala_nu_rulat": "The local loop did not run ({motiv}).",
  "struct.rzv.cand_verificare_nu_trece": "When a check fails there are two ways to answer: change the foundation, or change the building. The second is expensive — it moves columns, thickens beams, redoes the whole calculation chain. The loop here first tries everything that can be solved <b>from the foundation alone</b>, at unchanged loads: a wider footing, a lower level, a deeper beam, stretches added where they are missing.",
  "struct.rzv.motivul_refoloseste": "Why it is allowed to reuse the reactions: a building does not «feel» 10 cm of extra concrete under it — the forces in the superstructure barely change. What does change is <i>the stiffness of the bed of springs</i>, and that is confirmed when the chain is re-run at Step 19.",
  "struct.rzv.alege_la_fiecare_pas": "<b>How it chooses</b>: at every step it computes, for each possible action, what it costs and how much closer to passing it gets — then takes the action with the best <b>cost per unit of exceedance solved</b>. Not the cheapest one: going 10 cm deeper is always cheap, but if it moves nothing it is money thrown away. The «alternatives» column shows exactly what it rejected, and at what price.",
  "struct.rzv.cand_opreste_totul_trece": "<b>When it stops</b>: when everything passes {rezerva}, or when the best remaining action costs more than the efficiency threshold ({prag} per unit) without solving anything. From there on, the answer is no longer in the foundation.",
  "struct.rzv.cu_cel_putin_rezerva": "with at least <b>{n} % margin</b>",
  "struct.rzv.exact_pe_prag": "(exactly on the threshold)",
  "struct.rzv.de_ce_rezerva": "<b>Why a margin and not the threshold exactly</b>: the loop does not re-run the model, so it works on the current chain's reactions. Were it to stop exactly on the threshold, the geometry would be left with <i>zero margin</i> — and when the chain is re-run the reactions shift by ~1 %, pushing the borderline checks back over the threshold. The chain would cycle around the admissible limit instead of converging. The {n} % target absorbs exactly that perturbation; <b>the verdict on screen stays the code one</b> (the margin is a design target, not a code threshold).",
  "struct.rzv.rezultat": "Result",
  "struct.rzv.picate_in_pasi": "failed checks, in {n} steps · stop: {oprire}",
  "struct.rzv.cost_beton_armat_simplu": "reinforced concrete {armat} · plain concrete {simplu} · ",
  "struct.rzv.de_la_cost": "from {v}",
  "struct.rzv.crestere_pct": " ({v} %)",
  "struct.rzv.eur_pe_ml": " · {e} €/lm over {l} m",
  "struct.rzv.geometria_de_la": "from B {b} · D_f {df} m — these are <b>the variables being searched</b>",
  "struct.rzv.axe_latite_local": " · {n} axes widened locally",
  "struct.rzv.h_derivat": "H = {h} m (from {h0} m) is <b>derived</b>: at given reactions it does not change the pressure, so it is not searched — it follows from the construction detail and from D_f",
  "struct.rzv.h_talpa_miscata": " · h_footing = {v} m (moved by F4, at punching)",
  "struct.rzv.latimi_lista": "{lista} — the rest stay at {b} m. Widening locally is far cheaper than widening everywhere: concrete goes only where it is really needed.",
  "struct.rzv.ax_egal_m": "{ax} = {b} m",
  "struct.rzv.f1_validare": "target: the pressure on the axis and under the columns. The most targeted action — concrete only where it is needed",
  "struct.rzv.f2_validare": "dearer than F1, but a uniform footing is simpler to set out and to pour on site",
  "struct.rzv.f3_validare": "helps with pressure, settlement, lift-off and sliding. Cheap at first, but the cost of excavation climbs with depth (battering, then shoring)",
  "struct.rzv.f4_formula": "H_c += 10 cm, capped at D_f − 5 cm",
  "struct.rzv.f4_validare": "the only action that solves punching and shear — neither widening nor going deeper touches them",
  "struct.rzv.f5_validare": "adds bearing area exactly where it is missing and ties the runs together",
  "struct.rzv.f6_validare": "it does not go below the minimum buildable width (the excavator bucket)",
  "struct.rzv.f7_nume": "F7 — raising the founding level",
  "struct.rzv.f7_validare": "it is not free: a smaller D_f lowers the conventional pressure (the C_D correction, NP 112 annex D) and lowers the soil ballast above the footing, so pressure, lift-off and sliding fail sooner. Floors: Step 6's minimum depth (frost / legal minimum) and D_f ≥ H_c + 5 cm, so the foundation beam fits in the trench. At every step the cheaper of F6 and F7 is chosen",
  "struct.rzv.n_cazuri#n": "1 case|{n} cases|{n} cases",

  // --- STRUCTURE · the guide to structural regimes -----------------------
  "struct.reg.gravitational": "Gravity",
  "struct.reg.meta_constructiv": "Detailing",
  "struct.reg.meta_forfecare": "Shear",
  "struct.reg.meta_drift": "Drift",
  "struct.reg.range_constructiv": "typically G … G+3",
  "struct.reg.range_gravitational": "typically G+4 … G+6",
  "struct.reg.range_forfecare": "typically G+6 … G+10",
  "struct.reg.range_drift": "typically G+8 and above",
  "struct.reg.ghid_eyebrow": "Implementation guide · automated structural design",
  "struct.reg.patru_regimuri_titlu": "Four regimes, four strategies",
  "struct.reg.punctul_de_plecare": "The starting point",
  "struct.reg.de_ce_patru_regimuri": "Why four regimes",
  "struct.reg.cladire_nu_limitata": "A building is not limited by «the earthquake» in general. It is limited by <i>one single thing</i> at a time, and that thing changes with height. An algorithm that does not know which constraint is active will spend computing effort and material in the wrong direction.",
  "struct.reg.formule_rho": "rho_grav   = 0.5 · n · g_grav / (nu_t · f_cd)          axial load limit\nrho_shear  = V / (A · v_Rd)                            shear capacity\nrho_drift  = 12 · I_req / (A · l_typ²)                 lateral stiffness\nrho_detail = 0.5 · (A / pitch²) · A_elem / A           minima from architecture\n\ngoverning  = argmax(rho_grav, rho_shear, rho_drift, rho_detail)",
  "struct.reg.tranzitie_doua": "{a} ({pa}%) and {b} ({pb}%) are within 15% of each other — check both scenarios before settling on a strategy.",
  "struct.reg.context_normativ": "Code context",
  "struct.reg.documentul_se_refera": "The document refers to <b>P 100-1/2013</b> (as amended by Order 2956/2019), <b>CR 2-1-1.1/2013</b> for walls, <b>NP 007-2025</b> for frames and <b>CR 6-2013</b> for masonry, alongside <b>SR EN 1998-1</b>. Every numerical value below must be read straight from the code tables before being put into code — here they are given as orders of magnitude and as a calculation structure.",
  "struct.reg.sageata_guverneaza": " ← governing",
  "struct.reg.pe_acest_proiect_guverneaza": "On this project the governing regime is",
  "struct.reg.sectiunea_deschisa_jos": "— its section is open below; the other three can be consulted from the buttons above.",
  "struct.reg.regim_nr_range": "Regime {nr} · {range}",
  "struct.reg.p100_2025_titlu": "P 100-1/2025 — the seismic code revision",
  "struct.reg.nu_codifica_valorile": "Do not hard-code the code values into the logic. Keep the spectrum, the behaviour factors, the <code>nu_d</code> limits and the drift limits in a versioned data layer, selectable per code edition. An increase in seismic action of the order discussed would move the thresholds between regimes by one to two storeys — the classifier stays valid, the thresholds need recalibrating. Check the current status of P 100-1/2025 before implementing it.",
  "struct.reg.rezumat": "Summary",
  "struct.reg.tabelul_de_decizie": "The decision table",
  "struct.reg.th_regim": "Regime",
  "struct.reg.rand_constructiv_structura": "a frame with columns hidden in the walls; confined masonry for houses",
  "struct.reg.rand_constructiv_parghie": "remove joints for as long as the beam fits above the door",
  "struct.reg.rand_constructiv_nu": "look for an optimal topology — there is none",
  "struct.reg.rand_grav_structura": "a frame, or a frame with a core for an open plan",
  "struct.reg.rand_grav_parghie": "more columns, or a higher concrete class",
  "struct.reg.rand_forf_parghie": "concrete class; spreading over more members",
  "struct.reg.rand_drift_structura": "walls, or a core with frames if the plan must stay open",
  "struct.reg.rand_drift_parghie": "lengthen before you thicken; push towards the perimeter",
  "struct.reg.rand_drift_nu": "bet on making it more flexible — that does not work at a large <code>T_C</code>",
  "struct.reg.trei_constante_titlu": "Three constants to calibrate before trusting the result",
  "struct.reg.constanta_ltyp": "<code>l_typ</code> — the typical length of continuous wall the architecture offers. It enters <code>rho_drift</code> squared, so a 30% error gives a 70% error in the verdict.",
  "struct.reg.constanta_vrd": "<code>v_Rd</code> — the target unit shear stress. Calibrate it on real projects, not on a textbook.",
  "struct.reg.constanta_concentrare": "the factor concentrating drift at the top — about 1.6 for a pure cantilever, 1.2 for a dual system, 1.0 for a frame.",
  "struct.reg.surse": "Sources",
  "struct.reg.ref_p100": "P 100-1/2013, Seismic design code — Part I, as amended by Order 2956/2019.",
  "struct.reg.ref_cr2": "CR 2-1-1.1/2013, Design code for buildings with reinforced concrete structural walls.",
  "struct.reg.ref_np007": "NP 007-2025, Design code for reinforced concrete frame structures, Official Gazette no. 630 bis / 4 July 2025.",
  "struct.reg.ref_comentarii_cap5": "Commentary on chapter 5 of P 100-1/2013 (table 5.1, <code>alpha_u/alpha_1</code> values, the <code>k_w</code> factor).",
  "struct.reg.ref_limite_nu": "The normalised axial force limits in EN 1998-1 for ductile walls and columns.",
  "struct.reg.ref_zonare": "The seismic zoning of Romania and the corner period values; the impact of the Eurocode 8 revision on design spectra.",
  "struct.reg.ref_zhang": "Zhang, Y., Mueller, C. — Shear wall layout optimization for conceptual design of tall buildings, <i>Engineering Structures</i> 140 (2017) 225–240.",
  "struct.reg.ref_doua_etape": "Two-stage optimisation, topology then sizing, for tall reinforced concrete structures.",
  "struct.reg.ref_tabu": "Wall layout optimisation by tabu search, coupled with structural analysis software.",
  "struct.reg.ref_bim": "Wall layout optimisation starting from BIM models, with a genetic algorithm.",
  "struct.reg.ref_aicps": "AICPS's position on the draft P 100-1/2025.",
  "struct.reg.ref_oar": "The Romanian Order of Architects' position on the draft P 100-1/2025.",
  "struct.reg.cererea_cubul": "The stiffness demand grows with the <i>cube</i> of the number of storeys, because the force grows linearly with the mass, the lever arm grows with the height, and the allowed displacement grows with the height too. At two storeys the product is small. What remains are three conditions that have nothing to do with the earthquake:",
  "struct.reg.efectul_de_cutie": "The box effect.",
  "struct.reg.ce_spune_normativul": "What the code says",
  "struct.reg.th_subiect": "Subject",
  "struct.reg.densitate_minima_p": "CR 6-2013 + the tables in P 100-1/2013 impose a <b>minimum wall density</b> <code>p%</code> in each direction, depending on the acceleration and the number of storeys",
  "struct.reg.en1996_mai_putin": "EN 1996 + EN 1998-1 ch. 9; less detailed rules, many left to the national annex",
  "struct.reg.valoare_tipica_p": "Typical <code>p%</code> value",
  "struct.reg.raport_goluri_plinuri": "a limited ratio between the length of the openings and of the solid parts, different for internal and external walls",
  "struct.reg.reguli_geometrice_minime": "minimum geometric rules for masonry walls",
  "struct.reg.cadre_de_beton": "Concrete frames",
  "struct.reg.np007_impreuna": "NP 007-2025 (new since July 2025), together with P 100-1",
  "struct.reg.densitatea_de_patru_ori": "for confined masonry at a_g = 0.30g is nearly four times what you get on a G+10 concrete block. It is not an inconsistency: masonry's shear strength is an order of magnitude below concrete's, so you compensate with area.",
  "struct.reg.algoritm_constructiv": "INPUTS    the architectural wall graph, persistent across all storeys\n          the clear height, the door opening height\nOUTPUTS   the list of members (position, I/L/T/+ shape, sizes), the list of beams\n\n1. nodes = intersections + corners + free ends + changes of direction\n2. merge nodes closer than 1.5 m\n3. member shape = f(the number of directions entering the node)\n4. length of each wing = max(2·thickness, the capacity-hierarchy requirement)\n5. beams on every edge of the graph;  h_beam = max(L/12, the minimum)\n6. CHECKS that can actually fail:\n     - short columns (partial openings tying the column over part of its height)\n     - regularity in plan (house plans are almost always irregular)\n     - the box effect: the distance between transverse walls",
  "struct.reg.optimizarea_eliminare": "Optimisation means elimination, not sizing",
  "struct.reg.nu_exista_spatiu_cautare": "There is no interesting search space for positions: the positions are given by the architecture. The only real variable is <i>how many joints you can remove</i>. Each removal lengthens a beam; you stop when the beam no longer fits above the door.",
  "struct.reg.problema_pruning": "with a geometric constraint, not one of allocating stiffness. It is solved greedily, in a few milliseconds, and converges to the same solution whatever the starting order, provided you sort correctly.",
  "struct.reg.hol": "hallway",
  "struct.reg.densitatea_de_6": "The density of <b>6% per direction</b> for confined masonry at a_g = 0.30g is nearly four times what you get on a G+10 concrete block. It is not an inconsistency: masonry's shear strength is an order of magnitude below concrete's, so you compensate with area.",
  "struct.reg.problema_de_pruning": "It is a problem of <b>pruning on a graph</b> with a geometric constraint, not one of allocating stiffness. It is solved greedily, in a few milliseconds, and converges to the same solution whatever the starting order, provided you sort correctly.",
  "struct.reg.formula_nud": "nu_d = N_Ed / (A_c · f_cd)        N_Ed  the axial force in the seismic group\n                                  A_c   the area of the concrete section\n                                  f_cd  the design strength of the concrete",
  "struct.reg.fractiune_adimensionala": "It is a dimensionless fraction: how close the section is to crushing, from weight alone. It is not a strength limit — the section does not fail at <code>nu_d = 0.7</code>. It is a limit on <b>rotation capacity</b>. The compressed zone of the section grows in proportion to <code>nu_d</code>, and the curvature the section can reach before the concrete crumbles falls in inverse proportion to the depth of that zone. An over-compressed member breaks brittly instead of bending.",
  "struct.reg.svg_suprafata_aferenta": "tributary area × storeys  →  axial force  →  minimum section",
  "struct.reg.svg_de_niveluri": "of storeys",
  "struct.reg.figcap_aria_aferenta": "The tributary area stays the same; what changes is how many floors pile up above. To keep <code>nu_d</code> constant, the concrete area must grow at the same rate.",
  "struct.reg.limitele_nud_coincid": "The <code>nu_d</code> limits are among the few values where Eurocode and Romanian practice substantially agree, because P 100-1/2013 was explicitly harmonised with EN 1998-1 in this area.",
  "struct.reg.th_element": "Member",
  "struct.reg.perete_ductil": "Ductile wall",
  "struct.reg.discontinuitate_titlu": "A discontinuity to implement correctly",
  "struct.reg.element_devine_perete": "A member becomes a «wall» when the ratio of its long side to its short one exceeds <b>4</b>. At that point the <code>nu_d</code> limit tightens from 0.55 to 0.35 — by 36%. A 30×115 member is a column; 30×125 is a wall and can become infeasible while having gained almost nothing in capacity. Do not treat the threshold as a law of physics: <b>put the discontinuity in the cost function and let the optimiser avoid it where it really pinches.</b>",
  "struct.reg.diferenta_reala_eurocode": "The real difference from Eurocode is not in the limit but in <i>the force you put in the numerator</i>: it is taken from the seismic group, not from the fundamental group of vertical loads, and the worst case is sought because the earthquake changes sign.",
  "struct.reg.de_implementat": "To implement",
  "struct.reg.algoritm_gravitational": "INPUTS    tributary areas per member, number of storeys, concrete class,\n          the chosen ductility class\nOUTPUTS   the minimum admissible section for each member\n\nnu_target = 0.35   # not the limit! leave margin for the seismic demand\nA_min     = N_Ed / (nu_target · f_cd)\n\n# the choice of shape matters less than the area: prefer a square\nb = h = ceil(sqrt(A_min), catalogue_step)\n\n# check WHERE the member changes class\nif h / b > 4: apply the wall limit, not the column one",
  "struct.reg.strategia_de_optimizare": "The optimisation strategy",
  "struct.reg.parghia_adauga_stalp": "— add a column and each one carries less. It costs only extra formwork and is almost always cheaper than enlarging the existing sections.",
  "struct.reg.parghia_clasa_betonului": "— <code>f_cd</code> is in the denominator, so going from C25/30 to C35/45 cuts the required area by about 30% for a far smaller cost per cubic metre. Usual at the base of tall buildings: different classes over height bands.",
  "struct.reg.forfecare_intro": "The members are stiff enough, but the total section is too small for the horizontal force that must be carried into the foundation. The wall does not bend too much — it cracks diagonally. (Typically G+6 … G+10, or any height with few walls.)",
  "struct.reg.svg_fisuri_orizontale": "horizontal cracks, ductile",
  "struct.reg.svg_scurt_hl": "short · h/l < 2",
  "struct.reg.svg_fisuri_diagonale": "diagonal cracks, brittle",
  "struct.reg.marimea_de_control_v": "The controlling quantity is the unit shear stress, <code>v = V / (b · l)</code>. It depends on <b>area</b>, not on shape. That is why, when shear governs, the rule «few and long» no longer applies: two 3 m walls give exactly the same area as one 6 m wall, but four times less stiffness — which is irrelevant if stiffness is not the problem.",
  "struct.reg.forta_dimensionare_majorata": "The design shear force is not the one from the analysis. It is amplified, so that the wall fails for certain in bending and not in shear, accounting for the real overstrength of the base section and for the contribution of the higher modes of vibration. It can mean <b>1.5 to 3 times</b> the computed force. If your engine designs for the force from the analysis, it under-designs systematically.",
  "struct.reg.cr2_prevederi_proprii": "CR 2-1-1.1/2013, with its own analysis and detailing provisions, plus the annex with a simplified procedure",
  "struct.reg.en1998_cap5_impreuna": "EN 1998-1 ch. 5 together with EN 1992-1-1; there is no separate code",
  "struct.reg.kw_se_aplica": "it applies; <code>alpha_0 = Σh_wi / Σl_wi</code>, and <code>k_w = 1</code> if <code>alpha_0 ≥ 2</code>, otherwise <code>k_w = (1+alpha_0)/3</code>, minimum 0.5",
  "struct.reg.acelasi_mecanism_dual": "the same mechanism, applied to wall systems and to wall-equivalent dual systems",
  "struct.reg.grinzile_cuplare_30": "the coupling beams must take, through indirect action, at least <b>30%</b>",
  "struct.reg.criteriu_echivalent_momente": "an equivalent criterion for reducing the base moments",
  "struct.reg.grinzi_cuplare_scurte": "Short coupling beams",
  "struct.reg.armare_carcase_diagonale": "diagonal cages when the span-to-depth ratio is small and the shear force exceeds the threshold",
  "struct.reg.factorul_kw_bucla": "The <code>k_w</code> factor is how both codes penalise exactly the situation of this regime: with many short, thick walls you get less overall ductility, hence a smaller behaviour factor, hence a larger design force. It is a positive feedback loop that has to be closed inside the algorithm, not evaluated once.",
  "struct.reg.algoritm_forfecare": "INPUTS    V in each direction, the list of candidate walls, the concrete class\nOUTPUTS   the total area and how it is distributed\n\n1. v_target = 1.5 … 2.5 MPa    (above that, transverse reinforcement dominates)\n2. A_required = V_amplified / v_target\n3. distribute over members of MEDIUM length, not maximum\n4. recompute alpha_0 = Σh / Σl  ->  k_w  ->  q  ->  V     # a loop!\n5. repeat until q settles (typically 2-3 iterations)",
  "struct.reg.transferul_in_perete": "The force collected by the floor enters the wall over its length: <code>V_wall / (l_w · h_slab)</code> must stay below the capacity of the interface. With few long walls this stress rises fast and forces collectors or a local thickening of the slab.",
  "struct.reg.drift_intro": "The building has enough strength but bends too much. The members do not fail; everything attached to them degrades — joinery, partitions, façade, services — and second-order effects become significant. (Typically G+8 and above; dominant in Bucharest.)",
  "struct.reg.factor": "Factor",
  "struct.reg.se_verifica_drift": "What is checked is the <b>interstorey drift</b>: how far one floor moves relative to the one below it. Not the total displacement at the top. A tower leaning rigidly like a mast breaks nothing; a building in which each storey slides against the next breaks every partition wall.",
  "struct.reg.cele_doua_verificari_pre": "SLS   d_r = nu · q · d_re  ≤  the limit     nu = 0.5\n      frequent earthquake, protection of non-structural components\n\nULS   d_r = c · q · d_re   ≤  0.025 · h\n      design earthquake, avoiding collapse",
  "struct.reg.dre_deplasarea": "<code>d_re</code> is the displacement from the elastic analysis with the reduced spectrum; multiplying by <code>q</code> restores the real displacement. The factor <code>c</code> in the ULS check is a peculiarity of the 2013 edition: it accounts not only for the period of the structure and the corner period, but also for the behaviour factor used in design.",
  "struct.reg.formula_ireq": "I_req = 11 · V · H³ / (60 · E_c · k_cr · D_top)       cantilever, triangular load\n                                                      k_cr ≈ 0.5 for a cracked section\nrho_drift = 12 · I_req / (A · l_typ²)",
  "struct.reg.cererea_n3_oferta_l3": "One thing follows from this relation and drives the whole optimisation: demand grows with <code>n³</code>, and supply grows with <code>l³</code>.",
  "struct.reg.svg_dublare_beton": "each doubling of the number of walls costs about 59% more concrete",
  "struct.reg.figcap_eficienta_patratic": "A wall's stiffness grows with <code>l³</code>, its cost only with <code>l</code>, so the benefit/cost ratio goes with <code>l²</code>. The same calculation shows that lengthening is exactly <b>three times</b> more efficient than thickening.",
  "struct.reg.perioada_de_colt": "The corner period <code>T_C</code>",
  "struct.reg.trei_valori_teritoriu": "three values across the country: <b>0.7 / 1.0 / 1.6 s</b>",
  "struct.reg.uzual_04_08": "usually 0.4 – 0.8 s depending on the ground type",
  "struct.reg.palierul_pana_16": "the plateau of maximum acceleration stretches to 1.6 s, so",
  "struct.reg.structurile_flexibile_aluneca": "flexible structures slide down the descending branch and attract a smaller force",
  "struct.reg.bucuresti_ag_030": "Bucharest a_g = 0.30g, mean return period 225 years",
  "struct.reg.parametru_national_475": "a national parameter, usually referred to 475 years",
  "struct.reg.in_restul_europei": "In the rest of Europe there is a legitimate strategy: make the structure more flexible, the period grows, the seismic force drops.",
  "struct.reg.valorile_alpha": "The <code>alpha_u/alpha_1</code> values can be taken from the table for ordinary cases: for frames, 1.15 at one storey, 1.25 for several storeys with a single bay, 1.35 for several bays; for wall systems, 1.00 with only two walls per direction, 1.15 with more, 1.25 for coupled walls and for wall-equivalent dual structures.",
  "struct.reg.algoritm_drift": "INPUTS    V, H, A, l_typ, concrete class, the drift limit\nOUTPUTS   I_req, the allocation over positions, the resulting n\n\n1. I_req from the cantilever formula, fully in EACH direction\n   (it is not split between X and Y — each direction takes the whole force)\n2. subtract the contribution of the free members: cores, party walls, basement walls\n   -> typically 30-60 % of the budget is already covered\n3. sort the candidates by the efficiency score\n       score = l_max² · (1 + (d/l_s)²)        d = the arm from the centre of mass\n4. fill greedily, each candidate at its MAXIMUM length\n5. n = how many got in.  n is NOT an input of the algorithm.\n6. constraints that force n higher:\n       a member's maximum share of shear force  ≤ 0.35\n       transfer through the diaphragm into the wall  ≤ the interface capacity\n       uplift at the foundation                      ≤ the raft capacity",
  "struct.reg.metoda_ground_structure": "The ground structure method",
  "struct.reg.amprenta_discretizata": "The building's footprint is discretised into a grid of quadrilaterals; each edge of the grid becomes a potential wall, activated or not. The topology of the whole structure is then encoded as a <b>binary string</b>, which turns a continuous geometric problem into a discrete combinatorial one.",
  "struct.reg.svg_1_perete_activat": "1 = wall activated",
  "struct.reg.svg_toate_constrangerile": "every constraint (drift, torsion,",
  "struct.reg.marimea_de_control_nud": "The controlling quantity is called",
  "struct.reg.constrangerile_par_neliniare": "The eccentricity and torsion constraints look non-linear, because they depend on the centre of stiffness, which in turn depends on the solution. But if you take the moments about the <b>centre of mass</b>, which is known in advance, they become linear in the binary variables:",
  "struct.reg.arhitectura_pipeline": "candidate generation  from the architecture + the basement grid   (once)\n   ↓\ndiscrete optimisation MILP / CP-SAT on the linear constraints    (seconds)\n   ↓\nsurrogate model       rigid diaphragms, 3 DOF per storey         (milliseconds)\n   ↓  cut if a non-linear constraint fails\nfinite elements       only on the best N solutions               (minutes)\n   ↓\nreinforcement and detailing",
  "struct.reg.limiteaza_fapt_cladire": "What actually limits a building, what the solution looks like in each case, what differs between the Romanian code and the Eurocode, and which optimisation methods work. The regime that comes out of this step's own calculations is highlighted and preselected below.",
  "struct.reg.singur_lucru": "a single thing",
  "struct.reg.metoda_identificare_expr": "The identification method: express every constraint as the same quantity — the area of vertical elements required, referred to the floor area, on a single direction — and take the maximum. The largest value names the regime.",
  "struct.reg.cele_patru_marimi": "The four comparable quantities",
  "struct.reg.daca_doua_valori": "If two values are within 15% of each other you are in a transition zone: check both scenarios before fixing the strategy.",
  "struct.reg.zona_tranzitie_proiect": "A transition zone on this project",
  "struct.reg.urmarit": "To watch",
  "struct.reg.cadrul_normativ_miscare": "The code framework is moving",
  "struct.reg.doua_schimbari_afecteaza": "Two changes bear directly on any engine built on P 100-1/2013.",
  "struct.reg.aprobat_prin_ordin": "Approved by order on 23 June 2025 and published in the Official Gazette no. 630 bis of 4 July 2025. It replaces the previous edition and covers beams, columns, beam–column joints, substructures and floors acting as horizontal diaphragms. It applies both to new buildings and to intervention works on existing ones that use reinforced concrete frames. It is used together with P 100-1 and with CR 0.",
  "struct.reg.aflat_proces_avizare": "Under approval, as part of implementing the national strategy for seismic risk reduction. The draft proposes an increase in the design seismic action. Both the Association of Structural Design Engineers and the Romanian Order of Architects have submitted critical positions; AICPS cites studies presented at the Prefbeton 2024 and AICPS 2025 conferences according to which the consumption of materials in foundations and in the seismic-resisting vertical elements would rise by 30% to 200%, and holds that approving it at that point would be untimely.",
  "struct.reg.implicatie_arhitectura_s": "An implication for the software architecture",
  "struct.reg.structura_tipica": "The typical structure",
  "struct.reg.parghia_functioneaza": "The lever that works",
  "struct.reg.sa_faci": "What NOT to do",
  "struct.reg.cazul_tau": "your case",
  "struct.reg.sa_lungesti_sectiunile": "to lengthen the sections",
  "struct.reg.nucleu_plus_pereti": "a core plus walls of medium length",
  "struct.reg.sa_concentrezi_putini": "to concentrate it in a few long walls",
  "struct.reg.testul_validare_ruleaza": "The validation test: run the classifier on five to ten already-built projects and check whether its verdict matches what the engineer actually sized. If it does not, the problem is in the calibration, not in the structure of the algorithm.",
  "struct.reg.referinte": "References",
  "struct.reg.sr_en_1998": "SR EN 1998-1 / EN 1998-1:2004, Eurocode 8, ch. 5 — specific rules for concrete buildings.",
  "struct.reg.comparatie_a_factorilor": "A comparison of behaviour factors between P 100-1 and SR EN 1998-1 for reinforced concrete wall structures.",
  "struct.reg.densitatea_minima_pereti": "The minimum wall density for confined masonry, per the tables of P 100-1/2013 and CR 6-2013.",
  "struct.reg.document_lucru_echipa": "A working document for the development team. The numerical values are given as orders of magnitude and as a structure of calculation; before implementation they are read straight from the edition of each code in force. It does not replace checking by a practising structural engineer.",
  "struct.reg.guverneaza_proiect": "governs on this project",
  "struct.reg.guverneaza_aici": "does not govern here",
  "struct.reg.nimic_seismic_strange": "Nothing seismic is binding. The stiffness demand computed comes out tens of times below what the minimum detailing rules give anyway. The structure is dictated entirely by the geometry of the architecture.",
  "struct.reg.intampla_fizic": "What happens physically",
  "struct.reg.rezemarea_planseului": "Supporting the floor.",
  "struct.reg.fara_grinzi_vizibile": "With no visible beams, the slab has to find a support every 4–5 m.",
  "struct.reg.ierarhizarea_capacitatii": "Capacity hierarchy at the joints.",
  "struct.reg.grinda_trebuie_sa": "The beam has to yield before the column. With 25×50 beams framing into a joint, a 30×30 column cannot reach the capacity required.",
  "struct.reg.peretii_lungi_trebuie": "Long walls have to be braced transversely, or they buckle out of plane.",
  "struct.reg.arata_structura": "What the structure looks like",
  "struct.reg.camera": "room",
  "struct.reg.colt_l": "corner: L",
  "struct.reg.capat_dreptunghi": "end: rectangle",
  "struct.reg.intersectie_t": "junction: T or +",
  "struct.reg.elementele_urmeaza_nodur": "The elements follow the nodes of the architecture.",
  "struct.reg.grosimea_fiecarui_elemen": "The thickness of each element = the thickness of the wall it sits in, so nothing projects into the room. The shape comes from how many directions meet at the node.",
  "struct.reg.aici_cele_doua": "Here the two code systems part company most clearly, because Romania has a prescriptive code dedicated to low masonry buildings and the Eurocode has no equally detailed equivalent.",
  "struct.reg.romania": "Romania",
  "struct.reg.zidarie_confinata": "Confined masonry",
  "struct.reg.densitate_minima_pereti": "minimum wall density",
  "struct.reg.6_0_directie": "6.0% / direction (3 storeys, a_g = 0.30g)",
  "struct.reg.parametru_national": "— (a national parameter)",
  "struct.reg.goluri_perete": "Openings in a wall",
  "struct.reg.6_directie": "6% per direction",
  "struct.reg.cate_noduri_poti": "how many nodes you can take out",
  "struct.reg.pruning_graf": "pruning on a graph",
  "struct.reg.te_limiteaza_greutatea": "You are limited by the self-weight of the building, not by the earthquake. The sections have to be large enough that the concrete is not over-compressed before the earthquake even starts.",
  "struct.reg.forta_axiala_normalizata": "the normalised axial force",
  "struct.reg.aceeasi_suprafata": "the same area",
  "struct.reg.aferenta_alt_numar": "tributary, a different number",
  "struct.reg.sectiunea_creste_liniar": "The section grows linearly with the number of storeys.",
  "struct.reg.stalp": "Column",
  "struct.reg.conteaza_doar_aria": "Only the area counts, and an elongated element costs the same concrete as a square one of equal area, but consumes more side formwork and risks crossing the classification threshold above.",
  "struct.reg.perete_solicitat_lateral": "A laterally loaded wall can fail in two completely different ways:",
  "struct.reg.forfecarea": "shear does not",
  "struct.reg.avertizeaza_inainte": "warn beforehand",
  "struct.reg.sa_cedeze": "to fail",
  "struct.reg.doua_mecanisme_doua": "Two mechanisms, two consequences.",
  "struct.reg.cedarea_incovoiere_progr": "Failure in bending is progressive and absorbs energy. Failure in shear is sudden. The whole of seismic design is organised to force the first one.",
  "struct.reg.amplificarea_fortei_taie": "The amplification of the shear force",
  "struct.reg.1_5_pana": "1.5 to 2.5 times",
  "struct.reg.cod_dedicat_peretilor": "A code dedicated to walls",
  "struct.reg.pereti_scurti": "for short walls",
  "struct.reg.definitia_peretelui_cupl": "The definition of a coupled wall",
  "struct.reg.momentele_baza_montantil": "of the moments at the base of the piers",
  "struct.reg.aceeasi_solutie": "the same solution",
  "struct.reg.clasa_beton_inaintea": "Concrete class before quantity.",
  "struct.reg.capacitatea_forfecare_cr": "Shear capacity grows with the strength of the concrete; moving up a class is almost always cheaper than larger sections, and it consumes no architectural space.",
  "struct.reg.imparte_concentra": "Spread, do not concentrate.",
  "struct.reg.aria_aditiva_deci": "Area is additive, so you gain nothing from very long elements, but you lose at the foundations and at the diaphragm.",
  "struct.reg.verifica_transferul_pere": "Check the transfer into the wall.",
  "struct.reg.deplasarea_relativa_nive": "the storey drift",
  "struct.reg.cele_doua_verificari": "The two checks in P 100-1",
  "struct.reg.sursa_cererii_rigiditate": "The source of the stiffness demand, in closed form:",
  "struct.reg.lungimea_elementelor_bat": "The length of the elements beats their number.",
  "struct.reg.acelasi_i_beton": "the same ΣI · concrete in walls, per storey",
  "struct.reg.2_pereti_8": "2 walls × 8.00 m",
  "struct.reg.4_pereti_6": "4 walls × 6.35 m",
  "struct.reg.8_pereti_5": "8 walls × 5.04 m",
  "struct.reg.16_pereti_4": "16 walls × 4.00 m",
  "struct.reg.eficienta_scade_patratic": "Efficiency falls with the square of the shortening of the wall.",
  "struct.reg.aici_cele_mari": "Here lie the largest differences between Romania and the Eurocode, and they all run the same way: the Romanian earthquake is dominated by the intermediate-depth Vrancea events, which produce motions rich in long periods.",
  "struct.reg.bucuresti_are_1": "; Bucharest has 1.6 s",
  "struct.reg.consecinta": "The consequence",
  "struct.reg.flexibilizarea_reduce_fo": "making it more flexible does not reduce the force",
  "struct.reg.acceleratia_proiectare": "The design acceleration",
  "struct.reg.q_cadre_pereti": "q — frames, coupled walls, dual, DCH",
  "struct.reg.aceleasi_dcm": "the same, DCM",
  "struct.reg.pereti_zvelti_necuplati": "Slender uncoupled walls, DCH",
  "struct.reg.neregularitate_verticala": "Irregularity in elevation",
  "struct.reg.q_reduce_20": "q is reduced by 20%",
  "struct.reg.inseamna_algoritm": "What that means for an algorithm",
  "struct.reg.bucuresti_aceasta_strate": "In Bucharest this strategy does not work.",
  "struct.reg.structura_perioada_1": "A structure with a period of 1.2 s receives the same spectral acceleration as one at 0.3 s. You pay the displacements without gaining anything in force. Any heuristic taken from the western European literature has to be checked from this angle.",
  "struct.reg.acesta_singurul_regim": "This is the only regime with a rich search space, and the only one studied systematically in the literature. The methods that keep appearing:",
  "struct.reg.sir_binar": "a binary string",
  "struct.reg.retea_muchii_candidate": "a network of candidate edges",
  "struct.reg.bit_per_muchie": "one bit per edge",
  "struct.reg.0_fara_perete": "0 = no wall",
  "struct.reg.rezistenta_accesibilitat": "strength, accessibility) become",
  "struct.reg.functii_vector": "functions of this vector",
  "struct.reg.structura_baza": "The base formulation.",
  "struct.reg.formularea_publicata_zha": "The formulation published by Zhang and Mueller (2017) minimises the weight of the structure with simultaneous constraints on torsion, bending strength, shear strength, drift, and preserving the openings and the access.",
  "struct.reg.foloseste_peste_structur": "What is used on top of the base formulation",
  "struct.reg.rezolva_raporteaza": "What it solves and what is reported",
  "struct.reg.cea_raspandita_alegere": "The most widespread choice for topology. Variants with directed crossover are also used, exploiting the fact that good solutions share features.",
  "struct.reg.cautare_tabu_functie": "Tabu search with a penalty function",
  "struct.reg.aplicata_cod_binar": "Applied to the binary code, with drift and the period ratio treated as penalties in the objective, coupled to analysis software through its programming interface.",
  "struct.reg.doua_etape_topologie": "Two stages: topology, then sizing",
  "struct.reg.intai_algoritm_genetic": "First a genetic algorithm for the topology, then optimality criteria for the sections. On a tall-building case, reductions of 18–24% in material cost and emissions were reported.",
  "struct.reg.extragere_candidati_bim": "Extracting candidates from BIM",
  "struct.reg.pozitiile_posibile_peret": "The possible wall positions are extracted automatically from the architectural model, then series of analyses are run through the analysis program's interface. It removes the manual step of generating variants.",
  "struct.reg.modele_surogat_invatate": "Learned surrogate models",
  "struct.reg.retele_neuronale_graf": "Graph neural networks trained to predict displacements, storey drift and natural period; savings of about 90% of the computing time against finite element analysis are reported. Separately, networks trained on a base of 165 buildings built in Chile predict wall thickness and length from the architectural plans.",
  "struct.reg.simplificarea_face_probl": "The simplification that makes the problem linear",
  "struct.reg.centrul_masa": "the centre of mass",
  "struct.reg.coeficientii_constante_g": "The coefficients are geometric constants known before the optimisation. The whole topology stage thus becomes an integer linear program, solvable exactly — an alternative to metaheuristics, and one that can be combined with them: the exact solution on the simplified model, then refinement on the full one.",
  "struct.reg.arhitectura_recomandata": "The recommended architecture of the loop",
  "struct.reg.ratiunea_impartirii_meta": "The reason for the split: metaheuristics need thousands of evaluations. If each evaluation means a full finite element analysis, the loop does not close in reasonable time. The surrogate model breaks that dependency, and the full analysis is kept for validation.",

  // --- STRUCTURE · Step 3, the structural strategy -----------------------
  "struct.strateg.decide": "How is it decided?",
  "struct.strateg.constrangere_cere_anumit": "Each constraint asks for a certain amount of concrete, expressed as a fraction ρ of the floor area. All four are computed on the same data; the greediest one wins.",
  "struct.strateg.strategia_structurii_cad": "The structural strategy (frame / core / walls) depends on WHAT binds you: if drift governs you need long elements at the edges; if shear — total area; if gravity — thicker sections; if nothing seismic — the architecture decides.",
  "struct.strateg.masoara_l_typ": "How is l_typ measured?",
  "struct.strateg.iau_doar_peretii": "Only the walls that persist across all storeys are taken (Step 2), they are cut at every door/window opening (from any storey) and «free runs» remain. l_typ = the mean of the 6 longest runs on the critical direction.",
  "struct.strateg.alocarea_elementelor_str": "Allocating the structural elements will use exactly the long runs — hence the mean of the long ones, not the median of all (which would underestimate the budget). The direction with the smaller l_typ is the one critical for drift.",
  "struct.strateg.h_inaltimea_totala": "H — the total height",
  "struct.strateg.formula_h_n": "Formula: H = n · h_st — the number of storeys times the mean storey height.",
  "struct.strateg.h_intra_puterea": "H enters the bending calculation to the third power (a building twice as tall bends eight times as much at the same stiffness).",
  "struct.strateg.corectia_modul_vibratie": "λ — the correction for the mode of vibration",
  "struct.strateg.0_85_cladiri": "0.85 for buildings over 2 storeys, otherwise 1.0.",
  "struct.strateg.cladirile_inalte_toata": "In taller buildings not all the mass sways at once in the first mode — the effective force is about 15% below the total mass × acceleration. It is the same λ correction as in the equivalent static force method (P100-1 / Eurocode 8).",
  "struct.strateg.c_s_fractiune": "c_s — what fraction of the weight pushes sideways",
  "struct.strateg.formula_c_s": "Formula: c_s = (a_g · β0 / q) · λ. It says what percentage of the building's weight becomes a horizontal force in the design earthquake.",
  "struct.strateg.a_g_tare": "a_g is how hard the ground shakes (a fraction of gravitational acceleration), β0 the amplification within the structure (constant 2.5 in Romania), and q the «forgiveness» we allow ourselves because the structure can dissipate energy by deforming in a controlled way. We divide by q because we design for reduced forces, accepting deformations.",
  "struct.strateg.v_impingerea_totala": "V — the total push of the earthquake",
  "struct.strateg.formula_v_c": "Formula: V = c_s · A · n · g_seismic — the seismic coefficient × the whole seismic weight of the building (area × storeys × weight per square metre).",
  "struct.strateg.forta_orizontala_totala": "It is the total horizontal force the structure has to carry at its base — as if someone pushed the building sideways with V kilonewtons. All the vertical elements together have to take it.",
  "struct.strateg.v_rd_duce": "v_Rd — how much a square metre of concrete carries in shear",
  "struct.strateg.tinta_practica_2000": "The practical target: 2000 kPa (2 MPa) of horizontal force on every square metre of concrete section.",
  "struct.strateg.valoare_predimensionare": "It is a preliminary-sizing value proven in practice for reinforced concrete walls — prudent enough to leave room for the reinforcement in the later stages.",
  "struct.strateg.d_top_are": "D_top — how much the top is allowed to move",
  "struct.strateg.formula_d_top": "Formula: D_top = δ_lim · H / f_conc — the storey drift limit (1%) times the height, divided by the drift concentration (1.6).",
  "struct.strateg.norma_limiteaza_poate": "The code limits how far one storey may move relative to the one below (the drift, 1% of the storey height), so that infill walls and finishes do not crack. Since drift is not uniform (the lower storeys deform more), we divide by the concentration factor 1.6.",
  "struct.strateg.i_req_cata": "I_req — how much bending stiffness is needed",
  "struct.strateg.formula_i_req": "Formula: I_req = 11 · V · H³ / (60 · E_c · 0.5 · D_top) — the deflection of a cantilever under a triangular load (11·V·H³/60·EI), inverted to give I from the permitted displacement.",
  "struct.strateg.cladirea_consola_vertica": "The building is a vertical cantilever pushed by the earthquake (harder at the top, weaker at the bottom — hence the triangular distribution). From the classic deflection formula we extract the I required; 0.5 accounts for cracked concrete being about half the full stiffness.",
  "struct.strateg.l_max_departe": "L_max — how far apart the elements can be",
  "struct.strateg.formula_l_max": "Formula: L_max = 12 · h_beam — a concrete beam reasonably spans about 12 times its own depth.",
  "struct.strateg.grinzile_trebuie_sa": "The beams have to stay HIDDEN within the depth of the floor or over the doors (architects dislike exposed beams). The depth available over the doors limits the span — the slenderness rule L/h ≈ 12 is the point beyond which deflections and reinforcement become unpleasant.",
  "struct.strateg.formula_pas_min": "Formula: spacing = min(L_max; 5 m) — the smaller of the beam limit and the practical 5 m spacing.",
  "struct.strateg.chiar_daca_grinda": "Even if the beam could span more, beyond ~5 m between columns the floors and foundations get disproportionately expensive — 5 m is the usual economic threshold in residential work.",
  "struct.strateg.n_elem_cate": "n_elem — how many elements fit",
  "struct.strateg.formula_n_elem": "Formula: n_elem = A / spacing² — the storey area divided by the area «served» by one element (a square whose side is the spacing).",
  "struct.strateg.daca_pui_elemente": "If you put elements on a grid at the spacing above, each element serves roughly spacing × spacing square metres of floor.",
  "struct.strateg.a_elem_mare": "A_elem — how large a constructional column is",
  "struct.strateg.formula_a_elem": "Formula: A_elem = max(0.09 m²; h_beam²) — the column side at least as large as the beam depth, but a minimum of 30×30 cm.",
  "struct.strateg.regula_alcatuire_capacit": "A detailing rule (capacity design): the beam reinforcement has to anchor in the joint with the column, so the column cannot be narrower than the beam is deep. 30×30 cm is the constructional minimum for a reinforced concrete column.",
  "struct.strateg.clasa_ductilitate_alege": "What the ductility class is and how it is chosen",
  "struct.strateg.p100_1_2013": "P100-1/2013 lets the engineer choose HOW MUCH energy the structure dissipates by deforming in a controlled way during an earthquake: DCH (high ductility — small design forces, severe detailing), DCM (medium — larger forces, simpler detailing) or DCL (low — practically elastic design, allowed only at low seismicity).",
  "struct.strateg.alegerea_compromis_econo": "The choice is an economic trade-off under code thresholds: at a_g ≥ 0.30g the code explicitly recommends DCH (§5.2.1(3)); DCL is allowed only at a_g ≤ 0.10g (§5.2.1(4)); between them economics decides — if the earthquake does not size the sections anyway (constructional governs), high ductility saves no concrete but makes the detailing dearer. The class chosen enters the reinforcement: the lengths of the critical zones l_cr, the νd limits in columns, the steel class.",
  "struct.strateg.bugetul_forfecare_re": "the shear budget rescaled with the class's q (the strength required ∝ 1/q)",
  "struct.strateg.driftul_scade_q": "drift does NOT fall with q (the equal-displacement rule)",
  "struct.strateg.bugetul_gravitational": "The gravity budget",
  "struct.strateg.beton_iti_trebuie": "How much concrete you need just to hold the weight of the building up, without crushing the columns. Formula: ρ = 0.5 · n · g_grav / (νt · f_cd).",
  "struct.strateg.stalp_aduna_greutatea": "Each column gathers the weight of the storeys above it. The codes do not let concrete be stressed to its maximum (it would fail brittly in an earthquake) — the mean stress is capped at the fraction νt of the concrete strength. The 0.5 appears because the vertical elements carry on average about half the area on each direction of load transfer.",
  "struct.strateg.numarul_niveluri_deasupr": "the number of storeys above the fixity",
  "struct.strateg.incarcarea_gravitational": "the design gravity load per square metre (1.35·permanent + 1.5·imposed)",
  "struct.strateg.constanta_a_metodei": "a constant of the method",
  "struct.strateg.apasarea_maxima_admisa": "the maximum stress permitted in the concrete, as a fraction of its strength (0.55 for frames)",
  "struct.strateg.p100_1_presupunere": "P100-1 — initial assumption: frame",
  "struct.strateg.rezistenta_calcul_a": "the design strength of the concrete (fck divided by 1.5)",
  "struct.strateg.clasa_beton_date": "the concrete class from Building data",
  "struct.strateg.beton_iti_trebuie2": "How much concrete you need so that the walls/columns do not «slide» horizontally under the earthquake force. Formula: ρ = V / (A · v_Rd).",
  "struct.strateg.cutremurul_impinge_cladi": "The earthquake pushes the building sideways with the force V. That force is shared over all the concrete area available; each square metre of concrete practically carries v_Rd = 2000 kPa in shear. Dividing the force by the capacity gives the concrete area required, referred to the floor area.",
  "struct.strateg.forta_taietoare_baza": "the base shear (the total push of the earthquake)",
  "struct.strateg.calculata_jos_v": "computed below: V = c_s · A · n · g_seismic",
  "struct.strateg.tinta_practica_forfecare": "the practical shear target in walls (2000 kPa)",
  "struct.strateg.documentul_metoda": "the method document",
  "struct.strateg.bugetul_drift_indoire": "The drift budget (bending)",
  "struct.strateg.beton_iti_trebuie3": "How much concrete you need so the building does not bend sideways past the permitted limit. Formula: ρ = 12 · I_req / (A · l_typ²).",
  "struct.strateg.cladire_inalta_lucreaza": "A tall building works as a vertical cantilever: it bends under the push of the earthquake. Bending stiffness grows with the CUBE of the element length (I = t·l³/12) — which is why long elements are far more efficient than thick ones, and the formula divides the required inertia I_req by the square of the typical length available l_typ.",
  "struct.strateg.inertia_totala_necesara": "the total inertia needed so that the top does not move past the limit",
  "struct.strateg.calculata_jos_v2": "computed below from V, H and the permitted displacement",
  "struct.strateg.lungimea_tipica_element": "the typical element length available in the architecture",
  "struct.strateg.masurata_peretii_persist": "measured from the persistent walls (Step 2)",
  "struct.strateg.beton_iese_sine": "How much concrete comes out «by itself» if you put a column at every meeting of walls, at the practical spacing. Formula: ρ = 0.5 · n_elem · A_elem / A.",
  "struct.strateg.chiar_daca_seismic": "Even if nothing seismic binds you, you still put vertical elements where the architecture asks for them (corners, wall junctions, under beams). If this constructional minimum comes out the largest of the budgets, it means the architecture decides the structure, not the earthquake.",
  "struct.strateg.numarul_elemente_estimat": "the number of elements estimated (the area divided by spacing²)",
  "struct.strateg.calculat_jos": "computed below",
  "struct.strateg.aria_unui_element": "the area of one element (side ≥ the beam depth, minimum 30×30 cm)",
  "struct.strateg.regula_alcatuire_armatur": "a detailing rule: the beam reinforcement has to anchor in the column",
  "struct.strateg.doar_circa_jumatate": "only about half the elements work on each direction",
  "struct.strateg.aria_beton_aria": "concrete area / floor area",
  "struct.strateg.formulele_a_d": "formulas A–D from the method document",
  "struct.strateg.lungimea_minima_a": "the minimum length of a structural run",
  "struct.strateg.regula_practica": "a rule of practice",
  "struct.strateg.cate_tronsoane_intra": "how many runs go into the mean",
  "struct.strateg.documentul_metoda_n": "the method document (n_expected)",
  "struct.strateg.inaltimea_medie_etaj": "the mean storey height",
  "struct.strateg.pasul_2_cotele": "Step 2 — the elevation levels",
  "struct.strateg.0_85_daca": "0.85 if n > 2, otherwise 1.0",
  "struct.strateg.p100_1_metoda": "P100-1, the equivalent static force method",
  "struct.strateg.acceleratia_terenului_cu": "the ground acceleration in the design earthquake",
  "struct.strateg.harta_zonare_a": "the zoning map of the locality (Step 1)",
  "struct.strateg.p100_1_constant": "P100-1, constant in Romania",
  "struct.strateg.constanta_a_metodei2": "a constant of the method (refined at the analysis)",
  "struct.strateg.corectia_modala": "the modal correction",
  "struct.strateg.randul_sus": "the row above",
  "struct.strateg.numarul_niveluri": "the number of storeys",
  "struct.strateg.greutatea_seismica_metru": "the seismic weight per square metre (permanent + a fraction of imposed)",
  "struct.strateg.capacitatea_practica_for": "the practical shear capacity",
  "struct.strateg.documentul_metoda_consta": "the method document (the spacing constant)",
  "struct.strateg.documentul_metoda_p100": "the method document (P100-1, SLS)",
  "struct.strateg.inaltimea_totala": "the total height",
  "struct.strateg.concentrarea_driftului_e": "the concentration of drift at the lower storeys (1.6)",
  "struct.strateg.forta_taietoare_inaltime": "the shear force and the height",
  "struct.strateg.randurile_sus": "the rows above",
  "struct.strateg.modulul_elasticitate_bet": "the modulus of elasticity of the concrete",
  "struct.strateg.clasa_date_cladire": "the class from Building data (EN 1992-1-1)",
  "struct.strateg.reducerea_rigiditate_bet": "the stiffness reduction for cracked concrete",
  "struct.strateg.deplasarea_admisa_varf": "the displacement permitted at the top",
  "struct.strateg.inaltimea_grinda_incape": "the beam depth that fits over the doors",
  "struct.strateg.raportul_practic_deschid": "the practical span/depth ratio",
  "struct.strateg.regula_predimensionare_d": "a preliminary-sizing rule (the method document)",
  "struct.strateg.deschiderea_maxima_grind": "the maximum span with a hidden beam",
  "struct.strateg.distanta_dintre_elemente": "the distance between elements",
  "struct.strateg.inaltimea_grinzii": "the beam depth",
  "struct.strateg.aria_minima_30": "the minimum area (30×30 cm)",
  "struct.strateg.regula_constructiva": "a constructional rule",
  "struct.strateg.factorul_comportare_redu": "the behaviour factor — how far the design seismic forces are reduced",
  "struct.strateg.redundanta_suprarezisten": "redundancy (system overstrength)",
  "struct.strateg.acceleratia_proiectare_a": "the design ground acceleration",
  "struct.strateg.pasul_ruleaza": "The step is running…",
  "struct.strateg.masoara_l_typ2": "It measures l_typ from the persistent walls (Step 2), reads the seismic data (Step 1) and computes the four constraints.",
  "struct.strateg.ruleaza_nou_meniul": "Run it again from the left-hand menu.",
  "struct.strateg.constrangere": "Constraint",
  "struct.strateg.intrarile_sursa_fiecarei": "The inputs and the source of each",
  "struct.strateg.directie": "Direction",
  "struct.strateg.cele_lungi": "The longest",
  "struct.strateg.criteriile_ordinea_aplic": "The criteria, in the order they are applied",
  "struct.strateg.referinta": "Why (with the reference)",
  "struct.strateg.permisa_aici": "Allowed here?",

  // --- STRUCTURE · Step 11, the load combinations ------------------------
  "struct.combina.1_1": "θ and α = 1/(1−θ)",
  "struct.combina.p_d_r": "θ = P·d_r/(V·h) measures how much the weight that «pushes» through the lateral displacement matters (the P-Δ effect). If θ ≤ 0.1 it is neglected; between 0.1 and 0.2 the seismic forces are amplified by α = 1/(1−θ).",
  "struct.combina.cladire_deplasata_latera": "A building displaced sideways is pushed further by its own weight — an additional moment P·d_r. P100-1 lets you cover it simply, by amplifying the seismic forces with α, instead of a non-linear analysis.",
  "struct.combina.nicio_cifra_matrice": "No figure in the matrix is chosen by the program",
  "struct.combina.coeficientii_fie_impusi": "The coefficients are either imposed by the code (1.35 / 1.00 / 1.50 / 0.30 / ±1), or constants of the method (ψ₀, ψ₂), or computed in this step from the analysis results (α).",
  "struct.combina.combinatiile_impreuna_fo": "All the combinations together form the «envelope»: for each section the most unfavourable value among all those that can really occur is sought, not their average.",
  "struct.combina.rand_combinatie_coloana": "Row = combination, column = load case",
  "struct.combina.combinatie_lista_coefici": "Each combination is a list of coefficients: its forces = the sum of the cases times the coefficients (R = C·F). Empty cells mean a zero coefficient — that case does not take part.",
  "struct.combina.gruparea_fundamentala_cr": "The fundamental group (CR 0-2012): 1.35·G + 1.5·Q_dominant + 1.5·ψ₀·the rest. The seismic group (P100-1): G + ψ₂·Q ± α·E ± α·T_acc. Hover over a column heading to see what that case is.",
  "struct.combina.face_grupa_combinatia": "What the group does with the combination",
  "struct.combina.fundamentala_seismica_in": "«fundamental» and «seismic» enter the two envelopes from which Step 12 takes the design M/V/N. «gs» is kept apart: it is only the gravity part of the seismic group.",
  "struct.combina.verificarile_rezistenta": "The strength checks are done separately on the two groups, because they have different material factors and different ductility requirements.",
  "struct.combina.infrastructura_grinzile": "The substructure: the foundation beams + the earth fill +",
  "struct.combina.peretii_nestructurali_au": "The non-structural walls that have a beam under them",
  "struct.combina.peretii_nestructurali_fa": "The non-structural walls with no beam under them (bearing on the slab)",
  "struct.combina.peretii_parterului_fara": "The ground-floor walls — with no slab beneath, straight onto the foundation beams",
  "struct.combina.finisajele_sapa_pardosea": "The finishes (screed, floor finish, plaster) — SDL",
  "struct.combina.utila_a_spatii": "Imposed load cat. A (living spaces)",
  "struct.combina.utila_c_circulatii": "Imposed load cat. C (circulation and common areas)",
  "struct.combina.zapada_acoperis": "Snow on the roof",
  "struct.combina.cutremurul_directia_0": "The earthquake on direction 0° (the X axis)",
  "struct.combina.cutremurul_directia_45": "The earthquake on direction 45°",
  "struct.combina.cutremurul_directia_90": "The earthquake on direction 90° (the Y axis)",
  "struct.combina.cutremurul_directia_135": "The earthquake on direction 135°",
  "struct.combina.torsiunea_accidentala_cu": "Accidental torsion, earthquake on X",
  "struct.combina.torsiunea_accidentala_cu2": "Accidental torsion, earthquake on Y",
  "struct.combina.greutatea_totala_deasupr": "the total weight above the storey (the seismic group)",
  "struct.combina.deplasarea_relativa_nive": "the storey drift at ULS (c·q·d_re)",
  "struct.combina.deplasarile_cazurilor_0": "the displacements of cases E_0/E_90",
  "struct.combina.forta_taietoare_a": "the storey shear force",
  "struct.combina.permanentele_g1_g4": "all the permanent actions (G1…G4) multiplied by 1.35 — you assume that",
  "struct.combina.cr_0_2012": "CR 0-2012 §6.3, rel. (6.7) — a decision of the code, not of",
  "struct.combina.aceleasi_permanente_dar": "the same permanent actions, but taken with 1.00 — for the situations in",
  "struct.combina.cr_0_20122": "CR 0-2012 §6.3 — the permanent actions are taken under both assumptions",
  "struct.combina.actiunea_variabila_consi": "the variable action considered dominant in that combination",
  "struct.combina.celelalte_actiuni_variab": "the other variable actions in the same combination, reduced by ψ₀",
  "struct.combina.0_cr_0": "ψ₀ from CR 0-2012 tab. 7.1",
  "struct.combina.gruparea_seismica_variab": "in the seismic group the variable actions enter only with the fraction",
  "struct.combina.amplificarea_ordinul_ii": "the second-order amplification, applied to the E and T_acc cases.",
  "struct.combina.p100_1_2013": "P100-1/2013 §4.6.2.2 — see the drift tables above",
  "struct.combina.cutremurul_vine_niciodat": "an earthquake never arrives perfectly along one axis: 100 % is taken on one",
  "struct.combina.cutremurul_alternant_ace": "an earthquake alternates: the same combination is taken with the sign",
  "struct.combina.coeficientul_importanta": "the importance factor of the building. It does NOT appear in the matrix,",
  "struct.combina.pasul_ruleaza": "The step is running…",
  "struct.combina.genereaza_combinatiile_c": "The CR 0-2012 combinations are generated and applied to the force tensor (R = C·F), with the second-order amplification α received from Step 10.",
  "struct.combina.ruleaza_nou_meniul": "Run it again from the left-hand menu.",
  "struct.combina.facute": "What they are made of",
  "struct.combina.setul_redus_alegere": "The reduced set is a deliberate choice (the speed of Steps 13–16), not a silent approximation. Below is exactly what is left unexplored, what the code requires and how it is covered. All the load cases are computed anyway in Steps 8–9 — what is missing are their COMBINATIONS, so moving to the full set does not call for a new FEM run of the cases, only Steps 11–16.",
  "struct.combina.acopera": "Covered by:",
  "struct.combina.directia": "The direction",
  "struct.combina.rezultatul_acesta_a": "This result was computed before the step returned the project's own values — run Step 11 again to see the figures too (kN, ψ, the source of each case).",
  "struct.combina.atentie_doua_cazuri": "Two «special» cases to watch.",
  "struct.combina.valoarea_proiect": "The value on this project",
  "struct.combina.inseamna": "What it means",
  "struct.combina.input_decizie_sursa": "Input or decision — the source",
  "struct.combina.citeste_numele": "How the name is read.",
  "struct.combina.explicatia_ultima_coloan": "The explanation in the last column is not read from the name but from the row's coefficients — they are exactly the figures in the table.",
  "struct.combina.combinatie": "Combination",
  "struct.combina.reprezinta_combinatia": "What the combination represents",

  // --- STRUCTURE · Step 14, reinforcing the basement walls --------------
  "struct.arp.grinda_grinda_perete": "Beam or deep beam?",
  "struct.arp.sr_en_1992": "SR EN 1992-1-1 §5.3.1(3): an element is a BEAM only if its span is at least 3× the depth of the section. Otherwise it is a DEEP BEAM and the plane-section assumption no longer holds.",
  "struct.arp.panourile_subsol_au": "Basement panels typically have l₀/h ≈ 1.5…2 — exactly the «short beam proportions» for which CR 2-1-1.1 §10.3.2 requires a strut-and-tie mechanism. The practical consequence: the tension reinforcement is not spread over the whole depth but concentrated in a band at the tension face.",
  "struct.arp.rigiditatea_laterala_a": "The lateral stiffness of the basement (columns + concrete walls) against the stiffest storey above ground, on each direction.",
  "struct.arp.criteriul_practica_struc": "The engineer's rule of practice: ≥1.5 (the basement at least 50 % stiffer). P100-1 asks qualitatively for a substructure with «rigid-body behaviour» — the 50 % figure comes from practice, and the remaining conditions of the box (the slab over the basement, the anchorages, the openings) are in the recommendations below.",
  "struct.arp.deschiderea_calcul_inalt": "the design span / the depth of the section; below 3 it is a deep beam",
  "struct.arp.montate_inaltimea_subsol": "Placed over the height of the basement, on both faces — the «links» of the deep beam: they take the in-plane shear AND they cross the construction joints (base–wall, wall–slab), where they act as SHEAR CONNECTORS.",
  "struct.arp.minim_10_distanta": "Minimum Ø10, max 350 mm spacing horizontally, ρ ≥ 0.30 % with both meshes (CR 2-1-1.1 §8.4.2 + §10.4.6); in a deep beam also ≤ min(2·t, 300 mm) (SR EN 1992-1-1 §9.7(2)). The connector role is required explicitly by CR 2-1-1.1 §10.3.2.",
  "struct.arp.barele_orizontale_repart": "The horizontal distribution bars",
  "struct.arp.plasa_ortogonala_ambele": "The orthogonal mesh on both faces, over the whole length of the panel: it takes the in-plane bending (M+N).",
  "struct.arp.minim_8_distanta": "Minimum Ø8, max 250 mm spacing vertically, ρ ≥ 0.30 % with both meshes (CR 2-1-1.1 §8.4.2 + §10.4.6); in a deep beam the mesh is mandatory near EACH face, min. 0.1 % and 150 mm²/m (SR EN 1992-1-1 §9.7(1)).",
  "struct.arp.tirantii_banda_intinsa": "The ties in the tension band",
  "struct.arp.bare_orizontale_concentr": "Horizontal bars CONCENTRATED in the band at the tension face — at the top over the supports (negative moment), at the bottom in the span (positive moment). They are the tie reinforcement of the strut-and-tie mechanism.",
  "struct.arp.grinda_perete_deformatii": "In a deep beam the strains no longer vary linearly over the depth, so the bars at the opposite face never yield: T = M/z is taken by the reinforcement in the band. CR 2-1-1.1 §10.3.2 requires the mechanism, SR EN 1992-1-1 §9.7(3) requires the tie to be FULLY anchored in the node.",
  "struct.arp.grinda_perete_sr": "deep beam (SR EN 1992-1-1 §5.3.1(3))",
  "struct.arp.portiunile_panou_interio": "The lengths of panel inside the columns, modelled as RIGID: l₀ = L_axis − off_i − off_j.",
  "struct.arp.peretele_foarte_asemanat": "A wall is very much like a beam, so the natural question is «does check X from the beams apply here too?». The absence of a check is not an answer — here each one has its reason and its article.",
  "struct.arp.scutirile_pornesc_acelas": "All the exemptions start from the same place: CR 2-1-1.1 §10.4.1 and P100-1 §5.8.1(2) — the substructure is designed to stay ELASTIC, the post-elastic deformations being directed into the superstructure. With no plastic hinges there is no section ductility, no hinge steering, no rotation capacity and no capacity design of the shear.",
  "struct.arp.cutia_rigida_a": "The rigid box of the substructure",
  "struct.arp.peretii_dintre_stalpi": "The walls between the columns are only one part: the box is closed by the slab over the basement acting as a diaphragm, the anchorages into the substructure, the trimmed openings and the joints handled correctly.",
  "struct.arp.p100_1_4": "P100-1 §4.4.1.7 and C5.8, CR 2-1-1.1 §10.3 and §10.4 — the list below, with its articles, is what the engineer checks at detailed analysis. The solver does not size them: they are pieces of the detailed analysis.",
  "struct.arp.1_geometria_regimul": "1. The geometry and the regime of the section — beam or deep beam?",
  "struct.arp.pozitia_statiei_axul": "the position of the station, from the axis of column 1",
  "struct.arp.momentul_calcul_planul": "the design moment in the plane of the wall",
  "struct.arp.fata_intinsa_rezultata": "the tension face resulting from the sign of M₃",
  "struct.arp.momentul_combinatia_cvas": "the moment from the quasi-permanent combination GS",
  "struct.arp.combinatia_da_extremul": "the combination that gives the extreme — the source of the whole row",
  "struct.arp.3_combinatia_dimensionea": "3. The combination that sizes it and the substructure amplification",
  "struct.arp.4_armarea_aleasa": "4. The reinforcement chosen (the final solution + the detailing drawings)",
  "struct.arp.5_incovoierea_plan": "5. In-plane bending — two models, the smaller one decides",
  "struct.arp.6_taietoarea_plan": "6. In-plane shear, the compression strut and the support node",
  "struct.arp.7_lunecarea_rosturile": "7. Sliding at the construction joints — the vertical bars as CONNECTORS",
  "struct.arp.8_deschiderea_fisurilor": "8. Crack width under the quasi-permanent combination (INFORMATIVE)",
  "struct.arp.armatura_intinsa_conside": "the tension reinforcement considered (the tie in the band)",
  "struct.arp.efortul_armatura_intinsa": "the stress in the tension reinforcement, state II",
  "struct.arp.distanta_maxima_intre": "the maximum distance between cracks",
  "struct.arp.9_regulile_constructive": "9. The detailing rules — the reinforcement has to be both LEGAL and buildable",
  "struct.arp.10_ancorarea_innadirea": "10. Anchoring and lapping the starter bars from the foundation",
  "struct.arp.raportul_decide_regimul": "the ratio that decides the regime",
  "struct.arp.bratul_parghie_grinzii": "the lever arm of the strut-and-tie mechanism",
  "struct.arp.banda_intinsa_aduna": "the tension band in which the tie gathers",
  "struct.arp.unghiul_forta_biela": "the angle and the force in the support strut",
  "struct.arp.presiunea_nod_vs": "the pressure in the node vs σ_Rd,max = k₂·ν′·f_cd",
  "struct.arp.rezistenta_v_rdi": "the resistance v_Rdi = c·f_ctd + µ·σ_n + ρ·f_yd·µ",
  "struct.arp.aderenta_proiectare_f": "the design bond strength f_bd = 2.25·η₁·η₂·f_ctd",
  "struct.arp.lungimea_ancorare_baza": "the basic anchorage length and the design one",
  "struct.arp.lungimea_innadire_a": "the lap length of the starter bars",
  "struct.arp.termenii_folositi_romane": "The terms used, in plain words",
  "struct.arp.pasul_ruleaza": "The step is running…",
  "struct.arp.armeaza_peretii_beton": "The concrete basement walls are reinforced: the forces at the 5 stations (Step 12, with the substructure amplification on the seismic combination), the regime of the section (slender beam or deep beam), then the meshes, the ties in the tension band, the construction joint and the detailing rules.",
  "struct.arp.ruleaza_nou_meniul": "Run it again from the left-hand menu.",
  "struct.arp.panourile_nasc_pasul": "The panels are born in Step 7, between two basement columns sitting on the SAME wall axis from Step 2, with the wall covering at least 70 % of the bay. If none meets the condition, check the walls of the underground storey in Step 2 first.",
  "struct.arp.pas_inactiv_proiect": "This step is inactive on this project",
  "struct.arp.panourile": "All the panels",
  "struct.arp.intre_stalpii": "between columns",
  "struct.arp.otel_kg": "steel [kg]",
  "struct.arp.plasa_banda_ajunge": "the mesh in the band is enough",
  "struct.arp.click_rand_deschide": "Clicking a row opens the panel's calculation note, below.",
  "struct.arp.inseamna_verdictul": "What the verdict means.",
  "struct.arp.marja_subdimensionare_ac": "The under-sizing margin accepted:",
  "struct.arp.verificarea_grinzi": "the check from the beams",
  "struct.arp.unde_grinzi": "where it is in the beams",
  "struct.arp.aplica": "why it does not apply",
  "struct.arp.baza_normativa": "The code basis",
  "struct.arp.romanesc_da_cerinta": "the ROMANIAN code gives the requirement",
  "struct.arp.tirantii": "the ties",
  "struct.arp.fata_intinsa": "the tension face",
  "struct.arp.combinatia": "the combination",
  "struct.arp.intinsa": "IN TENSION",
  "struct.arp.plasa_verticala_ambele": "Vertical mesh (both faces)",
  "struct.arp.plasa_orizontala_ambele": "Horizontal mesh (both faces)",
  "struct.arp.tirant_sus_banda": "Tie at the TOP (the band at the top face)",
  "struct.arp.tirant_jos_banda": "Tie at the BOTTOM (the band at the bottom face)",
  "struct.arp.aplica2": "does not apply",

  // --- STRUCTURE · Step 15, reinforcing the columns ---------------------
  "struct.ars.verifica_stalp": "How a column is checked",
  "struct.ars.capacitatea_unei_sectiun": "The capacity of a reinforced section under (N, M2, M3) is not a number but a closed SURFACE in 3D (the «onion»): on each direction of bending there is a P–M curve. The points from the analysis have to lie inside it.",
  "struct.ars.pozitia_zonei_comprimate": "The position of the compression zone depends on the axial force — which is why the engineer used XTRACT (fibre analysis). Our engine reimplements that calculation with the project's own materials, and the check is R/E = the radial ratio of capacity to demand.",
  "struct.ars.desenele_arata_numai": "the drawings show only the selected storey",
  "struct.ars.stiva_intreaga_talpa": "the whole stack, from the foundation base to the topmost floor",
  "struct.ars.h_flex_coteaza": "Why h_flex is not dimensioned on the drawing",
  "struct.ars.h_flex_h": "h_flex = H − the rigid node offsets, that is the length of column that deforms in the model.",
  "struct.ars.ofsetul_rigid_stalpului": "The column's rigid offset is capped at min(0.25 m; 0.20·L/2) in fem.py, so it is a MODEL quantity, not the real floor thickness. The drawing dimensions the depth of beam that actually enters the joint.",
  "struct.ars.explicatia_larg": "The explanation, at length",
  "struct.ars.plafonul_echilibru_nodul": "the equilibrium cap of the lower / upper joint",
  "struct.ars.taietoarea_rezultata_ana": "the shear resulting from the analysis",
  "struct.ars.latimea_inaltimea_utila": "the width × the effective depth of the shear section",
  "struct.ars.efortul_nod_m": "the demand on the joint = ΣM_Eb (analysis) × Ω, with Ω = ΣM_Rb/ΣM_Eb floored at 1",
  "struct.ars.m_rb_m": "Ω = ΣM_Rb/ΣM_Eb — the amplification factor from the beams; below one it is taken as 1, so as not to reduce the demand on the joint",
  "struct.ars.v_pereti_v": "V_walls + V_rest − V_base: CQC is not additive",
  "struct.ars.pasul_ruleaza": "The step is running…",
  "struct.ars.stalp_genereaza_suprafet": "For each column: the P–M–M interaction surfaces are generated with the fibre engine (the replacement for XTRACT), then all the combinations are checked, along with νd, the joint hierarchy (with M_Rb from Step 13), the shear capacity and the joint details.",
  "struct.ars.ruleaza_nou_meniul": "Run it again from the left-hand menu.",
  "struct.ars.doar_nivelul": "This storey only",
  "struct.ars.tot_stalpul": "The whole column",
  "struct.ars.armare_simetrica_contur": "Symmetric reinforcement around the perimeter (the corners always occupied), the same mark per storey — the solver starts from the constructional minimum (the p_min of the ductility class) and works up the range until every point (N, M2, M3) falls inside the P–M–M surface and the joint hierarchy is satisfied. The bars drawn are EXACTLY the ones the fibre engine computed the capacity with.",
  "struct.ars.etrieri_zonele_plastice": "Links in the plastic zones",
  "struct.ars.etrieri_zona_curenta": "Links in the middle zone",
  "struct.ars.zona_plastica_l": "Plastic zone l_cr (bottom / top)",
  "struct.ars.bara_trece_prin": "the bar runs through the storey (it holds two floors)",
  "struct.ars.ax_planseu_ax": "(floor axis → floor axis)",
  "struct.ars.libera": "clear",
  "struct.ars.a_stalpului": "of the column",
  "struct.ars.grinda_cea_inalta": "(the deepest beam)",
  "struct.ars.placa": "of which the slab",
  "struct.ars.innadirea_barelor_l0": "Bar laps l₀",
  "struct.ars.citeste_tabelul_jos": "How to read the table below",
  "struct.ars.vezi_explicatia_detaliat": "— see the detailed explanation under the table.",
  "struct.ars.efortul_nod": "The demand on the joint",
  "struct.ars.5_6_nodul": "5–6. The joint: shear and anchorage",
  "struct.ars.7_confinarea_wd": "7. Confinement (ω_wd and α·ω_wd)",
  "struct.ars.8_interaxul_barelor": "8. Spacing of restrained bars ≤ 18 cm",
  "struct.ars.detalii_referinta": "Details / the reference",
  "struct.ars.incovoiere": "bending",
  "struct.ars.taietoare": "shear",
  "struct.ars.ea_spune_intreba": "It says: do not ask the analysis «what shear appears?», because the analysis gives you the shear from the design earthquake, and the real earthquake may be larger. Ask instead: «what is the largest shear the column could EVER feel?» — that is, the one at the moment it hinges plastically at both ends. Then the end moments are the CAPACITIES, and from the equilibrium of the member:",
  "struct.ars.plafonul_echilibru_nodul2": "the equilibrium cap of the joint",
  "struct.ars.m_rc_ia": "M_Rc is taken at the MAXIMUM seismic axial force.",
  "struct.ars.verifica_ambele_directii": "BOTH directions are checked separately.",
  "struct.ars.directia": "The direction",
  "struct.ars.v_analiza_kn": "V_analysis [kN]",
  "struct.ars.brate": "legs",
  "struct.ars.umfla_lateral": "bulges sideways",
  "struct.ars.deformatie_mult_mare": "a far larger deformation",
  "struct.ars.consecinta_practica": "The practical consequence:",
  "struct.ars.pasul_etrierilor_zona": "The link spacing in the critical zone",
  "struct.ars.s_8_bara": "s ≤ 8·Ø_bar",
  "struct.ars.eficienta_confinarii": "α (the efficiency of the confinement)",
  "struct.ars.betonul_confinat_f": "The confined concrete (f_cc, ε_cu,c — the Mander model) is reported for INFORMATION only: the P–M–M capacities above are computed on the UNCONFINED design concrete, which is on the safe side.",
  "struct.ars.interax_18_cm": "spacing ≤ 18 cm between restrained bars",
  "struct.ars.forfecarea_orizontala": "the horizontal shear",
  "struct.ars.nodul_cel_solicitat": "Why the joint is the most heavily loaded place in a frame.",
  "struct.ars.cutremur_intr_nod": "In an earthquake, at an internal joint the TOP reinforcement of the left-hand beam is in tension while the BOTTOM reinforcement of the right-hand beam is also in tension — in opposite senses. Those two forces, plus the column shear, add up to a horizontal force V_jhd that tries to «shear» the joint horizontally. The joint takes it through a COMPRESSION DIAGONAL of concrete, between the top-left and the bottom-right corner. Check (2) compares the force demanded with how much that diagonal can carry before it crushes: V_jhd ≤ η·f_cd·b_j·h_jc·√(1−ν_d/η). The term √(1−ν_d/η) says that a column already heavily compressed leaves less of the concrete available for the diagonal. b_j is the effective width of the joint: the overlap between beam and column, widened by a quarter of h_c on each side (the stress spreads at 45°), but never beyond the wider of the two elements.",
  "struct.ars.ancorarea_barelor_limite": "Why anchoring the bars limits their diameter.",
  "struct.ars.bara_continua_printr": "A bar running through an internal joint enters in tension on one side and leaves in compression on the other. The difference — practically 2·f_yd — has to be transferred to the concrete by bond, over the column depth h_c. The thicker the bar, the more length it needs for the same force (the area grows with Ø², the bond surface only with Ø). Hence the d_bL/h_c limit: through a narrow column you cannot run thick bars, however well you reinforce it. If it fails, the only real solutions are a column wider along the beam direction, or more and thinner bars in the beam — reinforcing the column does not help at all. The favourable effect of compression (1+0.8·ν_d) is taken with the MINIMUM ν_d of the column ABOVE the joint, because that is the case in which compression helps least; where there is no column above, ν_d = 0. The check covers every bar that reaches the joint: both the continuous layers and the extra bars at the support.",
  "struct.ars.nodul_varful_nivelului": "The joint (at the top of the storey)",
  "struct.ars.fara_grinzi_nod": "no beams at the joint",
  "struct.ars.directie": "on each direction",
  "struct.ars.taietoarea_baza": "the base shear",
  "struct.ars.cere": "is not required",
  "struct.ars.nivel": "At which storey",
  "struct.ars.fractiunea": "The fraction",
  "struct.ars.pereti": "walls",
  "struct.ars.baza": "base",
  "struct.ars.pasul_9_lipseste": "Step 9 is missing",
  "struct.ars.directia2": "direction",
  "struct.ars.i_pereti_m4": "ΣI walls (m⁴)",
  "struct.ars.fractiune": "fraction",

  // --- STRUCTURE · Step 13, reinforcing the beams -----------------------
  "struct.arg.alege_armarea_fiecarei": "It chooses the reinforcement of each beam and CHECKS it against the engineer's spreadsheet (GRINZI_CP.xlsm, sheet B) — resisting moments, axial force, plastic hinge, shear over 3 zones.",
  "struct.arg.modelul_fem_contine": "The FEM model contains concrete only; the steel is sized so that the capacity (R) covers the demand (E) at every check station — R/E ≥ 1. The result is the input for reinforcing the columns (the weak-beam / strong-column hierarchy).",
  "struct.arg.portiunile_grinda_interi": "The lengths of beam inside the columns, modelled as RIGID: l₀ = L_axis − off_i − off_j.",
  "struct.arg.pasul_nu_rulat": "This step has not been run yet. Press «Run» in the left-hand menu (after Step 12).",
  "struct.arg.ultima_rulare_esuat": "The last run failed: {motiv}",
  "struct.arg.eroare_necunoscuta": "unknown error",
  "struct.arg.parametrii_de_calcul": "Design parameters",
  "struct.arg.param_beton_otel": "concrete {beton} (f_cd = {fcd} MPa, f_ctd = {fctd} MPa) · steel {otel} (f_yd = {fyd} MPa) · class {clasa} · b_eff: {beff} · covers {csus}/{cjos} cm · slab reinforcement: {placa}",
  "struct.arg.param_gamma": "γ_Rd = {g1} for the capacity shear and {g2} for anchorage through the joint — {sursa}. It changes with the ductility class, with no setting to touch.",
  "struct.arg.optiune_grinda": "{el} ({nivel}) — {verdict}",
  "struct.arg.grinda_dimensiuni": "{b}×{h} cm · L = {l} m (axis to axis) · l₀ = {l0} m · columns {si} → {sj}",
  "struct.arg.rigid_off": " · rigid {i} + {j} cm",
  "struct.arg.verdictul_aduna": "<b>What the verdict means.</b> It gathers ALL of the beam's checks, and the severest one dictates it: bending (the moments, in the fundamental and in the seismic group), <b>shear</b> over every zone, <b>the ductility of the section</b> (x ≤ x_u), <b>steering the plastic hinge</b>, serviceability (SLS: deflection and crack width) and the detailing rules (the bars fit in the section, A_s ≤ 4 %, A_s,bottom ≥ 0.5·A_s,top at the support). Each has a ratio R/E: <i>R</i> = what the beam can do, <i>E</i> = what is asked of it. R/E ≥ 1 means it passes. «ATENTIE!!!» = it passes, but with less than 9 % to spare. «NU SE VERIFICA!!!» = it fails, and Step 19 will see it as a failed check and try to solve it.",
  "struct.arg.marja_acceptata": "<b>Accepted under-design margin:</b> this project accepts R/E ≥ {prag} (a margin of {pct} %) — the engineer's practice: a check at 0.95 does not stop a project. The solver still aims at R/E = 1.00; only the verdict of the checks left inside the margin becomes accepted, marked explicitly «prin marja», and the R/E shown stays the real one.",
  "struct.arg.croiala_barelor": "Cutting the longitudinal bars",
  "struct.arg.th_bara": "bar",
  "struct.arg.th_traseul": "run [m]",
  "struct.arg.th_suprapuneri": "laps",
  "struct.arg.innadire_la": "{s} m, l₀ = {l0} m",
  "struct.arg.innadire_zona": " ({zona})",
  "struct.arg.innadire_fara_zona": " — with NO free zone",
  "struct.arg.abscisa_din_capat": "The abscissa is measured from the end of axis{ax} ({l} m), and its nodes fall at {noduri} m. A bar passing through a node belongs to the axis, not to the span: it enters the schedule once, on the beam that hosts it.",
  "struct.arg.suprapuneri_fortate": "Some laps did not fit in any permitted zone",
  "struct.arg.suprapuneri_fortate_de_ce": " — the span is too long for a commercial bar with no free zone at hand; where they go is decided at detailing.",
  "struct.arg.legenda_scena": "Concrete = transparent grey; the active slab (b_eff) = green; the continuous top layer = red, the bottom one = blue; additional bars = ochre (top) / turquoise (span); the stirrups = blue lines (closer together in the critical zones l_cr = {lcr} m); the context (columns, slab, neighbouring beams) is sketched.",
  "struct.arg.legenda_petrecere_a": "<b>The plum zones at the ends = the RIGID stretches.</b> <b>A DOUBLED bar = the lap</b> (the same bar, twice, dimensioned l₀), and",
  "struct.arg.legenda_petrecere_b": ", where the code does not allow laps. Rotate with the mouse, zoom with the scroll wheel.",
  "struct.arg.banda_rosie_scurt": "the red band at the ends is the critical zone",
  "struct.arg.zona_rigida_explic": "<b>What the «rigid zone» is.</b> The beam does not start at the column face but on its axis — the piece between axis and face is swallowed by the concrete joint, which is far thicker than the beam and barely deforms. In the analysis model it is NOT a flexible member but a <i>rigid</i> stretch: it passes the force on without bending. Here it is {i} cm at end i and {j} cm at end j (half the column's section, projected on the beam's axis — a 30×30 column gives 15 cm, a blade wall with its long side along the beam can give 55 cm).",
  "struct.arg.de_ce_conteaza_l0": "<b>Why it matters.</b> The design moments and shear are not read on the axis (where they would come out artificially large) but at the <b>COLUMN FACE</b> — that is where the real beam starts. The design span is therefore l₀ = L_axis − off_i − off_j = {lax} − {oi} − {oj} = <b>{l0} m</b>, and every check (M_Rd, the l₀/250 deflection, the shear span) runs on it, not on L_axis.",
  "struct.arg.termen_brat": "lever arm (z)",
  "struct.arg.termen_brat_def": "the distance between the resultant of the concrete compression and the tensioned reinforcement — the resisting moment is the force in the steel × z",
  "struct.arg.termen_d": "effective depth (d)",
  "struct.arg.termen_d_def": "from the compressed fibre to the CENTRE of the tensioned reinforcement — not the whole depth h works",
  "struct.arg.termen_beff": "b_eff (the active width)",
  "struct.arg.termen_beff_def": "the strip of slab that works with the beam in bending; the slab reinforcement inside that strip adds to the capacity",
  "struct.arg.s2_titlu": "2. The design forces (the 5 points)",
  "struct.arg.s2_explicatie": "The forces come from Step 11's combined tensor, taken per combination (not as an anonymous envelope): for each point the extreme moment is kept, together with the combination that gives it, the position x and the concomitant axial force N. {corectie}",
  "struct.arg.deschiderea_sub_4_5": "The span is under 4.5 m — the long-span correction (sheet B: AZ10) does not apply.",
  "struct.arg.s3_seismic_da": "The seismic moments exceed the fundamental ones at the supports (sheet B criterion, AX21: M_fund < 1.05·M_seism) ⇒ the beam is reinforced for the SPECIAL (seismic) COMBINATION, with the slab contribution reduced to {coef} and the effect of the axial force included.",
  "struct.arg.s3_seismic_nu": "The fundamental group covers the seismic one with a margin ≥ 5 % at both supports (sheet B, AX21) ⇒ «NU E CAZUL ARMĂRII LA COMBINAȚIA SPECIALĂ» — it is reinforced with the capacity from the fundamental group.",
  "struct.arg.s4_explicatie": "The solver starts from the detailing minimum (2 continuous bars top and bottom — P100-1 requires them over the whole length) and iteratively repairs the failed check with the cheapest correction; the additional span bars are NOT carried into the support (they do not raise V_Ed,cap or the demand on the columns).",
  "struct.arg.carcasa_sus_axul": "The top cage is the same along the whole axis {ax}: the bars run through the intermediate joints, so they are the same bar. The largest one on the axis was taken.",
  "struct.arg.sus_continuu": "Top, continuous",
  "struct.arg.sus_supl_r1": "Extra top bars at R1",
  "struct.arg.randul_2": "(2nd row)",
  "struct.arg.jos_continuu": "Bottom, continuous",
  "struct.arg.bara_retezata_drept": "the bar is cut off straight, at the theoretical point + the shift a_l (EC2 §9.2.1.3)",
  "struct.arg.bara_ridicata_45": "the bar is not cut off: it rises at 45° through the depth of the section and is anchored on the top fibre (EC2 §9.2.1.3)",
  "struct.arg.jos_supl_camp": "Extra bottom bars in the span",
  "struct.arg.retezate": "(cut off)",
  "struct.arg.ridicate_45": "(bent up at 45°)",
  "struct.arg.etrieri_pas_unic": "Stirrups (one spacing over the whole beam)",
  "struct.arg.etrieri_doua_zone": "Stirrups (critical zone / current zone)",
  "struct.arg.armarea_se_cauta": "The reinforcement is not computed from a formula, it is SEARCHED for: it starts from the detailing minimum and corrects, one step at a time, the first problem found (area from the moments → the resisting moments → the ductility of the section → steering the plastic hinge → shear → SLS), and after each correction the check restarts from the beginning.",
  "struct.arg.jurnalul_nu_se_pastreaza": "The step-by-step log is no longer kept — the only history retained is Step 19's, «what changed in the structure».",
  "struct.arg.s5_explicatie": "M_Rd = (A_s·f_yd + the contribution of the slab reinforcement)·z. The slab participates with the coefficient {cf} in the fundamental group and {cs} in the seismic one (sheet B practice — not a code value). In the seismic group the moment «consumed» by the axial force M_c is also subtracted: a beam in TENSION loses capacity (the engineer took this from the American literature; it appears in EC2 too, but not in the Romanian code).",
  "struct.arg.th_punct": "Point",
  "struct.arg.th_grupare": "Group",
  "struct.arg.th_verdict": "Verdict",
  "struct.arg.momentul_de_calcul": "the design moment",
  "struct.arg.momentul_consumat_axial": "the moment consumed by the axial force",
  "struct.arg.s6_explicatie": "WHAT x IS. When the beam bends, part of the section goes into compression (the top at the negative moment over the support, the bottom at the positive moment in the span) and the rest into tension. The boundary between them is called the NEUTRAL AXIS, and x is the distance from the most compressed fibre to it. Small x = a thin compressed zone; large x = a deep compressed zone.\n\nWHY IT MATTERS. A well designed beam fails like this: the tensioned reinforcement starts to yield (the steel stretches a lot, the cracks open visibly, the beam sags) and only much later does the compressed concrete crush. You get warning, you get deformation, the structure dissipates energy — exactly what an earthquake asks for. If x is too deep, the section has too much tensioned reinforcement for the compressed concrete available: the concrete crushes FIRST, suddenly, before the steel has yielded. The failure is brittle. That is why P100-1 limits x to a quarter of the effective depth: x_u = 0.25·d = {xu} cm.\n\nHOW IT IS COMPUTED. The real stress in the compressed concrete follows a curve (parabola–rectangle) that nobody integrates by hand. The codes replace it with an EQUIVALENT RECTANGLE giving the same force. Careful: there are two conventions, and they call different things «x». EC2 §3.1.7 uses a rectangle of depth 0.8·x at the stress f_cd, where x is the real neutral axis. STAS and the office spreadsheet use a rectangle of depth x at the stress f_cd — that is, «x» IS the rectangle. The work here is in the spreadsheet's convention, from the equilibrium of forces over the section:\n\n    x·b·f_cd  +  A_compressed·f_yd  =  A_tensioned·f_yd  +  N\n\nOn the left: the force in the rectangle of compressed concrete plus the reinforcement on the compressed face. On the right: the tensioned reinforcement plus the axial tension N. Solved for x:\n\n    x = [ (A_tensioned − A_compressed)·f_yd − N ] / (b·f_cd)\n\nNumerically this x is 0.8 of the neutral axis in the EC2 convention, and the limit x ≤ 0.25·d is read in the same convention (equivalent to x_EC2/d ≤ 0.31). If you compare against a calculation done to EC2, remember the factor.\n\nPOSITIVE N = TENSION. In an earthquake the floor pulls on the beam, so N > 0 and x grows — a beam in axial tension needs more compressed concrete to balance.\n\nA NEGATIVE x is not an error: it means the face assumed to be compressed is not in fact compressed (the section has more reinforcement on that face than on the other). The LARGEST x among all 5 points × all groups is the one checked.\n\nHOW IT IS FIXED. More reinforcement on the COMPRESSED face: it takes part of the compression, so the concrete needs less depth and the neutral axis rises. Reinforcement on the TENSIONED face does exactly the opposite — it deepens the neutral axis. If no more bars fit on the compressed face (2 rows at most), the only real solution is a larger concrete section.",
  "struct.arg.s7_explicatie": "In an earthquake the beam must «break» in a controlled way ONLY at its ends (plastic hinges at the supports). Were a third hinge to form in the span, the three collinear breaks would become a mechanism — the floor «bellies» and falls. Sheet B's criterion (BD15): a hinge in the span is possible if the concomitant span moment exceeds 0.85·the maximum span moment and the moment at the other end as well; it is STEERED to the supports if M_Rd,span ≥ 1.45·M_Rd,bottom at BOTH supports.",
  "struct.arg.s8_explicatie": "WHAT IT IS. Shear «cuts» the beam across and produces OBLIQUE cracks (at about 45°), not the vertical ones of bending. Shear failure is BRITTLE: the oblique crack opens suddenly, without warning and without plastic deformation. That is why the beam must be STRONGER in shear than in bending — we want it to fail in bending, in a controlled way.\n\nTHE CAPACITY CALCULATION. For exactly that reason, in the critical zone V_Ed is NOT taken from the analysis. The question is put the other way round: «if the beam really does hinge plastically at both ends, what shear appears?» — V_Ed,cap = γ_Rd·(M_Rd,i + M_Rd,j)/l_as, plus the shear from the gravity loads. That keeps the shear always above what bending can deliver.\n\nTHE THREE ZONES. The «critical zone» is the end of the beam, over the length where the plastic hinge can form — that is where the stirrups are closest together. The «current zone» is the middle, where the shear from the analysis is smaller. The «joint» is the stretch inside the column.\n\nWHAT ELSE IS CHECKED. The stirrups take the tension across the oblique crack, but between cracks the concrete works in COMPRESSION, like the diagonals of a truss. If those concrete diagonals crush, the stirrups no longer help — so V_Rd,max (the compression struts) is checked as well.\n\nThe check is made both by the Romanian method (STAS — sheet B) AND to EC2, taking the safer one. Every zone enters the beam's verdict.",
  "struct.arg.nicio_parte": "neither side",
  "struct.arg.o_parte": "one side",
  "struct.arg.ambele_parti": "both sides",
  "struct.arg.articulatie_praguri": "M_Rd,span = {m} kNm against the thresholds 1.45·M_Rd,bottom = {praguri} kNm →",
  "struct.arg.articulatie_nu_e_cazul": "The concomitant span moment does not exceed 0.85 of the reference — the hinge cannot appear in the span; no steering is needed.",
  "struct.arg.inclinate_necesare": "the shear changes direction significantly (ζ = {zeta} ≤ −0.5) → {n}Ø{phi} bars at 45° are added, taking {v} kN",
  "struct.arg.inclinate_nu_e_cazul": "not the case (ζ = {zeta} > −0.5) — the shear reversal is moderate (sheet B: BT52–BT54).",
  "struct.arg.s9_explicatie": "SERVICEABILITY checks (not strength ones): under everyday loading — the quasi-permanent combination GS (G + ψ₂·Q) — the beam must not sag visibly and the cracks must stay capillary-tight. The deflection is computed by integrating the curvature M/EI over the span, with the stiffness INTERPOLATED between the uncracked section (state I) and the cracked one (state II, EC2 §7.4.3, ζ = 1 − 0.5·(M_cr/M)²) and with the creep of the concrete: E_eff = Ecm/(1+φ) = {e} MPa (φ = {phi}).",
  "struct.arg.momentul_de_fisurare": "the cracking moment M_cr = f_ctm·b·h²/6",
  "struct.arg.sageata_eticheta": "Deflection:",
  "struct.arg.otelul_nu_mai_ajuta": " — steel no longer helps much: increase the depth of the beam (an external, manual loop)",
  "struct.arg.wk_formula": "w_k = s_r,max·(ε_sm − ε_cm), with s_r,max = 3.4·c + 0.425·k₁·k₂·Ø/ρ_eff (EC2 §7.3.4; k₁ = 0.8 ribbed bars, k₂ = 0.5 bending, k_t = 0.4 long term). «Nefisurat» = the moment does not reach M_cr.",
  "struct.arg.s10_explicatie": "How far each end of the beam can ROTATE before losing its capacity (the EC8-3 annex A formula, with MEAN strengths: f_cm = 1.75·f_cd, f_ym = 1.35·f_yd; the confinement factor α = {alpha} from the arrangement of the stirrups). {nota}.",
  "struct.arg.lv_deschiderea_forfecare": "L_v (the shear span, end → zero moment): R1 = {r1} m, R2 = {r2} m · ρ_sw = {rho}",
  "struct.arg.s10b_explicatie": "A calculation can ask for an area of reinforcement that checks out perfectly on paper — but does not fit in the formwork, or cannot be concreted. The rules below are the ones that turn «the required area» into «real reinforcement».\n\nTHE BARS MUST FIT. Across one face, the usable width is b − 2·cover − 2·Ø_stirrup = {spatiu} cm. Between the axes of two neighbouring bars there must be at least {imin} cm and at most {imax} cm: below the minimum the concrete no longer passes with the poker vibrator and honeycombing forms, above the maximum the reinforcement gathers into a few thick bars, which a single defect takes out of service. At most 2 rows of bars are accepted on one face — a third row would lower the centroid of the reinforcement too far and reduce the lever arm.\n\nA_s ≤ 4 % OF THE CONCRETE AREA (EC2 §9.2.1.1). Above 4 % the section becomes dominated by steel: the concrete can no longer be compacted between the bars, and the behaviour is no longer that of a reinforced concrete member. In practice, if you get there the beam is under-sized — it is not short of reinforcement, it is short of concrete.\n\nA_s,bottom ≥ 0.5·A_s,top AT THE SUPPORT (P100-1 §5.3.4.1.2). In an earthquake the moment at the end of the beam changes sign from one second to the next: now it stretches the top fibre, a moment later the bottom one. The bottom reinforcement at the support is no whim — it takes the positive moment of the reversed cycle. The minimum of half the top reinforcement makes sure the beam has capacity in both directions.\n\nMINIMUM Ø AND THE CAP THROUGH THE JOINT. The bars cannot be arbitrarily thin (Ø {phimin} minimum, by detailing) nor arbitrarily thick: a bar passing through a joint must manage to hand its force to the concrete over the thickness of the column, otherwise it slips. The cap here is {plafon}, computed from Ø ≤ h_c·7.5·f_ctm/(γ_Rd·f_yd)·(1+0.8·ν_d,min), with γ_Rd = {gamma} ({sursa}). It is EXACTLY the same function, with the same values, that the anchorage check at Step 15 uses — if the cap here were stricter than the check there, you would get warnings with no failed check behind them, and the solver would be pushed towards thicker bars, that is, in precisely the wrong direction.",
  "struct.arg.plafon_dezactivat": "off",
  "struct.arg.plafon_mm": "{v} mm",
  "struct.arg.th_verificarea": "Check",
  "struct.arg.th_e_cerut": "E (required / actual)",
  "struct.arg.th_r_limita": "R (the limit)",
  "struct.arg.plafon_depasit": "The bars were chosen ABOVE the diameter cap through the joint{plafon}: the required area did not fit under it. The anchorage of the joint is judged at Step 15 (the category «Nod: ancorarea barelor grinzii»), and the lever is there — columns wider along the beam, or a deeper beam so the required area drops.",
  "struct.arg.plafon_paren": " ({v} mm)",
  "struct.arg.armarea_efectiva": "Reinforcement as built: top {sus}{suplr1}{suplr2} · bottom {jos}{suplcamp} · stirrups Ø{phi}/{critic} cm (critical zone) and /{curent} cm (current zone)",
  "struct.arg.supl_reazem_1": " + {n}Ø{phi} (support 1)",
  "struct.arg.supl_reazem_2": " + {n}Ø{phi} (support 2)",
  "struct.arg.supl_camp_scurt": " + {n}Ø{phi} (span)",
  "struct.arg.s11_explicatie": "What the beam «exports» for sizing the columns (the weak-beam / strong-column hierarchy, ΣM_Rc ≥ γ·ΣM_Rb at every joint) — the full beam↔column loop closes at the next step.",
  "struct.arg.strange": "▴ collapse",
  "struct.arg.arata_tot": "▾ show all",
  "struct.arg.corectia_4_5": " (from {m} + the 4.5 m correction)",
  "struct.arg.formula_exemplu": "Example, point R1_sus (fundamental): M_Rd = (A_s·f_yd + c·A_pl·f_yd,pl)·z = ({as} cm²·{fyd} MPa {placa})·{z} cm / 1000 = ",
  "struct.arg.formula_exemplu_placa": "+ {coef}·A_slab·f_yd,slab",
  "struct.arg.formula_exemplu_fara_placa": "+ 0 (no slab in the tensioned zone)",
  "struct.arg.formula_exemplu_sursa": "(sheet B: AX15; A_s = {bare}).",
  "struct.arg.sectiunea_la": "Section at {unde}",
  "struct.arg.la_reazem_r1": "the support (R1)",
  "struct.arg.la_mijlocul_campului": "mid-span",
  "struct.arg.cota_placa": "slab {v}",
  "struct.arg.valori_mcr": "M_cr = {mcr} kNm; I_I = {ii} cm⁴; I_II(span) = {iii} cm⁴ (x_II = {x} cm, ζ = {zeta})",
  "struct.arg.incercari#n": "1 attempt|{n} attempts|{n} attempts",
  "struct.arg.taie_bara": "Why the bar is cut",
  "struct.arg.otelul_livreaza_bare": "Steel is delivered in 12 m bars. A longer run is made up of pieces lapped over the lap length l₀ (EC2 §8.7.3: l₀ = 1.5·l_bd when more than half the bars are lapped in the same section).",
  "struct.arg.lungimea_suprapunerii_ba": "Over the lap the bar does not transmit the force through its own section but through bond to the concrete — which is why the lap is placed where the bar is least stressed: the TOP bars in the span (the top fibre is in compression there), the BOTTOM ones near the support.",
  "struct.arg.1_geometria_sectiunea": "1. The geometry and the active section",
  "struct.arg.3_combinatia_dimensionea": "3. The combination that sizes it",
  "struct.arg.4_armarea_aleasa": "4. The reinforcement chosen (the final solution + how it was reached)",
  "struct.arg.pasul_pune_multipli": "The spacing is set in multiples of 2.5 cm; the code caps are rounded DOWN to that step.",
  "struct.arg.5_momentele_capabile": "5. The resisting moments and the R/E check",
  "struct.arg.forta_axiala_concomitent": "the concurrent axial force",
  "struct.arg.6_ductilitatea_sectiunii": "6. Section ductility: x ≤ x_u",
  "struct.arg.7_dirijarea_articulatiei": "7. Steering the plastic hinge (the 1.45 rule)",
  "struct.arg.8_forta_taietoare": "8. The shear force — three zones",
  "struct.arg.q_10_v": "Q̄ = 10·V/(b·d·f_ctd) — the relative intensity of the shear",
  "struct.arg.9_sls_sageata": "9. SLS — deflection and crack width (in service)",
  "struct.arg.momentul_cvasipermanent": "the quasi-permanent moment (GS) at the section",
  "struct.arg.efortul_armatura_intinsa": "the stress in the tension reinforcement in state II",
  "struct.arg.distanta_maxima_intre": "the maximum distance between cracks",
  "struct.arg.deschiderea_caracteristi": "the characteristic crack width",
  "struct.arg.10_rotirile_capabile": "10. The rotation capacity of the hinges (INFORMATIVE)",
  "struct.arg.10b_reguli_constructive": "10b. Detailing rules — the reinforcement has to be BUILDABLE too",
  "struct.arg.11_interfata_catre": "11. The interface to the column step",
  "struct.arg.momentul_calcul_negativ": "the design moment; negative = the top fibre in tension",
  "struct.arg.pozitia_statiei_axul": "the position of the station, from the axis of column 1",
  "struct.arg.forta_axiala_concomitent2": "the concurrent axial force (negative = compression)",
  "struct.arg.combinatia_da_extremul": "the combination that gives the extreme — the source of the whole row",
  "struct.arg.raportul_capabil_efectiv": "the ratio of capacity to demand — below 1 it does not pass",
  "struct.arg.constanta_a_metodei": "a constant of the method",
  "struct.arg.acoperirea_utila_sus": "the effective cover at the top (a_top = c + Ø/2 [+1.5 cm for row 2])",
  "struct.arg.inaltimea_utila_d": "the effective depth d = h − a_top",
  "struct.arg.distanta_intre_armaturi": "the distance between the reinforcement layers ds = d − a_bottom",
  "struct.arg.bratul_parghie_z": "the lever arm z (corrected for the position of the slab reinforcement)",
  "struct.arg.sageata_durata_vs": "the long-term deflection δ against the limit l₀/250 (total) and l₀/500 (informative, after the finishes)",
  "struct.arg.pasul_ruleaza": "The step is running…",
  "struct.arg.grinda_extrag_eforturile": "For each beam: the forces per combination are extracted from the tensor of Step 11, the reinforcement is chosen in a loop, and the resisting moments, the neutral axis, the steering of the plastic hinge and the shear over 3 zones are checked — following the engineer's check sheet (GRINZI_CP).",
  "struct.arg.ruleaza_nou_meniul": "Run it again from the left-hand menu.",
  "struct.arg.inseamna_verdictul": "What the verdict means.",
  "struct.arg.taietoarea": "the shear",
  "struct.arg.ductilitatea_sectiunii": "section ductility",
  "struct.arg.marja_subdimensionare_ac": "The under-sizing margin accepted:",
  "struct.arg.bucati": "pieces",
  "struct.arg.otel_bara_m": "steel / bar [m]",
  "struct.arg.zonele_portocalii_capete": "The plum zones at the ends = the RIGID lengths.",
  "struct.arg.bara_dedublata_petrecere": "The DOUBLED bar = the lap",
  "struct.arg.banda_rosie_capete": "the red band at the ends is the critical zone",
  "struct.arg.zona_rigida": "What the «rigid zone» is.",
  "struct.arg.conteaza": "Why it matters.",
  "struct.arg.fata_stalpului": "the FACE of the column",
  "struct.arg.consecinta_practica": "The practical consequence.",
  "struct.arg.zona_rigida_lunga": "The longer the rigid zone (wide columns, blades), the smaller the span over which the beam actually «works» — the span moment drops and a marginal beam can pass the checks simply because the column got wider. But the gain is not free: the same wider column SHORTENS the shear length L_as, and therefore RAISES the capacity design shear V_Ed,cap = γ_Rd·ΣM_Rd/L_as (section 8), and it tightens the anchorage of the bars through the joint — see the Ø cap in section 10b and the check in Step 15.",
  "struct.arg.ax": "from the axis",
  "struct.arg.sus_suplimentar_r2": "Extra top bars at R2",
  "struct.arg.bare_inclinate_45": "Bars inclined at 45°",
  "struct.arg.zona_comprimata_prea": "The compression zone is too deep and the reinforcement at the compression face can no longer be increased (at most 2 rows of bars fit on one face). This is NOT solved with more steel — the concrete section is too small for the moment demanded of it. The solutions, in order of cost: a deeper beam (a larger h raises d and therefore x_u), a wider beam (a larger b makes the compression zone more efficient), or redistributing the moment to other elements. See the iteration log below for the exact point where it is exceeded.",
  "struct.arg.v_rd_s": "V_Rd,s to EC2 (informative)",
  "struct.arg.bare_inclinate": "Inclined bars:",
  "struct.arg.sectiunea": "The section",
  "struct.arg.termenii_folositi_romane": "The terms used, in plain words",
  "struct.arg.combinatia": "The combination",

  // --- STRUCTURE · Step 16, reinforcing the slab ------------------------
  "struct.arpl.unde_vin_momentele": "Where the moments come from",
  "struct.arpl.conturul_ochiului_rezult": "The outline of the panel is not in the result of Step 7 (an old result) — the drawing is made from the outline, so run Step 7 again.",
  "struct.arpl.grinda": "■ beam",
  "struct.arpl.perete_sub_placa": "■ wall under the slab",
  "struct.arpl.fara_reazem": "■ no support",
  "struct.arpl.rosu_x": "red = X",
  "struct.arpl.incastrat_continuu": "fixed (continuous)",
  "struct.arpl.pasul_ruleaza": "The step is running…",
  "struct.arpl.dimensioneaza_ochiurile": "The slab panels are sized: the support scheme from the sides of Step 7, the load from the strips of Step 8, the moments from the two-way distribution, then the meshes and the checks.",
  "struct.arpl.infasuratoarea": "the envelope",
  "struct.arpl.ochiurile_placa": "The slab panels",
  "struct.arpl.ochi_panoul_placa": "A «panel» is the piece of slab between its supports — beams, walls, or the edge of the building. Pick a panel from the list (or click it in the building): below is its full calculation note, drawn.",
  "struct.arpl.dreapta_cladirea_intreag": "On the right is the whole building, with the chosen panel lit in YELLOW: slab panels are surfaces, and clicking any of them selects it here. Clicking a beam or a column opens the step that reinforces it.",
  "struct.arpl.directie": "Direction",
  "struct.arpl.capatul_i": "End i",
  "struct.arpl.capatul_j": "End j",
  "struct.arpl.incarcarea": "The load",
  "struct.arpl.coeficienti": "Coefficients",
  "struct.arpl.m_camp": "M span",
  "struct.arpl.directia_duce_incarcare": "this direction carries no load",
  "struct.arpl.fem_guverneaza": "· FEM governs",
  "struct.arpl.pozitie": "Position",
  "struct.arpl.peste_gama": "(beyond the range)",
  "struct.arpl.consola_are_alta": "A cantilever has a DIFFERENT static scheme from a panel: it is fixed into the slab, so its reinforcement is at the TOP and continues into the span of the slab, and the deflection is checked with K = 0.4. The wall on the balcony side (masonry, or concrete where the architecture asks for it) bears at the tip, where the lever arm is largest.",
  "struct.arpl.verificarile": "The checks",
  "struct.arpl.iesire": "Projection",
  "struct.arpl.prindere_placa": "Fixity into the slab",
  "struct.arpl.armatura_sus": "Top reinforcement",
  "struct.arpl.nicio_consola_pasul": "No cantilevers: Step 2 found no cantilever slabs (balconies) on the storeys modelled.",
  "struct.arpl.transferul_diafragma_per": "The transfer from the diaphragm into the walls",
  "struct.arpl.armatura_conectare": "Connecting reinforcement",
  "struct.arpl.strapungerea": "Punching",
  "struct.arpl.stalp": "Column",
  "struct.arpl.v_ed_prin": "V_Ed through the slab",
  "struct.arpl.ochiuri_casa_scarii": "Panels with a stairwell / lift / service duct",
  "struct.arpl.acolo_placa_are": "There the slab actually has an OPENING, but Step 2 gives the zone (with the label from the drawing), not the outline of the opening: the slab is modelled as continuous, so the mass and the stiffness are locally overestimated, and the edge of the opening is not checked.",
  "struct.arpl.verifica_pasul_inca": "What the step does NOT check (yet)",

  // --- STRUCTURE · Step 18, reinforcing the foundation ------------------
  "struct.arf.pasul_ruleaza": "The step is running…",
  "struct.arf.armeaza_grinzile_fundare": "The foundation beams are reinforced, axis by axis: the longitudinal bars from the moment envelope of Step 12, the links from the strut-and-tie model, the base mesh from the cantilever moment.",
  "struct.arf.b_h_inima": "B × H · web",
  "struct.arf.otel": "Steel",
  "struct.arf.piesa_spatiu": "The member in space",
  "struct.arf.albastru_carcasa_continu": "Blue = the continuous cage (running end to end), amber = the extra bars, each over its own length — the faded ends are the shift a_l plus the anchorage l_bd. The links are drawn at the actual spacing of their zone; green = the transverse mesh and the distribution bars in the base. The grey boxes above are the columns.",
  "struct.arf.barele_suplimentare_unde": "The extra bars and where they are cut",
  "struct.arf.fata": "Face",
  "struct.arf.lucreaza": "Works over",
  "struct.arf.bara_croita": "Bar cut",
  "struct.arf.randul_2": "· row 2",
  "struct.arf.etrierii_zone": "The links, by zone",
  "struct.arf.pana": "From … to",
  "struct.arf.v_maxim_zona": "V maximum in the zone",
  "struct.arf.eforturile_au_cerut": "The forces that called for the reinforcement",
  "struct.arf.m_fibra_jos": "M⁺ (the bottom fibre in tension)",
  "struct.arf.m_fibra_sus": "M⁻ (the top fibre in tension)",
  "struct.arf.starea_verifica_fisura": "(the state the crack is checked in)",
  "struct.arf.armatura_aleasa": "The reinforcement chosen",
  "struct.arf.pozitia": "Position",
  "struct.arf.aleasa": "Chosen",
  "struct.arf.observatii": "Notes",
  "struct.arf.etrieri_inima": "links in the web",
  "struct.arf.transversale_talpa": "transverse bars in the base",
  "struct.arf.consola_talpii_np": "the base cantilever (NP 112 §II.6.1.1)",
  "struct.arf.repartitie_talpa": "distribution bars in the base",
  "struct.arf.armaturi_laterale": "side bars",
  "struct.arf.verificarile_axului": "The checks on the axis",
  "struct.arf.unde_ax": "Where on the axis",
  "struct.arf.otelul_axului_pozitii": "The steel of the axis, by position",
  "struct.arf.bucati": "Pieces",
  "struct.arf.aplica_grinzii_fundare": "What does NOT apply to a foundation beam",
  "struct.arf.cerinte_le_are": "Requirements a floor beam has and which we do NOT check here — stated explicitly, so that their absence from the table does not look like an oversight.",

  // --- STRUCTURE · the calculation preferences ---------------------------
  "struct.pref.incarca": "Loading…",
  "struct.pref.setari_calcul": "Calculation settings",
  "struct.pref.valori_alese_cautare": "Values the search chose for the current solution (widening the base, thickening the slab, capping a beam). They are used in the calculation OVER your settings, but they do not replace them: they are cleared on reset and remade on the next run.",
  "struct.pref.reglat": "Set at",
  "struct.pref.executia_armaturii": "Reinforcement fabrication",

  // --- STRUCTURE · Step 4, the structural model --------------------------
  "struct.modl.nivel_infrastructura_sal": "a substructure storey: the jump against the superstructure is the one the rigid box is after, not an irregularity",
  "struct.modl.sub_prag_nivelul": "below the threshold: the storey is softer than §4.4.3.3(2) allows",
  "struct.modl.12ei_h3_stalpii": "Σ12EI/h³ over the storey's columns, with the nodes locked against rotation — informative; it ignores the beams",
  "struct.modl.raportul_rigiditate_cadr": "the frame stiffness ratio against the storey below",
  "struct.modl.unde_punem_stalpii": "Where do we put the columns?",
  "struct.modl.stalp_picior_masa": "A column is like a table leg: it holds the floor up. We put one at every corner of every room and wherever two beams meet or change direction.",
  "struct.modl.grinda_duce_greutatea": "A beam carries the weight of the floor towards its ends — and there a column has to be waiting to take it further down, to the foundation. A corner with no column is like a table with no leg at that corner.",
  "struct.modl.unde_punem_grinzile": "Where do we put the beams?",
  "struct.modl.grinda_bara_beton": "A beam is a concrete bar laid down, hidden in the wall above the doors and windows. It catches the edge of the floor and carries its weight to the columns. We put beams on the axes of the persistent walls (the ones present on every storey), so that each room is framed.",
  "struct.modl.daca_grinda_sta": "If the beam sits on a wall, it is not seen in the room (architects dislike exposed beams). Each room becomes a «slab panel»: a frame closed by beams on which the floor rests, like a canvas stretched over a stretcher.",
  "struct.modl.unde_vin_inaltimile": "Where do the storey heights come from?",
  "struct.modl.etaj_model_are": "Every storey in the model has exactly its real height, taken from Step 2: there the level marks written on the elevations were read (e.g. «CONCRETE LEVEL FLOOR 1 +3.35»), and a storey height is the difference between two consecutive levels: h_storey = level_above − level.",
  "struct.modl.inaltimea_etajului_lungi": "The storey height is the length of the columns — it enters directly into their stiffness (which falls with the cube of the length!), into the weight of the structure and into the levels at which the floors sit. A wrongly assumed value would skew every analysis downstream.",
  "struct.modl.daca_doi_stalpi": "If two columns joined by a beam are more than 6 m apart, the beam would have to carry too much on its own. We put a column in the middle; if that still is not enough, two columns at the thirds — until no piece exceeds 6 m.",
  "struct.modl.grinda_scandura_intre": "A beam is like a plank between two chairs: the further apart the chairs, the more it sags in the middle (the deflection grows with the fourth power of the span!). In concrete, beyond ~6 m the beam would have to be much deeper and dearer — an extra column is cheaper.",
  "struct.modl.sectiunea_stalpului_cm": "the column section, in cm",
  "struct.modl.presupusa_initial_pasul": "assumed initially (Step 4); resized at the strength calculation",
  "struct.modl.distanta_maxima_intre": "the maximum distance between two columns on the same beam",
  "struct.modl.regula_practica_document": "a rule of practice from the method document — beyond 6 m intermediate columns are added (1 at midspan or 2 at the thirds)",
  "struct.modl.latimea_inaltimea_grinzi": "the width × depth of the beam, in cm",
  "struct.modl.liniile_stau_grinzile": "the lines the beams sit on (the middle of the persistent walls)",
  "struct.modl.peretii_persistenti_nive": "the walls persistent across all storeys, from the architectural analysis (Step 2)",
  "struct.modl.inaltimea_fiecarui_etaj": "the height of each storey, in metres",
  "struct.modl.pasul_2_cotele": "Step 2 — the level marks read off the elevations",
  "struct.modl.cota_fiecarui_planseu": "the level of each floor (the cumulative heights: z₁ = h₁, z₂ = h₁ + h₂ …)",
  "struct.modl.calculata_pas_inaltimile": "computed in this step from the heights of Step 2",
  "struct.modl.inaltime_rezerva_folosit": "a fallback height, used only if Step 2 did not find the level of a storey",
  "struct.modl.valoare_implicita_apare": "a default value — a warning appears when it is used",
  "struct.modl.deschiderea_maxima_admis": "the maximum span permitted between columns",
  "struct.modl.regula_practica_document2": "a rule of practice from the method document (Phase 2)",
  "struct.modl.stalpi_adaugati_aceasta": "columns added by this rule on the current storey",
  "struct.modl.calculati_automat_pas": "computed automatically in this step",
  "struct.modl.modul": "Per module",
  "struct.modl.pasul_ruleaza": "The step is running…",
  "struct.modl.structura_propune_pereti": "The structure is proposed on the persistent walls from Step 2 (the same columns and beams on every storey), then each storey's diagram is drawn with its simplified walls and its persistent walls.",
  "struct.modl.ruleaza_nou_meniul": "Run it again from the left-hand menu.",
  "struct.modl.presupuneri_initiale_rev": "Initial assumptions (we come back to them)",
  "struct.modl.clasa": "Class",
  "struct.modl.pereti_proprii_m": "Own walls [m]",
  "struct.modl.stalpi_noi": "New columns",
  "struct.modl.stalpi_plantati": "Planted columns",
  "struct.modl.stalpi_placa": "Columns on the slab",
  "struct.modl.stalpi_liberi": "Free-standing columns",
  "struct.modl.fara_structura_m": "No structure [m]",
  "struct.modl.toate": "all",
  "struct.modl.stalpii_plantati_element": "PLANTED columns (PRIMARY seismic elements — the decision taken in primary seismic element): they exist only at storey level, with their base on a transfer/host beam of the storey below, not on the foundation. The host beam receives amplified demands in Step 13 (capacity design: γ_Rd plus the vertical component of the earthquake) and suspension reinforcement (EC2 §9.2.5), and the base of the column is checked in Step 15 as an anchorage into the beam.",
  "struct.modl.stalp": "Column",
  "struct.modl.exista_doar_nivelul": "Exists only at storey",
  "struct.modl.plantat_grinda": "Planted on beam",
  "struct.modl.verificarile_amplasare": "The placement checks",
  "struct.modl.regulile_respectate": "✓ every rule respected",
  "struct.modl.regula_incalcata_desfaso": "Each rule broken unfolds: clicking an element in the list opens its storey and selects it on the drawing below (in blue). The link is addressable — you can copy it or send it.",
  "struct.modl.regula": "Rule",
  "struct.modl.incalcarile_una_cate": "The breaches, one by one",
  "struct.modl.peste_propunerea_automat": "Over the automatic proposal above, the persistent «modificari» overlay of Step 19 is applied. The column sections below are NO LONGER the 30×30 assumed initially — they are the ones that enter Step 7 (FEM) and Step 15. Each op is validated against the band of persistent walls: a lengthened or widened column has to stay inside the wall (the «we do not change the architecture» guarantee). The «slip» is the movement of the column centre (on the long axis, on the short one) needed for the new footprint to fit inside the wall.",
  "struct.modl.sectiunea_rezultata_cm": "The resulting section [cm]",
  "struct.modl.op_uri_sarite": "SKIPPED ops — asked for by Step 19, but the wall geometry does not allow them. They stay in the overlay (they are retried on every run) and are NOT applied to the model. The same table, grouped by cause, is also in the report of Step 19:",
  "struct.modl.maxim_x_cm": "Maximum on X [cm]",
  "struct.modl.maxim_y_cm": "Maximum on Y [cm]",
  "struct.modl.propunerea_naste_dintr": "The proposal is not born at one go: step 1 is the naive generator (a beam on every wall, columns at the ends), and each following step adds EXACTLY one deterministic repair over the state before it. Each state is frozen in a visitable clone (a «breakpoint»), so that it is immediately visible which step introduces a problem — a skewed beam, a corner inside a room. The solution continues with the LAST step: that is the geometry Steps 5→16 run on, and the page above shows that one.",
  "struct.modl.adauga": "What it adds",
  "struct.modl.stalpi": "Columns",
  "struct.modl.starea_inghetata": "The frozen state",
  "struct.modl.a_esuat": "✗ it failed",
  "struct.modl.geometria_ecran": "✓ this is the geometry on screen",
  "struct.modl.fara_clona": "no clone",

  // --- STRUCTURE · Step 2, reading the architecture ----------------------
  "struct.arh.descarca_dwg": "Download DWG",
  "struct.arh.unde_vin_inaltimile": "Where do the heights come from?",
  "struct.arh.fatadele_au_scrise": "The elevations carry the level marks of each storey (e.g. «CONCRETE LEVEL GROUND -0.15», «CONCRETE LEVEL FLOOR 1 +3.35»). The formula: h_storey = the level above − the level. E.g.: +3.35 − (−0.15) = 3.50 m.",
  "struct.arh.inaltimea_etaj_intra": "The storey height enters the structural model directly (Step 4: the length of the columns and the levels of the floors) and the calculation of the building's weight. CONCRETE (structural) levels are preferred to FINISHED ones, because they measure exactly between the concrete slabs — that is what the columns stand on.",
  "struct.arh.peretii_simplificati": "What are the simplified walls?",
  "struct.arh.peretii_plan_redusi": "The walls from the plan, reduced to straight rectangles. A T or L wall becomes two overlapping rectangles; the pieces cut by columns are joined into a single continuous rectangle. Walls drawn by hand from lines only are detected too — the space between two parallel lines.",
  "struct.arh.analiza_structurala_are": "Structural analysis needs simple, robust geometry: rectangles with an axis and a thickness, not the jagged outline of the architectural drawing. The quality criterion: at least 90% of the points on the wall layer have to fall inside the rectangles — the percentage is shown per storey.",
  "struct.arh.golurile_fasii_coliniare": "Openings as strips collinear with the wall",
  "struct.arh.usa_fereastra_devine": "Each door and window becomes a thin strip, on the axis of the wall it sits in and with that wall's thickness — the same shape as the simplified walls, so it can be used the same way. The type comes from the layer's role (doors / windows), and the host wall is attached where a collinear one exists.",
  "struct.arh.peste_gol_poate": "A beam can cross an opening without disturbing the architecture — but only if you know where the opening is. Until now Step 2 exported nothing about them: Step 4 inferred a few by itself, only those that join two collinear walls.",
  "struct.arh.zonele_afara": "The «outdoor» zones",
  "struct.arh.terasele_balcoanele_logi": "The terraces, balconies and loggias of the storey, with their outlines. They come from two sources that complement each other: the architect's zone label (which gives the name and the outline of the room) and the cantilever slabs measured geometrically (which give what is not labelled).",
  "struct.arh.suprafata_construita_dec": "They are built area, so they enter the floor — but they are not rooms: you do not put a column in the middle of a terrace, and a balcony under 2 m is made as a plain concrete slab (the walls on its sides stay masonry). Without them, the 32 m² of terrace on floor 1 ended up as ordinary slab panels in Step 7.",
  "struct.arh.cotele_unde_luate": "The levels — where they are taken from",
  "struct.arh.rand_text_existent": "Each row is a TEXT that exists in the elevation drawing, read as it stands: the storey it refers to, the level in metres and the exact text from the DWG. The «Source» column says which file — the drawing can be opened and the text checked.",
  "struct.arh.cota_valoare_citita": "A level is a value read, not computed: if the reading is wrong, everything that follows (the storey heights, the column lengths, the weight of the building) is wrong. That is why the source text and the file it comes from are shown.",
  "struct.arh.inseamna_coloana": "What each column means",
  "struct.arh.datele_masurate_nivel": "The data measured for each storey — they are carried on into Steps 3, 4, 5 and 7, so that every calculation uses the same values.",
  "struct.arh.a_vs_c": "A vs C — who gets which imposed load",
  "struct.arh.etichetele_spatiilor_pla": "The space labels on the plan are classified: A = living spaces (q = 1.5 kN/m²), C = circulation and common areas in multi-occupancy buildings — the stairwell, common halls, balconies (q = 3.0 kN/m²). In houses everything stays A.",
  "struct.arh.pasul_8_aplica": "Step 8 applies the imposed load differentially: case Q1 on the A zones and Q2 on the C zones — the C area of each storey decides where the larger load falls (SR EN 1991-1-1).",
  "struct.arh.inaltimea_unui_nivel": "the height of a storey against the building's ±0.00 datum, in metres",
  "struct.arh.textele_fatade_citite": "the texts on the elevations, read by the LLM",
  "struct.arh.inaltimea_etajului_difer": "the storey height = the difference between two consecutive levels",
  "struct.arh.calculata_aici_folosita": "computed here; used by Steps 3, 4 and 7",
  "struct.arh.inaltimea_rezerva_cand": "the fallback height when no levels are found",
  "struct.arh.valoare_implicita_apare": "a default value — a warning appears when it is used",
  "struct.arh.acoperirea_punctelor_lay": "the coverage of the points on the wall layer",
  "struct.arh.criteriul_acceptare_extr": "the acceptance criterion of the extraction — below the threshold a warning appears",
  "struct.arh.toleranta_unirea_bucatil": "the tolerance for joining collinear pieces",
  "struct.arh.unghiuri_180_acelasi": "angles of ~180° = the same wall, extended into the corners",
  "struct.arh.nivelul_refera_cota": "the storey the level refers to",
  "struct.arh.potrivit_llm_numele": "matched by the LLM against the storey names in the architectural plans",
  "struct.arh.inaltimea_fata_reperul": "the height against the ±0.00 datum, in metres",
  "struct.arh.numarul_textul_fatada": "the number in the elevation text",
  "struct.arh.textul_exact_dwg": "the exact text from the DWG",
  "struct.arh.entitatea_tip_text": "the text entity in the elevation drawing",
  "struct.arh.fisierul_dwg_s": "the DWG file the storey was extracted from",
  "struct.arh.planurile_arhitectura_in": "the architectural plans uploaded — opened from the «see the drawing» button",
  "struct.arh.nivelul_foloseste_planul": "the storey uses the plan of a storey below — in blocks, identical floors have a single drawn plan",
  "struct.arh.titlul_planului_plan": "the title of the plan («PLAN FLOOR 1÷3») or, where nothing is written, the assumption that floor 1 repeats until the number of storeys from the elevation is reached",
  "struct.arh.cati_milimetri_are": "how many millimetres one plan unit holds (1 = mm, 1000 = m)",
  "struct.arh.dedusa_inaltimea_textelo": "deduced from the height of the texts — the DXF header gets it wrong on many plans; all the lengths below are computed after conversion to millimetres",
  "struct.arh.inaltimea_etajului_dista": "the storey height (the vertical distance between its slab and the slab above)",
  "struct.arh.diferenta_cotelor_fatade": "the difference of the levels from the elevations — the exact subtraction appears under the figure; used by the structural model (Step 4)",
  "struct.arh.suprafata_construita_a": "the built area of the storey (the outer outline of the walls)",
  "struct.arh.masurata_aici_folosita": "measured here; used by the structural strategy (Step 3) and the foundation (Steps 5–6)",
  "struct.arh.criteriul_calitate_proce": "the quality criterion: what percentage of the points of the wall drawing fall inside the simplified rectangles (target ≥ 90%)",
  "struct.arh.masurat_aici_sub": "measured here — below the threshold a warning appears",
  "struct.arh.cati_pereti_simplificati": "how many simplified walls came out on the storey",
  "struct.arh.extractia_pas": "the extraction in this step",
  "struct.arh.suma_lungimilor_peretilo": "the sum of the lengths of the simplified walls",
  "struct.arh.calculata_dreptunghiuri": "computed from the rectangles",
  "struct.arh.clasificarea_llm_a": "the LLM's classification of the space labels",
  "struct.arh.circulatii_comune_balcoa": "common circulation / balconies in blocks — q = 3.0 kN/m²",
  "struct.arh.idem_zona_camera": "the same; the zone = the room that contains the label",
  "struct.arh.poligonul_desenat_diagra": "the polygon drawn on the diagram",
  "struct.arh.camera_ingradita_intre": "the room enclosed by the simplified walls; in open spaces, a marker with the area from the zone stamp, clipped on the same walls",
  "struct.arh.sursa_necunoscuta": "unknown source",
  "struct.arh.unde_vine_cifra": "Where each figure of the step comes from: which layers the structure was found on, by how much a footprint broken by doors was closed, what was assumed where labels were missing. These are not checks — this step reads the drawings, it does not judge the structure, so nothing here «fails» or «passes». What DOES fail is seen at the steps with criteria, gathered in Step 19.",
  "struct.arh.toata_cladirea": "the whole building",
  "struct.arh.pasul_ruleaza": "The step is running…",
  "struct.arh.identificarea_nivelurilo": "Identifying the storeys and elevations (LLM), reading the level marks off the elevations, then for each storey: choosing the layers and computing the simplified walls. The first run can take a few minutes (the drawings are interpreted one by one).",
  "struct.arh.ruleaza_nou_meniul": "Run it again from the left-hand menu.",
  "struct.arh.regim_inaltime": "Storey count",
  "struct.arh.cota": "Level",
  "struct.arh.text_sursa": "Source text",
  "struct.arh.sursa": "Source",
  "struct.arh.cotele_nivel_s": "The level marks — none found",
  "struct.arh.fatada_incercata": "Elevation tried",
  "struct.arh.plan_sursa_dwg": "Source plan (DWG)",
  "struct.arh.inaltime": "Height",
  "struct.arh.lungime_totala": "Total length",
  "struct.arh.peretii_simplificati2": "the simplified walls",
  "struct.arh.tip_cladire": "Building type",

  // --- STRUCTURE · the estimate, the work recipes and the prices ---------
  "struct.ret.incarca": "Loading…",
  "struct.ret.deviz_retete_executie": "Cost estimate, work recipes & prices",
  "struct.ret.afiseaza_sumele": "Show the amounts in",
  "struct.ret.devizul_apare_dupa": "The estimate appears once the steps that give the quantities have run (7, 6, 12, 13, 14, 16).",
  "struct.ret.componentele_apar_odata": "The components appear with the estimate, once the reinforcement steps have run.",
  "struct.ret.planul_cofrare_apare": "The formwork plan appears with the estimate, once the steps that give the geometry have run (4, 7, 12, 13).",
  "struct.ret.p_u_manopera": "Unit rate, labour",
  "struct.ret.total_manopera": "Total labour",
  "struct.ret.total_cost_direct": "TOTAL DIRECT COST (excl. VAT)",
  "struct.ret.cost_direct_pret": "From direct cost to price",
  "struct.ret.procente_aplicate_dupa": "Percentages, applied AFTER the exact part. The ordering of solutions (Step 6, Step 19) stays on the direct cost: the percentages are multipliers that do not depend on the move, so they would inflate every delta without changing the order.",
  "struct.ret.deviz_declarat_ascuns": "What is NOT in the estimate (declared, not hidden)",
  "struct.ret.articolele_jos_exista": "The items below exist in a real estimate, but we cannot compute them from the model yet. They are not covered with percentages: a missing item that cannot be seen makes the estimate look complete exactly where it is not.",
  "struct.ret.unde_vine_pretul": "Where the price comes from:",
  "struct.ret.extrasul_armatura_bara": "The bar schedule — bar by bar",
  "struct.ret.lungimile_includ_ancorar": "The lengths include the anchorages (EC2 §8.4), the laps (§8.7), the shift of the moment diagram (§9.2.1.3) and the 10·Ø hooks (P100-1 §5.7.1(5)). Click an element to see its positions.",
  "struct.ret.compune_lungimea": "What the length is made of",
  "struct.ret.derivat_norme": "Derived from the standards",
  "struct.ret.pret_referinta": "Reference price",
  "struct.ret.k_manopera": "k labour",
  "struct.ret.retetele_stabilesc_nivel": "The recipes no longer set the LEVEL of the price (the reference does): they say how the element is built and, through the norms, how the price is apportioned across elements. They also give the duration and the crew.",
  "struct.ret.retetele": "← all the recipes",
  "struct.ret.activitatile_normele_lor": "The activities and their norms",
  "struct.ret.plateste": "Paid as",
  "struct.ret.unde_vin_cantitatile": "Where the quantities come from",
  "struct.ret.folosesc_preturile_refer": "How the reference prices are used",
  "struct.ret.preturile_referinta_eur": "The reference prices (EUR excl. VAT)",
  "struct.ret.modelul_transport_ramane": "The transport model stays PHYSICAL where the kilometres are felt (concrete and earth) and becomes a single percentage where they are not (steel, formwork, accessories — 1.4 % of the price). The reference's material prices are delivered to site in its own region, so the formulas below add only the DIFFERENCE against it.",
  "struct.ret.otelul_structura_pretulu": "Steel — the structure of the price",
  "struct.ret.referinta_da_singur": "The reference gives a single price for all the steel. The scale below differentiates it by diameter class, normalised on the project's real composition (the weighted mean stays the reference price). Bending per PIECE is 0 by default: on the reference estimate it would be ~1.6 % of the steel cost — measurable, but negligible against the uncertainty of the other prices.",
  "struct.ret.manopera_raportul_dintre": "Labour (the ratio between trades)",
  "struct.ret.nivelul_decide_pretul": "The level no longer decides the price — the reference does. The ratio between trades does, however, decide how the labour is split across elements.",
  "struct.ret.utilaje_operator_unde": "Plant (with operator, where applicable)",
  "struct.ret.materiale_fara_reper": "Materials with no benchmark in the reference",
  "struct.ret.cursul_afisare": "The display exchange rate",
  "struct.ret.salveaza": "Save",
  "struct.ret.revino_valorile_implicit": "Back to the default values",
  "struct.ret.intro": "The quantities come from the geometry and reinforcement computed by the steps — including the bar schedule, bar by bar. The price level comes from <b>{referinta}</b>, apportioned across elements with the norms of the work recipes: the reference says “10\u00a0€/m³ labour for any concrete”, the norms know that pouring a 2\u00a0m³ column costs more per m³ than a 25\u00a0m³ slab. The same figures give the unit prices by which Step\u00a06 and Step\u00a017 rank the interventions.",
  "struct.ret.moneda_eur": "EUR (the calculation currency)",
  "struct.ret.moneda_ron": "lei (rate {curs} as of {la})",
  "struct.ret.deviz_incomplet": "The estimate is incomplete — still needed: {lipsa}.",
  "struct.ret.tab_deviz": "The estimate",
  "struct.ret.tab_retete": "Recipes",
  "struct.ret.modificate": " (modified)",
  "struct.ret.card_cost_direct": "Direct cost of the structure",
  "struct.ret.card_cost_direct_sub": "material + labour, excl. VAT",
  "struct.ret.card_materiale": "Materials",
  "struct.ret.card_materiale_sub": "concrete by class, steel by diameter, formwork",
  "struct.ret.card_manopera": "Labour",
  "struct.ret.card_manopera_sub": "including the plant for placing, as in the reference",
  "struct.ret.card_cu_indirecte": "Price with indirect costs",
  "struct.ret.repere": "Sanity checks: <b>{kg} kg steel/m³</b> of concrete and <b>{cofraj} m² formwork/m³</b> (the reference estimate gives {kg_ref} kg/m³ and {cofraj_ref} m²/m³).",
  "struct.ret.repere_in_afara": "<b>⚠ outside the usual band (100–220 kg/m³, 3–7 m²/m³) — a signal, not an error: check the reinforcement and the geometry, not the price.</b>",
  "struct.ret.repere_total": "Total: {beton} m³ concrete · {otel} kg steel · {cofraj} m² formwork.",
  "struct.ret.th_nr": "No.",
  "struct.ret.th_articol": "Item",
  "struct.ret.th_um": "Unit",
  "struct.ret.th_cantitate": "Quantity",
  "struct.ret.th_pu_material": "Unit rate, material",
  "struct.ret.th_total_material": "Total materials",
  "struct.ret.cost_direct": "Direct cost",
  "struct.ret.total": "Total",
  "struct.ret.material_pu": "material {pu}",
  "struct.ret.manopera_pu": "labour {pu}",
  "struct.ret.ore_om_kg_paren": "({v} man-hours/kg)",
  "struct.ret.categoria_element": "Element category",
  "struct.ret.extras_sumar#n": "1 bar mark in the chosen category, out of {total} in the project · {kg} kg with the cutting-waste allowance ({spor} %)|{n} bar marks in the chosen category, out of {total} in the project · {kg} kg with the cutting-waste allowance ({spor} %)|{n} bar marks in the chosen category, out of {total} in the project · {kg} kg with the cutting-waste allowance ({spor} %)",
  "struct.ret.extras_calculat": " · {pct} % of the steel is computed, the rest is a flat-rate allowance",
  "struct.ret.th_poz": "Mark",
  "struct.ret.th_rol": "Role",
  "struct.ret.th_forma": "Shape",
  "struct.ret.th_buc": "Pcs",
  "struct.ret.th_lungime_bara": "Length of one bar",
  "struct.ret.calibrare_titlu": "Calibrating the norms on the reference price",
  "struct.ret.calibrare_explicatie": "k = reference\u00a0price × quantity ÷ cost\u00a0derived\u00a0from\u00a0the\u00a0norms. The weighted mean comes out exactly at the reference price, while the differences between elements stay the physical ones. A k outside the 0.5…2.5 range is a sign that the norm is wrong, not that the market is odd.",
  "struct.ret.th_familie": "Work family",
  "struct.ret.th_k_material": "k materials",
  "struct.ret.calibrare_otel": "Steel: the reference has a single price (0.79\u00a0€/kg), differentiated by diameter with a normalised scale (factor {factor}), plus the bending surcharge of each shape family.",
  "struct.ret.th_activitate": "Activity",
  "struct.ret.th_echipa": "Crew",
  "struct.ret.th_utilaje": "Plant",
  "struct.ret.th_ore_om_um": "Man-hours / unit",
  "struct.ret.ore_din_extras": "per the bar schedule",
  "struct.ret.ore_din_extras_t": "Not a norm per kg: the fixing hours come from the project's bar schedule, by shape family — straight bars, bars with hooks, stirrups, ties, meshes —, each with its own norm (man-hours/kg) from «Prices, zone & norms → steel». A column with dense stirrups takes more hours per kg than a slab with straight bars.",
  "struct.ret.din_extras": "from the bar schedule (per Ø and shape family)",
  "struct.ret.coduri_deviz": "Estimate codes",
  "struct.ret.flux": "Workflow",
  "struct.ret.tarife_intro": "<b>{nume}</b> — {sursa}. The values are application-wide defaults; what you save here applies only to the current project.",
  "struct.ret.mod_calcul": "Calculation mode",
  "struct.ret.mod_calibrat": "calibrated — the reference gives the mean, the norms give the spread (recommended)",
  "struct.ret.mod_referinta": "reference — flat price, for a direct comparison with a quote",
  "struct.ret.mod_norme": "our norms only — for diagnostics",
  "struct.ret.beton_material": "Concrete {clasa} — material",
  "struct.ret.articol_material": "{articol} — materials",
  "struct.ret.zona_proiectului": "Project region",
  "struct.ret.presetare": "Preset",
  "struct.ret.spor_aprovizionare": "Procurement surcharge",
  "struct.ret.ori_pret_referinta": "× the reference price",
  "struct.ret.fasonare_familie": "Bending — {forma}",
  "struct.ret.montaj_familie": "Fixing — {forma}",
  "struct.ret.curs_leu_euro": "Leu/euro rate",
  "struct.ret.um_cursa": "€/trip",
  "struct.ret.um_tona": "€/tonne",
  "struct.ret.um_ora_om": "€/man-hour",
  "struct.ret.um_ora": "€/hour",
  "struct.ret.um_buc": "€/piece",
  "struct.ret.um_ore_om_kg": "man-hours/kg",
  "struct.ret.salvat": "✓ saved",
  "struct.ret.spec.dulgheri": "Carpenters",
  "struct.ret.spec.fierari": "Steel fixers",
  "struct.ret.spec.betonisti": "Concreters",
  "struct.ret.spec.muncitori_necalificati": "Labourers",
  "struct.ret.utilaj.excavator": "Excavator",
  "struct.ret.utilaj.pompa_beton": "Concrete pump",
  "struct.ret.utilaj.placa_compactoare": "Plate compactor",
  "struct.ret.utilaj.vibrator_beton": "Concrete vibrator",
  "struct.ret.forma.dreapta": "straight bar",
  "struct.ret.forma.dreapta_cu_ciocuri": "bar with hooks",
  "struct.ret.forma.etrier": "stirrup",
  "struct.ret.forma.agrafa": "link",
  "struct.ret.forma.plasa": "mesh",
  "struct.ret.forma.spirala": "spiral",
  "struct.ret.categorie.fundatie": "foundations",
  "struct.ret.categorie.placa_sol": "ground slab",
  "struct.ret.categorie.stalpi": "columns",
  "struct.ret.categorie.grinzi": "beams",
  "struct.ret.categorie.pereti": "walls",
  "struct.ret.categorie.placa": "floor slabs",
  "struct.ret.precizie_text.exact": "quantity fully computed",
  "struct.ret.precizie_text.partial": "part of the quantity is a flat-rate allowance on volume",
  "struct.ret.precizie_text.forfetar": "quantity estimated with an allowance on volume, not computed",
  "struct.ret.art.sapatura": "Excavation",
  "struct.ret.art.umplutura": "Backfill",
  "struct.ret.art.beton": "Concrete pouring",
  "struct.ret.art.elicopterizat": "Power floating",
  "struct.ret.art.cordon_bentonitic": "Bentonite waterstop",
  "struct.ret.art.armatura": "Reinforcement",
  "struct.ret.art.oprire_betonare": "Stop-ends",
  "struct.ret.art.cofraj": "Formwork",
  "struct.ret.art.probe_beton": "Concrete test samples",
  "struct.ret.art.probe_fier": "Reinforcement test samples",
  "struct.ret.art.accesorii": "Sundries",
  "struct.ret.mat.cofraj_fundatie_m2": "Foundation formwork, per m²",
  "struct.ret.mat.cofraj_elevatie_m2": "Superstructure formwork — columns, beams, walls, per m²",
  "struct.ret.mat.cofraj_planseu_m2": "Slab formwork, per m², with props",
  "struct.ret.mat.balast_m3": "Hardcore, per m³",
  "struct.ret.mat.folie_pe_m2": "Polyethylene membrane, per m²",
  "struct.ret.mat.accesorii_armare_kg": "Reinforcement accessories — spacers, tie wire, per kg",
  "struct.ret.ind.organizare_santier": "Site set-up",
  "struct.ret.ind.utilaje_ridicare": "Lifting plant (crane, vertical transport)",
  "struct.ret.ind.indirecte_profit": "Overheads + contractor's profit",
  "struct.ret.ind.diverse_neprevazute": "Sundries and contingencies",
  "struct.ret.ind.tva": "VAT",

  // --- STRUCTURE · Step 10, the lateral displacements --------------------
  "struct.dr.rigiditatea_cadru_d": "the frame stiffness (Muto's D) — THE VERDICT",
  "struct.dr.raportul_cadru_fata": "the frame ratio against the storey below — THE VERDICT",
  "struct.dr.12ei_h3_stalpi": "Σ12EI/h³ over the columns, informative",
  "struct.dr.raportul_rigiditatilor_p": "the ratio of the own stiffnesses, informative",
  "struct.dr.raportul_rigiditatilor_s": "the ratio of the secant stiffnesses, informative",
  "struct.dr.stalpii_nivelului_parant": "the columns of the storey; in brackets, how many have a pinned base (props)",
  "struct.dr.nivel_infrastructura_sal": "a substructure storey: the jump in stiffness against the superstructure is the one the rigid box IS AFTER (C5.8.1), not an irregularity",
  "struct.dr.stalpi_baza_articulata": "columns with a pinned base (props bearing on a beam)",
  "struct.dr.pasul_10_deplasari": "Step 10 — Lateral displacements (drift)",
  "struct.dr.pasul_10_deplasari2": "Step 10 — Lateral displacements (drift), P100-1/2013 Annex E",
  "struct.dr.rigiditatea_laterala_cad": "the lateral FRAME stiffness of the storey — THE VERDICT",
  "struct.dr.geometria_modelului_pasu": "the model geometry (Step 4) on WHOLE CONCRETE; the same quantity Step 4 shows before the chain",
  "struct.dr.ramane_rigiditatea_cadru": "how much is left of the frame stiffness of the storey below",
  "struct.dr.rigiditatea_stalpilor_si": "the stiffness of the columns alone, with the nodes locked against rotation",
  "struct.dr.k_secanta": "K secant",
  "struct.dr.rigiditatea_sistemului_v": "the stiffness of the system as seen at this storey",
  "struct.dr.v_pasul_9": "V from Step 9 and d_re from the DR block, both on the WHOLE CONCRETE model",
  "struct.dr.masa_seismica_a": "the seismic mass of the storey",
  "struct.dr.acceleratia_nivel_biciui": "the storey acceleration — the whip effect itself",
  "struct.dr.retrage_nivelul_plan": "how far the storey steps back in plan against the one below",
  "struct.dr.stalpi": "columns",
  "struct.dr.cati_stalpi_are": "how many columns the storey has, and how many of them have a PINNED base (props bearing on a beam)",
  "struct.dr.deplasarea_relativa_elas": "the elastic storey drift, at the worst point in plan",
  "struct.dr.blocul_dr_tensorului": "the DR block of the tensor (Steps 8+9)",
  "struct.dr.punctul_plan_s": "the point in plan at which the maximum was reached",
  "struct.dr.deplasarea_relativa_star": "the storey drift at the serviceability limit state, against the storey height",
  "struct.dr.idem_starea_limita": "the same, at the ultimate limit state",
  "struct.dr.driftul_sls_s": "the SLS drift with the rigid rotation of the storey removed — the value the letter of (E.1) asks for",
  "struct.dr.coeficientul_sensibilita": "the sensitivity coefficient of the storey drift (the P–Δ effect), on the MEAN displacements",
  "struct.dr.atat": "why that much",
  "struct.dr.costa_fisurarea_masurat": "What cracking costs, measured:",
  "struct.dr.unde_guverneaza": "Where it governs",
  "struct.dr.peste": "over",
  "struct.dr.consecinta": "consequence",
  "struct.dr.masurat": "measured",
  "struct.dr.conditia": "The condition",
  "struct.dr.verifica_deplasarea_rela": "The storey drift is checked at both limit states (P100-1/2013 Annex E) and so is the second-order coefficient θ (§4.6.2.2). Run Steps 8 and 9 first — the drift is read from their tensor.",
  "struct.dr.parametrii_intra_relatii": "The parameters that enter the relations",
  "struct.dr.0_4_clase": "ν = 0.4 for importance classes",
  "struct.dr.verificarile": "The checks",
  "struct.dr.niveluri": "By storey",
  "struct.dr.infrastructura": "(substructure)",

  // --- STRUCTURE · Step 19, the analysis of the solution -----------------
  "struct.opt.reperul_devizului_referi": "the benchmark of the reference estimate: 165 kg/m³ and 4.4 m² of formwork per m³",
  "struct.opt.a_ales_algoritmul": "How the algorithm chose",
  "struct.opt.aplicata": "applied",
  "struct.opt.acoperita_alta_modificar": "covered by another change",
  "struct.opt.refuzata_element_ingheat": "refused — the element is frozen by conformance",
  "struct.opt.verificari_citite": "Checks read",
  "struct.opt.limita": "Review",
  "struct.opt.pasii_lantului_emit": "The steps of the chain that emit no verdicts",
  "struct.opt.prioritate_ridicata": "high priority",
  "struct.opt.fara_date_despre": "— no data about the checks —",
  "struct.opt.0_verificari_picate": "✓ 0 checks failed",
  "struct.opt.cate_pica": "How many fail",
  "struct.opt.verificarea_a_cerut": "The check that asked for the change",
  "struct.opt.lipsea": "How much was missing",
  "struct.opt.abordarea_aleasa": "The approach chosen",
  "struct.opt.abordari_sarite_inainte": "Approaches skipped before it",
  "struct.opt.algoritmul_abordarii_ale": "The algorithm of the approach chosen",
  "struct.opt.a_ales": "how it chose",
  "struct.opt.solutia": "The solution",
  "struct.opt.verificari_picate": "Checks failed",
  "struct.opt.cost_structura": "Structure cost",
  "struct.opt.pasul_19_ruleaza": "Step 19 is running…",
  "struct.opt.pasul_a_rulat": "The step has not run yet. Step 19 is the ANALYSIS of a solution: it gathers the failed checks of every step, gives them a score (a single number, smaller is better) and says what is worth trying next.",
  "struct.opt.2_unde_a": "2. Where it started from and what changed",
  "struct.opt.parghia": "the lever",
  "struct.opt.mutare_promite_scadere": "Each move promises a drop in severity; if it delivers under a quarter of it twice on the same class of problem, the lever is demoted on this project — that is where the solver's self-correction comes from.",
  "struct.opt.grinda_in_afara": "{m} m of beam outside the walls",
  "struct.opt.grinda_pe_nivel": " — {lista}",
  "struct.opt.grinda_nivel_metri": "{nume}: {m} m",
  "struct.opt.grinda_exceptate": " (excluding {lista}, accepted from the preferences)",
  "struct.opt.ce_s_a_centralizat": "What was gathered: {total} checks from {citite} of the {toti} steps that issue verdicts{fara}{lipsa}",
  "struct.opt.din_pasi_lantului": " (out of the chain's {n} steps — the rest issue no verdicts)",
  "struct.opt.atentie_pasi_lipsa": " — WATCH OUT, {n} step(s) with no result read",
  "struct.opt.th_pasul": "Step",
  "struct.opt.th_picate": "Failed",
  "struct.opt.rezultat_lipsa": " — result missing when gathering",
  "struct.opt.enumerati_explicit": "— listed explicitly, so that «0 steps uncovered» means something you can check:",
  "struct.opt.lista_nu_e_in_raport": "— the detailed list is not in this solution's report (it was run by an older version); re-run Step 19 to see it —",
  "struct.opt.th_categoria_hover": "Check category (hover over the title)",
  "struct.opt.vezi_din_cele": "see {n} of the {total} (the rest did not fit in the saved report)",
  "struct.opt.vezi_verificarea_picata": "see the failed check",
  "struct.opt.deviz_indisponibil": "— no cost breakdown is available for this state —",
  "struct.opt.deviz_vechi": "This state's cost comes from the OLD calibration (lei, by recipe): {lei} lei ≈ {eur}. Costs are now built per item, in EUR, with the prices of the reference bill — re-run the solution to get it in the new form (the two figures are NOT comparable).",
  "struct.opt.cost_direct": " direct cost",
  "struct.opt.arie_desfasurata": " · {v} m² of built area",
  "struct.opt.cu_indirecte": " · with indirect costs {v}",
  "struct.opt.materiale_manopera": "materials {mat} · labour {man}",
  "struct.opt.kg_otel_m3": " · {v} kg steel/m³ concrete",
  "struct.opt.cofraj_m2_m3": " · {v} m² formwork/m³",
  "struct.opt.in_afara_benzii": " ⚠ outside the usual band",
  "struct.opt.nicio_modificare": "No change in this iteration — the starting iteration and the final solution only run the chain, without changing the geometry.",
  "struct.opt.probleme_tratate": "{probleme} handled: {aplicate} with a change applied{epuizate}{refuzate}{rest}",
  "struct.opt.cu_catalogul_epuizat": ", {n} with the catalogue exhausted",
  "struct.opt.pe_elemente_inghetate": ", {n} on members frozen by the conformance stage",
  "struct.opt.de_alte_modificari": ", {n} by other changes",
  "struct.opt.ce_s_a_schimbat": "What actually changed",
  "struct.opt.pica_in_puncte": "fails at {n} points",
  "struct.opt.precedenta": "previous —",
  "struct.opt.starea_de_plecare": "the stage's starting state",
  "struct.opt.curenta": "current —",
  "struct.opt.porneste": "starting…",
  "struct.opt.detaliu_acum": " — {acum}",
  "struct.opt.eroare": "Error: {motiv}",
  "struct.opt.scor_val": " · score {v}",
  "struct.opt.motiv_val": " · {v}",
  "struct.opt.raport_apare_cand": "The solution's report appears when its chain finishes.",
  "struct.opt.stuck_titlu": "Why the search stopped",
  "struct.opt.modificari_facute_titlu": "Changes made by the outer loop ({n})",
  "struct.opt.recomandari_geo_titlu": "What it would have changed in the macro geometry — recommendations, not applied in the detail phase ({n})",
  "struct.opt.recomandare_impact": "affects {n}",
  "struct.opt.solutia_fara_raport": "This solution has no report (it stopped before the chain — the reason is in its label in the list).",
  "struct.opt.h3_picate_curenta": "1. Failed checks on the current solution{verdict}{scor}",
  "struct.opt.verdict_dash": " — {v}",
  "struct.opt.durata_min": "The solution took {n} min",
  "struct.opt.rezolutia_lantului": "{sep}the chain's resolution: h = {h} m{fel}",
  "struct.opt.rezolutia_plina": " (the project's full resolution)",
  "struct.opt.rezolutia_proba": " (a trial, coarse mesh)",
  "struct.opt.costul_curente": "The cost of the current solution (by recipe and quantity)",
  "struct.opt.atentie_comparatie": "Careful with the comparison: the solution it started from may be a TRIAL (coarse mesh), while this one is measured at full resolution (h = {h} m) — so the difference is not necessarily a regression, it can be the finer mesh bringing out what the coarse one did not see.",
  "struct.opt.ce_se_recomanda": "What is recommended next ({n})",
  "struct.opt.reason_dash": " — {v}",
  "struct.opt.ce_s_a_modificat": "What changed",
  "struct.opt.amanate_titlu": "Deferred to later iterations ({n}) — each with its reason",
  "struct.opt.amanat_rand": "{verificare} ({sev} %){motiv}",
  "struct.opt.amanat_motiv": ": {v}",
  "struct.opt.netratate_titlu": "Not handled by this algorithm ({n})",
  "struct.opt.netratat_rand": "{verificare}: {motiv}",
  "struct.opt.registru_titlu": "What the previous solution promised vs what it delivered ({n})",
  "struct.opt.th_problema": "problem",
  "struct.opt.th_promis": "promised",
  "struct.opt.th_livrat": "delivered",
  "struct.opt.th_verdict": "verdict",
  "struct.opt.rateu": "miss",
  "struct.opt.livrat": "delivered",
  "struct.opt.demotata": " · demoted",
  "struct.opt.picate_precedenta": "Failed checks on the previous solution{n}",
  "struct.opt.paren_n": " ({n})",
  "struct.opt.a_plecat_din": "It started from: {v}",
  "struct.opt.a_plecat_din_etapa": "It started from the stage's starting state",
  "struct.opt.nota_dash": " · {v}",
  "struct.opt.costul_precedente": "The cost of the previous solution (by recipe and quantity)",
  "struct.opt.probleme#n": "1 problem|{n} problems|{n} problems",
  "struct.opt.refuzate#n": "1 refused|{n} refused|{n} refused",
  "struct.opt.acoperite#n": "1 covered|{n} covered|{n} covered",

  // --- STRUCTURE · the future improvements -------------------------------
  "struct.imb.mesh_grosier_lanturile": "A coarse mesh for the exploratory chains (LOD)",
  "struct.imb.mutarea_unui_stalp": "Moving a column onto ANOTHER wall + the slab thickness in the catalogue",
  "struct.imb.unirea_a_doi": "Merging two neighbouring columns into a single element",
  "struct.imb.reducerea_ec2_6": "The EC2 §6.2.2(6) reduction for concentrated loads near a support",
  "struct.imb.excentricitatea_cm_cr": "The CM–CR eccentricity when separating targets of equal cost",
  "struct.imb.driftul_nivel_sls": "The storey drift (SLS/ULS) as a pipeline check",
  "struct.imb.vantul_cr_1": "Wind (CR 1-1-4) as a load on the model",
  "struct.imb.imbunatatiri_viitoare": "Future improvements",

  // --- STRUCTURE · Step 9, the seismic analysis --------------------------
  "struct.seism.curba_s_d": "The S_d(T) curve: what acceleration a structure of period T «feels» in the design earthquake — a plateau at β0·a_g/q between TB and TC, then falling.",
  "struct.seism.structurile_rigide_t": "Stiff structures (small T) and those on the plateau receive the maximum amplification β0 = 2.5; beyond TC the response falls as 1/T. We divide by q because we design for reduced forces, accepting controlled plastic deformation.",
  "struct.seism.citeste_graficul": "How to read the graph",
  "struct.seism.orizontala_perioada_t": "The horizontal axis is the period T (s), the vertical the spectral acceleration S_d (m/s²). The vertical lines mark the building's fundamental periods on each direction — that is where the acceleration hitting it is read.",
  "struct.seism.daca_t1_cade": "If T₁ falls on the plateau (TB–TC), the building gets the maximum amplification; if it lies to the right of TC, the forces fall as 1/T — which is why flexible structures «get off» more cheaply, paying with larger displacements.",
  "struct.seism.4_directii": "Why 4 directions?",
  "struct.seism.cutremurul_vine_dintr": "An earthquake does not come from one particular direction — the response is computed at 0°, 45°, 90° and 135°, each becoming a load case (E_0..E_135).",
  "struct.seism.testul_t7_compara": "Test T7 compares the CQC base shear with the equivalent static force (F_b = m·S_d(T₁)·λ): the ratio should be ~0.7–1.05 on the principal directions. At 45°/135° the mass splits between modes and the ratio falls naturally — there it is informative only.",
  "struct.seism.momentele_torsiune_accid": "The accidental torsion moments (P100-1)",
  "struct.seism.masa_reala_niciodata": "The real mass is never exactly where the model puts it — the code requires it to be shifted by e1 = ±5% of the building dimension perpendicular to the earthquake. The result: a torsion moment Mt = F_i · e1 at each storey.",
  "struct.seism.excentricitatea_accident": "The accidental eccentricity covers moved partitions, non-uniform loads, uncertainty in the stiffnesses. The T_accX / T_accY cases enter the seismic combinations with ±1.",
  "struct.seism.s_d_t": "S_d(T_n) — what each mode receives",
  "struct.seism.mod_propriu_are": "Each natural mode has its own period T_n and reads its own acceleration off the spectrum. The low modes (long T) usually lie to the right of the plateau, the high ones on it.",
  "struct.seism.raspunsul_total_combinar": "The total response is the CQC combination of the modal responses — the modes with large participating mass and large S_d dominate.",
  "struct.seism.perioadele_colt_spectrul": "the corner periods of the spectrum",
  "struct.seism.tc_pasul_1": "TC from Step 1; TB = 0.2·TC; TD from the code",
  "struct.seism.amplificarea_maxima_2": "the maximum amplification (2.5)",
  "struct.seism.factorul_importanta_seis": "the seismic importance factor",
  "struct.seism.clasa_importanta_a": "the importance class of the building",
  "struct.seism.amortizarea_critica_core": "the critical damping (the correlation of modes in CQC)",
  "struct.seism.constanta_a_metodei": "a constant of the method",
  "struct.seism.forta_taietoare_baza": "the base shear from the CQC combination",
  "struct.seism.calculata_aici": "computed here",
  "struct.seism.forta_statica_echivalent": "the reference equivalent static force",
  "struct.seism.masa_efectiva_cumulata": "the cumulative effective mass of the modes on the direction",
  "struct.seism.tinta_90_p100": "the target ≥ 90% (P100-1/EC8)",
  "struct.seism.forta_seismica_a": "the seismic force of the storey (the distribution of F_b)",
  "struct.seism.excentricitatea_accident2": "the accidental eccentricity (5% of the perpendicular side)",
  "struct.seism.pasul_ruleaza": "The step is running…",
  "struct.seism.construieste_spectrul_pr": "The design spectrum is built (a_g, TC from Step 1), the natural modes are read from Step 7, the modal response is computed on 4 directions (CQC combination) along with the accidental torsion, and then the seismic cases are added to the force tensor.",
  "struct.seism.ruleaza_nou_meniul": "Run it again from the left-hand menu.",
  "struct.seism.acceleratia_terenului": "(the ground acceleration)",
  "struct.seism.0_amplificarea_maxima": "β0 (the maximum amplification)",
  "struct.seism.p100_1_constant": "P100-1 — constant in Romania",
  "struct.seism.i_factorul_importanta": "γI,e (the importance factor)",
  "struct.seism.amortizarea_critica": "ξ (the critical damping)",
  "struct.seism.numarul_moduri_considera": "The number of modes considered",
  "struct.seism.masa_efectiva_cumulata2": "Cumulative effective mass (0° / 90°)",
  "struct.seism.directie": "Direction",
  "struct.seism.v_baza": "V base",
  "struct.seism.spectrul_disponibil_rezu": "The spectrum is not available in the result.",

  // --- STRUCTURE · the calculation settings, field by field -------------
  "struct.pref.mod_intrerupere.eticheta": "How the span bars terminate",
  "struct.pref.mod_intrerupere.ajutor": "«bent» = the bar is not cut: at the theoretical point it rises at 45° over the depth of the section and anchors on the top fibre, so it stays in play across the shear crack, and the schedule counts its diagonal (√2·h) and its anchorage. «cut» = cut straight, at the theoretical point plus the shift a_l. EC2 §9.2.1.3 allows both — let the workshop choose.",
  "struct.pref.mod_intrerupere.opt_indoite": "bent at 45° (default)",
  "struct.pref.mod_intrerupere.opt_intrerupte": "cut straight",
  "struct.pref.etrieri_pas_unic.eticheta": "Link spacing along the beam",
  "struct.pref.etrieri_pas_unic.ajutor": "a single spacing over the whole beam (the default) means the steel fixer counts one distance only; over two zones, critical and middle, steel is saved but it reads harder on site. The spacing is set in multiples of 2.5 cm anyway, and the code caps are rounded DOWN to that step, so the choice cannot break the code.",
  "struct.pref.etrieri_pas_unic.opt_1": "a single spacing, over the whole beam (default)",
  "struct.pref.etrieri_pas_unic.opt_0": "two zones — critical and middle",
  "struct.pref.allow_odd_bars.eticheta": "An odd number of bars allowed",
  "struct.pref.allow_odd_bars.ajutor": "by default NO: the engineer reinforces symmetrically, with an EVEN number of bars — a middle bar complicates the bending and brings nothing. Where allowed, the odd central bar is held with a cross-tie and counts in the confinement factor α.",
  "struct.pref.allow_odd_bars.opt_0": "no — an even number of bars only (default)",
  "struct.pref.allow_odd_bars.opt_1": "yes — a central bar is admitted, with a cross-tie",
  "struct.pref.interax_min_cm.eticheta": "The minimum distance between bars, in a row",
  "struct.pref.interax_min_cm.ajutor": "centre to centre, as with the maximum below: 5 cm is the office rule. Below it, the code's CLEAR distance (EC2 §8.2: max(Ø, aggregate + 5 mm, 20 mm)) remains the floor and rises by itself for thick bars, so that the concrete and the poker vibrator can pass.",
  "struct.pref.s_max_bare_cm.eticheta": "The maximum distance between bars, in a row",
  "struct.pref.s_max_bare_cm.ajutor": "centre to centre: the code allows 25 cm, the office works at 18. Out of that rule comes a MINIMUM NUMBER of bars, independent of the area required — a wide beam is not reinforced with two thick bars at the corners. At equal area the solver prefers thin bars anyway: one with a defect takes less of the section out of play.",
  "struct.pref.niveluri_plan_liber.eticheta": "Storeys with an open plan",
  "struct.pref.niveluri_plan_liber.ajutor": "the storeys on which there are NO partitions — the open car park at ground level, the lobby, the hall. There the structure can put columns and blades anywhere within the footprint, not only in the drawn walls. The calculation has no way of knowing: it sees that there are no walls and reads «there is no room», exactly backwards — an open storey is the easiest place for a blade to fit. Write the names of the storeys from Step 2, separated by commas. Empty = every storey asks for its own walls.",
  "struct.pref.niveluri_plan_liber.exemplu": "ground floor",
  "struct.pref.infrastructura_libera.eticheta": "The basement is free for the structure",
  "struct.pref.infrastructura_libera.ajutor": "UNDERGROUND storeys have no architecture of their own: the car park under a block is designed AFTER the structure, so the columns come down wherever they need to reach the foundation, and the walls, blades and spaces drawn there are provisional. «auto» = follow the document: a basement whose walls are all copies brought down from the floors above (generated architecture) is free, one drawn on a sheet is not. «yes» frees it regardless; «no» respects it as drawn, even if the structure then has fewer places to come down.",
  "struct.pref.infrastructura_libera.opt_auto": "follow the document (default)",
  "struct.pref.infrastructura_libera.opt_da": "yes — the structure may come down anywhere in the basement",
  "struct.pref.infrastructura_libera.opt_nu": "no — the basement stays as drawn",
  "struct.pref.modul_grila_m.eticheta": "The module of the column grid",
  "struct.pref.modul_grila_m.ajutor": "the width of a parking space: 2.50 m. The column spacing is set on its multiples (the intermediate columns of long walls fall at 5.0 m), so that a WHOLE number of spaces fits between two columns and so that the bays are EQUAL — two equal spans mean one beam formwork, reused. How much of the grid sits on the module and how many different spans the frame has both enter the score, 5 % each. It can also be 2.60 if the site calls for it.",
  "struct.pref.distante_ideale_m.eticheta": "Ideal spacings between elements",
  "struct.pref.distante_ideale_m.exemplu": "2.5; 5; 7.5",
  "struct.pref.distante_ideale_m.ajutor": "the clear spans the structure aims for between the FACES of two neighbouring columns, in metres. Empty means the multiples of the grid module (2.5 and 5.0 m): that many whole parking spaces. The rule is followed ONLY when the basement is ours — a generated architecture with free infrastructure. When the parking is already drawn by the architect, the structure has to follow what is there, and the grid term of the score no longer penalises the project for a rule that does not apply to it.",
  "struct.pref.h_grinda_max_m.eticheta": "The maximum depth of the beams",
  "struct.pref.h_grinda_max_m.ajutor": "how deep a beam is allowed to be, so that it fits over the door openings and within the ceiling the architect asks for: 0.65 m is the engineer's requirement. The calculation has no way of finding this out — a 0.90 m beam over 5 m passes every check and stiffens nicely, but it does not get built if the office will not have it. The cap is paid for elsewhere: under it, the stiffness that opens ΔT₁₂ and tightens the drift has to come from lengthened columns and blades, not from deeper beams.",
  "struct.pref.h_grinda_fatada_max_m.eticheta": "The maximum depth of the facade beams",
  "struct.pref.h_grinda_fatada_max_m.ajutor": "the facade can have a LOWER cap than the project: when set, it lowers the facade beams' cap below the «maximum beam depth»; it never raises it — the office makes beams of at most 0.65 m over windows and doors too. Left empty, the facade keeps the project cap, and the openings on the axis lower it when the lintel above the window or the parapet below it is smaller.",
  "struct.pref.drift_pe_baza.eticheta": "Base of the drift model",
  "struct.pref.drift_pe_baza.ajutor": "what the model that yields d_re of annex E rests on. «Automatic» = fixed base when the strategy pursues a rigid basement box (its conditions are checked at step 10): above the box the building is as if fixed, and the box's rotation on the soil is not a deformation of the structure; with no box requested, drift is measured on the soil, with the foundation compliance. The variant used is written at step 9 (the models card) and at step 10.",
  "struct.pref.drift_pe_baza.opt_auto": "automatic — fixed when a rigid box is pursued",
  "struct.pref.drift_pe_baza.opt_incastrat": "fixed, always",
  "struct.pref.drift_pe_baza.opt_elastic": "on elastic soil, always",
  "struct.pref.h_radier_m.eticheta": "Raft thickness",
  "struct.pref.h_radier_m.ajutor": "the thickness of the raft decided at the macro geometry (from P+6 upwards). Left empty, macro takes the 0.80 m estimate; the detail checks the thickness by zones.",
  "struct.pref.phi_min_long_mm.eticheta": "The minimum Ø of the longitudinal bars",
  "struct.pref.phi_min_long_mm.ajutor": "the office's constructional minimum for beams (Ø14). The upper cap is set by the joint: the bars have to be able to anchor through the columns at the ends. If the joint's cap falls below the value here, the step raises a warning and the solver widens the column along the beam direction.",
  "struct.dwg.inchide_conturul_enter": "Close the outline (Enter, or click the first point)",
  "struct.dwg.pasii_analiza_vad": "The analysis steps see only the union of the marked areas. Edit or delete them to change that.",
  "struct.dwg.complex_imobiliar_alege": "A multi-building development: pick the block to analyse — the analysis steps see only the union of the saved areas. The outline is set point by point (you can zoom between points), and the vertices and edges can be moved afterwards.",
  "struct.dwg.incadreaza_extents_urile": "Fit the full extents, including the entities stranded far from the sheets",
  "struct.dwg.inapoi_dosar": "← Back to the documents",
  "struct.dwg.inapoi_proiect": "← Back to the project",
  "struct.dwg.inapoi_punct": "Back one point",
  "struct.dwg.inchide_conturul": "Close the outline",
  "struct.dwg.sterge_ultima_zona": "Delete the last area",
  "struct.dwg.renunta": "Cancel",
  "struct.dwg.sterge_zonele": "Delete the areas",
  "struct.dwg.incadreaza": "Fit to view",
  "struct.dwg.grupa_structura": "Structure",
  "struct.dwg.grupa_arhitectura": "From the architecture",
  "struct.dwg.toate": "All",
  "struct.dwg.directie": "Direction",
  "struct.dwg.sectiune_talpa": "Base section",
  "struct.dwg.punct_stanga": "Left point",
  "struct.dwg.latime": "Width",
  "struct.dwg.inaltime": "Height",
  "struct.dwg.continut": "Content",
  "struct.dwg.pozitie": "Position",
  "struct.rz.limita_seismica": "of the seismic limit",
  "struct.rz.pasul_ruleaza": "The step is running…",
  "struct.rz.ruleaza_nou_meniul": "Run it again from the left-hand menu.",
  "struct.rz.rigida": "rigid",
  "struct.rz.flexibila": "flexible",
  "struct.rz.cotele_fundare_ax": "The founding levels, axis by axis",
  "struct.rz.impune": "What imposes it",
  "struct.cob.masa_seismica_greutatea": "Why the seismic mass is not the weight divided by g",
  "struct.cob.masele_ops_mass": "The masses (ops.mass) — the balls at the nodes",
  "struct.cob.stalpi": "columns",
  "struct.cob.pereti_grinzi": "walls on beams",
  "struct.cob.pereti_placa": "walls on the slab",
  "struct.cob.utila_a": "imposed cat. A",
  "struct.cob.utila_c": "imposed cat. C",
  "struct.cob.zapada": "snow",
  "struct.cob.coborarea_incarcarilor": "Load take-down",
  "struct.cob.niveluri_kn": "By storey (kN)",
  "struct.cob.1_m2_planseu": "1 m² of floor (kN/m²)",
  "struct.cob.incarcare_vs_masa": "Load vs seismic mass",
  "struct.cob.3d_ajung_opensees": "3D — how they reach OpenSees",
  "struct.cob.bara_totalul_apasa": "Each bar is the total bearing on that storey's floor, broken down by source. The right-hand column adds from the top down: how much passes through that storey on its way to the foundation.",
  "struct.cob.descompunerea_surse": "The breakdown by source",
  "struct.cob.cumulat_spre_fundatie": "Cumulative towards the foundation",
  "struct.cob.aici_verifica_valorile": "This is where the values are checked: each slice is a figure from the code or from the wall build-up, with its own formula. The walls on beams are missing from the column — they are not a load per square metre of floor, but per linear metre of beam.",
  "struct.cob.vine": "What it comes from",
  "struct.cob.per_nivel_metru": "Per storey, per square metre",
  "struct.cob.doua_contabilitati_acele": "They are two accounts of the same structure, and they differ on purpose:",
  "struct.cob.stalpii_peretii": "the columns and the walls",
  "struct.cob.incarcare_pasul_8": "Load (Step 8)",
  "struct.cob.masa_seismica_pasul": "Seismic mass (Step 7)",
  "struct.cob.bilele_goale_gri": "The hollow (grey) balls, at level 0",
  "struct.cob.deja_impachetat_bile": "What is already «packed» into the balls: the slab and the finishes in full, the columns and walls half-and-half between the floor above and the one below, and the imposed load only with the fraction ψ_E — see the table in the «Load vs seismic mass» tab.",
  "struct.dgf.distribuie_presiunea_sub": "How the pressure distributes under the base, as a function of eccentricity",
  "struct.dgf.lunecarea_impinge_latera": "Sliding: what pushes sideways and what resists",
  "struct.dgf.rasturnarea_rotirea_juru": "Overturning: rotation about the edge of the foundation",
  "struct.dgf.subpresiunea_apa_impinge": "Uplift: the water pushes the foundation up",
  "struct.dgf.ridicarea_stalpul_latura": "Lift-off: the column on the tension side is pulled up",
  "struct.dgf.strapungerea_stalpul_tre": "Punching: the column «goes through» the base, shearing a truncated cone",
  "struct.dgf.tasarea_felie_pamant": "Settlement: each slice of soil compresses under the pressure that reaches it",
  "struct.dgf.modelul_winkler_terenul": "The Winkler model: the ground becomes a carpet of independent springs",
  "struct.dgf.grinda_fundare_incarcare": "The foundation beam: the load comes from BELOW, so the signs reverse",
  "struct.dgf.tasarea_uniforma_strica": "Uniform settlement harms nothing; DIFFERENTIAL settlement cracks",
  "struct.dgf.desprinde": "lifts off",
  "struct.dgf.frecare_talpa_v": "friction on the base = V · μ",
  "struct.dgf.impingerea": "the passive",
  "struct.dgf.pasiva_r_p": "earth pressure R_p",
  "struct.dgf.brat": "lever arm",
  "struct.dgf.w_h_apa": "γ_w · h_water",
  "struct.dgf.n_lt_0": "N &lt; 0 (tension)",
  "struct.dgf.umplutura": "fill",
  "struct.dgf.lungimea_aferenta": "the tributary length",
  "struct.dgf.slabeste_adancimea": "weakens with depth",
  "struct.dgf.m_camp_intinde": "M in the span stretches the TOP fibre",
  "struct.dgf.m_reazem": "M at the support",
  "struct.pg.numai_deschiderea_cotele": "this span only, with all its dimensions",
  "struct.pg.punct_gruparea_r": "for each station, the group with the smallest R/E — the one that decides it",
  "struct.pg.1_35g_1": "1.35G + 1.5Q — no earthquake",
  "struct.pg.g_q_seism": "G + ψQ ± earthquake, on the two directions",
  "struct.pg.rezultatul_grinzii_poart": "The beam's result does not carry the check stations (an older run).",
  "struct.pg.gruparea_decide": "the group that decides",
  "struct.pg.fundamentala": "fundamental",
  "struct.pg.seismica": "seismic",
  "struct.pg.fata_stalpului_aici": "–– the column face (M_Ed is taken from here)",
  "struct.pg.f_fundamentala_s": "(F) fundamental · (S) seismic",
  "struct.pg.trec_dar_intr": "do not pass, but not at a single station:",
  "struct.pg.punct_deseneaza_fibra": "Each station is drawn on its own fibre, with |M_Ed| and M_Rd to the same scale: the supports above the beam, the span below. Stations R1/R2 sit on the FACES of the columns, not on their axes, and the span station is sought between the faces — which is why its position moves from one group to another. The capacity at the support is drawn as a plateau over the critical zone: between the faces the step does not report the moment. Shear is not checked at a station but over zones.",
  "struct.dse.vederea_3d_poate": "The 3D view cannot be rebuilt from this result.",
  "struct.dse.desenul_poate_reconstitu": "The drawing cannot be rebuilt from this result (an older run).",
  "struct.dse.fara_desen_tipul": "No drawing for this kind of element.",
  "struct.imb.parghia_lipseste_grinzil2": "The lever missing for STUB beams: two columns less than ~1 m apart with a beam between them give a clear span of a few centimetres, and the CAPACITY shear (V = γ_Rd·ΣM_Rd/L_as) explodes. The section does not help either — at capacity shear M_Rd grows with the depth, so Q̄ does not fall —, and lengthening the end columns is now (rightly) CAPPED so as not to squeeze it further.",
  "struct.imb.h_mesh_02": "h_mesh 0.40 m → ~0.8 m in the attempts of the economy phase (about 4× fewer unknowns, an eigen solve 4–6× cheaper), with a CONFIRMATION chain at full resolution before a move is kept — the verdict reported stays the one at normal precision. The typical difference in T₁/forces between 0.4 and 0.8 is of the order of a few per cent: enough for triage, not for a verdict. The grafting point already exists: every attempt in faza_economie is «apply the overlay → run the steps → read the state» — the cheap evaluator goes in there, and the confirmation becomes a candidate with top priority in the same queue. With LOD, the 6 chains per branch become ~15–20 within the same budget — only then does the queue reach the tail candidates (today mut:ST17 stays untried on project 6 when the budget runs out).",
  "struct.imb.stalp_plantat_cade2": "A planted column that lands less than 2·d from the face of the support puts practically the whole reaction into the shear at that end — on project 2, GR22.1 receives 406 kN from ST47.2 standing at 0.53 m, and no 30 cm wide section carries it. EC2 §6.2.2(6) allows the contribution to be reduced by β = a_v/(2d) for exactly this situation (the load goes straight into the support through a compression strut), but it is not implemented.",
  "struct.imb.calculeaza_raporteaza_de2": "It is already computed and reported; it is not yet used as a criterion for choosing between two interventions of equal price (the one that brings the centre of stiffness closer to the centre of mass ought to win).",
  "struct.imb.mutarea_a_lungul2": "Moving along the same axis exists (removal + re-adding on the merged beam); what is missing is moving onto another wall (the CM–CR criterion) and the slab thickness as an optimisation variable.",
  "struct.imb.abaterea_peste_202": "A deviation over 20 % goes into the failed checks today (type «fundatie_ks»), but re-running the chain with an updated K_s stays manual — the loop could do it by itself.",
  "struct.imb.deplasarile_relative_niv2": "Storey drifts are not yet a check, so they cannot enter the optimisation loop (P100-1 annex E).",
  "struct.imb.inca_incarcare_deci2": "It is not yet a load, so it can be neither checked nor optimised against.",
  "struct.ret.implicit_0_santier": "0 by default: on site the soil often leaves as fill for a neighbouring plot",
  "struct.ret.0_oprita_aprinde": "0 = off; turn it on if the supplier really invoices per piece",

  // --- STRUCTURE · the sentences with figures and with emphasis ---------
  "struct.cob.peretii_parterului_p1": "+ the ground-floor walls ({p1} kN)",
  "struct.cob.sectiunea_p1": "— the section: {p1}",
  "struct.cob.cazul_p1_primeste": "Case {p1} — what the solver receives",
  "struct.cob.numai_zonele_p1": ", on the cat. {p1} zones only",
  "struct.dwg.salveaza_p1_p2": "Save ({p1} {p2})",
  "struct.dwg.fundatie_p1_m": "(foundation {p1} m)",
  "struct.dwg.axele_cladirii_x": "the building axes · X {p1} · Y {p2}",
  "struct.dwg.cauta_p1_layere": "Search {p1} layers…",
  "struct.dwg.fisierul_nu_exista": "The file does not exist in this project",
  "struct.dwg.se_incarca": "Loading…",
  "struct.dwg.se_interpreteaza": "Reading the drawing… (the first time can take a while)",
  "struct.dwg.confirma_sterge_zone": "Delete every zone of interest? The analysis steps will see the whole drawing again.",
  "struct.dwg.unitati": "units: {u}",
  "struct.dwg.se_salveaza": "Saving…",
  "struct.dwg.salveaza_fara_zone": "Save (no zones)",
  "struct.dwg.scoate_ultimul_punct": "Remove the last point placed (Backspace)",
  "struct.dwg.scoate_ultimul_poligon": "Remove the last polygon drawn",
  "struct.dwg.editeaza_zonele": "Edit the zones",
  "struct.dwg.selecteaza_zone": "Select zones of interest",
  "struct.dwg.vezi_in_tabel": "↑ See in the table",
  "struct.dwg.puncte_inchis": " (closed)",
  "struct.dwg.banner_puncte": "{n} — clicking the first point (or Enter) closes the outline; Backspace removes the last point, Esc abandons the outline. Between points you can zoom with the wheel and pan by dragging.",
  "struct.dwg.banner_primul": "A click = one point of the outline. Zoom with the wheel, put the points exactly on the corners (they snap to the drawing on their own — hold Shift if you would rather they did not), and finally click the first point to close the polygon.",
  "struct.dwg.banner_zone": "{n} — drag the vertices or the edges to adjust them; double-clicking an edge adds a vertex, Alt+click on a vertex removes it. Clicking empty space starts a new outline. Nothing is saved until you press «Save».",
  "struct.dwg.tip_linie": "Line",
  "struct.dwg.tip_polilinie": "Polyline",
  "struct.dwg.tip_cerc": "Circle",
  "struct.dwg.tip_arc": "Arc",
  "struct.dwg.tip_elipsa": "Ellipse",
  "struct.dwg.tip_spline": "Spline",
  "struct.dwg.tip_hasura": "Hatch",
  "struct.dwg.tip_text": "Text",
  "struct.dwg.tip_atribut": "Attribute",
  "struct.dwg.tip_solid": "Solid",
  "struct.dwg.tip_fata_3d": "3D face",
  "struct.dwg.tip_contur_calculat": "Computed outline",
  "struct.dwg.tip_ax_fundatie": "Foundation axis",
  "struct.dwg.tip_volum_fundatie": "Foundation volume",
  "struct.dwg.tip_stalp_propus": "Proposed column",
  "struct.dwg.tip_grinda_propusa": "Proposed beam",
  "struct.dwg.tip_perete_simplificat": "Simplified wall",
  "struct.dwg.tip_perete_persistent": "Persistent wall",
  "struct.dwg.tip_eticheta_perete": "Wall label",
  "struct.dwg.tip_eticheta_perete_persistent": "Persistent wall label",
  "struct.dwg.tip_ax_fundatie_denumit": "Foundation axis (named)",
  "struct.dwg.tip_eticheta_ax": "Axis label",
  "struct.dwg.entitati#n": "1 entity|{n} entities|{n} entities",
  "struct.dwg.zone_salveaza#n": "Save 1 zone|Save {n} zones|Save {n} zones",
  "struct.dwg.zone_active#n": "1 zone of interest active|{n} zones of interest active|{n} zones of interest active",
  "struct.dwg.puncte_puse#n": "1 point placed|{n} points placed|{n} points placed",
  "struct.dwg.zone_desenate#n": "1 zone|{n} zones|{n} zones",
  "struct.ret.informativ_afisare_p1": "for information, for display; at {p1} ({p2})",
  "struct.ret.preturi_zona_norme": "Prices, region & norms{p1}",
  "struct.ret.p1_desfasurat": "{p1} developed",
  "struct.ret.manopera_p1_um": "labour {p1}/unit",
  "struct.ret.p1_manopera": "{p1} — labour",
  "struct.arh.pereti_desenati_intrerup": "walls drawn with breaks: closed with {p1} m (without closing: {p2} m²)",
  "struct.arh.contur_rupt_nici": "outline BROKEN — even {p1} m of closing does not reach the ~{p2} m² control",
  "struct.arh.peretii_suprapun_p1": "— the walls overlap {p1}%",
  "struct.arh.am_gasit_cote": "no levels found on the elevations — values assumed at {p1} m",
  "struct.arh.p1_pereti_persistenti": "· {p1} walls persistent across storeys (PP)",
  "struct.arh.p1": "· of {p1}",
  "struct.arg.grinda_p1_are": "Beam {p1} has a {p2}×{p3} cm section and acts together with the {p4} cm slab (a «T/L section»): slab on {p5} at R1 and on {p6} at R2 (detected geometrically from the slab model). The effective slab width taken: {p7} on each side — the engineer's convention (conservative); the full formula from EC2 §5.3.2.1 also depends on the span and gives close values (listed for information, not implemented).",
  "struct.arg.deschiderea_depaseste_4": "The span exceeds 4.5 m ⇒ LARGE-SPAN CORRECTION (sheet B: AZ10): the span moment was raised from {p1} to {p2} kNm with 0.2·|M_support,top| — it covers the redistributions on long spans.",
  "struct.arg.r_sub_1": "R/E is below 1, but above the acceptance threshold 1 − {p1}% — the project's accepted under-sizing margin (the «acceptare_grinzi_pct» constant). The R/E shown stays the real one.",
  "struct.arg.suprarezistenta_calcul_c": "the overstrength for capacity design: {p1} — {p2}",
  "struct.arp.conteaza_grinda_zvelta": "WHY IT MATTERS. In a slender beam the strains vary linearly over the depth of the section, so the bars at the opposite face also reach yield and can be counted. In a deep beam that is no longer true: the force «flows» along compression diagonals straight from the load to the support, and the tension gathers into a TIE near the tension face. That is exactly why CR 2-1-1.1 §10.3.2 says that substructure elements «often behave as short reinforced concrete members» and that «where the proportions are those of short beams, the methods specific to deep beams or methods based on the strut-and-tie mechanism shall be applied». THE CONSEQUENCE FOR THE REINFORCEMENT. The section is checked on BOTH models and the smaller one decides: the plane-section model (with the lever arm 0.8·h = {p1} m) and the strut-and-tie model (with the reduced lever arm z = {p2} m and the reinforcement in the {p3} cm band). On top of that, the mesh spacing is tightened to min(2·t, 300 mm) — SR EN 1992-1-1 §9.7(2).",
  "struct.arp.modelul_grinda_zabrele": "THE STRUT-AND-TIE MODEL (CR 2-1-1.1 §10.3.2 + SR EN 1992-1-1 §6.5/§9.7). Here the tension gathers into a tie, and the capacity is T_Rd·z: everything within the {p1} cm band from the tension face, times the reduced lever arm z = {p2} m. The bars beyond the band are NOT counted — in a short member they never reach yield. WHY IT IS MORE SEVERE. The plane section «sees» the whole depth, so it gives an optimistic M_Rd; the strut-and-tie sees only the tie. The difference between the two R/E values below is exactly the price of the wrong assumption — the reason CR 2-1-1.1 does not leave the choice to the engineer.",
  "struct.arp.r_sub_1": "R/E is below 1, but above the acceptance threshold 1 − {p1}% — the project's accepted under-sizing margin. The R/E shown stays the real one.",
  "struct.arp.pasul_nu_rulat": "This step has not been run yet. Press «Run» in the left-hand menu (after Steps 7, 11 and 12).",
  "struct.arp.ultima_rulare_esuat": "The last run failed: {motiv}",
  "struct.arp.eroare_necunoscuta": "unknown error",
  "struct.arp.subsol_cerut_fara_perete": "A rigid basement is required, but there is not a single wall",
  "struct.arp.rulat_la": "run at {data}",
  "struct.arp.nu_cere_subsol_rigid": "The building does not require a rigid basement.",
  "struct.arp.peretii_doar_p2": "Concrete walls are used only on G+2 buildings and above that have a basement — there the substructure is closed into a «rigid box» (P100-1 §4.4.1.7).",
  "struct.arp.pereti_armati": "Walls reinforced",
  "struct.arl.titlu": "Structural walls of the building",
  "struct.arl.intro": "Vertical members whose long side is at least {raport} times the short one are walls, not columns: they are reinforced here, with the rules for ductile walls (P100-1 §5.3.4.3), and no longer appear in Step 15. Each wall has column-type cages at its ends, two meshes in the web and confining hoops at the ends.",
  "struct.arl.card_nr": "Structural walls reinforced",
  "struct.arl.card_regim": "Design regime",
  "struct.arl.regim_sub": "q = {q}, k_M = {km}, k_V = {kv}, γ_Rd = {grd} (walls)",
  "struct.arl.card_materiale": "Steel in the walls",
  "struct.arl.materiale_sub": "{beton} m³ of concrete",
  "struct.arl.card_carcase": "Reinforcement cages",
  "struct.arl.carcase_sub": "{n} in the formwork catalogue, +{surplus} % steel; {ref} positions kept their own cages",
  "struct.arl.pozitii": "Per position: base, critical zone, Ω and ε",
  "struct.arl.th_pozitie": "Position",
  "struct.arl.th_sectiune": "Length × thickness [m]",
  "struct.arl.th_inaltime": "Height H_w [m]",
  "struct.arl.th_zona_critica": "Critical zone l_cr [m]",
  "struct.arl.th_omega": "Overstrength Ω / amplification ε",
  "struct.arl.th_taietoare_baza": "Base shear V_Ed,0 [kN]",
  "struct.arl.baza": "base {el}",
  "struct.arl.scurt": "(squat wall)",
  "struct.arl.niveluri": "Per level: boundary elements, hoops and web",
  "struct.arl.th_element": "Member",
  "struct.arl.th_zona": "Zone",
  "struct.arl.th_capete": "Boundary cages",
  "struct.arl.th_etrieri": "Boundary hoops (confined length)",
  "struct.arl.th_inima": "Web: vertical + horizontal",
  "struct.arl.zona_a": "critical (A)",
  "struct.arl.zona_b": "regular (B)",
  "struct.arl.zona_infra": "substructure",
  "struct.arl.click": "Click a row to see the checks of that level.",
  "struct.arl.detaliu": "Checks of wall {el} ({nivel})",
  "struct.arl.detaliu_sub": "Zone {zona}; Ω = {om}, ε = {eps}; x_u = {xu} cm against the limit {lim} cm; ν_d = {nud} (reference).",
  "struct.arl.nu_se_aplica": "What is not carried over from columns, and what is simplified",
  "struct.arl.limite": "Simplifications written in the result:",
  "struct.arl.fara_panouri": "The building has no basement wall panels; this step reinforces only the structural walls.",
  "struct.arp.regimul_sectiunii": "The section regime",
  "struct.arp.panouri_grinzi_pereti": "panels with l₀/h < {raport} ⇒ deep beams (strut-and-tie + a tie in a band)",
  "struct.arp.subsol_rigid": "Rigid basement",
  "struct.arp.criteriul_subsolului": "The rigid-basement criterion",
  "struct.arp.k_subsol_sub": "k basement / max(k storeys), along X / Y · threshold {prag}×",
  "struct.arp.materiale": "Materials",
  "struct.arp.materiale_sub": "steel ({otel}) · {beton} m³ concrete {clasa}{fcd}",
  "struct.arp.materiale_fcd": " (f_cd = {fcd} MPa, f_ctd = {fctd} MPa)",
  "struct.arp.th_panou": "panel",
  "struct.arp.plasa_verticala": "vertical mesh",
  "struct.arp.barele_verticale": "The vertical bars",
  "struct.arp.plasa_orizontala": "horizontal mesh",
  "struct.arp.tiranti_sus_jos": "ties top / bottom",
  "struct.arp.panoul_eticheta": "Panel",
  "struct.arp.optiune_panou": "{el} ({nivel}) — {verdict}",
  "struct.arp.panou_dimensiuni": "{t}×{h} cm · L = {l} m (axis to axis) · l₀ = {l0} m · columns {si} → {sj}",
  "struct.arp.rigid_off": " · rigid {i} + {j} cm",
  "struct.arp.verdictul_aduna": "It gathers ALL of the panel's checks, and the severest one dictates it: in-plane bending (on two models — plane section and strut-and-tie), in-plane shear, the compression strut, the support node, sliding in the construction joints and the detailing rules (minimum percentages, diameters, spacings, thickness, A_s ≤ 4 %). The crack width is computed but INFORMATIVE, so it does not enter the verdict — the §7.3.4 relations are written for slender members in bending.",
  "struct.arp.marja_proiect": "the project accepts R/E ≥ {prag} (a margin of {pct} %). The solver still aims at R/E = 1.00; only the verdict of the checks left inside the margin becomes accepted, marked explicitly «prin marja».",
  "struct.arp.ce_nu_se_preia": "What is NOT carried over from beams — and the clause that exempts it",
  "struct.arp.de_ce_lista_asta": "Why this list is needed",
  "struct.arp.th_articolul": "clause",
  "struct.arp.ce_cere_cutia": "What else the «rigid box» asks of the basement",
  "struct.arp.ierarhia_normativ": "The hierarchy: the <b>Romanian code sets the requirement</b>, and SR EN 1992-1-1 (the Romanian adoption of EC2) gives the method of calculation — exactly as P100-1 §5.3.4.1.1 refers it. Cover {acop} cm · substructure amplification ×{ampl} · w_max = {wmax} mm.",
  "struct.arp.legenda_3d": "The panel is {h} cm in clear height (from the top of the foundation beam to the underside of the slab) and {t} cm thick. Concrete = transparent grey; the vertical bars — a mesh on both faces, over the full height, carried a further ~35 cm BELOW the panel into the foundation beam as starter bars (they do not stick out sideways: they stay in the plane of each face, at a cover of {acop} cm); the horizontal distribution bars; the ties in the tension band (at the top over the supports, at the bottom in the span); the links tying the two meshes. The context (the end columns, the slab over the basement, the foundation beam) is sketched — it does NOT enter the calculation, the panel is checked on its own section. The <b>construction joints</b> are marked orange at the foot and the head of the panel: there the vertical bars work as CONNECTORS (CR 2-1-1.1 §10.3.2, section 7). Rotate with the mouse, zoom with the scroll wheel.",
  "struct.arp.s1_explicatie": "Panel {el} has a section of {t}×{h} cm and works as a bar member between columns {si} and {sj}. As with beams, the forces are not read on the column axis but at its FACE: l₀ = L_axis − off_i − off_j = {lax} − {oi} − {oj} = {l0} m.\n\nWHAT h = {h} cm IS. It is the CLEAR height of the panel, not the storey height: Step 7 computes it as the basement height minus what is occupied above — the thickness of the slab over the basement, or the web of the beam if a beam also runs between the same two columns (otherwise its concrete would be counted twice). The panel is therefore cast from the top face of the foundation beam up to the underside of the slab (or of the beam). In the analysis model h is the DEPTH OF THE SECTION of the bar member — it takes the in-plane bending, just as a beam's depth takes the vertical moment.\n\nIT IS NOT ANALYSED TOGETHER WITH THE FOUNDATION. In the FE model the panel is a bar between the nodes at the head of the two basement columns (at slab level), offset rigidly by its eccentricity; it has no nodes along its bottom edge, and the grid of foundation beams is a separate system. The consequence: the capacity below is computed on the t × h section of the panel ALONE, without counting on composite action with the foundation beam or the slab — on the safe side. The starter bars drawn going into the foundation are a CONSTRUCTION item (the anchorage and the connector role of sections 7 and 10), not a support the calculation leans on.\n\nWHAT THE l₀/h RATIO DECIDES. SR EN 1992-1-1 §5.3.1(3) says a member is a BEAM only if its span is at least 3× the depth of the section; otherwise it is a DEEP BEAM. Here l₀/h = {raport}, so the panel {regim}.\n\n{urmare}",
  "struct.arp.este_grinda_perete": "IS a deep beam",
  "struct.arp.e_grinda_obisnuita": "is an ordinary beam",
  "struct.arp.panou_zvelt": "The panel is slender enough for the plane-section assumption to hold, so it is checked only on the plane-section model and no concentrated tie in a band is required of it. The §9.7(2) spacing cap and the strut-and-tie check do not apply.",
  "struct.arp.valori_l0h": "l₀/h = {l0} / {h} = {raport} < {prag}",
  "struct.arp.valori_z": "z = 0.2·(l₀ + 2h) = 0.2·({l0} + {h2}) = {z} m (capped at 0.8·h = {zb} m)",
  "struct.arp.ref_conventia_grinzi_pereti": "the deep-beam convention — see the note below",
  "struct.arp.valori_banda": "the band = {frac} % · h = {cm} cm; {inb} of the mesh's {tot} rows fall inside it (A_s = {as} cm²)",
  "struct.arp.ref_tirantul_ancorat": "SR EN 1992-1-1 §9.7(3) (the tie is fully anchored in the node)",
  "struct.arp.termen_grinda_perete": "deep beam",
  "struct.arp.termen_grinda_perete_def": "a member in bending whose span does not reach 3× the depth of the section; the force travels along compression diagonals, not by classical bending",
  "struct.arp.termen_tirant": "tie",
  "struct.arp.termen_tirant_def": "the tensioned reinforcement of the strut-and-tie mechanism — the bottom (or top) «chord» of the truss",
  "struct.arp.termen_brat": "lever arm (z)",
  "struct.arp.termen_brat_def": "the distance between the compression resultant and the tie; the resisting moment is the force in the tie × z",
  "struct.arp.termen_zona_rigida": "rigid zone (off)",
  "struct.arp.termen_zona_rigida_def": "the stretch of panel inside the column: the concrete joint does not deform, so the design forces are read at the column FACE",
  "struct.arp.s2_titlu": "2. The design forces (the 5 points)",
  "struct.arp.s2_explicatie": "The forces come from Step 11's combined tensor, taken per combination (not as an anonymous envelope), at EXACTLY the same 5 points as for beams: R1 top/bottom and R2 top/bottom at the column faces, the span point = the extreme moment inside the clear span. For each point the extreme moment is kept, together with the combination that gives it, the position x and the concomitant components.\n\nTHE SIGN CONVENTION. M₃ is the bending IN THE PLANE of the wall, taken by the panel's depth h; positive = the BOTTOM fibre in tension, so the tie works at the bottom. Negative = the top fibre in tension (typically at the supports), so the tie works at the top. V₂ is the in-plane shear, P the axial force along the panel (negative = compression).\n\nThe «_GS» columns are the triple from the quasi-permanent combination (G + ψ₂·Q, no earthquake): the crack width and the normal force that closes the construction joint are read from it.",
  "struct.arp.th_punct": "point",
  "struct.arp.th_grupare": "group",
  "struct.arp.negativ_compresiune": "negative = compression",
  "struct.arp.amplificat_omega": " (×Ω)",
  "struct.arp.fata_sus": "top",
  "struct.arp.fata_jos": "bottom",
  "struct.arp.s3_explicatie": "With beams the question is «fundamental or seismic?». For a substructure panel there is one more: with WHAT values the seismic group enters.\n\nCR 2-1-1.1 §10.3.1 requires the connecting forces between superstructure and substructure to be taken «associated with the plastic mechanism of the superstructure» — that is, not the forces from the elastic analysis but the ones that appear when the superstructure actually yields. Here that is done in a simplified way: the forces of the SEISMIC group are amplified globally by Ω·γ_Rd = ×{ampl}. The amplification is applied to the whole combination, ON THE SAFE SIDE, because the G/E split is no longer available in Step 11's combined tensor (the gravity part is small anyway against the seismic one for M and V in the plane of the panel).\n\nThe fundamental group enters unchanged, and so does the quasi-permanent combination GS (no earthquake) — it serves the crack width and the joint, where amplification would make no sense.\n\nNote the converse: because the dissipation mechanism is in the superstructure, the shear is NO LONGER derived from the panel's resisting moments (the capacity calculation γ_Rd·ΣM_Rd/L_as used for beams does not apply) — it comes from the analysis, amplified.",
  "struct.arp.s4_explicatie": "The solver starts from the code minima — ρ ≥ {rho} % in each direction counting both meshes (CR 2-1-1.1 §10.4.6), Ø ≥ 10 vertical / 8 horizontal, spacings ≤ 350 mm horizontally / 250 mm vertically (§8.4.2) — and tightens the DIRECTION that fails: the horizontal mesh when the plane section fails, the band ties when the strut-and-tie model fails, the vertical mesh when shear or the construction joint fails.{incercari}\n\nA table says «Ø10/250 on both faces»; the drawings show WHAT that means at the formwork — two meshes, the cover, the links tying them, the band where the tie gathers and the two construction joints.",
  "struct.arp.s4_incercari": " It took {n} here.",
  "struct.arp.legenda_randuri_oriz": "● {n} horizontal rows Ø{phi}/{pas} on each face",
  "struct.arp.legenda_bare_vert": "│ vertical bars Ø{phi}/{pas}, one on each face, carried on as starter bars into the foundation beam",
  "struct.arp.legenda_tirant": "● tie at the top {sus} / at the bottom {jos}, in the hatched band of {banda} cm",
  "struct.arp.numai_plasa_banda": "(only the mesh inside the band)",
  "struct.arp.legenda_rosturi": "┄ the vertical bars stitch both construction joints (CR 2-1-1.1 §10.3.2)",
  "struct.arp.legenda_elev_vert": "│ vertical bars Ø{phi}/{pas}",
  "struct.arp.legenda_elev_oriz": "─ horizontal rows Ø{phi}/{pas}",
  "struct.arp.legenda_elevatie": "{otel} kg of steel, {beton} m³ of concrete. The blue dot marks the face in <b>tension</b> at that point (the amplified seismic group, where there is one), and the figure under it is M₃ in kN·m — from which you can see directly why the tie works at the TOP over the supports and at the BOTTOM in the span. The plum zones at the ends are the RIGID stretches inside the columns (l₀ = L_axis − off_i − off_j); the dashed horizontal lines are the two construction joints.",
  "struct.arp.val_plasa": "Ø{phi}/{pas} mm — {n} bars, ρ = {rho} %",
  "struct.arp.val_plasa_randuri": "Ø{phi}/{pas} mm — {n} rows, ρ = {rho} %",
  "struct.arp.val_tirant": "{n}Ø{phi} ({as} cm²) + {plasa} cm² from the mesh",
  "struct.arp.val_tirant_doar_plasa": "only the mesh inside the band: {plasa} cm²",
  "struct.arp.s5_explicatie": "THE PLANE-SECTION MODEL (SR EN 1992-1-1 §6.1). The equilibrium of the t×h section is written with ε_cu = 3.5 ‰ at the compressed fibre, the concrete as a 0.8·x rectangular block, the steel elastic – perfectly plastic, and the neutral axis is found by bisection on the axial equilibrium. Every row of the horizontal mesh counts, each with its own stress, plus the tie. The concomitant axial force P takes part: moderate compression RAISES the resisting moment, tension lowers it.\n\n{urmare}",
  "struct.arp.panou_zvelt_5": "The panel is slender (l₀ ≥ 3·h), so the plane-section assumption holds and the second model is not required.",
  "struct.arp.th_verificarea": "check",
  "struct.arp.momentul_de_calcul": "the design moment",
  "struct.arp.momentul_capabil": "the resisting moment",
  "struct.arp.th_verdict": "verdict",
  "struct.arp.exemplu_tirant": "A worked example for the tie, at the most heavily loaded point: T_Ed = M_Ed/z, and T_Rd = A_s,band·f_yd. With A_s,band = {as} cm² and f_yd = {fyd} MPa ⇒ T_Rd = {trd} kN, hence M_Rd = T_Rd·z = {mrd} kN·m.",
  "struct.arp.s6_explicatie": "WHO TAKES THE SHEAR. In a basement panel the role of the stirrups is played by the VERTICAL bars: they cross the inclined crack. The check runs on the strut-and-tie model of SR EN 1992-1-1 §6.2.3, with cotθ = 1 (diagonals at 45°) and the lever arm z = {z} m. The concrete term V_Rd,c is NOT added — the design goes entirely on the reinforcement, on the safe side; it is listed below for information only ({vrdc} kN).\n\nCR 2-1-1.1 §10.3.2 states that «depending on the proportions of the walls and on the distances between the points of contact with the superstructure members, either the vertical or the horizontal reinforcement is the more effective». At l₀/h = {raport} {care}.\n\nTHE COMPRESSION STRUT. Between the inclined cracks the concrete works in compression, like the diagonals of a truss. If those diagonals crush (V_Rd,max, §6.2.3(3)), the bars no longer help at all: the answer is THICKNESS, not reinforcement.\n\nTHE SUPPORT NODE. The strut coming down to the column must have something to bear on. §6.5.4 limits the pressure in the node to σ_Rd,max = k₂·ν′·f_cd, with k₂ = 0.85 for a compression-tension node (CCT) — the contact area is half the column's size × the thickness of the wall. Here too the lever is geometry: a thicker wall or a column wider along the panel.\n\nSR EN 1992-1-1 §6.5.2(3) explicitly allows that, for «struts between directly loaded zones, such as short corbels or short deep beams», the methods of §6.2.2/§6.2.3 may be used — which is why no separate strut-and-tie model is built for shear.",
  "struct.arp.verticale_mai_eficiente": "the compression diagonal is longer than it is tall, so the VERTICAL bars are the ones tying it",
  "struct.arp.orizontale_devin_eficiente": "the panel is taller than it is long, so the horizontal bars become effective too — the check here stays on the vertical ones, on the safe side",
  "struct.arp.s7_explicatie": "THE REQUIREMENT IS ROMANIAN AND EXPLICIT. CR 2-1-1.1 §10.3.2: «The vertical reinforcement crossing the construction joints between the footing (pad) of the foundation and the wall, as well as the construction joint under the floor, shall be designed for the role of connecting zones of concrete of different ages.» The panel is cast between two already hardened concretes — the foundation beam below and the slab over the basement above — so it has TWO joints, and the vertical bars are the only thing stitching them.\n\nTHE MODEL. The joints are exactly the situation of SR EN 1992-1-1 §6.2.5: interfaces between concretes cast at different ages, parallel to the axis of the member. β = 1 is taken, with the z of the panel ALONE, that is on the safe side: the whole longitudinal force of the panel is assumed to pass through the joint. No composite action with the foundation beam or the slab is counted on — if they were taken as the flange of a composite section, the lever arm would grow and v_Edi would FALL, so the assumption here is the severest.\n\nTHE RESISTANCE. v_Rdi = c·f_ctd + µ·σ_n + ρ·f_yd·µ, capped at 0.5·ν·f_cd. The surface is taken as ROUGH (c = {c}, µ = {mu}) — the joint is left with asperities, not cast over smooth concrete. In the seismic group §6.2.5(5) requires c to be halved (dynamic loading), so in the governing case below c = {cguv}.\n\nWHY σ_n = 0. §6.2.5(1) asks for the MINIMUM normal force that can act at the same time, in the direction NORMAL to the joint. The joints are horizontal planes, so their normal is the vertical — whereas the «P» of the bar member is the axial force ALONG the panel, not vertical. The vertical part (the weight of the panel + what comes down from the slab) is not available per panel in Step 11's tensor, and wrongly crediting P would artificially inflate the capacity of the joint. So it goes on the safe side, with σ_n = 0: the joint relies only on the connectors and on friction over the rough surface. The real compression, not credited, is extra reserve.\n\nIn practice: the vertical bars are cast as STARTER BARS out of the foundation, with the lengths from section 10.",
  "struct.arp.din_capabili": "Of the {mpa} MPa of resistance, {pct} % come from the vertical bars and the rest from friction on the rough joint plus the compression across it. In other words: the connectors carry the joint, not the roughness of the concrete.",
  "struct.arp.s8_explicatie": "WHY IT MATTERS IN A BASEMENT WALL. The outer face is in contact with the ground (exposure class XC2), so SR EN 1992-1-1 tab. 7.1N requires w_max = {wmax} mm for reinforced concrete members. A wider crack is not a strength problem but one of watertightness and of the durability of the reinforcement.\n\nHOW IT IS COMPUTED. w_k = s_r,max·(ε_sm − ε_cm), with s_r,max = 3.4·c + 0.425·k₁·k₂·Ø/ρ_eff (§7.3.4; k₁ = 0.8 ribbed bars, k₂ = 0.5 bending, k_t = 0.4 long term). The moment is the one from the quasi-permanent combination GS (G + ψ₂·Q, no earthquake and no substructure amplification), and the tensioned reinforcement taken is the tie in the band at the tension face — consistent with the strut-and-tie model. Creep enters through E_eff = E_cm/(1+φ) = {eeff} MPa (φ = {fi}).\n\nWHY IT IS MARKED «INFORMATIVE». The §7.3 relations are written for SLENDER members in bending; in a deep beam the crack pattern is different. So the figure is computed and shown, but it does NOT enter the panel's verdict and does not reach Step 19's list of failures. If it exceeds w_max an explicit warning appears.\n\n«Uncracked» means the quasi-permanent moment does not reach the cracking moment M_cr = f_ctm·t·h²/6 = {mcr} kN·m — on a panel 2.8 m tall M_cr is very large, so that is the usual case.",
  "struct.arp.momentul_cvasipermanent": "the quasi-permanent moment",
  "struct.arp.th_limita": "limit",
  "struct.arp.s9_explicatie": "A calculation can ask for an area of reinforcement that checks out perfectly on paper but is below the code minimum or does not fit in the formwork. Each rule below is a check with its own R/E and its own clause, not a note in the margin.\n\nTHE MINIMUM PERCENTAGES. CR 2-1-1.1 §10.4.6 is categorical: «The percentages of horizontal and vertical reinforcement in the web of walls, counting both meshes, shall be at least 0.30 %.» That is stricter than the table for the superstructure's field reinforcement (0.20–0.30 % by zone and steel grade) and than SR EN 1992-1-1 §9.6 (0.2 % vertical / 0.1 % horizontal) — so it governs. For a deep beam §9.7(1) is added: an orthogonal mesh next to EACH face, at least 0.1 % of the concrete area and at least 150 mm²/m per face and direction.\n\nDIAMETERS AND SPACINGS. §8.4.2: Ø ≥ 8 mm horizontal, Ø ≥ 10 mm vertical, the reinforcement in TWO meshes, one at each face, maximum spacings 350 mm horizontally and 250 mm vertically. For a deep beam SR EN 1992-1-1 §9.7(2) tightens this to min(2·t, 300 mm), so the stricter limit decides.\n\nTHE THICKNESS. §10.4.2: the PERIMETER walls of the basement are at least 250 mm, the internal ones at least 200 mm. The panel here is {fel}, so its minimum is {minim} cm.\n\nTHE DISTANCE BETWEEN POINTS OF VERTICAL LOADING. §10.4.5: «In choosing the spans and bays, the distances between the points of vertical loading of the substructure shall as a rule not exceed 6 m.» Beyond that, the grid of foundation beams bends between supports and the substructure stops working as a BOX. A «point of vertical loading» is where the SUPERSTRUCTURE discharges into the substructure: a column that exists only in the basement is part of the substructure (§10.4.1) and does not load it, so it does not count as a support. That is why E is not necessarily the length of the panel — panels bounded by such columns are joined to their collinear neighbours as far as the first column that GOES UP, and if there is no second one on the axis the check does not apply and the row says why. The remedy, when it really is exceeded, is ARCHITECTURAL (a column mid-span), not a recalculation.\n\nA_s ≤ 4 %. SR EN 1992-1-1 §9.6.2(1): above 4 % steel in the concrete area the concrete can no longer be compacted between the bars — if you get there, the panel is not short of reinforcement, it is short of concrete.",
  "struct.arp.perete_de_contur": "a PERIMETER one",
  "struct.arp.perete_interior": "an INTERNAL one",
  "struct.arp.th_e_adoptat": "E (adopted / required)",
  "struct.arp.th_r_limita": "R (the limit)",
  "struct.arp.s10_explicatie": "The vertical bars cannot be cast in one piece with the foundation: STARTER BARS come out of the foundation beam, and the wall's mesh is lapped over them. The lap length is what makes two bars into one.\n\nWHICH CLAUSE DECIDES. P100-1 §5.7.1(1) and (3) refer to chapter 8 of SR EN 1992-1-1 for anchorage and laps outside the critical zones. The substructure is NOT a critical zone — CR 2-1-1.1 §10.4.1 designs it to stay elastic — so the 20 % increase of P100-1 §5.7.1(4), written for the critical zones of DCH structures, does NOT apply. What remains is the plain EC2 calculation.\n\nHOW IT IS COMPUTED. f_bd = 2.25·η₁·η₂·f_ctd with η₁ = 1 (good bond) and η₂ = 1 (Ø ≤ 32 mm); l_b,rqd = (Ø/4)·(σ_sd/f_bd). The α coefficients of §8.4.4 are taken as 1.0 (straight bars, no transverse pressure), and α₆ = {alfa6} because ALL the starter bars are lapped in the same section (§8.7.3, tab. 8.3).\n\nThese bars have a second role too, checked in section 7: they are the connectors of the footing–wall joint (CR 2-1-1.1 §10.3.2).",
  "struct.arp.val_fbd": "f_bd = 2.25·1.0·1.0·{fctd} = {fbd} MPa",
  "struct.arp.val_lb": "l_b,rqd = (Ø{phi}/4)·({fyd}/{fbd}) = {lb} cm ⇒ l_bd = {lbd} cm",
  "struct.arp.ref_minime_ancorare": "§8.4.3–§8.4.4, with the minima max(10Ø; 100 mm)",
  "struct.arp.val_l0": "l₀ = α₆·l_b,rqd = {a6}·{lb} = {l0} cm",
  "struct.arp.ref_innadite_aceeasi": "§8.7.3, tab. 8.3 (100 % of the bars lapped in the same section)",
  "struct.arp.practic_plansa": "In practice, on the construction drawing: starter bars Ø{phi}/{pas} mm out of the foundation beam, with <b>{l0} cm</b> of lap into the wall, on both faces. The clear height of the panel ({h} cm) allows it with {rezerva} cm to spare.",
  "struct.arp.agrafe_legare": "Links tying the meshes",
  "struct.arp.val_agrafe": "{n}Ø{phi}/m² — {buc} pcs{carlig}",
  "struct.arp.val_agrafe_carlig": ", hook {c}",
  "struct.arp.val_materiale": "{otel} kg steel · {beton} m³ concrete · {rap} kg/m³",
  "struct.arp.svg_a_sectiune_grosime": "a) Section through the thickness (seen from above) — {t} cm, two meshes",
  "struct.arp.cota_pas": "spacing {v} mm",
  "struct.arp.cota_fasie": "strip {v} cm",
  "struct.arp.svg_acoperire_panou": "cover {c} cm on each face · panel l₀ = {l0} m × h = {h} m",
  "struct.arp.svg_verticale": "● vertical Ø{phi}/{pas}",
  "struct.arp.svg_orizontale": "● horizontal Ø{phi}/{pas}",
  "struct.arp.svg_agrafe": "│ links {n}Ø{phi}/m²",
  "struct.arp.svg_b_sectiune_verticala": "b) Vertical section — {t} × {h} cm",
  "struct.arp.rost_perete_placa": "wall–slab joint",
  "struct.arp.rost_talpa_perete": "footing–wall joint",
  "struct.arp.svg_c_elevatia": "c) Elevation of the panel — {l} × {h} m, between {si} and {sj} · the 5 check points",
  "struct.arp.eticheta_banda_tirant": "tie band {v} cm",
  "struct.arp.incercari#n": "1 attempt|{n} attempts|{n} attempts",
  "struct.arpl.reazemul_comun_ia": "· the shared support is taken over {p1} m (the neighbour)",
  "struct.arpl.p1_stalpi": "({p1} columns)",
  "struct.arpl.schema_doua_directii": "two-way",
  "struct.arpl.schema_una_directie": "one-way",
  "struct.arpl.schema_consola": "cantilever",
  "struct.arpl.schema_nerezemat": "UNSUPPORTED",
  "struct.arpl.din_2": "{n} of 2",
  "struct.arpl.reazem_m": "support {v} m",
  "struct.arpl.continuu": " · continuous",
  "struct.arpl.simplu": " · simple",
  "struct.arpl.vecini": " ({lista})",
  "struct.arpl.fara_reazem_liberi": "no support ({v} m free)",
  "struct.arpl.eticheta_grinda": "beam",
  "struct.arpl.eticheta_perete": "wall",
  "struct.arpl.eticheta_fara_reazem": "no support",
  "struct.arpl.deschidere_cerc": "{v} m (the inscribed circle)",
  "struct.arpl.legenda_ochi": "· thick grey = a wall standing ON the slab (no beam under it) · hatched = an opening in the slab. The BOTTOM mesh is drawn bar by bar at the real spacing:",
  "struct.arpl.desenul_rarit": " — the drawing shows one bar in {n} so that it stays readable; the spacing in the text is the real one.",
  "struct.arpl.directia_scurta_e": " The short direction is {dir} ({l} m), so its bars sit underneath, with the smallest cover.",
  "struct.arpl.directia_nu_duce": "Direction {dir} carries no load (the strip method gives it unloaded, and the FE model asked for no more).",
  "struct.arpl.m_camp_diag": "M_span = {v} kNm/m",
  "struct.arpl.fasii_fem": "strips: span {a} · FEM: span {b}",
  "struct.arpl.fem_guverneaza_diag": "⚠ method: the FE model governs — the design follows it",
  "struct.arpl.fasii_guverneaza": "the strips govern (on the safe side against the FE model)",
  "struct.arpl.numai_fasii": "the strip method only (the FE model produced no cuts in this direction)",
  "struct.arpl.armatura_sus_lipseste": "the top reinforcement is missing",
  "struct.arpl.prindere_fatada": "fixing to the façade {v} m",
  "struct.arpl.armatura_sus_ancorata": "the TOP reinforcement, anchored into the slab; the deflection with K = 0.4",
  "struct.arpl.pasul_nu_rulat": "This step has not been run yet. Press «Run» in the left-hand menu (after Steps 7, 8 and 14).",
  "struct.arpl.ochiuri_de_placa": "slab panels ·",
  "struct.arpl.verificari_dot": "checks ·",
  "struct.arpl.placa_de_cm": " · slab {v} cm · {otel} kg steel · {beton} m³ concrete",
  "struct.arpl.metode_fem": "of the two methods: strips (Grashof–Marcus + coefficients) and {n} design cuts from the FE model. In {noc} panel directions the FE model governs — ⚠ method.",
  "struct.arpl.numai_metoda_fasii": "The moments come ONLY from the strip method:",
  "struct.arpl.gabarit_deschidere": "{lx} × {ly} m · design span {d} m (the inscribed circle)",
  "struct.arpl.albastru_y": "blue = Y",
  "struct.arpl.sus_mrd": "TOP: {text} (M_Rd {v} kNm/m)",
  "struct.arpl.jos_mrd": "BOTTOM: {text} (M_Rd {v} kNm/m)",
  "struct.arpl.acoperirea_nominala": "nominal cover {c} cm; d = h − c − Ø/2, exactly the value M_Rd was computed with — direction {dir} (the short one)",
  "struct.arpl.rezumat_ochi": "{arie} m² · cat. {cat} · slab {h} cm · design span {d} m · {schema}{raport} · q_Ed {q} kN/m² · {otel} kg steel",
  "struct.arpl.raport_l": " (l_long/l_short = {v})",
  "struct.arpl.perimetrul_laturi": "The perimeter of {p} m splits into {n} homogeneous sides:",
  "struct.arpl.continuitatea_nu": ". Continuity is not assumed: a side is continuous only if another panel REALLY begins on the far side of the support — which is why the neighbours are written on the drawing, and clicking them moves the selection.",
  "struct.arpl.directia_dir": "Direction {dir}",
  "struct.arpl.scurta_moment_mare": " (the short one — it carries the large moment)",
  "struct.arpl.pereti_pe_ochi": "Walls standing ON the panel (with no beam beneath):",
  "struct.arpl.fasii_fem_scurt": "strips {a} · FEM {b}",
  "struct.arpl.incercari": "Attempts:",
  "struct.arpl.istoric_rand": "{de_la} → {la} ({de_ce}, R/E {re})",
  "struct.arpl.nu_se_aplica": "Not applicable: {motiv}",
  "struct.arpl.niciun_stalp": "No column to check: at every joint the force reaches the column through beams, in two perpendicular directions",
  "struct.arpl.unde_vin_ce": "Not from Step 7's slab shells: Step 8's tensor does not record them. They come from splitting the load over two directions (the short strip takes the larger share) and from moment coefficients.",
  "struct.arpl.unde_vin_de_ce": "Phase 2 replaces them with the FE resultants (Wood–Armer) and keeps the present method as a cross-check; divergences are marked «⚠ method», as at Step 10.",
  "struct.ars.fundatia_desenata_adanci": "The foundation is drawn at its real depth (base at {p1} m, B = {p2} m).",
  "struct.ars.procentul_armare_ar": "(the reinforcement ratio would have asked for only {p1})",
  "struct.ars.n_p1_kn": "N = {p1} kN (pure compression)",
  "struct.ars.n_p1_kn2": "N = {p1} kN (pure tension)",
  "struct.ars.toata_verticala_stack": "over the whole height (the {p1} stack)",
  "struct.ars.p1_fasii": "+ {p1} strips",
  "struct.ars.p1_nivelurile": "{p1}, all storeys",
  "struct.combina.p1_p2_0": "; {p1} {p2} with ψ₀ (×{p3})",
  "struct.combina.defavorabil_p1_favorabil": "(unfavourable) or {p1} (favourable);",
  "struct.combina.p1_0_30": "(α = {p1}) and ±α·0.30 in the 30 % rule; it does not appear at all",
  "struct.combina.p1_p2_p3": "{p1}·{p2} = {p3} (snow)",
  "struct.combina.seismica_2_p1": "seismic: ×ψ₂ = {p1} — the quasi-permanent fraction only",
  "struct.combina.actiunea_variabila_domin": "The dominant variable action: {p1} (×1.50)",
  "struct.combina.fundamentala_g_p1": "fundamental: γ_G = {p1}",
  "struct.combina.p1_0_p2": "×{p1}·ψ₀ = {p2} when it is not (ψ₀ = {p3});",
  "struct.combina.p1_fortei_seismice": "{p1} with that of the seismic force",
  "struct.combina.p1_p2_peste": "{p1}{p2}, over the loads",
  "struct.combina.numarul_mare_combinatii": "the large number of seismic combinations ({p1})",
  "struct.combina.gruparea_fundamentala_fa": "The fundamental group (no earthquake). {p1}{p2}.",
  "struct.combina.p1_p2": "{p1} % on {p2}°",
  "struct.combina.torsiunea_accidentala_p1": "Accidental torsion on {p1}, sign {p2} —",
  "struct.combina.fundamentala_p1_cand": "fundamental: ×{p1} when it is the dominant action,",
  "struct.combina.gravitationale_g_2": "gravity G + ψ₂·Q. {p1}",
  "struct.combina.arata_cele_p1": "Show all {p1}",
  "struct.combina.pasul_nu_rulat": "This step has not been run yet. Press «Run» in the left-hand menu (after Steps 8 and 9).",
  "struct.combina.ultima_rulare_esuat": "The last run failed: {motiv}",
  "struct.combina.eroare_necunoscuta": "unknown error",
  "struct.combina.combinatii_generate": "Combinations generated",
  "struct.combina.card_sub_fund_seism": "{f} fundamental + {s} seismic{gs} (CR 0-2012){set}",
  "struct.combina.card_sub_gs": " + {n} GS",
  "struct.combina.card_sub_set": " — {nume}",
  "struct.combina.compozitie_implicita": "<b>Fundamental</b> (the everyday situation, without an earthquake): γ_G·G + 1.5·Q_dominant + 1.5·ψ₀·Q_rest.",
  "struct.combina.compozitie_seismice": "<b>Seismic</b> — the 30 % rule: 100 % in one direction together with 30 % in the other.",
  "struct.combina.compozitie_gs": "<b>GS</b> — the gravity part of the seismic group (G + ψ₂·Q), kept separate: the gravity shear for the capacity calculation is taken from it.",
  "struct.combina.alpha_amplificarea": "α — the second-order amplification",
  "struct.combina.masele_pasului_7": "the masses of Step 7",
  "struct.combina.tensorul_de_eforturi": "the force tensor",
  "struct.combina.alpha_sub": "in the X / Y directions — applied to the seismic cases",
  "struct.combina.ce_nu_acopera": "What the set in use does NOT cover",
  "struct.combina.limitari_cunoscute": "— {n} known limitations",
  "struct.combina.normativul_eticheta": "The code:",
  "struct.combina.efectul_eticheta": "The effect:",
  "struct.combina.cum_se_schimba": "How it changes: <b>{cum}</b>.",
  "struct.combina.alpha_vine_din_10": "The second-order amplification comes from Step 10",
  "struct.combina.deplasari_laterale": "— lateral displacements (drift)",
  "struct.combina.driftul_s_a_mutat": "The interstorey drift and the coefficient θ have moved to <b>Step 10</b>: they are displacement checks, not combinations. Only α is applied here, to the seismic cases. The per-storey table, the governing point in plan and the Annex E verdicts are shown at Step 10.",
  "struct.combina.sursa_alpha": " Source: {sursa}.",
  "struct.combina.th_alpha_aplicat": "α applied",
  "struct.combina.rol_permanenta": "fundamental: γ_G = {gd} (unfavourable) or {gf} (favourable); seismic: 1.00 — the permanent actions are certainly there when the earthquake comes",
  "struct.combina.rol_variabila": "fundamental: ×{gq} when it is the leading action, ×{gq}·ψ₀ = {nedom} when it is not (ψ₀ = {psi0}); seismic: ×ψ₂ = {psi2} — only the quasi-permanent fraction is statistically present at the moment of the earthquake",
  "struct.combina.rol_seismica": "only in the seismic group: ±α·1.00 in its own direction (α = {a}) and ±α·0.30 in the 30 % rule; it does not appear at all in the fundamental group",
  "struct.combina.rol_torsiune": "it accompanies every seismic combination on its own axis, with both signs: ±α·1.00 (α is the same as for the E case on that axis)",
  "struct.combina.subsol_e_theta": "For the <code>E_θ</code> cases the figure in the «what comes out» column is the base shear force (CQC combined), not a weight — the seismic forces are already divided by the behaviour factor q{q} and already contain the importance factor γ_I,e, which is why no coefficient other than ±α appears for them in the matrix. For <code>T_acc</code> the figure is the sum of the torsional moments over the storeys (kNm), not a force.",
  "struct.combina.q_egal": " = {v}",
  "struct.combina.ce_gg_defav": "all the permanent actions (G1…G4) multiplied by 1.35 — you assume the concrete, the walls and the finishes weigh more than you reckoned",
  "struct.combina.sursa_gg_defav": "CR 0-2012 §6.3, eq. (6.7) — the code's decision, not the designer's",
  "struct.combina.ce_gg_fav": "the same permanent actions, but taken at 1.00 — for the situations where weight «helps» (overturning, tension in the edge columns, the footing lifting off). A combination at 1.35 would hide exactly those cases",
  "struct.combina.ce_gq_dom": "the variable action taken as leading in that combination (Q1, Q2, S in turn) enters with its characteristic value × 1.50",
  "struct.combina.ce_gq_nedom": "the other variable actions in the same combination, reduced by ψ₀ — it is unlikely that the maximum imposed load and the maximum snow fall on the same day",
  "struct.combina.valoare_psi0": "{gq}·{psi0u} = {u} (imposed), {gq}·{psi0z} = {z} (snow)",
  "struct.combina.valoare_psi2": "Q1: {q1} · Q2: {q2} · S: {s}",
  "struct.combina.ce_psi2": "in the seismic group the variable actions enter only with their quasi-permanent fraction: the part of the imposed load or snow that is statistically present in the building at the moment of the earthquake. The same ψ₂ were used for the masses in the modal analysis (Step 7) — which is why the seismic forces and the weight in the combination are consistent",
  "struct.combina.valoare_alpha_xy": "X: {x} · Y: {y}",
  "struct.combina.ce_alpha": "the second-order amplification, applied to the E and T_acc cases. When θ ≤ 0.10 it comes out exactly 1.000 and the seismic coefficients stay ±1",
  "struct.combina.ce_regula_30": "an earthquake never arrives perfectly along one axis: 100 % is taken in one direction together with 30 % in the other, in every combination of signs",
  "struct.combina.ce_semne": "the earthquake alternates: the same combination is taken with a plus and with a minus sign, because you do not know which way it comes from. Hence the large number of seismic combinations ({n})",
  "struct.combina.ce_gamma_ie": "the building's importance factor. It does NOT appear in the matrix, because it is already multiplied into the design spectrum at Step 9",
  "struct.combina.coeficientii_titlu": "The coefficients in the matrix — where each figure comes from",
  "struct.combina.th_coeficient": "Coefficient",
  "struct.combina.descrie_gs": "Only the gravity part of the seismic group: G + ψ₂·Q + ψ₂·S, without the earthquake. It does not enter the envelope — the gravity shear for the capacity-associated force is taken from it (Step 13).",
  "struct.combina.gg_defavorabil": "unfavourable — the self-weight increases the forces",
  "struct.combina.gg_favorabil": "favourable — the case where weight «helps»: overturning, tension in the columns, the footing lifting off",
  "struct.combina.nicio_dominanta": "No leading variable action",
  "struct.combina.descrie_fundamentala": "The fundamental group (without the earthquake). {dom}{rest}. Permanent actions: γ_G = {gg} — {fav}.",
  "struct.combina.parte_seismica": "{pct} % along {grade}° (sign {semn})",
  "struct.combina.torsiune_pe_axa": "The accidental torsion on {ax}, sign {semn} — {relatie} the sign of the governing seismic force",
  "struct.combina.acelasi_semn": "the same as",
  "struct.combina.semn_opus": "opposite to",
  "struct.combina.fara_torsiune": "No accidental torsion in this combination.",
  "struct.combina.regula_30_prefix": "The 30 % rule (the earthquake does not arrive perfectly along one axis): ",
  "struct.combina.o_singura_directie": "Earthquake in a single direction: ",
  "struct.combina.simultan_cu": " together with ",
  "struct.combina.descrie_seismica": "{reg}{parti}, on top of the gravity loads G + ψ₂·Q. {tors}",
  "struct.combina.rol_fundamentala": "enters the fundamental envelope (ULS, without the earthquake)",
  "struct.combina.rol_seismica_grupa": "enters the seismic envelope (ULS, the special group)",
  "struct.combina.rol_gs": "an internal service combination — it does not enter the envelope",
  "struct.combina.matricea_combinatiilor": "The matrix of combinations",
  "struct.combina.nr_comb_sub": "{f} fundamental and {s} seismic combinations, plus the <code>GS</code> combination (gravity, for Step 13).",
  "struct.combina.th_grupa": "Group",
  "struct.combina.ascunde": "Hide",
  "struct.combina.citeste_numele_lung": "<code>SLUF_…_1.35</code> = fundamental group (ULS), the leading action in the middle, the permanent actions at γ_G = 1.35. <code>ES30_XPM</code> = the 30 % rule with X leading: the first sign belongs to the 100 % direction, the second to the 30 % one. In the <b>full set</b> the name carries a lower-case letter at the end (<code>ES30_XPMp</code> / <code>…m</code>) — the sign of the accidental torsion, which there is taken both ways; in the reduced set the torsion follows the sign of the seismic force, so the letter is absent. The full set also has the family of rotated directions, <code>ES45Pp</code> = earthquake at 45°, the sign of the force then the sign of the torsion. <code>GS</code> = the gravity part of the seismic group, taken on its own.",
  "struct.combina.simbol_psi2": "ψ₂ (the seismic group)",
  "struct.combina.rest_reduse#n": "; {lista} enters reduced by ψ₀ (×{val})|; {lista} enter reduced by ψ₀ (×{val})|; {lista} enter reduced by ψ₀ (×{val})",
  "struct.dr.termen_neaplicat_metoda": "a term NOT APPLIED (the office method) — with it, the maximum SLS drift would be {p1} % instead of {p2} %",
  "struct.dr.p1_p2_d": "{p1}·{p2}·d_re on X, {p3}·{p4}·d_re on Y",
  "struct.dr.raportul_masoara_p1": "the ratio is measured {p1}",
  "struct.modl.30_30_cm": "30×30 cm proposed, of which {p1} resizings applied by the outer loop (Step 19) — the table is at the end of the page",
  "struct.modl.p1_p2_m": "onto {p1} at {p2} m (new segment {p3})",
  "struct.modl.stalpi_orfani_eliminati": "orphan columns removed: {p1}",
  "struct.modl.p1_perechi_stalpi": "· {p1} pairs of columns cast as one element",
  "struct.modl.grinzile_unesc_p1": "the beams merge into {p1} ({p2} m)",
  "struct.modl.vezi_cele_p1": "see all {p1} breaches",
  "struct.modl.intre_p1_p2": "between {p1}, {p2} m",
  "struct.modl.selecteaza_p1_plansa": "select {p1} on the storey drawing",
  "struct.modl.p1": "→ onto {p1}",
  "struct.modl.bp_titlu": "Step 4, geometric pass {bp} — frozen state",
  "struct.modl.bp_text": "a breakpoint from the solution's solving history: the geometry after pass {bp}, without the passes after it. The chain goes on from the LAST pass —",
  "struct.modl.bp_text_din": "a breakpoint from the solution's solving history: the geometry after pass {bp} of {n}, without the passes after it. The chain goes on from the LAST pass —",
  "struct.modl.bp_inapoi": "back to the delivered geometry",
  "struct.rz.geometria_minima_b": "the minimum geometry (B={p1} m, Df={p2} m) did not satisfy the checks — enlarged to the smallest feasible volume",
  "struct.rz.minimul_legal_b": "the legal minimum (B={p1} m, Df={p2} m) satisfies the pressure and the settlement — it stays",
  "struct.rz.g4_beton_25": "G4 = concrete·25 + earth·γ + B·q_CTN = {p1} kN/ml\n",
  "struct.rz.pamant_p1_m3": "earth {p1} m³/ml\n",
  "struct.rz.fundatie_b_p1": "Foundation B = {p1} m · Df = {p2} m",
  "struct.rz.p1_m_p2": "{p1} m over {p2} m",
  "struct.rz.unde_vine_h": "Where H = {p1} m comes from",
  "struct.seism.retinute_p1_extrase": "retained out of {p1} extracted — as many as the 90% modal mass criterion requires (P100-1 §4.5.3.3.1); the minimum required: {p2}",
  "struct.seism.arata_cele_p1": "Show all {p1} modes",
  "struct.cob.masa_aplica_elemente": "The mass is not applied to elements but to <b>nodes</b>: one value in each of the three translations (the rotations are left without inertia). The radius of the ball is proportional to the cube root of the mass, so that the volume drawn is proportional to it.",
  "struct.cob.b_utila_b": "<b>the imposed load</b> enters the mass only with the fraction ψ_E = φ·ψ₂ (typically 0.24), not in full: the chance that all the furniture and all the people are present in the very second of the earthquake is small;",
  "struct.cob.b_infrastructura_b": "<b>the substructure</b> appears only in the loads: EN 1998-1 §4.3.3.2.2(1)P defines the seismic mass as the mass of the building ABOVE the foundation.",
  "struct.ars.b_1_p": "<b>1. P–M–M</b> — a column is compressed (N) and bent on two directions (M2, M3) at the same time. The three quantities are not checked separately: the capacity of the section is a SURFACE in (N, M2, M3) space — the «onion». Each load combination gives a point; every point has to fall inside the onion. Moderate compression HELPS in bending (it closes the cracks), which is why the onion swells towards compression and closes at the apex, where the concrete crushes with no moment at all.",
  "struct.ars.b_n_s": "<b>α = α_n · α_s</b> is the efficiency of the confinement; not all transverse reinforcement «grips» the concrete equally. α_n = 1 − Σb_i²/(6·b₀·h₀) accounts for the concrete arching out between two restrained bars — the sparser the bars, the less confinement there is (hence the 18 cm spacing rule). α_s = (1 − s/2b₀)(1 − s/2h₀) accounts for the same arching, but vertically, between two levels of links.",
  "struct.ars.b_rd_b": "<b>γ_Rd</b> covers the fact that real steel is stronger than the design steel and hardens after yielding. The value comes from the code, according to the project's ductility class — the figure used here is in the γ_Rd table at the head of the tab, and the detail of every shear check carries it along.",
  "struct.ars.b_d_b": "<b>ν_d</b> appears because a more heavily compressed column has a larger zone of crushed concrete and therefore needs more confinement. Below ν_d ≤ 0.20 the requirement does not apply: the concrete is in no danger of crushing, and the minimum ω_wd is enough.",
  "struct.ars.b_perete_b": "<b>What a «wall» is</b> (EC8 §5.1.2): a vertical element whose side along the direction exceeds the transverse one by more than four times (l_w/b_w &gt; 4). It is judged PER DIRECTION — a 30×200 blade is a wall on the long direction and a column on the other.",
  "struct.ars.b_etrierii_orizontali": "<b>the horizontal joint links as a DESIGN quantity</b> (A_sh from the equilibrium of the tension diagonal) + the vertical joint reinforcement — at present only the compression of the diagonal is checked;",
  "struct.ars.verificarile_b_geometric": "the <b>geometric</b> checks: b_beam ≤ min(b_column + h_beam; 2·b_column), the eccentricity of the beam axis against the column axis ≤ b_column/4;",
  "struct.ars.b_v_c": "<b>the real V_c</b> (the shear of the column above) in V_jhd — at present 0 is taken, conservatively;",
  "struct.ars.pasul_nu_rulat": "This step has not been run yet. Press «Run» (after Step 13).",
  "struct.ars.ultima_rulare_esuat": "The last run failed: {motiv}",
  "struct.ars.eroare_necunoscuta": "unknown error",
  "struct.ars.stalpi_lamele_armate": "Columns and blade walls reinforced",
  "struct.ars.clasa_ductilitate_pas3": "the ductility class (Step 3)",
  "struct.ars.gamma_nod_nume": "{v} — {sursa}",
  "struct.ars.stalpi_pe_niveluri": "{n} columns across the storeys · {verdicte}",
  "struct.ars.verdict_x": "{n} × {v}",
  "struct.ars.parametrii_de_calcul": "Design parameters",
  "struct.ars.param_beton": "concrete {beton} (f_cd = {fcd} MPa — from «Building data») · steel f_yd = {fyd} MPa · class {clasa} · νd ≤ {nud} · p ∈ [{pmin}%, {pmax}%]",
  "struct.ars.param_gamma": "γ_Rd = {nod} for the joint hierarchy, {forf} for its shear, {taie} for the capacity shear and {anc} for anchorage through the joint — all from the code, by class {clasa}.",
  "struct.ars.avertismente": "Warnings",
  "struct.ars.vederea_cotata": "Dimensioned view — {ce}",
  "struct.ars.a_nivelului": "of storey {id}",
  "struct.ars.carcasa_legenda": "concrete = transparent grey; the longitudinal bars = red (laid out around the perimeter — exactly as the fibre engine uses them); the stirrups = blue lines, closer together in the plastic zones l_cr at the foot and head of each storey; at each joint the beams that ACTUALLY frame into it are sketched, on their own side (an edge column has two or three arms, not four) — green = every joint check passes, red = at least one fails. On older runs the joint stays without a sketch: their result does not say on which side of the joint each beam arrives.{fundatie} Rotate with the mouse, zoom with the scroll wheel.",
  "struct.ars.carcasa_de_armare": "The reinforcement cage {ce}:",
  "struct.ars.ceapa_legenda": "The P–M–M «onion» of the {b}×{h} section with {n}Ø{phi} (the translucent green surface; the vertical axis = N, the horizontal plane = M2/M3) and the (N, M2, M3) points from ALL the combinations at the foot and the head (red = outside the surface). Rotate with the mouse.",
  "struct.ars.armarea_aleasa_sectiune": "The reinforcement chosen — the cross section ({id})",
  "struct.ars.longitudinal": "Longitudinal",
  "struct.ars.val_longitudinal": "{n}Ø{phi} (p = {p}%)",
  "struct.ars.petrecerea_barelor": "Bar laps",
  "struct.ars.val_petrecere": "l₀ {l0} m at {z} m{etrieri}{ks}",
  "struct.ars.val_petrecere_etrieri": " · stirrups Ø{phi}/{pas} cm",
  "struct.ars.val_petrecere_ks": " · k_s 1.25",
  "struct.ars.acoperirea": "Cover",
  "struct.ars.inaltimea_de_nivel": "Storey height",
  "struct.ars.inaltimea_a_stalpului": "The",
  "struct.ars.banda_planseului": "The floor band at the joint",
  "struct.ars.grosimea_sectiunii": "Section size",
  "struct.ars.inaltimea_flexibila": "The flexible height from the FE model",
  "struct.ars.nota_l0": "{nota} — it is placed ABOVE the critical zone l_cr, because laps are not allowed inside it (P100-1 §5.6.3).",
  "struct.ars.val_m": "{v} m",
  "struct.ars.val_bh_cm": "{b}×{h} cm",
  "struct.ars.verificarile_nivelului": "Checks for storey {id}",
  "struct.ars.rezumat_nivel": "Section {b}×{h} cm, H = {H} m, reinforcement {n}Ø{phi} (p = {p}%), stirrups Ø{et}/{pas} cm in the plastic zones (l_cr = {lcrj} m at the foot and {lcrs} m below the joint — P100-1 §5.3.4.2.1(5), equations (5.22) and (5.23)) and /{pascur} cm elsewhere. Resisting moments about the axes at maximum seismic N: {mh} / {mb} kNm.",
  "struct.ars.cele_8_familii": "— the 8 families of checks on a column, explained for a beginner",
  "struct.ars.fiecare_rand_compara": "Each row compares <b>R</b> (what the column CAN do) with <b>E</b> (what is ASKED of it). A ratio R/E ≥ 1 means it passes. The first two checks concern the strength of the section, the next two the protection against brittle failure, then the joint, then the detailing.",
  "struct.ars.ceapa_nu_e_simetrica": "The onion is NOT symmetric about N = 0, and could not be: concrete carries no tension, so downwards the axial capacity is that of the steel alone (N = {jos} kN = A_s·f_yd), while upwards it is concrete + steel (N = {sus} kN). In the drawing the plane N = 0 is marked by the dark blue outline, and the widest section of the onion is above it — at the «balanced» compression.",
  "struct.ars.nud_explicatie": "<b>2. ν_d ≤ {lim}</b> — ν_d = N/(A_c·f_cd) is the normalised compression: how much of the concrete's capacity in pure compression is already used up. A heavily compressed column fails brittly, by crushing of the concrete, without being able to deform. That is why the code sets a cap. (The informal name «the Venezuela case» comes from the 1967 collapses, where over-compressed columns failed explosively.)",
  "struct.ars.taietoarea_capabil_titlu": "3. The capacity-design shear, in each direction",
  "struct.ars.ierarhia_nodului_titlu": "4. The joint hierarchy (ΣM_Rc ≥ γ_Rd·max(Ω; 1)·ΣM_Eb)",
  "struct.ars.ierarhia_explicatie_a": "— the «weak beam, strong column» principle. In a building, if the beams fail you get repairable local damage; if the columns of one storey fail, everything above comes down — collapse. So the columns at a joint must be stronger than the beams, with a reserve γ_Rd.",
  "struct.ars.ierarhia_explicatie_b": "is ΣM_Eb (the sum of the beam moments from the ANALYSIS), multiplied by the amplification factor coming from the beams, Ω = ΣM_Rb/ΣM_Eb — by how much their actual reinforcement exceeds what the analysis asked for. The engineer's rule: if Ω comes out <b>below one</b> (the beams may have been accepted at Step 13 with an under-design margin of ~5 %), the work goes on with <b>Ω = 1</b> — a beam's missing capacity must not artificially reduce the demand downstream — but the real value of Ω is shown in the table (the coef_amplificare / coef_folosit columns). Equivalently: the demand = γ_Rd·max(ΣM_Rb; ΣM_Eb). Exception: at the top storey the hierarchy is not required, because there is nothing above left to fall.",
  "struct.ars.vezi_sectiunea_noduri": "— see the section «The joints of the stack» below.",
  "struct.ars.vezi_explicatia_sub_tabel": "— see the detailed explanation below the table.",
  "struct.ars.interax_explicatie": "— every longitudinal bar must be held by a corner of a stirrup or by a link. An unrestrained bar in compression buckles outwards at the first plastic excursion and throws off the cover. A practical consequence: in elongated columns the number of bars is NOT set by the reinforcement percentage but by this geometric rule. Here: {nb} bars along the {b} cm side × {nh} along the {h} cm one = {n} bars in all{procent}, maximum bar spacing {interax} cm.",
  "struct.ars.punct_vulnerabil": "The vulnerable point: {loc}, combination {combo} — N = {N} kN, M2 = {M2} kNm, M3 = {M3} kNm → R/E = {re}. For comparison, the biaxial index of sheet P (α = {alpha}, interpolated on νd): {indice}.",
  "struct.ars.th_verificarea_hover": "Check (hover over the title)",
  "struct.ars.th_verdict": "Verdict",
  "struct.ars.omega_subunitar": "Ω = ΣM_Rb/ΣM_Eb = {om} is below one (the beams at this joint were accepted with an under-design margin at Step 13). The demand on the joint is NOT reduced: the work went on with Ω = 1, that is, the demand = γ_Rd·ΣM_Eb — the real value of Ω stays on display.",
  "struct.ars.taietoarea_titlu": "The column's shear — why it is not taken from the analysis",
  "struct.ars.taietoarea_rezumat": "The design shear does not come from the analysis but from the RESISTING moments at the ends (capacity design): V_Ed = γ_Rd·(M_Rc,bottom·k_bottom + M_Rc,top·k_top)/H_clear.",
  "struct.ars.stalp_care_cedeaza_a": "hinges: it rotates, cracks, gives warning, dissipates energy. A column that fails in",
  "struct.ars.stalp_care_cedeaza_b": "breaks diagonally, all at once, and instantly loses its ability to carry the weight above as well. The first failure is repairable, the second means collapse. The design rule that protects us from the second is called <b>capacity design</b>.",
  "struct.ars.un_stalp_care_cedeaza": "A column that fails in",
  "struct.ars.plafonul_k_explicatie": ", and it is the part most often forgotten. A column cannot develop more moment than the joint hands it, and the joint cannot hand over more than the beams give it. If the column is much stronger than the beams (exactly what point 4 aims at), the plastic hinges will form in the BEAMS, the column stays elastic, and its real shear is limited by the beams' capacity — not by its own. Without this cap, any lengthening of the column would artificially raise its design shear and a vicious circle would follow: you lengthen the column so the anchorage passes → its M_Rc grows → its V_Ed grows → it fails in shear → you thicken the stirrups without end.",
  "struct.ars.mrc_compresiune": "Below the balance point, compression raises the resisting moment; taking the minimum compression would underestimate the moment, and so the shear, and the column could fail brittly. (In check 4 — the joint hierarchy — it is exactly the other way round: there a SMALL resisting moment is unfavourable, so the minimum compression is taken. The same quantity, two opposite worst cases.)",
  "struct.ars.doua_directii_explicatie": "An elongated 35×105 column is two different members: along the long direction it is very strong in bending (so it develops a large shear) but it also has a generous section to take it; along the short one it is weak in bending (small shear) but also thin. The shear section takes its width from the side PERPENDICULAR to the direction of action and its effective depth along the PARALLEL side. The stirrup legs that cross the diagonal crack are the ones parallel to the direction — one at each line of bars on the perpendicular side, that is, including the intermediate links (here between {min} and {max} legs, depending on the direction).",
  "struct.ars.ved_max_explicatie": "V_Ed = max(V_cap; V_analysis) — the capacity calculation is usually much larger, but if the analysis gives a larger value still (a short column, the «captive column» effect), that one governs. H_clear = {h} m is the FLEXIBLE height of the column, that is, between the faces of the beams, not between the floor axes — the shorter a column, the more heavily it is loaded in shear.",
  "struct.ars.confinarea_titlu": "Confining the core — why the stirrups are so close together",
  "struct.ars.confinarea_rezumat": "The stirrups stop the concrete core from swelling sideways; confined concrete withstands strains several times larger, and the column becomes ductile.",
  "struct.ars.betonul_comprimat_a": "Compressed concrete shortens along the force and",
  "struct.ars.betonul_comprimat_b": "(the Poisson effect). When the swelling exceeds what it can take, the concrete splits lengthwise and crumbles. But if you hold it tight in a «box» of stirrups it can no longer swell — and then it takes a higher stress and, far more importantly, a",
  "struct.ars.betonul_comprimat_c": "before it fails. That is confinement, and it is the only reason a concrete column can behave ductilely in an earthquake.",
  "struct.ars.miezul_confinat_explicatie": "The cover outside the stirrups spalls off at the first plastic excursions — you cannot rely on it. What remains is the <b>confined core</b>, measured between the axes of the perimeter stirrups: here b₀ × h₀ = {b0} × {h0} cm, against the gross section {b} × {h} cm.",
  "struct.ars.cantitatea_confinare": "The amount of confinement is measured by <b>ω_wd = (volume of stirrups / volume of core) · f_ywd/f_cd</b> — how much confining steel you have, against the concrete you are holding. The code requires an absolute minimum (0.08 for DCM, 0.12 for DCH), whatever the loading.",
  "struct.ars.peste_minim_vine": "Above that minimum comes the requirement tied to <b>the ductility you asked of the structure</b>:",
  "struct.ars.muphi_explicatie": "<b>μ_φ</b> is the curvature ductility demand. It comes straight from the behaviour factor q: if you divided the seismic forces by q, the structure must take up the difference by deforming plastically. μ_φ = 2q − 1 for flexible buildings (T₁ ≥ T_C), and μ_φ = 1 + 2(q − 1)·T_C/T₁ for stiff ones (T₁ < T_C), where the equal-displacement rule no longer holds and the demand RISES sharply.",
  "struct.ars.muphi_consecinta": "a stiff building designed with a large q pays dearly in confinement — if this check fails on many members, the right answer is not more transverse reinforcement but a smaller q (larger seismic forces, but a far smaller ductility demand).",
  "struct.ars.pasul_etrierilor_explicatie": "follows from three simultaneous conditions, the smallest of which is taken: s ≤ b₀/2 (so the concrete arch between stirrups does not leave the core unsupported), s ≤ 175 mm (an absolute cap) and",
  "struct.ars.pasul_etrierilor_flambaj": "— the BUCKLING term: a compressed longitudinal bar behaves like a pin-ended strut between two stirrups, and beyond 8 diameters it buckles before reaching its strength. For DCH the values are stricter: b₀/3, 125 mm, 6·Ø. With Ø14–Ø20 bars the last term almost always governs — which is why the spacing here comes out {pas} cm, not 17.5.",
  "struct.ars.miezul_confinat_b0h0": "The confined core b₀ × h₀",
  "struct.ars.val_cm_x_cm": "{a} × {b} cm",
  "struct.ars.stalpul_eticheta": "Column",
  "struct.ars.optiune_stalp": "{el} — {verdict}",
  "struct.ars.etrieri": "Stirrups",
  "struct.ars.val_etrieri": "Ø{phi} / {pas} cm in the critical zone · {min}–{max} legs",
  "struct.ars.rho_omega": "ρ_s (volumetric ratio) / ω_wd (mechanical)",
  "struct.ars.confinarea_model_titlu": "Confinement — which model was used",
  "struct.ars.confinarea_model_text": "{model}: from the stirrups chosen (ρ_w = {rho}, effective pressure f_l = {fl} MPa) the confined concrete of the core comes out with f_cc = {fcc} MPa and an ultimate strain ε_cu,c = {eps} (against 0.0035 unconfined). {nota}.",
  "struct.ars.cautarea_armarii": "The longitudinal reinforcement of the stack is searched iteratively, over all the storeys at once: it starts from the smallest candidate that satisfies both the ductility class's minimum percentage AND the geometrically required minimum number of bars, then corrects the first problem found — an (N, M2, M3) point outside the P–M–M surface, or a joint where the «weak beam – strong column» hierarchy is not met. For joint problems the reinforcement propagates to the storey above as well. The loop stops when it finds no more problems, or at the 4 % maximum — in which case the concrete section must be enlarged, not the reinforcement. The stirrups are sized separately at the end, per storey: Ø climbs 8 → 10 → 12 until both the shear (in both directions) and the minimum confinement of the core pass.",
  "struct.ars.aici_a_luat": " It took {n} here.",
  "struct.ars.jurnalul_nu_se_pastreaza": "The step-by-step log is no longer kept — the only history retained is Step 19's, «what changed in the structure».",
  "struct.ars.coaja_titlu": "The «shell» over the height — capacity vs demand per storey",
  "struct.ars.coaja_legenda": "green = the capacity (minimum over the axes); red = the largest resultant moment from the combinations — the green must «clothe» the red (docx §5.3)",
  "struct.ars.sectiunea_titlu": "Section {id} — {b}×{h} cm{alungit}",
  "struct.ars.sectiunea_alungita": " (elongated, oriented to the plan)",
  "struct.ars.nodurile_titlu": "The beam–column joints — the checking strategy",
  "struct.ars.nodul_rigid_a": "The joint must stay RIGID — the 90° angle between beam and column is preserved, the deformation happens outside the joint (docx §5.4). At each joint of the stack three things are checked: (1)",
  "struct.ars.ierarhia_cuvant": "the hierarchy",
  "struct.ars.nodul_rigid_b": "«weak beam – strong column» (ΣM_Rc ≥ γ_Rd·max(ΣM_Rb; ΣM_Eb), with the REAL resisting moments of the beams from Step 13, per direction — the demand on the joint from the analysis is multiplied by the amplification factor coming from the beams, Ω = ΣM_Rb/ΣM_Eb, but at LEAST 1: an Ω below one, possible when the beams are accepted with an under-design margin, does not reduce the demand; the real value of Ω is shown; waived at the top storey — P100-1 §5.2.3.3.2); (2)",
  "struct.ars.nodul_rigid_c": "of the joint against the compression of the diagonal (V_jhd = γ_Rd·(A_s1+A_s2)·f_yd − V_c ≤ η·f_cd·b_j·h_jc·√(1−ν_d/η), §5.3.4.3.2); (3)",
  "struct.ars.ancorarea_barelor_grinzii": "the anchorage of the beam bars",
  "struct.ars.nodul_rigid_d": "through the joint (the d_bL/h_c limit, §5.3.4.3.3). Only the hierarchy can be repaired from the column's reinforcement — the solver does that automatically (and propagates the reinforcement to the storey above); shear and anchorage depend on geometry and on the beams, so they stay as verdicts with recommendations for the outer loop (a wider column, thinner and more numerous bars in the beams, or class DCM).",
  "struct.ars.toate_trei_directii_titlu": "All three checks are made IN EACH DIRECTION that has beams (X / Y)",
  "struct.ars.toate_trei_directii_text": ", not once for the whole joint: h_c in the formulas is the side of the column PARALLEL to the beam bars, and the interior/exterior character is judged per direction. <i>Interior</i> means beams on BOTH SIDES of the joint in that direction — not «two beams»: two beams leaving on the same side do not make an interior joint. In an elongated 35×105 column the short direction sees h_c = 35 cm, not 105 — and it usually governs. The columns below list each direction separately; where only one direction appears, the joint has beams only in it.",
  "struct.ars.th_forfecare_diagonala": "Shear / the diagonal",
  "struct.ars.th_ancorarea_barelor": "Anchorage of the beam bars",
  "struct.ars.th_ierarhia": "Hierarchy ΣM_Rc ≥ γ_Rd·max(ΣM_Rb; ΣM_Eb)",
  "struct.ars.exceptat_ultimul_nivel": "waived (top storey — P100-1 §5.2.3.3.2)",
  "struct.ars.fara_grinzi_in_nod": "no beams at this joint",
  "struct.ars.noduri_picate_evidentiate": "the failed joints highlighted on the structural plan",
  "struct.ars.noduri_picate_acum": "(for now: the colouring of the beams in the 3D cage + the table above).",
  "struct.ars.sistemul_titlu": "The system type per direction — and why the joint hierarchy is (or is not) required",
  "struct.ars.nivelul_baza_text": ": at the base of the SUPERSTRUCTURE (storey {n}). With the substructure closed in concrete walls, that is the section of fixity, so the basement walls do not count.",
  "struct.ars.taietoarea_masurata_titlu": "The shear, measured from the response-spectrum analysis",
  "struct.ars.taietoarea_masurata_text": "(Step 9): for each mode the shears of the members at the base are summed — projected on the direction, using each member's own local axes — and only then are the modes combined by CQC. The order matters: CQC gives only positive values, so combining per member and summing afterwards would overestimate the shear.",
  "struct.ars.cqc_nu_e_aditiv": "does not come out exactly",
  "struct.ars.diferenta_in_tabel": "; the difference is written in the table, so you can see how much it is.",
  "struct.ars.pasul9_lipseste_text": ", so the shear could not be measured. Only the stiffness fraction Σ b·h³/12 is shown, as a guide — and the classification stays «frames», so the hierarchy is required anyway. The reason: the stiffness proxy OVERESTIMATES the walls (I ∝ l³, and a wall works as a cantilever over the whole building, not over one storey), and a false «walls» classification would quietly drop a safety requirement.",
  "struct.ars.th_elemente_perete": "«wall» members / total",
  "struct.ars.pereti_predominanti": "wall-predominant",
  "struct.ars.cadre": "frames",
  "struct.ars.nu_se_cere_informativ": "not required (shown for information)",
  "struct.ars.se_cere_fiecare_nod": "required at every joint",
  "struct.ars.elementele_ca_pereti": "The members counted as walls: {lista}",
  "struct.ars.sursa_fractiunii": "Source of the fraction: {sursa}",
  "struct.ars.restul": "the rest",
  "struct.ars.ce_urmeaza_noduri": "What comes next for the joints (planned)",
  "struct.ars.clasifica_sistemul": "P100-1 §5.2.1 and SR EN 1998-1 §5.1.2 + §5.2.2.1 classify the system",
  "struct.ars.dupa_cat_din": ", by how much of the",
  "struct.ars.preiau_peretii": "the walls take. Above the threshold = «wall-predominant», and there the joint hierarchy (ΣM_Rc ≥ γ_Rd·ΣM_Eb, P100-1 §5.3.3.3(2), EC8 §4.4.2.3(4))",
  "struct.ars.regula_cadrelor": ": it is the rule for frames. Below the threshold = «frames», and the hierarchy is required at every joint.",
  "struct.ars.ambele_cqc": ", both combined by CQC. Because CQC is not additive,",
  "struct.ars.incercari#n": "1 attempt|{n} attempts|{n} attempts",
  "struct.dr.perioada_control_a": "the control period of the spectrum, from the <b>P100-1/2013 zoning map</b> (fig. 3.2) for the project's locality. It is not read from the geotechnical report: it is a map value, and the transcription in the report can be wrong — the divergence, where there is one, is written in Step 1.",
  "struct.dr.directia_nivelul": "direction {dir}, storey {niv}",
  "struct.dr.punctul_cazul": "point «{punct}», case {caz}",
  "struct.dr.directia_val": "direction {dir}:",
  "struct.dr.deficit_pct": " (−{v} %)",
  "struct.dr.nivelul_n": "storey {n}",
  "struct.dr.virgula_punctul": ", point «{punct}»",
  "struct.dr.virgula_cazul": ", case {caz}",
  "struct.dr.pica_amandoua": "{lista} fail — both are stiffened (the figure above is the maximum, on {dir})",
  "struct.dr.rigiditatea_titlu": "The stiffness used for displacements: the «drift» model (M2), 0.5 uniform",
  "struct.dr.rigiditatea_text": "The displacements here — d_re, the factor <code>c</code>, θ and α — come from an analysis of <b>their own</b>, on a model with 0.50·E_c over the whole superstructure, the slab included, whatever its thickness (the letter of table E.1 and of §E.1(2)); the substructure stays at full E_c. Under an earthquake the concrete cracks and the structure moves more; the same softening would lengthen the period and reduce the forces — which is why the superstructure's forces are taken from another model («forces», M3) and the reactions from uncracked concrete («foundation», M1).",
  "struct.dr.ancora_normativa": " Code reference: <b>{articol}</b>.",
  "struct.dr.cifra_raspunde": ". This figure answers «why does the drift no longer pass»: it says how much of the excess comes from moving to the softened model and how much was there already.",
  "struct.dr.perioada_fundamentala": "The fundamental period on the drift model is longer (X: {x} s, Y: {y} s) than on uncracked concrete — and it is <b>that one</b> which enters <code>c</code>: the factor amplifying the displacements is computed on the T₁ of the same model d_re comes from, so the formula does not mix two models. Where the cap √(T_C·q)/1.7 governs, the choice changes no figure.",
  "struct.dr.sectiunea_incastrare": "The section of fixity — storey {n}",
  "struct.dr.baza_drift_titlu": "Base of the drift model",
  "struct.dr.baza_incastrata": "d_re is measured on the model with a FIXED base",
  "struct.dr.baza_elastica": "d_re is measured on the model resting on elastic soil",
  "struct.dr.baza_mod_auto": "automatic decision",
  "struct.dr.baza_mod_fortat": "forced from the preferences (drift_pe_baza = {mod})",
  "struct.dr.cota_m": " (level {v} m)",
  "struct.dr.incastrare_text": "{motiv}. Drift is checked only <b>above</b> this section: below it is the substructure, whose displacement does not distort partition walls. Fixity at the floor over the basement is not a choice, it is a <i>consequence</i> of the conditions below — {articol}.",
  "struct.dr.raport_rigiditate": "Stiffness ratio <b>measured</b> on drift (largest above ground / last below ground): <b>{v}×</b>",
  "struct.dr.pe_modelul_elastic": " — on the elastic model, so WITH the flexibility of the foundation included",
  "struct.dr.th_valoare": "Value",
  "struct.dr.th_sursa": "Source",
  "struct.rzv.grinzile_stalpii_suprast": "The beams and columns of the superstructure have both ends tied into the structure. The foundation does not: it stands on soil, which compresses under it. The classic model for that is called <b>Winkler</b> — the ground is replaced with a carpet of independent springs, each obeying p = K_s·z: the harder you press, the further it goes down, proportionally. K_s (the subgrade modulus) is the «strength» of one spring and comes from the settlement computed in Step 6.",
  "struct.rzv.ax_primeste_b": "Each axis gets <b>three readings of the same pressure</b>, so that it can be seen how much the result depends on the assumption: the rigid-base formula of the spreadsheet, the code formula with a base that can lift off, and the pressure read straight off the model's springs. When the three are close, the result is robust; when they differ widely, the assumption matters and is worth looking at.",
  "struct.rzv.arcurile_b_liniare": "The springs are <b>linear</b>, not «tension-free». It is a deliberate choice: were they non-linear, the combinations of Step 11 (which come from superposition of effects, R = C·F) would no longer be exact. How much of the bed would go out of play in a tension-free model can be read for free anyway, from the sign of the reactions: a spring with a negative reaction has been pulled, so in reality it would have lifted off.",
  "struct.rzv.panglica_fundatie_verifi": "A strip foundation is not checked in one-metre pieces but by <b>axes</b>: everything standing on the same line in plan forms an axis, even where it is broken by gaps. That is how the engineer works with a pencil too — one spreadsheet per axis (AxA, AxB, … Ax1, Ax2), adding up the reactions of all the columns on that line and checking the strip of base beneath them.",
  "struct.rzv.b_pasul_resoartelor": "<b>the spring spacing</b> is not chosen arbitrarily: it is capped at π/4 of the elastic length λ⁻¹ = (4EI/(K_s·B))^¼, the distance over which the effect of a concentrated force «dies out» (NP 112 annex K.3.2). Below it, the discrete carpet of springs behaves like a continuous one.",
  "struct.rzv.b_pasul_17": "<b>Step 17</b> — only now is the foundation checked against its real reactions, axis by axis and combination by combination: pressures, lift-off, settlement, punching. If something fails, the local loop looks for a geometry that passes, each time choosing the cheapest action in euros.",
  "struct.ret.otel_cofraj_accesorii": "steel, formwork, accessories — 0 in the reference region, it is already in the price",
  "struct.ret.1_00_bucuresti": "1.00 Bucharest–Ilfov · 0.92 large city · 0.85 small urban/rural",
  "struct.ret.include_utilajele_punere": "includes the plant for placing, as in the reference",
  "struct.ret.franco_santier_zona": "delivered to site in the reference region",
  "struct.ret.sub_el_plateste": "below it an incomplete truck-mixer load is charged",
  "struct.ret.raza_transport_inclus": "Radius with transport included in the price",
  "struct.ret.distanta_statia_betoane": "Distance to the batching plant",
  "struct.ret.distanta_groapa_autoriza": "Distance to the licensed tip",
  "struct.ret.indice_manopera_zonei": "The region's labour index",
  "struct.ret.taxa_depozitare_pamant": "Soil disposal charge",
  "struct.ret.raza_max_groapa": "Reasonable radius to the pit",
  "struct.ret.raza_max_hint": "beyond it no pit is chosen automatically: the estimate uses the capped price",
  "struct.ret.tarif_plafon_pamant": "Capped tip charge, no pit identified",
  "struct.ret.tarif_plafon_hint": "market maximum (60 lei/t); the pit catalogue is not complete",
  "struct.ret.acces_titlu": "Truck access fee (Bucharest, zone A / B)",
  "struct.ret.acces_explicatie": "Per vehicle and per day, not per trip: the estimate counts truck-days per flow (soil, concrete, pump, rebar, formwork, plant) with the assumptions below, prices them on the site's zone at the tariff in force on the estimate date and subtracts the reference zone, whose fees are already in the delivered prices. The “Access fees” item in the estimate shows both amounts.",
  "struct.ret.acces_zona": "Site zone",
  "struct.ret.acces_unde": "changed on the location screen",
  "struct.ret.acces_necunoscut": "the site has no coordinates — the estimate adds no access fee",
  "struct.ret.acces_referinta": "Reference zone",
  "struct.ret.acces_ref_niciuna": "none (the whole fee)",
  "struct.ret.acces_viteza": "Average truck speed in the city",
  "struct.ret.acces_minute": "Dead time per trip",
  "struct.ret.acces_minute_hint": "loading, unloading, waiting",
  "struct.ret.acces_ore": "Working hours per day",
  "struct.ret.acces_zile_luna": "Working days per month",
  "struct.ret.acces_regim_pamant": "Soil dump trucks",
  "struct.ret.acces_regim_luna": "monthly subscription, when cheaper",
  "struct.ret.acces_regim_zi": "daily permits",
  "struct.ret.acces_camion": "Payload of the rebar / pallet truck",
  "struct.ret.acces_cofraj": "Formwork per truck",
  "struct.ret.acces_beton_zi": "Concrete poured in one pump day",
  "struct.ret.um_min": "min",
  "struct.ret.um_ore": "h",
  "struct.ret.um_zile": "days",
  "struct.ret.um_km_h": "km/h",
  "struct.ret.tarif_cifa_peste": "Truck-mixer rate beyond the radius",
  "struct.ret.taxa_cifa_incompleta": "Incomplete truck-mixer charge",
  "struct.ret.fasonare_per_bucata": "Bending per piece",
  "struct.ret.prag_turnare_mica": "Small-pour threshold",
  "struct.ret.tarif_basculanta": "Tipper rate",

  "struct.reg.fara_limita": "no limit",
  "struct.reg.forta_pui_numarator": "what force you put in the numerator",
  "struct.reg.doua_parghii_ordinea": "Two levers, in order of effectiveness:",
  "struct.reg.redu_suprafata_aferenta": "Reduce the tributary area",
  "struct.reg.creste_clasa_betonului": "Raise the concrete class",
  "struct.reg.lungirea_sectiunii_ajuta": "Lengthening the section does not help here.",




  // --- ecranele structurii: textele care erau scrise direct în cod ---
  "struct.arf.ancorare_innadire": "anchorage {a} m · lap splice {i} m",
  "struct.arf.aria_sectiune": "Reinforced section of the foundation beam on {ax}",
  "struct.arf.atinge_capatul": "· reaches the end of the axis",
  "struct.arf.axe_armate": "<b>{n}</b> foundation axes reinforced ·",
  "struct.arf.axele_de_fundare": "Foundation axes",
  "struct.arf.cauza_incastrata": "The usual cause is a FIXED base: without a subgrade modulus, Step 7 does not build the spring bed, so there are no foundation beams to reinforce. That verdict is in",
  "struct.arf.clasa_expunere": "Exposure class of the foundation: {clasa}",
  "struct.arf.constructivi": "· detailing minimum",
  "struct.arf.constructivi_vrdc": "· detailing minimum (V_Ed ≤ V_Rd,c)",
  "struct.arf.cota_axului": "The foundation depth belongs to THIS axis, not to the building: it follows from ITS span (H ≥ L₀/6, and for the «T» detail the section fills the trench).",
  "struct.arf.detaliere_aici": "{regula}. Here: a_l = {al} m (cotθ = {cot}), stirrup support zone {z} m on either side of each column.",
  "struct.arf.eforturile_din": ", the action effects from",
  "struct.arf.etrier_montaj": "assembly stirrup, {fata} bundle",
  "struct.arf.etrieri_ramuri": "Ø{phi}/{pas} mm · {ramuri} legs",
  "struct.arf.expunere_de_ce": "{motiv}. The foundation is NOT in the exposure class of the superstructure: the concrete sits directly on the soil, and below the groundwater level it is permanently wet. On top of the durability cover, SR EN 1992-1-1 §4.4.1.3(4) requires at least 40 mm when cast on blinding concrete and 75 mm when cast directly against unprepared ground.",
  "struct.arf.fara_rezultate": "The step produced no results.",
  "struct.arf.fetele_inimii": "on the faces of the deep web (NP 112 §II.7.5.2)",
  "struct.arf.fisura_wk": "w_k = {wk} mm · σ_s = {sigma} MPa · {fata} face",
  "struct.arf.geometria_vine_din": "The geometry comes from",
  "struct.arf.grinda_fundare_ax": "Foundation beam · {ax}",
  "struct.arf.iar_cauza_lui_la": ", and its cause in",
  "struct.arf.infasuratoarea_lungime": "The envelope along the length and the reinforcement that covers it",
  "struct.arf.laterale_la": "{n}Ø{phi} at {pas} mm",
  "struct.arf.longitudinale_jos": "bottom longitudinal bars",
  "struct.arf.longitudinale_sus": "top longitudinal bars",
  "struct.arf.m_cvasipermanenta": "M under the quasi-permanent combination",
  "struct.arf.montaj_leaga": "ties {bare} bars ({pe_rand} per layer × {straturi} layers at {interax} cm) into one bundle · developed length {desf} m · not counted in the resistance: it holds the cage in position, and its centre is the point from which the lever arm is measured",
  "struct.arf.nerulat": "The step has not been run yet. It needs the action effects of Step 12 and the geometry adopted in Step 17.",
  "struct.arf.otel_pe_beton": "· {otel} kg steel over {beton} m³ concrete ({kgm3} kg/m³)",
  "struct.arf.prelungire_fiecare_parte": "{al} + {lbd} = {p} m on each side",
  "struct.arf.randuri": "· {n} layers",
  "struct.arf.rho_pas_maxim": "ρ_w = {rho} % · maximum spacing {pas} mm",
  "struct.arf.rulat": "run {data}",
  "struct.arf.sectiune_acoperire": ". Cover {c} mm, d = {d} m.",
  "struct.arf.sectiune_cap": "Section of axis <b>{ax}</b> at level −{df} m: bottom {jn}Ø{jphi}, top {sn}Ø{sphi}, stirrups Ø{ephi}/{epas} mm ({ramuri} legs)",
  "struct.arf.sectiune_plasa": "· footing mesh Ø{phi}/{pas} mm",
  "struct.arf.supl": "+ {n} add.",
  "struct.arf.th_as_cerut_acolo": "A_s required there",
  "struct.arf.th_as_efectiv": "A_s provided",
  "struct.arf.th_as_necesar": "A_s required",
  "struct.arf.th_asw_efectiv": "A_sw/s provided",
  "struct.arf.th_asw_necesar": "A_sw/s required",
  "struct.arf.th_bara": "Bar",
  "struct.arf.th_cota": "Level",
  "struct.arf.th_e_cerut": "E (demand)",
  "struct.arf.th_lungimea_barei": "Bar length",
  "struct.arf.th_prelungirea": "Extension (a_l + l_bd)",
  "struct.arf.th_r_capabil": "R (capacity)",
  "struct.arf.tin_transversalele": "hold the transverse bars at spacing, along the length of the footing",
  "struct.arf.zone": "· {n} zones",
  "struct.arg.ce_face_pasul": "What the step does",
  "struct.arg.foaia_b": "sheet B",
  "struct.arg.foaia_b_ref": "sheet B: {ref}",
  "struct.arg.grinzi_armate": "Reinforced beams",
  "struct.arg.lbl_grinda": "Beam",
  "struct.arg.simbol_marja": "margin",
  "struct.arg.strivirea_diagonalelor": "crushing of the compression struts (EC2)",
  "struct.arg.svg_etr": "stirr. Ø{phi}",
  "struct.arg.svg_h_include_placa": "h includes the slab",
  "struct.arg.svg_jos": "bottom: {arm}",
  "struct.arg.svg_sus": "top: {arm}",
  "struct.arg.th_limita": "Limit",
  "struct.arg.th_pas_etrieri": "stirrup spacing",
  "struct.arg.zona_critica": "critical zone",
  "struct.arg.zona_curenta": "regular zone",
  "struct.arg.zona_fundamentala": "fundamental",
  "struct.arh.acoperire_puncte": "Point coverage",
  "struct.arh.aliniat_peste": "aligned over the other storeys by ({dx}, {dy}) m",
  "struct.arh.arhitectul_a_desenat": "the architect drew on this storey <b>{st} columns</b>{sst}, <b>{d} diaphragms</b>{sd} and <b>{g} beams</b>{sg} — read as a reference, the structural proposal remains Step 4's",
  "struct.arh.arie_a": "Area A",
  "struct.arh.conturul_se_inchide": "the wall outline closes by itself",
  "struct.arh.conturul_zonei_urmeaza": "Each zone's outline follows <b>{pereti}</b>: for an enclosed room the zone is exactly the space between the walls, and for an open space (open-plan, glazed walls) it is a marker with the area from the zone stamp, oriented along the walls and clipped by them. The zones appear on the diagram below, layer “Imposed-load uses (A/C)”.",
  "struct.arh.cotele_de_nivel_gasite": "Levels found",
  "struct.arh.deschide_desenul": "Open drawing {fisier}",
  "struct.arh.desen_in_centimetri": "drawing in centimetres — converted to millimetres (×{f})",
  "struct.arh.desen_in_metri": "drawing in metres — converted to millimetres (×{f})",
  "struct.arh.diagrama_per_nivel": "Diagram per storey",
  "struct.arh.din_cotele_fatadei": "from the elevation levels",
  "struct.arh.dreptunghiuri": "Rectangles",
  "struct.arh.dreptunghiuri_pe_niveluri": "rectangles on {n} storeys",
  "struct.arh.etaj_repetat_nota": "repeated storey — same plan as the storey below",
  "struct.arh.fatade_incercate": "Elevations tried, in order:",
  "struct.arh.fisiere_fatade": "Files identified as elevations:",
  "struct.arh.functiunile_spatiilor": "Room uses (imposed load categories)",
  "struct.arh.goluri_nivel": "{n} openings — {usi} doors, {ferestre} windows; widths {min}–{max} m",
  "struct.arh.goluri_usi_ferestre": "Openings (doors + windows)",
  "struct.arh.inaltimi_de_etaj": "Storey heights",
  "struct.arh.inclusiv_placi_consola": "including {n} cantilever slabs (balconies/terraces) of {arie} m² — category C for imposed load",
  "struct.arh.jurnalul_extractiei": "Extraction log ({n})",
  "struct.arh.latimi": "(widths {l})",
  "struct.arh.lay_balcoane": "in view / balconies:",
  "struct.arh.lay_etichete": "labels:",
  "struct.arh.lay_goluri": "openings:",
  "struct.arh.lay_pereti": "walls:",
  "struct.arh.lay_structura_arhitect": "architect's structure:",
  "struct.arh.leg_click_perete": "click on a wall = the point on the left + its dimensions (length × thickness × storey height)",
  "struct.arh.leg_layere_aprinse": "layers can be switched on/off from the diagram panel",
  "struct.arh.leg_pereti_persistenti": "walls persistent across storeys (the same PP list on every storey)",
  "struct.arh.leg_pereti_simplificati": "simplified walls (rectangles)",
  "struct.arh.leg_placi_consola": "BC cantilever slabs (balconies, terraces — no walls along the edge)",
  "struct.arh.locuit_q": "residential — q = 1.5 kN/m²",
  "struct.arh.m2_in_total": "{arie} m² in total",
  "struct.arh.n_niveluri": "{n} storeys — ",
  "struct.arh.nicio_fatada": "No elevation gave usable levels, so the storey heights are the fallback value of {h} m (it also appears under Warnings).",
  "struct.arh.nivelurile": "Storeys",
  "struct.arh.pereti_simplificati_card": "Simplified walls",
  "struct.arh.separare_ales": "{n} plan-sized drawings on the sheet; chosen",
  "struct.arh.separare_cel_de": "the {a}×{b} m one",
  "struct.arh.simbol_conturul_zonei": "zone outline",
  "struct.arh.simbol_etaj_repetat": "repeated storey",
  "struct.arh.simbol_plan_sursa": "Source plan",
  "struct.arh.simbol_unitatea_desenului": "drawing unit",
  "struct.arh.sursa_doua_puncte": "Source:",
  "struct.arh.terase_balcoane_logii": "Terraces, balconies, loggias",
  "struct.arh.titlu_desen": "Architecture analysis — {nivel}",
  "struct.arh.usi_ferestre_gazda": "{usi} doors · {ferestre} windows · {gazda} with a host wall",
  "struct.arh.vezi_desenul": "view drawing",
  "struct.arh.zone_exterioare": "{n} outdoor zones:",
  "struct.arh.zonele_c": "C zones",
  "struct.arp.contur": "perimeter",
  "struct.arp.efortul_lunecare_vedi": "shear stress at the interface v_Edi = β·V_Ed/(z·b_i)",
  "struct.arp.prin_marja": "within margin {marja}%",
  "struct.arp.ref_nod_cct": "k₂ = 0.85 (CCT node), ν′ = 1 − f_ck/250",
  "struct.arp.ref_rost_vedi": "SR EN 1992-1-1 eq. (6.24); governing point {punct} ({grupa})",
  "struct.arp.ref_rost_vrdi": "eq. (6.25); ρ = A_s/A_i = {rho} % (vertical bars Ø{phi}/{pas}, both meshes), σ_n = {sigma} MPa",
  "struct.arp.th_agrafe": "cross-ties",
  "struct.arpl.arie_din": "{a} m² of {b} m²",
  "struct.arpl.consolele_balcoanele": "Cantilevers (balconies)",
  "struct.arpl.consolele_ochiului": "Cantilevers of the panel (balconies)",
  "struct.arpl.continuu_spre": "· continuous → {vecin}",
  "struct.arpl.ipotezele_metodei": "Method assumptions",
  "struct.arpl.laturile": "Edges:",
  "struct.arpl.liber": "free",
  "struct.arpl.momentele_de_proiectare": "Design moments",
  "struct.arpl.momentele_se_iau_pe": "The moments are taken as",
  "struct.arpl.n_picate": "{n} failed",
  "struct.arpl.ochiul": "Panel",
  "struct.arpl.plasele_alese": "Meshes chosen",
  "struct.arpl.reazem_de_la_vecin": "(support: {v} m from the neighbour)",
  "struct.arpl.schema_de_reazem": "Support scheme",
  "struct.arpl.simplu_rezemat": "simply supported",
  "struct.arpl.th_adus_grinzi": "Brought by beams",
  "struct.arpl.th_armare": "Reinforcement",
  "struct.arpl.th_as_minim": "A_s minimum",
  "struct.arpl.th_capete_continue": "Continuous ends",
  "struct.arpl.th_deschidere": "Span",
  "struct.arpl.th_m_reazem_i": "M support i",
  "struct.arpl.th_m_reazem_j": "M support j",
  "struct.arpl.th_ochi": "Panel",
  "struct.arpl.th_salt_axial": "Axial force jump",
  "struct.ars.betonul_confinat_rezultat": "Resulting confined concrete",
  "struct.ars.folosit_1": " → 1 used",
  "struct.ars.k_este_plafon": "<b>k = min(1; ΣM_Rb/ΣM_Rc)</b> is <i>{plafon}</i>{explicatie}",
  "struct.ars.m_pe_latura": "M about side {l}",
  "struct.ars.neaditivitate_cqc": "CQC non-additivity",
  "struct.ars.simbol_beton": "concrete",
  "struct.ars.simbol_gamma_nod": "γ_Rd joint hierarchy",
  "struct.ars.sub_restul": "rest",
  "struct.ars.th_ierarhia_nodului": "joint hierarchy",
  "struct.ars.th_sistem": "system",
  "struct.cob.bare_beam_uniform": "{n} elements with <code>-beamUniform</code>, between {min} and {max} kN/m. Darker colour = higher kN/m. Note the detail: OpenSees integrates the distributed load only over the FLEXIBLE length of the member (without the rigid zones <code>-jntOffset</code>), so w is scaled by L_nod/L_flex so that the applied total is exactly the physical one — hence equilibrium closing below 0.1 %.",
  "struct.cob.bare_nimic": "no member receives a line load in this case.",
  "struct.cob.barele": "Frame members",
  "struct.cob.bile_goale_text": "{t} t of substructure: the weight of the foundation beams exists in gravity case G4, in the reactions and in the cost estimate, but it is NOT seismic mass (EN 1998-1 §4.3.3.2.2(1)P: the mass is that above the foundation). Toggle the <code>masa_infrastructura_seismica</code> preference if you want them included too.",
  "struct.cob.bile_pline": "<b>Filled spheres (blue)</b> — {t} t that go into <code>ops.mass</code> and hence into the modal analysis and the seismic force.",
  "struct.cob.finisaje_sdl": "finishes (SDL)",
  "struct.cob.infra_componente": "foundation beams + backfill + surcharge at natural ground level (CTN)",
  "struct.cob.infra_nu_masa": "The substructure ({kn} kN) appears in the loads (cases G4 and G2c) and in the reactions, but not in the table above: it is not seismic mass.",
  "struct.cob.infrastructura": "substructure",
  "struct.cob.masele_ops": "Masses (ops.mass)",
  "struct.cob.nivel_n": "level {n}",
  "struct.cob.placa": "Slab",
  "struct.cob.placa_beton": "concrete slab",
  "struct.cob.placa_forte_nodale": ". OpenSees cannot take “kN/m²” on a shell: each element receives four nodal forces <code>p·A/4</code>, one at each corner (<code>ops.load</code>). That is why the face is coloured, but the forces sit at the nodes.",
  "struct.cob.placa_nimic": "receives nothing in this case.",
  "struct.cob.presiune_de": "pressure of {lista}",
  "struct.cob.presiune_pe_nivel": "{p} kN/m² on level {n}",
  "struct.cob.raport": "Ratio",
  "struct.cob.se_aplica_grinzi_fundare": "Applied to the <b>foundation beams</b> (level 0), not to the superstructure",
  "struct.cob.stalpii_peretii_jumatate": "<b>columns and walls</b> are split in half between the floor above and the floor below (storey masses, P100-1), whereas as a LOAD the weight of a column goes entirely to the floor it supports;",
  "struct.cob.ultima_cifra": "The last figure — <b>{kn} kN</b> — is everything that reaches the ground, unfactored. It must also come out of the sum of the vertical reactions (the equilibrium check below), and divided by the footing area it gives the order of magnitude of the pressure from Step 6.",
  "struct.combina.caz_g1": "Self-weight of the concrete structure",
  "struct.combina.caz_g4": "Substructure: foundation beams + soil backfill + surcharge at ground level",
  "struct.combina.p_articolul_aplicat": "article applied",
  "struct.combina.p_normativ": "code",
  "struct.combina.p_proiectul_curent": "current project",
  "struct.combina.p_sursa_datelor": "data source",
  "struct.combina.pe_proiect_mt": "On this project: ΣM_t = {v} kNm.",
  "struct.combina.pe_proiect_total": "On this project: {eticheta} = {v} kN.",
  "struct.combina.simbol_gg_defav": "γ_G unfavourable",
  "struct.combina.simbol_gg_fav": "γ_G favourable",
  "struct.combina.simbol_gq_dom": "γ_Q leading",
  "struct.combina.simbol_gq_nedom": "γ_Q·ψ₀ accompanying",
  "struct.combina.simbol_regula_30": "0.30 (the 30 % rule)",
  "struct.combina.simbol_semne": "± (the signs)",
  "struct.dgf.criteriul_panta": "the criterion is the slope: Δs / L ≤ 0.002 (frames) or 0.001 (with masonry) — NP 112 Table H.1",
  "struct.dgf.desprins": "uplifted",
  "struct.dgf.fiecare_arc": "each spring: k_v = K_s · B · l_tributary  →  p = K_s · z",
  "struct.dgf.g_lest": "G (ballast)",
  "struct.dgf.grinda_fundare_ei": "foundation beam (B × H_c, uncracked EI)",
  "struct.dgf.h_cutremur": "H (earthquake)",
  "struct.dgf.muchie": "edge",
  "struct.dgf.nivelul_apei": "water table",
  "struct.dgf.reactiunea_arc_aria": "the reaction of a spring ÷ its area = the actual pressure beneath it (the “p model” column)",
  "struct.dgf.reactiunea_incarca_jos": "soil reaction — loads the beam from below",
  "struct.dgf.reactiunea_se_scade": "the soil reaction inside the control perimeter is SUBTRACTED from N_Ed",
  "struct.dgf.tasare_diferentiala": "differential — the structure distorts",
  "struct.dgf.tasare_uniforma": "uniform — the whole building goes down, no action effects",
  "struct.dgf.trapez": "trapezoid",
  "struct.dgf.v_greutatea": "V (weight)",
  "struct.dgf.zona_activa_inchide": "the active zone closes where σz < 0.2 · σgz — below that, the soil does not even “feel” the house",
  "struct.dr.amplificare_max": "· the acceleration is amplified at most {v} times",
  "struct.dr.amplificare_titlu": "Amplification of the response up the height (the «whiplash effect»)",
  "struct.dr.arie": "area",
  "struct.dr.articulati#n": "({n} pinned)|({n} pinned)|({n} pinned)",
  "struct.dr.beton_intreg_text": "<b>Measured on UNCRACKED CONCRETE</b> (E_c, I_g), not on the E_c,ef model that gives the drift above. The criterion is a <b>ratio</b>: under a uniform stiffness reduction it would come out identical on both models, and it becomes sensitive only through the per-element-family factors (0.80 columns / 0.50 beams / thickness-based for the slab), which are an office convention, not a code provision. Measured on E_c,ef, the same building could come out regular or irregular in elevation depending on an office convention — and that classification determines the permitted analysis method and q. When the check fails, the chain reduces q by 20 % (P100-1 §4.4.3.1); the explanation of the reduction is at Step 7, where q is chosen.",
  "struct.dr.c_propriu": "a factor specific to P100-1, absent from the Eurocode",
  "struct.dr.ca_mai_sus": "as above",
  "struct.dr.cerere_text": "For each direction, the spectral case of that direction is taken plus its accidental torsion — the equivalent of the ETABS <code>ESXP/ESXM</code> cases, as the structural engineer's calculation sheet filters them:",
  "struct.dr.cererea_deplasare": "Displacement demand",
  "struct.dr.coloane_informative": "The other two columns are INFORMATIVE, and each has a reason not to be the verdict. <b>Own K</b> (Σ12EI/h³ over the columns) ignores the beams: on a G+4 building with a set-back storey it gave 0.014 where the frame gives 0.175 — the set-back storey looked 12 times softer than it is. <b>Secant K</b> (V/d_re) is that of the SYSTEM seen at the storey: a storey's drift inherits the rotation of the storeys below, so the ratio drops going up even on a regular building — on a regular one it gave 0.69 at the ground floor, i.e. an irregularity that does not exist.",
  "struct.dr.distorsiune": "distortion",
  "struct.dr.drift_total": "— the drift is the TOTAL drift of the point, not only the distortion that the wording of (E.1) asks for",
  "struct.dr.f_acceleratie": "a_i = F_i/m_i, with F_i = V_i − V_i+1 the storey inertia force",
  "struct.dr.f_d_re": "|E_θ| + |T_acc,θ|, combined by CQC per mode",
  "struct.dr.f_distorsiune": "from the DRD block",
  "struct.dr.f_k_cadru": "K = Σ D_column, with D = a·12EI_c/h³, k̄ = Σ(I_b/L_b)/(I_c/h) from the beams at the column ends; a = k̄/(2+k̄) at a typical storey, (0.5+k̄)/(2+k̄) at a fixed base, 0.5·k̄/(1+2·k̄) at a pinned base",
  "struct.dr.f_k_proprie": "Σ12EI_c/h³ over the storey's columns",
  "struct.dr.f_k_secanta": "K_i = V_i / d_re,i, with the drift at the CENTROID (not at the unfavourable point, so that the rotation from accidental torsion does not enter)",
  "struct.dr.f_masa": "G + ψ₂·Q + ψ₂·S, lumped at the diaphragm",
  "struct.dr.f_punct": "the rigid-body field of the floor, evaluated at the centroid and at the four extremes",
  "struct.dr.f_raport_cadru": "the ratio of the two frame stiffnesses",
  "struct.dr.f_retragere": "1 − √(A_i/A_i−1), the linear equivalent of the set-back",
  "struct.dr.f_stalpi": "counted in the model; a pier enters K frame with a = 0.5·k̄/(1+2·k̄)",
  "struct.dr.fatada_agatata": " · hung façade: +30 % (E.1(5))",
  "struct.dr.in_loc_de_sls": " : {a} % instead of {b} % at SLS",
  "struct.dr.infrastructura_rand": "substructure",
  "struct.dr.intro": "The structure was analysed with seismic forces divided by <code>q</code>, so in the model it deforms <code>q</code> times less than in reality. The drift check brings the displacements back to reality and compares them with what the <i>partition walls</i> can take (serviceability limit state) and with the margin against collapse (ultimate limit state). The displacement is taken at the <b>most unfavourable point in plan</b>, not at the centre of mass: the panel that cracks is the one that distorts the most (commentary C E.2 of the code).",
  "struct.dr.k_cadru": "K frame",
  "struct.dr.k_proprie": "Own K",
  "struct.dr.la_nume": "at «{nume}»",
  "struct.dr.limita_sls": "SLS limit",
  "struct.dr.limita_uls": "ULS limit",
  "struct.dr.masa": "mass",
  "struct.dr.modul_n": "(mode {n})",
  "struct.dr.muto_text": "<b>The verdict is on the FRAME stiffness.</b> The lateral stiffness of a storey is not the sum of its columns: at the end of each column the joint rotates, and how much it rotates is decided by the beams. Muto's formula says exactly this — <i>D = a·12EI_c/h³</i>, with <i>k̄ = Σ(I_b/L_b)/(I_c/h)</i> the ratio between the stiffness of the beams at the joint and that of the column, and <i>a = k̄/(2+k̄)</i> at a typical storey, <i>(0.5+k̄)/(2+k̄)</i> at a fixed base, <i>0.5·k̄/(1+2·k̄)</i> at a PINNED base (bridge-type piers, supported on a beam). A 30×115 column with 30×65 beams spanning 6.5 m has, in the frame, about 5 % of its own 12EI/h³: the beams cap it, not the column section.",
  "struct.dr.nu_alternativa": "(EC8: 0.4 for classes III–IV)",
  "struct.dr.nu_se_cere": "not required: {nota}",
  "struct.dr.nu_text": "the SLS reduction factor — <b>0.5 fixed</b>, P100-1 §E.1(2).",
  "struct.dr.pasul_7": "Step 7",
  "struct.dr.q_text": "the behaviour factor by which the forces were reduced (Step 9)",
  "struct.dr.rap": "ratio",
  "struct.dr.raport_minim": "— lowest stiffness ratio (frame): {v}",
  "struct.dr.retragere": "set-back",
  "struct.dr.se_transmite_11": "(passed on to Step 11)",
  "struct.dr.si": " and ",
  "struct.dr.t1_directie": "the fundamental period <b>of each direction</b> — the mode with the largest effective mass along it",
  "struct.dr.t1_litera": ". The wording of (E.3) says “the fundamental natural period of vibration of the <i>building</i>” (mode 1: {t1} s), but a single T₁ breaks down where the directions have different stiffnesses — and mode 1 may be a LOCAL mode, with negligible effective mass.",
  "struct.dr.t1_moduri": "(mode {x} in X, mode {y} in Y)",
  "struct.dr.tabelul_e2": "Table E.2 — {cns}",
  "struct.dr.termen_neaplicat": "term NOT APPLIED (office method)",
  "struct.dr.th_e_calcul": "design E",
  "struct.dr.th_familia": "element family",
  "struct.dr.th_verificarea": "Check (hover over the title)",
  "struct.dr.titlu_k_secanta": "V/d_re, informative",
  "struct.dr.uls_text": "Annex E.2(2) — a requirement specific to P100-1; the Eurocode has no drift limit at ULS",
  "struct.dr.v_acceleratie": "informative — an increase towards the top is normal; the discontinuity is what is abnormal",
  "struct.dr.v_distorsiune": "informative — see divergence ⓔ6",
  "struct.dr.v_k_cadru": "its ratio to the storey below ≥ 0.70",
  "struct.dr.v_k_proprie": "informative — ignores the beams, so it exaggerates the jump at a storey with few columns",
  "struct.dr.v_k_secanta": "informative — inherits the rotation of the storeys below, so it drops going up even on a regular building",
  "struct.dr.v_raport_cadru": "≥ 0.70 (§4.4.3.3(2): reduction of at most 30 %)",
  "struct.dr.v_sls": "≤ {v} % (Table E.2)",
  "struct.dr.v_theta": "≤ 0.10 (§4.6.2.2)",
  "struct.dr.v_tot_theta": "V_tot for θ: {sursa}",
  "struct.dr.v_uls": "≤ {v} % (Annex E.2)",
  "struct.dwg.cardinal_vest": "W",
  "struct.dwg.contur": "Perimeter",
  "struct.dwg.culoare": "Colour",
  "struct.dwg.lungime_ax": "Axis length",
  "struct.dwg.niciunul": "None",
  "struct.dwg.puncte": "Points",
  "struct.dwg.tot_desenul": "Whole drawing",
  "struct.dwg.vizibile": "{n}/{total} visible",
  "struct.editor.msg_grinda_oblica": "{id}: skewed beam between {i} and {j}",
  "struct.fem.ce_se_vede": "What is shown",
  "struct.fem.coloanele_masa_baze": "The “mass X/Y/torsion” columns are referred to different bases, so they do not add up to 100 % per row; the cumulative values are the ones the 90 % criterion is checked against.",
  "struct.fem.decuplare_ok": "the first modes have periods separated by ≥ {sep}%, and modes 1–2 are translations with ≥ {dir}% in a single direction each — met",
  "struct.fem.ebc_nefisurat": "elasticBeamColumn, UNcracked E",
  "struct.fem.gol_click_ochi_placa": "Click a <b>slab panel</b> for its id, spans, thickness and how its outline was closed. While the slab is ticked it takes priority on click — untick it to reach the beams and columns beneath it.",
  "struct.fem.gol_puncte_mici": "The small dots on the beams are the subdivision nodes for the finite element method; the yellow sphere is the centre of mass of each floor (where the seismic mass of the diaphragm is lumped).",
  "struct.fem.gol_sub_cladire": "Beneath the building is the <b>foundation</b>: the concrete at its real levels — the footing, the stem wall and the portion of column that goes down into it. Click the footing for the axis section, its levels, the spring bed and where the member sits in the analysis model. The soil backfill is not drawn.",
  "struct.fem.inima_sub_placa": "web below slab",
  "struct.fem.moduri_decuplate_gate": "Decoupled modes (gate):",
  "struct.fem.nestructural": "non-structural",
  "struct.fem.parapet_titlu": "Parapet {id}",
  "struct.fem.reper_calibrare": "The calibration reference remains the FIXED-BASE variant: {nota}",
  "struct.fem.simb_beton_e": "concrete, E",
  "struct.fem.simb_destinatie": "occupancy",
  "struct.fem.simb_geometrie": "geometry",
  "struct.fem.simb_h_etaje": "storey h",
  "struct.gpan.se_socotesc": "working out the tips…",
  "struct.imb.cat_incarcari": "loads",
  "struct.imb.cat_optimizator": "optimiser",
  "struct.imb.cat_performanta": "performance",
  "struct.imb.intro": "The work list after Step 19: what we know is still to be done, with enough context to be picked up at any time. It is maintained in <code>ui/src/Imbunatatiri.tsx</code> — an implemented item is deleted from here.",
  "struct.imb.ks_recalculat_reimpus": "K_s recalculated by Step 6, automatically re-imposed on the model",
  "struct.marime.formula_cu_valori": "Formula, with the project's values:",
  "struct.modl.acum_xy": "Current (X × Y) [cm]",
  "struct.modl.afara_modul": "✗ {a} of {n} bays off the module",
  "struct.modl.alunecare_cm": "Shift [cm]",
  "struct.modl.buget_nota": "(the 12 columns with the smallest margin, out of {n}; beams also have a maximum width within their wall, used by rule R3 of Step 19)",
  "struct.modl.buget_text": "For each column, how much it can grow in each direction without leaving the persistent walls — searched in 5 cm steps, with the same fit test (including the centre shift) that applying the overlay performs. The outer loop (Step 19) caps its targets at these values <i>before</i> spending an iteration: without them it would find out that an elongation does not fit only after rerunning the whole analysis chain. A column at its maximum in both directions has no geometric leverage left — there the solution must be sought elsewhere (reinforcement, beam sections, ductility class).",
  "struct.modl.buget_titlu": "How much room is left in the walls (the geometric budget) — for debugging",
  "struct.modl.deschide_pasul": "open step {k}",
  "struct.modl.diagrama_etaj": "Diagram per storey",
  "struct.modl.elemente_schimbate": "Elements changed",
  "struct.modl.etaj": "Storey",
  "struct.modl.etaj_rezumat": "{s} columns ({a} added by the 6 m rule) · {g} beams · {L} m of beams · maximum span {d} m",
  "struct.modl.etaje_modelate": "Modelled storeys",
  "struct.modl.fara_gazda": " (no host beam)",
  "struct.modl.fasii_30_50": "strips 30 cm wide × 50 cm deep, on the wall axes",
  "struct.modl.grila_dominanta": ", the most frequent {m} m ({n} bays)",
  "struct.modl.grila_intermediari": "; the proposal placed {n} intermediate columns on the module",
  "struct.modl.grila_text": "A bay is the CLEAR distance between the faces of two neighbouring columns on the same beam, counted once per plan — that is where the cars fit, not between axes. On module = a whole number of {modul} m spaces in the clear span, with at most {tol} mm lost. EQUAL spans are the same beam formwork reused — the frame has {distincte}",
  "struct.modl.grila_titlu": "Grid on the parking module ({m} m)",
  "struct.modl.grinzi_noi": "New beams",
  "struct.modl.grinzi_propuse": "Proposed beams",
  "struct.modl.grinzi_scoase": " · {n} middle beams removed (3 parallel under 6 m)",
  "struct.modl.ierarhie_titlu": "Differing storeys — hierarchy of structure sources (A/B/C)",
  "struct.modl.inaltime_etaj": "storey height {h} m",
  "struct.modl.inca_valori": "… and {n} more values.",
  "struct.modl.istoric_nota": "★ = the step the design option continues with; the Step 19 overlay is then applied on top of it (the «Changes from the outer loop» table), so after a promotion the on-screen geometry is the last step PLUS its ops. “Open” shows the frozen state of the step, on this page (`?bp=`); the way back is the link at its top. The history is rebuilt on every run of Step 4, so the current clones belong to the current geometry.",
  "struct.modl.istoric_titlu": "Solution history — the {n} geometric steps of Step 4",
  "struct.modl.k_cadru_titlu": "K frame = {v} kN/mm",
  "struct.modl.leg_arhitectura": "walls, windows and doors come from the architectural plan",
  "struct.modl.leg_click": "click an element = the left-hand point + its dimensions (length × width × height)",
  "struct.modl.leg_grinzi": "30 cm wide beams",
  "struct.modl.leg_pereti_pers": "persistent walls (all storeys)",
  "struct.modl.leg_pereti_simpl": "simplified walls (current storey)",
  "struct.modl.leg_stalpi": "30×30 columns",
  "struct.modl.leg_stalpi_redim": "columns (30×30 proposed + the Step 19 resizings)",
  "struct.modl.liberi_text": "FREE columns ({n}): <code>{lista}</code> — they go down to the foundation OUTSIDE the building below (terrace/yard), so they are visible in the architecture; validate them.",
  "struct.modl.modificari_titlu": "Changes from the outer loop (Step 19) — for debugging",
  "struct.modl.nerulat": "The step has not been run yet. Press “Run” in the left-hand menu (after Step 2).",
  "struct.modl.patrate_30": "30×30 cm squares — an initially assumed size, we come back to it",
  "struct.modl.pe_placa_text": "Columns supported ON THE SLAB ({n}): <code>{lista}</code> — their storey walls have neither a column nor a beam below them, and a new column would have landed in the middle of a room on the storey below. The slab carries them (their bases are slab nodes in the FEM model), but PUNCHING of the slab under them is not yet checked automatically: the column reaction passes through the slab, and its punching shear (EC2 §6.4.6) must be checked manually for the listed columns.",
  "struct.modl.pereti_persistenti_rand": "persistent walls — the columns proposed on them are reused identically at every storey (the complete ground floor also gives the foundation axes)",
  "struct.modl.primul_nivel": "first storey: nothing below it to compare with",
  "struct.modl.raport_titlu": "K frame of the storey / K frame of the storey below; threshold {prag}",
  "struct.modl.rarire_capete": "{n} partition ends",
  "struct.modl.rarire_fara_stalp": "were left without a column — Step 19 brings them back where a direction is weak or a beam fails.",
  "struct.modl.rarire_iar": ", while",
  "struct.modl.rarire_text": "On-module proposal: {linii} load-bearing wall lines placed their columns on multiples of the module ({noi} new intermediate, {lipiti} snapped), the skeleton of transverse frames keeps {schelet} partitions",
  "struct.modl.refuzate_amplasare": "Rejected by placement",
  "struct.modl.regim_inaltimi": "height regime {regim} · heights {h} m — from Step 2 (the façade levels)",
  "struct.modl.regula_6m": "The 6 m rule",
  "struct.modl.reguli_incalcate#n": "{n} rule violated|{n} rules violated|{n} rules violated",
  "struct.modl.rig_text": "It is the same quantity that Step 10 checks — the <b>frame</b> stiffness of the storey, D = a·12EI_c/h³ with a from the ratio k̄ of the beams at the joint to the column —, computed here <b>without FEM</b>, from the proposed sections and spans, so that before the chain runs you can see how much the set-back storey is missing. The bar is proportional to K frame; the ratio is to the storey below and fails below {prag} (P100-1 §4.4.3.3(2)). Own K (Σ12EI/h³) is informative: it ignores the beams.",
  "struct.modl.rig_titlu": "Lateral stiffness per storey (whiplash effect, from geometry)",
  "struct.modl.simbol_axe": "axes",
  "struct.modl.stalpi_lamele_propuse": "Proposed columns and wall piers",
  "struct.modl.th_pas": "Step",
  "struct.modl.toate_pe_modul": "✓ all {n} bays on the module",
  "struct.modl.vezi_incalcarea": "see the violation",
  "struct.opt.abordarea_din": "approach {n} of {total}: ",
  "struct.opt.neschimbat": " (unchanged)",
  "struct.opt.precizie": " · precision: {p}",
  "struct.opt.reglaj": "adjustment",
  "struct.opt.th_elementul": "Element",
  "struct.opt.vezi_toate_cele": "see all {n}",
  "struct.pg.ax_cota#n": "axis {directie} · L = {L} m · 1 span|axis {directie} · L = {L} m · {n} spans|axis {directie} · L = {L} m · {n} spans",
  "struct.pg.ax_deschiderea_pagina": "{element} — the span on this page",
  "struct.pg.bara_consumat": "demand {e} out of capacity {r} — {p} % used",
  "struct.pg.explicatie": "{m} and M_Rd on the tension fibre, at the same scale — the capacity must envelop the demand. Shows the beam's 5 check points.",
  "struct.pg.explicatie_ax": "{m} and M_Rd on the tension fibre, at the same scale — the capacity must envelop the demand. Shows the beam's 5 check points, on the current span or along the whole axis.",
  "struct.pg.fara_zona_libera": "no free zone",
  "struct.pg.leg_banda": "band between them = margin",
  "struct.pg.leg_efort": "● demand {m}",
  "struct.pg.leg_rezistenta": "▬ resistance M_Rd — must envelop the demand",
  "struct.pg.leg_zona_fete": "critical zone = l_cr {l} m from each face",
  "struct.pg.leg_zona_toata": "critical zone = the whole span (l_cr {l} m ≥ l₀)",
  "struct.pg.mini_zona_curenta": "current zone, between the critical zones",
  "struct.pg.mini_zone_critice": "critical zones, l_cr = {l} m from each face",
  "struct.pg.petrecere_jos": "bottom lap splice · Ø{phi} · l₀ = {l0} m · {zona}",
  "struct.pg.petrecere_sus": "top lap splice · Ø{phi} · l₀ = {l0} m · {zona}",
  "struct.pg.punct_camp": "span: the station with the maximum positive moment between the faces (bottom fibre)",
  "struct.pg.punct_r1_jos": "BOTTOM fibre at the face of the first column — under seismic action the support can also put the bottom in tension",
  "struct.pg.punct_r1_sus": "TOP fibre at the face of the first column — the negative support moment",
  "struct.pg.punct_r2_jos": "BOTTOM fibre at the face of the other column",
  "struct.pg.punct_r2_sus": "TOP fibre at the face of the other column",
  "struct.pg.tab_ax#n": "whole axis (1 span)|whole axis ({n} spans)|whole axis ({n} spans)",
  "struct.pg.tab_ax_titlu#n": "the axis's collinear span, at the same scale|all {n} collinear spans of the axis, at the same scale|all {n} collinear spans of the axis, at the same scale",
  "struct.pg.tab_deschiderea": "span",
  "struct.pg.titlu_combinatia": ", load combination {combo}",
  "struct.pg.titlu_diagrama": "Moment envelope (resisting moment diagram)",
  "struct.pg.titlu_gruparea": "{grupa} combination",
  "struct.plan.gol": "The plan appears after Step 4 (the structural model) has run.",
  "struct.pref.arunca_reglajele": "Discard the adjustments — the calculation goes back to my settings",
  "struct.pref.chei#n": "{n} key|{n} keys|{n} keys",
  "struct.pref.de_cine": "Set by",
  "struct.pref.doar_strange": "Each setting here can only <b>tighten</b> relative to the code, never breach it: the code minimums remain the lower bound in the calculation.",
  "struct.pref.et.adancime_minima": "Minimum foundation depth",
  "struct.pref.et.amprente_incercate": "Layouts already tried",
  "struct.pref.et.beton": "Concrete class",
  "struct.pref.et.coef_excentricitate": "Eccentricity coefficient, per axis",
  "struct.pref.et.grosime_perete_subsol": "Interior basement wall thickness",
  "struct.pref.et.grosime_perete_subsol_contur": "Perimeter basement wall thickness",
  "struct.pref.et.grosime_placa": "Slab thickness",
  "struct.pref.et.h_grinda_fundare": "Foundation beam depth",
  "struct.pref.et.h_mesh": "Slab mesh size",
  "struct.pref.et.h_mesh_baza": "Project base mesh",
  "struct.pref.et.latime_minima": "Minimum footing width",
  "struct.pref.et.plafon_grosime_placa": "Slab thickness cap",
  "struct.pref.et.plafon_h_grinda": "Beam depth cap",
  "struct.pref.et.plafon_h_grinda_fatada": "Façade beam depth cap",
  "struct.pref.et.rigidizare_directie": "Stiffened direction for the T₁–T₂ separation",
  "struct.pref.et.solver_memorie": "Solver memory",
  "struct.pref.flux_automat": "automatic pipeline",
  "struct.pref.intrari#n": "{n} entry|{n} entries|{n} entries",
  "struct.pref.intro": "What <b>the bar-bending shop, the site and the architecture</b> dictate — the only choices the calculation has no way of working out. The rest is not set here, because it is not a preference: the building's properties come from the brief, the architecture and the geotechnical report; the structural layout, the sections and the concrete class are chosen by the optimisation, on score; and the code coefficients and office practice live in the code, with their source next to them. The steps that use them must be rerun after a change.",
  "struct.pref.reglaje_optimizare": "Adjustments set by the optimisation",
  "struct.pref.valoarea_ta": "Your value",
  "struct.reg.algoritmi_evolutivi": "Evolutionary / genetic algorithms",
  "struct.reg.np007_cod_nou": "NP 007-2025 — new code for reinforced concrete frames",
  "struct.reg.svg_zvelt": "slender · h/l > 2",
  "struct.reg.th_metoda": "Method",
  "struct.rz.calc_adoptat": "→ H_c adopted at the base foundation depth = {v} m",
  "struct.rz.calc_cota": "the beam's footing sits at the foundation depth, so H_c ≤ Df − 0.05 = {v} m",
  "struct.rz.calc_interval": "NP 112 §II.7.5.1:  H_c ∈ [L₀/6 ; L₀/3] = [{a} ; {b}] m,  but at least 0.50 m",
  "struct.rz.calc_l0": "design L₀ = {L0} m (the span of the least loaded axis; the building maximum is {L0_max} m, on another axis)",
  "struct.rz.calc_marginea_jos": "the LOWER bound is chosen (the most economical) → {v} m",
  "struct.rz.cat_duce_terenul": "What the soil can carry",
  "struct.rz.coeficient_pat": "Subgrade modulus Ks",
  "struct.rz.consecinta_flexibila": "<b>Consequence:</b> H_c/L₀ = {h} / {L0} = {r} < 0.125 → the beam is <b>flexible</b>. NP 112 §II.7.5.2 states that approximate methods are then NO longer allowed: the beam bends noticeably between columns and the actual pressure is no longer a straight line. The action effects must be taken from the model on an elastic medium — exactly what Step 7 does, where the foundation rests on springs. If you still want a rigid beam, the remedy is either lowering the foundation depth (room for a deeper beam) or an intermediate column to shorten the {L0} m span.",
  "struct.rz.consecinta_rigida": "<b>Consequence:</b> H_c/L₀ = {h} / {L0} = {r} > 0.125 → the beam is <b>rigid</b>. NP 112 §II.7.5.2 then allows approximate calculation methods and the assumption that soil pressures are distributed linearly beneath it.",
  "struct.rz.criteriu_sectiune": "the section (H ≥ L₀/6)",
  "struct.rz.criteriu_terenul": "the soil",
  "struct.rz.descrisa_pentru": " — described for the {b} m footing; wider axes have a thicker footing (the cantilever requires h_t ≥ (B−b_i)/2)",
  "struct.rz.dimensiunile_fundatiei": "Foundation dimensions (the step's result)",
  "struct.rz.din_cuantizare": " (+{v} from quantisation)",
  "struct.rz.doua_variabile": "<b>footing width × foundation depth</b> — the two variables the optimisation searches for",
  "struct.rz.f_apasarea": "Stress at depth and the active zone:",
  "struct.rz.f_cat_duce": "What the soil can carry, at B = {B} m and Df = {Df} m:",
  "struct.rz.f_coeficientul_pat": "Subgrade modulus (the soil spring):",
  "struct.rz.f_greutatea": "Building weight (rule-of-thumb estimate):",
  "struct.rz.f_presiunea_efectiva": "Effective pressure on the footing (Step 6 — average over the whole strip):",
  "struct.rz.f_presiunea_neta": "Net pressure (the one that actually settles the soil):",
  "struct.rz.f_s": "s = 0.8 · Σ (σmed,i · hi / Es,i) = <b>{s} cm</b> {rel} {max} cm allowed",
  "struct.rz.f_sigma_z": "σz(z) = α0(z/B, L/B) · p_net · active zone down to where σz < 0.2 · σgz → <b>{z} m below the footing</b>",
  "struct.rz.f_tasarea": "Settlement (summation over elementary layers):",
  "struct.rz.formula_flexibila": "< 0.125 → flexible",
  "struct.rz.formula_h_nec": "H_nec = max(L₀/6 ; 0.50 m ; h_punching)          NP 112 §II.7.5.1\n",
  "struct.rz.formula_rigida": "> 0.125 → RIGID",
  "struct.rz.formulele_pasului": "The step's formulas, with the project's values",
  "struct.rz.fundare_in": "founded in {strat}",
  "struct.rz.fundatia_continua": "A continuous foundation is not just a wide footing cast on the ground: on top of the footing runs a <b>reinforced concrete beam</b> that ties the columns together. It makes a large load on one column press not only beneath it but spread over a portion of the strip — the deeper the beam, the further it spreads the load. That is why its depth is tied to the distance between columns.",
  "struct.rz.fundatia_propusa": "Proposed foundation (optimised for minimum volume)",
  "struct.rz.greutate_cladire": "Building weight",
  "struct.rz.hc_retezat": "H_c was <b>capped by the foundation depth</b>: the beam cannot be deeper than the excavation it sits in, otherwise it would stick out of the ground (the 5 cm deducted are the blinding concrete under the footing). The remedy is either lowering the foundation depth or an intermediate column to shorten the {L0} m span.",
  "struct.rz.hc_sub_l0": "⚠ H_c = {h} m is below L₀/6 = {l6} m, <b>although the foundation depth would allow {df} m</b>. The depth stayed at the one with which the foundation was checked against the actual reactions (the search increases it only when punching or shear fails). The excavation is already paid for, so a deeper beam is cheap and would bring H_c/L₀ above 0.125 — that is, a rigid beam. See check S2d.",
  "struct.rz.ks_dinamic": "dynamic {ks} daN/cm³ (× {c})",
  "struct.rz.ks_estimare": "The estimated weight would have given <b>{ks} daN/cm³</b> ({dif} difference) — which is why the estimate is no longer used once the actual pressures exist.",
  "struct.rz.ks_estimat": "<b>Where it comes from:</b> on the first pass through the chain the model does not exist yet, so the pressure is estimated — the empirical building weight (g/m² × gross floor area) spread uniformly over the whole footing. <b>It updates itself:</b> once Step 17 has run, Step 6 recomputes K_s from the actual pressures on each axis (k_s = p_ef/s, NP 112 Annex K.3.1.4) and the model rests on that value on the next pass. The consistency of the two is checked explicitly — see “Soil–structure loop” in the Global tab.",
  "struct.rz.ks_reale": "<b>Where it comes from:</b> from the <b>actual</b> pressures on the axes, not from the estimated weight — k_s = p_ef/s computed on each axis and averaged weighted by its footing area (“back-calculation”, NP 112 Annex K.3.1.4). Over the {n} checked axes the values range from {min} to {max} daN/cm³.",
  "struct.rz.l_cumulat": " · L = {L} m in total",
  "struct.rz.la_cutremur": "under earthquake (seismic combination): <b>{p} kPa</b> = 1.35 · p_conv",
  "struct.rz.latime_medie": " (Σ L_ax·B_ax·D_f; an average width of {b} m would give the same volume — an indicative figure, not a construction dimension)",
  "struct.rz.latimi_talpa": "<b>{n} footing widths</b> (at most 3 per project), each axis at the width required by ITS own load — see column “B” in the “Axes” table. The foundation depth is a single one for the whole building.",
  "struct.rz.legenda_desen": "The foundation is drawn in <b>yellow</b> (the band = the footing at its actual width, B = {B} m; the line and label = the named axis, with its width). Clicking an element selects it and colours it <b>turquoise</b>, without moving you off the drawing — for the axis checks there is the button in the info panel.",
  "struct.rz.limita_exploatare_formula": "<b>service limit</b> (fundamental combination) = 1.10 · p_conv",
  "struct.rz.limita_paranteza": " (limit {limita})",
  "struct.rz.marime_derivata": "a <b>derived</b> quantity, not an optimisation variable — see “What the optimisation searches for and what follows deterministically” above",
  "struct.rz.marimi_derivate": "Derived quantities, with the project's figures",
  "struct.rz.media_pas6": "average over the whole strip, from the Step 6 preliminary sizing: {p} kPa — indicative, it does not decide the verdict",
  "struct.rz.medie_estimata": "<b>average over the whole strip</b>, from the estimated weight (Step 6) — Step 17, per axis, has not run yet",
  "struct.rz.optimizare": "Optimisation",
  "struct.rz.p_conv_corectat": "p_conv = {p} kPa, corrected for B = {B} m and D_f = {D} m",
  "struct.rz.presiune_neta": " · net pressure {p} kPa",
  "struct.rz.presiune_teren": "Soil pressure",
  "struct.rz.presiunea_calcul": "<b>design pressure</b> = the maximum over the axes from Step 17, from the model reactions",
  "struct.rz.presiunea_calcul_ax": "<b>design pressure</b> = the maximum over the axes from Step 17 (on {ax}), from the model reactions",
  "struct.rz.repartitia": "Distribution: {lista}.",
  "struct.rz.ruleaza_predim": "Pressures, required width and settlement by the layer-summation method.",
  "struct.rz.ruleaza_verificare": "Pressures axis by axis from the actual reactions, for all combinations.",
  "struct.rz.sapatura": " · excavation {v} m³",
  "struct.rz.sapatura_strat": " · excavation {v} m³ · bearing layer: {strat}",
  "struct.rz.sect_beton_armat": "reinforced concrete  {v} m³/m\n",
  "struct.rz.sect_beton_simplu": "plain concrete       {v} m³/m\n",
  "struct.rz.sect_cofraj": "formwork             {v} m²/m\n",
  "struct.rz.sect_nota": "A and I_y go straight into Step 7 (the foundation beam elements on the spring bed); G4 into Step 8. The checks are made on the part that actually works: the transverse cantilever and the distribution across the width on the <b>footing</b>, longitudinal bending and shear on the <b>web</b>, stiffness on the whole section.",
  "struct.rz.sectiune_detaliul": "section (<b>derived</b>, detail",
  "struct.rz.sectiunea_aleasa": "Chosen section",
  "struct.rz.stratul_cota_df": "the layer at depth Df",
  "struct.rz.t_mp_desfasurat": "{g} t/m² × {A} m² gross floor area",
  "struct.rz.talpa_inima": " (footing {B}×{h_t} m + web {b_i}×{h_i} m)",
  "struct.rz.tasare_estimata": "Estimated settlement",
  "struct.rz.th_df_adoptat": "Adopted D_f",
  "struct.rz.th_df_necesar": "Required D_f",
  "struct.rz.th_h_necesar": "Required H",
  "struct.rz.trepte_cel_mult#n": "{n} tier, at most {max}|{n} tiers, at most {max}|{n} tiers, at most {max}",
  "struct.rz.valoarea_calculata_limita": "<b>calculated value</b>; limit: {l} cm (the method's target)",
  "struct.rz.verif_predim": "<b>Section preliminary sizing checks</b> — detailing conditions from NP 112, checked before any calculation of action effects:",
  "struct.rz.vezi_verificarile_axului": "↑ See the axis checks",
  "struct.rz.zona_activa": "active zone {z} m below the footing",
  "struct.rzv.coef_exc_egal": "Eccentricity coefficient k_exc = {k}",
  "struct.rzv.col_as_jos_sus": "A_s bottom / top",
  "struct.rzv.col_tasare": "Settlement",
  "struct.rzv.formula_consola": "L_c = (B − b_element)/2,   M = p_max · L_c²/2  [kNm per linear metre]",
  "struct.rzv.formula_ks_invers": "k_s = p_ef / s   (back-calculation, NP 112 Annex K.3.1.4)",
  "struct.rzv.formula_p_np112": "p_max = μ · ΣF_Z/(L·B),  μ = f(e_L/L, e_B/B) from NP 112 Table E.1",
  "struct.rzv.nod_n": "node {n}",
  "struct.rzv.ref_metoda_02": "working method (02.calcul presiune conventionala)",
  "struct.rzv.ref_metoda_np112": "working method + NP 112",
  "struct.rzv.ref_np112_i5_anexa_e": "NP 112-2014 Table I.5 + Annex E",
  "struct.rzv.sim_implicit": "default",
  "struct.seism.acceleratia_spectrala_mod": "Spectral acceleration per mode",
  "struct.seism.arata_primele_10": "Show only the first 10",
  "struct.seism.aria_spectrul": "Design spectrum Sd(T)",
  "struct.seism.cazuri_seismice_adaugate": "Seismic load cases added",
  "struct.seism.constanta_cqc": "method constant — enters the CQC correlation",
  "struct.seism.constanta_presupus_armare": "method constant — assumed, to be confirmed at reinforcement design",
  "struct.seism.criteriu_90_indeplinit": "≥ 90% criterion met · at the minimum number of modes ({n}) it was {x}% / {y}%",
  "struct.seism.criteriu_90_neindeplinit": "≥ 90% criterion NOT met · at the minimum number of modes ({n}) it was {x}% / {y}%",
  "struct.seism.din_analiza_modala_p7": "from the modal analysis of Step 7",
  "struct.seism.fb_echiv": "equiv. F_b",
  "struct.seism.intra_combinatii_p11": "{lista} — enter the load combinations of Step 11",
  "struct.seism.lu_rar_multifrontal": "sparse LU (multifrontal) on K·u = f — the same exact solution as BandGen, but ~124× faster on the shell model",
  "struct.seism.meff_cumulat": "cumulative m_eff",
  "struct.seism.meff_sd_metoda_a": "m_eff · S_d(T₁) — P100-1, method A",
  "struct.seism.nerulat": "The step has not been run yet. Press “Run” in the left-hand menu (after Steps 7 and 8).",
  "struct.seism.parametrii_spectrului": "Design spectrum parameters",
  "struct.seism.pe_proiectul_acesta": "On this project: {sursa}.",
  "struct.seism.perioada_fundamentala_x": "Fundamental period (X)",
  "struct.seism.perioadele_de_colt": "(corner periods)",
  "struct.seism.q_factorul_comportare": "q (behaviour factor)",
  "struct.seism.raspunsul_pe_directii": "Response by direction",
  "struct.seism.sd_t1_mod": "S_d(T₁) = {sd} m/s² · mode {mod}",
  "struct.seism.simbol_v_baza": "V_base",
  "struct.seism.solverul_liniar": "Linear system solver",
  "struct.seism.spectrul_p100_1": "P100-1 design spectrum",
  "struct.seism.spectrul_proiectare_sd": "Design spectrum S_d(T)",
  "struct.seism.tc_din_zonare": "T_C from the P100-1/2013 zonation (fig. 3.2) by the project's locality — see Step 1; T_B = 0.2·T_C, T_D per code",
  "struct.seism.torsiunea_accidentala": "Accidental torsion",
  "struct.strateg.aria_unui_nivel": "floor area of one storey",
  "struct.strateg.bugetul_constructiv": "Detailing budget",
  "struct.strateg.bugetul_forfecare": "Shear budget",
  "struct.strateg.ce_implica_clasa": "What class {clasa} implies further on",
  "struct.strateg.proeminenta_titlu": "Setback floor ({nivel}): appendage or storey",
  "struct.strateg.proeminenta_da": "appendage of confined masonry",
  "struct.strateg.proeminenta_nu": "frame storey",
  "struct.strateg.cele_patru_bugete": "The four concrete budgets (ρ)",
  "struct.strateg.clasa_ductilitate_titlu": "Ductility class (P100-1/2013)",
  "struct.strateg.clasa_selectata": "Selected class",
  "struct.strateg.coeficientul_seismic": "seismic coefficient",
  "struct.strateg.col_clasa": "Class",
  "struct.strateg.col_rho_forfecare": "ρ shear",
  "struct.strateg.comparatia_claselor": "Class comparison ({sistem}, αu/α1 = {alfa})",
  "struct.strateg.constrangerea_guverneaza": "Governing constraint",
  "struct.strateg.driftul_real_admis": "allowed actual interstorey drift ({delta})",
  "struct.strateg.factorul_amplificare": "amplification factor (2.5)",
  "struct.strateg.factorul_comportare": "Behaviour factor",
  "struct.strateg.factorul_comportare_presupus": "assumed behaviour factor",
  "struct.strateg.l_typ_directii": "l_typ per direction",
  "struct.strateg.nepermisa_ag": "no (a_g > 0.10g)",
  "struct.strateg.nerulat": "This step has not been run yet. Press “Run” in the left-hand menu (after Steps 1 and 2).",
  "struct.strateg.pasul_dintre_elemente": "Spacing of the vertical elements",
  "struct.strateg.pasul_practic_maxim": "maximum practical spacing",
  "struct.strateg.q_max_pereti": "q = q0 · k_w · αu/α1 (P100-1 Table 5.1, walls; k_w = {k_w})",
  "struct.strateg.rand_a_elem": "A_elem (area of one element)",
  "struct.strateg.rand_c_s": "c_s (seismic coefficient)",
  "struct.strateg.rand_d_top": "D_top (allowed top displacement)",
  "struct.strateg.rand_h": "H (total building height)",
  "struct.strateg.rand_i_req": "I_req (total required moment of inertia)",
  "struct.strateg.rand_l_max": "L_max (span with a concealed beam)",
  "struct.strateg.rand_lambda": "λ (modal correction factor)",
  "struct.strateg.rand_n_elem": "n_elem (estimated vertical elements)",
  "struct.strateg.rand_nu_t": "νt (maximum allowed axial compression on columns)",
  "struct.strateg.rand_pas": "spacing (practical distance between elements)",
  "struct.strateg.rand_v": "V (base shear)",
  "struct.strateg.rand_v_rd": "v_Rd (shear target)",
  "struct.strateg.rho_constructiv": "Detailing minimum (the architecture decides)",
  "struct.strateg.rho_drift": "Drift (the building bends)",
  "struct.strateg.rho_forfecare": "Shear (the walls would slide)",
  "struct.strateg.rho_gravitational": "Gravity (νd limit)",
  "struct.strateg.sageata_guverneaza": " ← governs",
  "struct.strateg.sageata_selectata": " ← selected",
  "struct.strateg.sim_h_grinda": "h_beam",
  "struct.strateg.structura_recomandata": "Recommended structure",
  "struct.strateg.sursa_pasul_1_zonare": "Step 1 (P100-1 seismic zonation)",
  "struct.strateg.sursa_pasul_2": "Step 2",
  "struct.strateg.sursa_pasul_2_media": "Step 2 (average)",
  "struct.strateg.sursa_pasul_2_media_niv": "Step 2 (average over storeys)",
  "struct.strateg.sursa_tab51_nota": "P100-1/2013 Table 5.1, note — {nota}",
  "struct.strateg.tronsoane": "Segments",
  "struct.strateg.valori_intermediare": "Intermediate values — each with its formula and source",
  // programul de execuție (`viewer/cost/PanouProgram.tsx`)
  "costuri.tab.program": "Schedule",
  "costuri.tab.piata": "Sales in the area",
  "prog.sub.grafic": "Chart",
  "prog.sub.resurse": "Resources",
  "prog.sub.reguli": "Rules and calendar",
  "prog.kpi.inceput": "Start",
  "prog.kpi.sfarsit": "End",
  "prog.kpi.durata": "Duration",
  "prog.kpi.luni": "{n} months",
  "prog.kpi.zile_lucratoare": "{n} working days",
  "prog.kpi.muncitori": "Workers",
  "prog.kpi.la_varf": "{n} at peak",
  "prog.kpi.in_medie": "{n} on average · {m} trades",
  "prog.kpi.macarale": "Cranes",
  "prog.kpi.luni_macara": "{n} crane-months",
  "prog.kpi.max_simultan": "at most {n} at once",
  "prog.kpi.fronturi": "{n} structure fronts · cap {p} cranes",
  "prog.kpi.camioane": "Trucks",
  "prog.kpi.zile_camion": "{n} truck-days",
  "prog.kpi.camioane_varf": "at most {n} per day · zone {z}",
  "prog.kpi.camioane_varf_fara": "at most {n} per day · no access fee",
  "prog.kpi.start_implicit": "import + 3 months",
  "prog.kpi.start_pus": "set on the project",
  "prog.zoom.luna": "Month",
  "prog.zoom.saptamana": "Week",
  "prog.zoom.titlu": "Time scale",
  "prog.zoom.tot": "Whole project",
  "prog.desfa_tot": "Expand all",
  "prog.strange_tot": "Collapse all",
  "prog.desfa": "Expand {n}",
  "prog.strange": "Collapse {n}",
  "prog.doar_ritmul": "Critical chain only",
  "prog.santier": "Site",
  "prog.corp_nivel": "Building · level",
  "prog.detaliu.interval": "Period",
  "prog.detaliu.muncitori": "Workers on the stage",
  "prog.detaliu.ore": "{n} man-hours",
  "prog.detaliu.ore_macara": "{n} crane hours",
  "prog.detaliu.impins": "start pushed {n} days: not enough work front",
  "prog.detaliu.ciclu": "Level cycle",
  "prog.detaliu.ciclu_text": "{n} working days, set by {de}",
  "prog.ritm.minim": "the minimum cycle",
  "prog.ritm.macara": "the crane",
  "prog.ritm.echipa": "the structure crew fitting the level",
  "prog.ritm.urmasi": "the following trades fitting the level",
  "prog.ritm.beton": "concrete per day (pump or plant)",
  "prog.detaliu.plafon": "Level cap",
  "prog.detaliu.plafon_text": "{n} people ({a} m²)",
  "prog.detaliu.articole": "Articles",
  "prog.detaliu.gol": "Click a bar to see the stage, or a building to see all of it.",
  "prog.detaliu.inchide": "Close details",
  "prog.detaliu.faze": "Package phases",
  "prog.detaliu.faza_suprapusa": "in parallel with the slab",
  "prog.faza.cofraj": "formwork",
  "prog.faza.armare": "rebar",
  "prog.faza.beton": "pour",
  "prog.faza.decofrare": "striking",
  "prog.faza.montaj": "erection",
  "prog.faza.sapatura": "excavation",
  "prog.faza.fundatii": "foundations",
  "prog.faza.lucru": "work",
  "prog.parte.verticale": "verticals (columns, walls)",
  "prog.parte.placa": "slab (beams, slab)",
  "prog.lant.structura": "Structure",
  "prog.lant.niveluri": "{n} levels",
  "prog.corp.amprenta": "{a} m² footprint",
  "prog.corp.macarale": "{b} × {l} months",
  "prog.corp.asteptat": "Waited for cranes",
  "prog.corp.asteptat_text": "{n} working days for free cranes (cap {p}); started on {d}, when {c} released its cranes.",
  "prog.corp.asteptat_text_fara": "{n} working days for free cranes (cap {p}); started on {d}.",
  "prog.corp.lucrari": "Site works",
  "prog.corp.pe_subsol": "Stands on the basement",
  "prog.corp.pe_subsol_text": "The structure starts on the slab over {s}, ready on {d}.",
  "prog.subsol.regim": "shared basement · {n} levels",
  "prog.subsol.pe_el": "Buildings on it",
  "prog.subsol.pe_el_sub": "start after the slab over its top level",
  "prog.avertismente": "Warnings",
  "prog.explicatii": "How the buildings were placed",
  "prog.etape.titlu": "Stages",
  "prog.etape.th.etapa": "Stage",
  "prog.etape.th.inceput": "Start",
  "prog.etape.th.sfarsit": "End",
  "prog.etape.th.muncitori": "Workers",
  "prog.etape.th.ore": "Man-hours",
  "prog.etape.th.decalaj": "End lag",
  "prog.etape.th.unitati": "Units",
  "prog.etape.decalaj_text": "{n} days after {de}",
  "prog.etape.da_ritmul": "sets the pace",
  "prog.hist.titlu": "Workers on site, by week",
  "prog.hist.sub": "peak {v} · average {m} · {s} weeks",
  "prog.hist.saptamana": "week {n} ({d}): {t} workers",
  "prog.resurse.intro": "The recommended headcounts come from the structure pace and the lags; what you change here becomes a new scenario.",
  "prog.meserii.titlu": "Workers by trade",
  "prog.meserii.sub": "one calendar for all · {z} · {h} h/day · productivity {p}",
  "prog.meserii.th.categorie": "Trade",
  "prog.meserii.th.recomandat": "Recommended",
  "prog.meserii.th.folosit": "Used",
  "prog.meserii.th.varf": "Peak",
  "prog.meserii.th.ore": "Man-hours",
  "prog.meserii.th.zile": "Days on site",
  "prog.meserii.th.utilizare": "Utilisation",
  "prog.meserii.th.stare": "State",
  "prog.stare.impusa": "set by you",
  "prog.stare.la_ritm": "in step",
  "prog.stare.standby": "many idle days",
  "prog.stare.putin": "little used",
  "prog.revino": "Back to recommended",
  "prog.reprogrameaza": "Reschedule as a new scenario",
  "prog.scenariu_resurse": "Changed headcounts",
  "prog.scenariu_nota": "headcounts set on the Resources screen",
  "prog.macarale.titlu": "Tower cranes",
  "prog.macarale.sub": "per building, by footprint; counted against the cap while the structure runs, kept until the façade",
  "prog.macarale.th.corp": "Building",
  "prog.macarale.th.buc": "Cranes",
  "prog.macarale.th.pe_santier": "On site",
  "prog.macarale.th.luni": "Crane-months",
  "prog.macarale.th.structura": "Structure",
  "prog.macarale.th.asteptat": "Wait",
  "prog.macarale.asteptat": "{n} days, after {c}",
  "prog.macarale.asteptat_fara": "{n} days",
  "prog.macarale.santier": "site works: {n} cranes",
  "prog.macarale.total": "Total",
  "prog.macarale.plafon": "at most {n} in structure, {m} with the façades · cap {p}",
  "prog.reguli.intro": "The rules belong to the project; a change redoes the schedule of every scenario.",
  "prog.reguli.titlu": "The stages and their order",
  "prog.reguli.sub": "each stage starts after the previous one on the same unit, at the minimum distance, and keeps it to the end",
  "prog.reguli.th.cod": "Code",
  "prog.reguli.th.etapa": "Stage",
  "prog.reguli.th.unitate": "Unit",
  "prog.reguli.th.dupa": "Starts after",
  "prog.reguli.th.lag": "Lag (days)",
  "prog.reguli.catalog.titlu": "Planning rules",
  "prog.reguli.catalog.sub": "what keeps the programme in check: each rule with its parameters and why it exists; a rule tied to a material does not apply to another material",
  "prog.reguli.inactiva": "inactive",
  "prog.reguli.categorie.lag": "gap between stages",
  "prog.reguli.categorie.asteptare": "technological wait",
  "prog.reguli.categorie.capacitate": "capacity",
  "prog.reguli.categorie.resursa": "resource",
  "prog.reguli.categorie.succesiune": "sequence",
  "prog.reguli.categorie.calendar": "calendar",
  "prog.reguli.categorie.fereastra": "seasonal window",
  "prog.set.material": "Structure material",
  "prog.set.material_sub": "cast concrete has curing and stripping (the minimum cycle); precast does not",
  "prog.material.beton": "cast concrete",
  "prog.material.prefab": "precast",
  "prog.sub.parghii": "Restrictions and levers",
  "prog.kpi.cost_parghii": "Cost of levers",
  "prog.kpi.cost_parghii_sub": "{n} extra costs in the schedule",
  "prog.kpi.restrictii": "Restrictions hit",
  "prog.kpi.restrictii_atinse": "{n} hit",
  "prog.parghii.fara": "This schedule has no restrictions and levers (old engine): redo it.",
  "prog.parghii.intro": "Restrictions are checked on the site plan, the architecture and the schedule; a lever shortens the schedule at a cost and stops at a restriction. A chosen lever makes a new scenario.",
  "prog.restrictii.titlu": "Physical restrictions",
  "prog.restrictii.sub": "what the project needs against what fits; “hit” = the schedule ran into it",
  "prog.restrictii.atinsa": "hit",
  "prog.restrictii.ok": "checked",
  "prog.restrictii.fara_date": "no data",
  "prog.restrictii.valoare": "{v} of {l} {um}",
  "prog.costuri.titlu": "Extra costs",
  "prog.costuri.sub": "what the active settings cost (levers set by a person or the assistant) and the restrictions hit; they enter the business case under construction",
  "prog.costuri.niciunul": "No active lever, no restriction with a cost: the schedule runs on the base settings.",
  "prog.costuri.total": "Total",
  "prog.costuri.macarale": "The schedule's cranes: {l} crane-months × {pl} € + {b} mobilisations × {pm} € = {eur} € (already in the bill, item S.MAC).",
  "prog.parghii.titlu": "Levers",
  "prog.parghii.sub": "what shortens the schedule, what it gains, what it costs and why it stops; refused = it would hit a restriction or gain nothing now",
  "prog.parghii.aplicata": "applied",
  "prog.parghii.disponibila": "available",
  "prog.parghii.refuzata": "refused",
  "prog.parghii.neverificata": "limit unchecked",
  "prog.parghii.aplica": "Scenario with this lever",
  "prog.parghii.motiv": "Refused: {m}",
  "prog.parghii.cost": "Cost:",
  "prog.parghii.risc": "Risk:",
  "prog.scenariu_parghie": "Lever: {n}",
  "prog.scenariu_parghie_nota": "lever “{n}” set on the Levers screen",
  "prog.reguli.categorie.logistica": "logistics",
  "prog.set.pompa": "Concrete pump",
  "prog.set.pompa_sub": "concrete goes by pump; the crane lifts only formwork and rebar",
  "prog.set.pompa_da": "with a pump",
  "prog.set.statie": "Concrete delivered per day",
  "prog.set.statie_sub": "what the plant delivers to the site in a day, with traffic; a project assumption",
  "prog.set.brat": "Crane jib",
  "prog.set.brat_sub": "masts stand jib + 5 m apart; from this, how many fit on the lot",
  "prog.set.schimburi": "Shifts on the structure",
  "prog.set.schimburi_sub": "in two shifts the crane and the crew do double per day; curing remains; night premium",
  "prog.schimburi.unu": "one shift",
  "prog.schimburi.doua": "two shifts",
  "prog.set.cofraje_rapide": "Fast formwork",
  "prog.set.cofraje_rapide_sub": "table forms and admixtures: minimum cycle 5 days (8 in the basement), +15 % on formwork",
  "prog.set.cofraje_rapide_da": "table forms and admixtures",
  "prog.set.lift": "Site hoist",
  "prog.set.lift_sub": "finishing in tall buildings goes up by hoist; without it, at 80 % of the pace",
  "prog.set.lift_da": "present, within site organisation",
  "prog.set.lift_etaj": "Hoist from floor",
  "prog.set.lift_etaj_sub": "above this many floors finishing needs a site hoist",
  "prog.um.m3_zi": "m³/day",
  "prog.um.m": "m",
  "prog.sub.tinta": "Target",
  "prog.tinta.intro": "The optimizer tries the levers step by step, on the schedule and on the business case: each time it picks the step with the lowest cost per month gained (or the largest gain in annual ROI), until the target is met or nothing helps any more. The whole path is shown; the scenario is made from the plan.",
  "prog.tinta.titlu": "What we want",
  "prog.tinta.sub": "today's schedule: {n} months",
  "prog.tinta.mod_luni": "to take at most",
  "prog.tinta.luni": "months, as cheaply as possible",
  "prog.tinta.mod_roi": "the best annual ROI",
  "prog.tinta.cauta": "Find the levers",
  "prog.tinta.cauta_acum": "Searching…",
  "prog.tinta.de_la_la": "{a} → {b} months",
  "prog.tinta.atins": "the target is met",
  "prog.tinta.neatins": "below {n} months is out of reach with the available levers",
  "prog.tinta.cel_mai_bun": "best annual ROI found",
  "prog.tinta.roi_an": "Annual ROI",
  "prog.tinta.tic": "TIC {a} → {b} €",
  "prog.tinta.cost_extra": "Extra money",
  "prog.tinta.cost_extra_sub": "levers {s} € · cranes {m} € versus today",
  "prog.tinta.pasi": "Steps",
  "prog.tinta.plan": "The plan",
  "prog.tinta.plan_sub": "the levers in the order found; each with months gained, extra money and annual ROI",
  "prog.tinta.scenariu": "Scenario from plan",
  "prog.tinta.scenariu_luni": "Plan: {n} months",
  "prog.tinta.scenariu_roi": "Plan: best annual ROI",
  "prog.tinta.scenariu_nota": "levers found by the optimizer: {p}",
  "prog.tinta.niciun_pas": "No lever helps any more: today's schedule is the best on the chosen criterion.",
  "prog.tinta.th.parghie": "Lever",
  "prog.tinta.th.luni": "Months",
  "prog.tinta.th.cost": "Extra money",
  "prog.tinta.th.roi": "Annual ROI",
  "prog.tinta.th.dupa": "Months after",
  "prog.tinta.refuzate": "What stayed out of the plan",
  "prog.tinta.refuzate_sub": "refused = would hit a restriction; no gain = tried on the last schedule, brought nothing",
  "prog.tinta.nimic_refuzat": "Every lever made it into the plan.",
  "prog.tinta.fara_castig": "no gain",
  "prog.tinta.fara_castig_sub": "would give {l} months for {c} € ({r} pp annual ROI)",
  "prog.unitate.nivel": "level",
  "prog.unitate.corp": "building",
  "prog.unitate.santier": "site",
  "prog.mod.acelasi": "same level",
  "prog.mod.corp": "the building",
  "prog.mod.ultimul": "last level",
  "prog.mod.toate": "all",
  "prog.ritm.titlu": "Pace and sizing",
  "prog.set.ciclu_min": "Minimum cycle per typical level",
  "prog.set.ciclu_min_sub": "the structure goes no faster, whatever the crew",
  "prog.set.ciclu_subsol": "Minimum cycle at basement and ground floor",
  "prog.set.ciclu_subsol_sub": "heavier levels: raft, walls",
  "prog.set.marja": "Safety margin at the end",
  "prog.set.marja_sub": "each trade ends at lag + margin after the previous one",
  "prog.set.productivitate": "Productivity",
  "prog.set.productivitate_sub": "recipes and hourly rates are already at 1.0",
  "prog.set.ore_macara": "Structure crane norm",
  "prog.set.ore_macara_sub": "crane hours per m³ of concrete",
  "prog.set.amprenta_macara": "Cranes per building",
  "prog.set.amprenta_macara_sub": "one crane per this many m² of footprint (started); kept until the façade",
  "prog.set.macarale_max": "Cranes on site, at most",
  "prog.set.macarale_max_sub": "the cap: buildings with their structure in progress at once; checked when each building starts",
  "prog.set.m2_macara": "Pace of the site works",
  "prog.set.m2_macara_sub": "m² per day per crane, for earthworks, foundations and the shared basement (one zone per crane)",
  "prog.perioade.titlu": "Crane cap by period",
  "prog.perioade.sub": "one more building starts in a period with a higher cap and keeps its cranes until its structure is done",
  "prog.perioade.maxim": "cap",
  "prog.perioade.adauga": "+ Add period",
  "prog.um.m2_zi": "m²/day",
  "prog.set.densitate": "Density check",
  "prog.set.densitate_sub": "m² of level per worker; when a trade does not fit, the structure slows down",
  "prog.set.min_om": "At least this many people per level",
  "prog.set.min_om_sub": "the cap of a small level",
  "prog.um.zl": "working days",
  "prog.um.h_m3": "h/m³",
  "prog.um.m2": "m²",
  "prog.um.m2_om": "m²/worker",
  "prog.calendar.titlu": "Calendar",
  "prog.calendar.sub": "one for all trades",
  "prog.calendar.start": "Start date",
  "prog.calendar.start_sub": "default: project import + 3 months ({d})",
  "prog.calendar.zile": "Working days",
  "prog.calendar.zile_sub": "{h} hours a day",
  "prog.calendar.sarbatori": "Public holidays (Romania)",
  "prog.calendar.sarbatori_sub": "Easter and Pentecost are computed per year",
  "prog.calendar.sarbatori_da": "skipped",
  "prog.calendar.libere": "Project days off",
  "prog.calendar.libere_sub": "site closures, collective leave",
  "prog.calendar.adauga": "+ Add",
  "prog.calendar.de_la": "from",
  "prog.calendar.pana_la": "to",
  "prog.calendar.sterge": "Remove",
  "prog.zi.0": "Mo",
  "prog.zi.1": "Tu",
  "prog.zi.2": "We",
  "prog.zi.3": "Th",
  "prog.zi.4": "Fr",
  "prog.zi.5": "Sa",
  "prog.zi.6": "Su",
  "prog.luna.0": "Jan",
  "prog.luna.1": "Feb",
  "prog.luna.2": "Mar",
  "prog.luna.3": "Apr",
  "prog.luna.4": "May",
  "prog.luna.5": "Jun",
  "prog.luna.6": "Jul",
  "prog.luna.7": "Aug",
  "prog.luna.8": "Sep",
  "prog.luna.9": "Oct",
  "prog.luna.10": "Nov",
  "prog.luna.11": "Dec",
  "prog.salveaza": "Save the rules",
  "prog.revino_global": "Back to the defaults",
  "prog.salvat": "Rules saved; the schedule is being redone.",
  "prog.rev_vechi": "Someone else changed the rules meanwhile; the screen now shows theirs.",
  "prog.eroare": "The schedule could not be computed: {m}",
  "prog.fara_solutie": "The schedule needs a solution with a bill of quantities.",
  "prog.refa": "Redo the schedule",
  "prog.scenariul": "scenario “{n}”",
  "prog.pe_baza": "on the project's list and rules",
  "prog.m.necalificat": "unskilled",
  "prog.m.zidar": "masons",
  "prog.m.zugrav": "painters",
  "prog.m.faiantar": "tilers",
  "prog.m.parchetar": "floor fitters",
  "prog.m.rigipsar": "drywall fitters",
  "prog.m.montator": "fitters",
  "prog.m.instalator": "plumbers",
  "prog.m.electrician": "electricians",
  "prog.m.lacatus": "metalworkers",
  "prog.m.izolator": "insulators",
  "prog.m.mecanic_lift": "lift mechanics",
  "prog.m.pavator": "pavers",
  "prog.m.peisagist": "landscapers",
  "prog.m.dulgher": "carpenters",
  "prog.m.fierar": "rebar fixers",
  "prog.m.betonist": "concrete workers",
  "sc.th.durata": "Duration",
  "sc.th.sfarsit": "End",
  "sc.luni": "{n} months",
  "sc.ax_durata": "duration, months",
  "sc.axa_roi": "TIC × ROI per year",
  "sc.axa_durata": "TIC × duration",
  "sc.durata_se_calculeaza": "computing…",
  "bc.r.durata.obs_program": "from the construction schedule: {n} months",
  "bc.r.durata.de_unde_program": "The duration of the scenario's construction programme (the «Programme» tab), in months: not an assumption. It is shortened through the programme — cranes per period, headcounts, lags —, not by editing this figure.",
  // --- Planning → Sales in the area (`viewer/cost/PanouPiata.tsx`) ----------
  "pz.niciuna": "No search yet for this site. The search gathers the sale listings around it: price per m², rooms, floor area, building year, finishes and facade.",
  "pz.niciuna_cititor": "No search yet for this site. An editor of the project can start one.",
  "pz.cauta_prima": "Find offers in the area",
  "pz.reimprospateaza": "Refresh",
  "pz.cauta_acum": "Search now",
  "pz.cauta_acum_t": "Search the sites again, even though the offers are less than {zile} old",
  "pz.din_cache": "The offers are from {data}, less than {zile} old, so nothing was searched again. After {pana} the button searches again; until then, «Search now» searches anyway.",
  "pz.fara_locatie": "The site has no point on the map yet. It comes from the land documents (zoning certificate, cadastral plan) or from the project's location.",
  "pz.colector_mut": "The price collector is not answering right now. Try again in a few moments.",
  "pz.se_arata_vechi": "Searching again; until it finishes, the offers of the previous search stay on screen.",
  "pz.zona": "{raza} km around the site, buildings from {an} on",
  "pz.zona_orice": "{raza} km around the site, buildings of any age",
  "pz.cautat_la": "searched on {data}",
  "pz.sursa.interzis": "access refused",
  "pz.stare.in_asteptare": "queued",
  "pz.stare.ruleaza": "searching",
  "pz.stare.eroare": "error",
  "pz.trepte": "The area was widened in steps, so the sample is large enough: {lista}.",
  "pz.treapta_item": "{raza} km and {ani} → {oferte}",
  "pz.treapta_item_orice": "{raza} km, any age → {oferte}",
  "pz.regula": "The rule: up to {esantion} are gathered while the area stays within {tkm} km and {ani} since construction; below {min}, the area widens further. Farther and older offers weigh less.",
  "pz.n_oferte#n": "one offer|{n} offers|{n} offers",
  "pz.n_ani#n": "one year|{n} years|{n} years",
  "pz.n_zile#n": "one day|{n} days|{n} days",
  "pz.suspect": "suspect",
  "pz.suspect.pret_mp_prea_mic": "The price per m² is below €300: not an apartment sale.",
  "pz.suspect.pare_inchiriere": "The listing looks like a rental posted as a sale.",
  "pz.material.concrete": "concrete",
  "pz.material.brick": "brick",
  "pz.material.reinforced_concrete": "reinforced concrete",
  "pz.material.concrete_plate": "precast panels",
  "pz.material.cellular_concrete": "AAC blocks",
  "pz.material.breezeblock": "breeze blocks",
  "pz.material.wood": "timber",
  "pz.material.other": "other",
  "pz.incalzire.gas": "gas boiler",
  "pz.incalzire.urban": "district heating",
  "pz.incalzire.boiler_room": "building boiler room",
  "pz.incalzire.other": "other",
  "pz.sursa.neconfigurat": "not configured",
  "pz.stare_c.in_constructie": "under construction",
  "pz.si_pe": "also on",
  "pz.d.dubluri": "The same apartment is also listed on",
  "pz.f.surse": "Source",
  "pz.f.toate_sursele": "All sources",
  "pz.progres.locatii": "finding the neighbourhoods around the site",
  "pz.progres.cautare": "reading the result pages: {n} listings",
  "pz.progres.detalii": "reading the listings: {facut} of {total}",
  "pz.f.camere": "Rooms",
  "pz.f.toate": "All",
  "pz.f.camere.1": "Studios",
  "pz.f.camere.2": "2 rooms",
  "pz.f.camere.3": "3 rooms",
  "pz.f.camere.4": "4+ rooms",
  "pz.f.camere.necunoscut": "No room count",
  "pz.f.vechi": "also buildings older than {ani} ({n})",
  "pz.f.sortare": "Sort by",
  "pz.sort.distanta": "distance",
  "pz.sort.pondere": "weight",
  "pz.sort.pret_mp": "price per m²",
  "pz.sort.pret": "price",
  "pz.sort.suprafata": "floor area",
  "pz.sort.an": "building year",
  "pz.th.anunt": "Listing",
  "pz.th.pret_mp": "€/m²",
  "pz.th.pret": "Price",
  "pz.th.camere": "Rooms",
  "pz.th.etaj": "Floor",
  "pz.th.an": "Year",
  "pz.th.finisaje": "Finishes",
  "pz.th.fatada": "Facade",
  "pz.th.distanta": "km",
  "pz.th.pondere": "Weight",
  "pz.pondere_t": "How much the offer weighs in the area profile: half for every {km} km from the site and for every {ani} of building age.",
  "pz.aprox_t": "Distance to the listing's neighbourhood: its page has not been read yet, so the map pin is unknown.",
  "pz.mai_multe": "Show {n} more (of {tot})",
  "pz.etaj.parter": "G",
  "pz.etaj.demisol": "B",
  "pz.vechi": "old",
  "pz.vechi_t": "Building from {an}: older than {ani}, so it does not say what a new building sells for today.",
  "pz.fin.low": "low",
  "pz.fin.standard": "standard",
  "pz.fin.premium": "premium",
  "pz.fin.luxury": "luxury",
  "pz.fat.umeda": "rendered",
  "pz.fat.ventilata": "ventilated",
  "pz.fat.placata": "clad",
  "pz.fat.cortina": "curtain wall",
  "pz.fat.umeda_t": "Rendered facade: ETICS (polystyrene or mineral wool, plastered)",
  "pz.fat.ventilata_t": "Ventilated facade: cladding fixed on a subframe, with air between it and the insulation",
  "pz.fat.placata_t": "Clad facade: ceramic, stone, HPL or face brick",
  "pz.fat.cortina_t": "Curtain wall: glass facade on mullions",
  "pz.din_text": "estimated from the listing text: «{dovezi}»",
  "pz.ansamblu": "development",
  "pz.piata.primara": "primary (from the developer)",
  "pz.piata.secundara": "secondary (resale)",
  "pz.stare_c.gata": "ready to move in",
  "pz.stare_c.de_finisat": "to be finished",
  "pz.stare_c.de_renovat": "to be renovated",
  "pz.d.deschide": "Open the listing on {sursa} ↗",
  "pz.d.inchide": "Close",
  "pz.d.pret": "Price",
  "pz.d.pret_mp": "Price per m²",
  "pz.d.suprafata": "Floor area",
  "pz.d.camere": "Rooms",
  "pz.d.etaj": "Floor",
  "pz.d.an": "Building year",
  "pz.d.piata": "Market",
  "pz.d.stare": "Condition",
  "pz.d.material": "Structure",
  "pz.d.incalzire": "Heating",
  "pz.d.cartier": "Neighbourhood",
  "pz.d.adresa": "Address",
  "pz.d.agentie": "Agency",
  "pz.d.distanta": "Distance to the site",
  "pz.d.pondere": "Weight in the area",
  "pz.d.publicat": "Published",
  "pz.d.citit": "Read by us",
  "pz.d.necitit": "the listing page has not been read yet",
  "pz.d.finisaje": "Finishes",
  "pz.d.fatada": "Facade",
  "pz.d.fara_semne": "the listing says nothing about it",
  "pz.d.caracteristici": "All the features in the listing",
  "pz.d.descriere": "Description",
  "pz.d.tot_textul": "Whole text",
  "pz.d.mai_putin": "Less",
  "pz.nota": "Sources: Storia (read directly), imobiliare.ro and OLX (through Zyte); an apartment listed on several sources or by several agencies shows once, with links to the other listings. Finishes and facade are estimated from the listing text with fixed rules, and show the phrases the estimate rests on: guessing from the photos would take an AI model, and Storia does not allow its listings to reach AI. An offer's weight: half for every {km} km from the site and for every {ani} of building age.",
  "pz.tva_fara": "+ VAT",
  "pz.tva_fara_t": "The listing says the price excludes VAT: «{dovezi}». The area profile takes it as a net price.",
  "pz.p.provizoriu": "provisional",
  "pz.p.provizoriu_t": "Still searching: the profile is built from the offers found so far.",
  "pz.p.pe_ce": "on {oferte} with a price and a map pin; each weighs by its distance from the site and the age of its building",
  "pz.p.ponderat": "weighted average of asking prices",
  "pz.p.plaja": "median {mediana} · 80% of offers between {p10} and {p90}",
  "pz.p.excluse": "Left out: {lista}.",
  "pz.p.ex.suspect#n": "one suspect offer|{n} suspect offers|{n} suspect offers",
  "pz.p.ex.ansamblu#n": "one development without a price|{n} developments without a price|{n} developments without a price",
  "pz.p.ex.fara_pret#n": "one offer without a price|{n} offers without a price|{n} offers without a price",
  "pz.p.ex.alta_moneda#n": "one offer in another currency|{n} offers in another currency|{n} offers in another currency",
  "pz.p.aprox": "{oferte} without a map pin are kept apart: their distance is to the neighbourhood, so whether they are in the area is unknown. Among them: {ponderat} weighted, median {mediana}.",
  "pz.p.camere": "By rooms",
  "pz.p.parte": "Share of the area",
  "pz.p.pret_mp": "Weighted €/m²",
  "pz.p.suprafata": "Typical floor area",
  "pz.p.oferte": "Offers",
  "pz.p.finisaje": "Finishes, estimated from the text",
  "pz.p.fatade": "Facades, estimated from the text",
  "pz.p.fara_semne#n": "one offer without any sign is left out of the split|{n} offers without any sign are left out of the split|{n} offers without any sign are left out of the split",
  "pz.p.piata": "Market",
  "pz.p.mediana": "median {v}",
  "pz.p.tva#n": "VAT: one offer says «+ VAT», so its price excludes VAT|VAT: {n} offers say «+ VAT», so their price excludes VAT|VAT: {n} offers say «+ VAT», so their price excludes VAT",
  "pz.p.tva_rest": "«VAT included»: {cu}; no mention: {nespus}.",
  "pz.p.inele": "By distance",
  "pz.p.inel": "{de_la}–{pana_la} km",
  "pz.p.inel_sub": "under {km} km",
  "pz.p.g.1": "studios",
  "pz.p.g.2": "2 rooms",
  "pz.p.g.3": "3 rooms",
  "pz.p.g.4": "4+ rooms",
  "pz.p.rec.mix_pe": "The weighted split of {oferte} in the area, with the typical floor area of each group. Offers show what is on offer — what developers chose and what gets resold —, not how fast it sells.",
  "pz.p.rec.finisaje_pe": "The class with the largest share ({pct}) of the {estimate} with finishes estimated from the text: {n}.",
  "pz.p.rec.fata_de": "The other classes ask: {lista}.",
  "pz.p.rec.pret_pe_finisaj": "The weighted average of the {oferte} with {clasa} finishes ({cerut}/m² asked), without VAT: {fara} already say «+ VAT», the others are divided by {factor}.",
  "pz.p.rec.pret_pe_toate": "The weighted average of the {oferte} ({cerut}/m² asked), without VAT: {fara} already say «+ VAT», the others are divided by {factor}.",
  "pz.p.rec.cerute": "These are asking prices in listings, not closed sale prices.",
  "pz.p.rec.putine": "A recommendation needs at least {min} with a price and a map pin; the area has {n}.",
  "pz.p.rec.fara_oferte": "No offer with a price and a map pin yet; the profile appears once they are found.",
  "cp.pana_acum": "from {oferte} found so far",
  "cp.se_cauta": "Searching the offers around the site: {cat} to go. The figures below come from the offers found so far.",
  "cp.mix": "The mix on offer in the area",
  "cp.seg_t": "{pct}% {grupa} · ~{suprafata} m² · {pret}/m² asked · {oferte}",
  "cp.standard": "The standard that sells",
  "cp.standard_pe": "{pct} of the {estimate} with finishes estimated",
  "cp.pret": "The price, for the business case",
  "cp.pret_net": "{pret}/m² without VAT",
  "cp.pret_pe": "The final weighted average price in the listings {cerut}/m², with VAT",
  "cp.fara_punct": "We don't know yet what sells in the area: the site has no point on the map. It comes from the zoning certificate or the cadastral plan, and the search starts once it is there.",
  "cp.asteapta": "You can move on: when the search ends, the market's mix and standard go into the brief on their own, unless you have changed them.",
  "cp.aplicat": "Applied to the brief",
  "cp.aplicat_t": "The brief's mix and areas come from the market for as long as you don't change them.",
  "cp.egal_t": "Your brief already has the market's mix.",
  "cp.tema_ta": "Your brief",
  "cp.piata": "The market",
  "cp.aplica": "Apply to the brief",
  "cp.aplica_provizoriu_t": "Sets the brief's percentages and areas from what has been found so far; when the search ends they update to the final figures, unless you have changed them.",
  "cp.detalii": "See details",
  "cp.detalii_t": "Everything found: the area profile, all the offers, every listing",
  "cp.din_piata": "from the area's market",
  "cp.cifrele": "The area's figures",
  "cp.cifrele_arata": "Show by rooms, finishes, facades and distance",
  "cp.cifrele_ascunde": "Hide the figures",
  "cp.pe_ce_sta": "What the recommendation rests on",
  // STRUCTURA FAȚADEI — vântul pe anvelopă și cortina (`fatadaStructura/`)
  "fs.titlu": "Facade structure — wind and curtain wall",
  "fs.subtitlu": "Wind pressures on the envelope per CR 1-1-4/2012 and the sizing of the curtain wall's mullions and transoms (NP 102-04, EN 13830, EN 1999-1-1). Everything is computed from the architecture document on read; nothing is written.",
  "fs.inapoi": "← Structure",
  "fs.se_incarca": "computing…",
  "fs.eroare": "The calculation could not be run: {motiv}",
  "fs.amplasament": "Site and wind",
  "fs.localitate": "Locality",
  "fs.necunoscuta": "unknown",
  "fs.q_b": "q_b — reference pressure (50-year return)",
  "fs.q_b_calcul": "design q_b (with altitude)",
  "fs.sursa": "source",
  "fs.teren": "Terrain category",
  "fs.teren_cat": "{cat} — {descriere} (z₀ = {z0} m)",
  "fs.altitudine": "Site altitude (m)",
  "fs.norma": "Allowable deflection per",
  "fs.norma.np102": "NP 102-04 §5.1.4 — H/300, at most 8 mm",
  "fs.norma.en13830": "EN 13830 §5.7 — L/200 (≤ 3 m), 5 mm + L/300, L/250",
  "fs.schema": "Mullion static scheme",
  "fs.schema.simpla": "simply supported, slab to slab",
  "fs.schema.continua_2": "continuous over two storeys (sliding intermediate support)",
  "fs.arie": "Element area for c_pe (cladding, fixings)",
  "fs.grafic_qp": "Peak pressure q_p(z) per terrain category — the chosen one is bold; dots are the storey slabs",
  "fs.corp": "Building {cod}",
  "fs.corp_rezumat": "{h} m tall · {n} faces · {etaje} storeys",
  "fs.directii": "Wind directions (on the building's axes)",
  "fs.dir_rand": "{id}: from {dinspre} ({unghi}°), terrain {teren} — b = {b} m, d = {d} m, h/d = {hd}, e = min(b, 2h) = {e} m",
  "fs.forte_titlu": "External force on each face, per direction (kN; + pushes, − pulls)",
  "fs.fata": "face",
  "fs.plan": "Plan and directions",
  "fs.elevatie": "Face {nume} ({L} m) — {ce}; on a 1 m² element: pressure up to {p} kPa, suction up to {s} kPa",
  "fs.arata": "Show",
  "fs.suctiune": "suction (cell, at its area)",
  "fs.presiune": "pressure (cell, at its area)",
  "fs.suctiune_1": "suction on a 1 m² element",
  "fs.presiune_1": "pressure on a 1 m² element",
  "fs.legenda_kpa": "kPa",
  "fs.forte_anvelopa": "Envelope forces on the face: pressure {p} kN · suction {s} kN (cells, with internal pressure)",
  "fs.cortina": "Curtain wall — mullions and transoms",
  "fs.fara_cortina": "The building has no curtain wall in its composition; the pressures above hold for any facade system.",
  "fs.portiune": "Portion “{op}” on face {fata}, {u0}–{u1} m, mean module {modul} m",
  "fs.col.nivel": "storey",
  "fs.col.L": "L (m)",
  "fs.col.n": "mullions",
  "fs.col.w": "max w_k (kPa)",
  "fs.col.q": "max q_k (kN/m)",
  "fs.col.ix": "I_x req (cm⁴)",
  "fs.col.profil": "profile",
  "fs.col.sageata": "deflection / allowed (mm)",
  "fs.col.sigma": "σ / f_d (MPa)",
  "fs.col.rd": "R_d (kN)",
  "fs.col.g": "G_k (kN)",
  "fs.col.traversa": "transom",
  "fs.col.verdict": "result",
  "fs.teren.auto": "automatic — from the city map",
  "fs.teren.auto_rezultat": "automatic — from the map: {cat} (most exposed sector)",
  "fs.teren.auto_lipsa": "automatic — no map: default category {cat}",
  "fs.teren.pe_directii": "each wind direction takes the category of the sector it blows from",
  "fs.roza_titlu": "Surrounding terrain by sector — the category the wind comes from (CR 1-1-4 Table 2.1, over {raza} m)",
  "fs.roza_sector": "{nume} ({az0}–{az1}°): category {cat} · buildings > 15 m: {inalt} % of the area · built {construit} % (within 500 m: {c500} %) · water {apa} % · green {verde} % · {n} buildings",
  "fs.roza_lipsa": "The city map is not available for this lot: the category is the default or the one chosen above.",
  "fs.roza_incompleta": "the map is still incomplete ({gata} of {total} tiles) — the category is recomputed when it is done",
  "fs.roza_cladiri": "{n} buildings within {raza} m of the lot · {sursa}",
  "fs.legenda_teren": "0 sea · I lake, flat land · II open field · III villages, suburbs, forests · IV city (≥ 15 % buildings > 15 m)",
  "fs.corpuri": "Buildings",
  "fs.alege_corp": "Pick the building whose faces and mullions to see; the table compares all buildings.",
  "fs.col.corp": "building",
  "fs.col.h": "h (m)",
  "fs.col.fete": "faces",
  "fs.col.etaje": "storeys",
  "fs.col.wp": "pressure 1 m² (kPa)",
  "fs.col.ws": "suction 1 m² (kPa)",
  "fs.col.fmax": "max force on a face (kN)",
  "fs.col.profil_max": "governing mullion",
  "fs.fara_cortina_scurt": "no curtain wall",
  "fs.nu_trece": "fails",
  "fs.se_reface": "recomputing…",
  "fs.dir_teren": "terrain {teren}",
  "fs.compunere": "In the composition (written in the document): mullion {montant}, transom {traversa} — terrain {teren}, q_b {qb} kPa, max w_k {w} kPa. The model and the cost use this profile.",
  "fs.compunere_lipsa": "{n} storeys without a catalogue profile",
  "fs.compunere_veche": "The document was composed before the sizing: the model has the starting mullion (125 mm). Recompose (any facade edit) to pick up the computed profile.",
  "fs.trece": "passes",
  "fs.guvernant": "governed by the mullion at u = {u} m (zone {zona}, {caz})",
  "fs.profile": "Chosen profiles (mullions × storeys)",
  "fs.catalog_estimat": "The profile catalogue is ESTIMATED (50 × d tube, 2.5 mm wall) — to be replaced by the supplier's static catalogue; the report stays the same.",
  "fs.ipoteze": "Assumptions and codes",
  "fs.nefacute": "Not done yet",
  "fs.avertismente": "Warnings",
  "fs.fara_avertismente": "no warnings",
  "struct.studio.fatada_structura": "Facade structure →",
  "struct.studio.fatada_structura_titlu": "Wind on the architecture's faces and the sizing of the curtain wall",
  // --- timelapse-ul șantierului (fila «Timelapse» din Planificare)
  "costuri.tab.timelapse": "Time-lapse",
  "tl.fara_situatie": "The bill's solution has no site plan in its chain: the time-lapse has nothing to draw on.",
  "tl.se_incarca": "Loading the programme, the site plan and the structure…",
  "tl.mod.3d": "3D site",
  "tl.mod.plan": "Site layout plan",
  "tl.mod.plan_in_lucru": "The site organisation plan comes with its document (phase 2)",
  "tl.vedere": "View",
  "tl.vedere.ansamblu": "Overview",
  "tl.vedere.nord": "From the north",
  "tl.vedere.sud": "From the south",
  "tl.vedere.sus": "From above",
  "tl.straturi": "Layers",
  "tl.strat.teren": "Ground and excavation",
  "tl.strat.structura": "Structure",
  "tl.strat.zidarie": "Masonry",
  "tl.strat.fatada": "Façade",
  "tl.strat.exterioare": "Exterior works",
  "tl.strat.santier": "Site and cranes",
  "tl.fara_model": "the structural model is not built: buildings as volumes",
  "tl.fara_structura": "the bill is on the architecture: buildings as volumes, no structure",
  "tl.saptamana_din": "Week {n} of {t}",
  "tl.luna_din": "month {n} of {t}",
  "tl.oameni": "People",
  "tl.macarale": "Cranes",
  "tl.cost": "Direct cost",
  "tl.stadiul_structurii": "Structure progress",
  "tl.gata": "done",
  "tl.saptamana_asta": "This week",
  "tl.pachete_n": "{n} packages",
  "tl.nimic": "Nothing is being worked on this week.",
  "tl.la_inceput": "To the start",
  "tl.pauza": "Pause",
  "tl.porneste": "Play",
  "tl.la_sfarsit": "To the end",
  "tl.viteza": "Playback speed",
  "tl.viteza_v": "1 week = {s} s",
  "tl.saptamana": "Week",
  "tl.leg.E10": "excavation and foundations",
  "tl.leg.E30": "structure",
  "tl.leg.E40": "masonry",
  "tl.leg.E90": "façade",
  "tl.leg.E70": "fit-out",
  "tl.leg.E99": "exterior works",
  "tl.link": "Copy link",
  "tl.link.titlu": "The link opens the tab at this week",
  "tl.link.copiat": "Copied",
  "tl.fara_organizare": "The site organisation could not be computed; the scene shows its proposal",
  "tl.camioane": "Trucks / day",
  "tl.plan.faza": "Phase {id}",
  "tl.plan.oameni_varf": "Peak crew",
  "tl.plan.curte": "Yard",
  "tl.plan.containere": "Containers",
  "tl.plan.etaje": "on {n} levels",
  "tl.plan.mutare": "{zile} days, {oameni} people",
  "tl.plan.macarale": "This week's cranes",
  "tl.plan.in_groapa": "in the pit",
  "tl.plan.pe_placa": "on the slab",
  "tl.plan.in_curte": "in the yard",
  "tl.plan.peste_vecini": "jib over a neighbour",
  "tl.plan.incap": "{n} masts fit on the plot at {d} m from each other; the programme asks for at most {plafon} at once.",
  "tl.plan.avertismente": "Warnings",
  "tl.plan.sursa": "Crane count and windows come from the programme (cap {n}); positions, cabins and the road from the site organisation.",
  "tl.plan.fel.office": "offices",
  "tl.plan.fel.locker": "locker rooms",
  "tl.plan.fel.wc": "sanitary units",
  "tl.plan.fel.canteen": "canteens",
  "tl.plan.fel.first_aid": "first aid",
  "tl.plan.fel.guard": "guard",
  "tl.plan.fel.rebar": "rebar",
  "tl.plan.fel.formwork": "formwork",
  "tl.plan.fel.precast": "precast",
  "tl.plan.fel.materials": "materials",
  "tl.plan.fel.waste": "waste",
  "tl.plan.fel.concrete_pump": "pump",
  "tl.zl": "working days",
  "tl.cant.beton": "concrete",
  "tl.cant.cofraj": "formwork",
  "tl.cant.otel": "steel",
  "tl.cant.sapatura": "excavation",
  "tl.cant.umplutura": "backfill",
  "tl.faza.cofraj": "formwork",
  "tl.faza.armare": "reinforcement",
  "tl.faza.beton": "pour",
  "tl.faza.decofrare": "striking",
  "tl.faza.montaj": "erection",
  "tl.faza.sapatura": "excavation",
  "tl.faza.fundatii": "foundations",
  "tl.faza.lucru": "in progress",
  "tl.jalon.organizare": "Site organisation",
  "tl.jalon.placa_sus": "Top slab of basement {u}",
  "tl.jalon.structura": "Structure of {u} starts",
  "tl.jalon.exterioare": "Exterior works",
  "tl.jalon.receptie": "Handover",
};
